import { createUri, GenericFormData, createEnum } from '@oahz/neact-openapi';
import request, { ReqOpts } from '#utils/nxios';
/**
 * 状态: 0-待执行 1-执行中 2-已完成 3-失败 4-超时 9-已取消
 */
export type BizTaskStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'TIMEOUT'
  | 'CANCELLED';
/**
 * 业务任务详情（含输入参数和结果）
 */
export type BizTaskDetailVo = {
  /** 任务ID */
  id?: number;
  /** 单据ID */
  demand_id?: number;
  /** 单据类型: 1-需求单 2-小喇叭 3-比选 4-闭环 20-预付单 */
  demand_type?: number;
  /** 任务类型标识 */
  task_type?: string;
  /** 任务名称 */
  task_name?: string;
  /** 任务描述 */
  task_desc?: string;
  /** 执行模式: 0-本地异步 1-外部回调 */
  execute_mode?: number;
  status?: BizTaskStatus;
  /** 状态描述 */
  status_desc?: string;
  /** 发起用户ID */
  user_id?: number;
  /** 进度类型: 0-确定进度（有百分比）1-不确定进度（仅文案） */
  progress_type?: number;
  /** 当前进度 0.0~100.0（仅确定进度有意义） */
  progress?: number;
  /** 进度说明文案 */
  progress_message?: string;
  /** 失败/超时原因 */
  error_message?: string;
  /** 已重试次数 */
  retry_count?: number;
  /** 最大重试次数 */
  max_retry?: number;
  /** 开始执行时间 */
  started_at?: string;
  /** 完成时间 */
  completed_at?: string;
  /** 创建时间 */
  created_at?: string;
  /** 请求参数JSON */
  param?: string;
  /** 结果数据JSON */
  result?: string;
};
export type ResponseBizTaskDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BizTaskDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type CreateUserCmd = {
  name: string;
  email: string;
};
export type ResponseObject = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: object;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 产品范围，至少1项
 */
export type ProductScopeItem = {
  type?: string;
  value?: string;
};
/**
 * 配置明细项
 */
export type DetailItem = {
  /** 序号，从1开始 */
  seq: number;
  /** 费用类型/比选需求类型ID列表；["all"]表示全部 */
  category_ids: string[];
  /** 渠道类型列表；["all"]表示全部 */
  channel_types: string[];
};
/**
 * 供应商使用限制配置新增/编辑表单
 */
export type SupplierUsageLimitConfigForm = {
  /** 配置ID（编辑时由路径参数传入，新增时为空） */
  id?: number;
  /** 配置名称，最长100字符 */
  name?: string;
  /** 单据类型：1=常规采购，3=比选 */
  demand_type: number;
  /** 需求接收部门，至少1个：0=渠道发行中心，1=市场中心 */
  group_types: number[];
  /** 产品范围，至少1项 */
  product_scope: ProductScopeItem[];
  /** 优先级，正整数，越小越高 */
  priority: number;
  /** 状态：1=启用，0=禁用 */
  status: number;
  /** 配置明细列表，至少1行 */
  details: DetailItem[];
};
export type ResponseVoid = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: object;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商风险标签
 */
export type SupplierRiskTagVo = {
  /** 标签级别 */
  tag_level?: number;
  /** 标签名称 */
  label?: string;
  /** 标签描述 */
  desc?: string;
};
/**
 * vmdm类型
 */
export type SupplierVendorType = 'enterprise' | 'individuals';
/**
 * 组织类型
 */
export type SupplierProfitType = 0 | 1;
/**
 * 地区类型
 */
export type SupplierAreaType = 0 | 1;
/**
 * 供应商详细信息
 */
export type SimpleMainSupplierVo = {
  /** 供应商风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 主供应商名称 */
  main_supplier_name?: string;
  /** 主供应商简称 */
  main_supplier_abbr_name?: string;
  /** 主供应商编码 */
  main_supplier_code?: string;
  /** vmdm编号 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
};
export type Operator = {
  name?: string;
  job_number?: string;
  mail?: string;
};
export type SupplierLinkVo = {
  /** 对象主体id，主供应商id */
  target_id?: number;
  main_supplier?: SimpleMainSupplierVo;
  /** 关联说明 */
  remark?: string;
  operator?: Operator;
  /** 操作时间 */
  operator_time?: string;
};
export type ResponseListSupplierLinkVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierLinkVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 关联列表
 */
export type SupplierLinkInfo = {
  /** 对象主体id，主供应商id */
  target_id?: number;
  /** 关联说明 */
  remark?: string;
};
export type EditSupplierLinkForm = {
  /** 关联列表 */
  list?: SupplierLinkInfo[];
};
export type ChannelSupplierSetSingleSourceForm = {
  /** 是否单一来源 */
  single_source: boolean;
};
/**
 * 附件
 */
export type SaveStorageObjectForm = {
  id?: number;
  name?: string;
  size?: number;
};
export type UpdateMainSupplierForm = {
  /** 名称 */
  name: string;
  /** 简称名称 */
  abbr_name: string;
  /** 地址 */
  address?: string;
  /** 公司人数 */
  staff_amount: number;
  /** 是否前员工创建 */
  former_employee_founded: boolean;
  /** 附件 */
  attachments?: SaveStorageObjectForm[];
};
export type ChannelSupplierAdvanceScale = 1 | 2 | 3 | 4 | 5;
export type ChannelSupplierAdvantageResourcePlatform =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 29
  | 30
  | 31
  | 32
  | 33
  | 34
  | 35
  | 100
  | 101
  | 102
  | 103
  | 104
  | 105
  | 106
  | 107
  | 108
  | 109
  | 110
  | 111
  | 112
  | 113
  | 114
  | 115
  | 116
  | 117
  | 118
  | 119
  | 120
  | 121
  | 122
  | 123
  | 124
  | 125
  | 126
  | 127
  | 128
  | 129
  | 130
  | 131
  | 132
  | 133
  | 134;
/**
 * 服务信息
 */
export type ChannelSupplierServiceInfoForm = {
  content?: string;
  main_customers?: string;
  examples?: string;
  net_ease_game_examples?: string;
  recommended_team?: string;
  advance_scale?: ChannelSupplierAdvanceScale;
  advantage_resource_platform?: ChannelSupplierAdvantageResourcePlatform[];
  recommender?: string;
  recommender_mail?: string;
  recommendation_reason?: string;
  recommender_relationship?: string;
};
/**
 * 语种
 */
export type LanguageEnum = 'zh' | 'en' | 'ja' | 'fr';
/**
 * 渠道供应商银行账号信息
 */
export type ChannelSupplierBankInfoForm = {
  /** 银行账号/IBAN */
  bank_account?: string;
  bank_language?: LanguageEnum;
  /** 银行名称 */
  bank_name?: string;
  /** 账号名称 */
  bank_account_name?: string;
  /** 国家名称 */
  bank_country_name?: string;
  /** 国家编码 */
  bank_country_code?: string;
  /** 开户行省 */
  bank_province?: string;
  /** 开户行市 */
  bank_city?: string;
  /** Swift Code */
  swift_code?: string;
  /** 支行名称 */
  bank_branch_name?: string;
  /** 银行账号唯一code，来源于VMDM */
  bank_business_code?: string;
};
/**
 * 渠道供应商合同联系人信息
 */
export type ChannelSupplierContractContactInfoForm = {
  /** 合同联系人姓名 */
  contract_contact_name?: string;
  /** 合同联系人邮箱 */
  contract_contact_email?: string;
  /** 合同联系人地址 */
  contract_contact_address?: string;
  /** 合同联系人电话 */
  contract_contact_phone?: string;
};
/**
 * 负责人
 */
export type ChannelSupplierRelatedPersonForm = {
  name?: string;
  position?: string;
  mail?: string;
  tel?: string;
};
/**
 * 账号状态
 */
export type SupplierEmployeeEnum = 'ACTIVE' | 'LOCKED';
/**
 * 账号信息
 */
export type Account = {
  /** 用户名 */
  username?: string;
  /** 邮件 */
  email?: string;
  /** popo账号 */
  popo_account?: string;
  status?: SupplierEmployeeEnum;
};
export type UpdateValidSupplierForm = {
  main_supplier: UpdateMainSupplierForm;
  content: ChannelSupplierServiceInfoForm;
  bank_info?: ChannelSupplierBankInfoForm;
  contract_contact_info?: ChannelSupplierContractContactInfoForm;
  /** 对接人 */
  contacts?: ChannelSupplierRelatedPersonForm[];
  /** 负责人 */
  principals?: ChannelSupplierRelatedPersonForm[];
  /** 账号信息 */
  account_list?: Account[];
  /** 是否已知需要审批 */
  confirm_need_approval: boolean;
  /** 有效期 */
  invalid_date?: string;
  /** 变更原因 */
  remarks?: string;
};
/**
 * 资质文件
 */
export type StorageObjectVo = {
  /** 文件ID */
  id?: number;
  /** 文件名称 */
  name?: string;
  /** 文件大小 */
  size?: number;
  /** 下载链接 */
  download_url?: string;
  /** 预览链接 */
  preview_url?: string;
};
/**
 * 风险备注
 */
export type SupplierRiskRemarkVo = {
  operator?: Operator;
  create_time?: string;
  risk_remark?: string;
};
/**
 * 供应商备注
 */
export type SupplierCommentVo = {
  operator?: Operator;
  create_time?: string;
  supplier_comment?: string;
};
/**
 * 操作人
 */
export type OperatorDto = {
  name?: string;
  job_number?: string;
  mail?: string;
};
/**
 * 当前审批节点2
 */
export type ApprovalNode = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
/**
 * 审批意见
 */
export type ApprovalOpinionVo = {
  /** 主供应商ID */
  main_supplier_id?: number;
  operator?: OperatorDto;
  /** 创建时间 */
  create_time?: string;
  /** 审批意见 */
  approval_opinion?: string;
  approval_node?: ApprovalNode;
};
/**
 * 主供应商详情权限
 */
export type MainSupplierViewPermission = {
  /** 是否可以查看需求单 */
  can_view_demand?: boolean;
  /** 是否可以查看比选单 */
  can_view_bidding?: boolean;
  /** 是否可以编辑风险开关 */
  can_edit_risk_switch?: boolean;
  /** 是否可以新增服务类型供应商（简易） */
  can_add_channel_supplier_simple?: boolean;
  /** 是否可以新增供应商风险备注 */
  can_add_supplier_risk_remark?: boolean;
  /** 是否可以新增供应商备注 */
  can_add_supplier_comment?: boolean;
  /** 是否可以操作主体关联 */
  can_edit_links?: boolean;
};
/**
 * 供应商主体
 */
export type MainSupplierVo = {
  id?: string;
  /** vmdm编号 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 主供应商编码 */
  code?: string;
  /** 主供应商名称 */
  name?: string;
  /** 简称 */
  abbr_name?: string;
  /** 地址 */
  address?: string;
  /** 主供应商员工总数 */
  staff_amount?: number;
  /** 是否前员工创办 */
  former_employee_founded?: boolean;
  /** 是否单一来源 */
  single_source?: boolean;
  /** 资质文件 */
  attachments?: StorageObjectVo[];
  /** 证件类型 */
  identity_type?: string;
  /** 证件号码 */
  identity_no?: string;
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注 */
  risk_remarks?: SupplierRiskRemarkVo[];
  /** 供应商备注 */
  supplier_comments?: SupplierCommentVo[];
  /** 审批意见 */
  approval_opinions?: ApprovalOpinionVo[];
  /** 供应商风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 公司统一编码 */
  company_code?: string;
  /** 公司财务编码 */
  company_financial_code?: string;
  permissions?: MainSupplierViewPermission;
};
/**
 * 渠道类型
 */
export type ChannelType =
  | '01'
  | '02'
  | '03'
  | '04'
  | '05'
  | '06'
  | '07'
  | '08'
  | '09'
  | '10'
  | '11'
  | '12'
  | '13'
  | '14'
  | '15'
  | '16'
  | '17'
  | '18'
  | '19'
  | '20'
  | '21'
  | '22'
  | '23'
  | '24'
  | '25'
  | '26'
  | '27'
  | '28'
  | '90';
/**
 * 服务信息
 */
export type ChannelSupplierContentVo = {
  content?: string;
  advantage_resource_platform?: ChannelSupplierAdvantageResourcePlatform[];
  main_customers?: string;
  examples?: string;
  net_ease_game_examples?: string;
  advance_scale?: ChannelSupplierAdvanceScale;
  recommended_team?: string;
  recommender?: string;
  recommender_mail?: string;
  recommendation_reason?: string;
  recommender_relationship?: string;
};
/**
 * 银行账号信息
 */
export type ChannelSupplierBankInfoVo = {
  /** 银行账号/IBAN */
  bank_account?: string;
  bank_language?: LanguageEnum;
  /** 银行账号唯一code，来源于VMDM */
  bank_business_code?: string;
  /** 银行名称 */
  bank_name?: string;
  /** 银行账号名称 */
  bank_account_name?: string;
  /** 银行国家名称 */
  bank_country_name?: string;
  /** 银行国家编码 */
  bank_country_code?: string;
  /** 银行开户行省 */
  bank_province?: string;
  /** 银行开户行市 */
  bank_city?: string;
  /** Swift Code */
  swift_code?: string;
  /** 银行支行名称 */
  bank_branch_name?: string;
};
/**
 * 合同联系人信息
 */
export type ChannelSupplierContractContactInfoVo = {
  /** 合同联系人姓名 */
  contract_contact_name?: string;
  /** 合同联系人邮箱 */
  contract_contact_email?: string;
  /** 合同联系人地址 */
  contract_contact_address?: string;
  /** 合同联系人电话 */
  contract_contact_phone?: string;
};
/**
 * 负责人
 */
export type ChannelSupplierRelatedPersonVo = {
  name?: string;
  position?: string;
  mail?: string;
  tel?: string;
};
export type ChannelSupplierType = 3 | 2 | 1 | 6 | 7 | 5 | 4 | 99;
/**
 * 供应商类型信息
 */
export type ChannelSupplierTypeInfoVo = {
  type?: ChannelSupplierType;
  valid_date?: string;
  invalid_date?: string;
};
/**
 * 渠道供应商状态
 */
export type ChannelSupplierStatus = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
/**
 * 标签
 */
export type TagVo = {
  /** 标签id */
  id?: number;
  /** 标签名称 */
  name?: string;
};
export type ApprovalLineElementVo = {
  /** 是否可以审批 */
  can_approval?: boolean;
  /** 审批条线 */
  line_no?: number;
  /** 审批按钮名称前缀 */
  label?: string;
};
/**
 * 权限信息
 */
export type ChannelSupplierOperationPermission = {
  /** 是否编辑 */
  can_edit?: boolean;
  /** 是否可查看 */
  can_view?: boolean;
  /** 是否有商务审核权限 */
  can_biz_audit?: boolean;
  /** 是否有财务BP审核权限 */
  can_fin_audit?: boolean;
  /** 是否可以修改供应商类型 */
  can_change_supplier_type?: boolean;
  /** 是否可以打标 */
  can_tag?: boolean;
  /** 是否可以设置单一来源 */
  can_set_single_source?: boolean;
  /** 是否可以查看需求单 */
  can_view_demand?: boolean;
  /** 是否可以查看比选单 */
  can_view_bidding?: boolean;
  /** 是否可以查看个人供应商隐私信息 */
  can_view_individual_secret?: boolean;
  /** 是否可以查看个人供应商奖励信息 */
  can_view_individual_award?: boolean;
  /** 是否有策划审核权限 */
  can_plan_audit?: boolean;
  /** 是否有法务审核权限 */
  can_legal_affairs_audit?: boolean;
  /** 是否可以编辑风险开关 */
  can_edit_risk_switch?: boolean;
  /** 是否可以新增服务类型供应商（简易） */
  can_add_channel_supplier_simple?: boolean;
  /** 是否可以新增供应商风险备注 */
  can_add_supplier_risk_remark?: boolean;
  /** 是否可以新增供应商备注 */
  can_add_supplier_comment?: boolean;
  /** 是否可以拉黑供应商 */
  can_block_supplier?: boolean;
  /** 是否可编辑有效期 */
  can_edit_invalid_date?: boolean;
  /** 是否可编辑新 */
  can_edit_new?: boolean;
  /** 是否可查看新 */
  can_view_new?: boolean;
  /** 是否可以审核, 数组, 默认只有1个元素, 为true表示可以审批, 为false表示不可以审批, 会签时会有2个元素, 元素1表示条线1的审批权限, 元素2表示条线2的审批权限, 例如[true, false]表示条线1可以审批, 条线2不可以审批 */
  can_approval_list?: boolean[];
  approval_lines?: ApprovalLineElementVo[];
};
export type ChannelSupplierDetailVo = {
  /** 渠道供应商ID */
  id?: string;
  /** 渠道供应商编码 */
  code?: string;
  main_supplier?: MainSupplierVo;
  channel_type?: ChannelType;
  content?: ChannelSupplierContentVo;
  bank_info?: ChannelSupplierBankInfoVo;
  contract_contact_info?: ChannelSupplierContractContactInfoVo;
  /** 对接人 */
  contacts?: ChannelSupplierRelatedPersonVo[];
  /** 负责人 */
  principals?: ChannelSupplierRelatedPersonVo[];
  /** 账号信息 */
  account_list?: Account[];
  type_info?: ChannelSupplierTypeInfoVo;
  approval_node?: ApprovalNode;
  approval_node2?: ApprovalNode;
  status?: ChannelSupplierStatus;
  /** 标签 */
  tags?: TagVo[];
  permissions?: ChannelSupplierOperationPermission;
  /** 是否为KOL合作MCN */
  kol_mcn?: boolean;
};
export type ResponseChannelSupplierDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ChannelSupplierDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 渠道内容
 */
export type ChannelSupplierContentForm = {
  service_info?: ChannelSupplierServiceInfoForm;
  contacts?: ChannelSupplierRelatedPersonForm[];
  principals?: ChannelSupplierRelatedPersonForm[];
  bank_info?: ChannelSupplierBankInfoForm;
  contract_contact_info?: ChannelSupplierContractContactInfoForm;
  /** 账号信息 */
  account_list?: Account[];
};
export type UpdateValidChannelSupplierForm = {
  content: ChannelSupplierContentForm;
  /** 账号信息 */
  account_list?: Account[];
};
export type UpdateUnApprovalSupplierForm = {
  main_supplier: UpdateMainSupplierForm;
  content: ChannelSupplierServiceInfoForm;
  bank_info?: ChannelSupplierBankInfoForm;
  contract_contact_info?: ChannelSupplierContractContactInfoForm;
  /** 对接人 */
  contacts?: ChannelSupplierRelatedPersonForm[];
  /** 负责人 */
  principals?: ChannelSupplierRelatedPersonForm[];
  /** 是否需要提交审批 */
  need_submit: boolean;
  /** 提交原因 */
  submit_reason?: string;
  /** 账号信息 */
  account_list?: Account[];
};
export type UpdateUnApprovalChannelSupplierForm = {
  content: ChannelSupplierContentForm;
  /** 是否需要提交审批 */
  need_submit: boolean;
};
/**
 * 供应商备注
 */
export type SupplierCommentDto = {
  main_supplier_id?: number;
  operator?: OperatorDto;
  create_time?: string;
  supplier_comment?: string;
};
export type ResponseListSupplierCommentDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierCommentDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type MainSupplierAddSupplierCommentForm = {
  /** 供应商备注 */
  supplier_comment: string;
};
export type MainSupplierSetSingleSourceForm = {
  /** 是否单一来源 */
  single_source: boolean;
};
export type MainSupplierRiskSwitchForm = {
  /** 风险提示开关, 0:关闭, 1:开启 */
  risk_switch: number;
};
/**
 * 风险备注
 */
export type SupplierRiskRemarkDto = {
  main_supplier_id?: number;
  operator?: OperatorDto;
  create_time?: string;
  risk_remark?: string;
};
export type ResponseListSupplierRiskRemarkDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierRiskRemarkDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type MainSupplierAddRiskRemarkForm = {
  /** 风险备注 */
  risk_remark: string;
};
export type CreateExportTaskForm = {
  /** 导出的数据需要满足的条件（json格式），例如：列表的搜索条件 */
  condition?: string;
};
export type ResponseString = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: string;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type CreateChannelSupplierForm = {
  channel_type: ChannelType;
  /** 供应商ID */
  main_supplier_id: number;
  content: ChannelSupplierContentForm;
  /** 是否需要提交 */
  need_submit: boolean;
  /** 是否为KOL合作MCN */
  kol_mcn: boolean;
  /** 账号信息 */
  account_list?: Account[];
};
export type ResponseLong = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: number;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 角色分类
 */
export type RoleCategory =
  | 'MARKETING'
  | 'PLANNING'
  | 'BUSINESS'
  | 'GROUP_FUNCTION';
export type EditRoleCmd = {
  /** 角色ID */
  id?: number;
  /** 角色名 */
  name?: string;
  /** 权限key */
  permission?: string[];
  /** 渠道供应商类型 */
  channel_supplier_type?: string[];
  /** 需求类型 */
  demand_category?: number[];
  /** 供应商类型 */
  supplier_type?: number[];
  role_category?: RoleCategory;
  /** 是否支持线上申请 */
  is_support_online_apply?: boolean;
};
export type RubyResponseVoVoid = {
  res_code?: number;
  res_msg?: string;
  data?: object;
  version?: number;
  trace_id?: string;
};
/**
 * 产品负责人
 */
export type ProductManagerCmd = {
  /** 名字 */
  name?: string;
  /** 邮箱 */
  email?: string;
  /** 值 */
  value?: string;
  /** 是否销毁 */
  _destroy?: boolean;
};
/**
 * 新增、编辑产品
 */
export type SaveProductCmd = {
  /** 产品编号 */
  identifier?: string;
  /** 产品名称 */
  name?: string;
  /** 产品类型 */
  product_type?: string;
  /** 一级部门ID */
  dept1_id?: number;
  /** 二级部门ID */
  dept2_id?: number;
  /** 三级部门ID */
  dept3_id?: number;
  /** 费用归属公司id */
  cost_company_id?: number;
  /** 归属地区 */
  belonging_loc?: string;
  /** 产品归属 */
  ascription_type?: string;
  /** 所在地区 */
  area?: string;
  /** 是否支持星图度量 */
  support_xingtu_measure?: boolean;
  /** 产品负责人 */
  product_managers_attributes?: ProductManagerCmd[];
};
export type RubyResponseVo = {
  res_code?: number;
  res_msg?: string;
  data?: object;
  version?: number;
  trace_id?: string;
};
export type OutsourceSupplierEditForm = {
  /** 联系人 */
  contacts?: ChannelSupplierRelatedPersonForm[];
  /** 负责人 */
  principals?: ChannelSupplierRelatedPersonForm[];
  bank_info?: ChannelSupplierBankInfoForm;
  contract_contact_info?: ChannelSupplierContractContactInfoForm;
};
export type SendNewAccountEmailReq = {
  /** 邮箱 */
  email: string;
  /** 姓名 */
  name: string;
  /** 跳转链接 */
  jump_link: string;
};
export type CheckLoginReq = {
  /** 登录账号 */
  account: string;
  /** 登录密码 */
  password: string;
};
export type OutsourceCheckLoginResultDto = {
  /** 用户ID */
  user_id?: number;
  /** 用户名称 */
  user_name?: string;
  /** 用户邮箱 */
  email?: string;
};
export type ResponseOutsourceCheckLoginResultDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceCheckLoginResultDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type MuseSupplierDeviceSaveRequest = {
  mac?: string;
  mpc_supplier_ids?: string[];
  remark?: string;
};
export type MuseSupplierAccountSaveRequest = {
  email?: string;
  name?: string;
  mpc_supplier_ids?: string[];
  read?: boolean;
  write?: boolean;
  download?: boolean;
  edit?: boolean;
  remark?: string;
};
export type MuseSupplierDeviceDeleteRequest = {
  mac?: string;
  mpc_supplier_ids?: string[];
};
export type MuseSupplierAccountDeleteRequest = {
  email?: string;
  mpc_supplier_ids?: string[];
};
export type MuseSupplier = {
  mpc_supplier_id?: string;
  mpc_supplier_name?: string;
};
export type MuseSupplierRequest = {
  suppliers?: MuseSupplier[];
};
export type MuseSupplierSpaceRequest = {
  mpc_supplier_ids?: string[];
};
export type SupplierBasicDto = {
  vendor_id?: string;
  tenant_code?: string;
};
export type ResponseListString = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: string[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 查询条件
 */
export type VendorLabelQuery = {
  /** 供应商名称 */
  name?: string;
  /** 网易供应商ID */
  nev_id?: string;
};
export type OaQueryRiskTagReq = {
  /** 查询条件 */
  query?: VendorLabelQuery[];
};
/**
 * oa查询VMDM风险标签返回值
 */
export type OaQueryVmdmRiskTagRes = {
  /** 标签名称 */
  label?: string;
  /** 标签描述 */
  desc?: string;
  /** 供应商名称 */
  name?: string;
  /** vmdm供应商Code */
  nev_id?: string;
  /** 标签id */
  tag_id?: string;
  /** 标签级别 */
  tag_level?: number;
};
export type EaseFlowResponseListListOaQueryVmdmRiskTagRes = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OaQueryVmdmRiskTagRes[][];
  stacktrace?: string[];
  time?: number;
  status?: boolean;
  desc?: string;
  trace_id?: string;
};
export type DemandThirdCloseReq = {
  demand_id?: number;
};
export type DemandBillPushReq = {
  demand_id?: number;
  month?: string;
  amount?: number;
  currency?: string;
};
/**
 * 费控回调参数
 */
export type FeeControlRequestParam = {
  /** 费控回调信息，需要进行解析 */
  data: string;
};
export type FeeControlResponseVoid = {
  code?: string;
  msg?: string;
  data?: object;
};
export type ReleaseDetail = {
  release_month?: string;
  actual_release_amount_no_tax?: string;
  actual_release_currency?: string;
};
export type ClosedLoopDemandMonthReleaseCmd = {
  demand_id?: number;
  activity_end?: boolean;
  release_detail_list?: ReleaseDetail[];
};
export type ExpResponseVoid = {
  /** code */
  code?: string;
  /** 消息 */
  msg?: string;
  /** 数据 */
  data?: object;
  success?: boolean;
};
export type ClosedLoopDemandDiscardForbidCmd = {
  demand_id?: number;
};
export type GetClosedLoopDemandDetailReq = {
  demand_id?: number;
};
export type User = {
  user_id?: number;
  real_name?: string;
  email?: string;
  dept_name?: string;
  dept_code?: string;
  state?: string;
  type?: string;
  jon_number?: string;
};
export type OpenApiCloseLoopCaseDemandVo = {
  id?: number;
  state?: string;
  project_code?: string;
  finance_product_code?: string;
  finance_product_name?: string;
  estimated_release_amount_no_tax?: string;
  budget_currency?: string;
  desc?: string;
  activity_name?: string;
  cost_company_code?: string;
  supplier_name?: string;
  first_normal_user?: User;
  estimated_execution_start_at?: string;
  estimated_execution_end_at?: string;
  prepay_pay_at?: string;
};
export type ResponseOpenApiCloseLoopCaseDemandVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OpenApiCloseLoopCaseDemandVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ClosedLoopDemandActivityEndCmd = {
  demand_id?: number;
};
export type BizTaskCallbackForm = {
  /** 外部系统关联ID */
  external_id?: string;
  /** 是否成功 */
  success?: boolean;
  /** 结果JSON（成功时） */
  result?: string;
  /** 错误信息（失败时） */
  error_message?: string;
};
export type Env = 'FORMAL' | 'SANDBOX';
export type FileDetailInfo = {
  file_id?: string;
  file_name?: string;
  file_size?: string;
  file_type?: string;
  is_image?: number;
};
export type ActivityName = {
  id?: string;
  source?: string;
  type?: string;
};
export type ActivityInfo = {
  activity_id?: string;
  activity_code?: string;
  activity_name?: string;
  activity_name_dto?: ActivityName;
};
export type DelegationType = 'AGENT' | 'PRE_ADD_SIGN' | 'POST_ADD_SIGN';
export type OperationRecordUserInfoDto = {
  user_id?: number;
  user_code?: string;
  username?: string;
  email?: string;
  alternate_email?: string;
  delegation?: string;
  owner_id?: number;
  owner_code?: string;
  owner_name?: string;
  owner_email?: string;
  owner_alternate_email?: string;
  delegation_enum?: DelegationType;
};
export type NotifyEventType =
  | 'DRAFT_CREATE'
  | 'DRAFT_UPDATE'
  | 'START'
  | 'HANDLE'
  | 'USER_APPROVE'
  | 'RETURN_TO_START'
  | 'FORCE_REVOKE_TO_START'
  | 'PRE_ADD_SIGN'
  | 'POST_ADD_SIGN'
  | 'REASSIGN'
  | 'REASSIGN_STARTER'
  | 'REMIND'
  | 'TERMINATE'
  | 'CANCEL'
  | 'ABORT'
  | 'COPYFOR'
  | 'COPYFOR_CONFIRM'
  | 'DELETE'
  | 'END'
  | 'PROCESS_CREATE_FAIL';
export type OaNotifyDto = {
  current_activity_instance_id?: string;
  activity_id?: string;
  activity_code?: string;
  source_activity_instance_id?: string;
  source_activity_id?: string;
  source_activity_code?: string;
  env?: Env;
  sys_code?: string;
  notify_event_type?: string;
  return_type?: string;
  arrive_time?: number;
  notify_time?: number;
  request_id?: string;
  workflow_definition_id?: string;
  workflow_id?: string;
  remark?: string;
  remark_file_info_list?: FileDetailInfo[];
  relate_id?: string;
  request_relate_id?: string;
  callback_id?: string;
  biz_flow_id?: string;
  skip_node_list?: ActivityInfo[];
  returned_node_list?: ActivityInfo[];
  operate_user_code?: string;
  receiver_list?: OperationRecordUserInfoDto[];
  notify_event_type_enum?: NotifyEventType;
};
/**
 * 更新的数据，包含新增和变化的
 */
export type TrumpetAccountGroupDto = {
  /** 批次 */
  batch_id?: string;
  /** 小喇叭用户id */
  trumpet_id?: string;
  /** 创作匠用户id */
  kol_user_id?: string;
  /** 易闪用户id */
  ys_user_id?: string;
  /** 成为团长的游戏 */
  group_game?: string;
  /** 团名称 */
  group_name?: string;
  /** 团id */
  group_id?: string;
  /** 生效时间 */
  join_time?: string;
  /** 参与活动数 */
  join_activity_num?: number;
  /** 团员数量 */
  member_count?: number;
};
export type TrumpetSyncDataTrumpetAccountGroupDto = {
  /** 更新的数据，包含新增和变化的 */
  update_list?: TrumpetAccountGroupDto[];
  /** 删除的数据，只需要同步id */
  delete_list?: string[];
};
export type SyncDataResult = {
  /** 成功的数量 */
  success_count?: number;
  /** 忽略的数量，即未实际更新，比如数据没有变化 */
  ignore_count?: number;
  /** 失败的数量 */
  fail_count?: number;
  /** 失败的信息 */
  failed_messages?: string[];
};
export type ResponseSyncDataResult = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SyncDataResult;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 更新的数据，包含新增和变化的
 */
export type TrumpetAccountCertificationDto = {
  /** 批次 */
  batch_id?: string;
  /** 小喇叭用户id */
  trumpet_id?: string;
  /** 创作匠用户id */
  kol_user_id?: string;
  /** 易闪用户id */
  ys_user_id?: string;
  /** 产品名称 */
  game_name?: string;
  /** 认证类型, 1=全产品,2=单产品 */
  game_type?: number;
  /** 产品ID   单产品 */
  game_id?: string;
  /** 认证状态   1=生效,2=失效 */
  status?: number;
  /** 认证截止时间 */
  valid_time?: string;
  /** 认证来源   1-自主认证 2-营销推荐 */
  cer_source?: number;
  /** 邀请人姓名 */
  inviter_name?: string;
  /** 邀请人邮箱 */
  inviter_email?: string;
};
export type TrumpetSyncDataTrumpetAccountCertificationDto = {
  /** 更新的数据，包含新增和变化的 */
  update_list?: TrumpetAccountCertificationDto[];
  /** 删除的数据，只需要同步id */
  delete_list?: string[];
};
/**
 * 币种
 */
export type CurrencyEnum =
  | 'RMB'
  | 'HKD'
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'KRW'
  | 'JPY'
  | 'TWD'
  | 'SGD'
  | 'MYR'
  | 'AUD'
  | 'NULL';
/**
 * 更新的数据，包含新增和变化的
 */
export type TrumpetAccountAwardDto = {
  /** 批次 */
  batch_id?: string;
  /** 小喇叭用户id */
  trumpet_id?: string;
  /** 创作匠用户id */
  kol_user_id?: string;
  /** 易闪用户id */
  ys_user_id?: string;
  /** 活动场景 */
  activity_scene?: string;
  /** 小喇叭活动ID */
  activity_id?: string;
  /** 小喇叭活动名称 */
  activity_title?: string;
  /** 产品名称 */
  game_name?: string;
  /** MPC小喇叭二级单列表 */
  trumpet_activity_ids?: number[];
  /** MPC小喇叭需求三级单号列表 */
  trumpet_case_ids?: number[];
  /** 周账单日期 */
  week_belong_time?: string;
  /** 奖励发放日期 */
  pay_time?: string;
  /** 作者身份，个人、机构 */
  institution_type?: string;
  /** 机构名称 */
  company_name?: string;
  currency?: CurrencyEnum;
  /** 预计发放金额（含税） */
  expected_amount_with_tax?: number;
  /** 实际发放金额（含税） */
  actual_amount_with_tax?: number;
  /** 实际发放金额（不含税） */
  actual_amount_no_tax?: number;
  /** 实际发放金额（税额） */
  actual_amount_tax?: number;
};
export type TrumpetSyncDataTrumpetAccountAwardDto = {
  /** 更新的数据，包含新增和变化的 */
  update_list?: TrumpetAccountAwardDto[];
  /** 删除的数据，只需要同步id */
  delete_list?: string[];
};
/**
 * 实名信息
 */
export type Autonym = {
  /** 小喇叭用户ID */
  trumpet_id?: string;
  /** 易闪ID */
  ys_user_id?: string;
  /** 创作匠ID */
  kol_user_id?: string;
  /** 是否中国大陆 1-是 0-否 */
  is_inland_id_card?: number;
  /** 证件类型 1-身份证 */
  papers_type?: number;
  /** 真实姓名 */
  real_name?: string;
  /** 证件号 */
  id_card?: string;
};
/**
 * 签约公司信息
 */
export type SignedCompany = {
  /** 小喇叭用户ID */
  trumpet_id?: string;
  /** 易闪ID */
  ys_user_id?: string;
  /** 创作匠ID */
  kol_user_id?: string;
  /** 公司名称 */
  name?: string;
  /** MPC供应商编号 */
  mpc_no?: string;
  /** VMDM供应商编号 */
  vmdm_code?: string;
};
export type TrumpetAccountDto = {
  /** 小喇叭用户ID */
  trumpet_id?: string;
  /** 小喇叭用户状态 */
  trumpet_id_status?: string;
  /** 手机号 */
  mobile?: string;
  /** 易闪ID */
  ys_user_id?: string;
  /** 易闪注册时间 */
  ys_user_register_time?: string;
  /** 易闪ID状态 */
  ys_user_status?: string;
  /** 创作匠ID */
  kol_user_id?: string;
  /** 创作匠时间 */
  kol_user_register_time?: string;
  /** 创作匠ID状态 */
  kol_user_status?: string;
  /** 实名信息 */
  autonym?: Autonym[];
  /** 首次成为团长时间 */
  be_commander_time?: string;
  currency?: CurrencyEnum;
  /** 累计账单收入（含税） */
  wy_pay_income?: number;
  /** 累计支付宝收入（含税） */
  ali_pay_income?: number;
  /** 团长奖励收入 */
  group_pay_income?: number;
  /** 收入作者身份 1-游戏作者 2-集团作者 */
  income_authorship?: number;
  /** 是否和游戏签约 1-是 0-否 */
  is_signed_game?: number;
  /** 意向游戏名称，比如率土之滨,梦幻西游端游 */
  intention_game?: string;
  /** 签约游戏名称，比如率土之滨,梦幻西游端游 */
  signed_game_name?: string;
  /** 黑名单次数 */
  penalties_nums?: number;
  /** 签约公司信息 */
  signed_companies?: SignedCompany[];
  /** 是否被封号 1-是 0-否 */
  blocked?: number;
  income_all?: number;
  commander?: boolean;
  signed_company?: boolean;
};
/**
 * 结案报告附件
 */
export type TrumpetAttachmentDto = {
  file_url: string;
  file_name: string;
  file_type: string;
  biz_type?: string;
};
export type TrumpetSupplierReleaseDto = {
  supplier_vendor_type?: string;
  supplier_name?: string;
  supplier_vmdm?: string;
  supplier_mpc_no?: string;
  estimated_release_amount?: number;
  estimated_release_currency?: number;
  attachments?: TrumpetAttachmentDto[];
};
/**
 * 指标填写记录
 */
export type KpiInfoValue = {
  kpi_code?: string;
  value?: string;
};
export type TrumpetCaseSubmitCaseClosurePassReq = {
  case_order_id: number;
  /** 预计发放金额 */
  estimated_release_amount?: string;
  estimated_release_currency?: number;
  estimated_release_total_amount?: string;
  attachments?: TrumpetAttachmentDto[];
  details?: TrumpetSupplierReleaseDto[];
  /** 效果描述 */
  effect_description: string;
  /** 指标填写记录 */
  kpi_info_values?: KpiInfoValue[];
  /** 结案报告附件 */
  report_attachments?: TrumpetAttachmentDto[];
  note?: string;
};
export type ReleaseRecordDto = {
  release_month: string;
  actual_amount: string;
  actual_amount_no_tax: string;
  actual_tax: string;
  actual_release_attachment: TrumpetAttachmentDto;
};
export type ReleaseDetailReq = {
  case_order_id: number;
  finished: boolean;
  actual_release_amount: string;
  cancel_release_amount?: string;
  cancel_release_attachment?: TrumpetAttachmentDto;
  release_record_info?: ReleaseRecordDto;
};
export type ResponseBoolean = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: boolean;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type TrumpetCaseDeleteReq = {
  case_order_id: number;
};
export type TrumpetCaseCreateReq = {
  activity_order_id: number;
  estimated_release_amount?: string;
  estimated_release_currency?: number;
  estimated_release_total_amount?: string;
  attachments?: TrumpetAttachmentDto[];
  details?: TrumpetSupplierReleaseDto[];
};
export type TrumpetCaseCreateRes = {
  case_order_id?: number;
};
export type ResponseTrumpetCaseCreateRes = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TrumpetCaseCreateRes;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type CaseBillHandleReq = {
  case_order_id: number;
  handle_type: number;
  estimated_release_amount: string;
  estimated_release_currency: number;
  estimated_release_total_amount?: string;
  attachments?: TrumpetAttachmentDto[];
  details?: TrumpetSupplierReleaseDto[];
};
export type TrumpetActivityUpdateReq = {
  id: number;
  activity_start_at?: string;
  activity_end_at?: string;
};
/**
 * 小喇叭查询价格库请求
 */
export type TrumpetPriceLibraryQueryReq = {
  /** 标品库id */
  standard_library_id?: number;
  /** 标品等级 */
  label_level?: string;
  /** 状态：1-有效，0-失效 */
  status?: number;
  /** 页码，从1开始 */
  current_page: number;
  /** 每页大小，最大200 */
  page_size: number;
};
/**
 * 标品交付类型：1-资产类交付，2-凭证类交付
 */
export type StandardCatalogDeliveryTypeEnum = 1 | 2;
/**
 * 标品库数据项
 */
export type StandardLibraryItem = {
  /** 标品库主键id */
  source_id?: number;
  /** 标品一级名称 */
  standard1_name?: string;
  /** 标品二级名称 */
  standard2_name?: string;
  /** 标品一级Id */
  standard1_id?: number;
  /** 标品二级Id */
  standard2_id?: number;
  delivery_type?: StandardCatalogDeliveryTypeEnum;
  /** 内容描述 */
  content_desc?: string;
  /** 标签等级 */
  label_level?: string;
  /** 参考链接列表 */
  reference_link_list?: string[];
  /** 参考附件下载链接列表 */
  reference_attachment_list?: string[];
  /** 标品库id */
  standard_library_id?: number;
  /** 状态：1-有效，0-失效 */
  status?: number;
};
/**
 * 小喇叭查询标品库响应
 */
export type TrumpetStandardLibraryQueryRes = {
  /** 总数 */
  total?: number;
  /** 当前页码 */
  current_page?: number;
  /** 每页大小 */
  page_size?: number;
  /** 数据列表 */
  data?: StandardLibraryItem[];
};
export type ResponseTrumpetStandardLibraryQueryRes = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TrumpetStandardLibraryQueryRes;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 价格库数据项
 */
export type PriceLibraryItem = {
  /** 价格库主键id */
  price_library_id?: number;
  /** 标品一级名称 */
  standard1_name?: string;
  /** 标品二级名称 */
  standard2_name?: string;
  /** 标品一级Id */
  standard1_id?: number;
  /** 标品二级Id */
  standard2_id?: number;
  /** 内容描述 */
  content_desc?: string;
  /** 标签等级 */
  label_level?: string;
  /** 参考链接列表 */
  reference_link_list?: string[];
  /** 参考附件下载链接列表 */
  reference_attachment_list?: string[];
  /** 标品库id */
  standard_library_id?: number;
  /** 价格下限 */
  price_lower?: number;
  /** 价格上限 */
  price_upper?: number;
  /** 货币 */
  currency?: string;
  /** 生效时间 */
  effective_time?: string;
  /** 失效时间 */
  expiry_time?: string;
  /** 状态：1-有效，0-失效 */
  status?: number;
};
/**
 * 小喇叭查询价格库响应
 */
export type TrumpetPriceLibraryQueryRes = {
  /** 总数 */
  total?: number;
  /** 当前页码 */
  current_page?: number;
  /** 每页大小 */
  page_size?: number;
  /** 数据列表 */
  data?: PriceLibraryItem[];
};
export type ResponseTrumpetPriceLibraryQueryRes = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TrumpetPriceLibraryQueryRes;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 合作信息
 */
export type OpenapiCooperateAccount = {
  /** 合作内容（达人信息） */
  account: string;
  /** 备注 */
  remark?: string;
  /** 合作平台列表 */
  superior_resource_ships?: string[];
};
export type PlanInfo = {
  /** 用于确定后续返回二级单对应 */
  number?: number;
  /** 预算 */
  budget?: number;
  /** 描述 */
  description: string;
  /** 二级分类类型 */
  l2_category_id?: number;
  /** 三级分类类型 */
  l3_category_id?: number;
  /** 四级分类类型 */
  l4_category_id?: number;
  /** 发布时间 yyyy-MM-dd */
  publish_date?: string;
  /** 商务人员邮箱列表 */
  business_emails?: string[];
  /** 策划人员邮箱列表-传当前用户即可 */
  planner_emails: string[];
  /** 合作信息 */
  cooperate_accounts: OpenapiCooperateAccount[];
};
export type OpenapiSplitPlanForm = {
  plan_infos: PlanInfo[];
};
export type OpenapiSplitPlanFormReqDto = {
  /** 一级表单号 */
  mpc_id?: number;
  form?: OpenapiSplitPlanForm;
};
export type SplitDetail = {
  /** 记录入参id */
  number?: number;
  /** 二级单id */
  second_mpc_id?: number;
};
export type OpenapiSplitPlanFormRes = {
  split_details?: SplitDetail[];
};
export type ResponseOpenapiSplitPlanFormRes = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OpenapiSplitPlanFormRes;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 二级单状态
 */
export type TrumpetActivityState = 'running' | 'finished' | 'closed';
/**
 * 指标信息
 */
export type KpiInfo = {
  kpi_name?: string;
  kpi_code?: string;
  expect_value?: string;
};
export type TrumpetActivityVo = {
  /** 一级单单号 */
  demand_order_id?: string;
  /** 活动单Id，二级单单号 */
  activity_order_id?: string;
  /** 三级单单号 */
  case_order_id?: string;
  /** 流程标识，-1:老流程；1:新流程，参考TrumpetFlowFlagEnum */
  trumpet_flow_flag?: number;
  /** 是否小喇叭：是/否 */
  is_trumpet?: boolean;
  activity_order_status?: TrumpetActivityState;
  /** 二级单活动预算 */
  activity_order_budget?: number;
  activity_order_currency?: CurrencyEnum;
  /** 二级单标题 */
  activity_order_title?: string;
  /** 一级单所属产品编号 */
  primary_order_product_id?: string;
  /** 一级单项目编号 */
  primary_order_project_id?: string;
  /** 需求人员邮箱列表 */
  demand_principal_emails?: string[];
  /** 活动开始日期 */
  activity_start_at?: string;
  /** 活动结束日期 */
  activity_end_at?: string;
  /** 需求类型一级ID */
  l1_category_id?: number;
  /** 需求类型一级名称 */
  l1_category_name?: string;
  /** 需求类型二级ID */
  l2_category_id?: number;
  /** 需求类型二级名称 */
  l2_category_name?: string;
  /** 需求类型三级ID */
  l3_category_id?: number;
  /** 需求类型三级名称 */
  l3_category_name?: string;
  /** 需求类型四级ID */
  l4_category_id?: number;
  /** 需求类型四级名称 */
  l4_category_name?: string;
  /** 是否有策划场景，供前端使用 */
  is_need_plan_design?: boolean;
  /** 指标信息 */
  kpi_infos?: KpiInfo[];
  /** 策划人员邮箱列表 */
  demand_plan_emails?: string[];
};
export type ResponseTrumpetActivityVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TrumpetActivityVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 产品数据列表
 */
export type ProductInfoResForOa = {
  /** 产品编码 */
  product_code?: string;
  /** 产品ID */
  id?: string;
  /** 产品名称 */
  product_name?: string;
};
export type ProductInfoListRes = {
  /** 表头列定义 */
  columns?: {
    [key: string]: any;
  }[];
  /** 产品数据列表 */
  data_source?: ProductInfoResForOa[];
};
export type ResponseProductInfoListRes = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ProductInfoListRes;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ProductInfoRes = {
  /** 产品Id */
  id?: number;
  /** 产品代号 */
  identifier?: string;
  /** 产品名称 */
  name?: string;
};
export type ResponseListProductInfoRes = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ProductInfoRes[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandGroupType =
  | 'CHANNEL_GROUP'
  | 'MARKET_GROUP'
  | 'CD_TRUMPET_GROUP'
  | 'M_CLOSED_LOOP';
export type OpenapiCommonOptionsForm = {
  keys: string[];
  mpc_id?: number;
  product_ids?: number[];
  group_type?: DemandGroupType;
};
export type OptionBase = {
  name?: string;
  value?: object;
};
export type OpenapiOptionDetailWrapperVoOptionBase = {
  key?: string;
  options?: OptionBase[];
};
export type ResponseListOpenapiOptionDetailWrapperVoOptionBase = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OpenapiOptionDetailWrapperVoOptionBase[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandInfoReqDto = {
  mpc_id?: number;
};
export type DemandInfoRes = {
  /** 需求单标题 */
  title?: string;
  /** 需求类型(一级) */
  category_id?: number;
  /** 项目负责人邮箱列表 */
  principal_emails?: string[];
  /** 策划人员邮箱列表 */
  planner_emails?: string[];
  /** 项目编号 */
  project_code?: string;
};
export type ResponseDemandInfoRes = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandInfoRes;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type WorkKpiInfoDto = {
  kpi_code: string;
  kpi_actual_value: string;
};
export type CooperateContentKpiInfoDto = {
  cooperate_content_id: number;
  kpi_infos: WorkKpiInfoDto[];
};
export type ContentMarketWorkKpiReq = {
  demand_l3_id: number;
  cooperate_content_kpi_infos: CooperateContentKpiInfoDto[];
};
export type ContentMarketWorkExceptionReq = {
  demand_l3_id: number;
  handle_type: number;
};
export type Work = {
  nickname?: string;
  homepage?: string;
  title?: string;
  link?: string;
  platform?: string;
  auth_time?: string;
};
export type Item = {
  demand_l3_id: number;
  sub_demand_id: number;
  works: Work[];
};
export type ContentMarketWorkNotifyReq = {
  items: Item[];
  is_auth_expired?: boolean;
};
export type ContentMarketSupplierKocWrongNotify = {
  demand_id: number;
  url?: string;
  platform?: string;
  nick_name?: string;
  demand_cooperate_no?: string;
};
export type ContentMarketQueryUserPermissionReq = {
  /** 三级需求单号集合 */
  demand_l3_ids: number[];
  /** 用户邮箱 */
  user_email: string;
  /** 用户类型（plan-策划；business-商务；principal-需求人员；creator-创建人；all-全部（包含 策划、商务、需求人员）） */
  user_type: string;
};
export type ContentMarketUserPermissionDto = {
  demand_l3_id?: number;
  has_permission?: boolean;
};
export type ContentMarketQueryUserPermissionRes = {
  user_email?: string;
  user_type?: string;
  permission_info?: ContentMarketUserPermissionDto[];
};
export type ResponseContentMarketQueryUserPermissionRes = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ContentMarketQueryUserPermissionRes;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商类型
 */
export type DemandSupportTypeEnum =
  | 'ONLY_CP'
  | 'CP_SELF_CONTACT'
  | 'SELF_CONTACT';
export type PendingassessDemandSupplierQuoteDto = {
  cooperate_account_id?: number;
  quote_price?: number;
  currency?: CurrencyEnum;
  winning?: boolean;
  /** 报价金额（优惠前、不含税） */
  quote_price_no_tax?: number;
  /** 报价金额(优惠前、含税和服务费) */
  quote_price_with_tax?: number;
  /** 报价金额(优惠后、不含税) */
  discount_quote_price_no_tax?: number;
  /** 报价金额(优惠后、含税和服务费) */
  discount_quote_price_with_tax?: number;
  /** 报价详情附件 */
  quote_attachs_blob_keys?: string[];
};
export type PendingassessDemandSupplierDto = {
  contract_no?: string;
  supplier_id?: number;
  is_self_contact?: boolean;
  is_quoted?: boolean;
  advance_info?: string;
  supplier_category?: number;
  /** 是否智能推荐的供应商 */
  smart_recommend?: boolean;
  demand_supplier_quotes?: PendingassessDemandSupplierQuoteDto[];
};
export type OpenapiAssessToQuote = {
  support_type?: DemandSupportTypeEnum;
  /** 供应商编号列表 */
  supplier_mpc_numbers: string[];
  /** 报价截止时间 2024-01-29 21:00   yyyy-MM-dd HH:mm */
  quote_deadline: string;
  /** 备注 */
  remark?: string;
  demand_suppliers?: PendingassessDemandSupplierDto[];
};
export type OpenapiAssessToQuoteReqDto = {
  /** 二级表单号 */
  mpc_id?: number;
  form?: OpenapiAssessToQuote;
};
/**
 * 排序参数对象
 */
export type SortParam = {
  /** 排序字段名 */
  sort_field?: string;
  /** 排序方向：true表示升序(ASC)，false表示降序(DESC) */
  is_asc?: boolean;
};
/**
 * 需求优先级, 多选
 */
export type PriorityEnum =
  | 'URGENCY_LEVEL'
  | 'HIGH_LEVEL'
  | 'MIDDLE_LEVEL'
  | 'LOW_LEVEL';
/**
 * 单号层级
 */
export type TrumpetDemandType = 1 | 2 | 3;
/**
 * 我的工作台-喇叭待办 筛选表单
 */
export type WorkspaceTrumpetTodoListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单ID/名称(模糊查询) */
  search_text?: string;
  /** 需求单状态, 多选 */
  trumpet_states?: string[];
  /** 需求单类型, id, 多选 */
  trumpet_search_demand_categories?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 外部系统产品代号列表, 多选 */
  external_product_codes?: string[];
  /** 需求人员, 多选 */
  trumpet_principals?: number[];
  /** 需求优先级, 多选 */
  priorities?: PriorityEnum[];
  /** 搜索的预计开始时间区间, 最大范围200天 */
  expect_begin_at_range?: string[];
  /** 搜索的预计结束时间区间, 最大范围200天 */
  expect_end_at_range?: string[];
  /** 搜索的创建时间区间, 最大范围200天 */
  created_at_range?: string[];
  /** 单号层级 */
  levels?: TrumpetDemandType[];
  /** 执行开始时间 */
  execution_start_time?: string[];
  /** 执行结束时间 */
  execution_end_time?: string[];
  /** 仅展示被退回 */
  show_return_only?: boolean;
  /** 项目编号 */
  number?: string;
  /** 需求单号 */
  demand_id?: string;
  /** 需求单ID列表(用于详情权限反查,限定只查这些id) */
  demand_id_list?: number[];
};
/**
 * 用户信息
 */
export type UserSimpleVo = {
  /** 用户ID */
  id?: number;
  /** 用户姓名 */
  name?: string;
  /** 用户邮箱 */
  email?: string;
  /** 是否外部用户 */
  is_external?: boolean;
  /** 工号 */
  job_number?: string;
};
/**
 * 能进行的操作
 */
export type PermitActions = {
  /** 是否能进行处理 */
  can_handle?: boolean;
};
/**
 * 工作台-喇叭待办
 */
export type WorkspaceTrumpetTodoVo = {
  /** ID */
  id?: number;
  /** 单号层级 */
  level_text?: string;
  /** 关联需求单号 */
  mpc_demand_id?: number;
  /** 需求单状态 */
  state?: string;
  /** 需求单状态名称 */
  state_text?: string;
  /** 产品ID */
  product_id?: number;
  /** 产品identifier */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 需求单名称 */
  title?: string;
  /** 项目编号 */
  number?: string;
  priority?: PriorityEnum;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 需求类型(如果需求一级单，显示需求类型一级；需求二级单/三级单显示需求类型1-2-3-4) */
  category_names?: string;
  /** 需求人员 */
  demand_principals?: UserSimpleVo[];
  /** 期望开始时间 */
  expect_begin_at?: string;
  /** 期望结束时间 */
  expect_end_at?: string;
  /** 活动开始时间 */
  execution_begin_at?: string;
  /** 活动结束时间 */
  execution_end_at?: string;
  /** 预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  permit_actions?: PermitActions;
  /** 退回标识 */
  return_flag?: boolean;
  /** 创建时间 */
  created_at?: string;
  operation?: string;
};
export type ColumnVo = {
  name?: string;
  key?: string;
};
/**
 * 分页结果
 */
export type PageResultWorkspaceTrumpetTodoVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkspaceTrumpetTodoVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkspaceTrumpetTodoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkspaceTrumpetTodoVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 我的工作台-喇叭-已办 筛选表单
 */
export type WorkspaceTrumpetDoneListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单ID/名称(模糊查询) */
  search_text?: string;
  /** 需求单状态, 多选 */
  trumpet_states?: string[];
  /** 需求单类型, id, 多选 */
  trumpet_search_demand_categories?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 外部系统产品代号列表, 多选 */
  external_product_codes?: string[];
  /** 需求人员, 多选 */
  trumpet_principals?: number[];
  /** 需求优先级, 多选 */
  priorities?: PriorityEnum[];
  /** 搜索的预计开始时间区间, 最大范围200天 */
  expect_begin_at_range?: string[];
  /** 搜索的预计结束时间区间, 最大范围200天 */
  expect_end_at_range?: string[];
  /** 搜索的创建时间区间, 最大范围200天 */
  created_at_range?: string[];
  /** 单号层级 */
  levels?: TrumpetDemandType[];
  /** 执行开始时间 */
  execution_start_time?: string[];
  /** 执行结束时间 */
  execution_end_time?: string[];
  /** 仅展示被退回 */
  show_return_only?: boolean;
  /** 项目编号 */
  number?: string;
  /** 需求单号 */
  demand_id?: string;
  /** 需求单ID列表(用于详情权限反查,限定只查这些id) */
  demand_id_list?: number[];
};
/**
 * 工作台-喇叭-已办
 */
export type WorkspaceTrumpetDoneVo = {
  /** ID */
  id?: number;
  /** 单号层级 */
  level_text?: string;
  /** 关联需求单号 */
  mpc_demand_id?: number;
  /** 需求单状态 */
  state?: string;
  /** 需求单状态名称 */
  state_text?: string;
  /** 产品ID */
  product_id?: number;
  /** 产品identifier */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 需求单名称 */
  title?: string;
  /** 项目编号 */
  number?: string;
  priority?: PriorityEnum;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 需求类型(如果需求一级单，显示需求类型一级；需求二级单/三级单显示需求类型1-2-3-4) */
  category_names?: string;
  /** 需求人员 */
  demand_principals?: UserSimpleVo[];
  /** 期望开始时间 */
  expect_begin_at?: string;
  /** 期望结束时间 */
  expect_end_at?: string;
  /** 活动开始时间 */
  execution_begin_at?: string;
  /** 活动结束时间 */
  execution_end_at?: string;
  /** 预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  permit_actions?: PermitActions;
  /** 退回标识 */
  return_flag?: boolean;
  /** 创建时间 */
  created_at?: string;
  operation?: string;
};
/**
 * 分页结果
 */
export type PageResultWorkspaceTrumpetDoneVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkspaceTrumpetDoneVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkspaceTrumpetDoneVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkspaceTrumpetDoneVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 我的工作台-喇叭-全部 筛选表单
 */
export type WorkspaceTrumpetAllListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单ID/名称(模糊查询) */
  search_text?: string;
  /** 需求单状态, 多选 */
  trumpet_states?: string[];
  /** 需求单类型, id, 多选 */
  trumpet_search_demand_categories?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 外部系统产品代号列表, 多选 */
  external_product_codes?: string[];
  /** 需求人员, 多选 */
  trumpet_principals?: number[];
  /** 需求优先级, 多选 */
  priorities?: PriorityEnum[];
  /** 搜索的预计开始时间区间, 最大范围200天 */
  expect_begin_at_range?: string[];
  /** 搜索的预计结束时间区间, 最大范围200天 */
  expect_end_at_range?: string[];
  /** 搜索的创建时间区间, 最大范围200天 */
  created_at_range?: string[];
  /** 单号层级 */
  levels?: TrumpetDemandType[];
  /** 执行开始时间 */
  execution_start_time?: string[];
  /** 执行结束时间 */
  execution_end_time?: string[];
  /** 仅展示被退回 */
  show_return_only?: boolean;
  /** 项目编号 */
  number?: string;
  /** 需求单号 */
  demand_id?: string;
  /** 需求单ID列表(用于详情权限反查,限定只查这些id) */
  demand_id_list?: number[];
};
/**
 * 工作台-喇叭-全部
 */
export type WorkspaceTrumpetAllVo = {
  /** ID */
  id?: number;
  /** 单号层级 */
  level_text?: string;
  /** 关联需求单号 */
  mpc_demand_id?: number;
  /** 需求单状态 */
  state?: string;
  /** 需求单状态名称 */
  state_text?: string;
  /** 产品ID */
  product_id?: number;
  /** 产品identifier */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 需求单名称 */
  title?: string;
  /** 项目编号 */
  number?: string;
  priority?: PriorityEnum;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 需求类型(如果需求一级单，显示需求类型一级；需求二级单/三级单显示需求类型1-2-3-4) */
  category_names?: string;
  /** 需求人员 */
  demand_principals?: UserSimpleVo[];
  /** 期望开始时间 */
  expect_begin_at?: string;
  /** 期望结束时间 */
  expect_end_at?: string;
  /** 活动开始时间 */
  execution_begin_at?: string;
  /** 活动结束时间 */
  execution_end_at?: string;
  /** 预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  permit_actions?: PermitActions;
  /** 退回标识 */
  return_flag?: boolean;
  /** 创建时间 */
  created_at?: string;
  operation?: string;
};
/**
 * 分页结果
 */
export type PageResultWorkspaceTrumpetAllVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkspaceTrumpetAllVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkspaceTrumpetAllVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkspaceTrumpetAllVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 刷新待办用户数据请求入参
 */
export type WorkbenchRefreshTodoUserDataForm = {
  /** 用户ID集合 */
  user_ids?: number[];
};
/**
 * OA状态, 多选
 */
export type OaState =
  | 'NOT_SUBMITTED_OTHER'
  | 'SUBMITTING'
  | 'NOT_SUBMITTED'
  | 'SUBMITTED'
  | 'REJECTED'
  | 'ARCHIVING';
/**
 * 我的工作台-预付流程-待办 筛选表单
 */
export type WorkspacePrepaymentTodoListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 预付单号/来源单号/需求标题(模糊查询) */
  search_text?: string;
  /** 预付单状态, 多选 */
  status_list?: number[];
  /** 发票方式, 多选: 1-有票预付 2-无票预付 */
  invoice_method_list?: number[];
  /** 所属产品, 多选 */
  product_id_list?: number[];
  /** 需求分类ID, 多选（关联需求单的业务分类） */
  demand_category_ids?: number[];
  /** 供应商ID, 多选 */
  supplier_id_list?: number[];
  /** OA状态, 多选 */
  oa_status_list?: OaState[];
  /** OA流程号(精确查询) */
  oa_process_no?: string;
  /** 常规采购二级单ID(精确查询) */
  plan_demand_id?: number;
};
/**
 * 工作台-预付流程列表项
 */
export type WorkspacePrepaymentListVo = {
  /** 预付单ID */
  id?: number;
  /** MPC预付单号 */
  prepayment_no?: string;
  /** 状态 */
  status?: string;
  /** 状态描述 */
  status_desc?: string;
  /** 关联需求单ID */
  demand_id?: number;
  /** 发票方式: 1-有票预付 2-无票预付 */
  invoice_method?: number;
  /** 发票方式描述 */
  invoice_method_desc?: string;
  /** 所属产品ID */
  product_id?: number;
  /** 所属产品 */
  product_code?: string;
  /** 需求标题 */
  demand_title?: string;
  /** 需求类型(如需求一级单显示需求类型一级；需求二级单/三级单显示需求类型1-2-3-4) */
  category_names?: string;
  /** 供应商ID */
  supplier_id?: number;
  /** 供应商名称 */
  supplier_name?: string;
  /** 预付金额 */
  prepayment_amount?: number;
  /** 币种 */
  currency?: string;
  /** OA流程号 */
  oa_process_no?: string;
  oa_status?: OaState;
  /** OA链接 */
  oa_url?: string;
  /** 创建人ID */
  creator_id?: number;
  /** 创建人姓名 */
  creator_name?: string;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** 待办类型描述(用于待办列表) */
  todo_type_desc?: string;
  permit_actions?: PermitActions;
  operation?: string;
};
/**
 * 分页结果
 */
export type PageResultWorkspacePrepaymentListVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkspacePrepaymentListVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkspacePrepaymentListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkspacePrepaymentListVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 我的工作台-预付流程-已办 筛选表单
 */
export type WorkspacePrepaymentDoneListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 预付单号/来源单号/需求标题(模糊查询) */
  search_text?: string;
  /** 预付单状态, 多选 */
  status_list?: number[];
  /** 发票方式, 多选: 1-有票预付 2-无票预付 */
  invoice_method_list?: number[];
  /** 所属产品, 多选 */
  product_id_list?: number[];
  /** 需求分类ID, 多选（关联需求单的业务分类） */
  demand_category_ids?: number[];
  /** 供应商ID, 多选 */
  supplier_id_list?: number[];
  /** OA状态, 多选 */
  oa_status_list?: OaState[];
  /** OA流程号(精确查询) */
  oa_process_no?: string;
  /** 常规采购二级单ID(精确查询) */
  plan_demand_id?: number;
  /** 创建时间范围 */
  created_at_range?: string[];
};
/**
 * 我的工作台-预付流程-全部 筛选表单
 */
export type WorkspacePrepaymentAllListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 预付单号/来源单号/需求标题(模糊查询) */
  search_text?: string;
  /** 预付单状态, 多选 */
  status_list?: number[];
  /** 发票方式, 多选: 1-有票预付 2-无票预付 */
  invoice_method_list?: number[];
  /** 所属产品, 多选 */
  product_id_list?: number[];
  /** 需求分类ID, 多选（关联需求单的业务分类） */
  demand_category_ids?: number[];
  /** 供应商ID, 多选 */
  supplier_id_list?: number[];
  /** OA状态, 多选 */
  oa_status_list?: OaState[];
  /** OA流程号(精确查询) */
  oa_process_no?: string;
  /** 常规采购二级单ID(精确查询) */
  plan_demand_id?: number;
  /** 创建时间范围 */
  created_at_range?: string[];
};
export type PlatformTypeEnum = 'INNER_SOURCING' | 'MOBILE' | 'OUTSOURCING';
/**
 * 工作台小红点数量入参
 */
export type WorkbenchMenuCountForm = {
  group_type?: DemandGroupType;
  /** 父菜单key，在不传递groupType的时候使用 */
  parent_menu_key?: string;
  platform_type?: PlatformTypeEnum;
};
export type MyWorkspaceMenuCountVo = {
  key?: string;
  count?: number;
};
export type ResponseListMyWorkspaceMenuCountVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MyWorkspaceMenuCountVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 流程状态，支持多选，例如：待策划确认、待发起
 */
export type KpiAdjustStateEnum =
  | 'newly_built'
  | 'planner_approve'
  | 'oa_approve'
  | 'complete'
  | 'reject';
/**
 * 我的工作台-KPI调整-待办 筛选表单
 */
export type WorkspaceDemandKpiAdjustTodoListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** MPC一级单号（精确查询） */
  demand_id?: number;
  /** 需求标题（模糊查询） */
  name?: string;
  /** 流程状态，支持多选，例如：待策划确认、待发起 */
  states?: KpiAdjustStateEnum[];
  /** OA状态，支持多选 */
  oa_statuses?: OaState[];
  /** 需求类型一级（分类ID） */
  l1_category_ids?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 需求人员, 多选 */
  principals?: number[];
  /** 策划人员, 多选 */
  planners?: number[];
  /** 需求接收部门 */
  group_types?: DemandGroupType[];
  /** 发起时间区间 */
  created_at_range?: string[];
};
/**
 * 工作台-kpi调整单
 */
export type WorkspaceDemandKpiAdjustVo = {
  id?: number;
  /** 来源单号 */
  demand_id?: number;
  state?: KpiAdjustStateEnum;
  /** 流程状态名称 */
  state_text?: string;
  initiator?: UserSimpleVo;
  /** 发起时间 */
  created_at?: string;
  /** OA流程号 */
  oa_id?: string;
  oa_status?: OaState;
  /** OA链接 */
  oa_url?: string;
  /** OA状态 */
  oa_status_text?: string;
  /** 所属产品 */
  product_identifier?: string;
  /** 需求标题 */
  title?: string;
  priority?: PriorityEnum;
  /** 需求类型 */
  category_names?: string;
  /** 需求人员 */
  demand_principal_users?: UserSimpleVo[];
  /** 策划人员 */
  planner_users?: UserSimpleVo[];
  group_type?: DemandGroupType;
  /** 需求接收部门 */
  group_type_text?: string;
  /** 是否可处理 */
  can_handle?: boolean;
};
/**
 * 分页结果
 */
export type PageResultWorkspaceDemandKpiAdjustVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkspaceDemandKpiAdjustVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkspaceDemandKpiAdjustVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkspaceDemandKpiAdjustVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 我的工作台-KPI调整-已办 筛选表单
 */
export type WorkspaceDemandKpiAdjustDoneListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** MPC一级单号（精确查询） */
  demand_id?: number;
  /** 需求标题（模糊查询） */
  name?: string;
  /** 流程状态，支持多选，例如：待策划确认、待发起 */
  states?: KpiAdjustStateEnum[];
  /** OA状态，支持多选 */
  oa_statuses?: OaState[];
  /** 需求类型一级（分类ID） */
  l1_category_ids?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 需求人员, 多选 */
  principals?: number[];
  /** 策划人员, 多选 */
  planners?: number[];
  /** 需求接收部门 */
  group_types?: DemandGroupType[];
  /** 发起时间区间 */
  created_at_range?: string[];
};
/**
 * 我的工作台-KPI调整-全部 筛选表单
 */
export type WorkspaceDemandKpiAdjustAllListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** MPC一级单号（精确查询） */
  demand_id?: number;
  /** 需求标题（模糊查询） */
  name?: string;
  /** 流程状态，支持多选，例如：待策划确认、待发起 */
  states?: KpiAdjustStateEnum[];
  /** OA状态，支持多选 */
  oa_statuses?: OaState[];
  /** 需求类型一级（分类ID） */
  l1_category_ids?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 需求人员, 多选 */
  principals?: number[];
  /** 策划人员, 多选 */
  planners?: number[];
  /** 需求接收部门 */
  group_types?: DemandGroupType[];
  /** 发起时间区间 */
  created_at_range?: string[];
};
/**
 * 需求单状态, 多选
 */
export type DemandState =
  | 'DRAFT'
  | 'NEWLY_BUILT'
  | 'PRE_APPROVE'
  | 'PENDING_DESIGN'
  | 'PENDING_BIDDING'
  | 'PENDING_ASSESS'
  | 'PENDING_QUOTE'
  | 'QUOTE_SCORE'
  | 'BUSINESS_AUDIT_QUOTE'
  | 'BUSINESS_LEADER_AUDIT_QUOTE'
  | 'PLANNER_AUDIT_QUOTE'
  | 'PLANNER_LEADER_AUDIT_QUOTE'
  | 'MARKETING_AUDIT_QUOTE'
  | 'MARKETING_LEADER_AUDIT_QUOTE'
  | 'FEE_APPROVE'
  | 'MAKING'
  | 'BIND_EXECUTION_DATA'
  | 'UNDERWAY'
  | 'QC_ACCEPT'
  | 'PLANNER_ACCEPT'
  | 'PLANNER_LEADER_ACCEPT'
  | 'BUSINESS_ACCEPT'
  | 'DEMANDER_ACCEPT'
  | 'APPROVE_SETTLE'
  | 'SUBMIT_OA'
  | 'SUBMIT_REIMBURSE'
  | 'CLOSED'
  | 'ABANDON'
  | 'UNKNOWN'
  | 'MY_DEMAND';
/**
 * 采购方法
 */
export type PurchasingMethodEnum =
  | 'S_V_NORMAL'
  | 'S_OLD'
  | 'S_TRADITIONAL_BIDDING'
  | 'S_INQUIRY'
  | 'S_FRAMEWORK'
  | 'S_IMMEDIATELY_BIDDING'
  | 'S_ONLY_ONE_SOURCE'
  | 'S_DIRECTIONAL_BIDDING'
  | 'S_SPECIAL_MATTER_BIDDING'
  | 'S_BIDDING_FINISHED'
  | 'S_FRAMEWORK_BIDDING_FINISHED'
  | 'S_URGENT_ORDER_RESTOCKING'
  | 'S_RENEW';
/**
 * 采购策略
 */
export type PurchasingStrategyEnum =
  | 'PRICE'
  | 'DISCOUNT'
  | 'LOWEST_PRICE'
  | 'MULTI_QUOTA'
  | 'MULTI_QUOTA_FINISHED';
/**
 * 采购方法+采购策略参数
 */
export type PurchasingMethodAndStrategyParam = {
  purchasing_method?: PurchasingMethodEnum;
  purchasing_strategy?: PurchasingStrategyEnum;
};
/**
 * 二级单类型列表
 */
export type DemandPlanTypeEnum = 0 | 1 | 2;
/**
 * 我的工作台-常规采购-待办 筛选表单
 */
export type WorkspaceDemandTodoListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单ID/名称(模糊查询) */
  search_text?: string;
  /** 需求单状态, 多选 */
  demand_states?: DemandState[];
  /** 需求单类型, 多选 */
  demand_categories?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 外部系统产品代号列表, 多选 */
  external_product_codes?: string[];
  /** 需求人员, 多选 */
  principals?: number[];
  /** 需求优先级, 多选 */
  priorities?: PriorityEnum[];
  /** 是否加急 */
  urgent?: boolean;
  /** 需求单层级，多选 */
  levels?: number[];
  /** 执行开始时间 */
  execution_start_time?: string[];
  /** 执行结束时间 */
  execution_end_time?: string[];
  /** 仅展示被退回 */
  show_return_only?: boolean;
  /** 需求接收部门，渠道0 市场1，多选 */
  group_types?: DemandGroupType[];
  /** 供应商列表，多选 */
  supplier_id_list?: number[];
  /** 策划人员, 多选 */
  planners?: number[];
  /** 商务人员, 多选 */
  business_users?: number[];
  /** 采购方式, 多选 */
  purchasing_method_and_strategy_params?: PurchasingMethodAndStrategyParam[];
  /** 项目编号，精确搜索 */
  number?: string;
  /** 需求单ID */
  demand_id?: string;
  /** 需求单ID列表 */
  demand_id_list?: number[];
  /** 二级单类型列表 */
  plan_type_list?: DemandPlanTypeEnum[];
};
/**
 * 付款状态, 仅三级需求单有值
 */
export type ReimburseStatusEnum = 0 | 1 | 2 | 3 | -1;
/**
 * 我的工作台-需求单-待办
 */
export type WorkspaceDemandTodoVo = {
  /** 需求单号 */
  id?: number;
  depth?: number;
  /** 单据层级 */
  depth_text?: string;
  state?: DemandState;
  plan_type?: DemandPlanTypeEnum;
  /** 需求单状态名称 */
  state_text?: string;
  /** 所属产品 */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 需求标题 */
  name?: string;
  /** 项目编号 */
  number?: string;
  priority?: PriorityEnum;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  l2_category_name?: string;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 需求类型(如果需求一级单，显示需求类型一级；需求二级单/三级单显示需求类型1-2-3-4) */
  category_names?: string;
  /** 采购方式，采购方法（采购策略） */
  purchasing_method_and_strategy_text?: string;
  /** 需求人员 */
  demand_principals?: UserSimpleVo[];
  /** 策划人员 */
  demand_planners?: UserSimpleVo[];
  /** 商务人员 */
  demand_business_users?: UserSimpleVo[];
  /** 需求预计开始日期 */
  expect_begin_at?: string;
  /** 需求预计结束日期 */
  expect_end_at?: string;
  /** 执行开始日期 */
  execution_begin_at?: string;
  /** 执行结束日期 */
  execution_end_at?: string;
  group_type?: DemandGroupType;
  /** 供应商ID */
  channel_supplier_id?: number;
  supplier_name?: string;
  supplier_short_name?: string;
  supplier_channel_type?: ChannelType;
  is_self_contact?: boolean;
  reimburse_status?: ReimburseStatusEnum;
  /** 创建时间 */
  created_at?: string;
  /** 金额+币种：一级单取预算金额；二级单取中选供应商报价金额汇总；三级单优先取最终结算金额，如果没有则取定标报价金额一级单取预算币种；二级单取中选供应商报价币种，如果报价币种不同，则不展示币种；三级单优先取最终结算币种，如果没有则取定标报价币种 */
  amount?: number;
  currency?: CurrencyEnum;
  permit_actions?: PermitActions;
  /** 是否加急 */
  urgent?: boolean;
  /** 退回标识 */
  return_flag?: boolean;
  operation?: string;
  /** 供应商名称(服务类型), 仅三级需求单有值 */
  supplier_name_and_channel_type?: string;
};
/**
 * 分页结果
 */
export type PageResultWorkspaceDemandTodoVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkspaceDemandTodoVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkspaceDemandTodoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkspaceDemandTodoVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 我的工作台-常规采购-已办 筛选表单
 */
export type WorkspaceDemandDoneListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单ID/名称(模糊查询) */
  search_text?: string;
  /** 需求单状态, 多选 */
  demand_states?: DemandState[];
  /** 需求单类型, 多选 */
  demand_categories?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 外部系统产品代号列表, 多选 */
  external_product_codes?: string[];
  /** 需求人员, 多选 */
  principals?: number[];
  /** 需求优先级, 多选 */
  priorities?: PriorityEnum[];
  /** 是否加急 */
  urgent?: boolean;
  /** 需求单层级，多选 */
  levels?: number[];
  /** 执行开始时间 */
  execution_start_time?: string[];
  /** 执行结束时间 */
  execution_end_time?: string[];
  /** 仅展示被退回 */
  show_return_only?: boolean;
  /** 需求接收部门，渠道0 市场1，多选 */
  group_types?: DemandGroupType[];
  /** 供应商列表，多选 */
  supplier_id_list?: number[];
  /** 策划人员, 多选 */
  planners?: number[];
  /** 商务人员, 多选 */
  business_users?: number[];
  /** 采购方式, 多选 */
  purchasing_method_and_strategy_params?: PurchasingMethodAndStrategyParam[];
  /** 项目编号，精确搜索 */
  number?: string;
  /** 需求单ID */
  demand_id?: string;
  /** 需求单ID列表 */
  demand_id_list?: number[];
  /** 二级单类型列表 */
  plan_type_list?: DemandPlanTypeEnum[];
  /** 预计开始时间 */
  expect_begin_at_range?: string[];
  /** 预计结束时间 */
  expect_end_at_range?: string[];
  /** 创建时间, 最大范围200天 */
  created_at_range?: string[];
};
/**
 * 我的工作台-需求单-已办
 */
export type WorkspaceDemandDoneVo = {
  /** 需求单号 */
  id?: number;
  depth?: number;
  /** 单据层级 */
  depth_text?: string;
  state?: DemandState;
  plan_type?: DemandPlanTypeEnum;
  /** 需求单状态名称 */
  state_text?: string;
  /** 所属产品 */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 需求标题 */
  name?: string;
  /** 项目编号 */
  number?: string;
  priority?: PriorityEnum;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  l2_category_name?: string;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 需求类型(如果需求一级单，显示需求类型一级；需求二级单/三级单显示需求类型1-2-3-4) */
  category_names?: string;
  /** 采购方式，采购方法（采购策略） */
  purchasing_method_and_strategy_text?: string;
  /** 需求人员 */
  demand_principals?: UserSimpleVo[];
  /** 策划人员 */
  demand_planners?: UserSimpleVo[];
  /** 商务人员 */
  demand_business_users?: UserSimpleVo[];
  /** 需求预计开始日期 */
  expect_begin_at?: string;
  /** 需求预计结束日期 */
  expect_end_at?: string;
  /** 执行开始日期 */
  execution_begin_at?: string;
  /** 执行结束日期 */
  execution_end_at?: string;
  group_type?: DemandGroupType;
  /** 供应商ID */
  channel_supplier_id?: number;
  supplier_name?: string;
  supplier_short_name?: string;
  supplier_channel_type?: ChannelType;
  is_self_contact?: boolean;
  reimburse_status?: ReimburseStatusEnum;
  /** 创建时间 */
  created_at?: string;
  /** 金额+币种：一级单取预算金额；二级单取中选供应商报价金额汇总；三级单优先取最终结算金额，如果没有则取定标报价金额一级单取预算币种；二级单取中选供应商报价币种，如果报价币种不同，则不展示币种；三级单优先取最终结算币种，如果没有则取定标报价币种 */
  amount?: number;
  currency?: CurrencyEnum;
  permit_actions?: PermitActions;
  /** 是否加急 */
  urgent?: boolean;
  /** 退回标识 */
  return_flag?: boolean;
  operation?: string;
  /** 供应商名称(服务类型), 仅三级需求单有值 */
  supplier_name_and_channel_type?: string;
};
/**
 * 分页结果
 */
export type PageResultWorkspaceDemandDoneVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkspaceDemandDoneVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkspaceDemandDoneVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkspaceDemandDoneVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 我的工作台-常规采购-全部 筛选表单
 */
export type WorkspaceDemandAllListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单ID/名称(模糊查询) */
  search_text?: string;
  /** 需求单状态, 多选 */
  demand_states?: DemandState[];
  /** 需求单类型, 多选 */
  demand_categories?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 外部系统产品代号列表, 多选 */
  external_product_codes?: string[];
  /** 需求人员, 多选 */
  principals?: number[];
  /** 需求优先级, 多选 */
  priorities?: PriorityEnum[];
  /** 是否加急 */
  urgent?: boolean;
  /** 需求单层级，多选 */
  levels?: number[];
  /** 执行开始时间 */
  execution_start_time?: string[];
  /** 执行结束时间 */
  execution_end_time?: string[];
  /** 仅展示被退回 */
  show_return_only?: boolean;
  /** 需求接收部门，渠道0 市场1，多选 */
  group_types?: DemandGroupType[];
  /** 供应商列表，多选 */
  supplier_id_list?: number[];
  /** 策划人员, 多选 */
  planners?: number[];
  /** 商务人员, 多选 */
  business_users?: number[];
  /** 采购方式, 多选 */
  purchasing_method_and_strategy_params?: PurchasingMethodAndStrategyParam[];
  /** 项目编号，精确搜索 */
  number?: string;
  /** 需求单ID */
  demand_id?: string;
  /** 需求单ID列表 */
  demand_id_list?: number[];
  /** 二级单类型列表 */
  plan_type_list?: DemandPlanTypeEnum[];
  /** 预计开始时间 */
  expect_begin_at_range?: string[];
  /** 预计结束时间 */
  expect_end_at_range?: string[];
  /** 创建时间, 最大范围200天 */
  created_at_range?: string[];
};
/**
 * 我的工作台-需求单-待办
 */
export type WorkspaceDemandAllVo = {
  /** 需求单号 */
  id?: number;
  depth?: number;
  /** 单据层级 */
  depth_text?: string;
  state?: DemandState;
  plan_type?: DemandPlanTypeEnum;
  /** 需求单状态名称 */
  state_text?: string;
  /** 所属产品 */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 需求标题 */
  name?: string;
  /** 项目编号 */
  number?: string;
  priority?: PriorityEnum;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  l2_category_name?: string;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 需求类型(如果需求一级单，显示需求类型一级；需求二级单/三级单显示需求类型1-2-3-4) */
  category_names?: string;
  /** 采购方式，采购方法（采购策略） */
  purchasing_method_and_strategy_text?: string;
  /** 需求人员 */
  demand_principals?: UserSimpleVo[];
  /** 策划人员 */
  demand_planners?: UserSimpleVo[];
  /** 商务人员 */
  demand_business_users?: UserSimpleVo[];
  /** 需求预计开始日期 */
  expect_begin_at?: string;
  /** 需求预计结束日期 */
  expect_end_at?: string;
  /** 执行开始日期 */
  execution_begin_at?: string;
  /** 执行结束日期 */
  execution_end_at?: string;
  group_type?: DemandGroupType;
  /** 供应商ID */
  channel_supplier_id?: number;
  supplier_name?: string;
  supplier_short_name?: string;
  supplier_channel_type?: ChannelType;
  is_self_contact?: boolean;
  reimburse_status?: ReimburseStatusEnum;
  /** 创建时间 */
  created_at?: string;
  /** 金额+币种：一级单取预算金额；二级单取中选供应商报价金额汇总；三级单优先取最终结算金额，如果没有则取定标报价金额一级单取预算币种；二级单取中选供应商报价币种，如果报价币种不同，则不展示币种；三级单优先取最终结算币种，如果没有则取定标报价币种 */
  amount?: number;
  currency?: CurrencyEnum;
  permit_actions?: PermitActions;
  /** 是否加急 */
  urgent?: boolean;
  /** 退回标识 */
  return_flag?: boolean;
  operation?: string;
  /** 供应商名称(服务类型), 仅三级需求单有值 */
  supplier_name_and_channel_type?: string;
};
/**
 * 分页结果
 */
export type PageResultWorkspaceDemandAllVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkspaceDemandAllVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkspaceDemandAllVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkspaceDemandAllVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 单号层级
 */
export type ClosedLoopDemandType = 1 | 2 | 3;
/**
 * 付款状态
 */
export type ClosedLoopPayStatus = 'wait_submitted' | 'submitted' | 'archived';
/**
 * 我的工作台-闭环需求 筛选表单
 */
export type WorkspaceClosedLoopTodoListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单ID/名称(模糊查询) */
  search_text?: string;
  /** 需求单状态, 多选 */
  closed_loop_states?: string[];
  /** 需求单状态, 单选 */
  state?: string;
  /** 需求单类型, id, 多选 */
  closed_loop_search_demand_categories?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 外部系统产品代号列表, 多选 */
  external_product_codes?: string[];
  /** 需求人员, 多选 */
  closed_loop_principals?: number[];
  /** 需求优先级, 多选 */
  priorities?: PriorityEnum[];
  /** 搜索的预计开始时间区间, 最大范围200天 */
  expect_begin_at_range?: string[];
  /** 搜索的预计结束时间区间, 最大范围200天 */
  expect_end_at_range?: string[];
  /** 搜索的创建时间区间, 最大范围200天 */
  created_at_range?: string[];
  /** 单号层级 */
  levels?: ClosedLoopDemandType[];
  /** 执行开始时间 */
  execution_start_time?: string[];
  /** 执行结束时间 */
  execution_end_time?: string[];
  /** 仅展示被退回 */
  show_return_only?: boolean;
  /** 项目编号 */
  number?: string;
  /** 需求单号 */
  demand_id?: string;
  /** 付款状态 */
  pay_status?: ClosedLoopPayStatus[];
  /** 发起人Id */
  author_id?: number;
  /** 父节点需求单的Id */
  parent_id?: number;
  /** oa单状态 */
  oa_status?: OaState[];
};
/**
 * 工作台-闭环需求待办
 */
export type WorkspaceClosedLoopListVo = {
  /** ID */
  id?: number;
  /** 层级类型 */
  type_text?: string;
  /** 需求单状态 */
  state?: string;
  /** 需求单状态名称 */
  state_text?: string;
  /** 产品ID */
  product_id?: number;
  /** 产品identifier */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 需求单名称 */
  title?: string;
  /** 项目编号 */
  number?: string;
  priority?: PriorityEnum;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 需求类型(如果需求一级单，显示需求类型一级；需求二级单/三级单显示需求类型1-2-3-4) */
  category_names?: string;
  /** 需求人员 */
  demand_principals?: UserSimpleVo[];
  pay_status?: ClosedLoopPayStatus;
  /** 付款状态名称,仅三级单存在 */
  pay_status_name?: string;
  /** 预计开始时间 */
  expect_begin_at?: string;
  /** 预计结束时间 */
  expect_end_at?: string;
  /** 执行开始时间 */
  execution_begin_at?: string;
  /** 执行结束时间 */
  execution_end_at?: string;
  /** 预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  permit_actions?: PermitActions;
  /** 退回标识 */
  return_flag?: boolean;
  /** 创建时间 */
  created_at?: string;
  /** OA单号,仅三级单存在 */
  oa_id?: string;
  /** oa的URL */
  oa_url?: string;
  /** OA单状态 */
  oa_status?: string;
  /** OA单状态的名称 */
  translated_oa_status?: string;
  operation?: string;
};
/**
 * 分页结果
 */
export type PageResultWorkspaceClosedLoopListVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkspaceClosedLoopListVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkspaceClosedLoopListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkspaceClosedLoopListVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 我的工作台-闭环需求-全部 筛选表单
 */
export type WorkspaceClosedLoopAllListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单ID/名称(模糊查询) */
  search_text?: string;
  /** 需求单状态, 多选 */
  closed_loop_states?: string[];
  /** 需求单状态, 单选 */
  state?: string;
  /** 需求单类型, id, 多选 */
  closed_loop_search_demand_categories?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 外部系统产品代号列表, 多选 */
  external_product_codes?: string[];
  /** 需求人员, 多选 */
  closed_loop_principals?: number[];
  /** 需求优先级, 多选 */
  priorities?: PriorityEnum[];
  /** 搜索的预计开始时间区间, 最大范围200天 */
  expect_begin_at_range?: string[];
  /** 搜索的预计结束时间区间, 最大范围200天 */
  expect_end_at_range?: string[];
  /** 搜索的创建时间区间, 最大范围200天 */
  created_at_range?: string[];
  /** 单号层级 */
  levels?: ClosedLoopDemandType[];
  /** 执行开始时间 */
  execution_start_time?: string[];
  /** 执行结束时间 */
  execution_end_time?: string[];
  /** 仅展示被退回 */
  show_return_only?: boolean;
  /** 项目编号 */
  number?: string;
  /** 需求单号 */
  demand_id?: string;
  /** 付款状态 */
  pay_status?: ClosedLoopPayStatus[];
  /** 发起人Id */
  author_id?: number;
  /** 父节点需求单的Id */
  parent_id?: number;
  /** oa单状态 */
  oa_status?: OaState[];
};
/**
 * 我的工作台-闭环需求-全部 筛选表单
 */
export type WorkbenchClosedLoopForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  conditions?: WorkspaceClosedLoopAllListForm;
  state?: string;
};
/**
 * 工作台-闭环需求待办
 */
export type WorkspaceClosedLoopMyListVo = {
  /** ID */
  id?: number;
  /** 层级类型 */
  type_text?: string;
  /** 需求单状态 */
  state?: string;
  /** 需求单状态名称 */
  state_text?: string;
  /** 产品ID */
  product_id?: number;
  /** 产品identifier */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 需求单名称 */
  title?: string;
  /** 项目编号 */
  number?: string;
  priority?: PriorityEnum;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 需求类型(如果需求一级单，显示需求类型一级；需求二级单/三级单显示需求类型1-2-3-4) */
  category_names?: string;
  /** 需求人员 */
  demand_principals?: UserSimpleVo[];
  pay_status?: ClosedLoopPayStatus;
  /** 付款状态名称,仅三级单存在 */
  pay_status_name?: string;
  /** 预计开始时间 */
  expect_begin_at?: string;
  /** 预计结束时间 */
  expect_end_at?: string;
  /** 执行开始时间 */
  execution_begin_at?: string;
  /** 执行结束时间 */
  execution_end_at?: string;
  /** 预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  permit_actions?: PermitActions;
  /** 退回标识 */
  return_flag?: boolean;
  /** 创建时间 */
  created_at?: string;
  /** OA单号,仅三级单存在 */
  oa_id?: string;
  /** oa的URL */
  oa_url?: string;
  /** OA单状态 */
  oa_status?: string;
  /** OA单状态的名称 */
  translated_oa_status?: string;
  operation?: string;
  /** 子节点的数量 */
  child_size?: number;
};
/**
 * 分页结果
 */
export type PageResultWorkspaceClosedLoopMyListVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkspaceClosedLoopMyListVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkspaceClosedLoopMyListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkspaceClosedLoopMyListVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type WorkspaceClosedLoopWorkspaceMenuCountVo = {
  key?: string;
  count?: number;
};
export type ResponseListWorkspaceClosedLoopWorkspaceMenuCountVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: WorkspaceClosedLoopWorkspaceMenuCountVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 我的工作台-闭环需求-已办 筛选表单
 */
export type WorkspaceClosedLoopDoneListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单ID/名称(模糊查询) */
  search_text?: string;
  /** 需求单状态, 多选 */
  closed_loop_states?: string[];
  /** 需求单状态, 单选 */
  state?: string;
  /** 需求单类型, id, 多选 */
  closed_loop_search_demand_categories?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 外部系统产品代号列表, 多选 */
  external_product_codes?: string[];
  /** 需求人员, 多选 */
  closed_loop_principals?: number[];
  /** 需求优先级, 多选 */
  priorities?: PriorityEnum[];
  /** 搜索的预计开始时间区间, 最大范围200天 */
  expect_begin_at_range?: string[];
  /** 搜索的预计结束时间区间, 最大范围200天 */
  expect_end_at_range?: string[];
  /** 搜索的创建时间区间, 最大范围200天 */
  created_at_range?: string[];
  /** 单号层级 */
  levels?: ClosedLoopDemandType[];
  /** 执行开始时间 */
  execution_start_time?: string[];
  /** 执行结束时间 */
  execution_end_time?: string[];
  /** 仅展示被退回 */
  show_return_only?: boolean;
  /** 项目编号 */
  number?: string;
  /** 需求单号 */
  demand_id?: string;
  /** 付款状态 */
  pay_status?: ClosedLoopPayStatus[];
  /** 发起人Id */
  author_id?: number;
  /** 父节点需求单的Id */
  parent_id?: number;
  /** oa单状态 */
  oa_status?: OaState[];
};
export type ResponseListWorkspaceClosedLoopMyListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: WorkspaceClosedLoopMyListVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 我的工作台-预算调整单-待办 筛选表单
 */
export type WorkspaceBudgetAdjustTodoListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单标题/关联一级单号(模糊查询) */
  search_text?: string;
  /** 项目编号(模糊查询) */
  project_number?: string;
  /** 预算调整单状态, 多选 */
  state_list?: string[];
  /** 所属产品, 多选 */
  product_id_list?: number[];
  /** 需求分类ID, 多选 */
  demand_category_ids?: number[];
  /** OA状态, 多选 */
  oa_status_list?: OaState[];
  /** OA流程ID(精确查询) */
  oa_id?: string;
};
/**
 * 工作台-预算调整单列表项
 */
export type WorkspaceBudgetAdjustListVo = {
  /** 预算调整单ID */
  id?: number;
  /** 关联一级单ID列表（一个调整单可能关联多个一级单） */
  demand_ids?: number[];
  /** 需求标题 */
  demand_title?: string;
  /** 状态枚举值：PENDING-待发起, APPROVING-审批中, EFFECTIVE-已生效, NOT_EFFECTIVE-不生效, REJECTED-已驳回, ABANDONED-已废弃 */
  state?: string;
  /** 状态描述 */
  state_desc?: string;
  /** 所属产品 */
  product_code?: string;
  /** 项目编号 */
  project_number?: string;
  /** 调整说明 */
  description?: string;
  oa_state?: OaState;
  /** OA流程单号 */
  oa_id?: string;
  /** OA链接 */
  oa_url?: string;
  /** 发起人ID */
  initiator_id?: number;
  /** 发起人邮箱 */
  initiator_email?: string;
  /** 发起人姓名 */
  initiator_name?: string;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  permit_actions?: PermitActions;
  operation?: string;
};
/**
 * 分页结果
 */
export type PageResultWorkspaceBudgetAdjustListVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkspaceBudgetAdjustListVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkspaceBudgetAdjustListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkspaceBudgetAdjustListVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 我的工作台-预算调整单-已办 筛选表单
 */
export type WorkspaceBudgetAdjustDoneListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单标题/关联一级单号(模糊查询) */
  search_text?: string;
  /** 项目编号(模糊查询) */
  project_number?: string;
  /** 预算调整单状态, 多选 */
  state_list?: string[];
  /** 所属产品, 多选 */
  product_id_list?: number[];
  /** 需求分类ID, 多选 */
  demand_category_ids?: number[];
  /** OA状态, 多选 */
  oa_status_list?: OaState[];
  /** OA流程ID(精确查询) */
  oa_id?: string;
  /** 创建时间范围 */
  created_at_range?: string[];
};
/**
 * 我的工作台-预算调整单-全部 筛选表单
 */
export type WorkspaceBudgetAdjustAllListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单标题/关联一级单号(模糊查询) */
  search_text?: string;
  /** 项目编号(模糊查询) */
  project_number?: string;
  /** 预算调整单状态, 多选 */
  state_list?: string[];
  /** 所属产品, 多选 */
  product_id_list?: number[];
  /** 需求分类ID, 多选 */
  demand_category_ids?: number[];
  /** OA状态, 多选 */
  oa_status_list?: OaState[];
  /** OA流程ID(精确查询) */
  oa_id?: string;
  /** 创建时间范围 */
  created_at_range?: string[];
};
/**
 * 比选类型
 */
export type BiddingTypeEnum =
  | 'SIMPLIFIED_TENDER'
  | 'TENDER'
  | 'BIDDING'
  | 'FRAMEWORK_TENDER';
/**
 * 比选类型V2
 */
export type BiddingTypeV2Enum = 'WHOLE' | 'EVENT' | 'OTHER' | 'ANNUAL';
/**
 * 比选方式
 */
export type BiddingMethodEnum =
  | 'SIMPLE_EVALUATION'
  | 'EXPERT_EVALUATION'
  | 'COMPREHENSIVE_EVALUATION';
export type BiddingState =
  | 'NEWLY_BUILT'
  | 'PLAN_DESIGN'
  | 'AWAITING_BIDDING'
  | 'BUSINESS_CONFIRM_BID_SP'
  | 'AWAITING_SUBMISSION_BIDS'
  | 'AWAITING_RETRIEVAL_BIDS'
  | 'AWAITING_BIDDING_ADMIN_CONFIRM'
  | 'BID_OPEN_REVIEW'
  | 'UPLOAD_BID_RESULT'
  | 'AWAITING_DECISION_BIDS'
  | 'BIDING_ADMIN_ACCEPT'
  | 'CHANNEL_LEADER_ACCEPT'
  | 'MARKETING_LEADER_ACCEPT'
  | 'CLOSED'
  | 'ABANDON'
  | 'PLAN_DESIGN_ACCEPT'
  | 'LEADER_ACCEPT_CONFIRM'
  | 'BUSINESS_PLAN_DESIGN_ACCEPT'
  | 'AWAITING_JUDGES_CONFIRM_BIDS'
  | 'EVALUATION_RESULT_ACCEPT'
  | 'BUDGET_ALLOCATION'
  | 'BUSINESS_LEADER_ACCEPT'
  | 'PLANNER_LEADER_ACCEPT'
  | 'OA_PLAN_APPROVE'
  | 'OA_PLAN_APPROVE_ACCEPT'
  | 'BID_RESULT_ACCEPT'
  | 'BIDDING_FAILED';
export type MyWorkspaceBiddingTodoListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  search_text?: string;
  principals?: number[];
  priorities?: PriorityEnum[];
  /** 比选类型 */
  bidding_types?: BiddingTypeEnum[];
  /** 比选类型V2 */
  bidding_types_v2?: BiddingTypeV2Enum[];
  /** 比选方式 */
  bidding_methods?: BiddingMethodEnum[];
  /** 当前登录用户 */
  current_user_id?: number;
  /** 比选状态 */
  bidding_states?: BiddingState[];
  /** 项目编号，精确搜索 */
  number?: string;
  product?: number[];
  external_product_codes?: string[];
  permit_state_list?: BiddingState[];
  permit_product_list?: number[];
  /** 搜索的预计开始时间区间 */
  expect_start_date_range?: string[];
  /** 搜索的预计结束时间区间 */
  expect_end_date_range?: string[];
  /** 搜索的创建时间区间 */
  created_at_range?: string[];
};
/**
 * 分页数据
 */
export type BiddingWorkspaceList = {
  id?: number;
  state?: BiddingState;
  state_text?: string;
  product_identifier?: string;
  external_product_code?: string;
  name?: string;
  number?: string;
  priority?: PriorityEnum;
  product_id?: number;
  bidding_type_v2?: BiddingTypeV2Enum;
  bidding_type_text_v2?: string;
  bidding_type?: BiddingTypeEnum;
  businesses_users?: UserSimpleVo[];
  expect_begin_at?: string;
  expect_end_at?: string;
  created_at?: string;
  budget?: number;
  budget_currency?: CurrencyEnum;
  budget_text?: string;
  can_move?: boolean;
  operation?: string;
};
/**
 * 分页结果
 */
export type PageResultBiddingWorkspaceList = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: BiddingWorkspaceList[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultBiddingWorkspaceList = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultBiddingWorkspaceList;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type MyWorkspaceBiddingDoneListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  search_text?: string;
  principals?: number[];
  priorities?: PriorityEnum[];
  /** 比选类型 */
  bidding_types?: BiddingTypeEnum[];
  /** 比选类型V2 */
  bidding_types_v2?: BiddingTypeV2Enum[];
  /** 比选方式 */
  bidding_methods?: BiddingMethodEnum[];
  /** 当前登录用户 */
  current_user_id?: number;
  /** 比选状态 */
  bidding_states?: BiddingState[];
  /** 项目编号，精确搜索 */
  number?: string;
  product?: number[];
  external_product_codes?: string[];
  permit_state_list?: BiddingState[];
  permit_product_list?: number[];
  /** 搜索的预计开始时间区间 */
  expect_start_date_range?: string[];
  /** 搜索的预计结束时间区间 */
  expect_end_date_range?: string[];
  /** 搜索的创建时间区间 */
  created_at_range?: string[];
  special_states?: BiddingState[];
  planner_leader_accept_sub_users?: number[];
  marketing_leader_accept_sub_users?: number[];
};
export type MyWorkspaceBiddingAllListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  search_text?: string;
  principals?: number[];
  priorities?: PriorityEnum[];
  /** 比选类型 */
  bidding_types?: BiddingTypeEnum[];
  /** 比选类型V2 */
  bidding_types_v2?: BiddingTypeV2Enum[];
  /** 比选方式 */
  bidding_methods?: BiddingMethodEnum[];
  /** 当前登录用户 */
  current_user_id?: number;
  /** 比选状态 */
  bidding_states?: BiddingState[];
  /** 项目编号，精确搜索 */
  number?: string;
  product?: number[];
  external_product_codes?: string[];
  permit_state_list?: BiddingState[];
  permit_product_list?: number[];
  /** 搜索的预计开始时间区间 */
  expect_start_date_range?: string[];
  /** 搜索的预计结束时间区间 */
  expect_end_date_range?: string[];
  /** 搜索的创建时间区间 */
  created_at_range?: string[];
  special_states?: BiddingState[];
  planner_leader_accept_sub_users?: number[];
  marketing_leader_accept_sub_users?: number[];
};
/**
 * 工作台小红点数量出参
 */
export type WorkbenchMenuCountFormDto = {
  key?: string;
  count?: number;
};
export type ResponseListWorkbenchMenuCountFormDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: WorkbenchMenuCountFormDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 小喇叭查询条件
 */
export type TrumpetWorkbenchConditionsForm = {
  /** 查询条件（id或者名称） */
  search_text?: string;
  /** 优先级 */
  priorities?: PriorityEnum[];
  /** 产品列表 */
  product?: number[];
  /** 需求类型（一级需求分类） */
  l1_category_id?: number[];
  /** 需求类型（二级需求分类） */
  l2_category_id?: number[];
  /** 需求类型（三级需求分类） */
  l3_category_id?: number[];
  /** 需求类型（四级需求分类） */
  l4_category_id?: number[];
  /** 需求人员 */
  principals?: number[];
  /** 需求策划人 */
  planners?: number[];
  /** 运营人员 */
  operators?: number[];
  /** OA借款流程Id */
  oa_borrow_workflow_id?: string;
  /** OA状态 */
  oa_status?: OaState[];
};
/**
 * 状态
 */
export type TrumpetState =
  | 'NEWLY_BUILT'
  | 'DEMAND_PRINCIPAL_APPROVAL'
  | 'PLAN_DESIGN'
  | 'PLAN_APPROVAL'
  | 'ACTIVITY_DESIGN'
  | 'IN_PROGRESS'
  | 'CLOSED'
  | 'RUNNING'
  | 'ACTIVITY_FINISHED'
  | 'ACTIVITY_CLOSED'
  | 'SUBMIT_CASE_CLOSURE'
  | 'PLAN_ACCEPT'
  | 'APPROVE_SETTLE'
  | 'SECONDARY_APPROVE_SETTLE'
  | 'MARKETING_APPROVE_SETTLE'
  | 'QC_SETTLE'
  | 'MARKET_INTERNAL_AUDIT_TEAM_REVIEW'
  | 'STRATEGY_RESEARCH_DEPARTMENT_REVIEW'
  | 'OPERATIONS_REVIEW'
  | 'SUBMIT_OA'
  | 'CONFIRM_VERIFICATION'
  | 'FINISHED'
  | 'MY_DEMAND';
export type TrumpetWorkbenchForm = {
  current?: number;
  size?: number;
  order_field?: string;
  order_direction?: string;
  conditions?: TrumpetWorkbenchConditionsForm;
  state: TrumpetState;
  platform_type?: PlatformTypeEnum;
  /** 是否导出 */
  export?: boolean;
  group_type: DemandGroupType;
};
/**
 * 分页结果
 */
export type PageResultObject = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: object[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultObject = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultObject;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type TrumpetChildForm = {
  parent_id: number;
};
/**
 * 用户类型
 */
export type TrumpetMemberType = 1 | 2 | 3 | 4 | 9;
/**
 * 运营人员
 */
export type BaseUserVo = {
  /** 关联的主体ID */
  biz_id?: number;
  biz_type?: TrumpetDemandType;
  /** 用户id */
  user_id?: number;
  /** 姓名 */
  name?: string;
  /** 工号 */
  job_number?: string;
  /** 邮件 */
  email?: string;
  member_type?: TrumpetMemberType;
};
export type TrumpetMyDemandListVo = {
  /** id */
  id?: number;
  /** 父级单id */
  parent_id?: number;
  /** 一级单号 */
  demand_id?: number;
  /** 所属产品 */
  product_identifier?: string;
  /** 需求标题 */
  title?: string;
  priority?: PriorityEnum;
  /** 需求类型 */
  demand_type?: string;
  /** 需求人员 */
  principals?: BaseUserVo[];
  /** 预计开始时间 */
  expect_begin_at?: string;
  /** 预计结束时间 */
  expect_end_at?: string;
  activity_start_at?: string;
  activity_end_at?: string;
  planners?: BaseUserVo[];
  /** 活动预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  /** 是否可以查看详情 */
  can_view?: boolean;
  /** 是否可以处理 */
  can_handle?: boolean;
  /** 是否可以编辑 */
  can_edit?: boolean;
  /** 是否可以关闭 */
  can_close?: boolean;
  /** oaId */
  oa_id?: string;
  /** oa 状态 */
  translated_oa_status?: string;
  /** 状态 */
  state?: string;
  /** 状态描述 */
  state_desc?: string;
  /** 运营人员 */
  operators?: BaseUserVo[];
  /** 预计发放金额 */
  expect_amount?: number;
  /** 实际发放金额 */
  actual_amount?: number;
  /** 子节点数量 */
  child_size?: number;
  /** 子节点列表 */
  children?: TrumpetMyDemandListVo[];
};
export type ResponseListTrumpetMyDemandListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TrumpetMyDemandListVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 审批类型
 */
export type ApproveTypeEnum =
  | 'PLAN_APPROVE'
  | 'PRE_APPROVE'
  | 'FEE_APPROVE'
  | 'SUBMIT_APPROVE'
  | 'SPECIAL_MATTER_APPROVE'
  | 'DIRECTIONAL_BIDDING_APPROVE'
  | 'BIDDING_PLAN_APPROVE'
  | 'NIANKUANG_BIDDING_PLAN_APPROVE'
  | 'TRUMPET_PLAN_APPROVE'
  | 'TRUMPET_SUBMIT_APPROVE'
  | 'CLOSED_LOOP_PLAN_APPROVE'
  | 'CLOSED_LOOP_SUBMIT_APPROVE'
  | 'KPI_ADJUSTMENT_APPROVE'
  | 'TRUMPET_BUDGET_INCREASE_APPROVE'
  | 'PERMISSION_APPLICATION_APPROVE'
  | 'OVER_AMOUNT_APPROVE'
  | 'PRICE_LIBRARY_APPROVE'
  | 'PREPAYMENT_APPROVE'
  | 'BUDGET_ADJUST_APPROVE'
  | 'SM_EXPENSE_BUDGET_APPROVE';
/**
 * 询比价评分状态
 */
export type QuoteScoreStateEnum =
  | 'NOT_SCORED'
  | 'SCORING'
  | 'SCORED'
  | 'NO_NEED';
/**
 * 查询条件
 */
export type WorkbenchConditionsForm = {
  /** 查询条件（id或者名称） */
  search_text?: string;
  /** 优先级 */
  priorities?: PriorityEnum[];
  /** 产品列表 */
  product?: number[];
  /** 项目负责人 */
  principals?: number[];
  /** 供应商 */
  suppliers?: number[];
  /** 一级需求分类 */
  l1_category_id?: number[];
  /** 二级需求分类 */
  l2_category_id?: number[];
  /** 三级需求分类 */
  l3_category_id?: number[];
  /** 四级需求分类 */
  l4_category_id?: number[];
  /** 需求分类，不区分级别 */
  demand_categories?: number[];
  /** 策划 */
  demand_plan_users?: number[];
  /** 商务 */
  demand_business_users?: number[];
  /** OA状态 */
  oa_status?: OaState[];
  /** 是否无合同事项 */
  no_contract_flag?: boolean;
  /** 是否未填写合同编号 */
  miss_contract_no?: boolean;
  /** 付款状态 */
  reimburse_status?: ReimburseStatusEnum[];
  /** 无纸化处理，0=否，1=是 */
  paperless_flag?: boolean;
  /** 审批类型 */
  pre_approve_type?: ApproveTypeEnum[];
  /** 报价评分状态 */
  quote_score_states?: QuoteScoreStateEnum[];
  /** 是否超额 */
  over_amount_flag?: boolean;
  /** 超额验收OA状态 */
  over_amount_oa_status?: OaState[];
  /** 是否加急 */
  urgent?: boolean;
};
/**
 * 工作台表单
 */
export type WorkbenchForm = {
  current?: number;
  size?: number;
  order_field?: string;
  order_direction?: string;
  conditions?: WorkbenchConditionsForm;
  state: DemandState;
  platform_type?: PlatformTypeEnum;
  /** 是否导出 */
  export?: boolean;
  group_type: DemandGroupType;
};
/**
 * 批量处理类型
 */
export type DemandBatchHandleTypeEnum = 0 | 1 | 2 | 3;
/**
 * 分页数据
 */
export type Workbenches = {
  id?: number;
  product_identifier?: string;
  name?: string;
  priority?: number;
  demand_category?: string;
  principal_names?: string[];
  state?: DemandState;
  expect_begin_at?: string;
  expect_end_at?: string;
  operation?: string;
  can_move?: boolean;
  can_back?: boolean;
  can_submit_oa?: boolean;
  can_back_oa?: boolean;
  can_cost_reimburse?: boolean;
  can_return_to_upload_invoice?: boolean;
  can_batch_handle?: boolean;
  batch_handle_type?: DemandBatchHandleTypeEnum;
  source_id?: number;
  source_type?: string;
  source_oa?: boolean;
  cost_company_id?: number;
  cost_company_city?: string;
  is_bind_execution_data?: number;
  urgent?: boolean;
  urgent_reason?: string;
  urgent_step?: string;
};
/**
 * 分页结果
 */
export type PageResultWorkbenches = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: Workbenches[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkbenches = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkbenches;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ChildForm = {
  parent_id: number;
  closed?: boolean;
};
export type ResponseListWorkbenches = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: Workbenches[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseMapObjectObject = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: {
    [key: string]: any;
  };
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type TrumpetLoanInfoForm = {
  /** 借款金额 */
  borrow_amount?: number;
  borrow_currency?: CurrencyEnum;
  oa_borrow_workflow_id?: string;
};
export type Response = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: object;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 平台编码, MPC, DSPF
 */
export type DemandKpiPlatformEnum = 'MPC' | 'DSPF';
/**
 * KPI类型编码, numeric 数值, plain_text 文本
 */
export type DemandKpiTypeEnum = 'numeric' | 'plain_text';
/**
 * KPI状态编码, disabled-停用, enabled-启用
 */
export type CommonEnableStatusEnum = 0 | 1;
/**
 * 是否正向指标编码, positive-正向, negative-负向
 */
export type DemandKpiPositiveEnum = 0 | 1;
/**
 * 是否对供应商显示编码, no-否, yes-是
 */
export type CommonYesNoEnum = 0 | 1;
/**
 * 短期/长期指标
 */
export type DemandKpiLongOrShortTermEnum = 0 | 1 | 2;
/**
 * 指标格式
 */
export type DemandKpiSchemaData = {
  platform?: DemandKpiPlatformEnum;
  /** KPI编码 */
  code?: string;
  /** 数易编码 */
  dspf_code?: string;
  /** KPI名称 */
  name?: string;
  type?: DemandKpiTypeEnum;
  /** KPI描述 */
  description?: string;
  status?: CommonEnableStatusEnum;
  positive?: DemandKpiPositiveEnum;
  percent?: CommonYesNoEnum;
  show_for_out?: CommonYesNoEnum;
  long_or_short_term?: DemandKpiLongOrShortTermEnum;
};
/**
 * 指标要求
 */
export type TrumpetAssessmentIndicatorRequirementDto = {
  /** 序号 */
  index_id?: number;
  /** 指标ID */
  kpi_code: string;
  kpi_schema: DemandKpiSchemaData;
  /** 达标要求 */
  expect_value?: string;
};
export type SplitTrumpetDemandForm = {
  /** 标题 */
  title: string;
  /** 活动详情 */
  activity_detail?: string;
  /** 二级需求分类 */
  l2_category_id?: number;
  /** 三级需求分类 */
  l3_category_id?: number;
  /** 四级需求分类 */
  l4_category_id?: number;
  /** 预算 */
  budget?: number;
  /** 策划ids数组 */
  planner_ids?: number[];
  /** 运营ids数组 */
  operator_ids?: number[];
  /** 活动开始日期 */
  activity_start_at?: string;
  /** 活动结束日期 */
  activity_end_at?: string;
  /** 附件ids数组 */
  activity_execution_attachment_ids?: number[];
  /** 指标要求 */
  assessment_indicator_requirements?: TrumpetAssessmentIndicatorRequirementDto[];
  user?: User;
};
export type SplitTrumpetDemandVo = {
  id?: number;
};
export type ResponseSplitTrumpetDemandVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SplitTrumpetDemandVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 策划编辑一级单请求（小喇叭，首期仅 title 生效）
 */
export type PlannerEditTrumpetDemandForm = {
  /** 需求标题 */
  title?: string;
};
export type EditTrumpetDemandForm = {
  /** 标题 */
  title: string;
  /** 背景 */
  background?: string;
  /** 期望 */
  expectation?: string;
  /** 一级需求分类 */
  l1_category_id?: number;
  /** 二级需求分类 */
  l2_category_id?: number;
  /** 所属产品 */
  product_id?: number;
  /** 关联项目 */
  project_code?: string;
  priority?: PriorityEnum;
  /** 预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  /** 运营服务类预算 */
  budget_operation?: number;
  /** 运营服务类预算-备注 */
  budget_operation_desc?: string;
  /** 活动激励类预算 */
  budget_activity?: number;
  /** 活动激励类预算-备注 */
  budget_activity_desc?: string;
  /** 需求人员ids数组 */
  principal_ids: number[];
  /** 策划ids数组 */
  planner_ids?: number[];
  /** 运营ids数组 */
  operator_ids: number[];
  /** 执行预期开始日期 */
  estimated_execution_start_at?: string;
  /** 执行预期结束日期 */
  estimated_execution_end_at?: string;
  /** 附件ids数组 */
  introduce_attachs_blob_ids?: number[];
  /** 状态枚举 */
  state?: string;
};
export type EditTrumpetDemandPlannerForm = {
  /** 策划ids数组 */
  planner_ids?: number[];
};
/**
 * 小喇叭状态变更结果
 */
export type TrumpetMoveVo = {
  id?: number;
  before_state?: TrumpetState;
  current_state?: TrumpetState;
  /** 源状态名称 */
  before_state_name?: string;
  /** 变化后状态名称 */
  current_state_name?: string;
};
export type ResponseTrumpetMoveVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TrumpetMoveVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type TrumpetApprovalBizFormType =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10;
/**
 * 审批时提交的业务表单
 */
export type TrumpetApprovalBizForm = {
  type?: TrumpetApprovalBizFormType;
};
export type CloseForm = TrumpetApprovalBizForm & {
  drop_reason?: string;
  drop_reason_option?: string;
};
export type TrumpetApproveSettleForm = TrumpetApprovalBizForm;
export type TrumpetConfirmVerificationAuditForm = TrumpetApprovalBizForm & {
  /** 实际发放金额(含税) */
  actual_release_amount?: number;
  actual_release_currency?: CurrencyEnum;
  /** OA报销流程编号 */
  oa_flow_id?: string;
  /** OA流程附件 */
  oa_attachments?: number[];
};
export type TrumpetMarketInternalAuditForm = TrumpetApprovalBizForm;
export type TrumpetMarketingApproveSettleForm = TrumpetApprovalBizForm;
export type TrumpetOperationsAuditForm = TrumpetApprovalBizForm;
export type TrumpetPlanDesignAuditForm = TrumpetApprovalBizForm & {
  /** 方案附件blobId */
  plan_attachment_blob_ids?: number[];
  /** 使用方案 */
  usage_plan?: string;
};
/**
 * 指标填写记录
 */
export type TrumpetAssessmentIndicatorRecordDto = {
  /** 序号，前端不需要上传 */
  index_id?: number;
  /** 指标ID */
  kpi_code: string;
  kpi_schema?: DemandKpiSchemaData;
  /** 填写值 */
  actual_value?: string;
  /** 期望值，前端不需要上传 */
  expect_value?: string;
  /** 是否通过 */
  result: boolean;
  updated_by?: number;
};
export type TrumpetPostCaseBizForm = TrumpetApprovalBizForm & {
  /** 效果描述 */
  effect_description?: string;
  /** 预计发放金额(含税) */
  estimated_release_amount?: number;
  estimated_release_currency?: CurrencyEnum;
  /** 结案报告附件 */
  report_attachments?: number[];
  /** 账单明细附件 */
  billing_attachments?: number[];
  /** 指标填写记录 */
  assessment_indicator_records?: TrumpetAssessmentIndicatorRecordDto[];
};
export type TrumpetQcSettleForm = TrumpetApprovalBizForm & {
  has_risk?: number;
};
export type TrumpetSecondaryApproveSettleForm = TrumpetApprovalBizForm;
export type TrumpetStrategyResearchAuditForm = TrumpetApprovalBizForm;
export type TrumpetSumbitOaForm = TrumpetApprovalBizForm;
export type TrumpetTestApprovalBizForm = TrumpetApprovalBizForm & {
  val?: string;
};
export type TrumpetApprovalForm = {
  /** 备注 */
  note?: string;
  /** 审批时的状态 */
  state: string;
  biz_form:
    | CloseForm
    | TrumpetApproveSettleForm
    | TrumpetConfirmVerificationAuditForm
    | TrumpetMarketInternalAuditForm
    | TrumpetMarketingApproveSettleForm
    | TrumpetOperationsAuditForm
    | TrumpetPlanDesignAuditForm
    | TrumpetPostCaseBizForm
    | TrumpetQcSettleForm
    | TrumpetSecondaryApproveSettleForm
    | TrumpetStrategyResearchAuditForm
    | TrumpetSumbitOaForm
    | TrumpetTestApprovalBizForm;
  user?: User;
};
export type AddBudgetForm = {
  /** 预算（追加后总预算） */
  budget?: number;
  /** 追加原因 */
  reason: string;
  /** 追加后需求预期 */
  new_expectation: string;
};
export type AddBudgetResultVo = {
  /** demand_oa 记录主键 */
  demand_oa_id?: number;
  /** OA 流程号 */
  oa_id?: string;
};
export type ResponseAddBudgetResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: AddBudgetResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type CreateTrumpetDemandForm = {
  /** 标题 */
  title: string;
  /** 背景 */
  background?: string;
  /** 期望 */
  expectation?: string;
  /** 一级需求分类 */
  l1_category_id?: number;
  /** 二级需求分类 */
  l2_category_id?: number;
  /** 所属产品 */
  product_id?: number;
  /** 关联项目 */
  project_code?: string;
  priority?: PriorityEnum;
  /** 预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  /** 运营服务类预算 */
  budget_operation?: number;
  /** 运营服务类预算-备注 */
  budget_operation_desc?: string;
  /** 活动激励类预算 */
  budget_activity?: number;
  /** 活动激励类预算-备注 */
  budget_activity_desc?: string;
  /** 需求人员ids数组 */
  principal_ids: number[];
  /** 策划ids数组 */
  planner_ids?: number[];
  /** 运营ids数组 */
  operator_ids: number[];
  /** 执行预期开始日期 */
  estimated_execution_start_at?: string;
  /** 执行预期结束日期 */
  estimated_execution_end_at?: string;
  /** 附件ids数组 */
  introduce_attachs_blob_ids?: number[];
  group_type?: DemandGroupType;
  user?: User;
  /** 状态枚举newly_built/demand_principal_approval */
  state?: string;
};
export type CreateTrumpetDemandVo = {
  id?: number;
};
export type ResponseCreateTrumpetDemandVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: CreateTrumpetDemandVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DeleteCaseForm = {
  /** 废弃原因 */
  drop_reason?: string;
  /** 废弃原因分类 */
  drop_reason_option?: string;
};
export type EditActivityBudgetForm = {
  /** 预算 */
  budget?: number;
};
export type EditTrumpetActivityForm = {
  /** 活动详情 */
  activity_detail?: string;
  /** 策划ids数组 */
  planner_ids?: number[];
  /** 运营ids数组 */
  operator_ids: number[];
  /** 活动开始日期 */
  activity_start_at?: string;
  /** 活动结束日期 */
  activity_end_at?: string;
  /** 附件ids数组 */
  activity_execution_attachment_ids?: number[];
};
export type DeleteActivityForm = {
  /** 废弃原因 */
  drop_reason?: string;
  /** 废弃原因分类 */
  drop_reason_option?: string;
};
/**
 * OA审批概述
 */
export type OaProcessDescVo = {
  process_type?: ApproveTypeEnum;
  /** 关联需求单ID */
  demand_id?: number;
  /** OaId */
  oa_id?: string;
  /** oa流程地址 */
  oa_url?: string;
  /** 提交人Email */
  submit_user_email?: string;
  /** 提交人 */
  submit_user_name?: string;
  /** 提交时间 */
  submit_time?: string;
  oa_status?: OaState;
  /** oa拒绝原因 */
  oa_reject_reason?: string;
  /** 方案综合评分法对应的2级单号 */
  multi_quota_plan_demand_id?: number;
  /** 超额审批金额，使用的需求单的原币种 */
  over_amount_settle_price?: number;
  /** 超额审批原因 */
  over_amount_reason?: string;
  /** 超出的金额，使用人民币 */
  over_amount_cny?: number;
  /** 项目总预算金额 */
  approval_total_budget_amount?: number;
  approval_total_budget_currency?: CurrencyEnum;
  /** 合并审批单号 */
  merged_oa_process_number?: string[];
  /** 合并审批单号 */
  bidding_ids?: string[];
};
export type ResponseListOaProcessDescVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OaProcessDescVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 审批详细节点信息
 */
export type OaAuditNodes = {
  /** 审批人 */
  audit_user?: string;
  /** 审批人列表 */
  audit_users?: UserSimpleVo[];
  /** 接收人 */
  receive_user?: string;
  /** 接收人列表 */
  receive_users?: UserSimpleVo[];
  /** 操作 */
  op?: string;
  /** 操作类型 */
  op_type?:
    | 'START'
    | 'AUDITING'
    | 'APPROVED'
    | 'RETURN'
    | 'PRE_ADD_SIGN'
    | 'POST_ADD_SIGN'
    | 'REASSIGN'
    | 'SYSTEM_REASSIGN'
    | 'AUTO_COMPLETED'
    | 'SKIP'
    | 'FORCE_REVOKE_USER_TASK'
    | 'TERMINATE'
    | 'CANCEL'
    | 'ABORT'
    | 'COPYFOR'
    | 'COPYFOR_CONFIRM'
    | 'END';
  /** 审批时间 */
  audit_time?: string;
  /** 审批建议 */
  suggest?: string;
};
/**
 * 审批过程
 */
export type OaProcessNode = {
  /** 审批节点名 */
  node_name?: string;
  /** 审批过程明细 */
  oa_audit_nodes?: OaAuditNodes[];
};
/**
 * 审批过程记录
 */
export type OaProcessDetailVo = {
  process_type?: ApproveTypeEnum;
  /** 关联需求单ID */
  demand_id?: number;
  /** OaId */
  oa_id?: string;
  /** oa流程地址 */
  oa_url?: string;
  /** 提交人Email */
  submit_user_email?: string;
  /** 提交人 */
  submit_user_name?: string;
  /** 提交时间 */
  submit_time?: string;
  oa_status?: OaState;
  /** oa拒绝原因 */
  oa_reject_reason?: string;
  /** 方案综合评分法对应的2级单号 */
  multi_quota_plan_demand_id?: number;
  /** 超额审批金额，使用的需求单的原币种 */
  over_amount_settle_price?: number;
  /** 超额审批原因 */
  over_amount_reason?: string;
  /** 超出的金额，使用人民币 */
  over_amount_cny?: number;
  /** 项目总预算金额 */
  approval_total_budget_amount?: number;
  approval_total_budget_currency?: CurrencyEnum;
  /** 合并审批单号 */
  merged_oa_process_number?: string[];
  /** 合并审批单号 */
  bidding_ids?: string[];
  /** 本地id */
  local_biz_id?: string;
  /** 项目总预算 */
  budget?: string;
  /** 合并审批单号 */
  oa_ids?: string[];
  /** 审批记录过程数据 */
  oa_process_node?: OaProcessNode[];
  /** mpc单号 */
  mpc_no?: string;
};
export type ResponseOaProcessDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OaProcessDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type MpcDemandTypeEnum =
  | 'MPC_DEMAND'
  | 'TRUMPET_DEMAND'
  | 'BIDDING_DEMAND'
  | 'CLOSED_LOOP_DEMAND'
  | 'PERMISSION_APPLICATION'
  | 'MPC_DEMAND_KPI_ADJUST'
  | 'PREPAYMENT'
  | 'BUDGET_ADJUST';
export type ShowRiskTipsEnum = 'SHOW' | 'HIDDEN';
export type JournalOpTypeEnum =
  | 'SYSTEM_AUTO'
  | 'CREATE'
  | 'DESTROY'
  | 'EDIT'
  | 'SUBMIT'
  | 'PASS'
  | 'RETURN'
  | 'DELETE'
  | 'SAVE'
  | 'CLOSE'
  | 'FORCE_RETURN'
  | 'FORCE_PASS'
  | 'TIMEOUT'
  | 'FINISH'
  | 'UPDATE'
  | 'EDIT_BUDGET'
  | 'UPDATE_BUDGET'
  | 'OA_SUBMIT'
  | 'OA_REJECT'
  | 'OA_ARCHIVED'
  | 'DEMAND_NEWLY_BUILT_SAVE'
  | 'DEMAND_SUBMIT_TO_PENDING_DESIGN'
  | 'DEMAND_SPLIT_TO_PLAN'
  | 'DEMAND_PMO_EDIT'
  | 'DEMAND_COOPERATE_ACCOUNT_EDIT'
  | 'DEMAND_PLAN_USER_EDIT_LEAVES'
  | 'DEMAND_PRE_APPROVE_SUBMIT'
  | 'DEMAND_PRE_APPROVE_ARCHIVED'
  | 'DEMAND_SUPPLIER_REQUOTE'
  | 'DEMAND_FEE_APPROVAL_SUBMIT'
  | 'DEMAND_PLAN_APPROVAL_SUBMIT'
  | 'DEMAND_FEE_APPROVAL_ARCHIVED'
  | 'DEMAND_REIMBURSEMENT_SUBMIT'
  | 'DEMAND_REIMBURSEMENT_RESUBMIT_REJECTED'
  | 'DEMAND_REIMBURSEMENT_ARCHIVED'
  | 'DEMAND_MODIFY_CONTRACT_NO'
  | 'DEMAND_PAYMENT_SUBMIT'
  | 'DEMAND_PAYMENT_DELETED'
  | 'DEMAND_PAYMENT_ARCHIVED'
  | 'DEMAND_UPLOAD_INVOICE'
  | 'DEMAND_SUBMIT_OA_REJECTED'
  | 'DEMAND_SUPPLIER_REJECT'
  | 'DEMAND_START_QUOTE_SCORE'
  | 'DEMAND_START_QUOTE_SCORE_AUTO'
  | 'DEMAND_PLANNER_QUOTE_SCORE'
  | 'DEMAND_BUSINESS_QUOTE_SCORE'
  | 'DEMAND_DEMANDER_QUOTE_SCORE'
  | 'DEMAND_EDIT_QUOTE_SCORE_WEIGHT'
  | 'DEMAND_OVER_AMOUNT_APPROVE_SUBMIT'
  | 'DEMAND_EDIT_SETTLE_AMOUNT'
  | 'DEMAND_OVER_AMOUNT_APPROVE_PASS'
  | 'DEMAND_OVER_AMOUNT_APPROVE_REJECT'
  | 'DEMAND_KPI_ADJUST_SUBMIT'
  | 'DEMAND_KPI_ADJUST_ARCHIVED'
  | 'DEMAND_KPI_ADJUST_CHANGED'
  | 'DEMAND_CREATE_MONTH_RELEASE'
  | 'TRUMPET_SPLIT_DEMAND'
  | 'TRUMPET_ADD_BUDGET'
  | 'TRUMPET_ADD_BUDGET_SUBMIT'
  | 'TRUMPET_ADD_BUDGET_PASS'
  | 'TRUMPET_ADD_BUDGET_REJECT'
  | 'TRUMPET_CLOSE'
  | 'TRUMPET_EDIT_PLANNER'
  | 'TRUMPET_PLAN_USER_EDIT_L1'
  | 'CLOSED_LOOP_SPLIT_DEMAND'
  | 'CLOSED_LOOP_CLOSE'
  | 'CLOSED_LOOP_SUBMIT_PAYMENT'
  | 'CLOSED_LOOP_CASE_APPROVE_SETTLE_PASS'
  | 'CLOSED_LOOP_CASE_APPROVE_SETTLE_REJECT'
  | 'BIDDING_ADD_SUPPLIER'
  | 'BIDDING_OPEN_REVIEW'
  | 'BIDDING_OPEN_REVIEW_DEFER'
  | 'BIDDING_OPEN_REVIEW_TECHNICAL'
  | 'BIDDING_OPEN_REVIEW_COMMERCIAL'
  | 'BIDDING_MANUAL_SUBMIT_OA'
  | 'BIDDING_EVALUATION_RESULT_PASS'
  | 'BIDDING_EVALUATION_RESULT_RESTART'
  | 'BIDDING_EVALUATION_RESULT_LISTEN'
  | 'BIDDING_EVALUATION_RESULT_DEFER'
  | 'BIDDING_EVALUATION_RESULT_FAIL'
  | 'BIDDING_SAVE_QA_TIME'
  | 'BIDDING_UPDATE_QA_TIME'
  | 'BIDDING_SAVE_QA_ATTACH'
  | 'BIDDING_UPDATE_QA_ATTACH'
  | 'BIDDING_SKIP_TECH_SCORE'
  | 'BIDDING_SKIP_BUSINESS_SCORE'
  | 'DEMAND_KPI_ADJUSTMENT_SUBMIT'
  | 'DEMAND_KPI_ADJUSTMENT_PLANNER_PASS'
  | 'DEMAND_KPI_ADJUSTMENT_PLANNER_REJECT'
  | 'DEMAND_KPI_ADJUSTMENT_OA_REJECT'
  | 'DEMAND_KPI_ADJUSTMENT_OA_PASS'
  | 'DEMAND_KPI_ADJUSTMENT_PRINCIPAL_RECALL'
  | 'PREPAYMENT_CREATE'
  | 'PREPAYMENT_SUBMIT'
  | 'PREPAYMENT_RESUBMIT'
  | 'PREPAYMENT_WITHDRAW'
  | 'PREPAYMENT_OA_PASS'
  | 'PREPAYMENT_OA_REJECT'
  | 'PREPAYMENT_PAYMENT_COMPLETE'
  | 'PREPAYMENT_ABANDON'
  | 'PREPAYMENT_UPLOAD_INVOICE'
  | 'PREPAYMENT_REMOVE_INVOICE'
  | 'SUPPLIER_SUBMIT_REVIEW'
  | 'EXPORT_ALL_PROPOSALS'
  | 'BUDGET_ADJUST_SAVE'
  | 'BUDGET_ADJUST_SUBMIT'
  | 'BUDGET_ADJUST_OA_PASS'
  | 'BUDGET_ADJUST_OA_PASS_NOT_EFFECTIVE'
  | 'BUDGET_ADJUST_OA_REJECT'
  | 'BUDGET_ADJUST_ABANDON'
  | 'EDIT_AUXILIARY_KPI'
  | 'PERMISSION_APPLICATION_CREATE'
  | 'PERMISSION_APPLICATION_OA_SUBMIT'
  | 'PERMISSION_APPLICATION_OA_REJECT'
  | 'PERMISSION_APPLICATION_OA_PASS';
export type FlowEvents =
  | 'SUBMIT_DRAFT'
  | 'START_DESIGN'
  | 'DESIGN'
  | 'ASSESS'
  | 'QUOTE'
  | 'QUOTE_SCORE'
  | 'TODO'
  | 'AUDIT_QUOTE_BUSINESS'
  | 'AUDIT_QUOTE_BUSINESS_LEADER'
  | 'AUDIT_QUOTE_PLANNER'
  | 'AUDIT_QUOTE_MARKETING'
  | 'AUDIT_QUOTE_MARKETING_LEADER'
  | 'AUDIT_QUOTE_PLANNER_LEADER'
  | 'NONE'
  | 'AUDIT_BING_EXECUTION_DATA'
  | 'AUDIT_MAKING'
  | 'AUDIT_PLANNER_ACCEPT'
  | 'AUDIT_PLANNER_LEADER_ACCEPT'
  | 'AUDIT_BUSINESS_ACCEPT'
  | 'AUDIT_DEMANDER_ACCEPT'
  | 'AUDIT_APPROVE_SETTLE'
  | 'AUDIT_QC_ACCEPT'
  | 'AUDIT_SUBMIT_OA'
  | 'AUDIT_SUBMIT_REIMBURSE'
  | 'CLOSED'
  | 'PRE_SUBMIT_OA'
  | 'FEE_SUBMIT_OA'
  | 'START_BIDDING'
  | 'BREAK_UP'
  | 'OA_ROLLBACK'
  | 'FEE_ROLLBACK'
  | 'DISCARD';
export type DemandMoveDto = {
  id?: number;
  pass?: boolean;
  /** 操作的按钮，当前只在询比价评分中使用 */
  action_button?: string;
  abort?: boolean;
  discard?: boolean;
  drop_reason?: string;
  drop_reason_option?: string;
  note?: string;
  attachment_list?: number[];
  show_risk_tips?: ShowRiskTipsEnum;
  risk_tips?: string;
  state?: DemandState;
  from_oa_callback?: boolean;
  supplier_submit_closure?: boolean;
  planner_back?: boolean;
  journal_op_type?: JournalOpTypeEnum;
  /** 是否确认废弃含已完成预付单的三级单 */
  confirm_abandon_with_completed_prepayment?: boolean;
  /** 流转表单，每个状态会对应不用的表单 */
  form?: {
    [key: string]: any;
  };
  flow_pass?: boolean;
  state_next_event?: FlowEvents;
};
export type QuerySupplierQuoteDto = {
  /** 数易单号 */
  dspf_mpc_no?: string;
  /** 计提起始日期 */
  start_date?: string;
  /** 计提结束日期 */
  end_date?: string;
  /** 供应商报价金额 */
  fee?: number;
  /** 供应商报价币种 */
  currency?: string;
  /** demandId */
  demand_id?: number;
};
export type ResponseListQuerySupplierQuoteDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: QuerySupplierQuoteDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * AI审核结果明细项详情
 */
export type DemandAiReviewResultItemDetailDto = {
  /** 异常类型 */
  item_name?: string;
  /** 异常类型 */
  item_code?: string;
  /** 是否图片 */
  is_image?: boolean;
  /** 是否链接 */
  is_link?: boolean;
  /** 检测对象名称 */
  content?: string;
  /** 检测内容-文本 */
  text?: string;
  /** 检测内容-链接地址，可能是图片地址，文件地址等，也可能为空 */
  link_url?: string;
  /** 备注信息 */
  remark?: string;
  /** 检测结果 */
  error_message?: string;
  /** 重复单号 */
  repeat_number?: string;
  /** 相关内容 */
  related_content?: string;
};
/**
 * 分组的内容，只有文件类型有分组的内容
 */
export type DemandAiReviewResultItemGroupDto = {
  /** 检测内容 */
  content?: string;
  /** 详细内容，只有文件类型有详细内容 */
  details?: DemandAiReviewResultItemDetailDto[];
};
/**
 * AI审核结果明细项
 */
export type DemandAiReviewResultItemDto = {
  /** 审核项目标识，作为页面上可以定位的标记。需求详情中：finalSettlePrice(最终结算金额)、caseCloseReportCooperateAccountAttachments:{IndexId}(差旅附件)、quoteSettleReport(结案明细)、quoteSettleReport:{IndexId}(合作内容的结案明细)、settleReportAttachs(结案报告)；结案明细中：quoteSettleReport:{quoteKey}:attachments、quoteSettleReport:{quoteKey}:content */
  item_id?: string;
  /** 错误异常，可能为空 */
  error_message?: string;
  /** 分组的内容，只有文件类型有分组的内容 */
  groups?: DemandAiReviewResultItemGroupDto[];
};
/**
 * AI验收结论
 */
export type AcceptanceConclusionEnum =
  | 'PASS'
  | 'CONDITIONAL_PASS_LOW_RISK'
  | 'CONDITIONAL_PASS_MID_RISK'
  | 'REJECT';
/**
 * AI验收信息
 */
export type DemandAiAcceptInfoDto = {
  acceptance_conclusion?: AcceptanceConclusionEnum;
  /** 智能验收不通过次数 */
  ai_reject_count?: number;
  /** AI验收分析报告 */
  ai_analysis_report?: string;
  summary?: string;
  is_amount_consistent?: boolean;
  has_abnormal?: boolean;
};
/**
 * 需求AI审核结果
 */
export type DemandAiReviewResultDto = {
  /** AI审核结果明细项列表 */
  items?: DemandAiReviewResultItemDto[];
  /** AI审核总结 */
  review_summary?: string;
  /** 供应商备注 */
  supplier_remark?: string;
  ai_accept_info?: DemandAiAcceptInfoDto;
};
export type ResponseDemandAiReviewResultDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandAiReviewResultDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type FeeControlFirstDemandDeleteCmd = {
  record_id?: number;
  record_type?: MpcDemandTypeEnum;
};
export type NotifyTestDto = {
  id?: number;
  record_type: string;
};
export type SwiftTagCommand = {
  id: number;
  status: number;
  updated_by_id?: number;
};
export type ListTagCommand = {
  keyword?: string;
  status?: number;
  cat_ids?: number[];
  page_num: number;
  page_size: number;
};
/**
 * 数据
 */
export type SuppliersTagDto = {
  id?: number;
  name?: string;
  cat_id?: number;
  status?: number;
  latest_status_changed_at?: string;
  updated_at?: string;
  created_at?: string;
  human_cat_names?: string;
};
export type TagListPageVoSuppliersTagDto = {
  /** 总数 */
  total?: number;
  /** 数据 */
  records?: SuppliersTagDto[];
  can_add?: boolean;
  can_edit?: boolean;
  can_swift?: boolean;
  can_delete?: boolean;
};
export type ResponseTagListPageVoSuppliersTagDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TagListPageVoSuppliersTagDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type EditTagCommand = {
  id: number;
  name: string;
  cat_id: number;
  updated_by_id?: number;
};
export type CreateTagCommand = {
  name: string;
  cat_id: number;
  created_by_id?: number;
};
export type EditTagCatCommand = {
  id: number;
  parent_id?: number;
  name: string;
  updated_by_id?: number;
};
export type CreateTagCatCommand = {
  parent_id?: number;
  name: string;
  created_by_id?: number;
};
/**
 * 供应商使用限制配置分页查询表单
 */
export type SupplierUsageLimitConfigPageForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 单据类型筛选：1=常规采购，3=比选 */
  demand_type?: number;
  /** 需求接收部门筛选：0=渠道发行中心，1=市场中心 */
  group_type?: number;
  /** 状态筛选：1=启用，0=禁用 */
  status?: number;
};
/**
 * 配置明细项
 */
export type DetailVo = {
  /** 明细ID */
  id?: number;
  /** 序号 */
  seq?: number;
  /** 费用类型/比选需求类型ID列表；["all"]表示全部 */
  category_ids?: string[];
  /** 渠道类型列表；["all"]表示全部 */
  channel_types?: string[];
};
/**
 * 供应商使用限制配置列表项
 */
export type SupplierUsageLimitConfigVo = {
  /** 配置ID */
  id?: number;
  /** 配置名称 */
  name?: string;
  /** 单据类型：1=常规采购，3=比选 */
  demand_type?: number;
  /** 单据类型描述 */
  demand_type_desc?: string;
  /** 需求接收部门列表：0=渠道发行中心，1=市场中心 */
  group_types?: number[];
  /** 需求接收部门描述列表 */
  group_type_descs?: string[];
  /** 产品范围 */
  product_scope?: ProductScopeItem[];
  /** 状态：1=启用，0=禁用 */
  status?: number;
  /** 优先级，越小越高 */
  priority?: number;
  /** 配置明细列表 */
  details?: DetailVo[];
  /** 更新人姓名 */
  updated_by?: string;
  /** 更新时间 */
  updated_at?: string;
};
/**
 * 分页结果
 */
export type PageResultSupplierUsageLimitConfigVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: SupplierUsageLimitConfigVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultSupplierUsageLimitConfigVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultSupplierUsageLimitConfigVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type SupplierServiceTypeEnum =
  | 'SERVICE_ALL'
  | 'SERVICE_MCN'
  | 'SERVICE_OFFLINE'
  | 'MATERIAL_MAKING'
  | 'SERVICE_BROADCAST'
  | 'SERVICE_ANCHOR'
  | 'PUBLIC_RELATIONS_ALL'
  | 'PUBLIC_RELATIONS_ONLINE'
  | 'PUBLIC_RELATIONS_OFFLINE'
  | 'BRAND_LAUNCH'
  | 'EFFECT_LAUNCH'
  | 'COMPLETE_CREATIVE_PROGRAM'
  | 'VIDEO_PRODUCTION'
  | 'AUDIO_PRODUCTION'
  | 'PROMOTIONAL_ILLUSTRATION'
  | 'GRAPHIC_DESIGN'
  | 'PHOTOGRAPHY'
  | 'NETWORK_INTERACTION'
  | 'ARTISTIC_COOPERATION'
  | 'PURCHASE_AD_PRODUCTION'
  | 'ADVERTISEMENT_MAKING'
  | 'ADVERTISING_CREATIVITY'
  | 'CULTURE_AND_ENTERTAINMENT_PUBLICITY'
  | 'NEW_MEDIA_OPERATIONS'
  | 'INTEGRATED_PROMOTION'
  | 'LIVE_ACTION_VIDEO'
  | 'ANIMATION_ALL'
  | 'HIGHLIGHT_VIDEO'
  | 'VARIETY_SHOW_OR_MINISERIES'
  | 'MOTION_CAPTURE'
  | 'VIDEO_POST_PACKAGING'
  | 'VIRTUAL_IDOL'
  | 'MUSIC_SOUND_PRODUCTION'
  | 'VOICE_ACTORS_AND_DUBBING'
  | 'PROMOTION_ILLUSTRATION'
  | 'CONCEPT_SETTING'
  | 'MODEL_RETOUCHING_3D'
  | 'MODEL_MAKING_RENDERING_3D'
  | 'COMPOSITING'
  | 'POSTER_DESIGN'
  | 'VI_DESIGN'
  | 'FONT_DESIGN'
  | 'OFFLINE_EXPERIENCE_DESIGN'
  | 'DESIGN_PRODUCTION_OF_CULTURAL_AND_CREATIVE_DERIVATIVES'
  | 'MOVIE_POSTER'
  | 'GRAPHIC_PHOTOGRAPHER'
  | 'POST_RETOUCHING'
  | 'DRESSMAKER'
  | 'STYLIST'
  | 'GRAPHIC_PRODUCTION_COMPANY'
  | 'HTML5_PAGE'
  | 'CHANNEL_LAUNCH_DEMO'
  | 'CHANNEL_PAGE'
  | 'WEB_DESIGN_PRODUCTION'
  | 'APP_STORE_ICON_AND_DISPLAY_DIAGRAM'
  | 'DYNAMIC_ANIMATION_DEMO'
  | 'ADVERTISING_EXTENSION'
  | 'NEW_MEDIA_ART'
  | 'ARTIST'
  | 'DESIGNER'
  | 'PHOTOGRAPHER'
  | 'TRENDY_FASHION_INTEGRATOR'
  | 'PURCHASE_2D_ANIMATION_VIDEO'
  | 'INGAME_VIDEO_RECORDING'
  | 'UE4_ENGINE_VIDEO'
  | 'PURCHASE_PACKAGING_EDITING_VIDEO'
  | 'PURCHASE_REAL_SHOT_VIDEO'
  | 'MARKETING_ACTIVITIES'
  | 'PLAYER_ACTIVITIES'
  | 'MANUSCRIPT_WRITING'
  | 'WEBSITE_MAINTENANCE'
  | 'MEDIA_BUYING'
  | 'TRANSLATION_SERVICE'
  | 'WHOLE_CLASS'
  | 'ANDROID_PURCHASED_QUANTITY'
  | 'BUSINESS_KOL'
  | 'VISUAL_GRAPHIC_DESIGN'
  | 'BUY_MATERIAL_PRODUCTION'
  | 'COMMUNITY_BBS_OPERATION'
  | 'CONTENT_PRODUCTION_RELEASE'
  | 'COMMUNITY_GAME_OPERATIONS'
  | 'EVENT_VENUES_BUILDING_SERVICES'
  | 'DIRECT_BROADCAST_PRODUCTION'
  | 'THIRD_PARTY_RESOURCES'
  | 'GAMES_WHOLE'
  | 'DIRECT_BROADCAST_PRODUCTION_PARTLY';
export type SupplierAdvancePaymentEnum =
  | 'FIRST_AP'
  | 'SECOND_AP'
  | 'THIRD_AP'
  | 'FOURTH_AP'
  | 'OTHER_AP';
export type SupplierStatusEnum = 'ACTIVE' | 'PENDING' | 'LOCKED' | 'REJECTED';
export type TemporaryEnum = 'TRUE' | 'FALSE';
export type WhetherFormerEmployeesEnum = 'TRUE' | 'FALSE';
export type UseTypeEnum = 'COMMONLY_USED' | 'NOT_COMMONLY_USED' | 'STORE_USED';
export type SupplierTypeEnum =
  | 'COMPLEX_INLAND'
  | 'COMPLEX_OVERSEA'
  | 'COMPLEX_SPECIAL'
  | 'COMPLEX_GAME'
  | 'COMPLEX_OPERATION'
  | 'COMPLEX_MATERIAL'
  | 'COMPLEX_OTHER'
  | 'COMPLEX_SALE'
  | 'SOCIAL_MEDIA_SALE'
  | 'MEDIA_AD'
  | 'ESPORTS_EVENT'
  | 'FLOW_INLAND'
  | 'FLOW_WEB_SUPPORT'
  | 'FLOW_OPERATION'
  | 'BRAND_COMPANY'
  | 'BRAND_PERSON'
  | 'OVERSEA_ACTIVITIES'
  | 'OVERSEA_UA_MATERIAL'
  | 'OVERSEA_LOCALIZED'
  | 'OVERSEA_WEBSITE'
  | 'OVERSEA_UA_LAUNCH';
export type RoleStatusEnum = 'ACTIVE' | 'LOCKED';
export type UserBelongableTypeEnum = 'GROUP' | 'PRODUCT';
export type SFunctionBaseEntityLongObject = object;
export type UserBelongs = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  user_id?: number;
  belongable_id?: number;
  belongable_type?: UserBelongableTypeEnum;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type Group = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  name?: string;
  status?: RoleStatusEnum;
  group_type?: number;
  user_belongs?: UserBelongs[];
  name_for_option?: string;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
/**
 * SupplierServiceInfo对象
 */
export type SupplierServiceInfo = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  /** customer_name */
  customer_name?: string;
  /** case_digest */
  case_digest?: string;
  /** netease_case_digest */
  netease_case_digest?: string;
  /** total_employee */
  total_employee?: string;
  /** supplier_id */
  supplier_id?: number;
  /** recommend_group_id */
  recommend_group_id?: string;
  /** recommend_user_id */
  recommend_user_id?: number;
  /** recommend_reason */
  recommend_reason?: string;
  supplier?: Supplier;
  referrer?: User;
  recommend_group?: Group;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
/**
 * invoice_type
 */
export type BankInfoInvoiceTypeEnum =
  | 'SMALL_SCALE_TAX'
  | 'GENERAL_TAX'
  | 'OTHER_TAX'
  | 'NON_TAX';
/**
 * BankInfo对象
 */
export type BankInfo = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  /** bank_name */
  bank_name?: string;
  /** bank_number */
  bank_number?: string;
  /** supplier_id */
  supplier_id?: number;
  invoice_type?: BankInfoInvoiceTypeEnum;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type AuditUser = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  user_id?: number;
  audit_at?: string;
  remark?: string;
  supplier_id?: number;
  user?: User;
  supplier?: Supplier;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type SupplierAudit = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  supplier_id?: number;
  change_detail?: string;
  lock_version?: number;
  supplier?: Supplier;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type SupplierAttacheEnum = 'ACTIVE' | 'ADDED' | 'REMOVED';
/**
 * 需求相关附件
 */
export type Attachment = {
  id?: number;
  destroy?: boolean;
  name?: string;
  record_id?: number;
  record_type?: string;
  blob_id?: number;
  attachment_blob?: AttachmentBlob;
  created_at?: string;
  oa_file_id?: string;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type AttachmentBlob = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  key?: string;
  filename?: string;
  original_filename?: string;
  content_type?: string;
  metadata?: string;
  byte_size?: number;
  checksum?: string;
  supplier_attache?: SupplierAttache;
  attachments?: Attachment[];
  created_by?: number;
  expire_time?: string;
  data?: string;
  remark?: string;
  auto_generated?: boolean;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type SupplierAttache = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  name?: string;
  status?: SupplierAttacheEnum;
  blob_id?: number;
  supplier_id?: number;
  attachment_blob?: AttachmentBlob;
  attachment?: Attachment;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type ContactTypeEnum = 'LEADER' | 'INTERFACE';
export type SupplierContact = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  name?: string;
  job_title?: string;
  email?: string;
  telephone?: string;
  supplier_id?: number;
  supplier_id_and_type?: string;
  contact_type?: ContactTypeEnum;
  supplier?: Supplier;
  translated_supplier_type?: string;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type SupplierEmployee = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  status?: SupplierEmployeeEnum;
  supplier_id?: number;
  user_id?: number;
  supplier?: Supplier;
  referrer?: User;
  is_deleted?: boolean;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type SupplierSuperiorResource = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  name?: string;
  resource_category?: number;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type SupplierResource = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  supplier_id?: number;
  superior_resource_id?: number;
  name?: string;
  superior_resource?: SupplierSuperiorResource;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type SupplierCategoryEnum = 'PR' | 'MM' | 'BC' | 'OC' | 'FD' | 'ES';
export type AuditItem = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  parent_cate?: string;
  cate?: string;
  position?: number;
  info?: string;
  category?: SupplierCategoryEnum;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type JsonConfig = {
  key_comparator?: object;
  ignore_error?: boolean;
  ignore_case?: boolean;
  date_format?: string;
  ignore_null_value?: boolean;
  transient_support?: boolean;
  strip_trailing_zeros?: boolean;
  check_duplicate?: boolean;
  write_long_as_string?: boolean;
  order?: boolean;
};
export type JsonObject = {
  raw?: {
    [key: string]: any;
  };
  config?: JsonConfig;
  empty?: boolean;
  [key: string]: any;
};
export type Dict = {
  empty?: boolean;
  [key: string]: any;
};
export type Supplier = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  /** 源供应商表id */
  source_id?: number;
  /** 源供应商类型 */
  source_type?: string;
  full_name?: string;
  short_name?: string;
  lock_version?: number;
  service_type?: SupplierServiceTypeEnum;
  advance_payment?: SupplierAdvancePaymentEnum;
  address?: string;
  parent_company?: string;
  status?: SupplierStatusEnum;
  mpc_no?: string;
  region?: string;
  service_content?: string;
  currency?: CurrencyEnum;
  temporary?: TemporaryEnum;
  whether_former_employees?: WhetherFormerEmployeesEnum;
  use_type?: UseTypeEnum;
  enterprise_risk_query?: string;
  contract_begin_at?: string;
  contract_end_at?: string;
  discarded_at?: string;
  supplier_type?: SupplierTypeEnum;
  service_info?: SupplierServiceInfo;
  bank_info?: BankInfo;
  audit_user?: AuditUser;
  supplier_audit?: SupplierAudit;
  supplier_attaches?: SupplierAttache[];
  supplier_contacts?: SupplierContact[];
  supplier_employees?: SupplierEmployee[];
  supplier_resources?: SupplierResource[];
  audit_items?: AuditItem[];
  /** 使用类型 */
  attachs_ids?: number[];
  translated_advance_payment?: string;
  translated_currency?: string;
  translated_service_type?: string;
  translated_status?: string;
  translated_supplier_type?: string;
  translated_use_type?: string;
  dirty_attrs?: JsonObject;
  resources?: string;
  total_employee?: string;
  interface_name?: string;
  interface_job_title?: string;
  interface_email?: string;
  options?: Dict;
  permit_buttons?: Dict;
  skip_section_validation?: boolean;
  can_view_sensitive_info?: boolean;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
/**
 * 提交调研反馈命令
 */
export type SubmitFeedbackCmd = {
  /** 二级需求单 ID */
  demand_id: number;
  /** 需求单类型，1:常规需求单, 3:比选单 */
  demand_type?: number;
  /** 推荐批次号（triggerBatch） */
  trigger_batch: string;
  /** 推荐会话 ID（针对具体规则反馈时传入，整批反馈时可为空） */
  session_id?: string;
  /** 反馈标签列表（可多选） */
  labels?: string[];
  /** 调研反馈文本 */
  text?: string;
};
export type SupplierListTypeEnum = 'ALL_LIST' | 'AUDIT_LIST';
export type SupplierManageListForm = {
  current_page?: number;
  page_size?: number;
  order_field?: string;
  order_direction?: string;
  search_text?: string;
  category?: string[];
  status?: string[];
  region?: string[];
  service_type?: string[];
  supplier_type?: string[];
  advance_payment?: SupplierAdvancePaymentEnum;
  superior_resources?: string[];
  sort?: string;
  type?: SupplierListTypeEnum;
  show_permit_keys?: string[];
  service_type_value?: number[];
  status_value?: number[];
  supplier_type_value?: number[];
  category_value?: number[];
  advance_payment_value?: number[];
  audit_btn?: boolean;
  edit_btn?: boolean;
  permit_filter?: object;
};
export type SupplierExportReq = {
  search_text?: string;
  status?: SupplierStatusEnum[];
  region?: string[];
  supplier_type?: SupplierTypeEnum[];
  advance_payment?: SupplierAdvancePaymentEnum;
  superior_resources?: number[];
  type: SupplierListTypeEnum;
};
export type ResponseSupplier = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: Supplier;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type SupplieAuditDto = {
  id?: number;
  pass?: boolean;
  lock_version?: number;
};
/**
 * 供应商主体
 */
export type CreateMainSupplierForm = {
  mdm_type?: SupplierVendorType;
  area_type?: SupplierAreaType;
  profit_type?: SupplierProfitType;
  /** 名称 */
  name: string;
  /** 简称名称 */
  abbr_name: string;
  /** 地址 */
  address?: string;
  /** 公司人数 */
  staff_amount: number;
  /** 是否前员工创建 */
  former_employee_founded: boolean;
  /** 附件 */
  attachments?: SaveStorageObjectForm[];
};
export type CreateSupplierForm = {
  main_supplier?: CreateMainSupplierForm;
  /** 供应商主体ID */
  main_supplier_id?: number;
  channel_type: ChannelType;
  content: ChannelSupplierServiceInfoForm;
  bank_info?: ChannelSupplierBankInfoForm;
  contract_contact_info?: ChannelSupplierContractContactInfoForm;
  /** 对接人 */
  contacts?: ChannelSupplierRelatedPersonForm[];
  /** 负责人 */
  principals?: ChannelSupplierRelatedPersonForm[];
  /** 是否需要提交 */
  need_submit?: boolean;
  /** 是否为KOL合作MCN */
  kol_mcn?: boolean;
  /** 账号信息 */
  account_list?: Account[];
};
export type QueryChannelSupplierTotalAmountForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 渠道供应商ID，不传则查询全部 */
  channel_supplier_id?: number;
  /** 开始时间 */
  start_time?: string;
  /** 结束时间 */
  end_time?: string;
};
export type ChannelSupplierTotalAmountRecordVo = {
  /** 总金额（原币） */
  total_amount?: number;
  currency?: CurrencyEnum;
  /** 总金额（人民币） */
  total_amount_cny?: number;
  /** 时间，格式是 yyyy 或者 yyyy-MM */
  time?: string;
};
export type PageVoChannelSupplierTotalAmountRecordVo = {
  total?: number;
  records?: ChannelSupplierTotalAmountRecordVo[];
};
export type ResponsePageVoChannelSupplierTotalAmountRecordVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoChannelSupplierTotalAmountRecordVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryCsDemandForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 服务类型 */
  channel_types?: ChannelType[];
  /** 关联主体（主供应商ID） */
  relate_main_supplier_ids?: number[];
  /** 关联主体服务类型 */
  relate_channel_types?: ChannelType[];
  /** 需求单号 */
  demand_id?: number;
  /** 需求类型一级 */
  l1_category_ids?: number[];
  /** 【供应商综合评分】所属产品 */
  product_ids?: number[];
  /** 搜索的创建时间区间 */
  create_date_range?: string[];
  /** 搜索的执行时间区间 */
  publish_data_range?: string[];
  /** 搜索的预计开始时间区间 */
  expect_start_date_range?: string[];
  /** 搜索的预计结束时间区间 */
  expect_end_date_range?: string[];
  /** 状态 */
  states?: DemandState[];
  /** 采购方式 */
  purchase_methods?: PurchasingMethodEnum[];
  /** 【供应商详情页优化2.0&供应商列表优化】采购方式和策略 */
  purchasing_method_and_strategy_params?: PurchasingMethodAndStrategyParam[];
};
/**
 * 结果打分明细
 */
export type EvaluateItemDescVo = {
  id?: number;
  item_id?: number;
  /** 打分项名称 */
  item_name?: string;
  /** 打分 */
  score?: number;
  /** 分值权重,百分比 */
  weight?: string;
  /** 打分值 */
  real_score?: number;
};
export type ChannelSupplierDemandVo = {
  /** 需求单号 */
  id?: number;
  /** 产品编码 */
  product_code?: string;
  /** 产品名称 */
  product_name?: string;
  /** 需求标题 */
  name?: string;
  /** 需求类型一级 */
  l1_category_id?: number;
  /** 需求类型 */
  l1_category_name?: string;
  /** 报价总金额 */
  quote_price?: number;
  quote_currency?: CurrencyEnum;
  /** 最终结算金额 */
  settle_price?: number;
  settle_currency?: CurrencyEnum;
  /** 需求执行日期 */
  publish_date?: string;
  /** 需求执行预计开始日期 */
  expect_begin_date?: string;
  /** 需求执行预计结束日期 */
  expect_end_date?: string;
  state?: DemandState;
  /** 过程打分 */
  project_processing_score?: string;
  /** 过程打分明细 */
  project_processing_score_detail_list?: EvaluateItemDescVo[];
  /** 过程打分时间 */
  project_processing_score_rating_time?: string;
  /** 结果打分 */
  project_result_score?: string;
  /** 结果打分明细 */
  project_result_score_detail_list?: EvaluateItemDescVo[];
  /** 结果打分时间 */
  project_result_score_rating_time?: string;
  /** 需求单创建日期 */
  create_time?: string;
  /** 【供应商评分应用】综合评分 */
  overall_score?: string;
  /** 服务类型名称 */
  channel_type_name?: string;
  /** 供应商名称 */
  supplier_name?: string;
  /** 采购方式&采购策略 */
  purchase_method_and_strategy?: string;
  /** 需求单状态名称 */
  state_name?: string;
};
/**
 * 合作总金额
 */
export type ChannelSupplierTotalAmountVo = {
  /** 总金额（原币） */
  total_amount?: number;
  currency?: CurrencyEnum;
  /** 总金额（人民币） */
  total_amount_cny?: number;
};
/**
 * 平均综合评分
 */
export type AvgOverallScoreVo = {
  /** 平均综合评分 */
  score?: number;
  /** 平均过程评分 */
  processing_score?: number;
  /** 平均结果评分 */
  result_score?: number;
};
/**
 * 需求单数量
 */
export type DemandCountVo = {
  /** 总数 */
  total?: number;
  /** 进行中数量 */
  running_count?: number;
  /** 已结束数量 */
  finished_count?: number;
  /** 已废弃数量 */
  abandon_count?: number;
};
/**
 * 【供应商详情页优化2.0&供应商列表优化】寻源统计
 */
export type SourcingCountVo = {
  /** 发需次数 */
  required_count?: number;
  /** 中选次数 */
  win_count?: number;
  /** 报价次数 */
  quote_count?: number;
};
/**
 * 产品需求信息
 */
export type ProductDemandVo = {
  /** 产品ID */
  product_id?: number;
  /** 产品编码 */
  product_identifier?: string;
  /** 产品名称 */
  product_name?: string;
  /** 需求数量 */
  demand_count?: number;
};
export type ChannelSupplierQueryTotalResultVo = {
  quoted?: ChannelSupplierTotalAmountVo;
  final_settle?: ChannelSupplierTotalAmountVo;
  cooperate_total_amount?: ChannelSupplierTotalAmountVo;
  avg_overall_score?: AvgOverallScoreVo;
  demand_count?: DemandCountVo;
  sourcing_count?: SourcingCountVo;
  /** 产品需求信息 */
  product_demand_list?: ProductDemandVo[];
};
export type PageWithExtInfoVoChannelSupplierDemandVoChannelSupplierQueryTotalResultVo =
  {
    total?: number;
    records?: ChannelSupplierDemandVo[];
    ext_info?: ChannelSupplierQueryTotalResultVo;
  };
export type ResponsePageWithExtInfoVoChannelSupplierDemandVoChannelSupplierQueryTotalResultVo =
  {
    code?: number;
    sub_code?: string;
    message?: string;
    message_detail?: object;
    data?: PageWithExtInfoVoChannelSupplierDemandVoChannelSupplierQueryTotalResultVo;
    stacktrace?: string[];
    time?: number;
    trace_id?: string;
  };
/**
 * 渠道供应商信息
 */
export type ChannelSupplierInfo = {
  /** 渠道类型名称 */
  channel_type_name?: string;
  channel_type?: ChannelType;
  /** 渠道供应商id */
  channel_supplier_id?: number;
};
export type ChannelSupplierDemandSummaryVo = {
  /** 近期综合评分 */
  recent_overall_score?: string;
  /** 近期过程打分 */
  recent_processing_score?: string;
  /** 近期结果打分 */
  recent_result_score?: string;
  /** 历史综合评分 */
  all_overall_score?: string;
  /** 历史过程打分 */
  all_processing_score?: string;
  /** 历史结果打分 */
  all_result_score?: string;
  /** 产品需求信息 */
  product_demand_list?: ProductDemandVo[];
  /** 月度总金额 */
  monthly_total_amount?: number;
  monthly_total_amount_currency?: CurrencyEnum;
  /** 月度总金额（人民币） */
  monthly_total_amount_cny?: number;
  /** 年度总金额 */
  yearly_total_amount?: number;
  yearly_total_amount_currency?: CurrencyEnum;
  /** 年度总金额（人民币） */
  yearly_total_amount_cny?: number;
  /** 合作总金额 */
  cooperate_total_amount?: number;
  /** 合作总金额（人民币） */
  cooperate_total_amount_cny?: number;
  cooperate_total_amount_currency?: CurrencyEnum;
  /** 中选次数 */
  select_count?: number;
  /** 发需次数 */
  ask_quote_count?: number;
  /** 参与报价次数 */
  quote_count?: number;
  /** 具有查询权限的渠道供应商列表 */
  channel_supplier_list?: ChannelSupplierInfo[];
};
export type ResponseChannelSupplierDemandSummaryVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ChannelSupplierDemandSummaryVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryCsBiddingForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 服务类型 */
  channel_types?: ChannelType[];
  /** 关联主体（主供应商ID） */
  relate_main_supplier_ids?: number[];
  /** 关联主体服务类型 */
  relate_channel_types?: ChannelType[];
  /** 比选单号或者标题 */
  bidding_id_or_name?: string;
  /** 所属产品 */
  product_ids?: number[];
  /** 比选类型 */
  bidding_types?: BiddingTypeV2Enum[];
  /** 搜索的创建时间区间 */
  create_date_range?: string[];
  /** 搜索的预计开始时间区间 */
  expect_start_date_range?: string[];
  /** 搜索的预计结束时间区间 */
  expect_end_date_range?: string[];
  /** 状态 */
  states?: BiddingState[];
};
/**
 * 采购方商务评分信息
 */
export type ChannelSupplierBiddingScoreInfo = {
  /** 权重 */
  weight?: number;
  /** 评分 */
  score?: number;
  /** 评分详情 */
  detail?: Item[];
  /** 优势 */
  advantage?: string;
  /** 不足 */
  shortage?: string;
};
export type ChannelSupplierBiddingVo = {
  /** 比选单号 */
  id?: number;
  /** 供应商id */
  supplier_id?: number;
  /** 行id */
  row_id?: string;
  /** 供应商名称 */
  supplier_name?: string;
  /** 服务类型名称 */
  channel_type_name?: string;
  /** 产品编码 */
  product_code?: string;
  /** 产品名称 */
  product_name?: string;
  /** 比选标题 */
  name?: string;
  state?: BiddingState;
  bidding_type?: BiddingTypeV2Enum;
  /** 报价总金额 */
  quote_price?: number;
  quote_currency?: CurrencyEnum;
  /** 中选金额 */
  winning_amount?: number;
  winning_currency?: CurrencyEnum;
  /** 执行金额 */
  execution_amount?: number;
  execution_currency?: CurrencyEnum;
  /** 供应商评分 */
  score?: number;
  /** 需求方评分 */
  demand_score?: number;
  /** 职能策划评分 */
  plan_score?: number;
  /** 采购方商务评分 */
  purchase_score?: number;
  demand_score_info?: ChannelSupplierBiddingScoreInfo;
  plan_score_info?: ChannelSupplierBiddingScoreInfo;
  purchase_score_info?: ChannelSupplierBiddingScoreInfo;
  /** 预计开始时间 */
  expect_begin_at?: string;
  /** 预计结束时间 */
  expect_end_at?: string;
  /** 创建日期 */
  create_time?: string;
  /** 比选类型名称 */
  type_name?: string;
  /** 比选状态名称 */
  state_name?: string;
};
/**
 * 【供应商详情页优化2.0&供应商列表优化】比选单总计
 */
export type BiddingCountVo = {
  /** 总数 */
  total?: number;
  /** 进行中数量 */
  running_count?: number;
  /** 已结束数量 */
  finished_count?: number;
  /** 流标数量 */
  failed_count?: number;
};
/**
 * 【供应商详情页优化2.0&供应商列表优化】比选单评分
 */
export type AvgBiddingScoreVo = {
  /** 平均评分 */
  score?: number;
  /** 需求方评分 */
  demand_score?: number;
  /** 职能策划评分 */
  plan_score?: number;
  /** 采购方商务评分 */
  purchase_score?: number;
};
export type ChannelSupplierBiddingTotalResultVo = {
  quoted?: ChannelSupplierTotalAmountVo;
  winning?: ChannelSupplierTotalAmountVo;
  execution?: ChannelSupplierTotalAmountVo;
  sourcing_count?: SourcingCountVo;
  bidding_count?: BiddingCountVo;
  avg_bidding_score?: AvgBiddingScoreVo;
};
export type PageWithExtInfoVoChannelSupplierBiddingVoChannelSupplierBiddingTotalResultVo =
  {
    total?: number;
    records?: ChannelSupplierBiddingVo[];
    ext_info?: ChannelSupplierBiddingTotalResultVo;
  };
export type ResponsePageWithExtInfoVoChannelSupplierBiddingVoChannelSupplierBiddingTotalResultVo =
  {
    code?: number;
    sub_code?: string;
    message?: string;
    message_detail?: object;
    data?: PageWithExtInfoVoChannelSupplierBiddingVoChannelSupplierBiddingTotalResultVo;
    stacktrace?: string[];
    time?: number;
    trace_id?: string;
  };
export type UpdateChannelSupplierTagsForm = {
  /** 标签id */
  tag_ids: number[];
};
export type SubmitChannelSupplierForm = {
  /** 提交原因 */
  submit_reason?: string;
};
export type ResponseListProductDemandVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ProductDemandVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryDemandOverallScoreUpdateListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
};
export type OverallScoreUpdateRecordVo = {
  /** 版本号 */
  version?: number;
  /** 综合评分 */
  overall_score?: string;
  /** 过程打分 */
  processing_score?: string;
  /** 结果打分 */
  result_score?: string;
  /** 更新时间 */
  update_date?: string;
};
export type PageVoOverallScoreUpdateRecordVo = {
  total?: number;
  records?: OverallScoreUpdateRecordVo[];
};
export type ResponsePageVoOverallScoreUpdateRecordVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoOverallScoreUpdateRecordVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryDemandOverallScoreUpdateDetailForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 评分版本 */
  version?: number;
};
export type OverallScoreUpdateDetailVo = {
  /** 需求单ID */
  demand_id?: number;
  demand_group_type?: DemandGroupType;
  /** 综合评分 */
  overall_score?: string;
  /** 过程打分 */
  processing_score?: string;
  /** 结果打分 */
  result_score?: string;
};
export type PageVoOverallScoreUpdateDetailVo = {
  total?: number;
  records?: OverallScoreUpdateDetailVo[];
};
export type ResponsePageVoOverallScoreUpdateDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoOverallScoreUpdateDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ChangeChannelSupplierTypeForm = {
  type: ChannelSupplierType;
  /** 申请原因 */
  submit_reason?: string;
  /** 限制截至日期 */
  limit_end_date?: string;
  /** 附件 */
  attachments?: SaveStorageObjectForm[];
};
export type ChannelSupplierApprovalReturnForm = {
  /** 退回原因 */
  reason: string;
  line_no?: number;
};
export type ChannelSupplierApprovalRejectForm = {
  /** 拒绝原因 */
  reason: string;
  /** 审批条线 */
  line_no?: number;
};
export type ChannelSupplierApprovalPassForm = {
  /** 不传默认为条线1; 目前用于兼容入库会签审批, 条线1: 法务审核 财务BP审核; 条线2: 商务负责人审核 策划审核; */
  approval_line_no?: number;
  /** 审批意见, 文本, 非必填 */
  approval_opinion?: string;
};
export type UpdateVmdmBankCommand = {
  /** VMDM供应商Code */
  mdm_code?: string;
  /** 银行账号/IBAN */
  account?: string;
  language?: LanguageEnum;
  /** 银行名称 */
  name?: string;
  /** 国家名称 */
  country_name?: string;
  /** 国家编码 */
  country_code?: string;
  /** 开户行省 */
  province?: string;
  /** 开户行市 */
  city?: string;
  /** Swift Code */
  swift_code?: string;
  /** 支行名称 */
  branch_name?: string;
  /** 账号名称 */
  account_name?: string;
};
/**
 * VMDM供应商银行信息
 */
export type VmdmSupplierBankVo = {
  /** VMDM供应商编码 */
  nev_id?: string;
  /** 银行名称 */
  bank_name?: string;
  /** 银行账号/IBAN */
  bank_account?: string;
  /** 账号名称 */
  bank_account_name?: string;
  /** 支行名称 */
  bank_branch_name?: string;
  /** 国家 */
  bank_country_name?: string;
  /** 国家code */
  bank_country_code?: string;
  /** 省 */
  bank_province?: string;
  /** 市 */
  bank_city?: string;
  /** 业务编码 */
  bank_business_code?: string;
  /** Swift Code */
  swift_code?: string;
};
export type ResponseVmdmSupplierBankVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: VmdmSupplierBankVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 审批类型
 */
export type SupplierApprovalType =
  | 'INBOUND'
  | 'INFO_CHANGE'
  | 'UDG_UP_TO_ANNUL_FRAME'
  | 'UDG_UP_DOWN_TO_RESERVED'
  | 'UDG_UP_DOWN_TO_TEMPORARY'
  | 'UDG_UP_DOWN_TO_LIN_SHI'
  | 'UDG_DOWN_TO_RESTRICTIVE'
  | 'UDG_OUTBOUND_TO_BLACKLISTED'
  | 'UDG_OUTBOUND_TO_ELIMINATED'
  | 'UDG_OUTBOUND_TO_OTHER';
/**
 * 简化审批类型
 */
export type SupplierSimplifyApprovalType =
  | 'UP_DOWN'
  | 'OUTBOUND'
  | 'BLACKLIST'
  | 'INFO_CHANGE'
  | 'INBOUND';
/**
 * 供应商升降级状态类型
 */
export type ChannelSupplierTypeChangeType = 1 | 2 | 3;
/**
 * 审核状态
 */
export type SupplierApprovalStatus = 0 | 1 | 2 | 3;
export type QueryMySubmittedCsForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 供应商服务编码 */
  code?: string;
  /** 审批类型 */
  approval_types?: SupplierApprovalType[];
  /** 简化审批类型 */
  simplify_approval_types?: SupplierSimplifyApprovalType[];
  /** 供应商升降级状态类型 */
  supplier_type_change_types?: ChannelSupplierTypeChangeType[];
  /** 供应商名称/简称：精确匹配 */
  supplier_name?: string;
  /** MPC供应商编号：精确匹配 */
  main_supplier_code?: string;
  /** MPC供应商ID：精确匹配 */
  main_supplier_id?: number;
  /** 审批节点 */
  approval_nodes?: ApprovalNode[];
  /** 是否单一来源 */
  single_source?: boolean;
  /** 服务类型：支持多选，枚举值：广告投放，内容营销，生态运营，创意与素材，活动赛事，名人明星，其他 */
  channel_types?: ChannelType[];
  /** vmdm编号 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 是否开启风险开关 */
  risk_switch?: boolean;
  /** 是否包含供应商备注 */
  has_supplier_comment?: boolean;
  /** 是否包含风险备注 */
  has_risk_remark?: boolean;
  /** 审核状态 */
  approval_status_list?: SupplierApprovalStatus[];
  /** 申请单号, 模糊匹配 */
  approval_no_fuzzy?: string;
};
export type MySubmittedCsVo = {
  approval_type?: SupplierApprovalType;
  simplify_approval_type?: SupplierSimplifyApprovalType;
  /** 审批id */
  approval_id?: string;
  /** 渠道供应商编码 */
  channel_supplier_code?: string;
  /** 渠道供应商id */
  channel_supplier_id?: string;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 主供应商编码 */
  main_supplier_code?: string;
  /** vmdm编号 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 供应商名称 */
  supplier_name?: string;
  /** 简称 */
  abbr_name?: string;
  channel_type?: ChannelType;
  supplier_types?: ChannelSupplierType;
  supplier_type?: ChannelSupplierType;
  target_supplier_type?: ChannelSupplierType;
  supplier_type_change_type?: ChannelSupplierTypeChangeType;
  /** 创建时间 */
  create_date?: string;
  /** 提交时间 */
  submit_date?: string;
  status?: ChannelSupplierStatus;
  approval_node?: ApprovalNode;
  approval_node2?: ApprovalNode;
  /** 标签 */
  tags?: TagVo[];
  permissions?: ChannelSupplierOperationPermission;
  /** 风险开关 */
  risk_switch?: boolean;
  /** 是否包含风险备注 */
  has_risk_remark?: boolean;
  /** 是否包含供应商备注 */
  has_supplier_comment?: boolean;
  /** 供应商备注 */
  supplier_comments?: SupplierCommentVo[];
  /** 风险提示 */
  risk_remarks?: SupplierRiskRemarkVo[];
  /** 供应商风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  approval_status?: SupplierApprovalStatus;
  /** 申请单号 */
  approval_no?: string;
  /** 申请原因 */
  submit_reason?: string;
  /** 申请人, 邮箱 */
  submitter?: string;
  submit_user?: Operator;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 审批节点列表 */
  approval_node_list?: ApprovalNode[];
};
export type PageVoMySubmittedCsVo = {
  total?: number;
  records?: MySubmittedCsVo[];
};
export type ResponsePageVoMySubmittedCsVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoMySubmittedCsVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryMyApprovalCsForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 供应商服务编码 */
  code?: string;
  /** 审批类型 */
  approval_types?: SupplierApprovalType[];
  /** 简化审批类型 */
  simplify_approval_types?: SupplierSimplifyApprovalType[];
  /** 供应商升降级状态类型 */
  supplier_type_change_types?: ChannelSupplierTypeChangeType[];
  /** 供应商名称/简称：精确匹配 */
  supplier_name?: string;
  /** MPC供应商编号：精确匹配 */
  main_supplier_code?: string;
  /** MPC供应商ID：精确匹配 */
  main_supplier_id?: number;
  /** 是否单一来源 */
  single_source?: boolean;
  /** 服务类型：支持多选，枚举值：广告投放，内容营销，生态运营，创意与素材，活动赛事，名人明星，其他 */
  channel_types?: ChannelType[];
  /** 审批节点 */
  approval_nodes?: ApprovalNode[];
  /** vmdm编号 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 是否开启风险开关 */
  risk_switch?: boolean;
  /** 是否包含供应商备注 */
  has_supplier_comment?: boolean;
  /** 是否包含风险备注 */
  has_risk_remark?: boolean;
  /** 审核状态 */
  approval_status_list?: SupplierApprovalStatus[];
  /** 申请单号, 模糊匹配 */
  approval_no_fuzzy?: string;
};
export type MyApprovalCsVo = {
  approval_type?: SupplierApprovalType;
  simplify_approval_type?: SupplierSimplifyApprovalType;
  /** 审批id */
  approval_id?: string;
  /** 渠道供应商编码 */
  channel_supplier_code?: string;
  /** 渠道供应商id */
  channel_supplier_id?: string;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 主供应商编码 */
  main_supplier_code?: string;
  /** vmdm编号 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 供应商名称 */
  supplier_name?: string;
  /** 简称 */
  abbr_name?: string;
  channel_type?: ChannelType;
  supplier_types?: ChannelSupplierType;
  supplier_type?: ChannelSupplierType;
  target_supplier_type?: ChannelSupplierType;
  supplier_type_change_type?: ChannelSupplierTypeChangeType;
  /** 创建时间 */
  create_date?: string;
  /** 提交时间 */
  submit_date?: string;
  status?: ChannelSupplierStatus;
  approval_node?: ApprovalNode;
  approval_node2?: ApprovalNode;
  /** 标签 */
  tags?: TagVo[];
  permissions?: ChannelSupplierOperationPermission;
  /** 风险开关 */
  risk_switch?: boolean;
  /** 是否包含风险备注 */
  has_risk_remark?: boolean;
  /** 是否包含供应商备注 */
  has_supplier_comment?: boolean;
  /** 供应商备注 */
  supplier_comments?: SupplierCommentVo[];
  /** 风险提示 */
  risk_remarks?: SupplierRiskRemarkVo[];
  /** 供应商风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  approval_status?: SupplierApprovalStatus;
  /** 申请单号 */
  approval_no?: string;
  /** 申请原因 */
  submit_reason?: string;
  /** 申请人, 邮箱 */
  submitter?: string;
  submit_user?: Operator;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 审批节点列表 */
  approval_node_list?: ApprovalNode[];
};
export type PageVoMyApprovalCsVo = {
  total?: number;
  records?: MyApprovalCsVo[];
};
export type ResponsePageVoMyApprovalCsVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoMyApprovalCsVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandSimpleVo = {
  /** 需求单号 */
  id?: number;
  /** 需求单标题 */
  name?: string;
  /** 所属产品名 */
  production_name?: string;
  /** 项目执行满意度分 */
  project_processing_score?: number;
  /** 项目执行满意度打分时间 */
  project_processing_score_date?: string;
  /** 项目效果满意度分 */
  project_result_score?: number;
  /** 项目效果满意度打分时间 */
  project_result_score_date?: string;
};
export type ResponseListDemandSimpleVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSimpleVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandEvaluateScoreDescVo = {
  name?: string;
  detail?: EvaluateItemDescVo[];
};
export type ResponseDemandEvaluateScoreDescVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandEvaluateScoreDescVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryValidSupplierForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 供应商服务编码 */
  code?: string;
  /** 供应商名称/简称：精确匹配 */
  supplier_name?: string;
  /** MPC供应商编号：精确匹配 */
  main_supplier_code?: string;
  /** 是否单一来源 */
  single_source?: boolean;
  /** 供应商类型：支持多选，枚举值：临时供应商、储备供应商、年框供应商 */
  supplier_types?: ChannelSupplierType[];
  /** 服务类型：支持多选，枚举值：广告投放，内容营销，生态运营，创意与素材，活动赛事，名人明星，其他 */
  channel_types?: ChannelType[];
  /** 标签id */
  tag_ids?: number[];
  /** vmdm编码 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 风险提示开启 */
  risk_switch?: boolean;
  /** 包含风险备注 */
  has_risk_remark?: boolean;
  /** 包含供应商备注 */
  has_supplier_comment?: boolean;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 搜索的入库时间区间 */
  inbound_date_range?: string[];
};
/**
 * 渠道供应商列表
 */
export type ChannelSupplierInfoVo = {
  /** 渠道供应商ID */
  id?: number;
  channel_type?: ChannelType;
  type?: ChannelSupplierType;
  /** 渠道供应商编码 */
  code?: string;
  /** 标签id列表 */
  tag_ids?: number[];
  /** 标签 */
  tags?: TagVo[];
  /** 入库时间 */
  inbound_time?: string;
  permissions?: ChannelSupplierOperationPermission;
};
/**
 * 权限信息
 */
export type SupplierOperationPermission = {
  /** 是否编辑 */
  can_edit?: boolean;
  /** 是否可查看 */
  can_view?: boolean;
  /** 是否可以修改供应商类型 */
  can_change_supplier_type?: boolean;
  /** 是否可以打标 */
  can_tag?: boolean;
  /** 是否可以设置单一来源 */
  can_set_single_source?: boolean;
  /** 是否可以编辑风险开关 */
  can_edit_risk_switch?: boolean;
  /** 是否可以新增服务类型供应商（简易） */
  can_add_channel_supplier_simple?: boolean;
  /** 是否可以新增供应商风险备注 */
  can_add_supplier_risk_remark?: boolean;
  /** 是否可以新增供应商备注 */
  can_add_supplier_comment?: boolean;
  /** 是否可以拉黑供应商 */
  can_block_supplier?: boolean;
  /** 是否可以操作主体关联 */
  can_edit_links?: boolean;
  /** 是否可以编辑主信息 */
  can_edit_main_info?: boolean;
  /** 是否可以查看需求单 */
  can_view_demand?: boolean;
  /** 是否可以查看比选单 */
  can_view_bidding?: boolean;
  /** 是否可查看新 */
  can_view_new?: boolean;
  /** 是否可创建走访考察任务 */
  can_create_visit_task?: boolean;
  /** 是否可查看走访报告 */
  can_view_visit_report?: boolean;
  /** 是否可查看走访考察页签 */
  can_view_visit_info?: boolean;
  /** 是否可以编辑账号信息 */
  can_edit_account?: boolean;
};
export type ValidSupplierVo = {
  /** 风险备注 */
  risk_remarks?: SupplierRiskRemarkVo[];
  /** 供应商备注 */
  supplier_comments?: SupplierCommentVo[];
  /** 供应商风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 主供应商名称 */
  main_supplier_name?: string;
  /** 主供应商简称 */
  main_supplier_abbr_name?: string;
  /** 渠道供应商列表 */
  channel_suppliers?: ChannelSupplierInfoVo[];
  /** 主供应商编码 */
  main_supplier_code?: string;
  /** vmdm编号 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 是否单一来源 */
  single_source?: boolean;
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  operation_permission?: SupplierOperationPermission;
};
export type PageVoValidSupplierVo = {
  total?: number;
  records?: ValidSupplierVo[];
};
export type ResponsePageVoValidSupplierVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoValidSupplierVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryValidCsForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 供应商服务编码 */
  code?: string;
  /** 供应商名称/简称：精确匹配 */
  supplier_name?: string;
  /** MPC供应商编号：精确匹配 */
  main_supplier_code?: string;
  /** MPC供应商ID：精确匹配 */
  main_supplier_id?: number;
  /** 是否单一来源 */
  single_source?: boolean;
  /** 供应商类型：支持多选，枚举值：临时供应商、储备供应商、年框供应商 */
  supplier_types?: ChannelSupplierType[];
  /** 服务类型：支持多选，枚举值：广告投放，内容营销，生态运营，创意与素材，活动赛事，名人明星，其他 */
  channel_types?: ChannelType[];
  /** 垫支量级：下拉选择，支持多选，枚举值：0-50万、50-100万、100-500万、500万以上、不明 */
  advance_amount_range?: ChannelSupplierAdvanceScale[];
  /** 类型生效开始时间：日历控件，年月日，支持选择日期范围 */
  valid_start_date?: string;
  /** 类型生效结束时间：日历控件，年月日，支持选择日期范围 */
  valid_end_date?: string;
  /** 标签id */
  tag_ids?: number[];
  /** vmdm编码 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 风险提示开启 */
  risk_switch?: boolean;
  /** 包含风险备注 */
  has_risk_remark?: boolean;
  /** 包含供应商备注 */
  has_supplier_comment?: boolean;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
};
export type ValidChannelSupplierVo = {
  /** 渠道供应商ID */
  id?: number;
  /** 渠道供应商编码 */
  code?: string;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 主供应商编码 */
  main_supplier_code?: string;
  /** vmdm编号 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 主供应商名称 */
  main_supplier_name?: string;
  /** 主供应商简称 */
  main_supplier_abbr_name?: string;
  channel_type?: ChannelType;
  advance_scale?: ChannelSupplierAdvanceScale;
  type?: ChannelSupplierType;
  /** 类型生效时间 */
  type_valid_date?: string;
  /** 类型失效时间 */
  type_invalid_date?: string;
  /** 标签 */
  tags?: TagVo[];
  permissions?: ChannelSupplierOperationPermission;
  /** 是否单一来源 */
  single_source?: boolean;
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注 */
  risk_remarks?: SupplierRiskRemarkVo[];
  /** 供应商备注 */
  supplier_comments?: SupplierCommentVo[];
  /** 供应商风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 是否内部供应商 */
  inner_supplier?: boolean;
};
export type PageVoValidChannelSupplierVo = {
  total?: number;
  records?: ValidChannelSupplierVo[];
};
export type ResponsePageVoValidChannelSupplierVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoValidChannelSupplierVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QuerySupplierTrumpetAccountInComeForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  account_id?: number;
  start?: string;
  end?: string;
};
export type SupplierTrumpetAccountIncomeVo = {
  /** 小喇叭活动名称 */
  activity_title?: string;
  /** 活动场景 */
  activity_scene?: string;
  /** 小喇叭账号id */
  account_id?: number;
  /** 产品名称 */
  game_name?: string;
  /** MPC小喇叭需求三级单号列表 */
  trumpet_case_ids?: number[];
  /** 周账单日期 */
  week_belong_time?: string;
  /** 作者身份，1：个人作者；2：机构 */
  institution_type?: number;
  /** 机构名称 */
  company_name?: string;
  /** 预计发放金额（含税） */
  expected_amount_with_tax?: number;
  /** 实际发放金额（不含税） */
  actual_amount_no_tax?: number;
  /** 实际发放金额（税额） */
  actual_amount_tax?: number;
  /** 实际发放金额（含税） */
  actual_amount_with_tax?: number;
  income_currency?: CurrencyEnum;
};
export type PageVoSupplierTrumpetAccountIncomeVo = {
  total?: number;
  records?: SupplierTrumpetAccountIncomeVo[];
};
export type ResponsePageVoSupplierTrumpetAccountIncomeVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoSupplierTrumpetAccountIncomeVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QuerySupplierTrumpetAgrForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  account_id?: number;
};
export type SupplierTrumpetAccountGroupVo = {
  group_name?: string;
  group_game?: string;
  group_id?: string;
  valid_time?: string;
  member_num?: number;
  join_activity_num?: number;
};
export type PageVoSupplierTrumpetAccountGroupVo = {
  total?: number;
  records?: SupplierTrumpetAccountGroupVo[];
};
export type ResponsePageVoSupplierTrumpetAccountGroupVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoSupplierTrumpetAccountGroupVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryNotInStorageCsForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 供应商服务编码 */
  code?: string;
  /** 供应商名称/简称：精确匹配 */
  supplier_name?: string;
  /** MPC供应商编号：精确匹配 */
  main_supplier_code?: string;
  /** MPC供应商ID：精确匹配 */
  main_supplier_id?: number;
  /** 是否单一来源 */
  single_source?: boolean;
  /** 服务类型：支持多选，枚举值：广告投放，内容营销，生态运营，创意与素材，活动赛事，名人明星，其他 */
  channel_types?: ChannelType[];
  /** 状态列表 */
  statuses?: ChannelSupplierStatus[];
  /** 创建时间 */
  create_date?: string;
  /** 创建时间-结束 */
  create_date_end?: string;
  /** vmdm编码 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 风险提示开启 */
  risk_switch?: boolean;
  /** 包含风险备注 */
  has_risk_remark?: boolean;
  /** 包含供应商备注 */
  has_supplier_comment?: boolean;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 垫支量级：下拉选择，支持多选，枚举值：0-50万、50-100万、100-500万、500万以上、不明 */
  advance_amount_range?: ChannelSupplierAdvanceScale[];
  /** 类型生效开始时间：日历控件，年月日，支持选择日期范围 */
  valid_start_date?: string;
  /** 类型生效结束时间：日历控件，年月日，支持选择日期范围 */
  valid_end_date?: string;
  /** 供应商类型 */
  supplier_types?: string[];
  /** 审核节点 */
  approval_nodes?: number[];
  /** 申请单号模糊匹配 */
  approval_no_fuzzy?: string;
};
/**
 * 未入库渠道供应商VO
 */
export type NotInStorageCsVo = {
  /** 渠道供应商ID */
  id?: number;
  /** 渠道供应商编码 */
  code?: string;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 主供应商编码 */
  main_supplier_code?: string;
  /** 主供应商名称 */
  main_supplier_name?: string;
  /** 主供应商简称 */
  main_supplier_abbr_name?: string;
  /** vmdm编号 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  channel_type?: ChannelType;
  type?: ChannelSupplierType;
  /** 创建时间 */
  created_at?: string;
  status?: ChannelSupplierStatus;
  /** 状态已退回，显示原因理由 */
  reason?: string;
  approval_node?: ApprovalNode;
  approval_node2?: ApprovalNode;
  /** 标签 */
  tags?: TagVo[];
  permissions?: ChannelSupplierOperationPermission;
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注 */
  risk_remarks?: SupplierRiskRemarkVo[];
  /** 供应商备注 */
  supplier_comments?: SupplierCommentVo[];
  /** 供应商风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 提交人邮箱 */
  submitter?: string;
  submitter_operator?: Operator;
  /** 申请单号 */
  approval_no?: string;
  advance_scale?: ChannelSupplierAdvanceScale;
  /** 类型生效时间 */
  type_valid_date?: string;
  /** 审核节点列表 */
  approval_node_list?: ApprovalNode[];
};
export type PageVoNotInStorageCsVo = {
  total?: number;
  records?: NotInStorageCsVo[];
};
export type ResponsePageVoNotInStorageCsVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoNotInStorageCsVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryApprovalPendingCsForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 供应商服务编码 */
  code?: string;
  /** 审批类型 */
  approval_types?: SupplierApprovalType[];
  /** 简化审批类型 */
  simplify_approval_types?: SupplierSimplifyApprovalType[];
  /** 审批节点 */
  approval_nodes?: ApprovalNode[];
  /** 供应商升降级状态类型 */
  supplier_type_change_types?: ChannelSupplierTypeChangeType[];
  /** 供应商名称/简称：精确匹配 */
  supplier_name?: string;
  /** MPC供应商编号：精确匹配 */
  main_supplier_code?: string;
  /** MPC供应商ID：精确匹配 */
  main_supplier_id?: number;
  /** 是否单一来源 */
  single_source?: boolean;
  /** 服务类型：支持多选，枚举值：广告投放，内容营销，生态运营，创意与素材，活动赛事，名人明星，其他 */
  channel_types?: ChannelType[];
  /** vmdm编号 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 是否开启风险开关 */
  risk_switch?: boolean;
  /** 是否包含供应商备注 */
  has_supplier_comment?: boolean;
  /** 是否包含风险备注 */
  has_risk_remark?: boolean;
  /** 申请单号, 模糊匹配 */
  approval_no_fuzzy?: string;
};
export type ApprovalPendingCsVo = {
  approval_type?: SupplierApprovalType;
  simplify_approval_type?: SupplierSimplifyApprovalType;
  /** 审批id */
  approval_id?: string;
  /** 渠道供应商编码 */
  channel_supplier_code?: string;
  /** 渠道供应商id */
  channel_supplier_id?: string;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 主供应商编码 */
  main_supplier_code?: string;
  /** vmdm编号 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 供应商名称 */
  supplier_name?: string;
  /** 简称 */
  abbr_name?: string;
  channel_type?: ChannelType;
  supplier_types?: ChannelSupplierType;
  supplier_type?: ChannelSupplierType;
  target_supplier_type?: ChannelSupplierType;
  supplier_type_change_type?: ChannelSupplierTypeChangeType;
  /** 创建时间 */
  create_date?: string;
  /** 提交时间 */
  submit_date?: string;
  status?: ChannelSupplierStatus;
  approval_node?: ApprovalNode;
  approval_node2?: ApprovalNode;
  /** 标签 */
  tags?: TagVo[];
  permissions?: ChannelSupplierOperationPermission;
  /** 风险开关 */
  risk_switch?: boolean;
  /** 是否包含风险备注 */
  has_risk_remark?: boolean;
  /** 是否包含供应商备注 */
  has_supplier_comment?: boolean;
  /** 供应商备注 */
  supplier_comments?: SupplierCommentVo[];
  /** 风险提示 */
  risk_remarks?: SupplierRiskRemarkVo[];
  /** 供应商风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 申请单号 */
  approval_no?: string;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 申请原因 */
  submit_reason?: string;
  /** 申请人, 邮箱 */
  submitter?: string;
  submit_user?: Operator;
  /** 审批节点列表 */
  approval_node_list?: ApprovalNode[];
};
export type PageVoApprovalPendingCsVo = {
  total?: number;
  records?: ApprovalPendingCsVo[];
};
export type ResponsePageVoApprovalPendingCsVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoApprovalPendingCsVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryIndividualSupplierForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 姓名 */
  name?: string;
  /** 供应商服务编码 */
  code?: string;
  /** vmdm编号 */
  mdm_code?: string;
  /** 0 非黑名单, 1 黑名单 */
  blocked?: number;
  /** 易闪ID */
  ys_user_id?: string;
  /** 创作匠ID */
  czj_user_id?: string;
  /** 小喇叭用户ID */
  trumpet_user_id?: string;
};
export type IndividualSupplierVo = {
  /** 姓名 */
  name?: string;
  /** 渠道供应商ID */
  id?: number;
  /** 渠道供应商编码 */
  code?: string;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 主供应商编码 */
  main_supplier_code?: string;
  /** vmdm编号 */
  mdm_code?: string;
  type?: ChannelSupplierType;
  mdm_type?: SupplierVendorType;
  /** 创作匠ID */
  czj_user_ids?: string[];
  /** 易闪ID */
  ys_user_ids?: string[];
  /** 小喇叭ID */
  trumpet_user_ids?: string[];
};
export type PageVoIndividualSupplierVo = {
  total?: number;
  records?: IndividualSupplierVo[];
};
export type ResponsePageVoIndividualSupplierVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoIndividualSupplierVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 小喇叭供应商信息
 */
export type TrumpetSupplierVo = {
  ys?: Account;
  czj?: Account;
};
/**
 * 签约机构
 */
export type Organization = {
  /** 签约机构名称 */
  name?: string;
  /** 主供应商编码 */
  code?: string;
  /** vmdm编号 */
  mdm_code?: string;
};
/**
 * 签约信息
 */
export type TrumpetSupplierContractVo = {
  /** 意向游戏 */
  intention_game?: string;
  /** 签约游戏 */
  contact_game?: string[];
  /** 是否和游戏签约 */
  is_contact_with_game?: boolean;
  /** 是否和机构签约 */
  is_contact_with_organization?: boolean;
  /** 签约机构 */
  contacted_organization?: Organization[];
};
/**
 * 认证信息
 */
export type TrumpetSupplierCertificationVo = {
  /** 意向游戏 */
  product_no?: string;
  /** 状态 */
  status?: string;
  /** 生效日期 */
  valid_date?: string;
  /** 认证发起方 */
  certification_sponsor?: string;
  /** 作者类型 */
  author_type?: string;
  /** 邀请人 */
  invitor?: string;
  /** 邮箱 */
  email?: string;
};
/**
 * 小喇叭供应商信息
 */
export type TrumpetDetailVo = {
  /** accountId */
  id?: number;
  /** 用户id */
  user_id?: string;
  /** 手机号 */
  phone?: string;
  /** 是否为团长 */
  group_leader?: boolean;
  /** 团数量 */
  group_num?: number;
  /** 成为团长时间 */
  group_leader_start_time?: string;
  /** 团长奖励收入 */
  group_pay_income?: number;
  /** 累计收入 */
  income_amount?: number;
  income_currency?: CurrencyEnum;
  trumpet_supplier?: TrumpetSupplierVo;
  trumpet_supplier_contract?: TrumpetSupplierContractVo;
  /** 认证信息 */
  trumpet_supplier_certification?: TrumpetSupplierCertificationVo[];
  /** 运营信息 -- 处罚次数 */
  penalty_count?: number;
  /** 运营信息 -- 是否黑名单 */
  blocked?: boolean;
  /** 状态, 1-生效 0-注销 */
  state?: number;
};
export type IndividualSupplierDetailVo = {
  /** 渠道供应商ID */
  id?: number;
  /** 渠道供应商编码 */
  code?: string;
  main_supplier?: MainSupplierVo;
  type_info?: ChannelSupplierTypeInfoVo;
  status?: ChannelSupplierStatus;
  /** 小喇叭供应商信息 */
  trumpet_suppliers?: TrumpetDetailVo[];
  channel_type?: ChannelType;
  /** 是否可查看奖励 */
  can_view_award?: boolean;
};
export type ResponseIndividualSupplierDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: IndividualSupplierDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryChannelSupplierApprovalRecordForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 渠道供应商ID：精确匹配，有值且只有一个值时可以不传递主供应商id */
  channel_supplier_ids?: number[];
  /** 渠道类型列表 */
  channel_types?: ChannelType[];
  /** 主供应商ID：精确匹配 */
  main_supplier_id?: number;
  /** 申请单号, 模糊匹配 */
  approval_no_fuzzy?: string;
  /** 审核状态 */
  approval_status_list?: SupplierApprovalStatus[];
  /** 审批节点 */
  approval_nodes?: number[];
  /** 简化版审批类型 */
  simplify_approval_types?: SupplierSimplifyApprovalType[];
  /** 审批类型 */
  approval_types?: SupplierApprovalType[];
};
export type ChannelSupplierApprovalRecordVo = {
  /** 申请单号 */
  approval_no?: string;
  status?: ChannelSupplierStatus;
  /** 申请原因 */
  submit_reason?: string;
  /** 申请人 */
  submitter?: string;
  approval_type?: SupplierApprovalType;
  simplify_approval_type?: SupplierSimplifyApprovalType;
  /** 退回原因 */
  reject_reason?: string;
  approval_node?: ApprovalNode;
  approval_node2?: ApprovalNode;
  supplier_type?: ChannelSupplierType;
  target_supplier_type?: ChannelSupplierType;
  supplier_type_change_type?: ChannelSupplierTypeChangeType;
  /** 提交时间 */
  submit_date?: string;
  approval_status?: SupplierApprovalStatus;
  submit_user?: Operator;
  channel_type?: ChannelType;
  /** 渠道供应商ID */
  channel_supplier_id?: number;
  /** 审批节点列表 */
  approval_node_list?: ApprovalNode[];
};
export type PageVoChannelSupplierApprovalRecordVo = {
  total?: number;
  records?: ChannelSupplierApprovalRecordVo[];
};
export type ResponsePageVoChannelSupplierApprovalRecordVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoChannelSupplierApprovalRecordVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type CreateImportTaskForm = {
  /** 文件id */
  file_id: number;
};
export type CheckChannelSupplierForm = {
  /** 供应商主体名称 */
  main_supplier_name: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  channel_type: ChannelType;
  /** 是否为KOL合作MCN */
  kol_mcn?: boolean;
};
export type CheckSupplierResultVo = {
  /** 供应商主体是否存在 */
  main_supplier_existed?: boolean;
  /** 主供应商id */
  main_supplier_id?: number;
  /** 主供应商编码 */
  main_supplier_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 渠道供应商id */
  channel_supplier_id?: number;
  /** 渠道供应商编码 */
  channel_supplier_code?: string;
  channel_supplier_status?: ChannelSupplierStatus;
  /** vmdm编码 */
  mdm_code?: string;
  channel_supplier_existed?: boolean;
};
export type ResponseCheckSupplierResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: CheckSupplierResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListInteger = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: number[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type BatchApprovalForm = {
  /** 是否审批通过 */
  pass?: boolean;
  /** 审批原因 */
  reason?: string;
  /** 批量处理的审批id列表 */
  approval_ids: string[];
};
/**
 * 供应商变更项信息
 */
export type SupplierChangeItemDto = {
  field_key?: string;
  field_name?: string;
  old_value?: object;
  new_value?: object;
};
/**
 * 批量审批结果
 */
export type BatchApprovalResultItemVo = {
  /** 审批id */
  approval_id?: string;
  /** 申请单号 */
  approval_no?: string;
  /** 是否成功 */
  success?: boolean;
  /** 错误消息 */
  message?: string;
  wait_approval_node?: ApprovalNode;
  /** 供应商id */
  channel_supplier_id?: number;
  supplier_channel_type?: ChannelType;
  /** 供应商变更项信息 */
  change_items?: SupplierChangeItemDto[];
};
export type BatchApprovalResultVo = {
  /** 批量审批结果 */
  results?: BatchApprovalResultItemVo[];
};
export type ResponseBatchApprovalResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BatchApprovalResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商指标-分页查询表单
 */
export type SupplierKpiPageableQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 名称（模糊匹配） */
  name?: string;
  /** 服务类型，任一命中 */
  channel_types?: string[];
  /** 需求类型，任一命中 */
  category_ids?: number[];
  /** 状态 */
  status?: boolean;
};
/**
 * 分页数据
 */
export type SupplierKpiSimpleVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 状态 */
  status?: boolean;
  /** 供应商服务类型列表 */
  channel_type_names?: string[];
  /** 需求分类id列表 */
  category_ids?: number[];
  /** 需求类型列表 */
  category_names?: string[];
  /** 指标 */
  item_names?: string[];
};
/**
 * 分页结果
 */
export type PageResultSupplierKpiSimpleVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: SupplierKpiSimpleVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultSupplierKpiSimpleVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultSupplierKpiSimpleVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type SupplierKpiItemTypeEnum = 0 | 1;
/**
 * 来源
 */
export type SupplierKpiItemRuleDetailSourceEnum = 1 | 2 | 3;
/**
 * 详情列表
 */
export type SupplierKpiItemRuleDataDetail = {
  source: SupplierKpiItemRuleDetailSourceEnum;
  /** 评分项 */
  evaluate_name: string;
  /** 系数：必填，百分数，精度1% */
  weight: number;
};
export type SupplierKpiItemRuleData = {
  /** 详情列表 */
  details: SupplierKpiItemRuleDataDetail[];
  /** 前端预存内容 */
  ext?: {
    [key: string]: any;
  };
};
/**
 * 项目列表
 */
export type SupplierKpiItemValueObj = {
  name?: string;
  type?: SupplierKpiItemTypeEnum;
  rule_index?: number;
  rule?: SupplierKpiItemRuleData;
  range?: number;
};
/**
 * 更新供应商指标
 */
export type EditSupplierKpiForm = {
  /** 服务类型 */
  channel_types: string[];
  /** 需求分类id列表 */
  category_ids: number[];
  /** 状态 */
  status: boolean;
  /** 项目列表 */
  items: SupplierKpiItemValueObj[];
  /** 指标id */
  id: number;
};
/**
 * 创建供应商指标
 */
export type CreateSupplierKpiForm = {
  /** 服务类型 */
  channel_types: string[];
  /** 需求分类id列表 */
  category_ids: number[];
  /** 状态 */
  status: boolean;
  /** 项目列表 */
  items: SupplierKpiItemValueObj[];
};
export type ResponseInteger = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: number;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ManualBatchSubmitParam = {
  /** 需求单ID集合 */
  demand_ids: number[];
};
export type OaPlanApprovalForm = {
  demand_id: number;
  plan_attach_blob_ids?: number[];
  usage_plan: string;
  kpi_special_declaration_flag?: boolean;
  kpi_special_declaration_reason?: string;
  low_risk_special_report_flag?: boolean;
};
export type AutoSubmitOaParam = {
  day?: string;
  l1_category_ids?: number[];
  product_ids?: number[];
  demand_ids?: number[];
  assign_job_number?: string;
};
export type Source = {
  id?: string;
  name?: string;
  nev_id?: string;
};
export type OaSupplier = {
  value?: string[];
  source?: Source[];
};
export type SubmitOaDetailDto = {
  $$field_ewp2qsgzift?: number;
  $$field_94mtfcw7f2u?: string[];
  $$field_es3hwfdpyz5?: string;
  $$field_jz9y45805wc?: string;
  supplier_name?: OaSupplier;
  $$field_ng3rapnkk8r?: string[];
  $$field_l4g30sez7mh?: number;
  actual_disbursement_amount?: number;
  $$field_abfb0m7c9n4?: string;
  $$field_wcjn3uauokg?: string;
  $$field_plv4eer7073?: string;
  $$field_yn12bjt1fwd?: string;
  $$field_uif001uocma?: string[];
  $$field_yh95mw0vek8?: string;
  mpc_bill_number?: string;
  demand_expectations?: string;
  req_background?: string;
  final_budget_amount?: string;
  usage_planning?: string;
  qc_remark?: string;
  achievement_status?: string;
  ai_acceptance_conclusion?: string;
  demand_title?: string;
  $$field_kit7axbwt47?: string;
};
export type SubmitOaDto = {
  departmentof_the_requirement_owner?: string[];
  $$field_0n6dyv3qe3x?: string;
  requirements_owner?: string[];
  department_of_the_requirement_owner?: string[];
  department_of_the_execution_party?: string[];
  dept_of_planning_personnel?: string[];
  $$field_vbnjll80vsa?: string[];
  $$field_dkdtr7x30e6?: string;
  mpc_product_code?: string;
  $$field_8hzti9n6ro1?: number;
  $$field_7kpl6bygtv2?: string;
  qc_assessment_opinion?: string;
  qc_acceptance_status?: string;
  achievement_status?: string;
  ai_acceptance_status?: string;
  $$field_x7h1o7xmvr0?: SubmitOaDetailDto[];
};
export type ResponseListSubmitOaDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SubmitOaDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type BlobDto = {
  id?: number;
  /** 文件名 */
  filename?: string;
  /** 文件大小 */
  byte_size?: number;
  /** 文件类型 */
  content_type?: string;
  /** 文件下载用的key */
  key?: string;
  /** 是否自动生成 */
  auto_generated?: boolean;
};
/**
 * 参考附件对象
 */
export type AttachmentDto = {
  id?: number;
  blob?: BlobDto;
};
/**
 * 需求标品列表表单
 */
export type DemandStandardSpecVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 标品规则级别 */
  level?: string;
  /** 标品Id */
  demand_standard_catalog_id?: number;
  /** 标品库ID(标品+费用类型) */
  standard_library_id?: number;
  /** 参考链接 */
  reference_links?: string[];
  /** 内容描述 */
  content_description?: string;
  /** 参考附件 */
  refer_attachment?: number[];
  /** 参考附件对象 */
  refer_attachment_object?: AttachmentDto[];
};
export type StandardLibraryUpdateCmd = {
  /** 主键Id */
  id?: number;
  /** 状态（0-禁用，1-启用） */
  status?: number;
  /** 等级模板 */
  level_template?: string;
  /** 是否需要标品等级 #9510 */
  has_level?: boolean;
  /** 是否同步小喇叭 #9510 */
  sync_trumpet?: boolean;
  /** 标品等级 */
  levels?: DemandStandardSpecVo[];
};
export type StandardLibraryListReq = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 费用类型Id，一二三级需求类型Id存在一个即可 */
  category_ids?: number[];
  /** 标品编码Id，一二级标品Id存在一个即可 */
  standard_ids?: number[];
  /** 参考链接-模糊匹配 */
  reference_link?: string;
  /** 状态（0-禁用，1-启用） */
  status?: number;
  /** 是否同步小喇叭 #9510（true-同步，false-不同步，null-全部） */
  sync_trumpet?: boolean;
  /** 二级授权费用类型ID集合（内部过滤参数，非前端传入）#9510 */
  authorized_l2_category_ids?: number[];
};
export type StandardLibraryDto = {
  /** 主键Id */
  id?: number;
  /** 标品编码（一级） */
  standard1_id?: number;
  /** 标品编码（一级） */
  standard1_name?: string;
  /** 标品编码（二级） */
  standard2_id?: number;
  /** 标品编码（二级） */
  standard2_name?: string;
  /** 标品编码（二级） */
  code?: string;
  /** 费用类型一级 */
  l1_category_id?: number;
  /** 费用类型一级名称 */
  l1_category_name?: string;
  /** 费用类型二级 */
  l2_category_id?: number;
  /** 费用类型二级名称 */
  l2_category_name?: string;
  /** 费用类型三级 */
  l3_category_id?: number;
  /** 费用类型三级名称 */
  l3_category_name?: string;
  /** 状态（0-禁用，1-启用） */
  status?: number;
  /** 标品等级模板 */
  level_template?: string;
  /** 是否需要标品等级 #9510 */
  has_level?: boolean;
  /** 是否同步小喇叭 #9510 */
  sync_trumpet?: boolean;
  /** 标品等级 */
  levels?: DemandStandardSpecVo[];
  /** 更新时间 */
  updated_at?: string;
  /** 更新人 */
  updated_by?: number;
  updated_by_user?: User;
};
export type PageVoStandardLibraryDto = {
  total?: number;
  records?: StandardLibraryDto[];
};
export type ResponsePageVoStandardLibraryDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoStandardLibraryDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type StandardLibraryCreateOrUpdateCmd = {
  /** 标品（一级） */
  standard1_id: number;
  /** 标品编码（二级） */
  standard2_id: number;
  /** 费用类型（一级） */
  l1_category_id: number;
  /** 费用类型（二级） */
  l2_category_id: number;
  /** 费用类型（三级） */
  l3_category_id: number;
  /** 状态（0-禁用，1-启用） */
  status: number;
  /** 标品等级模板 */
  level_template?: string;
  /** 是否需要标品等级 #9510 */
  has_level?: boolean;
  /** 是否同步小喇叭 #9510 */
  sync_trumpet?: boolean;
  /** 标品等级 */
  levels?: DemandStandardSpecVo[];
};
export type SolarInvokeRequest = {
  api?: string;
  param?: string;
  purchase_sub_unit?: string;
};
export type SolarInvokeResponse = {
  status?: number;
  message?: string;
  trace_id?: string;
  data?: object;
};
/**
 * SM费用类型预算查询入参
 */
export type SmExpenseBudgetQueryForm = {
  /** MPC产品ID */
  product_id: number;
  /** 二级需求类型ID */
  l2_category_id: number;
  /** SM项目编号 */
  number: string;
};
/**
 * 项目预算行
 */
export type ProjectBudgetRow = {
  /** 项目编号 */
  project_number?: string;
  /** 项目名称 */
  project_name?: string;
  /** 剩余可用预算 */
  available_budget?: number;
  /** 总预算 */
  total_budget?: number;
  /** 已使用预算 */
  used_budget?: number;
  /** 使用率 */
  usage_rate?: string;
  /** 币种 */
  currency?: string;
};
/**
 * 是否自定义，1=自定义，0=标准
 */
export type KpiValueCustomEnum = 1 | 0;
/**
 * KPI标签枚举
 */
export type DemandKpiLabel = 'OVER_15DAY_CPM';
/**
 * 标签对象
 */
export type LabelObj = {
  /** KPI标签名称 */
  label_name?: string;
  /** KPI标签级别 */
  level?: number;
  label?: DemandKpiLabel;
};
/**
 * 【结构化】【内容营销】指标列表，只处理非直接考核的指标
 */
export type DemandKpiValueDto = {
  /** 指标分类 */
  type?: string;
  /** 序号 */
  index_id?: number;
  /** 需求合作内容ID，当类型为Cooperate时有效 */
  demand_cooperate_account_id?: number;
  /** 是否对供应商显示 */
  show_for_out?: boolean;
  /** 是否验收标准 */
  accept_flag?: boolean;
  /** 验收标准备注 */
  accept_remark?: string;
  kpi_schema: DemandKpiSchemaData;
  /** demand_kpi.code */
  kpi_code: string;
  custom: KpiValueCustomEnum;
  /** KPI期望值 */
  expect_value?: string;
  /** KPI实际值 */
  actual_value?: string;
  /** KPI实际值扩展 */
  actual_value_extra?: string;
  /** 效果实际值 */
  effect_value?: string;
  /** 判定结果，1=符合,0=不符合 */
  result?: boolean;
  /** 判定结果，1=符合,0=不符合 */
  result_extra?: boolean;
  /** 效果判定结果，1=符合,0=不符合 */
  effect_result?: boolean;
  label?: DemandKpiLabel;
  label_obj?: LabelObj;
  /** 是否cpm */
  cpm?: boolean;
  /** 指标结果是否忽略数易结果 */
  ignore_dspf?: boolean;
};
/**
 * 费用类型预算行
 */
export type ExpenseBudgetRow = {
  /** 一级需求类型ID */
  l1_category_id?: number;
  /** 一级需求类型名称 */
  l1_category_name?: string;
  /** 二级需求类型ID */
  l2_category_id?: number;
  /** 二级需求类型名称 */
  l2_category_name?: string;
  /** MPC产品ID */
  product_id?: number;
  /** 剩余可用预算 */
  available_budget?: number;
  /** 总预算 */
  total_budget?: number;
  /** 已使用预算 */
  used_budget?: number;
  /** 使用率 */
  usage_rate?: string;
  /** 币种 */
  currency?: string;
  /** 说明 */
  description?: string;
  /** 需求预期 */
  demand_expectation?: string;
  /** 考核指标 */
  kpi_indicators?: DemandKpiValueDto[];
};
/**
 * SM费用类型预算查询返回
 */
export type SmExpenseBudgetQueryVo = {
  project_budget?: ProjectBudgetRow;
  expense_budget?: ExpenseBudgetRow;
};
export type ResponseSmExpenseBudgetQueryVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SmExpenseBudgetQueryVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商条目
 */
export type SupplierItem = {
  /** 记录 ID（新增时为 null，更新时必填） */
  id?: number;
  /** 供应商 ID（Solar bigint） */
  supplier_id: number;
  /** 供应商 编码 */
  supplier_code: string;
  /** 中选类型：WINNING=中选，RESERVE=储备，OTHER=其他 */
  selection_type: 'WINNING' | 'RESERVE' | 'OTHER';
  /** 有效期起 */
  valid_from: string;
  /** 有效期止 */
  valid_to: string;
  /** 来源单据 ID（可空） */
  demand_id?: number;
  /** 来源单据类型：1=需求单 2=小喇叭 3=比选 4=闭环 20=预付单 */
  demand_type?: number;
};
/**
 * 标段供应商保存请求（全量 Diff）
 */
export type SegmentSupplierSaveForm = {
  /** 标段配置 ID */
  segment_config_id: number;
  /** 标段编码 */
  segment_code: string;
  /** 当前供应商列表（全量，后端与库中数据做 Diff） */
  suppliers?: SupplierItem[];
};
/**
 * 供应商标签
 */
export type SupplierTagVo = {
  /** 标签 ID */
  tag_id?: number;
  /** 标签名称 */
  tag_name?: string;
  /** 标签分类名称 */
  cat_name?: string;
};
/**
 * 供应商详细信息
 */
export type SupplierInfoVo = {
  /** 供应商名称 */
  name?: string;
  /** 供应商简称 */
  short_name?: string;
  /** 供应商编码（MPC 编码） */
  supplier_code?: string;
  /** MDM 编码 */
  mdm_code?: string;
  /** 服务类型描述 */
  service_info?: string;
  /** 渠道类型（TALENT / CHANNEL 等） */
  channel_type?: string;
  /** 是否单一来源 */
  single_source?: boolean;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 供应商标签列表 */
  tags?: SupplierTagVo[];
};
/**
 * 标段供应商关联
 */
export type SegmentSupplierVo = {
  /** 记录 ID */
  id?: number;
  /** 标段配置 ID */
  segment_config_id?: number;
  /** 标段编码（冗余，便于查询） */
  segment_code?: string;
  /** 供应商 ID */
  supplier_id?: number;
  /** 供应商编码 */
  supplier_code?: string;
  /** 中选类型：WINNING=中选，RESERVE=储备，OTHER=其他 */
  selection_type?: string;
  /** 有效期起 */
  valid_from?: string;
  /** 有效期止 */
  valid_to?: string;
  /** 来源单据 ID */
  demand_id?: number;
  /** 来源单据类型编码（1/2/3/4/20） */
  demand_type?: number;
  /** 创建人 ID */
  created_by?: number;
  /** 更新人 ID */
  updated_by?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  supplier_info?: SupplierInfoVo;
};
export type ResponseListSegmentSupplierVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SegmentSupplierVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 字段值 Form
 */
export type FieldValueForm = {
  /** 属性 Code */
  property_code: string;
  /** 选中的属性值 */
  value?: string[];
};
/**
 * 比选标段配置保存请求
 */
export type SegmentConfigForm = {
  /** 标段 ID（新建时为 null，更新时必填） */
  id?: number;
  /** 关联类目 ID */
  segment_category_id: number;
  /** 标段号，全局唯一 */
  segment_code: string;
  /** 状态：active=启用，locked=未启用 */
  status?: string;
  /** 字段值列表 */
  template_value?: FieldValueForm[];
};
/**
 * 标段配置查询请求对象
 */
export type SegmentConfigQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 类目名模糊查询（命中的 segment_category 所属 name 将被筛选出来） */
  name_like?: string;
  /** 类目名（多值精确匹配，不传则不过滤） */
  name_list?: string[];
  /** 标段编码模糊查询（对 segment_config.segment_code 做 LIKE 过滤） */
  segment_code_like?: string;
  /** 标段状态过滤（active=启用，locked=未启用），不传则不过滤 */
  status_list?: string[];
};
/**
 * 选项配置 VO
 */
export type OptionsVo = {
  /** 可选值列表 */
  options?: string[];
  /** 是否启用使用条件 */
  use_condition?: boolean;
};
/**
 * 属性元数据 VO
 */
export type PropertySchemaVo = {
  /** 属性 ID（demand_cooperate_property 主键） */
  property_id?: number;
  /** 属性 Code */
  property_code?: string;
  /** 属性名称 */
  property_name?: string;
  /** 属性类型（dropdown_list / dropdown_radio / numeric / date_input / date_range / text / text_with_upload / input） */
  property_type?: string;
  /** 可选值列表（仅 dropdown_list / dropdown_radio 类型有值，来自 config_json.options） */
  property_options?: string[];
  options?: OptionsVo;
  /** 是否必填模式（1=必填，0=非必填） */
  required_mode?: number;
  /** 属性描述 */
  description?: string;
  /** 系统编码 */
  sys_code?: string;
};
/**
 * 字段值 VO
 */
export type FieldValueVo = {
  /** 属性 Code */
  property_code?: string;
  /** 选中的属性值 */
  value?: string[];
  property_schema?: PropertySchemaVo;
};
/**
 * 标段配置分页列表项
 */
export type SegmentConfigPageVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 关联类目名称 */
  segment_category_name?: string;
  /** 标段号 */
  segment_code?: string;
  /** 状态：active=启用，locked=未启用 */
  status?: string;
  /** 字段值列表 */
  template_value_list?: FieldValueVo[];
};
export type OrderItem = {
  column?: string;
  asc?: boolean;
};
export type PageSegmentConfigPageVo = {
  records?: SegmentConfigPageVo[];
  total?: number;
  size?: number;
  current?: number;
  orders?: OrderItem[];
  optimize_count_sql?: boolean;
  search_count?: boolean;
  optimize_join_of_count_sql?: boolean;
  max_limit?: number;
  count_id?: string;
  pages?: number;
};
export type ResponsePageSegmentConfigPageVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageSegmentConfigPageVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 字段定义 Form
 */
export type FieldDefinitionForm = {
  /** 属性 Code */
  property_code?: string;
  /** 是否必填 */
  required?: boolean;
  /** 字段状态：active / inactive */
  status?: string;
};
/**
 * 比选标段类目保存请求
 */
export type SegmentCategoryForm = {
  /** 类目 ID（新建时为 null，更新时必填） */
  id?: number;
  /** 类目编码（预留拓展，可为空） */
  code?: string;
  /** 类目名称 */
  name: string;
  /** 状态：active=启用，locked=未启用 */
  status?: string;
  /** 字段组定义列表 */
  template?: FieldDefinitionForm[];
};
/**
 * 类目查询请求对象
 */
export type SegmentCategoryQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 类目名模糊查询（命中的 segment_category 所属 name 将被筛选出来） */
  name_like?: string;
  /** 类目名（多值精确匹配，不传则不过滤） */
  name_list?: string[];
  /** 标段编码模糊查询（命中的 segment_config 所属 category 将被筛选出来） */
  segment_code_like?: string;
  /** 状态过滤（active=启用，locked=未启用），不传则不过滤 */
  status_list?: string[];
};
/**
 * 类目分页列表项
 */
export type SegmentCategoryPageVo = {
  /** 类目 ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 最后更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 类目名称 */
  name?: string;
  /** 状态：active=启用，locked=未启用 */
  status?: string;
};
export type PageSegmentCategoryPageVo = {
  records?: SegmentCategoryPageVo[];
  total?: number;
  size?: number;
  current?: number;
  orders?: OrderItem[];
  optimize_count_sql?: boolean;
  search_count?: boolean;
  optimize_join_of_count_sql?: boolean;
  max_limit?: number;
  count_id?: string;
  pages?: number;
};
export type ResponsePageSegmentCategoryPageVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageSegmentCategoryPageVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 标段号列表查询请求对象
 */
export type SegmentListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 标段类目 ID 精确匹配（必填，不传则查全表） */
  category_id?: number;
  /** 标段号 / 字段值模糊搜索（匹配 segment_code 与 template_value JSON 字符串） */
  keyword?: string;
};
export type SearchUserCmd = {
  /** 搜索文本 */
  search_text?: string;
  /** 返回数量限制，不传或0表示不限制 #9510 */
  limit?: number;
};
export type SimpleUserVo = {
  /** 用户ID */
  id?: number;
  /** 用户名称 */
  name?: string;
  /** 员工邮箱 */
  email?: string;
  /** 拼音 */
  pinyin?: string;
};
export type RubyResponseVoListSimpleUserVo = {
  res_code?: number;
  res_msg?: string;
  data?: SimpleUserVo[];
  version?: number;
  trace_id?: string;
};
export type EditUserCmd = {
  /** 用户ID，批量场景会有值 */
  user_id?: number;
  /** 用户状态 */
  status?: string;
  /** 角色ID集合 */
  role_ids?: number[];
};
export type UserVo = {
  /** 用户ID */
  id?: number;
  /** 用户名称 */
  name?: string;
  /** 员工工号 */
  job_number?: string;
  /** 员工邮箱 */
  email?: string;
  /** 职位 */
  jobtitlename?: string;
  /** 用户状态 */
  mpc_status?: string;
  /** 角色信息 */
  role_ids?: number[];
  /** 拼音 */
  pinyin?: string;
  /** 主管名称 */
  manager_name?: string;
  /** 员工状态 */
  ehr_user_translated_status?: string;
  /** 地区信息 */
  translated_area?: string;
  /** 角色信息 */
  translated_roles?: string;
  /** 性别信息 */
  translated_sex?: string;
};
export type RubyResponseVoUserVo = {
  res_code?: number;
  res_msg?: string;
  data?: UserVo;
  version?: number;
  trace_id?: string;
};
export type DeleteRoleMemberCmd = {
  role_id?: number;
  user_id?: number;
};
export type SetUserAdminCmd = {
  /** 用户ID */
  user_id?: number;
};
/**
 * 产品分类全部权限列表（互娱/雷火/音乐/其他）
 */
export type ProductSourceEnum = 'HUYU' | 'LEIHUO' | 'MUSIC' | 'OTHER';
export type EditUserChargeProductsCmd = {
  /** 用户ID */
  user_id?: number;
  /** 是否负责全部产品 */
  charge_all_product?: boolean;
  /** 负责产品的ID列表 */
  charge_product_ids?: number[];
  /** 产品分类全部权限列表（互娱/雷火/音乐/其他） */
  user_product_category_permission_list?: ProductSourceEnum[];
};
/**
 * 负责产品列表
 */
export type IdAndNameVo = {
  id?: number;
  name?: string;
};
export type UserChargeProductsVo = {
  /** 是否负责全部产品 */
  charge_all_products?: boolean;
  /** 负责产品列表 */
  charge_products?: IdAndNameVo[];
};
export type RubyResponseVoUserChargeProductsVo = {
  res_code?: number;
  res_msg?: string;
  data?: UserChargeProductsVo;
  version?: number;
  trace_id?: string;
};
export type BatchUpdateUserCmd = {
  /** 用户角色信息 */
  users?: EditUserCmd[];
};
export type RubyResponseVoListUserVo = {
  res_code?: number;
  res_msg?: string;
  data?: UserVo[];
  version?: number;
  trace_id?: string;
};
export type RubyResponseVoMapStringObject = {
  res_code?: number;
  res_msg?: string;
  data?: {
    [key: string]: any;
  };
  version?: number;
  trace_id?: string;
};
export type CreateRoleCmd = {
  /** 角色名 */
  name?: string;
  /** 权限key */
  permission?: string[];
  /** 渠道供应商类型 */
  channel_supplier_type?: string[];
  /** 需求类型 */
  demand_category?: number[];
  /** 供应商类型 */
  supplier_type?: number[];
  role_category: RoleCategory;
  /** 是否支持线上申请 */
  is_support_online_apply: boolean;
};
export type RoleDetailVo = {
  /** ID */
  id?: number;
  /** 名称 */
  name?: string;
  /** 权限Key */
  permission_keys?: string[];
  role_category?: RoleCategory;
  /** 是否支持线上申请 */
  is_support_online_apply?: boolean;
};
export type RubyResponseVoRoleDetailVo = {
  res_code?: number;
  res_msg?: string;
  data?: RoleDetailVo;
  version?: number;
  trace_id?: string;
};
export type RoleMemberQuery = {
  /** 当前页码，从1开始 */
  current_page?: number;
  /** 每页数量 */
  page_size?: number;
  /** 排序字段 */
  orders?: OrderItem[];
  /** 角色ID */
  id?: number;
  /** 搜索 姓名/工号/邮箱 */
  search_text?: string;
  /** 是否仅显示负责产品为空的人员 */
  products_is_blank?: boolean;
};
/**
 * 分类下的产品选项列表
 */
export type NameValueVo = {
  name?: string;
  value?: object;
};
/**
 * 产品下拉选项（按分类分组的树形结构）
 */
export type ProductOptionGroupVo = {
  /** 分组名称（互娱/雷火/音乐/其他） */
  name?: string;
  /** 分组标识 */
  value?: object;
  /** 分类下的产品选项列表 */
  children?: NameValueVo[];
};
/**
 * 当前页的数据
 */
export type RoleMemberVo = {
  /** 用户ID */
  id?: number;
  /** 姓名 */
  name?: string;
  /** 拼音 */
  pinyin?: string;
  /** 邮箱 */
  email?: string;
  /** 工号 */
  job_number?: string;
  /** 一级部门名称 */
  depid1?: string;
  /** 二级部门名称 */
  depid2?: string;
  /** 三级部门名称 */
  depid3?: string;
  /** 负责产品 */
  product_ids?: object;
  /** 负责产品名称 */
  product_names?: string;
  /** 产品下拉选项（按分类分组的树形结构） */
  select_products?: ProductOptionGroupVo[];
  /** 用户已拥有的产品分类全部权限 */
  user_product_category_permissions?: ProductSourceEnum[];
};
export type PageVoRoleMemberVo = {
  /** 当前页码，从1开始 */
  current_page?: number;
  /** 每页数量 */
  page_size?: number;
  /** 数据总数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  /** 当前页的数据 */
  res?: RoleMemberVo[];
};
export type RubyResponseVoPageVoRoleMemberVo = {
  res_code?: number;
  res_msg?: string;
  data?: PageVoRoleMemberVo;
  version?: number;
  trace_id?: string;
};
export type RoleQuery = {
  /** 搜索文本 */
  search_text?: string;
  /** 是否仅显示负责产品为空的人员 */
  products_is_blank?: boolean;
};
export type RoleVo = {
  /** 角色ID */
  value?: number;
  /** 角色名称 */
  name?: string;
  /** 角色关联的用户数量 */
  num?: number;
  role_category?: RoleCategory;
  /** 是否支持线上申请 */
  is_support_online_apply?: boolean;
};
export type RubyResponseVoListRoleVo = {
  res_code?: number;
  res_msg?: string;
  data?: RoleVo[];
  version?: number;
  trace_id?: string;
};
export type ContractQueryForm = {
  page_no?: number;
  page_size?: number;
  contract_num?: string;
  contract_name?: string;
  salesman_code?: string;
  requirement_number?: string;
  old_requirement_number?: string;
  requirement_group?: string;
};
export type RubyResponseVoObject = {
  res_code?: number;
  res_msg?: string;
  data?: object;
  version?: number;
  trace_id?: string;
};
/**
 * 产品列表查询
 */
export type ProductQuery = {
  /** 当前页码，从1开始 */
  current_page?: number;
  /** 每页数量 */
  page_size?: number;
  /** 排序字段 */
  orders?: OrderItem[];
  /** 查询字符串，产品编号或名称 */
  search_text?: string;
  /** 产品归属 */
  ascription_type?: string[];
  /** 来源 */
  source?: string[];
  /** 国内/海外 */
  area?: string[];
  /** 产品归属地 */
  belonging_loc?: string[];
  /** 所属阶段 */
  stage?: string[];
  /** 一级部门 */
  dept1_id?: number[];
  /** 是否支持星图度量 */
  support_xingtu_measure?: boolean[];
  /** 产品类型 */
  product_type?: string[];
  /** 状态 */
  status?: string[];
};
/**
 * 产品
 */
export type ProductVo = {
  id?: number;
  name?: string;
  identifier?: string;
  area?: string;
  source?: string;
  stage?: string;
  status?: string;
  desc?: string;
  parent_id?: string;
  ascription_type?: string;
  belonging_loc?: string;
  boss_product_id?: string;
  cost_company_id?: number;
  cost_company_name?: string;
  dept1_id?: number;
  dept1_name?: string;
  dept2_id?: number;
  dept2_name?: string;
  dept3_id?: number;
  dept3_name?: string;
  is_from_boss?: boolean;
  modify_by_mpc?: boolean;
  support_xingtu_measure?: boolean;
  product_managers?: string;
  product_type?: string;
  research_nature?: string;
  translated_area?: string;
  translated_ascription_type?: string;
  translated_belonging_loc?: string;
  translated_product_type?: string;
  translated_research_nature?: string;
  translated_source?: string;
};
export type PageVoProductVo = {
  /** 当前页码，从1开始 */
  current_page?: number;
  /** 每页数量 */
  page_size?: number;
  /** 数据总数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  /** 当前页的数据 */
  res?: ProductVo[];
};
export type RubyResponseVoPageVoProductVo = {
  res_code?: number;
  res_msg?: string;
  data?: PageVoProductVo;
  version?: number;
  trace_id?: string;
};
export type DeleteProductCheckCmd = {
  id?: number;
};
export type BatchUpdateProductStatusCmd = {
  ids?: number[];
  status?: string;
};
export type BatchUpdateProductStatusResultVo = {
  success?: number;
};
export type RubyResponseVoBatchUpdateProductStatusResultVo = {
  res_code?: number;
  res_msg?: string;
  data?: BatchUpdateProductStatusResultVo;
  version?: number;
  trace_id?: string;
};
export type RubyGlobalSearchRequest = {
  search_text?: string;
  page_size?: number;
  current_page?: number;
  default_tab?: string;
  tabs?: string[];
  extra?: {
    [key: string]: any;
  };
  sel_type?: string;
};
export type RubyResponseVoListString = {
  res_code?: number;
  res_msg?: string;
  data?: string[];
  version?: number;
  trace_id?: string;
};
export type GlobalSearchItem = {
  id?: number;
  source_id?: number;
  mod?: number;
  parent_id?: number;
  state?: string;
  name?: string;
};
export type RubyGlobalSearchResult = {
  res?: GlobalSearchItem[];
  total_page?: number;
  current_page?: number;
  total_count?: number;
  page_size?: number;
  pr_count?: number;
  sm_count?: number;
  ls_count?: number;
  mm_count?: number;
  bc_count?: number;
  oc_count?: number;
  fd_count?: number;
  es_count?: number;
  cd_count?: number;
  mcount?: number;
  m_count?: number;
};
export type RubyResponseVoRubyGlobalSearchResult = {
  res_code?: number;
  res_msg?: string;
  data?: RubyGlobalSearchResult;
  version?: number;
  trace_id?: string;
};
export type UserQuery = {
  /** 搜索文本 */
  search_text?: string;
  /** 所属部门ID */
  group_id?: number;
};
/**
 * 列头信息
 */
export type GroupMemberColumnVo = {
  name?: string;
  key?: string;
};
export type OptionVo = {
  value?: number;
  name?: string;
};
/**
 * 角色下拉数据
 */
export type GroupMemberOptionVo = {
  options?: OptionVo[];
};
export type GroupMemberVo = {
  /** 列头信息 */
  columns?: GroupMemberColumnVo[];
  /** 负责人信息 */
  manager_users?: UserVo[];
  /** 用户信息 */
  res?: UserVo[];
  select_option?: GroupMemberOptionVo;
};
export type RubyResponseVoGroupMemberVo = {
  res_code?: number;
  res_msg?: string;
  data?: GroupMemberVo;
  version?: number;
  trace_id?: string;
};
export type ListGroupCmd = {
  /** 关键字搜索 */
  search_text?: string;
};
export type DepartmentTreeNodeVo = {
  dep_id?: number;
  group_id?: number;
  name?: string;
  depth?: number;
  users_size?: number;
};
export type RubyResponseVoListDepartmentTreeNodeVo = {
  res_code?: number;
  res_msg?: string;
  data?: DepartmentTreeNodeVo[];
  version?: number;
  trace_id?: string;
};
export type DeleteGroupManagerCmd = {
  /** 组ID */
  group_id?: number;
  /** 用户ID */
  user_id?: number;
};
export type SimpleUserDto = {
  /** 用户ID */
  id?: number;
  /** 用户名称 */
  name?: string;
  /** 员工邮箱 */
  email?: string;
  /** 拼音 */
  pinyin?: string;
};
export type RubyResponseVoSimpleUserDto = {
  res_code?: number;
  res_msg?: string;
  data?: SimpleUserDto;
  version?: number;
  trace_id?: string;
};
export type AddGroupManagerCmd = {
  /** 组ID */
  group_id?: number;
  /** 用户ID */
  user_id?: number;
};
export type ConfigUrlDto = {
  dspf_mpc_no?: string;
  product_number?: string;
  requirement_id?: number;
  class1: string;
  class2: string;
  class3: string;
  class4?: string;
  department: string;
};
export type ConfigUrlResult = {
  url?: string;
  error_msg?: string;
  can_skip?: boolean;
};
export type RubyResponseVoConfigUrlResult = {
  res_code?: number;
  res_msg?: string;
  data?: ConfigUrlResult;
  version?: number;
  trace_id?: string;
};
export type RubyAccountsSetNotificationRequest = {
  popo?: boolean;
  mail?: boolean;
};
export type RubyAccountsSetLanguageRequest = {
  language?: string;
  is_init_default_lang?: boolean;
};
export type ReportFormsTypeEnum = 'NOT_CLOSED_DEMAND';
export type ReportFormsExportForm = {
  report_forms_type?: ReportFormsTypeEnum;
};
/**
 * 价格库查询入参
 */
export type PriceLibraryQueryDetailForm = {
  /** 标品编码（二级） */
  standard2_id?: number;
  /** 费用类型三级 */
  l3_category_id?: number;
};
/**
 * 价格信息
 */
export type PriceLibraryDetail = {
  /** 标品等级 */
  level?: string;
  /** 价格下限 */
  price_lower?: number;
  /** 价格上限 */
  price_upper?: number;
  currency?: CurrencyEnum;
  /** 关联标品等级模版Id */
  demand_standard_spec_id?: number;
};
/**
 * 等级详情信息
 */
export type PriceLibraryLevelDetail = {
  /** 主键id */
  id?: number;
  price_library_detail?: PriceLibraryDetail;
  /** 参考链接（多行存储） */
  reference_links?: string[];
  /** 参考附件对象 */
  refer_attachment_object?: AttachmentDto[];
  /** 内容描述 */
  content_description?: string;
};
/**
 * 状态
 */
export type PriceLibraryStatusEnum =
  | 'APPROVAL'
  | 'ACTIVE'
  | 'REFUSE'
  | 'INVALID';
/**
 * 价格库
 */
export type PriceLibraryVo = {
  /** 价格ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 标品库ID */
  standard_library_id?: number;
  /** 费用类型一级 */
  l1_category_id?: number;
  /** 费用类型一级名称 */
  l1_category_name?: string;
  /** 费用类型二级 */
  l2_category_id?: number;
  /** 费用类型二级名称 */
  l2_category_name?: string;
  /** 费用类型三级 */
  l3_category_id?: number;
  /** 费用类型三级名称 */
  l3_category_name?: string;
  /** 标品编码（一级） */
  standard1_id?: number;
  /** 标品编码（一级） */
  standard1_name?: string;
  /** 标品编码（二级） */
  standard2_id?: number;
  /** 标品编码（二级） */
  standard2_name?: string;
  /** 标品编码 */
  standard_code?: string;
  /** 等级详情信息 */
  level_info?: PriceLibraryLevelDetail[];
  /** 标品是否有等级（#9510：无等级模式 has_level=false 时标品无等级字段） */
  has_level?: boolean;
  /** 生效时间 */
  effective_time?: string;
  /** 到期时间 */
  expiry_time?: string;
  status?: PriceLibraryStatusEnum;
  /** 状态名称 */
  status_name?: string;
  /** OA审批流程号 */
  oa_approval_no?: string;
  /** OA跳转链接 */
  oa_url?: string;
};
export type ResponsePriceLibraryVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PriceLibraryVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 价格库查询入参
 */
export type PriceLibraryQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 费用类型Id，一二三级需求类型Id存在一个即可 */
  category_ids?: number[];
  /** 标品编码Id，一二级标品Id存在一个即可 */
  standard_ids?: number[];
  /** 生效时间 */
  effective_time?: string[];
  /** 到期时间 */
  expiry_time?: string[];
  /** 状态 */
  status_list?: number[];
  /** 二级授权费用类型ID集合（内部过滤参数，非前端传入）#9510 */
  authorized_l2_category_ids?: number[];
};
export type PageVoPriceLibraryVo = {
  total?: number;
  records?: PriceLibraryVo[];
};
export type ResponsePageVoPriceLibraryVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoPriceLibraryVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 价格库审批查询入参
 */
export type PriceLibraryApprovalQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 费用类型Id，一二三级需求类型Id存在一个即可 */
  category_ids?: number[];
  /** 标品编码Id，一二级标品Id存在一个即可 */
  standard_ids?: number[];
  /** 到期时间 */
  expiry_time?: string[];
  /** OA流程号 */
  oa_approval_no?: string;
  /** 状态 */
  status_list?: number[];
  /** 二级授权费用类型ID集合（内部过滤参数，非前端传入）#9510 */
  authorized_l2_category_ids?: number[];
};
/**
 * 价格库新增入参
 */
export type PriceLibraryAddForm = {
  /** 费用类型一级 */
  l1_category_id?: number;
  /** 费用类型二级 */
  l2_category_id?: number;
  /** 费用类型三级 */
  l3_category_id?: number;
  /** 标品编码（一级） */
  standard1_id?: number;
  /** 标品编码（二级） */
  standard2_id?: number;
  /** 费用类型一级名称 */
  l1_category_name?: string;
  /** 费用类型二级名称 */
  l2_category_name?: string;
  /** 费用类型三级名称 */
  l3_category_name?: string;
  /** 标品编码（一级）名称 */
  standard1_name?: string;
  /** 标品编码（二级）名称 */
  standard2_name?: string;
  /** 到期时间 */
  expiry_time: string;
  price_library_details?: PriceLibraryDetail[];
};
/**
 * 价格历史查询入参
 */
export type PriceHistoryQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 标品库ID */
  standard_library_id: number;
  /** OA流程号 */
  oa_approval_no?: string;
  /** 状态 */
  status_list?: number[];
  /** 生效时间 */
  effective_time?: string[];
  /** 到期时间 */
  expiry_time?: string[];
};
/**
 * 价格记录
 */
export type PriceHistoryVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 标品库ID */
  standard_library_id?: number;
  /** OA审批流程号 */
  oa_approval_no?: string;
  /** OA跳转链接 */
  oa_url?: string;
  status?: PriceLibraryStatusEnum;
  /** 生效时间 */
  effective_time?: string;
  /** 到期时间 */
  expiry_time?: string;
  /** 等级详情信息 */
  level_info?: PriceLibraryLevelDetail[];
};
export type PageVoPriceHistoryVo = {
  total?: number;
  records?: PriceHistoryVo[];
};
export type ResponsePageVoPriceHistoryVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoPriceHistoryVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * Blob信息
 */
export type BlobVo = {
  /** Blob ID */
  id?: number;
  /** 文件名 */
  filename?: string;
  /** 文件大小（字节） */
  byte_size?: number;
  /** 内容类型 */
  content_type?: string;
  /** 存储key */
  key?: string;
};
/**
 * 附件信息
 */
export type AttachmentVo = {
  /** 附件ID */
  id?: number;
  blob?: BlobVo;
};
/**
 * 基本信息
 */
export type BaseInfo = {
  /** 预付单ID */
  id?: number;
  /** 预付单编号 */
  prepayment_no?: string;
  /** 关联需求单ID */
  demand_id?: number;
  /** 预付金额 */
  prepayment_amount?: number;
  /** 币种 */
  currency?: string;
  /** 预付单状态: 1-待发起 2-发票待上传 3-审批中 4-付款中 5-已完成 6-已废弃 */
  status?: number;
  /** 预付单状态描述 */
  status_desc?: string;
  /** 申请简介 */
  apply_summary?: string;
  /** 申请内容详情 */
  apply_content?: string;
  /** 预付截止时间 */
  prepayment_deadline?: string;
  /** 供应商ID */
  supplier_id?: number;
  /** 供应商名称 */
  supplier_name?: string;
  /** 创建人ID */
  created_by?: number;
  /** 创建人姓名 */
  creator_name?: string;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** 预付单附件列表 */
  attachments?: AttachmentVo[];
  /** 开票主体 */
  invoice_entity?: string;
};
/**
 * 发票项信息
 */
export type InvoiceItemVo = {
  /** 发票ID */
  invoice_id?: number;
  /** 发票号码 */
  invoice_no?: string;
  /** 发票代码 */
  invoice_code?: string;
  /** 含税金额 */
  amount_with_tax?: number;
  /** 不含税金额 */
  amount_without_tax?: number;
  /** 税额 */
  tax?: number;
  /** 发票状态 */
  invoice_state?: string;
  /** 发票状态描述 */
  invoice_state_desc?: string;
  /** 发票类型 */
  invoice_type?: string;
  /** 币种 */
  currency?: string;
  /** 发票附件列表 */
  attachments?: AttachmentVo[];
};
/**
 * 发票信息
 */
export type InvoiceInfo = {
  /** 发票方式: 1-有票预付 2-无票预付 */
  invoice_method?: number;
  /** 发票方式描述 */
  invoice_method_desc?: string;
  /** 发票状态: 0-未上传 1-已上传 */
  invoice_status?: number;
  /** 发票状态描述 */
  invoice_status_desc?: string;
  /** 发票地区: 1-国内 2-海外 */
  invoice_belong_area?: number;
  /** 发票地区描述 */
  invoice_belong_area_desc?: string;
  /** 是否海外INVOICE */
  is_oversea_invoice?: boolean;
  /** 关联发票列表 */
  invoice_list?: InvoiceItemVo[];
};
/**
 * 合同信息
 */
export type ContractInfo = {
  /** 合同ID */
  contract_id?: number;
  /** 合同编号 */
  contract_no?: string;
  /** 合同名称 */
  contract_name?: string;
};
/**
 * 费控付款信息
 */
export type PaymentInfo = {
  /** 费控单号 */
  expense_control_no?: string;
  /** 费控展示 */
  doc_view_url?: string;
  /** 费控单创建时间 */
  expense_control_created_time?: string;
  /** 付款时间 */
  payment_time?: string;
  /** 付款金额 */
  payment_amount?: number;
  /** 币种 */
  currency?: string;
  /** 付款发起人ID */
  payment_initiator_id?: number;
  /** 付款发起人姓名 */
  payment_initiator_name?: string;
  /** 付款发起人邮箱 */
  payment_initiator_email?: string;
  /** 付款状态 */
  payment_status?: string;
  /** 付款状态描述 */
  payment_status_desc?: string;
};
/**
 * OA审批信息项
 */
export type OaAuditInfo = {
  /** 审批流程类型，如: PREPAYMENT_APPROVE */
  process_type?: string;
  /** 审批流程类型描述 */
  process_type_desc?: string;
  /** OA流程ID */
  oa_id?: string;
  /** OA流程链接 */
  oa_url?: string;
  /** 提交人姓名 */
  submit_user_name?: string;
  /** 提交人邮箱 */
  submit_user_email?: string;
  /** 提交时间 */
  submit_time?: string;
  /** OA审批状态: SUBMITTED-审批中 ARCHIVING-已归档 REJECTED-已驳回 NOT_SUBMITTED-未提交 */
  oa_status?: string;
  /** OA审批状态描述 */
  oa_status_desc?: string;
  /** OA驳回原因 */
  oa_reject_reason?: string;
  /** 关联需求ID */
  demand_id?: number;
};
/**
 * 操作权限信息
 */
export type PermissionInfo = {
  /** 是否可操作（提交、上传发票、废弃任一操作） */
  can_move?: boolean;
  /** 是否可提交审批 */
  can_submit_approval?: boolean;
  /** 是否可上传发票 */
  can_upload_invoice?: boolean;
  /** 是否可废弃 */
  can_abandon?: boolean;
};
/**
 * 预付单详情
 */
export type PrepaymentDetailVo = {
  base_info?: BaseInfo;
  invoice_info?: InvoiceInfo;
  contract_info?: ContractInfo;
  payment_info?: PaymentInfo;
  /** OA审批信息列表 */
  oa_audit_info_list?: OaAuditInfo[];
  permission_info?: PermissionInfo;
};
export type ResponsePrepaymentDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PrepaymentDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 发票方式：1-有票预付（先票后款），2-无票预付（先款后票）
 */
export type InvoiceMethodEnum = 'INVOICED' | 'UNINVOICED';
/**
 * 提交预付单命令
 */
export type PrepaymentSubmitCmd = {
  /** 需求单ID */
  demand_id: number;
  /** 预付金额 */
  prepayment_amount: number;
  invoice_method: InvoiceMethodEnum;
  /** 预付截止时间，精确到日，只能选择未来时间 */
  prepayment_deadline: string;
  /** 申请简介，限制20字 */
  apply_summary: string;
  /** 申请内容详情，限制200字 */
  apply_content: string;
  /** 附件ID列表，支持多文件上传 */
  attachment_ids?: number[];
};
/**
 * 更新预付单命令（OA驳回后重新发起）
 */
export type PrepaymentUpdateCmd = {
  /** 预付单ID */
  prepayment_id: number;
  /** 预付金额 */
  prepayment_amount: number;
  invoice_method: InvoiceMethodEnum;
  /** 预付截止时间，精确到日，只能选择未来时间 */
  prepayment_deadline: string;
  /** 申请简介，限制20字 */
  apply_summary: string;
  /** 申请内容详情，限制200字 */
  apply_content: string;
  /** 附件ID列表，支持多文件上传 */
  attachment_ids?: number[];
};
/**
 * 操作日志请求对象
 */
export type JournalReqDto = {
  id?: number;
  op_type?: JournalOpTypeEnum;
};
/**
 * 流程节点对象
 */
export type FlowNode = {
  /** 是否是当前节点 */
  is_current?: boolean;
  /** 状态节点key */
  key?: string;
  /** 操作名称，可能为空 */
  operate_name?: string;
  /** 状态节点名称 */
  state_name?: string;
  /** 操作时间 */
  operate_time?: string;
  operate_user?: UserSimpleVo;
  /** 操作耗时 */
  elapsed_time?: number;
};
/**
 * 变更类型
 */
export type JournalDetailChangeType =
  | 'JOURNAL_CHANGED_NO_DETAIL'
  | 'JOURNAL_CHANGED'
  | 'JOURNAL_DELETED'
  | 'JOURNAL_SET_TO'
  | 'JOURNAL_CREATED';
export type PayAdvancedDto = {
  pay_advanced?: boolean;
};
/**
 * 参数类型
 */
export type JournalDetailValueArgType = 'AMOUNT' | 'PROJECT' | 'USER';
/**
 * 占位符参数
 */
export type BaseValueArg = {
  /** 参数key */
  key?: string;
  type?: JournalDetailValueArgType;
  /** 文本 */
  text?: string;
};
/**
 * 节点记录详情对象
 */
export type JournalDetailAo = {
  /** 字段 */
  field?: string;
  property?: string;
  property_id?: number;
  /** 旧值 */
  old_value?: string;
  /** 新值 */
  value?: string;
  change_type?: JournalDetailChangeType;
  /** 属性key */
  prop_key?: string;
  extra_keys?: PayAdvancedDto;
  /** 新值占位符参数 */
  value_args?: BaseValueArg[];
  /** 旧值占位符参数 */
  old_value_args?: BaseValueArg[];
};
/**
 * 当前节点记录
 */
export type JournalVo = {
  operator?: string;
  operate_user?: UserSimpleVo;
  state?: string;
  created_at?: string;
  pass?: boolean;
  note?: string;
  details?: JournalDetailAo[];
  op_type?: JournalOpTypeEnum;
  op_type_name?: string;
};
/**
 * 状态通知记录
 */
export type StateNotifyRecordDto = {
  id?: number;
  notify_id?: number;
  state?: string;
  notify_type?: string;
  updated_at?: string;
};
/**
 * 操作日志中的通知信息
 */
export type NotifyResDto = {
  /** 是否可以提醒 */
  can_remind?: boolean;
  state_notify_record?: StateNotifyRecordDto;
};
/**
 * 需求单/小喇叭/比选
 */
export type JournalType =
  | 'demand'
  | 'demand_plan_info'
  | 'demand_principal'
  | 'demand_planner'
  | 'demand_supplier'
  | 'demand_source_supplier_sign_kol'
  | 'demand_supplier_quote'
  | 'demand_supplier_score'
  | 'demand_member'
  | 'demand_fee_control_record'
  | 'demand_expectation_kpi_indicator'
  | 'demand_plan_kpi_value'
  | 'demand_rounds'
  | 'bidding'
  | 'bidding_judge_review'
  | 'bidding_member'
  | 'bidding_supplier'
  | 'bidding_supplier_quote'
  | 'bidding_supplier_score'
  | 'bidding_supplier_score_group'
  | 'bidding_confirm_status_record'
  | 'trumpet_demand'
  | 'trumpet_member'
  | 'closed_loop_demand'
  | 'closed_loop_member'
  | 'demand_kpi_adjustment_order'
  | 'prepayment'
  | 'budget_adjust_order'
  | 'permission_application';
/**
 * 操作详细日志
 */
export type JournalResDto = {
  /** 流程节点mapNodes */
  map_nodes?: FlowNode[];
  /** 当前节点记录 */
  journals?: JournalVo[];
  /** 通知操作人 */
  notify_operate_user?: string;
  /** 通知操作人列表 */
  notify_operate_user_list?: UserSimpleVo[];
  reminded_msg?: NotifyResDto;
  journal_type?: JournalType;
};
export type ResponseJournalResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: JournalResDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 预付单废弃命令
 */
export type PrepaymentAbandonCmd = {
  /** 预付单ID */
  prepayment_id: number;
  /** 废弃原因（非必填） */
  reason?: string;
};
export type PlannerGroupUpdateForm = {
  /** 策划组Id */
  id: number;
  /** 一级需求分类 */
  l1_category_id?: number;
  /** 二级需求分类 */
  l2_category_ids?: number[];
  /** 策划UserId */
  user_ids?: number[];
  /** 是否启用 */
  enabled?: boolean;
};
/**
 * 策划组分页查询
 */
export type PlannerGroupPageQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 二级需求分类 */
  l2_category_ids?: number[];
  /** 策划人员姓名/邮箱 模糊匹配 */
  user_name_email_fuzzy?: string;
  /** 是否启用 */
  enabled?: boolean;
};
/**
 * 使用范围
 */
export type DemandCategoryScopeEnum = 'ONLY_TRANS' | 'NORMAL' | 'FOR_FILTER';
/**
 * 方案审批支持类型
 */
export type DemandCategoryPlanApproveEnum =
  | 'NOT_SUPPORT'
  | 'CAN_SUPPORT'
  | 'MUST_SUPPORT';
export type CooperateAccountConfig = {
  cooperate_kpi_flag?: number;
  common_kpi_flag?: number;
};
/**
 * 需求分类
 */
export type DemandCategory = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  /** 分类名称 */
  name?: string;
  /** root_id */
  root_id?: number;
  /** parent_id */
  parent_id?: number;
  /** 树左值 */
  lft?: number;
  /** 树右值 */
  rgt?: number;
  /** 树层级（0开始） */
  level?: number;
  /** 是否删除 */
  deleted?: boolean;
  scope?: DemandCategoryScopeEnum;
  /** 创建人ID */
  created_by?: number;
  /** 修改人ID */
  updated_by?: number;
  /** 排序， 从小到大 */
  sort?: number;
  /** 是否广告消耗结算类别，0否，1是 */
  ad_consume?: boolean;
  /** 是否小喇叭可用类别，0否，1是 */
  trumpet?: boolean;
  /** 是否营销闭环可用类别，0否，1是 */
  closed_loop?: boolean;
  /** 配置 */
  cooperate_account_config?: string;
  /** 全路径名 */
  full_name?: string;
  plan_approve?: DemandCategoryPlanApproveEnum;
  /** 禁用的采购类型 */
  disable_purchasing_methods?: string;
  cooperate_account_config_object?: CooperateAccountConfig;
  disable_purchasing_method_values?: number[];
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
/**
 * 策划人员
 */
export type PlannerUserDto = {
  /** 策划人员id */
  id?: number;
  /** 策划人员姓名 */
  name?: string;
  /** 策划人员邮箱 */
  email?: string;
};
/**
 * 策划组
 */
export type PlannerGroupVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 策划组名称, 取二级需求分类名称, 中文顿号分隔 */
  name?: string;
  /** 一级需求分类id */
  l1_category_id?: number;
  l1_category?: DemandCategory;
  /** 二级需求分类id */
  l2_category_ids?: number[];
  /** 二级需求分类 */
  l2_categories?: DemandCategory[];
  /** 策划人员 */
  users?: PlannerUserDto[];
  /** 是否启用 */
  enabled?: boolean;
};
export type PageVoPlannerGroupVo = {
  total?: number;
  records?: PlannerGroupVo[];
};
export type ResponsePageVoPlannerGroupVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoPlannerGroupVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 策划组二级需求分类选项查询表单
 */
export type PlannerGroupL2SelectionQueryForm = {
  /** 策划组id */
  id?: number;
  /** 一级需求分类id */
  l1_category_id: number;
};
/**
 * 策划组二级需求分类选项
 */
export type PlannerGroupL2SelectionItemDto = {
  /** 二级需求分类id */
  l2_category_id?: number;
  /** 二级需求分类名称 */
  l2_category_name?: string;
  /** 是否已被关联, 默认可以选择 */
  can_select?: boolean;
};
/**
 * 策划组二级需求分类选项
 */
export type PlannerGroupL2SelectionVo = {
  l2_selections?: PlannerGroupL2SelectionItemDto[];
  users?: PlannerUserDto[];
};
export type ResponsePlannerGroupL2SelectionVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PlannerGroupL2SelectionVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type PlannerGroupCreateForm = {
  /** 一级需求分类 */
  l1_category_id: number;
  /** 二级需求分类 */
  l2_category_ids: number[];
  /** 策划UserId */
  user_ids: number[];
  /** 是否启用 */
  enabled?: boolean;
};
export type ExchangeRateDto = {
  from_currency?: CurrencyEnum;
  to_currency?: CurrencyEnum;
  /** 汇率 */
  exchange_rate?: number;
};
export type ResponseListExchangeRateDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ExchangeRateDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 协作人员验证表单
 */
export type MuseCollaboratorVerifyForm = {
  /** 设计师邮箱 */
  email: string;
  /** 验证码（协作码） */
  collaborate_code: string;
};
/**
 * 协作人员验证结果
 */
export type MuseCollaboratorVerifyResultVo = {
  /** 验证是否通过 */
  success?: boolean;
  /** 失败原因（验证通过时为null） */
  fail_reason?: string;
  /** 需求单ID */
  demand_id?: number;
};
export type ResponseMuseCollaboratorVerifyResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseCollaboratorVerifyResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 考核指标-调整后
 */
export type Kpi = {
  /** 序号 */
  index_id: number;
  /** kpi的编码 */
  kpi_code: string;
  /** KPI期望值 */
  expect_value: string;
};
export type KpiAdjustmentSubmitCmd = {
  /** Kpi调整单的Id */
  kpi_adjustment_order_id?: number;
  /** 需求单Id */
  demand_id?: number;
  /** 调整原因 */
  adjustment_reason?: string;
  /** 需求预期-调整前 */
  expectation_before?: string;
  /** 需求预期-调整后 */
  expectation_after?: string;
  /** 考核指标-调整后 */
  kpi_after?: Kpi[];
  /** 附件列表 */
  attachment_ids?: number[];
};
export type KpiAdjustApprovalCmdVoid = {
  /** 需求id */
  id: number;
  /** 备注 */
  note?: string;
  state?: KpiAdjustStateEnum;
  /** 审批时提交的业务表单 */
  biz_cmd?: object;
};
/**
 * superiorResourceShips
 */
export type DemandDetailsResDtoSuperiorResourceShipDto = {
  id?: number;
  superior_resource_id?: number;
  resourceable_type?: string;
  resourceable_id?: number;
  name?: string;
};
/**
 * 条件不符合后的处理
 */
export type RequiredModeEnum = 0 | 1 | 2 | 3;
export type RequiredConditionData = {
  /** 条件表达式 */
  express?: string;
  /** 显示内容 */
  label?: string;
  /** 前端预存内容 */
  ext?: {
    [key: string]: any;
  };
  fallback?: RequiredModeEnum;
};
/**
 * 如果使用条件选项，则启用这个属性
 */
export type ConditionOptionsGroupData = {
  /** 条件表达式 */
  express?: string;
  /** 前端预存内容 */
  ext?: {
    [key: string]: any;
  };
  /** 选项列表 */
  options?: string[];
};
export type ConditionOptionsData = {
  /** 如果使用条件选项，则启用这个属性 */
  groups?: ConditionOptionsGroupData[];
  /** 如果不使用条件，则启用这个列表 */
  options?: string[];
  /** 是否使用条件组 */
  use_condition?: boolean;
  /** 回显内容 */
  label?: string;
};
export type DemandCooperatePropertyTypeEnum =
  | 'dropdown_list'
  | 'dropdown_radio'
  | 'numeric'
  | 'date_input'
  | 'date_range'
  | 'text'
  | 'text_with_upload'
  | 'input'
  | 'percentage';
/**
 * 只读
 */
export type PropertyRefDto = {
  index_id?: number;
  property_id?: number;
  required_mode?: RequiredModeEnum;
  required_condition?: RequiredConditionData;
  options?: ConditionOptionsData;
  property_name?: string;
  property_code?: string;
  property_type?: DemandCooperatePropertyTypeEnum;
  property_options?: string[];
  contract_attachment_visible?: boolean;
  inner_code?: string;
};
/**
 * 标品级别
 */
export type StandardCatalogLevelEnum = 1 | 2 | 3;
/**
 * 需求标品列表表单
 */
export type DemandStandardCatalogBizVo = {
  /** 标品Code */
  code?: string;
  /** 父标品Code */
  parent_code?: string;
  level?: StandardCatalogLevelEnum;
  /** 标品名称 */
  name?: string;
  /** 标品编码 */
  short_code?: string;
  /** 生效状态   1=生效,0=失效 */
  status?: number;
};
/**
 * 标品schema
 */
export type StandardSchema = {
  standard_codes?: string[];
  use_in_design?: boolean;
  use_in_quote?: boolean;
  required?: boolean;
  label?: string;
  outsource_modify?: boolean;
  options?: string;
  level?: number;
  code?: string;
  standards?: DemandStandardCatalogBizVo[];
};
/**
 * 【结构化】合作内容属性值
 */
export type DemandCooperatePropertyValueDto = {
  /** 序号，只读 */
  index_id?: number;
  property_schema?: PropertyRefDto;
  standard_schema?: StandardSchema;
  /** 属性code */
  property_code: string;
  /** 值 */
  value?: string[];
  /** 0=属性，1=标品 */
  type?: number;
  /** 是否禁止编辑 */
  disable_edit?: boolean;
};
/**
 * 标品schema
 */
export type TemplateStandardItemDto = {
  standard_ids?: number[];
  use_in_design?: boolean;
  use_in_quote?: boolean;
  required?: boolean;
  label?: string;
  outsource_modify?: boolean;
  options?: string;
  level?: number;
  code?: string;
  standards?: DemandStandardCatalogBizVo[];
};
/**
 * 标品属性值
 */
export type DemandStandardCatalogValueDto = {
  /** 标品code */
  property_code?: string;
  scheme?: TemplateStandardItemDto;
  /** 值 */
  value?: string[];
};
/**
 * 需求合作内容权益
 */
export type DemandCooperateRightsValueDto = {
  /** 需求合作内容ID */
  demand_cooperate_account_id?: number;
  /** 索引ID */
  index_id?: number;
  /** 类型（0=必要权益，1=期望权益） */
  type?: number;
  /** 属性code */
  rights_code?: string;
  /** 权益内容（覆盖值） */
  actual_content?: string;
  /** 权益库中的权益 */
  content?: string;
};
/**
 * 需求合作内容权益差异
 */
export type DemandCooperateDiffRightsValueDto = {
  /** 需求合作内容ID */
  demand_cooperate_account_id?: number;
  /** 索引ID */
  index_id?: number;
  /** 类型（0=必要权益，1=期望权益） */
  type?: number;
  /** 属性code */
  rights_code?: string;
  /** 权益内容（覆盖值） */
  actual_content?: string;
  /** 权益库中的权益 */
  content?: string;
  /** 差异类型（-1=无变化，0=新增，1=删除，2=修改） */
  diff_type?: number;
  /** 供应商填的属性code */
  supplier_rights_code?: string;
  /** 供应商填的权益内容 */
  supplier_content?: string;
};
/**
 * 供应商权益差异信息
 */
export type SupplierQuoteRights = {
  /** 供应商全称（渠道类型） */
  supplier_full_name?: string;
  /** demandSupplierId */
  demand_supplier_id?: number;
  /** 是否中选 */
  winning?: boolean;
  /** 差异信息 */
  diff_rights_value_dtos?: DemandCooperateDiffRightsValueDto[];
};
/**
 * 基准价
 */
export type BasePriceConfigDto = {
  /** 基准价类型名称 */
  source_name?: string;
  /** 基准价类型 */
  source_type?: string;
  /** 基准价类型 */
  base_price_type?: string;
  /** 基准价类型名称 */
  base_price_name?: string;
  /** 基准价金额 */
  base_price?: number;
  /** 基准价币种 */
  base_price_currency?: string;
};
export type DemandDetailsResDtoBlobDto = {
  id?: number;
  /** 文件名 */
  filename?: string;
  /** 文件大小 */
  byte_size?: number;
  /** 文件类型 */
  content_type?: string;
  key?: string;
  /** 是否自动生成 */
  auto_generated?: boolean;
  /** 下载链接 */
  download_url?: string;
};
/**
 * 文件内容
 */
export type DemandDetailsResDtoAttachmentDto = {
  id?: number;
  blob?: DemandDetailsResDtoBlobDto;
};
/**
 * 需求方核价备注
 */
export type CooperateAccountRemark = {
  /** 文本内容 */
  text?: string;
  /** 文件内容 */
  files?: DemandDetailsResDtoAttachmentDto[];
};
/**
 * 合作内容信息
 */
export type JournalDemandDetailsResDtoCooperateAccountDto = {
  id?: number;
  /** 序号 */
  index_id?: number;
  /** 需求信息的Id */
  demand_plan_info_id?: number;
  /** 账号 */
  account?: string;
  /** remark */
  remark?: string;
  /** 是否需要实际执行 */
  need_actual_execution?: boolean;
  /** superiorResourceShips */
  superior_resource_ships?: DemandDetailsResDtoSuperiorResourceShipDto[];
  /** 方案说明 */
  program_desc?: string;
  /** 内容上传超时时间标签 */
  delay_label?: string;
  /** 超时级别，0，1，2 */
  delay_grade?: number;
  /** 报价预填内容 - 合作内容维度 */
  prefill_quote_config?: string;
  /** 合作内容属性值 */
  property_values?: DemandCooperatePropertyValueDto[];
  /** 标品属性值 */
  standard_catalog_values?: DemandStandardCatalogValueDto[];
  /** 【结构化】kpi指标列表 */
  kpi_values?: DemandKpiValueDto[];
  /** 必要权益列表 */
  necessary_rights?: DemandCooperateRightsValueDto[];
  /** 期望权益列表 */
  expect_rights?: DemandCooperateRightsValueDto[];
  /** 供应商期望权益列表，只有三级单会有，存储的是三级单对应的供应商期望权益 */
  supplier_expect_rights_list?: DemandCooperateRightsValueDto[];
  /** 期望权益对比数据列表 */
  expect_rights_diff_list?: SupplierQuoteRights[];
  base_price_config?: BasePriceConfigDto;
  business_price_check_remark?: CooperateAccountRemark;
  plan_price_check_remark?: CooperateAccountRemark;
  principal_price_check_remark?: CooperateAccountRemark;
  /** 策划确认达人 */
  plan_confirm?: boolean;
  /** 是否需包含AI */
  need_ai?: boolean;
  /** 议价优惠金额（提前核价自动带入，支持编辑） */
  bargaining_discount?: string;
  /** 达人合作模式 */
  kol_cooperation_mode?: string;
  /** 提前核价带入标签（如：已同步提前核价 / 与提前核价结果不一致），只读展示 */
  apc_carryover_tag?: string;
  /** 提前核价带入 tooltip（仅部分一致场景，只读展示） */
  apc_carryover_tooltip?: string;
};
/**
 * 考核指标-操作类型
 */
export type DiffTypeEnum = 'SAME' | 'ADD' | 'DELETE' | 'MODIFY';
/**
 * kpi调整单信息
 */
export type KpiAdjustmentPlanKpiAfterDto = {
  /** 二级单Id */
  demand_id?: number;
  /** 合作内容信息 */
  cooperate_accounts?: JournalDemandDetailsResDtoCooperateAccountDto[];
  /** 调整批注说明 */
  kpi_adjustment_explain?: string;
  /** 序号 */
  index_id?: number;
  /** kpi的编码 */
  kpi_code?: string;
  /** 指标分类 */
  kpi_type?: string;
  /** 指标分类名称 */
  kpi_type_name?: string;
  kpi_schema?: DemandKpiSchemaData;
  diff_type?: DiffTypeEnum;
  /** KPI期望值-调整前 */
  expect_before?: string;
  /** KPI期望值-调整后 */
  expect_after?: string;
};
/**
 * 审批时提交的业务表单
 */
export type KpiAdjustPlanApprovalCmd = {
  /** 附件的Id */
  adjustment_attachment_id?: number;
  /** 是否调整二级单指标 */
  adjustment_plan_demand_kpi_flag?: boolean;
  /** kpi调整单信息 */
  plan_kpi_after?: KpiAdjustmentPlanKpiAfterDto[];
};
export type KpiAdjustApprovalCmdKpiAdjustPlanApprovalCmd = {
  /** 需求id */
  id: number;
  /** 备注 */
  note?: string;
  state?: KpiAdjustStateEnum;
  biz_cmd?: KpiAdjustPlanApprovalCmd;
};
export type KpiAdjustDeleteCmd = {
  /** KPi调整单Id */
  kpi_adjustment_id?: number;
};
export type GetKpiAdjustmentNodeRecordsCmd = {
  /** KPi调整单Id */
  id?: number;
};
export type GetKpiAdjustmentDetailCmd = {
  /** KPi调整单Id */
  kpi_adjustment_id?: number;
  /** 一级单的需求Id */
  demand_id?: number;
};
export type FlowFlagEnum =
  | 'OLD_ONLINE_PROCESS'
  | 'NO_SELECT'
  | 'BIND_EXECUTE_FLOW'
  | 'PRE_AFTER_AUDIT'
  | 'NORMAL_BIDDING_MIX_MPC'
  | 'NORMAL_MPC_OA'
  | 'NORMAL_MPC'
  | 'MULTI_QUOTA_PLAN_DEMAND';
/**
 * 产品区域
 */
export type ProductAreaEnum = 'DOMESTIC' | 'OVERSEAS';
/**
 * 状态节点
 */
export type DemandDetailsResDtoBottomTab = {
  tab_name?: string;
  value?: string;
};
/**
 * platformPrices
 */
export type PlatformPriceTypeEnum =
  | 'NONE'
  | 'COOPERATE_ACCOUNT'
  | 'PLATFORM_AND_OFFICIAL_PRIVATE';
/**
 * 关联二询单信息
 */
export type RelDemandDto = {
  id?: number;
  state_name?: string;
  start_date?: string;
  end_date?: string;
  budget_price?: number;
  quote_price?: number;
};
export type DemandSupplierSettleDetailDto = {
  id?: number;
  /** demand_id */
  demand_id?: number;
  /** supplier_id */
  supplier_id?: number;
  /** city */
  city?: string;
  /** price */
  price?: number;
  /** recorded_at */
  recorded_at?: string;
  /** code */
  code?: string;
  /** lock_version */
  lock_version?: number;
  /** demand_supplier_id */
  demand_supplier_id?: number;
  created_at?: string;
  updated_at?: string;
  /** last_sett_price */
  last_sett_price?: number;
};
/**
 * 需求人员评估得分
 */
export type DemandDetailsResDtoEvaluateScoreDto = {
  name?: string;
  score?: number;
  real_score?: number;
  percentage?: string;
};
/**
 * 需求人员
 */
export type DemandPrincipalDto = {
  id?: number;
  demand_id?: number;
  user_id?: number;
  user_name?: string;
  email?: string;
};
/**
 * 梯度 (T1/T2/T3/T4)
 */
export type GradientEnum =
  | 'T1'
  | 'T2'
  | 'T3'
  | 'T4'
  | 'T5'
  | 'T6'
  | 'T7'
  | 'T8'
  | 'T9'
  | 'T10'
  | 'T11'
  | 'T12'
  | 'T13'
  | 'T14'
  | 'T15';
export type DemandDetailsResDtoDemandPlannerDto = {
  id?: number;
  user_id?: number;
  demand_plan_info_id?: number;
  user_name?: string;
  email?: string;
};
export type MemberBusinessDto = {
  id?: number;
  name?: string;
  value?: number;
  email?: string;
};
export type DemandDetailsResDtoDemandQuotaDto = {
  id?: number;
  demand_id?: number;
  quota_type?: string;
  quota_alias?: string;
  quota_name?: string;
  quota_desc?: string;
  quota_value?: string;
  quota_real_value?: string;
  quota_effect_value?: string;
};
/**
 * 服务类型
 */
export type DemandDetailsResDtoServiceTypeDto = {
  name?: string;
  value?: string;
};
/**
 * 历史承接费用类型
 */
export type DemandDetailsResDtoDemandCategoryDto = {
  name?: string;
  value?: number;
};
/**
 * 供应商系统标签
 */
export type SupplierSysTagVo = {
  code?: string;
  /** 标签名称 */
  label?: string;
  /** 标签描述 */
  desc?: string;
};
/**
 * 标段供应商标签
 */
export type SegmentSupplierTagVo = {
  /** 供应商 ID */
  supplier_id?: number;
  /** 供应商编码 */
  supplier_code?: string;
  /** 中选类型：WINNING=中选，RESERVE=储备，OTHER=其他 */
  selection_type?: string;
  /** 中选类型：WINNING=中选，RESERVE=储备，OTHER=其他 */
  selection_type_name?: string;
  /** 有效期起 */
  valid_from?: string;
  /** 有效期止 */
  valid_to?: string;
};
/**
 * 策划阶段供应商DTO
 */
export type DemandDetailsResDtoDemandPlanSupplierDto = {
  /** 策划阶段供应商记录ID */
  id?: number;
  /** 关联 demand_plan_info 表ID */
  demand_plan_info_id?: number;
  /** 供应商ID */
  supplier_id?: number;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 供应商名称（主供应商名称） */
  supplier_name?: string;
  /** 供应商全名（带渠道类型） */
  supplier_full_name?: string;
  /** 供应商简称 */
  supplier_short_name?: string;
  /** 渠道类型编码 */
  channel_type?: string;
  /** 渠道类型名称 */
  channel_type_name?: string;
  /** 风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 系统标签 */
  sys_tags?: SupplierSysTagVo[];
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注数量 */
  risk_remark_count?: number;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 风险拦截标记 */
  risky?: boolean;
  segment_supplier_tag?: SegmentSupplierTagVo;
};
/**
 * 策划单详情
 */
export type JournalDemandDetailsResDtoDemandPlanInfoDto = {
  id?: number;
  demand_id?: number;
  plan_type?: string;
  budget?: number;
  budget_currency?: CurrencyEnum;
  publish_date?: string;
  /** 开始时间 */
  begin_at?: string;
  /** 结束时间 */
  end_at?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
  translated_plan_type?: string;
  /** 整单验收要求 */
  accept_desc?: string;
  /** 续约批次 */
  renewal_count?: number;
  /** 是否需全部报价 */
  need_all_quote?: boolean;
  gradient?: GradientEnum;
  ad_settle_way?: string;
  ad_settle_way_name?: string;
  category_names?: string;
  cooperate_accounts?: JournalDemandDetailsResDtoCooperateAccountDto[];
  demand_planners?: DemandDetailsResDtoDemandPlannerDto[];
  plan_attachs?: DemandDetailsResDtoAttachmentDto[];
  reference_attachs?: DemandDetailsResDtoAttachmentDto[];
  purchase_attachs?: DemandDetailsResDtoAttachmentDto[];
  member_business?: MemberBusinessDto[];
  demand_quota_dtos?: DemandDetailsResDtoDemandQuotaDto[];
  /** 【结构化】【绑定执行数据】主要指标列表 */
  main_kpi_values?: DemandKpiValueDto[];
  /** 【结构化】【绑定执行数据】其他指标列表 */
  other_kpi_values?: DemandKpiValueDto[];
  /** 【结构化】【内容营销】指标列表 */
  common_kpi_values?: DemandKpiValueDto[];
  /** 【结构化】【绑定执行数据】辅助自定义指标范围 */
  auxiliary_kpi_values?: DemandKpiValueDto[];
  /** 整单验收要求-列表 */
  accept_list?: string[];
  /** 报价预填内容 */
  prefill_quote_data?: string;
  /** 报价模板版本号 */
  category_template_version?: number;
  /** 服务类型 */
  service_type_list?: DemandDetailsResDtoServiceTypeDto[];
  /** 历史承接费用类型 */
  history_category_list?: DemandDetailsResDtoDemandCategoryDto[];
  demand_plan_supplier?: DemandDetailsResDtoDemandPlanSupplierDto;
  /** 是否预算调整发起（true=该二级单由预算调整流程创建） */
  budget_adjust_split?: boolean;
  /** 是否追加预算（true=是，false=否） */
  add_budget?: boolean;
  /** 关联需求二级单 ID（addBudget=true 时必填） */
  related_id?: number;
  /** 关联需求二级单 ID 列表（addBudget=true 时必填，取代 relatedId） */
  related_ids?: number[];
};
/**
 * 合同来源，前端目前无需关注，仅后端数据层处理依赖
 */
export type ContractSourceTypeEnum = 'REUSE' | 'NEW_CREATE';
/**
 * 合同签约类型，前端目前无需关注，仅后端数据层处理依赖
 */
export type ContractSignTypeEnum = 'online' | 'offline';
/**
 * 参与方, 策划/商务/需求方, 一次提交仅支持一方
 */
export type ParticipantType = 'planner' | 'business' | 'demander';
/**
 * 供应商报价评分明细
 */
export type DemandSupplierScoreDetailDto = {
  /** 项目 */
  item?: string;
  /** 权重 */
  weight?: number;
  /** 评分 */
  score?: number;
};
/**
 * 供应商报价评分
 */
export type DemandSupplierScoreDto = {
  participant_type?: ParticipantType;
  /** 参与方权重 */
  weight?: number;
  /** 参与方评分 */
  score?: number;
  /** 评分明细 */
  detail?: DemandSupplierScoreDetailDto[];
  /** 优势(描述) */
  pros?: string;
  /** 劣势(描述) */
  cons?: string;
  /** 评分人 */
  user_id?: number;
  user?: UserSimpleVo;
};
/**
 * 核价备注
 */
export type PriceCheckRemark = {
  /** 文本内容 */
  text?: string;
  /** 文件内容 */
  files?: DemandDetailsResDtoAttachmentDto[];
};
export type DemandDetailsResDtoDemandSupplierQuoteDto = {
  id?: number;
  /** demand_supplier_id */
  demand_supplier_id?: number;
  /** cooperate_account_id */
  cooperate_account_id?: number;
  /** quote_price */
  quote_price?: number;
  currency?: CurrencyEnum;
  /** winning */
  winning?: boolean;
  /** requote_price */
  requote_price?: number;
  /** last_quote_price */
  last_quote_price?: number;
  translated_currency?: string;
  created_at?: string;
  updated_at?: string;
  /** 方案说明 */
  program_desc?: string;
  /** 当前价格和上次对比是否变更 */
  current_price_change?: string;
  /** 报价金额(优惠前、不含税) */
  quote_price_no_tax?: number;
  /** 报价金额(优惠前、含税和服务费) */
  quote_price_with_tax?: number;
  /** 报价金额(优惠后、不含税) */
  discount_quote_price_no_tax?: number;
  /** 报价金额(优惠后、含税和服务费) */
  discount_quote_price_with_tax?: number;
  /** 报价详情附件 */
  quote_attachments?: DemandDetailsResDtoAttachmentDto[];
  /** 合作内容维度的结案报告附件列表 */
  case_close_report_cooperate_account_attachments?: DemandDetailsResDtoAttachmentDto[];
  /** 单项分数 */
  score?: number;
  /** 浮点数评分 */
  precise_score?: number;
  business_score?: DemandSupplierScoreDto;
  demander_score?: DemandSupplierScoreDto;
  planner_score?: DemandSupplierScoreDto;
  /** 是否需要实际执行 */
  need_actual_execution?: boolean;
  /** 是否含差旅餐补等实报实销，1=是，0=否 */
  contain_travel_expenses_reimbursement?: boolean;
  /** 是否星图度量测试，true=是，false=否 */
  xingtu_measure_test?: boolean;
  /** 抖音分发是否星图度量，true=是，false=否 */
  douyin_distribute_xingtu_measure?: boolean;
  /** 分批的结算金额 */
  settle_price?: number;
  /** 差异金额 */
  settle_price_and_quote_price_diff_amount?: number;
  /** 供应商提报的结算金额 */
  supplier_settle_price?: number;
  /** 期望权益 */
  expect_rights?: string;
  /** 报价模板快照，到标品维度 */
  template_detail?: string;
  /** 溢价比 */
  premium_ratio?: string;
  /** 核价结果 */
  price_check_result?: string;
  price_check_remark?: PriceCheckRemark;
  /** 系统核价结果 */
  system_price_check_result?: string;
  /** 比稿折扣 */
  bi_gao_discount?: number;
  /** 比稿税服 */
  bi_gao_tax?: number;
  /** 报价是否含AI */
  quote_contain_ai?: boolean;
  /** AI渗透率 */
  ai_penetration_rate?: number;
  /** AI降本率 */
  ai_cost_reduction_rate?: number;
};
/**
 * 发票类型
 */
export type InvoiceType =
  | 'INVOICE_10100'
  | 'INVOICE_10100A'
  | 'INVOICE_10101'
  | 'INVOICE_10102'
  | 'INVOICE_10102A'
  | 'INVOICE_10102B'
  | 'INVOICE_10103'
  | 'INVOICE_10104'
  | 'INVOICE_10400'
  | 'INVOICE_10400A'
  | 'INVOICE_10506'
  | 'INVOICE_10503'
  | 'INVOICE_10505'
  | 'INVOICE_10505A'
  | 'INVOICE_10500'
  | 'INVOICE_10200'
  | 'INVOICE_20105'
  | 'INVOICE_10107'
  | 'INVOICE_10108'
  | 'INVOICE_99999'
  | 'INVOICE_90001'
  | 'INVOICE_10090'
  | 'INVOICE_10091'
  | 'INVOICE_10700'
  | 'INVOICE_10600'
  | 'INVOICE_10800'
  | 'INVOICE_VIRTUAL';
/**
 * 发票国家/地区
 */
export type InvoiceBelongAreaEnum = 1 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 99;
/**
 * 需求单关联合同的方式
 */
export type DemandContractMethodEnum =
  | 'NO_NEED'
  | 'REUSE'
  | 'NEW_CREATE_ONLINE'
  | 'NEW_CREATE_MANUAL';
/**
 * 供应商侧状态
 */
export type MatchOutsourceStatusEnum =
  | 'NEWLY_BUILT'
  | 'PENDING_QUOTE'
  | 'NOT_QUOTED'
  | 'CONFIRM_QUOTE'
  | 'NOT_SELECTED'
  | 'SELECTED'
  | 'MAKING'
  | 'REVIEW_QUOTE'
  | 'SETTLED'
  | 'SETTLED_UPLOAD'
  | 'PAYING'
  | 'PAYED'
  | 'CANCELED'
  | 'MY_ADVANCE'
  | 'REJECT'
  | 'COMPLETED';
/**
 * 渠道供应商银行账号信息
 */
export type ChannelSupplierBankDto = {
  /** 银行账号/IBAN */
  bank_account?: string;
  bank_language?: LanguageEnum;
  /** 银行账号唯一code，来源于VMDM */
  bank_business_code?: string;
  /** 银行名称 */
  bank_name?: string;
  /** 银行账号名称 */
  bank_account_name?: string;
  /** 银行国家名称 */
  bank_country_name?: string;
  /** 银行国家编码 */
  bank_country_code?: string;
  /** 银行开户行省 */
  bank_province?: string;
  /** 银行开户行市 */
  bank_city?: string;
  /** Swift Code */
  swift_code?: string;
  /** 银行支行名称 */
  bank_branch_name?: string;
};
/**
 * 供应商指标值
 */
export type SupplierKpiValueVo = {
  /** 供应商的id */
  supplier_id?: number;
  /** 供应商指标项目名称 */
  item_name?: string;
  /** 供应商指标类型，0=默认规则，1=自定义规则 */
  type?: number;
  /** 指标值 */
  result_value?: number;
  /** 样本数量 */
  sample_count?: number;
};
/**
 * 平台核价配置列表
 */
export type PlatformPriceGroupVo = {
  /** 合作主平台 */
  main_platforms?: string[];
  /** 主平台官单/私单 */
  platform_types?: string[];
  /** 比稿折扣 */
  bi_gao_discount?: number;
  /** 比稿税服 */
  bi_gao_tax?: number;
};
/**
 * 续签供应商列表
 */
export type DemandDetailsResDtoDemandSupplierDto = {
  id?: number;
  /** demand_id */
  demand_id?: number;
  /** supplier_id */
  supplier_id?: number;
  /** is_quoted */
  is_quoted?: boolean;
  /** is_self_contact */
  is_self_contact?: boolean;
  /** advance_info */
  advance_info?: string;
  /** 【20240704版本】【三级单】合同编号 */
  contract_no?: string;
  /** 合同名称 */
  contract_name?: string;
  /** no_contract_flag */
  no_contract_flag?: boolean;
  contract_source_type?: ContractSourceTypeEnum;
  contract_sign_type?: ContractSignTypeEnum;
  /** 报价附件的有效标识 */
  quote_attachment_valid_flag?: boolean;
  /** turns_number */
  turns_number?: number;
  /** overall_score */
  overall_score?: number;
  /** 商务核价状态（供应商维度：unchecked 未核价 / checked 已核价） */
  business_price_check_status?: string;
  created_at?: string;
  updated_at?: string;
  demand_supplier_quotes?: DemandDetailsResDtoDemandSupplierQuoteDto[];
  last_round_quotes?: DemandDetailsResDtoDemandSupplierQuoteDto[];
  /** 合并报价附件 */
  merge_quote_attachs?: DemandDetailsResDtoAttachmentDto[];
  bid_attachs?: DemandDetailsResDtoAttachmentDto[];
  /** 供应商名称，带渠道类型 */
  supplier_full_name?: string;
  /** 供应商名称，主供应商名称 */
  supplier_name?: string;
  /** VMDM code */
  mdm_code?: string;
  /** 供应商渠道类型 */
  supplier_channel_type_name?: string;
  contract_balance?: string;
  /** 【20240704版本】【三级单】处理后的合同编号 */
  contract_code?: string;
  supplier_short_name?: string;
  city?: string;
  /** 供应商评分 */
  score?: number;
  /** 报价类型:0=不含税金额；1=含税金额 */
  quote_type?: number;
  /** 税率 */
  tax_rate?: number;
  /** 是否收取服务费,0=不收取,1=收取 */
  service_fee_mode?: number;
  /** 服务费率 */
  service_fee_rate?: number;
  currency?: CurrencyEnum;
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注数量 */
  risk_remark_count?: number;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 风险拦截标记 */
  risky?: boolean;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 是否重新报价 */
  re_quotation_flag?: boolean;
  /** 重新报价原因 */
  re_quotation_reason?: string;
  /** 供应商系统标签 */
  sys_tags?: SupplierSysTagVo[];
  segment_supplier_tag?: SegmentSupplierTagVo;
  /** 拒单原因 */
  reason?: string;
  /** 是否被供应商拒单 */
  reject_by_supplier?: boolean;
  /** 是否超时未报价 */
  expired?: boolean;
  invoice_type?: InvoiceType;
  invoice_belong_area?: InvoiceBelongAreaEnum;
  /** 是否海外发票,1=海外发票,0=非海外发票 */
  is_oversea_invoice?: number;
  contract_method?: DemandContractMethodEnum;
  /** 在提交付款时填写合同 */
  fill_contract_on_submit_reimburse?: boolean;
  outsource_state?: MatchOutsourceStatusEnum;
  /** 是否被退回 */
  return_flag?: boolean;
  channel_supplier_bank_dto?: ChannelSupplierBankDto;
  /** 供应商分类，1(常用推荐)，2(寻源)，3(跨品类) */
  supplier_category?: number;
  /** 是否智能推荐的供应商 */
  smart_recommend?: boolean;
  /** 指标列表 */
  kpi_value_vos?: SupplierKpiValueVo[];
  /** 近期综合评分 */
  recent_overall_score?: string;
  /** 近期结果打分 */
  recent_result_score?: string;
  /** 平台核价配置列表 */
  platform_prices?: PlatformPriceGroupVo[];
  /** 发票国家/地区名称 */
  invoice_belong_area_name?: string;
  /** 发票类型名称 */
  invoice_type_name?: string;
};
export type DemandStateValue = {
  value?: DemandState;
  /** 名称 */
  human_name?: string;
};
/**
 * 流程操作信息
 */
export type DemandFlowActionInfoValue = {
  reject_options?: DemandStateValue[];
};
/**
 * 策划单详情
 */
export type DemandPlanInfoBaseDto = {
  id?: number;
  demand_id?: number;
  plan_type?: string;
  budget?: number;
  budget_currency?: CurrencyEnum;
  /** 开始时间 */
  begin_at?: string;
  /** 结束时间 */
  end_at?: string;
};
/**
 * 最终预算金额
 */
export type Money = {
  amount?: number;
  currency?: CurrencyEnum;
  empty?: boolean;
};
export type ChildDemandDto = {
  /** 主键 */
  id?: number;
  /** 报价金额 */
  quote_price?: string;
  quote_currency?: CurrencyEnum;
  /** 结算金额 */
  settle_price?: string;
  settle_currency?: CurrencyEnum;
  ad_settle_way?: string;
  ad_settle_way_name?: string;
  demand_plan_info?: DemandPlanInfoBaseDto;
  /** 状态名称 */
  human_state_name?: string;
  /** 子状态名称 */
  human_sub_state_name?: string;
  /** 子状态 */
  sub_state?: string;
  the_quote_price?: Money;
  the_settle_price?: Money;
  the_plan_info_budget_amount?: Money;
  /** 是否是综合评分法的二级单 */
  multi_quota_plan_demand?: boolean;
  /** 三级分类名称 */
  l3_category_name?: string;
  /** 投放渠道 */
  marketing_channel?: string;
  /** 投放形式 */
  marketing_type?: string;
  /** 需求详情 */
  detail_desc?: string;
  /** 是否可以编辑预算 */
  edit_budget?: boolean;
  plan_type?: DemandPlanTypeEnum;
};
/**
 * 子单统计信息
 */
export type DemandDetailsResDtoChildDemandSummaryDto = {
  total_quote_price?: Money;
  total_settle_price?: Money;
  total_budget_amount?: Money;
  total_budget_amount_balance?: Money;
};
export type MemberPlannerDto = {
  id?: number;
  name?: string;
  value?: number;
  email?: string;
};
export type MediaKpiType = 0 | 1 | 2 | 3;
/**
 * 指标类型
 */
export type DemandQuotaTypeEnum =
  | 'MAIN'
  | 'OTHER'
  | 'COMMON'
  | 'COOPERATE'
  | 'AUXILIARY';
/**
 * 别名
 */
export type DemandQuotaName = {
  name?: string;
  desc?: string;
  alias?: string;
  is_positive_evaluation?: boolean;
};
/**
 * 策划复核QC辅助指标列表
 */
export type DspfQuotaDataIndex = {
  /** 指标id */
  quota_id?: number;
  quota_type?: DemandQuotaTypeEnum;
  quota_alias?: DemandQuotaName;
  /** 指标名称 */
  quota_name?: string;
  /** 指标值 */
  quota_value?: number;
  /** 指标实际值 */
  quota_real_value?: number;
  /** 单位 */
  unit?: string;
  /** 是否百分比 */
  is_percent?: boolean;
  /** 是否是预估值 */
  is_predict?: boolean;
  /** 是否通过 */
  pass?: boolean;
  /** 是否销毁 */
  destroy?: boolean;
  /** 媒体效果指标值 */
  quota_effect_value?: number;
  /** 效果是否通过 */
  effect_pass?: boolean;
};
/**
 * 数易指标数据
 */
export type DspfQuotaData = {
  /** 数易报价金额 */
  dspf_offer_price?: number;
  dspf_offer_currency?: CurrencyEnum;
  /** 数易结算金额 */
  dspf_settle_price?: number;
  dspf_settle_currency?: CurrencyEnum;
  /** 最终结算金额 */
  final_settle_price?: number;
  final_settle_currency?: CurrencyEnum;
  /** 计提起始日期 */
  fee_start_date?: string;
  /** 计提结束日期 */
  fee_end_date?: string;
  /** 效果描述 */
  effect_desc?: string;
  /** 指标列表 */
  dspf_quota_data_index_list?: DspfQuotaDataIndex[];
  /** 策划复核QC辅助指标列表 */
  qc_data_index_list?: DspfQuotaDataIndex[];
};
/**
 * 备注，包含了风险信息
 */
export type Remark = {
  id?: number;
  node_name?: string;
  risk_info?: string;
  user?: UserSimpleVo;
  note?: string;
  attachment_list?: DemandDetailsResDtoBlobDto[];
};
/**
 * 风险提示&备注说明
 */
export type RemarksTree = {
  remarks?: Remark[];
};
/**
 * 验收备注
 */
export type AcceptRemarks = {
  remarks?: Remark[];
};
/**
 * QC验收优先级：0=老流程(NOT_SET)，1=新流程(PRIORITY)
 */
export type QcAcceptPriorityEnum = 0 | 1;
/**
 * NO 不支持kol，YES 支持kol
 */
export type KolSupportTypeEnum = 'NO' | 'YES';
/**
 * kol oa 审批数据
 */
export type OaResDto = {
  oa_id?: string;
  oa_url?: string;
  oa_reject_reason?: string;
  translated_oa_status?: string;
};
/**
 * 续约单类型
 */
export type RenewTypeEnum = 'NONE' | 'NORMAL' | 'TRUMPET';
/**
 * 需求详情额外字段
 */
export type RootDemandExtColumnDto = {
  /** 需求单名称 */
  name?: string;
  /** oa_rejection_reason */
  oa_rejection_reason?: string;
  /** oa流程号 */
  oa_id?: string;
  /** oa url */
  oa_url?: string;
  /** 前置审批OA状态 */
  translated_oa_status?: string;
  purchasing_method?: PurchasingMethodEnum;
  purchasing_strategy?: PurchasingStrategyEnum;
  /** 采购方式展示字符串 */
  translated_purchasing_method?: string;
  /** 预期 */
  expectation?: string;
  /** 申请理由, 采购方式如果为指定采购或特殊事项报备时必填 */
  apply_reason?: string;
  /** 使用规划, 采购方式如果为指定采购或特殊事项报备时必填 */
  usage_plan?: string;
  /** 需求背景 */
  background?: string;
  /** 供应商详情 */
  demand_suppliers?: DemandDetailsResDtoDemandSupplierDto[];
  support_kol?: KolSupportTypeEnum;
  /** kol使用规划 */
  kol_usage_plan?: string;
  /** Kol提交时的策划文件 */
  kol_plan_attachs?: DemandDetailsResDtoAttachmentDto[];
  /** 一级单新增的紧急需求补单审核材料 */
  urgent_order_restocking_attachs?: DemandDetailsResDtoAttachmentDto[];
  kol_oa_info?: OaResDto;
  /** 是否特殊事项报备流程 */
  special_matters_demand?: boolean;
  renew_type?: RenewTypeEnum;
  /** 常规采购续约单id */
  normal_renew_demand_id?: number;
  /** 小喇叭续约单id */
  trumpet_renew_demand_id?: number;
  /** 常规采购续约单标题 */
  normal_renew_demand_title?: string;
  /** 小喇叭续约单标题 */
  trumpet_renew_demand_title?: string;
  /** OA营销方案与预算审批（综合评分法）是否完成 */
  multi_quota_oa_approval_finished?: number;
  /** 综合评分法已中选二级单供应商列表 */
  multi_quota_suppliers?: DemandDetailsResDtoDemandSupplierDto[];
  /** 综合评分法中的合作内容列表 */
  multi_quota_cooperate_accounts?: JournalDemandDetailsResDtoCooperateAccountDto[];
  /** 综合评分法已中选二级单供应商中选报价金额 */
  multi_quota_suppliers_quote_amount?: number;
  multi_quota_suppliers_quote_currency?: CurrencyEnum;
  /** 综合评分法已中选二级单Id */
  multi_quota_demand_id?: number;
  /** 综合评分法已中选二级单名称 */
  multi_quota_demand_title?: string;
  /** 是否KPI特殊申报 */
  kpi_special_declaration_flag?: boolean;
  /** KPI特殊申报原因 */
  kpi_special_declaration_reason?: string;
  /** 低风险 */
  low_risk_special_report_flag?: boolean;
  /** 需求预期 kpi指标列表 */
  expectation_kpi_indicators?: DemandKpiValueDto[];
  /** 最后生效的调整单ID */
  latest_kpi_adjustment_id?: number;
  /** 一级需求类型ID */
  l1_category_id?: number;
  /** 一级需求类型名称 */
  l1_category_name?: string;
  /** 二级需求类型ID */
  l2_category_id?: number;
  /** 二级需求类型名称 */
  l2_category_name?: string;
  /** 是否展示KPI调整记录 */
  view_kpi_adjustment_record?: boolean;
  /** 是否有KPI调整 */
  has_kpi_adjustment?: boolean;
};
/**
 * 招采流程
 */
export type BiddingDto = {
  /** 招采流程id */
  id?: number;
  /** 状态名称 */
  human_state_name?: string;
};
/**
 * 退回状态枚举
 */
export type AuditQuoteReturnStatusEnum = 'OA_REJECTED';
/**
 * 费用归属主体
 */
export type CostCompanyDto = {
  id?: number;
  name?: string;
};
/**
 * 关联业务类型（如：小喇叭、比选）
 */
export type DemandRelateBizTypeEnum = 'TRUMPET' | 'BIDDING';
/**
 * 比选供应商信息
 */
export type BiddingSupplierInfo = {
  /** 供应商ID */
  supplier_id?: number;
  /** 供应商名称 */
  supplier_name?: string;
  /** 执行金额 */
  execution_amount?: number;
  execution_amount_currency?: CurrencyEnum;
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注数量 */
  risk_remark_count?: number;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 风险拦截标记 */
  risky?: boolean;
};
/**
 * 比选信息
 */
export type BiddingInfo = {
  /** 比选ID */
  id?: number;
  /** 供应商信息集合 */
  supplier_infos?: BiddingSupplierInfo[];
};
/**
 * 评分规则明细
 */
export type ScoreRuleDetailDto = {
  /** 项目 */
  item: string;
  /** 说明 */
  desc?: string;
  /** 权重 */
  weight: number;
};
/**
 * 供应商评分规则
 */
export type ScoreRuleDto = {
  type: ParticipantType;
  /** 权重 */
  weight: number;
  /** 评分规则明细 */
  detail: ScoreRuleDetailDto[];
};
/**
 * 供应商评分模板
 */
export type DemandSupplierScoreRuleDto = {
  /** 评分规则list */
  score_rules?: ScoreRuleDto[];
};
/**
 * 报价明细上传方式
 */
export type DemandQuoteUploadMethodEnum = 'SINGLE_UPLOAD' | 'MULTI_UPLOAD';
/**
 * 退回原因对象
 */
export type DemandRejectInfoVo = {
  reject_reason?: string;
  last_state?: string;
  last_state_name?: string;
};
/**
 * 合作内容结算信息
 */
export type CooperateAccountSettleInfo = {
  /** 合作内容序号 */
  index_id?: number;
  /** 合作内容ID */
  cooperate_account_id?: number;
  /** 供应商id */
  supplier_id?: number;
  /** 分配的结算金额 */
  settle_price?: number;
  /** 报价金额 */
  quote_price?: number;
  /** 差额,人民币 */
  difference?: number;
};
/**
 * 能否创建 KPI调整单的状态
 */
export type DemandKpiCreateStateEnum =
  | 'CAN_CREATE'
  | 'OA_PROCESSING'
  | 'CAN_NOT_CREATE';
/**
 * 需求分类模板类型
 */
export type DemandCategoryPropertyTemplateTypeEnum = 0 | 1 | 2;
/**
 * 评分进行中展示的评分进度
 */
export type DemandSupplierScoreProgressDto = {
  participant_type?: ParticipantType;
  /** 权重 */
  weight?: number;
  /** 评分人 */
  users?: UserSimpleVo[];
  /** 评分人id */
  user_ids?: number[];
  /** 评分状态, 0:未评分, 1:已评分 */
  score_state?: number;
  /** 评分时间 */
  score_time?: string;
  scored?: boolean;
};
/**
 * 发票详情DTO
 */
export type InvoiceDetailDto = {
  /** 发票ID，MPC侧生成的发票表主键ID，同一张发票被删除重新生成，ID会不同 */
  id?: number;
  /** 发票ID，M税务系统生成的发票ID，同一张发票被删除重新生成，ID会不同 */
  invoice_id?: number;
  /** 发票唯一Key，税务系统生成的唯一Key，同一张发票的key相同 */
  invoice_key?: string;
  /** 发票代码 */
  invoice_code?: string;
  /** 发票号码 */
  invoice_no?: string;
  /** 发票对应的主供应商名称 */
  supplier_name?: string;
  /** 发票状态 */
  invoice_state?: string;
  invoice_type?: InvoiceType;
  /** 发票含税总金额 */
  amount_with_tax?: number;
  /** 发票不含税金额 */
  amount_without_tax?: number;
  /** 发票税额 */
  tax?: number;
  /** 发票数据状态 1=生效,0=失效 */
  is_valid?: number;
  /** 更新时间 */
  updated_at?: string;
  /** 创建时间 */
  create_at?: string;
  /** 删除标记，0是未删除，负数是删除 */
  deleted_flag?: number;
  currency?: CurrencyEnum;
  /** 发票用途：0=需求单发票，1=预付单发票 */
  invoice_source?: number;
  /** 发票附件 */
  attachments?: AttachmentDto[];
  /** 购方名称（OCR 识别） */
  buyer_name?: string;
  /** 销方名称（OCR 识别） */
  seller_name?: string;
};
/**
 * 付款状态
 */
export type FeeControlDocStatusEnum =
  | 'SUBMITTED'
  | 'ARCHIVED'
  | 'DELETED'
  | 'PROCESSING'
  | 'COMPLETE'
  | 'REJECTED'
  | 'TERMINATED';
/**
 * 所有付款记录（包含付款单和预付单）
 */
export type DemandPaymentDocRecordDto = {
  /** 付款单类型：1-预付单，2-付款单 */
  payment_order_type?: number;
  /** MPC预付单号 */
  prepayment_no?: string;
  /** MPC预付单号 */
  prepayment_id?: number;
  /** 付款单平台 */
  platform?: string;
  /** 付款单号 */
  doc_number?: string;
  doc_status?: FeeControlDocStatusEnum;
  /** 易费控报销单查看页面url */
  doc_view_url?: string;
  /** 发起人姓名 */
  submit_user_name?: string;
  /** 发起人邮箱 */
  submit_user_email?: string;
  /** 付款时间 */
  pay_time?: string;
  /** 创建时间 */
  created_at?: string;
};
/**
 * 付款单信息
 */
export type DemandPaymentDocInfoDto = {
  latest_payment_doc_record?: DemandPaymentDocRecordDto;
  /** 付款记录信息 */
  payment_doc_records?: DemandPaymentDocRecordDto[];
  /** 所有付款记录（包含付款单和预付单） */
  payment_doc_record_all?: DemandPaymentDocRecordDto[];
};
/**
 * 计提记录信息
 */
export type DemandExpenseDocRecordDto = {
  /** 付款单平台 */
  platform?: string;
  /** 计提月份 */
  month?: string;
  /** 付款单号 */
  doc_number?: string;
  doc_status?: FeeControlDocStatusEnum;
  /** 易费控报销单查看页面url */
  doc_view_url?: string;
  /** 发起人姓名 */
  submit_user_name?: string;
  /** 发起人邮箱 */
  submit_user_email?: string;
  /** 计提金额 */
  expense_amount?: string;
  /** 计提冲销金额 */
  close_accrual_amount?: string;
  currency?: CurrencyEnum;
  /** 创建时间 */
  created_at?: string;
};
/**
 * 计提单信息
 */
export type DemandExpenseDocInfoDto = {
  /** 已计提总金额 */
  total_expense_amount?: number;
  /** 已冲销合计金额 */
  total_close_accrual_amount?: number;
  /** 计提记录信息 */
  expense_doc_records?: DemandExpenseDocRecordDto[];
};
/**
 * 预付单列表
 */
export type JournalDemandDetailsResDtoPrepaymentInfoDto = {
  /** 预付单ID */
  id?: number;
  /** MPC预付单号 */
  prepayment_no?: string;
  /** 状态 */
  status?: string;
  /** 状态描述 */
  status_desc?: string;
  /** 预付金额 */
  prepayment_amount?: number;
  /** 币种 */
  currency?: string;
  /** 发票方式 */
  invoice_method?: string;
  /** 发票方式描述 */
  invoice_method_desc?: string;
  oa_status?: OaState;
  /** OA流程号 */
  oa_process_no?: string;
  /** OA跳转链接 */
  oa_url?: string;
  /** 创建人姓名 */
  creator_name?: string;
  /** 创建时间 */
  created_at?: string;
};
/**
 * 周期聚合-风险等级：HIGH-高风险, MEDIUM-中风险, LOW-正常
 */
export type AlertLevelEnum = 'HIGH' | 'MEDIUM' | 'LOW';
/**
 * 人员信息
 */
export type MemberInfoVo = {
  /** 用户ID */
  user_id?: number;
  /** 姓名 */
  name?: string;
  /** 邮箱 */
  email?: string;
};
/**
 * 一级单预算汇总信息
 */
export type BudgetDemandSummaryVo = {
  /** 一级单ID */
  demand_id?: number;
  /** 一级单编号 */
  demand_number?: string;
  /** 一级单名称 */
  demand_name?: string;
  effective_budget?: Money;
  remaining_budget?: Money;
  current_total_fee?: Money;
  available_fee?: Money;
  daily_avg_fee?: Money;
  /** 预估剩余可用天数 */
  budget_support_days?: number;
  /** 预算进度（百分比 0-100） */
  budget_progress?: number;
  /** 时间进度（百分比 0-100） */
  time_progress?: number;
  /** 执行周期开始日期 */
  cycle_begin_at?: string;
  /** 执行周期结束日期 */
  cycle_end_at?: string;
  alert_level?: AlertLevelEnum;
  /** 一级需求类型ID */
  l1_category_id?: number;
  /** 一级需求类型名称 */
  l1_category_name?: string;
  /** 产品编码 */
  product_code?: string;
  /** 需求人员列表 */
  demanders?: MemberInfoVo[];
  /** 策划人员列表 */
  planners?: MemberInfoVo[];
  /** 是否开启风险预警 */
  alert_enabled?: boolean;
  /** 是否有预算变更记录（已生效的预算调整单） */
  has_adjust_record?: boolean;
  /** 当前一级单关联的待发起状态（PENDING）预算调整单 ID，无则为空。包含两类：① 该一级单作为发起单创建的待发起调整单；② 该一级单作为关联单参与的待发起调整单。若同时存在多个，取首条（业务上不允许同时多个在途） */
  pending_budget_adjust_order_id?: number;
};
/**
 * 数据类型：PLAN_DEMAND-二级单明细
 */
export type BudgetDataTypeEnum = 'PLAN_DEMAND';
/**
 * KPI指标信息
 */
export type KpiIndicatorVo = {
  /** 指标编码 */
  kpi_code?: string;
  /** 指标名称 */
  kpi_name?: string;
  /** 指标期望值 */
  expect_value?: string;
  /** 指标实际值 */
  actual_value?: string;
  /** 指标效果值 */
  effect_value?: string;
  /** 是否达标 */
  pass?: boolean;
  /** 效果是否达标 */
  effect_pass?: boolean;
  /** KPI定义快照，JSON格式，包含code/name/type/positive/percent等字段 */
  kpi_schema?: string;
};
/**
 * 二级单明细项VO（以二级单为最细行粒度）
 */
export type BudgetDemandItemVo = {
  data_type?: BudgetDataTypeEnum;
  /** 二级单ID */
  plan_demand_id?: number;
  /** 二级单编号 */
  plan_demand_number?: string;
  /** 二级单名称 */
  plan_demand_name?: string;
  /** 二级单状态 */
  plan_demand_state?: string;
  /** 二级单状态枚举值，如 NEWLY_BUILT, PENDING_DESIGN, CLOSED 等 */
  plan_demand_state_enum?: string;
  /** 一级单ID */
  parent_demand_id?: number;
  /** 一级单编号 */
  parent_demand_number?: string;
  /** 一级单名称 */
  parent_demand_name?: string;
  /** 一级需求类型ID */
  l1_category_id?: number;
  /** 执行周期开始日期 */
  cycle_begin_at?: string;
  /** 执行周期结束日期 */
  cycle_end_at?: string;
  /** 梯度：T1/T2/T3/T4 */
  gradient?: string;
  /** 三级单ID */
  demand_id?: number;
  /** 三级单编号 */
  demand_number?: string;
  /** 三级单名称 */
  demand_name?: string;
  /** 三级单状态 */
  demand_state?: string;
  /** 三级单状态枚举值，如 PENDING_QUOTE, MAKING, CLOSED 等 */
  demand_state_enum?: string;
  /** 投放渠道名称 */
  channel_name?: string;
  /** 供应商名称 */
  supplier_name?: string;
  /** 投放形式 */
  placement_type?: string;
  budget?: Money;
  current_fee?: Money;
  /** 预算进度（百分比 0-100） */
  budget_progress?: number;
  /** 时间进度（百分比 0-100） */
  time_progress?: number;
  daily_avg_fee?: Money;
  /** 预估剩余可用天数 */
  budget_support_days?: number;
  alert_level?: AlertLevelEnum;
  /** 媒体达标类型 */
  media_compliance_type?: string;
  /** 媒体达标类型枚举值，如 ORDER_KPI_TYPE / MEDIA_KPI_TYPE / OVERALL_KPI_TYPE / UNKNOWN_TYPE */
  media_compliance_type_enum?: string;
  /** 主要指标列表 */
  main_kpis?: KpiIndicatorVo[];
  /** 本订单指标（效果投放目标） */
  effect_target_kpis?: KpiIndicatorVo[];
  /** 辅助自定义指标列表 */
  auxiliary_kpis?: KpiIndicatorVo[];
  gradient_budget?: Money;
  gradient_current_fee?: Money;
  /** 梯度聚合-预算进度 */
  gradient_budget_progress?: number;
  gradient_daily_avg_fee?: Money;
  /** 梯度聚合-预估剩余可用天数 */
  gradient_budget_support_days?: number;
  gradient_alert_level?: AlertLevelEnum;
  cycle_budget?: Money;
  cycle_current_fee?: Money;
  /** 周期聚合-预算进度 */
  cycle_budget_progress?: number;
  cycle_daily_avg_fee?: Money;
  /** 周期聚合-预估剩余可用天数 */
  cycle_budget_support_days?: number;
  cycle_alert_level?: AlertLevelEnum;
  /** 是否开启风险预警 */
  alert_enabled?: boolean;
  /** 是否有预算变更记录（已生效的预算调整单） */
  has_adjust_record?: boolean;
};
/**
 * 预算管理看板返回数据
 */
export type BudgetDashboardDto = {
  summary?: BudgetDemandSummaryVo;
  /** 二级单/三级单明细列表（包含梯度合计行和执行周期合计行） */
  items?: BudgetDemandItemVo[];
  /** 最后计算数据的时间（用于展示截止T-1日期） */
  calc_date_time?: string;
};
/**
 * 预算调整单列表简要信息
 */
export type BudgetAdjustOrderSimpleVo = {
  /** 预算调整单ID */
  order_id?: number;
  /** 发起一级单ID */
  demand_id?: number;
  /** 关联一级单ID列表 */
  related_demand_ids?: number[];
  /** 流程状态 */
  state?: string;
  /** 流程状态描述 */
  state_desc?: string;
  /** OA流程单号 */
  oa_id?: string;
  /** OA审批状态 */
  oa_state?: number;
  /** 创建时间 */
  created_at?: string;
};
/**
 * 合同系统流程状态
 */
export type SolarContractStatus =
  | 'DRAFT'
  | 'APPROVING'
  | 'FEE_SIGN_APPROVED'
  | 'EFFECTIVE'
  | 'EXPIRED'
  | 'CANCELLED';
/**
 * 合同信息
 */
export type DemandContractInfoDto = {
  /** 合同编号 */
  contract_no?: string;
  /** 合同名称 */
  contract_name?: string;
  /** 合同类型 */
  contract_type?: string;
  /** 我方主体名称 */
  our_party_name?: string;
  /** 对方主体名称 */
  other_party_name?: string;
  /** 合同系统流程编号 */
  contract_flow_id?: string;
  contract_flow_status?: SolarContractStatus;
};
/**
 * 一询单需求内容详情
 */
export type DemandSourceContentVo = {
  /** id */
  id?: number;
  /** 创建人 */
  created_by?: number;
  /** 修改人 */
  updated_by?: number;
  /** 创建时间 */
  created_at?: string;
  /** 最后一次更新时间 */
  updated_at?: string;
  /** 一询单ID */
  demand_source_id?: number;
  /** 合作主平台 */
  main_platform?: string[];
  /** 发布开始时间 */
  begin_at?: string;
  /** 发布结束时间 */
  end_at?: string;
  /** 需求内容 */
  content_desc?: string;
  /** 主平台官单/私单 */
  public_order?: string[];
  /** 主平台官单/私单-名称 */
  public_order_name?: string[];
};
/**
 * 报价数据项
 */
export type ItemVo = {
  /** 字段名称 */
  name?: string;
  /** 字段值 */
  value?: object;
  /** 字段别名 */
  alias?: string;
};
/**
 * 合作供应商信息
 */
export type KolSupplierVo = {
  /** 供应商名称 */
  supplier_name?: string;
  /** 供应商ID */
  supplier_id?: number;
  /** 字段名称 */
  kol_main_page?: string;
  /** MPC供应商编码 */
  mpc_supplier_code?: string;
  /** 是否独家供应商 (0:否, 1:是) */
  is_exclusive_supplier?: string;
  /** 是否6个月最低价 (0:否, 1:是) */
  is_lowest_price_last6m?: string;
  /** 是否6个月内合作次数最多 (0:否, 1:是) */
  is_most_coop_last6m?: string;
};
/**
 * 一询单指定达人内容营销信息
 */
export type DemandSourceKolContentVo = {
  /** 粉丝数量 */
  follower_num?: number;
  /** 近6个月是否合作 (0:否, 1:是) */
  is_coop_last6m?: string;
  /** 近6个月合作平均CPM-视频图文 */
  coop_cpm_six_month?: number;
  /** 近6个月合作平均CPM（直播UV） */
  cmp_uv_six_month?: number;
  /** 近6个月合作平均CPM（直播PV） */
  cmp_pv_six_month?: number;
  /** 近6个月合作平均单次/单小时价格视频图文 */
  coop_price_cny_per?: number;
  /** 近6个月合作平均单次/单小时价格直播 */
  coop_price_cny_per_live?: number;
  /** 平台报价 */
  all_quote_price_cny?: ItemVo[];
  /** 平台预期CPM */
  all_cmp_expected?: ItemVo[];
  /** 价格来源 */
  price_source?: string;
  /** 合作供应商信息 */
  kol_supplier_volist?: KolSupplierVo[];
  /** 是否独家供应商 (0:否, 1:是) */
  is_exclusive_supplier?: string;
};
/**
 * 一询单指定达人
 */
export type DemandSourceKolVo = {
  /** id */
  id?: number;
  /** 创建人 */
  created_by?: number;
  /** 修改人 */
  updated_by?: number;
  /** 创建时间 */
  created_at?: string;
  /** 最后一次更新时间 */
  updated_at?: string;
  /** 一询单ID */
  demand_source_id?: number;
  /** 达人昵称 */
  kol_name?: string;
  /** 合作主平台 */
  main_platform?: string;
  /** 主平台账号 */
  main_platform_account?: string;
  /** 主平台主页链接 */
  main_platform_link?: string;
  /** 主平台官单/私单 */
  public_order?: string;
  /** 主平台官单/私单-名称 */
  public_order_name?: string;
  /** 主平台报价项目 */
  main_platform_quote_item?: string;
  /** 主平台报价项目-名称 */
  main_platform_quote_item_name?: string;
  /** 达人添加来源 */
  import_type?: string;
  demand_source_kol_content_vo?: DemandSourceKolContentVo;
  /** 是否独家供应商 (0:否, 1:是) */
  is_exclusive_supplier?: string;
  /** 主平台主页链接 */
  origin_main_platform_link?: string;
};
/**
 * 定制排查字段
 */
export type CustomInspectionField = {
  /** 字段名称 */
  field_name?: string;
  /** 策划备注要求 */
  plan_remark?: string;
  /** 供应商填写样例 */
  value_example?: string;
  /** 值 */
  value?: string;
};
/**
 * 一询单需求内容详情
 */
export type DemandSourceCustomInspectionVo = {
  /** id */
  id?: number;
  /** 创建人 */
  created_by?: number;
  /** 修改人 */
  updated_by?: number;
  /** 创建时间 */
  created_at?: string;
  /** 最后一次更新时间 */
  updated_at?: string;
  /** 一询单ID */
  demand_source_id?: number;
  /** 合作主平台 */
  main_platform?: string;
  /** 定制排查字段 */
  custom_inspection_fields?: CustomInspectionField[];
  /** 定制排查模板 */
  custom_inspection_template_id?: number;
};
/**
 * 策划预筛选类型
 */
export type PlanSuggestionState = 3 | 2 | 1;
export type BlobDto2 = {
  id?: number;
  /** 文件名 */
  filename?: string;
  /** 文件大小 */
  byte_size?: number;
  /** 文件类型 */
  content_type?: string;
  key?: string;
  /** 是否自动生成 */
  auto_generated?: boolean;
};
/**
 * 附件列表
 */
export type AttachmentDto2 = {
  id?: number;
  blob?: BlobDto2;
};
/**
 * 报价备注
 */
export type RemarkDto = {
  /** 备注 */
  text?: string;
  /** 附件列表 */
  files?: AttachmentDto2[];
};
/**
 * 一询单供应商提报达人
 */
export type DemandSourceSupplierKolVo = {
  /** id */
  id?: number;
  /** 创建人 */
  created_by?: number;
  /** 修改人 */
  updated_by?: number;
  /** 创建时间 */
  created_at?: string;
  /** 最后一次更新时间 */
  updated_at?: string;
  /** 一询单供应商ID */
  demand_supplier_id?: number;
  /** 指定达人ID */
  demand_source_kol_id?: number;
  /** 定制排查ID */
  demand_source_custom_inspection_id?: number;
  /** 合作主平台 */
  main_platform?: string;
  /** 定制排查属性值 */
  demand_source_custom_inspection_fields_value?: CustomInspectionField[];
  /** 主平台账号 */
  main_platform_account?: string;
  /** 主平台链接 */
  main_platform_link?: string;
  /** 主平台官单/私单 */
  public_order?: string;
  /** 主平台报价项目 */
  main_platform_quote_item?: string;
  /** 主平台官单/私单-名称 */
  public_order_name?: string;
  /** 主平台报价项目-名称 */
  main_platform_quote_item_name?: string;
  /** 提报数量 */
  quote_count?: number;
  /** 是否独家 */
  exclusive_sign?: boolean;
  /** 报价 */
  quote_price?: number;
  quote_price_currency?: CurrencyEnum;
  /** cpm */
  cpm?: number;
  /** 直播CPMPV */
  cpm_pv?: number;
  /** 直播CPMUV */
  cpm_uv?: number;
  /** 平台报价 */
  platform_quote_price?: ItemVo[];
  /** 平台预期CPM */
  platform_cpm?: ItemVo[];
  /** 平台直播CPMPV */
  platform_cpm_pv?: ItemVo[];
  /** 平台直播CPMUV */
  platform_cpm_uv?: ItemVo[];
  /** 是否中选 */
  win?: boolean;
  /** 是否已提交到二级单 */
  submit_demand?: boolean;
  plan_suggestion?: PlanSuggestionState;
  /** 需求方确认 */
  principal_confirm?: boolean;
  /** 不推荐原因 */
  not_recommend_reason?: string;
  demand_source_kol_content_vo?: DemandSourceKolContentVo;
  demand_source_supplier_vo?: DemandSourceSupplierVo;
  /** 是否最低价 */
  lowest_price?: boolean;
  /** 指定达人 */
  by_plan?: boolean;
  /** 供应商推荐 */
  by_supplier?: boolean;
  /** 达人顺序 */
  index?: number;
  /** 主平台主页链接 */
  origin_main_platform_link?: string;
  /** 数量 */
  count?: string;
  remark_dto?: RemarkDto;
};
/**
 * 一询单需求内容详情
 */
export type DemandSourceSupplierVo = {
  /** id */
  id?: number;
  /** 创建人 */
  created_by?: number;
  /** 修改人 */
  updated_by?: number;
  /** 创建时间 */
  created_at?: string;
  /** 最后一次更新时间 */
  updated_at?: string;
  /** 一询单ID */
  demand_source_id?: number;
  /** demandSupplierId */
  demand_supplier_id?: number;
  /** 供应商ID */
  supplier_id?: number;
  /** 供应商名称 */
  supplier_name?: string;
  /** 提报达人列表 */
  demand_source_supplier_kol_vos?: DemandSourceSupplierKolVo[];
  /** 供应商拒单 */
  reject_by_supplier?: boolean;
  /** 供应商拒单原因 */
  reject_reason?: string;
  /** 提报截止时间 */
  source_end_time?: string;
  /** 策划预填建议退回标识 */
  plan_pre_chose_suggest_return_flag?: boolean;
  /** 策划预填建议退回原因 */
  plan_pre_chose_suggest_return_reason?: string;
};
/**
 * 一询单关联的二级单草稿
 */
export type RelatedPlanDemandVo = {
  /** 主键 */
  id?: number;
  /** 报价金额 */
  quote_price?: string;
  quote_currency?: CurrencyEnum;
  demand_plan_info?: DemandPlanInfoBaseDto;
  /** 状态名称 */
  human_state_name?: string;
};
/**
 * 策划拆单阶段达人推荐维度权重
 */
export type DimensionWeight = {
  /** 维度名称 */
  dimension?: string;
  /** 权重(0~100) */
  weight?: number;
};
/**
 * 一询单附加信息
 */
export type DemandSourceInfoVo = {
  /** id */
  id?: number;
  /** 一询单ID */
  demand_source_id?: number;
  /** 策划拆单阶段达人推荐维度权重 */
  plan_kol_recommend_weights?: DimensionWeight[];
};
/**
 * 达人推荐任务结果
 */
export type KolRecommendTaskVo = {
  /** 任务ID */
  task_id?: number;
  status?: BizTaskStatus;
  /** 状态描述 */
  status_desc?: string;
  /** 本次推荐使用的维度权重 */
  dimension_weights?: DimensionWeight[];
  /** 附件列表（仅已完成时有值） */
  attachments?: AttachmentVo[];
  /** 完成时间 */
  completed_at?: string;
};
/**
 * 一询单需求详情
 */
export type DemandSourceDetailVo = {
  /** 三级需求单类型id */
  l3_category_id?: number;
  /** 需求内容 */
  demand_source_content_vo?: DemandSourceContentVo[];
  /** 指定达人列表 */
  demand_source_kol_vo?: DemandSourceKolVo[];
  /** 定制排查列表 */
  demand_source_custom_inspection_vo?: DemandSourceCustomInspectionVo[];
  /** 供应商列表 */
  demand_source_supplier_vo?: DemandSourceSupplierVo[];
  /** 提报达人列表 */
  demand_source_supplier_kol_vos?: DemandSourceSupplierKolVo[];
  /** 一询单关联的二级单草稿 */
  relate_plan_demand_list?: RelatedPlanDemandVo[];
  demand_source_info?: DemandSourceInfoVo;
  plan_kol_recommend_task?: KolRecommendTaskVo;
  marketing_kol_recommend_task?: KolRecommendTaskVo;
};
/**
 * 关联一询单
 */
export type RelateDemandSourceDto = {
  /** 一询单id */
  demand_source_id?: number;
  demand_group_type?: DemandGroupType;
  /** 一询单标题 */
  title?: string;
};
/**
 * 关联提前核价记录（只读，由 kol-auto-pricing-enhance 自动建单时回填）
 */
export type RelateAdvancePriceCheckDto = {
  /** 提前核价记录 id */
  advance_price_check_id?: number;
  /** 提前核价单号（如 #123-886-01） */
  record_no?: string;
  /** 下单类型（直播 / 视频图文） */
  order_category?: string;
  /** 关联供应商 id */
  supplier_id?: number;
  /** 关联一级单ID或比选单ID */
  demand_id?: number;
  /** 关联单据类型：1=一级需求单 2=比选单 */
  demand_type?: number;
  /** 当前轮次（从1开始递增） */
  round?: number;
  /** 提前核价记录详情页跳转链接（新标签页打开） */
  jump_url?: string;
  group_type?: DemandGroupType;
};
/**
 * 达标结果/预期结果
 */
export type DemandKpiAchievementStatus =
  | 'pass'
  | 'part_pass'
  | 'no_pass'
  | 'unknown';
/**
 * 创建人
 */
export type DemandUserSimpleDto = {
  /** 用户ID */
  id?: number;
  /** 用户姓名 */
  name?: string;
  /** 用户邮箱 */
  email?: string;
  /** 工号 */
  job_number?: string;
};
/**
 * 考核指标-调整前
 */
export type KpiAdjustmentKpiDto = {
  /** 序号 */
  index_id: number;
  /** kpi的编码 */
  kpi_code: string;
  kpi_schema?: DemandKpiSchemaData;
  /** KPI期望值 */
  expect_value: string;
};
/**
 * 考核指标-调整后
 */
export type KpiAdjustmentKpiAfterDto = {
  /** 序号 */
  index_id?: number;
  /** kpi的编码 */
  kpi_code?: string;
  kpi_type?: DemandQuotaTypeEnum;
  kpi_schema?: DemandKpiSchemaData;
  diff_type?: DiffTypeEnum;
  /** KPI期望值-调整前 */
  expect_before?: string;
  /** KPI期望值-调整后 */
  expect_after?: string;
};
/**
 * 退回状态
 */
export type KpiAdjustmentReturnStateEnum = 'PLANNER_RETURN' | 'OA_RETURN';
/**
 * 用于渲染权限标志
 */
export type KpiAdjustmentPermitActions = {
  recall?: boolean;
  submit?: boolean;
  discard?: boolean;
  planner_approval?: boolean;
};
/**
 * 状态节点
 */
export type KpiAdjustmentDetailDtoBottomTab = {
  tab_name?: string;
  value?: string;
};
/**
 * kpi调整单信息
 */
export type KpiAdjustmentDetailDto = {
  /** id */
  id?: number;
  /** kpi调整单的编号 */
  kpi_adjustment_number?: string;
  /** 需求单Id */
  demand_id?: number;
  /** 调整原因 */
  adjustment_reason?: string;
  /** 发起时间 */
  create_time?: string;
  create_by?: DemandUserSimpleDto;
  /** 需求预期-调整前 */
  expectation_before?: string;
  /** 需求预期-调整后 */
  expectation_after?: string;
  /** 考核指标-调整前 */
  kpi_before?: KpiAdjustmentKpiDto[];
  /** 考核指标-调整后 */
  kpi_after?: KpiAdjustmentKpiAfterDto[];
  /** 调整单的附件 */
  attachments?: AttachmentDto[];
  /** 考核指标-调整后 */
  plan_kpi_after?: KpiAdjustmentPlanKpiAfterDto[];
  /** 是否调整二级单指标 */
  adjust_demand_plan_kpi?: boolean;
  adjust_attachment?: BlobDto;
  state?: KpiAdjustStateEnum;
  /** Kpi调整单的状态名称 */
  state_name?: string;
  return_status?: KpiAdjustmentReturnStateEnum;
  /** 退回状态名称 */
  return_status_name?: string;
  /** 退回原因 */
  return_reason?: string;
  permit_actions?: KpiAdjustmentPermitActions;
  /** 状态节点 */
  bottom_tabs?: KpiAdjustmentDetailDtoBottomTab[];
  /** OA审批概览 */
  oa_audit_desc_list?: OaProcessDescVo[];
};
export type IsAssociateExecutionDataEnum = 'FALSE' | 'TRUE';
export type KpiAdjustmentDetailIncludeDemandDto = {
  /** 主键 */
  id?: number;
  /** 源表id */
  source_id?: number;
  /** 源类型 */
  source_type?: string;
  /** 源需求是否旧OA流程 */
  source_oa?: boolean;
  /** name */
  name?: string;
  /** product_id */
  product_id?: number;
  /** desc */
  desc?: string;
  /** number */
  number?: string;
  /** before_number */
  before_number?: string;
  /** expect_begin_at */
  expect_begin_at?: string;
  /** expect_end_at */
  expect_end_at?: string;
  priority?: PriorityEnum;
  /** 预算 */
  budget?: string;
  budget_currency?: CurrencyEnum;
  state?: DemandState;
  /** 子状态 */
  sub_state?: string;
  flow_flag?: FlowFlagEnum;
  purchasing_method?: PurchasingMethodEnum;
  purchasing_strategy?: PurchasingStrategyEnum;
  /** 采购方式展示字符串 */
  translated_purchasing_method?: string;
  /** 是否绑定执行数据 */
  is_bind_execution_data?: number;
  /** 合并询价版本号，0=旧版本/不适用，1=合并询价版本 */
  merged_inquiry_version?: number;
  /** root_id */
  root_id?: number;
  /** parent_id */
  parent_id?: number;
  /** lft */
  lft?: number;
  /** rgt */
  rgt?: number;
  /** author_id */
  author_id?: number;
  /** 需求类型 */
  demand_type?: number;
  group_type?: DemandGroupType;
  /** 策划人员配置 */
  planner_group_id?: number;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 四级分类ID */
  l4_category_id?: number;
  /** 考核类型 */
  evaluation_type?: number;
  support_type?: DemandSupportTypeEnum;
  /** need_cp_pay */
  need_cp_pay?: boolean;
  /** depth */
  depth?: number;
  /** suggestion */
  suggestion?: string;
  /** demander_accept_suggestion */
  demander_accept_suggestion?: string;
  /** has_demander_accept_suggestion */
  has_demander_accept_suggestion?: boolean;
  /** 结算金额 */
  settle_price?: string;
  settle_currency?: CurrencyEnum;
  /** 【20240704版本】【二级单】报价截止时间 */
  quote_deadline?: string;
  /** 报价金额 */
  quote_price?: string;
  /** 策划复核报价金额 */
  plan_accept_settle_price?: number;
  /** 已OA审批结算金额 */
  oa_approved_settle_price?: number;
  quote_currency?: CurrencyEnum;
  /** lock_version */
  lock_version?: number;
  /** score */
  score?: number;
  /** business_state */
  business_state?: number;
  /** win_supplier_ids */
  win_supplier_ids?: string;
  /** oa_id */
  oa_id?: string;
  /** 超额验收OA单号 */
  over_amount_oa_id?: string;
  /** 超额验收OA状态 */
  over_amount_oa_status?: string;
  /** reimbursement_number */
  reimbursement_number?: string;
  /** 【20240704版本】【三级单】供应商变更报价备注 */
  remark?: string;
  /** total_requote_price */
  total_requote_price?: number;
  /** notes */
  notes?: string;
  /** finished */
  finished?: boolean;
  /** reasons_for_differences */
  reasons_for_differences?: string;
  /** requirement_closed */
  requirement_closed?: boolean;
  /** no_contract_flag */
  no_contract_flag?: boolean;
  /** dspf_mpc_no */
  dspf_mpc_no?: string;
  /** dspf_desc */
  dspf_desc?: string;
  oa_status?: OaState;
  /** oa_rejection_reason */
  oa_rejection_reason?: string;
  /** 费控状态 */
  fee_control_state?: boolean;
  /** 提交OA时间 */
  oa_submit_at?: string;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** 当前订单支持的供应商库版本 */
  enable_supplier_version?: string;
  /** 是否启用结构化数据 */
  structured?: boolean;
  /** 预期 */
  expectation?: string;
  /** 需求项目背景 */
  background?: string;
  /** 是否需要供应商上传内容营销附件, 0:不需要, 1:需要, -1:不可配置 */
  need_content_marketing_attach?: number;
  /** 供应商内容营销作品状态，0=正常，1=异常 */
  content_marketing_attach_flag?: number;
  /** 本需求单创建的提示文本 */
  create_tips?: string;
  reimburse_status?: ReimburseStatusEnum;
  /** overall_score */
  overall_score?: number;
  /** 保存时间 */
  saved_time?: string;
  /** 0=原始，1=报价结构化 */
  structured_version?: number;
  /** 是否KPI特殊申报 */
  kpi_special_declaration_flag?: boolean;
  /** KPI特殊申报原因 */
  kpi_special_declaration_reason?: string;
  /** 低风险 */
  low_risk_special_report_flag?: boolean;
  /** 最后生效的调整单ID */
  latest_kpi_adjustment_id?: number;
  /** 是否支持绑定执行数据 */
  support_bind_execution_data?: boolean;
  /** 是否自动拆单规则创建的二级单 */
  auto_split?: boolean;
  /** 是否跳过超额验收审批 */
  skip_over_amount_approval?: boolean;
  /** 作者名称 */
  author_name?: string;
  author?: UserSimpleVo;
  /** 状态名称 */
  human_state_name?: string;
  /** 子状态名称 */
  human_sub_state_name?: string;
  /** 产品编号 */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 产品名称 */
  product_name?: string;
  product_area?: ProductAreaEnum;
  /** 优先级 */
  translated_priority?: string;
  /** 组类型 */
  translated_group_type?: string;
  /** 创建日期（精确到天） */
  root_created_on?: string;
  /** 状态节点 */
  bottom_tabs?: DemandDetailsResDtoBottomTab[];
  /** 是否可以被修改 */
  can_modify?: boolean;
  /** 是否已经填写核价信息 */
  submit_price_check?: boolean;
  /** 是否需要核价 */
  has_price_check?: boolean;
  platform_price_type?: PlatformPriceTypeEnum;
  /** 是否为KOL类型需求 */
  is_kol?: boolean;
  /** 是否为标品价格库核价品类（小喇叭运营服务/创作者运营服务）：前端据此走只读【查看核价结果】分支，区别于 KOL 可编辑商务核价 */
  is_price_library_check?: boolean;
  /** 层级名称（用-连接） */
  category_names?: string;
  /** 一级分类名称 */
  l1_category_name?: string;
  /** 是否支持方案审批，根据一级需求类型处理的 */
  support_plan_approve?: boolean;
  /** 是否超额验收 */
  over_amount_flag?: boolean;
  /** 超额验收金额 */
  over_amount_cny?: number;
  /** 关联二询单信息 */
  rel_demand_info?: RelDemandDto[];
  demand_supplier_settle_details?: DemandSupplierSettleDetailDto[];
  /** 评估得分 */
  evaluate_scores?: DemandDetailsResDtoEvaluateScoreDto[];
  /** 需求人员评估得分 */
  demand_manger_evaluate_scores?: DemandDetailsResDtoEvaluateScoreDto[];
  /** 需求预期 kpi指标列表 */
  expectation_kpi_indicators?: DemandKpiValueDto[];
  /** 需求人员 */
  demand_principals?: DemandPrincipalDto[];
  demand_plan_info?: JournalDemandDetailsResDtoDemandPlanInfoDto;
  /** 是否需全部报价 */
  need_all_quote?: boolean;
  /** 供应商详情 */
  demand_suppliers?: DemandDetailsResDtoDemandSupplierDto[];
  settle_report_attachs?: DemandDetailsResDtoAttachmentDto[];
  planner_report_attachs?: DemandDetailsResDtoAttachmentDto[];
  demand_evaluation_attachs?: DemandDetailsResDtoAttachmentDto[];
  /** 结算单附件 */
  statement_attachs?: DemandDetailsResDtoAttachmentDto[];
  /** 签章结算单附件 */
  signed_statement_attachs?: DemandDetailsResDtoAttachmentDto[];
  is_self_contact?: boolean;
  introduce_attachs?: DemandDetailsResDtoAttachmentDto[];
  permit_actions?: PermitActions;
  flow_action_info_value?: DemandFlowActionInfoValue;
  child_demands?: ChildDemandDto[];
  child_demand_summary?: DemandDetailsResDtoChildDemandSummaryDto;
  current_demand_supplier_id?: string;
  default_self_contact_id?: number;
  current_supplier_id?: string;
  member_planners?: MemberPlannerDto[];
  /** 是否修改权益、验收标准，在商务确认报价节点提交后，此字段会恢复默认值，默认值为：false */
  modify_rights_or_accept_desc_flag?: boolean;
  old_source_category?: string;
  kpi_type?: MediaKpiType;
  dspf_quota_data?: DspfQuotaData;
  remarks_tree?: RemarksTree;
  accept_remarks?: AcceptRemarks;
  qc_accept_priority?: QcAcceptPriorityEnum;
  root_demand_ext_data?: RootDemandExtColumnDto;
  bidding?: BiddingDto;
  /** 多轮轮次 */
  turns_number?: number;
  return_status?: AuditQuoteReturnStatusEnum;
  /** 退回状态文案 */
  translated_return_status?: string;
  /** 不需求走费控相关校验 */
  non_need_fee_control_verify?: boolean;
  /** 是否为可编辑的三级单 */
  is_edit_leaf_demand?: boolean;
  /** 已完成综合评分法的二级单 */
  multi_quota_closed_demand_id?: number;
  /** 推荐供应商列表 */
  referrer_supplier_dto_list?: DemandDetailsResDtoDemandSupplierDto[];
  /** 供应商推荐人姓名 */
  supplier_referrer?: string;
  /** 供应商推荐理由 */
  supplier_referral_reason?: string;
  /** 回避供应商列表 */
  avoidance_supplier_dto_list?: DemandDetailsResDtoDemandSupplierDto[];
  /** 供应商回避理由 */
  supplier_avoidance_reason?: string;
  /** 【20240704版本】【二级单】商务评估备注 */
  business_audit_quote_note?: string;
  /** OA审批概览 */
  oa_audit_desc_list?: OaProcessDescVo[];
  /** KPI调整单OA审批概览 */
  kpi_adjust_oa_audit_desc_list?: OaProcessDescVo[];
  /** 是否KPI特殊申报（发起OA方案与预算审批时使用） */
  kpi_special_declaration_flag_for_oa_submit?: boolean;
  /** 费用归属主体id */
  cost_company_id?: number;
  cost_company?: CostCompanyDto;
  /** 关联的小喇叭需求单ID */
  trumpet_demand_id?: number;
  /** 关联业务ID（如：小喇叭需求单ID、比选ID） */
  relate_biz_id?: number;
  relate_biz_type?: DemandRelateBizTypeEnum;
  relate_bidding_info?: BiddingInfo;
  quote_score_state?: QuoteScoreStateEnum;
  demand_supplier_score_rule?: DemandSupplierScoreRuleDto;
  /** 是否启用询比价综合评分 */
  quote_score_enable?: boolean;
  /** 是否支持询比价综合评分 */
  support_quote_score?: boolean;
  /** 询比价综合评分模版编码 */
  score_template_code?: string;
  /** 询比价综合评分模版名称 */
  score_template_name?: string;
  quote_upload_method?: DemandQuoteUploadMethodEnum;
  reject_info?: DemandRejectInfoVo;
  /** 合作内容结算信息明细 */
  cooperate_account_settle_list?: CooperateAccountSettleInfo[];
  /** 最近一次审批通过的超额验收OA单号 */
  last_approved_over_amount_oa_id?: string;
  /** 退回到的节点 */
  reverted_human_state_name?: string;
  /** 是否有策划负责人验收节点 */
  has_planner_leader_accept_node?: boolean;
  /** 根据需求类型得出报价类型，0=不含税金额，1=含税金额（报价结构化版本后有值） */
  quote_type?: number;
  /** 推送OA失败原因 */
  push_oa_fail_reason?: string;
  /** 是否展示KPI调整记录 */
  view_kpi_adjustment_record?: boolean;
  kpi_create_state?: DemandKpiCreateStateEnum;
  template_type?: DemandCategoryPropertyTemplateTypeEnum;
  /** 是否满足KPI特殊申报的适用范围 */
  kpi_special_declaration_range_flag?: boolean;
  /** 定标金额-三级单独有 */
  fee_approval_amount?: number;
  /** 是否可以发起追加预算（true=常规采购二级单 + 未绑定执行数据） */
  can_add_budget_advance?: boolean;
  /** 评分进度 */
  demand_supplier_score_progresses?: DemandSupplierScoreProgressDto[];
  /** 发票信息 */
  invoice_list?: InvoiceDetailDto[];
  invoice_belong_area?: InvoiceBelongAreaEnum;
  /** 是否海外INVOICE */
  is_oversea_invoice?: boolean;
  payment_doc_info?: DemandPaymentDocInfoDto;
  expense_doc_info?: DemandExpenseDocInfoDto;
  /** 预付单列表 */
  prepayment_list?: JournalDemandDetailsResDtoPrepaymentInfoDto[];
  budget_dashboard?: BudgetDashboardDto;
  /** 关联预算调整单列表 */
  budget_adjust_orders?: BudgetAdjustOrderSimpleVo[];
  /** 是否有预算变更记录（已生效的预算调整单） */
  has_budget_adjust?: boolean;
  /** 是否有考核指标调整记录（已生效的预算调整单中对KPI的实际变更） */
  has_budget_kpi_adjust?: boolean;
  sm_budget?: SmExpenseBudgetQueryVo;
  /** 是否需要上传/查看加热助推数据 */
  need_boost_effect_data?: boolean;
  /** 是否有监测链接可查看，true=有监测链接，前端可展示查看入口 */
  has_monitor_links?: boolean;
  /** 紧急需求补单审核材料 */
  urgent_order_restocking_attachs?: DemandDetailsResDtoAttachmentDto[];
  /** 是否早于定标执行 */
  special_matters_reporting?: boolean;
  /** 是否需要供应商提交合作内容维度的结算金额 */
  need_cooperate_account_supplier_settle_price?: boolean;
  /** 供应商提交合作内容维度的结算金额总计 */
  cooperate_account_supplier_settle_price_sum?: number;
  /** 内容营销结案结构化，1：新单子，0：老单子 */
  content_marketing_case_structured_version?: number;
  /** 策划复核和需求方验收是否可以修改最终结算金额 */
  can_modify_final_settle_price_when_accept?: boolean;
  /** 是否需要供应商提交结构化结案报告 */
  need_structured_settle_report?: boolean;
  /** 是否提交了结构化结案报告 */
  submitted_structured_settle_report?: boolean;
  /** 是否加急 */
  urgent?: boolean;
  /** 加急原因 */
  urgent_reason?: string;
  /** 加急环节 */
  urgent_step?: string;
  renew_type?: RenewTypeEnum;
  /** 常规采购续约单id */
  normal_renew_demand_id?: number;
  /** 小喇叭续约单id */
  trumpet_renew_demand_id?: number;
  /** 常规采购续约单标题 */
  normal_renew_demand_title?: string;
  /** 小喇叭续约单标题 */
  trumpet_renew_demand_title?: string;
  contract_info?: DemandContractInfoDto;
  /** 续签供应商列表 */
  renew_demand_supplier_list?: DemandDetailsResDtoDemandSupplierDto[];
  /** 是否全部上传截图（主平台/付费分发） */
  is_screenshot_done?: number;
  plan_type?: DemandPlanTypeEnum;
  demand_source_detail_vo?: DemandSourceDetailVo;
  relate_demand_source_dto?: RelateDemandSourceDto;
  relate_advance_price_check_dto?: RelateAdvancePriceCheckDto;
  demand_aireview_result?: DemandAiReviewResultDto;
  /** Muse资源创建状态：NOT_CREATED=未创建, CREATED=已创建, CREATE_FAILED=创建失败 */
  muse_resource_status?: string;
  /** Muse归档状态：NOT_ARCHIVED=未归档, ARCHIVED=已归档, ARCHIVE_FAILED=归档失败 */
  muse_archive_status?: string;
  /** Muse资源包链接 */
  muse_resource_url?: string;
  init_budget?: Money;
  /** 调整预算金额 */
  adjusted_budget?: number;
  final_budget?: Money;
  /** 最小预算调整金额 */
  min_adjusted_budget?: number;
  /** 是否超预算 */
  over_budget?: boolean;
  /** 超预算说明 */
  over_amount_remark?: string;
  '是否支持合作内容_a_i报价&整单报价'?: boolean;
  kpi_result?: DemandKpiAchievementStatus;
  /** 达标说明/未预期说明 */
  kpi_result_remark?: string;
  kpi_adjustment_detail?: KpiAdjustmentDetailDto;
  /** 供应商评分规则 */
  supplier_score_rules?: ScoreRuleDto[];
  is_bind_execution_data_enum?: IsAssociateExecutionDataEnum;
  /** 是否有KPI调整 */
  has_kpi_adjustment?: boolean;
  qc_first_review?: boolean;
};
export type ResponseKpiAdjustmentDetailIncludeDemandDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: KpiAdjustmentDetailIncludeDemandDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type GlobalSearchForm = {
  current?: number;
  size?: number;
  order_field?: string;
  order_direction?: string;
  keyword?: string;
};
/**
 * 全局搜索VO
 */
export type TrumpetWorkBenchGlobalSearchVo = {
  id?: string;
  name?: string;
  parent_id?: string;
  state?: string;
  mod?: number;
  children?: TrumpetWorkBenchGlobalSearchVo[];
};
/**
 * 分页结果
 */
export type PageResultTrumpetWorkBenchGlobalSearchVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: TrumpetWorkBenchGlobalSearchVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultTrumpetWorkBenchGlobalSearchVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultTrumpetWorkBenchGlobalSearchVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ClosedLoopGlobalSearchForm = {
  current?: number;
  size?: number;
  order_field?: string;
  order_direction?: string;
  keyword?: string;
};
/**
 * 全局搜索VO
 */
export type ClosedLoopWorkBenchGlobalSearchVo = {
  id?: string;
  name?: string;
  parent_id?: string;
  state?: string;
  mod?: number;
  children?: ClosedLoopWorkBenchGlobalSearchVo[];
};
/**
 * 分页结果
 */
export type PageResultClosedLoopWorkBenchGlobalSearchVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: ClosedLoopWorkBenchGlobalSearchVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultClosedLoopWorkBenchGlobalSearchVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultClosedLoopWorkBenchGlobalSearchVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 全局搜索VO
 */
export type WorkBenchGlobalSearchVo = {
  id?: string;
  name?: string;
  parent_id?: string;
  state?: string;
  mod?: number;
  children?: WorkBenchGlobalSearchVo[];
};
/**
 * 分页结果
 */
export type PageResultWorkBenchGlobalSearchVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: WorkBenchGlobalSearchVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultWorkBenchGlobalSearchVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultWorkBenchGlobalSearchVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 查询发票列表参数
 */
export type InvoiceQueryPageForm = {
  current?: number;
  limit?: number;
  /** 税务系统发票唯一主键精确搜索 */
  invoice_key?: string;
  /** 发票ID精确搜索 */
  invoice_id?: string;
  /** 发票号码精确搜索 */
  invoice_no?: string;
  /** 关联需求单号精确搜索，若有多个关联需求单号，命中一个也可以被筛选 */
  demand_id?: string;
  /** 多选-发票状态搜索，USED：已使用，RELEASED：已释放 */
  invoice_state_list?: string[];
  /** 多选-发票类型搜索 */
  invoice_type_list?: string[];
  /** 多选-发票数据状态搜索，0：有效，1：无效 */
  is_valid_list?: number[];
  /** 单选-发票数据状态搜索，0：有效，1：无效 */
  is_valid?: number;
};
/**
 * 发票状态
 */
export type InvoiceState = 'USED' | 'RELEASED';
/**
 * 关联需求ID列表
 */
export type InvoiceDemandInfo = {
  /** 发票ID */
  invoice_id?: number;
  /** 需求单ID */
  demand_id?: number;
  /** 需求单所属：发行中心->channel_group，市场支持中心->market_group */
  group_type?: string;
};
export type InvoiceListVo = {
  /** 发票ID，MPC侧生成的发票表主键ID，同一张发票被删除重新生成，ID会不同 */
  id?: number;
  /** 发票ID，M税务系统生成的发票ID，同一张发票被删除重新生成，ID会不同 */
  invoice_id?: string;
  /** 发票唯一Key，税务系统生成的唯一Key，同一张发票的key相同 */
  invoice_key?: string;
  /** 发票号码 */
  invoice_no?: string;
  invoice_state?: InvoiceState;
  /** 关联需求ID列表 */
  demand_info_list?: InvoiceDemandInfo[];
  /** 发票类型Code */
  invoice_type?: string;
  /** 发票数据状态 1=生效,0=失效 */
  is_valid?: number;
  /** 更新时间 */
  updated_at?: string;
};
export type PageVoInvoiceListVo = {
  total?: number;
  records?: InvoiceListVo[];
};
export type ResponsePageVoInvoiceListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoInvoiceListVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 评分模版
 */
export type ScoreTemplateDto = {
  /** 模版编码 */
  code?: string;
  /** 模版名称 */
  name?: string;
  /** 是否启用 */
  enable?: boolean;
  /** 是否默认模版 */
  default_template?: boolean;
  /** 创建时间 */
  created_at?: string;
  /** 评分规则 */
  score_rules?: ScoreRuleDto[];
};
/**
 * update 询比价评分模板
 */
export type InquiryScoreRuleUpdateForm = {
  /** 询比价评分模板ID */
  id: number;
  /** 归属的一级需求分类ID */
  belong_l1_category_id?: number;
  /** 一级需求分类 */
  l1_category_id?: number;
  /** 二级需求分类 */
  l2_category_ids?: number[];
  /** 三级需求分类 */
  l3_category_ids?: number[];
  /** 评分模版 */
  score_templates?: ScoreTemplateDto[];
};
/**
 * 询比价评分模板-分页查询表单
 */
export type InquiryScoreRulePageQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 三级需求分类ID, 可多选 */
  l3_category_ids?: number[];
};
export type InquiryScoreRuleVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 归属的一级需求分类ID */
  belong_l1_category_id?: number;
  belong_l1_category?: DemandCategory;
  /** 关联的一级需求分类ID */
  l1_category_id?: number;
  l1_category?: DemandCategory;
  /** 关联的二级级需求分类 */
  l2_category_ids?: number[];
  /** 关联的二级需求分类 */
  l2_categories?: DemandCategory[];
  /** 关联的三级需求分类 */
  l3_category_ids?: number[];
  /** 关联的三级需求分类 */
  l3_categories?: DemandCategory[];
  /** 评分模版（允许多个） */
  score_templates?: ScoreTemplateDto[];
};
export type PageVoInquiryScoreRuleVo = {
  total?: number;
  records?: InquiryScoreRuleVo[];
};
export type ResponsePageVoInquiryScoreRuleVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoInquiryScoreRuleVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type InquiryScoreRuleCreateForm = {
  /** 归属的一级需求分类ID */
  belong_l1_category_id: number;
  /** 一级需求分类 */
  l1_category_id?: number;
  /** 二级级需求分类, 二级需求分类为空时，表示选择所有二级需求分类; 二级需求分类不为空时，表示选择指定二级需求分类下的所有三级需求分类 */
  l2_category_ids?: number[];
  /** 三级需求分类 */
  l3_category_ids?: number[];
  /** 评分模版 */
  score_templates: ScoreTemplateDto[];
};
/**
 * 权限有效期
 */
export type EffectiveDuration =
  | 'DAYS_7'
  | 'DAYS_30'
  | 'DAYS_180'
  | 'DAYS_360'
  | 'PERMANENT';
/**
 * 创建权限申请单
 */
export type CreatePermissionApplicationCmd = {
  /** 申请范围：role=职能权限, product=产品权限 */
  apply_scope?: string[];
  effective_duration: EffectiveDuration;
  /** 申请的角色 ID 列表（可空） */
  role_ids?: number[];
  /** 申请的具体产品 ID 列表（可空） */
  product_ids?: number[];
  /** 申请的产品分类全部（可空） */
  product_category_all?: ProductSourceEnum[];
  /** 代发起人 user_id（仅外包申请人时需要，必填） */
  delegate_applicant_id?: number;
};
/**
 * 权限申请单状态
 */
export type PermissionApplicationStatus =
  | 'INITIATED'
  | 'COMPLETED'
  | 'CANCELLED';
/**
 * 权限申请单详情
 */
export type PermissionApplicationDetailVo = {
  /** 申请单 ID */
  id?: number;
  /** 申请单号 */
  application_no?: string;
  /** 申请人 user_id */
  applicant_user_id?: number;
  /** 申请人姓名 */
  applicant_name?: string;
  /** 申请人邮箱 */
  applicant_email?: string;
  /** 申请人工号 */
  applicant_job_number?: string;
  /** 申请人部门名称 */
  department_name?: string;
  /** 部门完整路径 */
  department_full_path?: string;
  /** 申请标题（MPC权限申请-{姓名}-{YYYY-MM} 格式） */
  apply_title?: string;
  /** 生效时长小时数，NULL=永久 */
  effective_duration_hours?: number;
  effective_duration?: EffectiveDuration;
  /** 是否永久（effective_duration_hours == null） */
  is_permanent?: boolean;
  application_status?: PermissionApplicationStatus;
  /** 申请单状态中文名 */
  application_status_name?: string;
  oa_status?: OaState;
  /** OA 状态中文名 */
  oa_status_name?: string;
  /** OA 单号 */
  oa_id?: string;
  /** OA 单据跳转链接 */
  oa_url?: string;
  /** OA 驳回原因 */
  oa_reject_reason?: string;
  /** 归档时间 */
  archived_at?: string;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** 申请的角色 ID 列表 */
  role_ids?: number[];
  /** 申请的产品 ID 列表 */
  product_ids?: number[];
  /** 申请的分类全部 */
  product_category_all?: number[];
  /** 申请的角色名称列表 */
  role_names?: string[];
  /** 申请的产品名称列表 */
  product_names?: string[];
  /** 申请的分类全部名称列表 */
  category_all_names?: string[];
  /** 已申请角色详情列表 */
  role_details?: {
    [key: string]: any;
  }[];
  /** 已申请产品详情列表 */
  product_details?: {
    [key: string]: any;
  }[];
  /** 审批路由分支信息 */
  route_branch?: string;
};
export type ResponsePermissionApplicationDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PermissionApplicationDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ProjectListQueryForm = {
  page_no?: number;
  page_size?: number;
  project_number_or_project_name?: string;
  project_number?: string;
  project_name?: string;
  create_by?: string;
  product_code?: string;
  project_type_code_list?: string[];
  temp_project_flag?: number;
  doc_status?: string;
};
export type ProjectQueryDto = {
  project_id?: string;
  project_number?: string;
  project_name?: string;
  description?: string;
  product_code?: string;
  product_name?: string;
  create_by_code?: string;
  create_by_name?: string;
  create_by?: string;
  temp_project_flag?: number;
  biz_currency_code?: string;
  biz_amount?: string;
  available_amount?: string;
  intercept_amount?: string;
  warning_amount?: string;
  used_amount?: string;
  need_prerequisition?: number;
  doc_status?: string;
};
export type PageDtoProjectQueryDto = {
  page_no?: number;
  page_size?: number;
  total?: number;
  data?: ProjectQueryDto[];
};
export type ResponsePageDtoProjectQueryDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageDtoProjectQueryDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type CalculateProjectBudgetDto = {
  budget_currency?: string;
  budget_price?: number;
  number?: string;
  before_number?: string;
  group_type?: DemandGroupType;
};
export type CalculateProjectBudgetVo = {
  budge_tips?: string;
  budget?: string;
};
export type ResponseCalculateProjectBudgetVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: CalculateProjectBudgetVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 修改标品价格库二级权限配置
 */
export type FeeTypeL2PermissionUpdateForm = {
  /** 配置 ID */
  id: number;
  /** 一级费用类型 */
  l1_category_id?: number;
  /** 是否包含该一级下所有二级 */
  all_l2?: boolean;
  /** 二级费用类型 */
  l2_category_ids?: number[];
  /** 授权用户 UserId 列表 */
  user_ids?: number[];
};
/**
 * 标品价格库二级权限配置分页查询
 */
export type FeeTypeL2PermissionPageQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 一级费用类型(用于按一级筛选) */
  l1_category_ids?: number[];
  /** 二级费用类型(用于按二级筛选) */
  l2_category_ids?: number[];
  /** 授权用户姓名/邮箱 模糊匹配 */
  user_name_email_fuzzy?: string;
};
/**
 * 标品价格库二级权限-授权用户
 */
export type FeeTypeL2PermissionUserDto = {
  /** 用户 id */
  id?: number;
  /** 用户姓名 */
  name?: string;
  /** 用户邮箱 */
  email?: string;
};
/**
 * 标品价格库二级权限配置
 */
export type FeeTypeL2PermissionVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 一级费用类型 ID */
  l1_category_id?: number;
  l1_category?: DemandCategory;
  /** 二级费用类型 ID 列表 */
  l2_category_ids?: number[];
  /** 二级费用类型列表 */
  l2_categories?: DemandCategory[];
  /** 是否包含该一级下所有二级 */
  all_l2?: boolean;
  /** 授权用户列表 */
  users?: FeeTypeL2PermissionUserDto[];
  /** 二级费用类型名称展示 */
  name?: string;
};
export type PageVoFeeTypeL2PermissionVo = {
  total?: number;
  records?: FeeTypeL2PermissionVo[];
};
export type ResponsePageVoFeeTypeL2PermissionVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoFeeTypeL2PermissionVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 标品价格库二级权限配置-二级费用类型选项查询表单
 */
export type FeeTypeL2PermissionL2SelectionQueryForm = {
  /** 配置 ID, 编辑场景下传入用于排除自身占用 */
  id?: number;
  /** 一级费用类型 ID */
  l1_category_id: number;
};
/**
 * 标品价格库二级权限-二级费用类型选项
 */
export type FeeTypeL2PermissionL2SelectionItemDto = {
  /** 二级费用类型 ID */
  l2_category_id?: number;
  /** 二级费用类型名称 */
  l2_category_name?: string;
  /** 是否可选, 默认可选; 已被其他配置占用时为 false */
  can_select?: boolean;
};
/**
 * 标品价格库二级权限-二级选项
 */
export type FeeTypeL2PermissionL2SelectionVo = {
  /** 二级费用类型选项列表 */
  l2_selections?: FeeTypeL2PermissionL2SelectionItemDto[];
  /** 可关联的授权用户列表 */
  users?: FeeTypeL2PermissionUserDto[];
};
export type ResponseFeeTypeL2PermissionL2SelectionVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: FeeTypeL2PermissionL2SelectionVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 创建标品价格库二级权限配置
 */
export type FeeTypeL2PermissionCreateForm = {
  /** 一级费用类型 */
  l1_category_id: number;
  /** 是否包含该一级下所有二级; 默认 false */
  all_l2?: boolean;
  /** 二级费用类型; allL2=false 时必填, allL2=true 时留空 */
  l2_category_ids?: number[];
  /** 授权用户 UserId 列表 */
  user_ids: number[];
};
export type AdSyncNewDto = {
  demand_id: number;
  dspf_mpc_no: string;
  force_sync?: boolean;
};
export type CommonConfigUrlDto = {
  dspf_mpc_no?: string;
  product_number?: string;
  requirement_id?: number;
  class1: string;
  class2: string;
  class3: string;
  class4?: string;
  department: string;
  demand_id?: number;
};
export type ResponseConfigUrlResult = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ConfigUrlResult;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 数易产品映射更新表单
 */
export type DspfMappingProductUpdateForm = {
  /** 主键ID */
  id: number;
  /** MPC产品ID */
  product_id: number;
  /** MPC产品名称 */
  product_name: string;
  /** 数易项目ID */
  dspf_project_id: string;
  /** 数易项目名称 */
  dspf_project_name: string;
};
/**
 * 查询数易维度列表表单
 */
export type QueryDspfDimensionForm = {
  /** 查询类型：channel_type / channel / game / agent */
  dimension_type: string;
  /** 搜索关键字，支持模糊匹配 */
  keyword?: string;
  /** 按ID列表精确查询，优先级高于keyword */
  ids?: number[];
};
/**
 * 数易维度列表查询结果
 */
export type DspfDimensionVo = {
  /** 匹配结果列表 */
  items?: Item[];
};
export type ResponseDspfDimensionVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DspfDimensionVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 数易产品映射分页查询
 */
export type DspfMappingProductPageQuery = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 关键字搜索（模糊匹配产品名称 OR 精确匹配产品编号） */
  keyword?: string;
};
/**
 * 数易产品映射
 */
export type DspfMappingProductVo = {
  /** 主键 */
  id?: number;
  /** MPC产品ID */
  product_id?: number;
  /** MPC产品Code */
  product_code?: string;
  /** MPC产品名称 */
  product_name?: string;
  /** 数易项目ID */
  dspf_project_id?: string;
  /** 数易项目名称 */
  dspf_project_name?: string;
  /** 操作人ID */
  operator_id?: number;
  operator?: UserSimpleVo;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
};
/**
 * 分页结果
 */
export type PageResultDspfMappingProductVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: DspfMappingProductVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultDspfMappingProductVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultDspfMappingProductVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseDspfMappingProductVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DspfMappingProductVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 数易产品映射创建表单
 */
export type DspfMappingProductCreateForm = {
  /** MPC产品ID */
  product_id: number;
  /** MPC产品名称 */
  product_name: string;
  /** 数易项目ID */
  dspf_project_id: string;
  /** 数易项目名称 */
  dspf_project_name: string;
};
/**
 * 渠道映射项
 */
export type ChannelItem = {
  /** MPC投放渠道ID */
  mpc_channel_id: string;
  /** MPC投放渠道名称 */
  mpc_channel_name: string;
  /** 数易渠道ID列表 */
  dspf_channel_ids?: string[];
};
/**
 * 产品绑定方式更新表单
 */
export type DspfMappingProductBindUpdateForm = {
  /** 主键ID */
  id: number;
  /** 渠道映射列表 */
  channels?: ChannelItem[];
  /** 一级需求类型ID */
  l1_category_id?: number;
  /** 绑定方式(1=手游渠道+代理商,2=广告渠道) */
  bind_type?: number;
};
/**
 * 产品绑定方式分页查询
 */
export type DspfMappingProductBindPageQuery = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 一级需求类型ID */
  l1_category_id?: number;
  /** MPC产品ID */
  product_id?: number;
  /** 绑定方式 */
  bind_type?: number;
};
/**
 * 渠道映射项
 */
export type ChannelVo = {
  /** 主键 */
  id?: number;
  /** MPC投放渠道ID */
  mpc_channel_id?: string;
  /** MPC投放渠道名称 */
  mpc_channel_name?: string;
  /** 数易渠道ID列表 */
  dspf_channel_ids?: string[];
  /** 数易渠道名称列表（与 dspf_channel_ids 一一对应） */
  dspf_channel_names?: string[];
};
/**
 * 产品绑定方式映射
 */
export type DspfMappingProductBindVo = {
  /** 主键 */
  id?: number;
  /** 渠道映射列表 */
  channels?: ChannelVo[];
  /** 一级需求类型ID */
  l1_category_id?: number;
  /** 一级需求类型名称 */
  l1_category_name?: string;
  /** MPC产品ID */
  product_id?: number;
  /** MPC产品名称 */
  product_name?: string;
  /** 绑定方式(1=手游渠道+代理商,2=广告渠道) */
  bind_type?: number;
  /** 绑定方式文字 */
  bind_type_label?: string;
  operator?: UserSimpleVo;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
};
/**
 * 分页结果
 */
export type PageResultDspfMappingProductBindVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: DspfMappingProductBindVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultDspfMappingProductBindVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultDspfMappingProductBindVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseDspfMappingProductBindVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DspfMappingProductBindVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 产品绑定方式创建表单
 */
export type DspfMappingProductBindCreateForm = {
  /** 渠道映射列表 */
  channels?: ChannelItem[];
  /** 一级需求类型ID */
  l1_category_id: number;
  /** MPC产品ID */
  product_id: number;
  /** 绑定方式(1=手游渠道+代理商,2=广告渠道) */
  bind_type: number;
};
/**
 * 数易供应商映射更新表单
 */
export type DspfMappingSupplierUpdateForm = {
  /** 主键ID */
  id: number;
  /** MPC供应商ID */
  supplier_id?: number;
  /** MPC供应商名称 */
  supplier_name?: string;
  /** 数易代理商ID */
  dspf_agent_id?: string;
  /** 数易代理商名称 */
  dspf_agent_name?: string;
};
/**
 * 数易供应商映射分页查询
 */
export type DspfMappingSupplierPageQuery = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** MPC供应商ID */
  supplier_id?: number;
  /** MPC供应商名称（模糊搜索） */
  supplier_name?: string;
  /** 数易代理商ID */
  dspf_agent_id?: string;
  /** 数易代理商名称（模糊搜索） */
  dspf_agent_name?: string;
};
/**
 * 数易供应商映射列表项
 */
export type DspfMappingSupplierListVo = {
  /** 主键 */
  id?: number;
  /** MPC供应商ID */
  supplier_id?: number;
  /** MPC供应商名称 */
  supplier_name?: string;
  /** MPC供应商名称 */
  supplier_code?: string;
  /** MPC供应商名称 */
  vmdm_code?: string;
  /** 供应商服务类型编码 */
  channel_type?: string;
  /** 供应商服务类型名称 */
  channel_type_name?: string;
  /** 数易代理商ID */
  dspf_agent_id?: string;
  /** 数易代理商名称 */
  dspf_agent_name?: string;
  /** 操作人ID */
  operator_id?: number;
  operator?: UserSimpleVo;
  /** 更新时间 */
  updated_at?: string;
};
/**
 * 分页结果
 */
export type PageResultDspfMappingSupplierListVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: DspfMappingSupplierListVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultDspfMappingSupplierListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultDspfMappingSupplierListVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 数易供应商映射详情
 */
export type DspfMappingSupplierDetailVo = {
  /** 主键 */
  id?: number;
  /** MPC供应商ID */
  supplier_id?: number;
  /** MPC供应商名称 */
  supplier_name?: string;
  /** MPC供应商名称 */
  supplier_code?: string;
  /** MPC供应商名称 */
  vmdm_code?: string;
  /** 供应商服务类型编码 */
  channel_type?: string;
  /** 供应商服务类型名称 */
  channel_type_name?: string;
  /** 数易代理商ID */
  dspf_agent_id?: string;
  /** 数易代理商名称 */
  dspf_agent_name?: string;
  /** 操作人ID */
  operator_id?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
};
export type ResponseDspfMappingSupplierDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DspfMappingSupplierDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 数易供应商映射创建表单
 */
export type DspfMappingSupplierCreateForm = {
  /** MPC供应商ID */
  supplier_id: number;
  /** MPC供应商名称 */
  supplier_name?: string;
  /** 数易代理商ID */
  dspf_agent_id: string;
  /** 数易代理商名称 */
  dspf_agent_name?: string;
};
/**
 * 数易渠道映射更新表单
 */
export type DspfMappingChannelUpdateForm = {
  /** 主键ID */
  id: number;
  /** MPC投放渠道ID */
  channel_id?: string;
  /** MPC投放渠道名称 */
  channel_name?: string;
  /** 数易手游渠道ID列表 */
  dspf_game_channels?: string[];
  /** 数易广告渠道ID列表 */
  dspf_ad_channels?: string[];
};
/**
 * 数易渠道映射分页查询
 */
export type DspfMappingChannelPageQuery = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** MPC投放渠道ID */
  channel_id?: string;
  /** MPC投放渠道名称（模糊搜索） */
  channel_name?: string;
};
/**
 * 数易渠道映射列表项
 */
export type DspfMappingChannelListVo = {
  /** 主键ID */
  id?: number;
  /** MPC投放渠道ID */
  channel_id?: string;
  /** MPC投放渠道名称 */
  channel_name?: string;
  /** 数易手游渠道列表（包含ID和名称） */
  dspf_game_channels?: DspfDimensionVo[];
  /** 数易广告渠道列表（包含ID和名称） */
  dspf_ad_channels?: DspfDimensionVo[];
  /** 操作人ID */
  operator_id?: number;
  operator?: UserSimpleVo;
  /** 更新时间 */
  updated_at?: string;
};
/**
 * 分页结果
 */
export type PageResultDspfMappingChannelListVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: DspfMappingChannelListVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultDspfMappingChannelListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultDspfMappingChannelListVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 数易渠道映射详情
 */
export type DspfMappingChannelDetailVo = {
  /** 主键ID */
  id?: number;
  /** MPC投放渠道ID */
  channel_id?: string;
  /** MPC投放渠道名称 */
  channel_name?: string;
  /** 数易手游渠道列表（包含ID和名称） */
  dspf_game_channels?: DspfDimensionVo[];
  /** 数易广告渠道列表（包含ID和名称） */
  dspf_ad_channels?: DspfDimensionVo[];
  /** 操作人ID */
  operator_id?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
};
export type ResponseDspfMappingChannelDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DspfMappingChannelDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 数易渠道映射创建表单
 */
export type DspfMappingChannelCreateForm = {
  /** MPC投放渠道ID */
  channel_id: string;
  /** MPC投放渠道名称 */
  channel_name: string;
  /** 数易手游渠道ID列表 */
  dspf_game_channels?: string[];
  /** 数易广告渠道ID列表 */
  dspf_ad_channels?: string[];
};
export type DemandMoveCheckFailTypeEnum =
  | 'BUDGET_OVER'
  | 'MULTI_QUOTE_BUDGET_OVER'
  | 'PREPAYMENT_IN_PROGRESS'
  | 'PREPAYMENT_COMPLETED_CONFIRM';
export type PreCheckResultVoObject = {
  check_result?: boolean;
  check_fail_type?: DemandMoveCheckFailTypeEnum;
  check_fail_msg?: string;
  check_fail_info?: object;
};
export type ResponsePreCheckResultVoObject = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PreCheckResultVoObject;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandMoveVo = {
  id?: number;
  before_state?: DemandState;
  before_sub_state?: string;
  before_reimburse_status?: ReimburseStatusEnum;
  current_state?: DemandState;
  current_sub_state?: string;
  current_reimburse_status?: ReimburseStatusEnum;
  pass?: boolean;
  child_demand_ids?: number[];
  message?: string;
};
export type ResponseDemandMoveVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandMoveVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandBatchMoveDto = {
  ids?: number[];
  type?: DemandState;
};
export type ResponseListDemandMoveVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandMoveVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandDetailsResDto = {
  /** 主键 */
  id?: number;
  /** 源表id */
  source_id?: number;
  /** 源类型 */
  source_type?: string;
  /** 源需求是否旧OA流程 */
  source_oa?: boolean;
  /** name */
  name?: string;
  /** product_id */
  product_id?: number;
  /** desc */
  desc?: string;
  /** number */
  number?: string;
  /** before_number */
  before_number?: string;
  /** expect_begin_at */
  expect_begin_at?: string;
  /** expect_end_at */
  expect_end_at?: string;
  priority?: PriorityEnum;
  /** 预算 */
  budget?: string;
  budget_currency?: CurrencyEnum;
  state?: DemandState;
  /** 子状态 */
  sub_state?: string;
  flow_flag?: FlowFlagEnum;
  purchasing_method?: PurchasingMethodEnum;
  purchasing_strategy?: PurchasingStrategyEnum;
  /** 采购方式展示字符串 */
  translated_purchasing_method?: string;
  /** 是否绑定执行数据 */
  is_bind_execution_data?: number;
  /** 合并询价版本号，0=旧版本/不适用，1=合并询价版本 */
  merged_inquiry_version?: number;
  /** root_id */
  root_id?: number;
  /** parent_id */
  parent_id?: number;
  /** lft */
  lft?: number;
  /** rgt */
  rgt?: number;
  /** author_id */
  author_id?: number;
  /** 需求类型 */
  demand_type?: number;
  group_type?: DemandGroupType;
  /** 策划人员配置 */
  planner_group_id?: number;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 四级分类ID */
  l4_category_id?: number;
  /** 考核类型 */
  evaluation_type?: number;
  support_type?: DemandSupportTypeEnum;
  /** need_cp_pay */
  need_cp_pay?: boolean;
  /** depth */
  depth?: number;
  /** suggestion */
  suggestion?: string;
  /** demander_accept_suggestion */
  demander_accept_suggestion?: string;
  /** has_demander_accept_suggestion */
  has_demander_accept_suggestion?: boolean;
  /** 结算金额 */
  settle_price?: string;
  settle_currency?: CurrencyEnum;
  /** 【20240704版本】【二级单】报价截止时间 */
  quote_deadline?: string;
  /** 报价金额 */
  quote_price?: string;
  /** 策划复核报价金额 */
  plan_accept_settle_price?: number;
  /** 已OA审批结算金额 */
  oa_approved_settle_price?: number;
  quote_currency?: CurrencyEnum;
  /** lock_version */
  lock_version?: number;
  /** score */
  score?: number;
  /** business_state */
  business_state?: number;
  /** win_supplier_ids */
  win_supplier_ids?: string;
  /** oa_id */
  oa_id?: string;
  /** 超额验收OA单号 */
  over_amount_oa_id?: string;
  /** 超额验收OA状态 */
  over_amount_oa_status?: string;
  /** reimbursement_number */
  reimbursement_number?: string;
  /** 【20240704版本】【三级单】供应商变更报价备注 */
  remark?: string;
  /** total_requote_price */
  total_requote_price?: number;
  /** notes */
  notes?: string;
  /** finished */
  finished?: boolean;
  /** reasons_for_differences */
  reasons_for_differences?: string;
  /** requirement_closed */
  requirement_closed?: boolean;
  /** no_contract_flag */
  no_contract_flag?: boolean;
  /** dspf_mpc_no */
  dspf_mpc_no?: string;
  /** dspf_desc */
  dspf_desc?: string;
  oa_status?: OaState;
  /** oa_rejection_reason */
  oa_rejection_reason?: string;
  /** 费控状态 */
  fee_control_state?: boolean;
  /** 提交OA时间 */
  oa_submit_at?: string;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** 当前订单支持的供应商库版本 */
  enable_supplier_version?: string;
  /** 是否启用结构化数据 */
  structured?: boolean;
  /** 预期 */
  expectation?: string;
  /** 需求项目背景 */
  background?: string;
  /** 是否需要供应商上传内容营销附件, 0:不需要, 1:需要, -1:不可配置 */
  need_content_marketing_attach?: number;
  /** 供应商内容营销作品状态，0=正常，1=异常 */
  content_marketing_attach_flag?: number;
  /** 本需求单创建的提示文本 */
  create_tips?: string;
  reimburse_status?: ReimburseStatusEnum;
  /** overall_score */
  overall_score?: number;
  /** 保存时间 */
  saved_time?: string;
  /** 0=原始，1=报价结构化 */
  structured_version?: number;
  /** 是否KPI特殊申报 */
  kpi_special_declaration_flag?: boolean;
  /** KPI特殊申报原因 */
  kpi_special_declaration_reason?: string;
  /** 低风险 */
  low_risk_special_report_flag?: boolean;
  /** 最后生效的调整单ID */
  latest_kpi_adjustment_id?: number;
  /** 是否支持绑定执行数据 */
  support_bind_execution_data?: boolean;
  /** 是否自动拆单规则创建的二级单 */
  auto_split?: boolean;
  /** 是否跳过超额验收审批 */
  skip_over_amount_approval?: boolean;
  /** 作者名称 */
  author_name?: string;
  author?: UserSimpleVo;
  /** 状态名称 */
  human_state_name?: string;
  /** 子状态名称 */
  human_sub_state_name?: string;
  /** 产品编号 */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 产品名称 */
  product_name?: string;
  product_area?: ProductAreaEnum;
  /** 优先级 */
  translated_priority?: string;
  /** 组类型 */
  translated_group_type?: string;
  /** 创建日期（精确到天） */
  root_created_on?: string;
  /** 状态节点 */
  bottom_tabs?: DemandDetailsResDtoBottomTab[];
  /** 是否可以被修改 */
  can_modify?: boolean;
  /** 是否已经填写核价信息 */
  submit_price_check?: boolean;
  /** 是否需要核价 */
  has_price_check?: boolean;
  platform_price_type?: PlatformPriceTypeEnum;
  /** 是否为KOL类型需求 */
  is_kol?: boolean;
  /** 是否为标品价格库核价品类（小喇叭运营服务/创作者运营服务）：前端据此走只读【查看核价结果】分支，区别于 KOL 可编辑商务核价 */
  is_price_library_check?: boolean;
  /** 层级名称（用-连接） */
  category_names?: string;
  /** 一级分类名称 */
  l1_category_name?: string;
  /** 是否支持方案审批，根据一级需求类型处理的 */
  support_plan_approve?: boolean;
  /** 是否超额验收 */
  over_amount_flag?: boolean;
  /** 超额验收金额 */
  over_amount_cny?: number;
  /** 关联二询单信息 */
  rel_demand_info?: RelDemandDto[];
  demand_supplier_settle_details?: DemandSupplierSettleDetailDto[];
  /** 评估得分 */
  evaluate_scores?: DemandDetailsResDtoEvaluateScoreDto[];
  /** 需求人员评估得分 */
  demand_manger_evaluate_scores?: DemandDetailsResDtoEvaluateScoreDto[];
  /** 需求预期 kpi指标列表 */
  expectation_kpi_indicators?: DemandKpiValueDto[];
  /** 需求人员 */
  demand_principals?: DemandPrincipalDto[];
  demand_plan_info?: JournalDemandDetailsResDtoDemandPlanInfoDto;
  /** 是否需全部报价 */
  need_all_quote?: boolean;
  /** 供应商详情 */
  demand_suppliers?: DemandDetailsResDtoDemandSupplierDto[];
  settle_report_attachs?: DemandDetailsResDtoAttachmentDto[];
  planner_report_attachs?: DemandDetailsResDtoAttachmentDto[];
  demand_evaluation_attachs?: DemandDetailsResDtoAttachmentDto[];
  /** 结算单附件 */
  statement_attachs?: DemandDetailsResDtoAttachmentDto[];
  /** 签章结算单附件 */
  signed_statement_attachs?: DemandDetailsResDtoAttachmentDto[];
  is_self_contact?: boolean;
  introduce_attachs?: DemandDetailsResDtoAttachmentDto[];
  permit_actions?: PermitActions;
  flow_action_info_value?: DemandFlowActionInfoValue;
  child_demands?: ChildDemandDto[];
  child_demand_summary?: DemandDetailsResDtoChildDemandSummaryDto;
  current_demand_supplier_id?: string;
  default_self_contact_id?: number;
  current_supplier_id?: string;
  member_planners?: MemberPlannerDto[];
  /** 是否修改权益、验收标准，在商务确认报价节点提交后，此字段会恢复默认值，默认值为：false */
  modify_rights_or_accept_desc_flag?: boolean;
  old_source_category?: string;
  kpi_type?: MediaKpiType;
  dspf_quota_data?: DspfQuotaData;
  remarks_tree?: RemarksTree;
  accept_remarks?: AcceptRemarks;
  qc_accept_priority?: QcAcceptPriorityEnum;
  root_demand_ext_data?: RootDemandExtColumnDto;
  bidding?: BiddingDto;
  /** 多轮轮次 */
  turns_number?: number;
  return_status?: AuditQuoteReturnStatusEnum;
  /** 退回状态文案 */
  translated_return_status?: string;
  /** 不需求走费控相关校验 */
  non_need_fee_control_verify?: boolean;
  /** 是否为可编辑的三级单 */
  is_edit_leaf_demand?: boolean;
  /** 已完成综合评分法的二级单 */
  multi_quota_closed_demand_id?: number;
  /** 推荐供应商列表 */
  referrer_supplier_dto_list?: DemandDetailsResDtoDemandSupplierDto[];
  /** 供应商推荐人姓名 */
  supplier_referrer?: string;
  /** 供应商推荐理由 */
  supplier_referral_reason?: string;
  /** 回避供应商列表 */
  avoidance_supplier_dto_list?: DemandDetailsResDtoDemandSupplierDto[];
  /** 供应商回避理由 */
  supplier_avoidance_reason?: string;
  /** 【20240704版本】【二级单】商务评估备注 */
  business_audit_quote_note?: string;
  /** OA审批概览 */
  oa_audit_desc_list?: OaProcessDescVo[];
  /** KPI调整单OA审批概览 */
  kpi_adjust_oa_audit_desc_list?: OaProcessDescVo[];
  /** 是否KPI特殊申报（发起OA方案与预算审批时使用） */
  kpi_special_declaration_flag_for_oa_submit?: boolean;
  /** 费用归属主体id */
  cost_company_id?: number;
  cost_company?: CostCompanyDto;
  /** 关联的小喇叭需求单ID */
  trumpet_demand_id?: number;
  /** 关联业务ID（如：小喇叭需求单ID、比选ID） */
  relate_biz_id?: number;
  relate_biz_type?: DemandRelateBizTypeEnum;
  relate_bidding_info?: BiddingInfo;
  quote_score_state?: QuoteScoreStateEnum;
  demand_supplier_score_rule?: DemandSupplierScoreRuleDto;
  /** 是否启用询比价综合评分 */
  quote_score_enable?: boolean;
  /** 是否支持询比价综合评分 */
  support_quote_score?: boolean;
  /** 询比价综合评分模版编码 */
  score_template_code?: string;
  /** 询比价综合评分模版名称 */
  score_template_name?: string;
  quote_upload_method?: DemandQuoteUploadMethodEnum;
  reject_info?: DemandRejectInfoVo;
  /** 合作内容结算信息明细 */
  cooperate_account_settle_list?: CooperateAccountSettleInfo[];
  /** 最近一次审批通过的超额验收OA单号 */
  last_approved_over_amount_oa_id?: string;
  /** 退回到的节点 */
  reverted_human_state_name?: string;
  /** 是否有策划负责人验收节点 */
  has_planner_leader_accept_node?: boolean;
  /** 根据需求类型得出报价类型，0=不含税金额，1=含税金额（报价结构化版本后有值） */
  quote_type?: number;
  /** 推送OA失败原因 */
  push_oa_fail_reason?: string;
  /** 是否展示KPI调整记录 */
  view_kpi_adjustment_record?: boolean;
  kpi_create_state?: DemandKpiCreateStateEnum;
  template_type?: DemandCategoryPropertyTemplateTypeEnum;
  /** 是否满足KPI特殊申报的适用范围 */
  kpi_special_declaration_range_flag?: boolean;
  /** 定标金额-三级单独有 */
  fee_approval_amount?: number;
  /** 是否可以发起追加预算（true=常规采购二级单 + 未绑定执行数据） */
  can_add_budget_advance?: boolean;
  /** 评分进度 */
  demand_supplier_score_progresses?: DemandSupplierScoreProgressDto[];
  /** 发票信息 */
  invoice_list?: InvoiceDetailDto[];
  invoice_belong_area?: InvoiceBelongAreaEnum;
  /** 是否海外INVOICE */
  is_oversea_invoice?: boolean;
  payment_doc_info?: DemandPaymentDocInfoDto;
  expense_doc_info?: DemandExpenseDocInfoDto;
  /** 预付单列表 */
  prepayment_list?: JournalDemandDetailsResDtoPrepaymentInfoDto[];
  budget_dashboard?: BudgetDashboardDto;
  /** 关联预算调整单列表 */
  budget_adjust_orders?: BudgetAdjustOrderSimpleVo[];
  /** 是否有预算变更记录（已生效的预算调整单） */
  has_budget_adjust?: boolean;
  /** 是否有考核指标调整记录（已生效的预算调整单中对KPI的实际变更） */
  has_budget_kpi_adjust?: boolean;
  sm_budget?: SmExpenseBudgetQueryVo;
  /** 是否需要上传/查看加热助推数据 */
  need_boost_effect_data?: boolean;
  /** 是否有监测链接可查看，true=有监测链接，前端可展示查看入口 */
  has_monitor_links?: boolean;
  /** 紧急需求补单审核材料 */
  urgent_order_restocking_attachs?: DemandDetailsResDtoAttachmentDto[];
  /** 是否早于定标执行 */
  special_matters_reporting?: boolean;
  /** 是否需要供应商提交合作内容维度的结算金额 */
  need_cooperate_account_supplier_settle_price?: boolean;
  /** 供应商提交合作内容维度的结算金额总计 */
  cooperate_account_supplier_settle_price_sum?: number;
  /** 内容营销结案结构化，1：新单子，0：老单子 */
  content_marketing_case_structured_version?: number;
  /** 策划复核和需求方验收是否可以修改最终结算金额 */
  can_modify_final_settle_price_when_accept?: boolean;
  /** 是否需要供应商提交结构化结案报告 */
  need_structured_settle_report?: boolean;
  /** 是否提交了结构化结案报告 */
  submitted_structured_settle_report?: boolean;
  /** 是否加急 */
  urgent?: boolean;
  /** 加急原因 */
  urgent_reason?: string;
  /** 加急环节 */
  urgent_step?: string;
  renew_type?: RenewTypeEnum;
  /** 常规采购续约单id */
  normal_renew_demand_id?: number;
  /** 小喇叭续约单id */
  trumpet_renew_demand_id?: number;
  /** 常规采购续约单标题 */
  normal_renew_demand_title?: string;
  /** 小喇叭续约单标题 */
  trumpet_renew_demand_title?: string;
  contract_info?: DemandContractInfoDto;
  /** 续签供应商列表 */
  renew_demand_supplier_list?: DemandDetailsResDtoDemandSupplierDto[];
  /** 是否全部上传截图（主平台/付费分发） */
  is_screenshot_done?: number;
  plan_type?: DemandPlanTypeEnum;
  demand_source_detail_vo?: DemandSourceDetailVo;
  relate_demand_source_dto?: RelateDemandSourceDto;
  relate_advance_price_check_dto?: RelateAdvancePriceCheckDto;
  demand_aireview_result?: DemandAiReviewResultDto;
  /** Muse资源创建状态：NOT_CREATED=未创建, CREATED=已创建, CREATE_FAILED=创建失败 */
  muse_resource_status?: string;
  /** Muse归档状态：NOT_ARCHIVED=未归档, ARCHIVED=已归档, ARCHIVE_FAILED=归档失败 */
  muse_archive_status?: string;
  /** Muse资源包链接 */
  muse_resource_url?: string;
  init_budget?: Money;
  /** 调整预算金额 */
  adjusted_budget?: number;
  final_budget?: Money;
  /** 最小预算调整金额 */
  min_adjusted_budget?: number;
  /** 是否超预算 */
  over_budget?: boolean;
  /** 超预算说明 */
  over_amount_remark?: string;
  '是否支持合作内容_a_i报价&整单报价'?: boolean;
  kpi_result?: DemandKpiAchievementStatus;
  /** 达标说明/未预期说明 */
  kpi_result_remark?: string;
  /** 供应商评分规则 */
  supplier_score_rules?: ScoreRuleDto[];
  is_bind_execution_data_enum?: IsAssociateExecutionDataEnum;
  /** 是否有KPI调整 */
  has_kpi_adjustment?: boolean;
  qc_first_review?: boolean;
};
export type ResponseListDemandDetailsResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandDetailsResDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商Id列表
 */
export type IndicatorCompareDto = {
  /** 指标名称 */
  name?: string;
  /** 期望值 */
  target_value?: string;
  /** 操作符号 */
  operator?: string;
};
/**
 * 供应商查询
 */
export type SupplierQueryForm = {
  /** 需求单Id */
  demand_id?: number;
  /** 供应商名称 */
  name?: string;
  /** 供应商分类 */
  supplier_category_list?: number[];
  /** 供应商Id列表 */
  supplier_id_list?: number[];
  /** 历史承接费用类型 */
  history_category_list?: number[];
  /** 服务类型列表 */
  service_type_list?: string[];
  /** 历史承接产品 */
  history_product_list?: number[];
  /** 供应商标签 */
  tags_id?: number[];
  recent_result_score?: IndicatorCompareDto;
  recent_overall_score?: IndicatorCompareDto;
  /** 指标过滤列表 */
  supplier_indicator_list?: IndicatorCompareDto[];
  purchasing_method?: PurchasingMethodEnum;
  /** 是否策划拆单场景（true=仅名称搜索+不返回风险备注，用于策划填写供应商字段） */
  for_plan_split?: boolean;
};
/**
 * 服务产品
 */
export type DemandSuppliersNewOptionVoProductVo = {
  /** 产品ID */
  product_id?: number;
  /** 产品编码 */
  product_identifier?: string;
  /** 产品名称 */
  product_name?: string;
};
/**
 * 供应商列表
 */
export type SupplierChooseItemDto = {
  supplier_category?: number;
  name?: string;
  value?: number;
  short_name?: string;
  channel_type?: string;
  channel_type_name?: string;
  single_source?: boolean;
  type?: number;
  type_desc?: string;
  relate_biz_type?: string;
  mdm_code?: string;
  /** 综合评分法中选的供应商标识 */
  multi_quote_winning_supplier_flag?: boolean;
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注数量 */
  risk_remark_count?: number;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 风险拦截标记 */
  risky?: boolean;
  /** 供应商系统标签 */
  sys_tags?: SupplierSysTagVo[];
  /** 服务产品 */
  products?: DemandSuppliersNewOptionVoProductVo[];
  /** 供应商标签 */
  tags?: TagVo[];
  /** 历史综合评分 */
  all_overall_score?: string;
  /** 历史过程打分 */
  all_processing_score?: string;
  /** 历史结果打分 */
  all_result_score?: string;
  /** 近期综合评分 */
  recent_overall_score?: string;
  /** 近期过程打分 */
  recent_processing_score?: string;
  /** 近期结果打分 */
  recent_result_score?: string;
  /** 供应商全名 */
  supplier_full_name?: string;
  /** 指标列表 */
  kpi_value_vos?: SupplierKpiValueVo[];
  /** 一级需求类型 ID 列表 */
  l1_category_ids?: number[];
  /** 二级需求类型 ID 列表 */
  l2_category_ids?: number[];
  /** 三级需求类型 ID 列表 */
  l3_category_ids?: number[];
  /** 四级需求类型 ID 列表 */
  l4_category_ids?: number[];
};
export type SupplierChooseDto = {
  /** 供应商分类 */
  supplier_category?: number;
  /** 供应商列表 */
  supplier_list?: SupplierChooseItemDto[];
};
export type ResponseListSupplierChooseDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierChooseDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type CommonBlobDto = {
  id?: number;
  /** 文件名 */
  filename?: string;
  /** 文件大小 */
  byte_size?: number;
  /** 文件类型 */
  content_type?: string;
  /** 文件key */
  key?: string;
  /** 是否自动生成 */
  auto_generated?: boolean;
};
/**
 * 附件列表
 */
export type CommonAttachmentDto = {
  id?: number;
  blob?: CommonBlobDto;
};
/**
 * 供应商结算报告，报价行维度
 */
export type DemandSupplierQuoteSettleReportDto = {
  /** 报价行key */
  quote_key?: string;
  /** 结案内容说明 */
  content?: string;
  /** 附件ID列表 */
  attach_blob_ids?: number[];
  /** 附件列表 */
  attachments?: CommonAttachmentDto[];
  /** Muse文件夹ID（资产交付类才有） */
  muse_folder_id?: string;
  /** Muse文件夹文件统计，格式：共xx个：JPG(xx)、PNG(xx) */
  muse_file_stats?: string;
};
/**
 * 表头信息
 */
export type DemandQuoteTableColumnDto = {
  /** 分组类型 0=合作内容，1=标品，2=引用属性，3=报价字段，4=分开平台字段，5=差旅，6=单价数量总价，7=AI */
  group_type?: number;
  /** 显示的列头 */
  label?: string;
  /** 列的属性key */
  code?: string;
  data_type?: DemandCooperatePropertyTypeEnum;
  required_mode?: RequiredModeEnum;
  required_condition?: RequiredConditionData;
  options?: ConditionOptionsData;
  /** 策划预填写 */
  prefill?: boolean;
  /** 供应商可修改 */
  outsource_modify?: boolean;
  /** 是否在设计阶段使用这个标品 */
  use_in_design_standard?: boolean;
  /** 标品列表 */
  standards?: DemandStandardCatalogBizVo[];
};
export type DemandSupplierQuoteWithSettleReportVo = {
  /** 明细结案信息 */
  settle_reports?: DemandSupplierQuoteSettleReportDto[];
  /** 报价明细数据 */
  quote_data?: {
    [key: string]: any;
  }[];
  /** 表头信息 */
  columns?: DemandQuoteTableColumnDto[];
  demand_aireview_result?: DemandAiReviewResultDto;
};
export type ResponseDemandSupplierQuoteWithSettleReportVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSupplierQuoteWithSettleReportVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandPlannerDto = {
  id?: number;
  user_id?: number;
  destroy?: boolean;
};
/**
 * 旧时合并平台，结构化不需要
 */
export type SuperiorResourceShipDto = {
  id?: number;
  superior_resource_id?: number;
  destroy?: boolean;
};
/**
 * 权益信息
 */
export type DemandRightsValueDto = {
  /** 权益编码 */
  rights_code: string;
  /** 权益库标准权益 */
  content: string;
  /** 本单实际权益内容 */
  actual_content: string;
};
/**
 * 【结构化】【通用】合作内容列表，只处理非直接考核的指标
 */
export type DemandCooperateAccountDto = {
  id?: number;
  /** 旧时的账号，结构化后不需要 */
  account?: string;
  /** 备注 */
  remark?: string;
  /** 旧时合并平台，结构化不需要 */
  superior_resource_ships?: SuperiorResourceShipDto[];
  /** 序号，只读 */
  index_id?: number;
  destroy?: boolean;
  /** 供应商提报的结算金额 */
  supplier_settle_price?: number;
  /** 合作内容维度的结案报告Blob Id列表 */
  case_close_report_blob_ids?: number[];
  /** 【结构化】合作内容属性值 */
  property_values?: DemandCooperatePropertyValueDto[];
  /** 【结构化】kpi指标列表 */
  kpi_values?: DemandKpiValueDto[];
  /** 必要权益列表 */
  necessary_rights?: DemandRightsValueDto[];
  /** 期望权益列表 */
  expect_rights?: DemandRightsValueDto[];
  /** 策划预填信息json */
  prefill_quote_config?: string;
  /** 是否需包含AI */
  need_ai?: boolean;
};
/**
 * 报价明细数据DTO - 该结构前端在线表格可识别
 */
export type DemandSupplierQuoteRowDto = {
  /** 报价key */
  quote_key?: string;
  /** 合作内容ID */
  cooperate_account_id?: number;
  /** 标品1code */
  standard_code1?: string;
  /** 标品2code */
  standard_code2?: string;
  /** 标品3code */
  standard_code3?: string;
  child_field_map?: {
    [key: string]: any;
  };
  /** 差旅餐饮项目 */
  travel_catering_item?: string[];
  /** 差旅餐饮费用 */
  travel_catering_price?: number;
  /** 差旅餐饮描述 */
  travel_catering_desc?: string;
  /** 单价 */
  unit_price?: number;
  /** 数量1 */
  quantity1?: number;
  /** 单位1 */
  unit1?: string;
  /** 数量2 */
  quantity2?: number;
  /** 单位2 */
  unit2?: string;
  /** 总价 */
  total_price?: number;
  /** 是否使用AI */
  is_use_ai?: boolean;
  /** AI占比 */
  ai_ratio?: number;
};
export type DemandQuotaDto = {
  quota_id?: number;
  quota_type?: DemandQuotaTypeEnum;
  quota_name?: DemandQuotaName;
  quota_value?: number;
  destroy?: boolean;
};
export type DemandOperateSourceEnum = 'DEFAULT' | 'CONTENT_MARKETING';
/**
 * 策划单
 */
export type DemandSourcePlanInfo = {
  /** demand_id */
  demand_id?: number;
  /** budget */
  budget?: number;
  /** 开始日期 */
  begin_at?: string;
  /** 结束日期 */
  end_at?: string;
  /** 需求详情 */
  description?: string;
  /** 需求相关附件 */
  plan_attachments?: Attachment[];
};
/**
 * 需求内容
 */
export type DemandSourceContentCreateCmd = {
  /** 合作主平台 */
  main_platform?: string[];
  /** 发布开始时间 */
  begin_at?: string;
  /** 发布结束时间 */
  end_at?: string;
  /** 需求内容 */
  content_desc?: string;
  /** 主平台官单/私单 */
  public_order?: string[];
};
/**
 * 达人
 */
export type DemandSourceKolCreateCmd = {
  /** 合作主平台 */
  main_platform?: string;
  /** 主平台账号 */
  main_platform_account?: string;
  /** 主平台主页链接 */
  main_platform_link?: string;
  /** 主平台官单/私单 */
  public_order?: string;
  /** 主平台报价项目 */
  main_platform_quote_item?: string;
  /** 达人添加来源 */
  import_type?: string;
};
/**
 * 定制排查
 */
export type DemandSourceCustomInspectionCreateCmd = {
  /** 合作主平台 */
  main_platform?: string;
  /** 定制排查字段 */
  custom_inspection_fields?: CustomInspectionField[];
  /** 定制排查模板 */
  custom_inspection_template_id?: number;
};
/**
 * 一询单状态
 */
export type DemandSourceState =
  | 'PENDING_DESIGN'
  | 'PENDING_ASSESS'
  | 'PLANNER_AUDIT_QUOTE'
  | 'MARKETING_AUDIT_QUOTE'
  | 'PENDING_QUOTE'
  | 'CLOSED'
  | 'ABANDON';
/**
 * 一询单创建参数
 */
export type DemandSourceCreateCmd = {
  id?: number;
  /** name */
  name?: string;
  /** product_id */
  product_id?: number;
  /** desc */
  desc?: string;
  /** number */
  number?: string;
  /** expect_begin_at */
  expect_begin_at?: string;
  /** expect_end_at */
  expect_end_at?: string;
  /** 预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  /** root_id */
  root_id?: number;
  /** parent_id */
  parent_id?: number;
  /** author_id */
  author_id?: number;
  /** 需求类型 */
  demand_type?: number;
  group_type?: DemandGroupType;
  /** 策划人员配置 */
  planner_group_id?: number;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 四级分类ID */
  l4_category_id?: number;
  /** depth */
  depth?: number;
  /** remark */
  remark?: string;
  /** notes */
  notes?: string;
  /** 预期 */
  expectation?: string;
  /** 需求项目背景 */
  background?: string;
  /** 需求负责人 */
  demand_principals_user_id_list?: number[];
  plan_info?: DemandSourcePlanInfo;
  /** 策划单 */
  demand_member_plan_user_id_list?: number[];
  /** 策划单 */
  demand_member_business_user_id_list?: number[];
  /** 需求内容 */
  demand_source_content?: DemandSourceContentCreateCmd[];
  /** 达人 */
  demand_source_kol?: DemandSourceKolCreateCmd[];
  /** 定制排查 */
  demand_source_custom_inspection?: DemandSourceCustomInspectionCreateCmd[];
  state?: DemandSourceState;
  /** 策划拆单阶段达人推荐维度权重 */
  plan_kol_recommend_weights?: DimensionWeight[];
};
/**
 * 需求计划信息
 */
export type DemandPlanInfoDto = {
  budget?: number;
  /** 是否预算调整拆单 */
  budget_adjust_split?: boolean;
  /** 是否自动拆单（跳过预算超额校验，停留在策划设计） */
  auto_split?: boolean;
  /** 是否追加预算（true=是，false=否） */
  add_budget?: boolean;
  /** 关联需求二级单 ID（addBudget=true 时必填） */
  related_id?: number;
  /** 关联需求二级单 ID 列表（addBudget=true 时必填，取代 relatedId） */
  related_ids?: number[];
  description?: string;
  l2_category_id: number;
  l3_category_id: number;
  l4_category_id?: number;
  publish_date?: string;
  /** 开始日期 */
  begin_at?: string;
  /** 结束日期 */
  end_at?: string;
  plan_attachs_blob_ids?: number[];
  is_bind_execution_data?: IsAssociateExecutionDataEnum;
  /** 是否早于定标执行 */
  special_matters_reporting?: boolean;
  demand_planners: DemandPlannerDto[];
  /** 整单验收要求 */
  accept_desc?: string;
  gradient?: GradientEnum;
  /** 策划阶段供应商ID（写入 demand_plan_supplier 表，不写入 demand_supplier） */
  plan_supplier_id?: number;
  /** 整单验收要求-分行 */
  accept_list?: string[];
  /** 【结构化】【所有】合作内容列表 */
  cooperate_accounts?: DemandCooperateAccountDto[];
  /** 【结构化】【绑定执行数据】主要指标列表 */
  main_kpi_values?: DemandKpiValueDto[];
  /** 【结构化】【绑定执行数据】其他指标列表 */
  other_kpi_values?: DemandKpiValueDto[];
  /** 【结构化】【内容营销】指标列表 */
  common_kpi_values?: DemandKpiValueDto[];
  /** 【结构化】【绑定执行数据】辅助自定义指标范围 */
  auxiliary_kpi_values?: DemandKpiValueDto[];
  /** 【结构化】合作内容指标列表 */
  cooperate_kpi_values?: DemandKpiValueDto[];
  /** 供应商报价明细表 - 预填内容 */
  prefill_quote_data?: DemandSupplierQuoteRowDto[];
  business_user_ids?: number[];
  demand_quotas?: DemandQuotaDto[];
  operate_source?: DemandOperateSourceEnum;
  kpi_type?: MediaKpiType;
  /** 续约常规采购单 */
  normal_renew_demand_id?: number;
  /** 续约批次 */
  renewal_count?: number;
  /** 是否需全部报价 */
  need_all_quote?: boolean;
  /** 是否综合评分法的策划单，如果是的情况下只能有一个方案 */
  multi_quota_plan?: boolean;
  demand_source_create_cmd?: DemandSourceCreateCmd;
  actual_kpi_type?: MediaKpiType;
};
export type SplitDemandPlanInfoForm = {
  demand_id?: number;
  plan_infos: DemandPlanInfoDto[];
};
export type ResponseListLong = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: number[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 追加预算关联单号搜索请求
 */
export type SearchRelatedDemandForm = {
  /** L3 品类 ID（三级需求类型） */
  l3_category_id: number;
  /** 产品ID */
  product_id: number;
  /** 搜索关键字（当前支持需求单 ID 精确匹配，后续可扩展标题模糊匹配） */
  related_demand_key?: string;
  /** 关联单据列表 */
  related_demand_ids?: number[];
};
/**
 * 关联需求二级单基本信息
 */
export type RelatedDemandInfoVo = {
  /** 需求单 ID */
  demand_id?: number;
  /** 项目 */
  number?: string;
  /** 需求标题 */
  demand_title?: string;
  /** 产品名称 */
  product_name?: string;
};
export type ResponseListRelatedDemandInfoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: RelatedDemandInfoVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandReqDto = {
  id?: number;
};
export type StateNotifyVo = {
  id?: number;
  notify_id?: number;
  notify_type?: string;
  state?: DemandState;
  updated_at?: string;
};
export type ReminderResDto = {
  success?: boolean;
  state_notify?: StateNotifyVo;
  msg?: string;
};
export type ResponseReminderResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ReminderResDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 查询参数
 */
export type QuerySimilarDemandsForPaymentDto = {
  /** 需求单号（三级） */
  id: number;
  /** 最终结算金额 */
  final_settle_price?: number;
  final_settle_currency?: CurrencyEnum;
};
export type DemandSimplePaymentInfoVo = {
  /** 需求单号 */
  id?: number;
  group_type?: DemandGroupType;
  /** 需求单类型 */
  mod?: number;
  /** 需求类型 */
  l1_category_name?: string;
  /** 需求人员 */
  demand_principals?: UserSimpleVo[];
  /** 策划人员 */
  demand_planners?: UserSimpleVo[];
  /** 商务 */
  demand_businesses?: UserSimpleVo[];
  /** 最终结算金额 */
  final_settle_price?: number;
  final_settle_currency?: CurrencyEnum;
  /** 需求执行预计开始日期 */
  expect_begin_date?: string;
  /** 需求执行预计结束日期 */
  expect_end_date?: string;
  /** 创建时间 */
  created_at?: string;
};
export type ResponseListDemandSimplePaymentInfoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSimplePaymentInfoVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 查询超额相关信息
 */
export type QueryOverAmountInfoForm = {
  /** 需求ID */
  demand_id: number;
  /** 结算金额 */
  settle_price: number;
};
export type OverAmountInfoDto = {
  currency?: CurrencyEnum;
  /** 报价币种，原币种 */
  quote_price?: number;
  /** 报价币种，人民币计 */
  quote_price_cny?: number;
  /** 传入的结算币种，原币种 */
  settle_price?: number;
  /** 传入的结算币种，人民币计 */
  settle_price_cny?: number;
  /** 超出的金额，原币种 */
  over_amount?: number;
  /** 超出的金额，人民币计 */
  over_amount_cny?: number;
};
export type ResponseOverAmountInfoDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OverAmountInfoDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type PmoEditDemandForm = {
  demand_id?: number;
  l1_category_id?: number;
  demand_plan_user_ids: number[];
  background?: string;
  name?: string;
};
export type DemandTemporaryForm = {
  /** 一询单id */
  demand_id?: number;
  /** 暂存内容 */
  content?: string;
};
/**
 * 二级单拒绝信息
 */
export type DbrfDemandRejectInfoForm = {
  /** 需求单号 */
  demand_id?: number;
};
/**
 * 合并询报价 - 批量驳回入参
 */
export type PlanDemandBatchRejectForm = {
  /** 一级单ID */
  demand_id: number;
  state: DemandState;
  /** 驳回原因 */
  reject_reason: string;
  /** 二级单拒绝信息列表 */
  demands?: DbrfDemandRejectInfoForm[];
};
export type FailedInfo = {
  message?: string;
  demand_id?: number;
};
export type DemandBatchAcceptResultDto = {
  success_count?: number;
  fail_count?: number;
  failure_details?: FailedInfo[];
  all_success?: boolean;
};
export type ResponseDemandBatchAcceptResultDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandBatchAcceptResultDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type AdSettleWayEnum = 'BUYOUT_PRICE' | 'BY_CONSUME';
export type CooperateAccount = {
  id?: number;
  remark?: string;
};
export type DemandPlanInfo = {
  id?: number;
  cooperate_accounts?: CooperateAccount[];
};
/**
 * 核价备注附件列表
 */
export type PriceCheckRemarkFileDto = {
  /** 附件ID（blobId） */
  id?: number;
};
/**
 * 核价备注信息（用于非KOL类型需求单核价）
 */
export type PriceCheckRemarkDto = {
  /** 核价备注文本内容 */
  text?: string;
  /** 核价备注附件列表 */
  files?: PriceCheckRemarkFileDto[];
};
export type BusinessauditquoteDemandSupplierQuoteDto = {
  id: number;
  quote_price: number;
  winning: boolean;
  currency: CurrencyEnum;
  /** 报价金额（优惠前、不含税） */
  quote_price_no_tax?: number;
  /** 报价金额(优惠前、含税和服务费) */
  quote_price_with_tax?: number;
  /** 报价金额(优惠后、不含税) */
  discount_quote_price_no_tax?: number;
  /** 报价金额(优惠后、含税和服务费) */
  discount_quote_price_with_tax?: number;
  /** 报价详情附件 */
  quote_attachs_blob_keys?: string[];
  /** 单项报价的评分 */
  score?: number;
  /** 比稿折扣 */
  bi_gao_discount?: number;
  /** 比稿税服 */
  bi_gao_tax?: number;
  /** 溢价比（用于非KOL类型需求单核价） */
  premium_ratio?: number;
  /** 核价结果（用于非KOL类型需求单核价） */
  price_check_result?: string;
  price_check_remark?: PriceCheckRemarkDto;
  /** AI降本率（百分比数字），范围 [0, 100]，如 53.66 表示 53.66%，仅中选供应商需要填写 */
  ai_cost_reduction_rate?: number;
};
/**
 * 平台核价组列表
 */
export type PlatformPriceGroupDto = {
  /** 合作主平台 */
  main_platforms: string[];
  /** 主平台官单/私单 */
  platform_types: string[];
  /** 比稿折扣 */
  bi_gao_discount: number;
  /** 比稿税服 */
  bi_gao_tax: number;
};
export type BusinessauditquoteDemandSupplierDto = {
  id?: number;
  supplier_id?: string;
  /** 合同编号 */
  contract_no?: string;
  /** 是否无合同 */
  no_contract_flag?: boolean;
  is_quoted?: boolean;
  advance_info?: string;
  re_quotation_reason?: string;
  overall_score?: number;
  contract_source_type?: ContractSourceTypeEnum;
  contract_sign_type?: ContractSignTypeEnum;
  contract_method?: DemandContractMethodEnum;
  /** 在提交付款时填写合同 */
  fill_contract_on_submit_reimburse?: boolean;
  demand_supplier_quotes?: BusinessauditquoteDemandSupplierQuoteDto[];
  /** 平台核价组列表 */
  platform_prices?: PlatformPriceGroupDto[];
  supplier_category?: number;
};
/**
 * 二级单确认信息（各节点共用，按 state 区分必填字段）
 */
export type PlanDemandBatchConfirmFormDemandConfirmInfoForm = {
  /** 二级单ID */
  demand_id: number;
  /** 是否需要提交，前端勾选后传true */
  need_accept?: boolean;
  purchasing_method?: PurchasingMethodEnum;
  purchasing_strategy?: PurchasingStrategyEnum;
  /** [PENDING_ASSESS] 报价截止时间 */
  quote_deadline?: string;
  flow_flag?: FlowFlagEnum;
  /** [PENDING_ASSESS] 评分模板编码 */
  score_template_code?: string;
  /** [PENDING_ASSESS] 紧急补单附件blobId列表 */
  urgent_order_restocking_attachs_blob_ids?: number[];
  ad_settle_way?: AdSettleWayEnum;
  support_type?: DemandSupportTypeEnum;
  demand_plan_info?: DemandPlanInfo;
  /** [PENDING_ASSESS / BUSINESS_AUDIT_QUOTE] 是否需要CP付费 */
  need_cp_pay?: boolean;
  supplier?: BusinessauditquoteDemandSupplierDto;
  /** [BUSINESS_AUDIT_QUOTE] 是否需要评分 */
  need_score_state?: boolean;
  /** [BUSINESS_AUDIT_QUOTE] 评分附件blobKey列表 */
  demand_evaluation_attachs_blob_keys?: string[];
  /** [BUSINESS_AUDIT_QUOTE] 费用采购主体/费用归属公司ID */
  cost_company_id?: number;
  /** [品类商务负责人确认报价/策划负责人确认报价] 风险提示（纯文本） */
  risk_tip?: string;
  /** [全节点] 附件ID列表，映射到 DemandMoveDto 外层 attachmentList */
  attachment_list?: number[];
  /** [全节点] 附件ID列表，映射到 DemandMoveDto 外层 attachmentList */
  note?: string;
};
/**
 * 合并询报价 - 批量确认统一入参（适用全部4个节点）
 */
export type PlanDemandBatchConfirmForm = {
  /** 一级单ID */
  demand_id: number;
  state: DemandState;
  /** 二级单确认信息列表 */
  demands?: PlanDemandBatchConfirmFormDemandConfirmInfoForm[];
};
/**
 * 分配的结算金额
 */
export type OverAmountCooperateAllocationForm = {
  /** 合作内容ID */
  cooperate_account_id?: number;
  /** 结算金额 */
  settle_price?: number;
};
/**
 * 编辑最终结算金额表单
 */
export type SaveOrSubmitOverAmountForm = {
  /** 需求单ID */
  demand_id: number;
  /** 最终结算金额 */
  settle_amount: number;
  /** 超额验收金额 */
  over_amount_cny?: number;
  /** 超额审批原因 */
  over_amount_reason?: string;
  /** 是否提交 */
  is_submit?: boolean;
  /** 分配的结算金额 */
  cooperate_allocations?: OverAmountCooperateAllocationForm[];
};
/**
 * 获取报价表格数据请求入参
 */
export type DemandMonthReleaseDto = {
  /** 关联的ID */
  demand_id?: number;
  /** 本次消耗的月份 */
  month?: string;
  /** 本次消耗的金额 */
  amount?: number;
};
export type ModifyQuoteDetail = {
  id: number;
  quote_price: number;
};
export type ModifyQuoteDto = {
  id?: number;
  note?: string;
  requote_price?: ModifyQuoteDetail[];
};
export type ModifyContractNoDto = {
  /** 需求单号 */
  id: number;
  contract_method: DemandContractMethodEnum;
  /** 合同号 */
  contract_no?: string;
};
export type LeftNavsVo = {
  id?: number;
  name?: string;
  parent_id?: string;
  depth?: number;
  state?: DemandState;
  /** 二级单标识 */
  flag?: string;
};
export type ResponseListLeftNavsVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: LeftNavsVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type CooperateAccountEditDto = {
  /** 供应商报价表主键ID */
  demand_supplier_quote_id: number;
  /** 是否需要实际执行 */
  need_actual_execution: boolean;
  /** 是否含差旅餐补等实报实销，1=是，0=否 */
  contain_travel_expenses_reimbursement?: boolean;
  /** 是否星图度量测试，true=是，false=否 */
  xingtu_measure_test?: boolean;
  /** 抖音分发是否星图度量，true=是，false=否 */
  douyin_distribute_xingtu_measure?: boolean;
};
export type PlanUserEditLeavesForm = {
  demand_id: number;
  cooperate_accounts: CooperateAccountEditDto[];
};
/**
 * 供应商查询
 */
export type SupplierByIdsQueryForm = {
  /** 需求单Id */
  demand_id?: number;
  /** 供应商Id列表 */
  supplier_id_list?: number[];
};
export type ResponseListSupplierChooseItemDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierChooseItemDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 获取报价表格数据请求入参
 */
export type GetQuoteTableDataForm = {
  /** 需求单ID */
  demand_id?: number;
  /** 供应商ID */
  supplier_id?: number;
  /** 三级需求类型ID */
  l3_category_id?: number;
};
export type QuoteUnitTypeEnum = 0 | 1 | 2;
export type DemandQuoteTableDto = {
  /** 列信息 */
  columns?: DemandQuoteTableColumnDto[];
  /** 报价类型:0=不含税金额；1=含税金额 */
  quote_type?: number;
  unit_type?: QuoteUnitTypeEnum;
  template_type?: DemandCategoryPropertyTemplateTypeEnum;
  /** 报价实际数据 */
  data?: {
    [key: string]: any;
  }[];
  /** 报价暂存数据 */
  temp_data?: {
    [key: string]: any;
  }[];
  /** 报价预填数据 */
  pre_fill_data?: {
    [key: string]: any;
  }[];
  /** 上一次暂存时间 */
  last_temp_save_time?: string;
  /** 报价模板版本号 */
  version?: number;
  /** 是否包含AI列 */
  contain_ai?: boolean;
};
export type ResponseDemandQuoteTableDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandQuoteTableDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseDemandDetailsResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandDetailsResDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 导出预填报价表格数据请求入参
 */
export type ExportPrefillQuoteTableDataForm = {
  /** 需求单ID */
  demand_id?: number;
  /** 三级需求类型ID */
  l3_category_id?: number;
  /** 当前填写的预填报价数据 */
  data?: {
    [key: string]: any;
  }[];
};
/**
 * 常规采购导出
 */
export type DemandExportForm = {
  /** 三级需求类型ID */
  l3_category_id?: number;
  /** 合作内容列表 */
  cooperate_account_dto_list?: DemandCooperateAccountDto[];
};
export type EditDemandForm = {
  priority?: PriorityEnum;
  name: string;
  desc?: string;
  l1_category_id?: number;
  /** 二级需求类型ID */
  l2_category_id?: number;
  /** 策划人员配置ID */
  planner_group_id?: number;
  product_id?: number;
  number?: string;
  expect_begin_at?: string;
  expect_end_at?: string;
  budget?: number;
  budget_currency?: CurrencyEnum;
  introduce_attachs_blob_ids?: number[];
  urgent_order_restocking_attachs_blob_ids?: number[];
  demand_principals_user_ids: number[];
  demand_plan_user_ids?: number[];
  background?: string;
  expectation?: string;
  purchasing_method?: PurchasingMethodEnum;
  demand_supplier_ids?: number[];
  apply_reason?: string;
  usage_plan?: string;
  renew_type?: RenewTypeEnum;
  /** 关联小喇叭续约单id */
  trumpet_renew_demand_id?: number;
  /** 常规采购续约单id */
  normal_renew_demand_id?: number;
  /** 供应商推荐人姓名 */
  supplier_referrer?: string;
  /** 推荐供应商ID列表 */
  referrer_supplier_id_list?: number[];
  /** 供应商推荐理由 */
  supplier_referral_reason?: string;
  /** 回避供应商ID列表 */
  avoidance_supplier_id_list?: number[];
  /** 供应商回避理由 */
  supplier_avoidance_reason?: string;
  /** kpi指标列表 */
  expectation_kpi_indicators?: DemandKpiValueDto[];
  /** 是否KPI特殊申报 */
  kpi_special_declaration_flag?: boolean;
  /** KPI特殊申报原因 */
  kpi_special_declaration_reason?: string;
  demand_id?: number;
  demand_plan_user_ids_for_long_list?: number[];
};
export type EditDemandPlanInfoForm = {
  demand_id?: number;
  /** 预算 */
  budget?: number;
  demand_planners: DemandPlannerDto[];
  business_user_ids?: number[];
  /** 是否早于定标执行 */
  special_matters_reporting?: boolean;
  /** 常规采购续约单id */
  normal_renew_demand_id?: number;
  /** 开始时间 */
  begin_at?: string;
  /** 结束时间 */
  end_at?: string;
};
export type EditDemandLeavesForm = {
  demand_id?: number;
  /** 是否需要供应商上传内容营销附件, 0:不需要, 1:需要 */
  need_content_marketing_attach?: number;
};
export type EditAuxiliaryKpiForm = {
  demand_id?: number;
  /** 辅助自定义指标列表 */
  auxiliary_kpi_values?: DemandKpiValueDto[];
};
/**
 * 编辑需求评分权重表单
 */
export type EditDemandQuoteWeightForm = {
  /** 需求id */
  demand_id: number;
  /** 商务权重 */
  business_weight: string;
  /** 需求方权重 */
  demand_weight: string;
  /** 策划权重 */
  plan_weight: string;
  /** 备注 */
  note?: string;
};
/**
 * 差异明细
 */
export type DiffDetailString = {
  /** 供应商报价填写内容 */
  supplier_value?: string;
  /** 策划预填内容 */
  prefill_value?: string;
  /** 差异类型 */
  diff_type?: number;
};
/**
 * 差异明细
 */
export type DiffDetailObject = {
  /** 供应商报价填写内容 */
  supplier_value?: object;
  /** 策划预填内容 */
  prefill_value?: object;
  /** 差异类型 */
  diff_type?: number;
};
/**
 * 差异明细
 */
export type DiffDetailBigDecimal = {
  /** 供应商报价填写内容 */
  supplier_value?: number;
  /** 策划预填内容 */
  prefill_value?: number;
  /** 差异类型 */
  diff_type?: number;
};
/**
 * 差异明细
 */
export type DiffDetailInteger = {
  /** 供应商报价填写内容 */
  supplier_value?: number;
  /** 策划预填内容 */
  prefill_value?: number;
  /** 差异类型 */
  diff_type?: number;
};
/**
 * 报价明细对比数据
 */
export type DemandSupplierQuoteDiffDetailVo = {
  /** 合作内容ID */
  cooperate_account_id?: number;
  /** 分发平台合并逻辑：quoteKey相同即需要合并 */
  quote_key?: string;
  standard_code1?: DiffDetailString;
  standard_code2?: DiffDetailString;
  standard_code3?: DiffDetailString;
  travel_catering_item?: DiffDetailObject;
  travel_catering_price?: DiffDetailBigDecimal;
  travel_catering_desc?: DiffDetailString;
  unit_price?: DiffDetailBigDecimal;
  quantity1?: DiffDetailBigDecimal;
  unit1?: DiffDetailString;
  quantity2?: DiffDetailBigDecimal;
  unit2?: DiffDetailString;
  total_price?: DiffDetailBigDecimal;
  is_use_ai?: DiffDetailInteger;
  ai_ratio?: DiffDetailBigDecimal;
  /** 标识行差异类型 */
  line_diff_type?: number;
};
export type DemandSupplierQuoteDiffVo = {
  /** 报价明细对比数据 */
  diff_quote_data?: DemandSupplierQuoteDiffDetailVo[];
  /** 报价明细数据 */
  quote_data?: {
    [key: string]: any;
  }[];
  /** 表头信息 */
  columns?: DemandQuoteTableColumnDto[];
};
export type ResponseDemandSupplierQuoteDiffVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSupplierQuoteDiffVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商详情
 */
export type DemandSupplierHisDto = {
  id?: number;
  /** demand_id */
  demand_id?: number;
  /** supplier_id */
  supplier_id?: number;
  /** is_quoted */
  is_quoted?: boolean;
  /** is_self_contact */
  is_self_contact?: boolean;
  /** advance_info */
  advance_info?: string;
  /** 【20240704版本】【三级单】合同编号 */
  contract_no?: string;
  /** 合同名称 */
  contract_name?: string;
  /** no_contract_flag */
  no_contract_flag?: boolean;
  contract_source_type?: ContractSourceTypeEnum;
  contract_sign_type?: ContractSignTypeEnum;
  /** 报价附件的有效标识 */
  quote_attachment_valid_flag?: boolean;
  /** turns_number */
  turns_number?: number;
  /** overall_score */
  overall_score?: number;
  /** 商务核价状态（供应商维度：unchecked 未核价 / checked 已核价） */
  business_price_check_status?: string;
  created_at?: string;
  updated_at?: string;
  demand_supplier_quotes?: DemandDetailsResDtoDemandSupplierQuoteDto[];
  last_round_quotes?: DemandDetailsResDtoDemandSupplierQuoteDto[];
  /** 合并报价附件 */
  merge_quote_attachs?: DemandDetailsResDtoAttachmentDto[];
  bid_attachs?: DemandDetailsResDtoAttachmentDto[];
  /** 供应商名称，带渠道类型 */
  supplier_full_name?: string;
  /** 供应商名称，主供应商名称 */
  supplier_name?: string;
  /** VMDM code */
  mdm_code?: string;
  /** 供应商渠道类型 */
  supplier_channel_type_name?: string;
  contract_balance?: string;
  /** 【20240704版本】【三级单】处理后的合同编号 */
  contract_code?: string;
  supplier_short_name?: string;
  city?: string;
  /** 供应商评分 */
  score?: number;
  /** 报价类型:0=不含税金额；1=含税金额 */
  quote_type?: number;
  /** 税率 */
  tax_rate?: number;
  /** 是否收取服务费,0=不收取,1=收取 */
  service_fee_mode?: number;
  /** 服务费率 */
  service_fee_rate?: number;
  currency?: CurrencyEnum;
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注数量 */
  risk_remark_count?: number;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 风险拦截标记 */
  risky?: boolean;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 是否重新报价 */
  re_quotation_flag?: boolean;
  /** 重新报价原因 */
  re_quotation_reason?: string;
  /** 供应商系统标签 */
  sys_tags?: SupplierSysTagVo[];
  segment_supplier_tag?: SegmentSupplierTagVo;
  /** 拒单原因 */
  reason?: string;
  /** 是否被供应商拒单 */
  reject_by_supplier?: boolean;
  /** 是否超时未报价 */
  expired?: boolean;
  invoice_type?: InvoiceType;
  invoice_belong_area?: InvoiceBelongAreaEnum;
  /** 是否海外发票,1=海外发票,0=非海外发票 */
  is_oversea_invoice?: number;
  contract_method?: DemandContractMethodEnum;
  /** 在提交付款时填写合同 */
  fill_contract_on_submit_reimburse?: boolean;
  outsource_state?: MatchOutsourceStatusEnum;
  /** 是否被退回 */
  return_flag?: boolean;
  channel_supplier_bank_dto?: ChannelSupplierBankDto;
  /** 供应商分类，1(常用推荐)，2(寻源)，3(跨品类) */
  supplier_category?: number;
  /** 是否智能推荐的供应商 */
  smart_recommend?: boolean;
  /** 指标列表 */
  kpi_value_vos?: SupplierKpiValueVo[];
  /** 近期综合评分 */
  recent_overall_score?: string;
  /** 近期结果打分 */
  recent_result_score?: string;
  /** 平台核价配置列表 */
  platform_prices?: PlatformPriceGroupVo[];
  go_next_round_journal?: JournalVo;
  /** 发票国家/地区名称 */
  invoice_belong_area_name?: string;
  /** 发票类型名称 */
  invoice_type_name?: string;
};
export type DemandHisDetailResDto = {
  demand_id?: number;
  permit_actions?: PermitActions;
  /** 供应商详情 */
  demand_suppliers?: DemandSupplierHisDto[];
  demand_plan_info?: JournalDemandDetailsResDtoDemandPlanInfoDto;
  demand_source_detail_vo?: DemandSourceDetailVo;
};
export type ResponseDemandHisDetailResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandHisDetailResDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 批量修改策划单预算
 */
export type BatchEditPlanBudgetForm = {
  /** 一级单ID */
  root_demand_id?: number;
  /** 策划单预算修改模型 */
  demand_plan_info_forms?: EditDemandPlanInfoForm[];
};
export type CreateDemandBiddingForm = {
  /** 需求id */
  demand_id: number;
  /** 操作者id */
  operator_id: number;
};
export type DemandBiddingVo = {
  /** 与MPC流程关联的招采流程 */
  bidding_id?: number;
};
export type ResponseDemandBiddingVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandBiddingVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type CreateDemandForm = {
  priority?: PriorityEnum;
  name: string;
  desc?: string;
  l1_category_id?: number;
  /** 二级需求类型ID */
  l2_category_id?: number;
  /** 策划人员配置ID */
  planner_group_id?: number;
  product_id?: number;
  number?: string;
  expect_begin_at?: string;
  expect_end_at?: string;
  budget?: number;
  budget_currency?: CurrencyEnum;
  introduce_attachs_blob_ids?: number[];
  urgent_order_restocking_attachs_blob_ids?: number[];
  demand_principals_user_ids: number[];
  demand_plan_user_ids?: number[];
  background?: string;
  expectation?: string;
  purchasing_method?: PurchasingMethodEnum;
  demand_supplier_ids?: number[];
  apply_reason?: string;
  usage_plan?: string;
  renew_type?: RenewTypeEnum;
  /** 关联小喇叭续约单id */
  trumpet_renew_demand_id?: number;
  /** 常规采购续约单id */
  normal_renew_demand_id?: number;
  /** 供应商推荐人姓名 */
  supplier_referrer?: string;
  /** 推荐供应商ID列表 */
  referrer_supplier_id_list?: number[];
  /** 供应商推荐理由 */
  supplier_referral_reason?: string;
  /** 回避供应商ID列表 */
  avoidance_supplier_id_list?: number[];
  /** 供应商回避理由 */
  supplier_avoidance_reason?: string;
  /** kpi指标列表 */
  expectation_kpi_indicators?: DemandKpiValueDto[];
  /** 是否KPI特殊申报 */
  kpi_special_declaration_flag?: boolean;
  /** KPI特殊申报原因 */
  kpi_special_declaration_reason?: string;
  group_type?: DemandGroupType;
  state?: string;
  /** 是否启用结构化数据，不传则使用内部规则获取 */
  structured?: boolean;
  /** 创建说明 */
  create_tips?: string;
  /** 关联业务ID */
  relate_biz_id?: number;
  relate_biz_type?: DemandRelateBizTypeEnum;
  demand_plan_user_ids_for_long_list?: number[];
};
export type CreateDemandResultVo = {
  id?: number;
};
export type ResponseCreateDemandResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: CreateDemandResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 需求计划信息
 */
export type EditDemandCooperateAccountDto = {
  /** 【结构化】【所有】合作内容列表 */
  cooperate_accounts: DemandCooperateAccountDto[];
  /** 验收要求 */
  accept_desc?: string;
  /** 验收要求列表 */
  accept_list?: string[];
};
/**
 * 商务报价确认节点编辑合作内容
 */
export type EditDemandCooperateAccountForm = {
  plan_info?: EditDemandCooperateAccountDto;
  demand_id?: number;
};
export type CloseDemandForm = {
  id?: number;
};
export type DspfInputForm = {
  /** demand的id */
  id?: number;
  /** dspf_mpc_no */
  dspf_mpc_no?: string;
  /** product_number */
  product_number?: string;
  /** group_type */
  group_type?: string;
};
/**
 * 数据单元格异常详情集合
 */
export type DemandPrefillQuoteCellErrorDetailDto = {
  /** 属性编码 */
  property_code?: string;
  /** 属性标签 */
  property_label?: string;
  /** 异常信息 */
  error_msg?: string;
};
/**
 * 数据行异常详情集合，该字段目前前端不依赖，仅用于展示具体的错误信息，方便问题定位
 */
export type DemandPrefillQuoteRowErrorDetailDto = {
  /** 数据行索引 */
  row_index?: number;
  /** 数据单元格异常详情集合 */
  error_cell_details?: DemandPrefillQuoteCellErrorDetailDto[];
};
export type DemandPrefillQuoteDataCheckResultDto = {
  /** 预填报价数据校验结果 */
  check_result?: boolean;
  /** 数据异常的行索引集合 */
  error_row_indexes?: number[];
  /** 数据异常的行索引集合 */
  error_row_raw_data?: {
    [key: string]: any;
  }[];
  /** 数据行异常详情集合，该字段目前前端不依赖，仅用于展示具体的错误信息，方便问题定位 */
  error_row_details?: DemandPrefillQuoteRowErrorDetailDto[];
};
export type ResponseDemandPrefillQuoteDataCheckResultDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandPrefillQuoteDataCheckResultDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 汇率转换接口出参
 */
export type CalcOverAmountDto = {
  to_currency?: CurrencyEnum;
  from_currency?: CurrencyEnum;
  /** 结算金额 */
  settle_price?: number;
  /** 报价金额 */
  quote_price?: number;
  /** 汇率 */
  rate?: string;
  /** 计算后的金额 */
  res_amount?: number;
};
export type ResponseCalcOverAmountDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: CalcOverAmountDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 批量添加辅助自定义指标
 */
export type BatchAddAuxiliaryKpiForm = {
  /** 每个二级单及其要添加的辅助指标列表 */
  items: EditAuxiliaryKpiForm[];
};
/**
 * 需求批量拒绝入参
 */
export type DemandBatchRejectForm = {
  /** 一级单ID */
  demand_id?: number;
  state?: DemandState;
  target_state?: DemandState;
  /** 三级单拒绝信息列表 */
  demands?: DbrfDemandRejectInfoForm[];
  /** 拒绝原因 */
  reject_reason: string;
};
/**
 * 需求方评分项目
 */
export type EvaluateScoreVo = {
  /** 评分项目ID */
  evaluate_item_id?: number;
  /** 评分项名称 */
  name?: string;
  /** 评分项得分,10分制 */
  score?: number;
  /** 评分项实际得分 */
  real_score?: number;
  /** 评分项占比 */
  percentage?: string;
};
/**
 * planner_report_attachs_blob_ids
 */
export type DemandBlobDto = {
  /** blob_id */
  blob_id?: number;
  /** blob_key */
  blob_key?: string;
};
/**
 * 三级单验收信息
 */
export type DbaiDemandAcceptInfoForm = {
  /** 需求单号 */
  demand_id?: number;
  /** 项目编号 */
  project_code?: string;
  /** 需要验收，前端勾选后传true */
  need_accept?: boolean;
  /** 是否加急 */
  urgent?: boolean;
  /** 加急理由 */
  urgent_reason?: string;
  /** 过程评分 */
  score?: number;
  /** 策划评分项 */
  evaluate_scores?: EvaluateScoreVo[];
  /** 结果评分 */
  result_score?: number;
  /** 需求方评分项目 */
  demand_manger_evaluate_scores?: EvaluateScoreVo[];
  /** 合作内容列表 */
  cooperate_accounts?: DemandCooperateAccountDto[];
  /** 整体指标列表 */
  common_kpi_values?: DemandKpiValueDto[];
  settle_price?: Money;
  plan_accept_settle_price?: Money;
  /** 执行情况描述（策划填写） */
  dspf_desc?: string;
  /** 数易MPC单号 */
  dspf_mpc_no?: string;
  /** 意见及建议（策划） */
  suggestion?: string;
  /** 是否有效果描述补充 */
  has_demander_accept_suggestion?: boolean;
  /** 需求方验收意见/效果描述（需求方填写） */
  demander_accept_suggestion?: string;
  /** 结案报告（策划） */
  planner_report_attachs_blob_ids?: DemandBlobDto[];
  /** 验收备注 */
  note?: string;
  updated_at?: string;
};
/**
 * 非绑定数据-需求批量验收入参
 */
export type DemandBatchNoneBindingAcceptForm = {
  /** 一级单ID */
  demand_id?: number;
  state?: DemandState;
  /** 三级单列表 */
  demands?: DbaiDemandAcceptInfoForm[];
};
/**
 * 不参与考核的产品
 */
export type ProductSimpleVo = {
  /** 产品ID */
  id?: number;
  /** 产品名称 */
  name?: string;
  /** 产品Identifier */
  identifier?: string;
};
/**
 * 考核指标响应对象
 */
export type DemandKpiVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  platform?: DemandKpiPlatformEnum;
  /** KPI编码 */
  code?: string;
  /** 数易编码 */
  dspf_code?: string;
  /** KPI名称 */
  name?: string;
  type?: DemandKpiTypeEnum;
  /** KPI描述 */
  description?: string;
  status?: CommonEnableStatusEnum;
  positive?: DemandKpiPositiveEnum;
  percent?: CommonYesNoEnum;
  show_for_out?: CommonYesNoEnum;
  long_or_short_term?: DemandKpiLongOrShortTermEnum;
  /** 是否数易特殊指标 */
  dspf_special?: boolean;
  /** 是否跳过拆单一致性校验, true=是 false=否 #7370 */
  skip_validation?: boolean;
  /** 不参与考核投放渠道 */
  exclude_delivery_channels?: string[];
  /** 不参与考核的产品 */
  exclude_products?: ProductSimpleVo[];
  /** 系统编码 */
  sys_code?: string;
  is_direct?: boolean;
};
/**
 * 执行周期信息
 */
export type DbaiExecutionCycleForm = {
  /** 行唯一标识 */
  row_key?: string;
  /** 选择的辅助指标列表 */
  extra_kpis?: DemandKpiVo[];
};
/**
 * 需求批量验收入参
 */
export type DemandBatchAcceptForm = {
  /** 一级单ID */
  demand_id?: number;
  state?: DemandState;
  /** 超额说明 */
  over_amount_remark?: string;
  /** 执行周期信息列表 */
  execution_cycles?: DbaiExecutionCycleForm[];
  /** 三级单列表 */
  demands?: DbaiDemandAcceptInfoForm[];
};
/**
 * 列表查询标品表单
 */
export type PageDemandStandardCatalogForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 查询文本 */
  query_text?: string;
  level?: StandardCatalogLevelEnum;
  /** 交付类型 */
  delivery_types?: StandardCatalogDeliveryTypeEnum[];
  /** 生效状态   1=生效,0=失效 */
  status?: number;
  /** 标品ID列表 */
  id_list?: number[];
};
/**
 * 需求标品列表表单
 */
export type DemandStandardCatalogVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 父标品ID */
  parent_id?: number;
  level?: StandardCatalogLevelEnum;
  delivery_type?: StandardCatalogDeliveryTypeEnum;
  /** 标品名称 */
  name?: string;
  /** 标品编码 */
  short_code?: string;
  /** 从父级完整拼接的标品编码 */
  code?: string;
  /** 生效状态   1=生效,0=失效 */
  status?: number;
  /** 子标品 */
  children_demand_standard_catalog_vos?: DemandStandardCatalogVo[];
};
/**
 * 分页结果
 */
export type PageResultDemandStandardCatalogVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: DemandStandardCatalogVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultDemandStandardCatalogVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultDemandStandardCatalogVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 查询标品选项值及下属选项值表单
 */
export type ListValueAndSubValueForm = {
  /** 标品Id列表 */
  id_list?: number[];
};
/**
 * 需求标品按照层级展示列表VO
 */
export type DemandStandardCatalogListVo = {
  /** 1级标品列表 */
  level1_list?: DemandStandardCatalogVo[];
  /** 2级标品列表 */
  level2_list?: DemandStandardCatalogVo[];
  /** 3级标品列表 */
  level3_list?: DemandStandardCatalogVo[];
};
export type ResponseDemandStandardCatalogListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandStandardCatalogListVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 列表查询标品表单
 */
export type DemandStandardCatalogListParam = {
  standard_catalog_code_list?: string[];
};
export type ResponseListDemandStandardCatalogVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandStandardCatalogVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 编辑需求标品表单
 */
export type EditDemandStandardCatalogForm = {
  /** 标品ID */
  id: number;
  /** 标品名称 */
  name?: string;
  /** 标品编码 */
  short_code?: string;
  delivery_type?: StandardCatalogDeliveryTypeEnum;
  /** 生效状态   1=生效,0=失效 */
  status?: number;
};
/**
 * 创建需求标品表单
 */
export type CreateDemandStandardCatalogForm = {
  level: StandardCatalogLevelEnum;
  delivery_type?: StandardCatalogDeliveryTypeEnum;
  /** 父标品ID */
  parent_id?: number;
  /** 父标品Code */
  parent_code?: string;
  /** 标品名称 */
  name: string;
  /** 标品编码 */
  short_code: string;
  /** 生效状态   1=生效,0=失效 */
  status: number;
};
export type ResponseDemandSourceDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSourceDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type PriceLibraryCheckResultForm = {
  /** 需求单Id */
  demand_id?: number;
  /** 供应商Id（单供应商维度） */
  supplier_id?: number;
  /** 查询的轮次，默认最新轮次 */
  turns_number?: number;
};
/**
 * 上期续约单报价行列表（#9510-02 sync#02 D-04：原 BigDecimal 改 List；前端 cell 默认展示第一个单价，hover Popover 展示全部）
 */
export type RenewalQuoteRowVo = {
  /** 合作项目（demand_standard_catalog.name，按 standard_code 关联） */
  cooperation_project?: string;
  /** 合作详情说明（取原单报价明细 info_values 中「合作详情说明」列 code 对应的值） */
  cooperation_content_desc?: string;
  /** 原单同标品+同供应商+同合作内容序号最新轮次单价 */
  unit_price?: number;
  currency?: CurrencyEnum;
};
/**
 * 明细行
 */
export type ResultRow = {
  /** 报价明细行标识（报价 blob 内 quoteKey，前端行唯一键） */
  quote_key?: string;
  /** 标品code */
  standard_code?: string;
  /** 标品一级名称（类目，取需求快照标品树 level1） */
  standard_l1_name?: string;
  /** 标品二级名称（合作项目，取需求快照标品树 level2 叶子） */
  standard_l2_name?: string;
  /** 单位 */
  unit?: string;
  /** 币种（本供应商该合作内容最新报价单币种） */
  currency?: string;
  /** 供应商单价 */
  unit_price?: number;
  /** 参考价（取生效价格库 priceUpper，无生效价格库为空） */
  reference_price?: number;
  /** 参考价币种（价格库维护的币种） */
  reference_price_currency?: string;
  /** 行级核价结果：reasonable_price/high_price/unidentified_price */
  price_check_result?: string;
  /** 系统核价结果(自动核价基线快照，商务确认不覆盖；存量为空时 fallback 取 price_check_result) */
  system_price_check_result?: string;
  /** 行级商务核价备注（#9510-02 sync#02，商务确认核价时填写） */
  business_price_check_remark?: string;
  /** 数量 */
  quantity?: number;
  /** 总价 */
  total_price?: number;
  /** 上期续约单报价行列表（#9510-02 sync#02 D-04：原 BigDecimal 改 List；前端 cell 默认展示第一个单价，hover Popover 展示全部） */
  renewal_quote_price?: RenewalQuoteRowVo[];
  /** 是否超过上期续约单单价（续约单专属；本次 > 上期最高单价时 true） */
  exceed_renewal_price?: boolean;
  /** 续约单原单同标品+同供应商存在多行报价，需人工核验（renewalQuotePrice.size() > 1 时 true） */
  renewal_multiple_quote?: boolean;
};
/**
 * 合作内容分组列表
 */
export type CooperateContentGroup = {
  /** 合作内容Id */
  cooperate_account_id?: number;
  /** 合作内容名称 */
  cooperate_content_name?: string;
  /** 合作内容溢价比(%)：Σ报价总额 ÷ Σ参考价总额 ×100，无参考价行不计入；无法计算为空 */
  premium_ratio?: number;
  /** 合作内容维度核价结果：reasonable_price/high_price/unidentified_price */
  price_check_result?: string;
  /** 合作内容维度系统核价结果(自动核价基线快照，商务确认不覆盖) */
  system_price_check_result?: string;
  /** 是否最新轮次（#9510-02 sync#02 I2：最新轮次 true 可编辑+确认，历史轮次 false 只读） */
  is_latest_turns?: boolean;
  /** 明细行 */
  rows?: ResultRow[];
};
export type PriceLibraryCheckResultVo = {
  /** 需求单Id */
  demand_id?: number;
  /** 供应商Id */
  supplier_id?: number;
  /** 供应商名称 */
  supplier_name?: string;
  /** 轮次 */
  turns_number?: number;
  /** 是否展示参考价 */
  show_reference_price?: boolean;
  /** 合作内容分组列表 */
  cooperate_contents?: CooperateContentGroup[];
};
export type ResponsePriceLibraryCheckResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PriceLibraryCheckResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 行级编辑项（按 quoteKey 聚合；空列表=仅标记 CHECKED，不覆盖行级结果）
 */
export type RowEdit = {
  /** 报价明细行标识（与 VO ResultRow.quoteKey 对齐） */
  quote_key: string;
  /** 行级核价结果 PriceCheckResultEnum.name()（reasonable_price/high_price/unidentified_price）；null=不覆盖 */
  price_check_result?: string;
  /** 行级商务核价备注（纯文本，无附件） */
  business_price_check_remark?: string;
};
export type PriceCheckConfirmCmd = {
  /** 需求单 id */
  demand_id: number;
  /** 供应商 id */
  supplier_id: number;
  /** 轮次；null=最新轮次（由后端取 DemandSupplier.turnsNumber max 解析） */
  turns_number?: number;
  /** 行级编辑项（按 quoteKey 聚合；空列表=仅标记 CHECKED，不覆盖行级结果） */
  row_edits?: RowEdit[];
};
/**
 * 文件内容
 */
export type DemandSourcePriceCheckRemarkFileSaveCmd = {
  /** 文件内容Id-blobId */
  id?: number;
};
/**
 * 需求方核价备注
 */
export type DemandSourcePriceCheckRemarkSaveCmd = {
  /** 文本内容 */
  text?: string;
  /** 文件内容 */
  files?: DemandSourcePriceCheckRemarkFileSaveCmd[];
};
/**
 * 合作内容信息
 */
export type DemandSourcePriceCheckCooperateAccount = {
  /** demandCooperateAccountId */
  id?: number;
  base_price_config?: BasePriceConfigDto;
  business_price_check_remark?: DemandSourcePriceCheckRemarkSaveCmd;
  plan_price_check_remark?: DemandSourcePriceCheckRemarkSaveCmd;
  principal_price_check_remark?: DemandSourcePriceCheckRemarkSaveCmd;
  /** 策划确认达人 */
  plan_confirm?: boolean;
  /** 议价优惠金额（文本，非必填） */
  bargaining_discount?: string;
  /** 达人合作模式 */
  kol_cooperation_mode?: string;
};
/**
 * 报价信息
 */
export type DemandSourcePriceCheckSupplierQuote = {
  /** demandSupplierQuoteId */
  id?: number;
  /** cooperateAccountId */
  cooperate_account_id?: number;
  /** 是否中选 */
  winner?: boolean;
  /** 核价结果 */
  price_check_result?: string;
  /** 溢价比 */
  premium_ratio?: number;
  /** 系统核价结果 */
  system_price_check_result?: string;
};
export type DemandSourcePriceCheckSaveCmd = {
  /** 需求Id */
  demand_id?: number;
  demand_state?: DemandState;
  /** 合作内容信息 */
  cooperate_account_list?: DemandSourcePriceCheckCooperateAccount[];
  /** 报价信息 */
  supplier_quote_list?: DemandSourcePriceCheckSupplierQuote[];
};
export type DemandSourcePriceCheckSaveResp = {
  update_at?: string;
};
export type ResponseDemandSourcePriceCheckSaveResp = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSourcePriceCheckSaveResp;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type GetDemandSourcePriceCheckDetailForm = {
  /** 需求单Id */
  demand_id?: number;
  /** 查询的轮次 */
  turns_number?: number;
};
/**
 * 供应商报价信息
 */
export type DemandSourcePriceCheckDemandSupplierQuote = {
  id?: number;
  /** 供应商Id */
  supplier_id?: number;
  /** 需求与供应商关联Id */
  demand_supplier_id?: number;
  /** 合作内容Id */
  cooperate_account_id?: number;
  /** 报价金额 */
  quote_price?: number;
  currency?: CurrencyEnum;
  /** 报价金额 */
  quote_price_cny?: number;
  /** 是否中选 */
  winner?: boolean;
  /** 溢价比 */
  premium_ratio?: number;
  /** 核价结果 */
  price_check_result?: string;
  /** 策划是否确认达人 */
  plan_confirm?: boolean;
  /** 最低报价标识 */
  min_price?: boolean;
  /** 系统核价结果 */
  system_price_check_result?: string;
  /** 报价是否含AI（自动计算，只读） */
  quote_contain_ai?: boolean;
  /** AI渗透率（自动计算，只读） */
  ai_penetration_rate?: number;
  /** AI降本率（商务确认报价时手工输入，只读回显） */
  ai_cost_reduction_rate?: number;
  /** 核价结果是否与提前核价不一致（true=系统自动带入系统核价结果并标注不一致） */
  price_check_result_inconsistent_with_apc?: boolean;
};
/**
 * 核价供应商信息
 */
export type DemandSourcePriceCheckDemandSupplier = {
  id?: number;
  /** 需求Id */
  demand_id?: number;
  /** 供应商Id */
  supplier_id?: number;
  /** 供应商名称，带渠道类型 */
  supplier_full_name?: string;
  /** 供应商名称，主供应商名称 */
  supplier_name?: string;
  /** 供应商简要名称 */
  supplier_short_name?: string;
  /** 供应商报价信息 */
  demand_supplier_quote_list?: DemandSourcePriceCheckDemandSupplierQuote[];
};
export type DemandSourcePriceCheckDetailVo = {
  /** 需求单Id */
  demand_id?: number;
  demand_state?: DemandState;
  /** 合作内容信息 */
  cooperate_accounts?: JournalDemandDetailsResDtoCooperateAccountDto[];
  /** 核价供应商信息 */
  demand_supplier?: DemandSourcePriceCheckDemandSupplier[];
  /** 总轮次 */
  turns_number?: number;
  /** 最后更新时间 */
  update_at?: string;
  /** 是否需全部报价（整单报价标识） */
  need_all_quote?: boolean;
};
export type ResponseDemandSourcePriceCheckDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSourcePriceCheckDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type GetDemandSourceReferencePriceForm = {
  /** 合作内容Id */
  demand_cooperate_account_id?: number;
  /** demandId */
  demand_id?: number;
};
/**
 * 合作时间列表
 */
export type CooperateTime = {
  /** 合作时间 */
  cooperate_time?: string;
};
export type DemandSourceReferencePriceVo = {
  /** demandCooperateAccountId */
  demand_cooperate_account_id?: number;
  /** 基准价来源类型 */
  source_type?: string;
  /** 基准价来源类型名称 */
  source_name?: string;
  /** 基准价类型编码 */
  base_price_type?: string;
  /** 基准价类型名称 */
  base_price_name?: string;
  /** 基准价金额 */
  base_price?: number;
  base_price_currency?: CurrencyEnum;
  /** 合作时间列表 */
  cooperate_time_list?: CooperateTime[];
  /** 是否禁用，true禁用，不可选择 */
  disable?: boolean;
};
export type ResponseListDemandSourceReferencePriceVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSourceReferencePriceVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type BatchGetDemandSourceReferencePriceForm = {
  /** 合作内容Id */
  demand_cooperate_account_id?: number[];
  /** demandId */
  demand_id?: number;
  /** 基准价类型编码 */
  base_price_type?: string;
};
/**
 * 用于计算的对象
 */
export type DemandSourceCooperateAccount = {
  base_price_currency?: CurrencyEnum;
  /** 基准价金额 */
  base_price_amount?: number;
  /** demandSupplierQuoteId */
  demand_supplier_quote_id?: number;
  supplier_quote_currency?: CurrencyEnum;
  /** 金额 */
  supplier_quote_amount?: number;
  /** 基准价类型 */
  base_type?: string;
  /** 基准价来源类型 */
  source_type?: string;
  /** 提前核价明细ID（优先使用此ID查询核价记录补充参数） */
  advance_price_check_detail_id?: number;
};
export type DemandSourcePremiumRateComputeCmd = {
  /** 用于计算的对象 */
  compute_obj?: DemandSourceCooperateAccount[];
};
export type DemandSourcePremiumRateComputeVo = {
  /** 用于计算的对象 */
  compute_obj?: DemandSourceCooperateAccount[];
};
export type ResponseDemandSourcePremiumRateComputeVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSourcePremiumRateComputeVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandSourceApprovalBizFormType = 1 | 2 | 3 | 4 | 5 | 6 | 7;
/**
 * 审批时提交的业务表单
 */
export type DemandSourceApprovalBizCmd = {
  type?: DemandSourceApprovalBizFormType;
};
/**
 * 重新报价信息
 */
export type SupplierReturnParam = {
  /** id */
  id?: number;
  /** 供应商ID */
  supplier_id?: number;
  /** 重新报价原因 */
  re_quotation_reason?: string;
  /** 是否报价 */
  is_quoted?: boolean;
};
/**
 * 重新报价cmd
 */
export type DemandBusinessReQuoteCmd = DemandSourceApprovalBizCmd & {
  /** 重新报价信息 */
  demand_suppliers?: SupplierReturnParam[];
  /** 报价截至时间 */
  quote_deadline?: string;
};
/**
 * 废弃
 */
export type DemandSourceAbandonCmd = DemandSourceApprovalBizCmd;
/**
 * 供应商ID
 */
export type DemandSourceSupplierCmd = {
  /** 供应商ID */
  supplier_id?: number;
  /** 供应商分类 */
  supplier_category?: number;
  /** 是否智能推荐 */
  smart_recommend?: boolean;
};
/**
 * 商务派发一询
 */
export type DemandSourceBusinessAssessCmd = DemandSourceApprovalBizCmd & {
  /** 供应商ID */
  supplier_list?: DemandSourceSupplierCmd[];
  /** 提报截止时间 */
  quote_deadline?: string;
};
/**
 * 策划视图参数
 */
export type KolParam = {
  /** 供应商提报达人id */
  demand_source_supplier_sign_kol_id?: number;
  plan_suggestion_state?: PlanSuggestionState;
};
/**
 * 供应商视图参数
 */
export type SupplierParam = {
  /** 供应商ID */
  supplier_id?: number;
  /** 建议退回标识 */
  suggest_return_flag?: boolean;
  /** 退回原因 */
  reject_reason?: string;
};
/**
 * 策划预选cmd
 */
export type DemandSourcePlanAssessCmd = DemandSourceApprovalBizCmd & {
  /** 策划视图参数 */
  plan_params?: KolParam[];
  /** 供应商视图参数 */
  supplier_params?: SupplierParam[];
};
export type DemandSourcePlanDesignCmd = DemandSourceApprovalBizCmd;
/**
 * 需求方确认cmd
 */
export type DemandSourcePrincipalAuditCmd = DemandSourceApprovalBizCmd & {
  /** 供应商提报达人Id列表 */
  demand_source_supplier_sign_kol_ids?: number[];
};
/**
 * 平台直播CPM-UV
 */
export type QuoteItem = {
  /** 字段名称 */
  name?: string;
  /** 字段值 */
  value?: object;
  /** 字段别名 */
  alias?: string;
};
/**
 * 提报达人列表
 */
export type DemandSourceSupplierSignKolCreateCmd = {
  /** 一询单id */
  demand_source_id?: number;
  /** 一询单供应商ID */
  demand_supplier_id?: number;
  /** 达人id */
  demand_source_kol_id?: number;
  /** 定制排查id */
  demand_source_custom_inspection_id?: number;
  /** 定制排查属性值 */
  demand_source_custom_inspection_fields_value?: CustomInspectionField[];
  /** 合作主平台 */
  main_platform?: string;
  /** 主平台账号 */
  main_platform_account?: string;
  /** 主平台链接 */
  main_platform_link?: string;
  /** 主平台官单 / 私单 */
  public_order?: string;
  /** 主平台报价项目 */
  main_platform_quote_item?: string;
  /** 是否独家 */
  exclusive_sign?: boolean;
  /** 报价 */
  quote_price?: number;
  quote_price_currency?: CurrencyEnum;
  /** cpm */
  cpm?: number;
  /** 直播CPM-PV */
  cpm_pv?: number;
  /** 直播CPM-UV */
  cpm_uv?: number;
  /** 平台报价 */
  platform_quote_price?: QuoteItem[];
  /** 平台cpm */
  platform_cpm?: QuoteItem[];
  /** 平台直播CPM-PV */
  platform_cpm_pv?: QuoteItem[];
  /** 平台直播CPM-UV */
  platform_cpm_uv?: QuoteItem[];
  /** 数量 */
  count?: string;
  /** 备注 */
  remark?: string;
  /** 附件ID列表 */
  blob_id_list?: number[];
};
export type DemandSourceSupplierQuoteCmd = DemandSourceApprovalBizCmd & {
  /** 是否提报超时 */
  from_deadline_schedule?: boolean;
  /** 是否拒单 */
  reject_by_supplier?: boolean;
  /** 拒单原因 */
  reason?: string;
  /** 提报达人列表 */
  demand_source_supplier_sign_kol_create_cmd_list?: DemandSourceSupplierSignKolCreateCmd[];
};
export type DemandSourceApprovalCmd = {
  /** 需求id */
  id: number;
  /** 备注 */
  note?: string;
  state: DemandSourceState;
  /** 是否废弃 */
  discard?: boolean;
  /** 废弃原因 */
  drop_reason?: string;
  /** 原因选项 */
  drop_reason_option?: string;
  /** 是否通过 */
  pass?: boolean;
  /** 是否关闭 */
  close?: boolean;
  biz_cmd?:
    | DemandSourceApprovalBizCmd
    | DemandBusinessReQuoteCmd
    | DemandSourceAbandonCmd
    | DemandSourceBusinessAssessCmd
    | DemandSourcePlanAssessCmd
    | DemandSourcePlanDesignCmd
    | DemandSourcePrincipalAuditCmd
    | DemandSourceSupplierQuoteCmd;
  journal_op_type?: string;
};
/**
 * 默认供应商
 */
export type PendingAssessDemandSourceSupplier = {
  /** 供应商ID */
  supplier_id?: number;
  /** vmdmCode编码 */
  mdm_code?: string;
  /** 供应商编码 */
  supplier_code?: string;
  /** 供应商名称 */
  name?: string;
  /** 供应商简称 */
  short_name?: string;
  /** 供应商简称 */
  channel_type?: string;
  /** 取到类型名称 */
  channel_type_name?: string;
  channel_supplier_type?: ChannelSupplierType;
  /** 单一来源标识 */
  single_source?: boolean;
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注数量 */
  risk_remark_count?: number;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 风险拦截标记 */
  risky?: boolean;
  /** 供应商系统标签 */
  sys_tags?: SupplierSysTagVo[];
};
export type PendingAssessDemandSourceSupplierList = {
  supplier?: PendingAssessDemandSourceSupplier;
  /** 提报达人数 */
  sign_count?: number;
  /** 确认达人数 */
  confirmed_count?: number;
};
/**
 * 默认采购方式
 */
export type PendingAssessPurchasingMethodEnum =
  | 'S_INQUIRY'
  | 'S_FRAMEWORK'
  | 'S_IMMEDIATELY_BIDDING'
  | 'S_ONLY_ONE_SOURCE'
  | 'S_DIRECTIONAL_BIDDING'
  | 'S_SPECIAL_MATTER_BIDDING'
  | 'S_BIDDING_FINISHED'
  | 'S_FRAMEWORK_BIDDING_FINISHED'
  | 'S_URGENT_ORDER_RESTOCKING'
  | 'S_RENEW';
export type PendingAssessSupplierInfoVo = {
  demand_id?: number;
  supplier_list?: PendingAssessDemandSourceSupplierList[];
  purchasing_method?: PendingAssessPurchasingMethodEnum;
  /** 默认供应商 */
  default_supplier_list?: PendingAssessDemandSourceSupplier[];
  /** 是否独家 */
  exclusive_sign?: boolean;
};
export type ResponsePendingAssessSupplierInfoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PendingAssessSupplierInfoVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseDemandSourceKolVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSourceKolVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 根据达人主页链接批量查询
 */
export type BatchGetKolInfoCmd = {
  /** 需求类型 */
  content_type?: number;
  /** 主页链接 */
  main_platform_link?: string[];
};
export type ResponseListDemandSourceKolVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSourceKolVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListDemandCooperateAccountDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandCooperateAccountDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 分页查询权益内容列表表单
 */
export type PageDemandRightsContentForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 权益类目ID */
  category_ids?: number[];
  /** 权益内容 */
  content_fuzzy?: string;
  /** 生效状态   1=生效,0=失效 */
  status?: number;
};
/**
 * 需求权益类别表单
 */
export type DemandRightsContentVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 权益类目父ID */
  category_parent_id?: number;
  /** 权益类目ID */
  category_id?: number;
  /** 权益类目名称 */
  category_name?: string;
  /** 权益内容 */
  content?: string;
  /** 权益Code */
  code?: string;
  /** 生效状态   1=生效,0=失效 */
  status?: number;
};
/**
 * 分页结果
 */
export type PageResultDemandRightsContentVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: DemandRightsContentVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultDemandRightsContentVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultDemandRightsContentVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 树形类别查询列表表单
 */
export type ListTreeForDemandRightsCategoryForm = {
  /** 类别名称 */
  name?: string;
};
/**
 * 需求权益类别表单
 */
export type DemandRightsCategoryVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 父权益类别ID */
  parent_id?: number;
  /** 权益类别名称 */
  name?: string;
  /** 权益类别全称 */
  full_name?: string;
  /** 级别 */
  level?: number;
  /** 下属所有权益条数 */
  sub_sum_count?: number;
  /** 子权益类别 */
  children_demand_rights_category_list?: DemandRightsCategoryVo[];
};
/**
 * 权益类目树形VO
 */
export type DemandRightsTreeVo = {
  /** 权益类目树 */
  tree?: DemandRightsCategoryVo[];
  /** 全部数量 */
  all_count?: number;
};
export type ResponseDemandRightsTreeVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandRightsTreeVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 编辑权益内容表单
 */
export type EditDemandRightsContentForm = {
  /** 权益内容ID */
  id: number;
  /** 权益类目ID */
  category_id?: number;
  /** 权益内容 */
  content?: string;
  /** 生效状态   1=生效,0=失效 */
  status?: number;
};
/**
 * 编辑权益类别表单
 */
export type EditDemandRightsCategoryForm = {
  /** 权益ID */
  id: number;
  /** 权益名称 */
  name: string;
};
/**
 * 删除权益类别表单
 */
export type DeleteDemandRightsCategoryForm = {
  /** 权益ID */
  id: number;
};
/**
 * 创建权益内容表单
 */
export type CreateDemandRightsContentForm = {
  /** 权益类目ID */
  category_id: number;
  /** 权益内容 */
  content: string;
  /** 生效状态   1=生效,0=失效 */
  status: number;
};
/**
 * 级别
 */
export type RightsCategoryLevelEnum = 1 | 2;
/**
 * 创建权益类别表单
 */
export type CreateDemandRightsCategoryForm = {
  /** 父权益ID */
  parent_id?: number;
  level?: RightsCategoryLevelEnum;
  /** 权益名称 */
  name: string;
};
export type PageDemandCategoryPropertyForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求类型ids */
  category_ids?: number[];
  /** 过滤是否启用 */
  status?: boolean;
};
/**
 * 分页数据
 */
export type DemandCategoryPropertyTemplateVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 分类id */
  category_id?: number;
  /** 分类名称 */
  category_names?: string;
  /** 是否启用 */
  status?: boolean;
};
/**
 * 分页结果
 */
export type PageResultDemandCategoryPropertyTemplateVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: DemandCategoryPropertyTemplateVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultDemandCategoryPropertyTemplateVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultDemandCategoryPropertyTemplateVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 引用的标品信息
 */
export type DemandCategoryTemplateStandardItem = {
  /** 在策划拆单中使用 */
  use_in_design?: boolean;
  /** 在供应商报价使用 */
  use_in_quote?: boolean;
  /** 必填 */
  required?: boolean;
  /** 展示名称 */
  label?: string;
  /** 供应商可修改 */
  outsource_modify?: boolean;
  /** 选择的一级标品 */
  standard_ids?: number[];
};
/**
 * 选择的属性
 */
export type DemandCategoryPropertyItem = {
  /** 属性ID */
  property_id: number;
  required_mode: RequiredModeEnum;
  required_condition?: RequiredConditionData;
  options?: ConditionOptionsData;
  /** 合同附件是否显示，默认 true */
  contract_attachment_visible?: boolean;
};
/**
 * 报价引用的属性
 */
export type DemandCategoryPropertyRefInQuoteItem = {
  /** 属性ID */
  property_id: number;
  /** 供应商可修改 */
  outsource_modify?: boolean;
};
/**
 * 分发平台报价信息
 */
export type DemandCategoryTemplateQuoteItem = {
  /** 编码，前端生成 */
  code: string;
  /** 字段名称 */
  label: string;
  type: DemandCooperatePropertyTypeEnum;
  required_mode: RequiredModeEnum;
  required_condition?: RequiredConditionData;
  options?: ConditionOptionsData;
  /** 是否预填 */
  prefill?: boolean;
  /** 供应商可修改 */
  outsource_modify?: boolean;
};
/**
 * 分摊类型
 */
export type AmountAverageTypeEnum = 0 | 1;
export type AmountAverageDimension = 0 | 1;
export type AmountAveragePeriodValueTypeEnum = 0 | 1;
/**
 * 自定义分摊配置列表
 */
export type AmountAverageConfigData = {
  is_bind_execution_data?: IsAssociateExecutionDataEnum;
  dimension: AmountAverageDimension;
  period_value_type: AmountAveragePeriodValueTypeEnum;
  period_value?: string;
};
export type EditDemandCategoryPropertyForm = {
  /** 配置id */
  id: number;
  /** 版本号 */
  version: number;
  /** 状态，是否启用 */
  status: boolean;
  type: DemandCategoryPropertyTemplateTypeEnum;
  /** 引用的标品信息 */
  standard_items: DemandCategoryTemplateStandardItem[];
  /** 选择的属性 */
  items: DemandCategoryPropertyItem[];
  /** 报价引用的属性 */
  items_in_quote?: DemandCategoryPropertyRefInQuoteItem[];
  /** 报价类型 */
  quote_type: number;
  /** 报价其他信息列表 */
  quote_items: DemandCategoryTemplateQuoteItem[];
  /** 分发平台报价信息 */
  platform_items?: DemandCategoryTemplateQuoteItem[];
  /** 是否有差旅餐饮费用 */
  travel_catering_fee: boolean;
  travel_catering_options?: ConditionOptionsData;
  unit_type: QuoteUnitTypeEnum;
  /** 必要权益列表 */
  necessary_rights_ids: number[];
  /** 期望权益列表 */
  expect_rights_ids?: number[];
  /** 额外的验收标准 */
  accept_desc: string;
  amount_average_type: AmountAverageTypeEnum;
  /** 自定义分摊配置列表 */
  amount_average_configs?: AmountAverageConfigData[];
  /** 是否含AI模板 */
  contain_ai?: boolean;
  ai_ratio_condition?: RequiredConditionData;
};
export type CreateDemandCategoryPropertyForm = {
  /** 需求分类id */
  category_id: number;
  /** 引用的标品信息 */
  standard_items: DemandCategoryTemplateStandardItem[];
  /** 状态 */
  status: boolean;
  type: DemandCategoryPropertyTemplateTypeEnum;
  /** 选择的属性 */
  items: DemandCategoryPropertyItem[];
  /** 报价引用的属性 */
  items_in_quote?: DemandCategoryPropertyRefInQuoteItem[];
  /** 报价类型 */
  quote_type: number;
  /** 报价其他信息列表 */
  quote_items: DemandCategoryTemplateQuoteItem[];
  /** 分发平台报价信息 */
  platform_items?: DemandCategoryTemplateQuoteItem[];
  /** 是否有差旅餐饮费用 */
  travel_catering_fee: boolean;
  travel_catering_options?: ConditionOptionsData;
  unit_type: QuoteUnitTypeEnum;
  /** 必要权益列表 */
  necessary_rights_ids: number[];
  /** 期望权益列表 */
  expect_rights_ids?: number[];
  /** 额外的验收标准 */
  accept_desc: string;
  amount_average_type: AmountAverageTypeEnum;
  /** 自定义分摊配置列表 */
  amount_average_configs?: AmountAverageConfigData[];
  /** 是否含AI模板 */
  contain_ai?: boolean;
  ai_ratio_condition?: RequiredConditionData;
};
/**
 * 渠道绑定快照项
 */
export type BindSnapshotVo = {
  id?: number;
  mpc_channel_id?: string;
  mpc_channel_name?: string;
  dspf_channel_ids?: string[];
  dspf_channel_names?: string[];
};
/**
 * 自动拆单规则VO
 */
export type AutoSplitRuleVo = {
  /** 规则ID */
  id?: number;
  /** 状态:0=已废弃,1=生效中 */
  status?: number;
  /** 广告渠道项列表 */
  items?: ItemVo[];
  /** 一级需求单ID */
  demand_id?: number;
  /** 关联的二级单ID */
  plan_demand_id?: number;
  /** 二级单号 */
  plan_demand_number?: string;
  plan_demand_state?: DemandState;
  /** 二级单执行开始日期 */
  begin_at?: string;
  /** 二级单执行结束日期 */
  end_at?: string;
  /** 是否保留二级单 */
  keep_second_level_order?: boolean;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** 最新一次数易渠道费用查询时间 */
  latest_dspf_query_at?: string;
  /** 批次保存时的渠道绑定配置快照，用于非首次打开时回显 */
  bind_snapshot?: BindSnapshotVo[];
};
export type ResponseListAutoSplitRuleVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: AutoSplitRuleVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 广告渠道项
 */
export type ItemForm = {
  /** 数易广告渠道ID */
  dspf_channel_id: string;
  /** 数易广告渠道名称 */
  dspf_channel_name?: string;
};
/**
 * 单个操作
 */
export type OperationForm = {
  /** 操作类型：1=新增, 2=编辑, 3=删除 */
  action: number;
  /** 规则ID（编辑/删除时必填） */
  rule_id?: number;
  /** 广告渠道项列表（新增/编辑时必填） */
  items?: ItemForm[];
  /** 二级单ID（已有规则时必填） */
  plan_demand_id?: number;
  /** 执行开始日期（新增/编辑时必填） */
  begin_at?: string;
  /** 执行结束日期（新增/编辑时必填） */
  end_at?: string;
  /** 删除时是否保留二级单，默认false */
  keep_second_level_order?: boolean;
  /** 该行操作时的渠道绑定配置快照（JSON），由前端逐行传入 */
  bind_snapshot?: BindSnapshotVo[];
  /** MPC投放渠道ID（新增/编辑时必填） */
  mpc_channel_id?: string;
};
/**
 * 自动拆单规则批量操作表单
 */
export type AutoSplitRuleBatchForm = {
  /** 批量操作列表 */
  operations?: OperationForm[];
  /** 一级需求单ID */
  demand_id: number;
  /** 渠道绑定配置快照（JSON），由前端传入 */
  bind_snapshot?: BindSnapshotVo[];
};
/**
 * 定制排查字段列表
 */
export type DemandSourceMatchTemplateFieldEntity = {
  id?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  version?: number;
  demand_source_match_template_id?: number;
  field_name?: string;
  plan_remark?: string;
  value_example?: string;
};
export type DemandSourceMatchTemplateUpdateForm = {
  /** 模版id */
  id?: number;
  /** 模版名称 */
  name?: string;
  /** 合作主平台 */
  main_platform?: string[];
  /** 产品 */
  product_id?: number[];
  /** 状态 */
  state?: boolean;
  /** 定制排查字段列表 */
  demand_source_match_template_field_entities: DemandSourceMatchTemplateFieldEntity[];
};
/**
 * 定制排查模版分页查询
 */
export type DemandSourceMatchTemplateQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 合作主平台 */
  main_platform?: string[];
  /** 产品 */
  product_id?: number[];
  /** 模版名称 模糊匹配 */
  search_text?: string;
  /** 状态 */
  state?: boolean;
};
/**
 * 产品明细
 */
export type BizProductDto = {
  /** id */
  id?: number;
  /** name */
  name?: string;
  /** desc */
  desc?: string;
  /** parent_id */
  parent_id?: string;
  /** identifier */
  identifier?: string;
};
/**
 * 定制模版
 */
export type DemandSourceMatchTemplateFieldVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 关联模版ID */
  demand_source_match_template_id?: number;
  /** 字段名称 */
  field_name?: string;
  /** 策划备注要求 */
  plan_remark?: string;
  /** 供应商填写样例 */
  value_example?: string;
};
/**
 * 定制模版
 */
export type DemandSourceMatchTemplateVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 模版名称 */
  name?: string;
  /** 合作主平台 */
  main_platform?: string[];
  /** 产品ID列表 */
  product_id?: number[];
  /** 产品明细 */
  products?: BizProductDto[];
  /** 是否启用 */
  state?: boolean;
  /** 定制排查模版字段列表 */
  demand_source_match_template_field_entities?: DemandSourceMatchTemplateFieldVo[];
};
export type PageVoDemandSourceMatchTemplateVo = {
  total?: number;
  records?: DemandSourceMatchTemplateVo[];
};
export type ResponsePageVoDemandSourceMatchTemplateVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoDemandSourceMatchTemplateVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 定制模版详情
 */
export type DemandSourceMatchTemplateAllVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  demand_source_match_template?: DemandSourceMatchTemplateVo;
};
export type ResponseDemandSourceMatchTemplateAllVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSourceMatchTemplateAllVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandSourceMatchTemplateCreateForm = {
  /** 模版名称 */
  name: string;
  /** 合作主平台 */
  main_platform: string[];
  /** 产品 */
  product_id: number[];
  /** 状态 */
  state?: boolean;
  /** 定制排查字段列表 */
  demand_source_match_template_field_entities: DemandSourceMatchTemplateFieldEntity[];
};
/**
 * 报价内容模板类型：0=模板1, 1=模板2
 */
export type QuoteTempTypeEnum = 0 | 1;
/**
 * 报价明细上传方式（1-单个上传；2-合并上传）
 */
export type QuoteUploadMethodEnum = 1 | 2;
/**
 * 创建/更新报价模板 报价内容中的一行内容
 */
export type QuoteTempContentDto = {
  name?: string;
  l1_category_id?: number;
  l1_category_name?: string;
  l2_category_id?: number;
  l2_category_name?: string;
  l3_category_id?: number;
  l3_category_name?: string;
  level?: number;
  children?: QuoteTempContentDto[];
  col1?: string;
  col2?: string;
};
/**
 * 其他字段列表
 */
export type QuoteTempInfoDto = {
  id?: number;
  version?: number;
  created_by?: number;
  updated_by?: number;
  template_id?: number;
  label?: string;
  index_id?: number;
};
/**
 * 列名, 对应col1, col2
 */
export type IdLabelDto = {
  /** ID */
  id?: number;
  /** 标签 */
  label?: string;
};
/**
 * 前端要求的创建报价内容表单结构
 */
export type FeQuotationContentForm = {
  /** 模板内容 */
  quotation_template?: QuoteTempContentDto[];
  /** 列名, 对应col1, col2 */
  infos?: IdLabelDto[];
  contents?: QuoteTempContentDto[];
};
/**
 * 前端更新报价模板的请求对象
 */
export type FeQuoteTempUpdateForm = {
  /** 模板ID, 更新时必传 */
  id?: number;
  temp_type?: QuoteTempTypeEnum;
  unit_type?: QuoteUnitTypeEnum;
  upload_method?: QuoteUploadMethodEnum;
  /** 内容列表, 类别1 自定义的 title */
  col1?: string;
  /** 内容列表, 类别2 自定义的 title */
  col2?: string;
  /** 内容列表 */
  contents?: QuoteTempContentDto[];
  /** 其他字段列表 */
  infos?: QuoteTempInfoDto[];
  quotation_content?: FeQuotationContentForm;
};
/**
 * 报价模板分页查询请求对象
 */
export type QuoteTempPageQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  l1_category_ids?: number[];
  /** 分类ID列表 */
  category_ids?: number[];
};
/**
 * 报价模板
 */
export type QuoteTempVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 一级分类名称 */
  l1_category_name?: string;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 二级分类名称 */
  l2_category_name?: string;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 三级分类名称 */
  l3_category_name?: string;
  temp_type?: QuoteTempTypeEnum;
  unit_type?: QuoteUnitTypeEnum;
  upload_method?: QuoteUploadMethodEnum;
  /** 内容列表, 类别1 自定义的 title */
  col1?: string;
  /** 内容列表, 类别2 自定义的 title */
  col2?: string;
  /** 内容列表 */
  contents?: QuoteTempContentDto[];
  /** 其他字段列表 */
  infos?: QuoteTempInfoDto[];
  quotation_content?: FeQuotationContentForm;
  /** 需求分类全路径 */
  category_names?: string;
};
export type PageVoQuoteTempVo = {
  total?: number;
  records?: QuoteTempVo[];
};
export type ResponsePageVoQuoteTempVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoQuoteTempVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 前端要求的创建报价模板表单结构
 */
export type FeQuoteTempCreateForm = {
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  temp_type?: QuoteTempTypeEnum;
  unit_type?: QuoteUnitTypeEnum;
  upload_method?: QuoteUploadMethodEnum;
  /** 内容列表, 类别1 自定义的 title */
  col1?: string;
  /** 内容列表, 类别2 自定义的 title */
  col2?: string;
  /** 内容列表 */
  contents?: QuoteTempContentDto[];
  /** 其他字段列表 */
  infos?: QuoteTempInfoDto[];
  quotation_content?: FeQuotationContentForm;
};
/**
 * 核价规则类型
 */
export type PriceCheckTypeEnum = 'percent' | 'formula';
/**
 * 商务核价规则修改
 */
export type DemandPriceCheckTemplateUpdateForm = {
  /** 模版id */
  id?: number;
  /** 合作主平台 */
  main_platform?: string[];
  /** 采购方式 */
  purchasing_method?: string[];
  /** 主平台官单/私单 */
  public_order?: string[];
  /** 平台费 */
  platform_fee?: string[];
  /** 基准价类型 */
  base_price_type?: string[];
  price_check_type?: PriceCheckTypeEnum;
  /** 核价规则值 */
  price_check_value?: string;
  /** 状态 */
  state?: boolean;
};
/**
 * 商务核价规则查询
 */
export type DemandPriceCheckTemplateQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 模版id */
  id?: number;
  /** 合作主平台 */
  main_platform?: string[];
  /** 采购方式 */
  purchasing_method?: string[];
  /** 主平台官单/私单 */
  public_order?: string[];
  /** 平台费 */
  platform_fee?: string[];
  /** 基准价类型 */
  base_price_type?: string[];
  price_check_type?: PriceCheckTypeEnum;
  /** 状态 */
  state?: boolean;
};
/**
 * 商务核价规则配置
 */
export type DemandPriceCheckTemplateVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 合作主平台 */
  main_platform?: string[];
  /** 采购方式 */
  purchasing_method?: string[];
  /** 主平台官单/私单 */
  public_order?: string[];
  /** 平台费 */
  platform_fee?: string[];
  /** 基准价类型 */
  base_price_type?: string[];
  price_check_type?: PriceCheckTypeEnum;
  /** 核价规则值 */
  price_check_value?: string;
  /** 状态 */
  state?: boolean;
};
export type PageVoDemandPriceCheckTemplateVo = {
  total?: number;
  records?: DemandPriceCheckTemplateVo[];
};
export type ResponsePageVoDemandPriceCheckTemplateVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoDemandPriceCheckTemplateVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseDemandPriceCheckTemplateVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandPriceCheckTemplateVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandPriceCheckTemplateCreateForm = {
  /** 合作主平台 */
  main_platform?: string[];
  /** 采购方式 */
  purchasing_method?: string[];
  /** 主平台官单/私单 */
  public_order?: string[];
  /** 平台费 */
  platform_fee?: string[];
  /** 基准价类型 */
  base_price_type?: string[];
  price_check_type?: PriceCheckTypeEnum;
  /** 核价规则值 */
  price_check_value?: string;
  /** 状态 */
  state?: boolean;
};
/**
 * 考核指标-更新表单
 */
export type DemandKpiUpdateForm = {
  /** 考核指标ID, 更新时必填 */
  id?: number;
  /** 考核指标名称 */
  name?: string;
  type?: DemandKpiTypeEnum;
  show_for_out?: CommonYesNoEnum;
  platform?: DemandKpiPlatformEnum;
  /** 关联数易指标的指标Code, 如果是MPC则为空, 如果是DSPF则必填 */
  dspf_code?: string;
  positive?: DemandKpiPositiveEnum;
  percent?: CommonYesNoEnum;
  status?: CommonEnableStatusEnum;
  long_or_short_term?: DemandKpiLongOrShortTermEnum;
  /** 是否数易特殊指标 */
  dspf_special?: boolean;
  /** 是否跳过拆单一致性校验, 仅 DSPF 平台可配置, true=是 false=否 #7370 */
  skip_validation?: boolean;
  /** 不参与考核投放渠道 */
  exclude_delivery_channels?: string[];
  /** 不参与考核的产品 */
  exclude_products?: ProductSimpleVo[];
  /** 系统编码 */
  sys_code?: string;
  exclude_product_ids?: number[];
};
/**
 * 考核指标-新建&修改响应对象
 */
export type DemandKpiCreateUpdateRespVo = {
  /** 数据库主键ID */
  id?: number;
};
export type ResponseDemandKpiCreateUpdateRespVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandKpiCreateUpdateRespVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 考核指标查询表单
 */
export type DemandKpiQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 考核指标名称模糊查询 */
  name_like?: string;
  /** 考核指标类型, 多选 */
  type_list?: DemandKpiTypeEnum[];
  /** 考核指标状态 */
  status_list?: CommonEnableStatusEnum[];
  /** 关联平台code, 多选 */
  platform_list?: DemandKpiPlatformEnum[];
  /** 是否对供应商显示, 多选 */
  show_for_out_cod_list?: CommonYesNoEnum[];
  /** 短期/长期指标, 多选 */
  demand_kpi_long_or_short_terms?: DemandKpiLongOrShortTermEnum[];
  /** 是否特殊指标 */
  dspf_special_list?: boolean[];
  /** 是否跳过校验, 多选 #7370 */
  skip_validation_list?: boolean[];
  /** 系统编码，模糊查询 */
  sys_code?: string;
};
export type PageVoDemandKpiVo = {
  total?: number;
  records?: DemandKpiVo[];
};
export type ResponsePageVoDemandKpiVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoDemandKpiVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandKpiCreateForm = {
  /** 考核指标名称, 必填 */
  name?: string;
  type?: DemandKpiTypeEnum;
  show_for_out?: CommonYesNoEnum;
  platform?: DemandKpiPlatformEnum;
  /** 关联数易指标的指标, 如果是MPC则为空, 如果是DSPF则必填 */
  dspf_code?: string;
  positive?: DemandKpiPositiveEnum;
  percent?: CommonYesNoEnum;
  long_or_short_term?: DemandKpiLongOrShortTermEnum;
  /** 系统编码 */
  sys_code?: string;
  /** 是否特殊指标 */
  dspf_special?: boolean;
  /** 是否跳过拆单一致性校验, 仅 DSPF 平台可配置, true=是 false=否 #7370 */
  skip_validation?: boolean;
  /** 不参与考核投放渠道 */
  exclude_delivery_channels?: string[];
  /** 不参与考核的产品 */
  exclude_products?: ProductSimpleVo[];
  exclude_product_ids?: number[];
};
/**
 * 发票详情
 */
export type InvoiceDetailVo = {
  /** 发票ID，MPC侧生成的发票表主键ID，同一张发票被删除重新生成，ID会不同 */
  id?: number;
  /** 发票ID，M税务系统生成的发票ID，同一张发票被删除重新生成，ID会不同 */
  invoice_id?: string;
  /** 发票代码 */
  invoice_code?: string;
  /** 发票号码 */
  invoice_no?: string;
  /** 发票唯一Key，税务系统生成的唯一Key，同一张发票的key相同 */
  invoice_key?: string;
  invoice_type?: InvoiceType;
  /** 发票对应的主供应商名称 */
  supplier_name?: string;
  /** 发票含税总金额 */
  amount_with_tax?: number;
  /** 发票不含税金额 */
  amount_without_tax?: number;
  /** 发票税额 */
  tax?: number;
  /** 发票附件 */
  attachments?: AttachmentDto[];
  currency?: CurrencyEnum;
};
/**
 * 发票上传结果
 */
export type InvoiceUploadResultVo = {
  invoice_detail?: InvoiceDetailVo;
};
export type ResponseInvoiceUploadResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: InvoiceUploadResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 需求合作属性更新请求对象
 */
export type DemandCooperatePropertyUpdateForm = {
  /** 需求合作属性ID, 更新时必填 */
  id?: number;
  type?: DemandCooperatePropertyTypeEnum;
  /** 需求合作属性选项 */
  options?: string[];
  status?: CommonEnableStatusEnum;
  /** 系统编码(非必填) */
  sys_code?: string;
};
/**
 * 需求合作属性新建&修改响应对象
 */
export type DemandCooperatePropertyCreateUpdateRespVo = object;
export type ResponseDemandCooperatePropertyCreateUpdateRespVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandCooperatePropertyCreateUpdateRespVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 需求合作属性查询请求对象
 */
export type DemandCooperatePropertyQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求合作属性名称模糊查询 */
  name_like?: string;
  /** 需求合作属性名称&编号模糊查询 */
  name_or_code_like?: string;
  /** 需求合作属性状态 */
  status_list?: CommonEnableStatusEnum[];
};
/**
 * 需求合作属性对象
 */
export type DemandCooperatePropertyVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 需求合作属性编码 */
  code?: string;
  /** 需求合作属性名称 */
  name?: string;
  type?: DemandCooperatePropertyTypeEnum;
  /** 当 type=下拉列表时, 该字段为下拉列表选项 */
  options?: string[];
  /** 需求合作属性描述 */
  description?: string;
  status?: CommonEnableStatusEnum;
  /** 系统编码(非必填) */
  sys_code?: string;
};
export type PageVoDemandCooperatePropertyVo = {
  total?: number;
  records?: DemandCooperatePropertyVo[];
};
export type ResponsePageVoDemandCooperatePropertyVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoDemandCooperatePropertyVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 需求合作属性创建请求对象
 */
export type DemandCooperatePropertyCreateForm = {
  /** 需求合作属性名称, 不能重复 */
  name?: string;
  type?: DemandCooperatePropertyTypeEnum;
  options?: string[];
  /** 系统编码(非必填) */
  sys_code?: string;
};
/**
 * 分页数据
 */
export type DemandCopyVo = {
  id?: number;
  name?: string;
  product_name?: string;
  category_names?: string;
  l1_category_id?: number;
  l2_category_id?: number;
  l3_category_id?: number;
  group_type?: DemandGroupType;
};
/**
 * 分页结果
 */
export type PageResultDemandCopyVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: DemandCopyVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultDemandCopyVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultDemandCopyVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 复制拆单校验入参
 */
export type CopyDemandCheckForm = {
  /** 一级单ID */
  demand_id?: number;
  /** 被复制的二级单ID */
  copy_demand_id?: number;
};
export type QueryContractWorkflowForm = {
  /** 合同系统流程id */
  external_contract_id: string;
};
export type SolarMoneyVo = {
  amount?: number;
  currency?: string;
};
export type SolarCtProcessStatus =
  | 'DRAFT'
  | 'AUDITING'
  | 'ARCHIVED'
  | 'DELETED'
  | 'TERMINATED';
export type OperationRecordNodeVo = {
  act_id?: string;
  act_code?: string;
  task_name?: string;
  detail_list?: DetailVo[];
};
export type SolarContractWorkFlowOpenVo = {
  request_id?: string;
  contract_name?: string;
  contract_num?: string;
  contract_amount?: SolarMoneyVo;
  total_amount?: SolarMoneyVo;
  start_date?: string;
  end_date?: string;
  status?: SolarCtProcessStatus;
  operation_record_list?: OperationRecordNodeVo[];
};
export type ResponseSolarContractWorkFlowOpenVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SolarContractWorkFlowOpenVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type UpdateContractSignTypeForm = {
  /** 需求单id */
  demand_id?: number;
  /** demandSupplierId */
  demand_supplier_id?: number;
  contract_sign_type_enum?: ContractSignTypeEnum;
};
export type UpdateContractTemplateCmd = {
  /** 合同模版编码 */
  code?: string;
  /** 名称 */
  name: string;
  /** 合同类型 */
  type: string;
  /** 生效状态 */
  status?: boolean;
  /** 合同小类编码 */
  l2_category_code?: string;
  /** 收付款条款 */
  pay_and_receipt_terms?: string;
  /** 附件ID列表 */
  blob_ids?: number[];
  /** 费用类型（需求分类三级）ID列表 */
  demand_category_ids?: number[];
  /** 需求单类型：1=常规采购，3=比选 */
  demand_type?: number;
};
export type NewContractTemplateCmd = {
  /** 名称 */
  name: string;
  /** 合同类型 */
  type: string;
  /** 生效状态 */
  status?: boolean;
  /** 合同小类编码 */
  l2_category_code?: string;
  /** 收付款条款 */
  pay_and_receipt_terms?: string;
  /** 附件ID列表 */
  blob_ids?: number[];
  /** 费用类型（需求分类三级）ID列表 */
  demand_category_ids?: number[];
  /** 需求单类型：1=常规采购，3=比选 */
  demand_type?: number;
};
export type ContractTemplateQuery = {
  /** 合同名称 或者 合同模版编码 */
  keyword?: string;
  /** 合同类型 */
  types?: string[];
  /** 生效状态 */
  status?: number[];
  /** 费用类型（需求分类三级）ID列表（按需求分类过滤模板） */
  demand_category_ids?: number[];
  /** 需求单类型：1=常规采购，3=比选，支持多选 */
  demand_type?: number[];
  /** 页码 */
  page_num: number;
  /** 每页条数 */
  page_size: number;
};
/**
 * 附件ID列表
 */
export type ContractTemplateDtoBlobDto = {
  id?: number;
  /** 文件名 */
  filename?: string;
  /** 文件大小 */
  byte_size?: number;
  /** 文件类型 */
  content_type?: string;
  /** 文件key */
  key?: string;
  /** 是否自动生成 */
  auto_generated?: boolean;
};
export type ContractTemplateVo = {
  /** 名称 */
  name: string;
  /** 合同模版编码 */
  code?: string;
  /** 合同类型 */
  type: string;
  /** 生效状态 */
  status?: boolean;
  /** 合同大类编码 */
  l1_category_code?: string;
  /** 合同小类编码 */
  l2_category_code?: string;
  /** 合同大类名称 */
  l1_category_name?: string;
  /** 合同小类名称 */
  l2_category_name?: string;
  /** 收付款条款 */
  pay_and_receipt_terms?: string;
  /** 费用类型（需求分类三级）ID列表 */
  demand_category_ids?: number[];
  /** 需求单类型：1=常规采购，3=比选 */
  demand_type?: number;
  /** 版本号 */
  version?: number;
  /** 附件ID列表 */
  blobs?: ContractTemplateDtoBlobDto[];
  /** 更新人 */
  updated_by?: number;
  /** 更新人 */
  updated_user_name?: string;
  /** 更新时间 */
  updated_at?: string;
};
export type PageVoContractTemplateVo = {
  page_num?: number;
  page_size?: number;
  total?: number;
  data?: ContractTemplateVo[];
};
export type ResponsePageVoContractTemplateVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoContractTemplateVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 合作有效期
 */
export type DateRange = {
  begin?: string;
  end?: string;
};
export type ContractCostCompany = {
  id?: number;
  name?: string;
  expense_code?: string;
  address?: string;
};
/**
 * 我方主体
 */
export type OurContractParty = {
  cost_company?: ContractCostCompany;
  contact_name?: string;
  contact_method?: string;
  contact_mail?: string;
  our_party_contact_address?: string;
};
/**
 * 标签类型
 */
export type TagType =
  | 'BLOCKLIST'
  | 'ENTERPRISE_RISK'
  | 'ENTERPRISE_BASIC'
  | 'HISTORY_CORPORATION';
/**
 * 标签级别
 */
export type TagLevel =
  | 'LIMIT_COOPERATION'
  | 'HIGH'
  | 'MEDIUM'
  | 'LOW'
  | 'OTHER';
/**
 * 供应商风险标签DTO
 */
export type SupplierRiskTagDto = {
  /** 主供应商ID */
  main_supplier_id?: number;
  /** vmdm供应商Code */
  mdm_code?: string;
  tag_type?: TagType;
  tag_level?: TagLevel;
  /** 标签名称 */
  label?: string;
  /** 标签描述 */
  desc?: string;
};
export type ContractMainSupplier = {
  /** 主供应商id */
  id?: number;
  /** 主供应商名称 */
  name?: string;
  /** 主供应商简称 */
  abbr_name?: string;
  mdm_code?: string;
  /** 供应商风险标签 */
  risk_tags?: SupplierRiskTagDto[];
  /** 是否内部供应商 */
  inner_supplier?: boolean;
};
/**
 * 对方主体
 */
export type OtherContractParty = {
  main_supplier?: ContractMainSupplier;
  contact_name?: string;
  contact_method?: string;
  contact_mail?: string;
  other_party_contact_address?: string;
};
/**
 * 甲方银行账户信息
 */
export type PartyaBankInfo = {
  partya_company_name?: string;
  partya_identification_number?: string;
  partya_address?: string;
  partya_phone?: string;
  partya_bank_name?: string;
  partya_account?: string;
};
/**
 * 乙方银行账户信息
 */
export type PartybBankInfo = {
  partyb_company_name?: string;
  partyb_bank_name?: string;
  partyb_account?: string;
  partyb_account_name?: string;
  partyb_swift_code?: string;
  bank_business_code?: string;
};
export type SubmitContractForm = {
  /** 合同名称 */
  name: string;
  time_range: DateRange;
  invoice_type: InvoiceType;
  /** 税率 */
  tax_rate: number;
  total_amount: Money;
  /** 申请内容 */
  approval_content?: string;
  /** 申请简介 */
  approval_summary?: string;
  /** 参与合同发起的供应商关联列表 */
  demand_supplier_ids: number[];
  /** 使用的合同模板编码 */
  template_code: string;
  our_party: OurContractParty;
  other_party: OtherContractParty;
  partya_bank_info: PartyaBankInfo;
  partyb_bank_info: PartybBankInfo;
  /** 附件列表 */
  attachment_blob_ids: number[];
  /** 提交人 */
  submit_user_id: number;
  /** 附件防篡改签名 */
  attachment_signature: string;
  esigning?: boolean;
};
export type QueryContractDraftContentForm = {
  /** 用于发起的需求供应商关联id列表 */
  demand_supplier_ids: number[];
  /** 模板code */
  template_code: string;
};
export type ContractDraftContentVoBlobDto = {
  id?: number;
  /** 文件名 */
  filename?: string;
  /** 文件大小 */
  byte_size?: number;
  /** 文件类型 */
  content_type?: string;
  key?: string;
};
/**
 * 附件列表
 */
export type ContractDraftContentVoAttachmentDto = {
  blob?: ContractDraftContentVoBlobDto;
};
export type ContractDraftContentVo = {
  /** 合同名称 */
  name?: string;
  time_range?: DateRange;
  our_party?: OurContractParty;
  other_party?: OtherContractParty;
  partya_bank_info?: PartyaBankInfo;
  partyb_bank_info?: PartybBankInfo;
  invoice_type?: InvoiceType;
  /** 税率 */
  tax_rate?: number;
  total_amount?: Money;
  /** 附件列表 */
  attachments?: ContractDraftContentVoAttachmentDto[];
  /** 申请内容 */
  approval_content?: string;
  /** 申请简介 */
  approval_summary?: string;
  /** 附件是否只读 */
  attachment_readonly?: boolean;
  /** 附件防篡改签名 */
  attachment_signature?: string;
  esigning?: boolean;
};
export type ResponseContractDraftContentVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ContractDraftContentVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type PendingContractQuery = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求单编号/需求标题 */
  keyword?: string;
  /** 需求单类型, 多选 */
  demand_categories?: number[];
  /** 产品, id, 多选 */
  product?: number[];
  /** 需求人员, 多选 */
  principals?: number[];
  /** 商务人员, 多选 */
  business_members?: number[];
  /** 签约类型, 多选 */
  contract_sign_type_list?: ContractSignTypeEnum[];
  /** 我方主体, 多选 */
  cost_company_id_list?: number[];
  /** 对方主体, 多选 */
  demand_supplier_id_list?: number[];
};
/**
 * 分页数据
 */
export type PendingContractVo = {
  /** 三级单号 */
  demand_id?: number;
  /** 一级单号 */
  root_id?: number;
  /** 产品 */
  product_identifier?: string;
  /** 需求标题 */
  name?: string;
  /** 需求类型 */
  demand_category_name?: string;
  /** 一级需求分类ID */
  l1_category_id?: number;
  /** 二级需求分类ID */
  l2_category_id?: number;
  /** 三级需求分类ID */
  l3_category_id?: number;
  /** 需求人员 */
  demand_principals?: UserSimpleVo[];
  /** 商务人员名称 */
  member_business?: MemberBusinessDto[];
  cost_company?: CostCompanyDto;
  /** 对方主体 */
  supplier_name?: string;
  contract_sign_type_enum?: ContractSignTypeEnum;
  /** 金额 */
  amount?: number;
  currency?: CurrencyEnum;
  /** 供应商id */
  supplier_id?: number;
  /** 主供应商id */
  main_supplier_id?: number;
  /** 对方主体id */
  demand_supplier_id?: number;
  /** 供应商收款银行账号 */
  bank_account?: string;
  /** 供应商收款银行账户名称 */
  bank_account_name?: string;
};
/**
 * 分页结果
 */
export type PageResultPendingContractVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: PendingContractVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultPendingContractVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultPendingContractVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type UpdateBiddingContractSignTypeForm = {
  /** bidding_supplier ID */
  bidding_supplier_id: number;
  contract_sign_type: ContractSignTypeEnum;
};
/**
 * 每个中选供应商的草稿数据
 */
export type SupplierDraft = {
  /** 比选单ID */
  bidding_id: number;
  /** 合同模板code */
  template_code: string;
  our_party: OurContractParty;
  partya_bank_info: PartyaBankInfo;
  /** 合同发起人ID */
  submit_user_id: number;
  /** 中选供应商ID */
  bidding_supplier_id: number;
  /** 合同名称（自动填充，可编辑，≤30字） */
  name: string;
  time_range: DateRange;
  total_amount: Money;
  other_party: OtherContractParty;
  partyb_bank_info: PartybBankInfo;
  invoice_type: InvoiceType;
  /** 税率（默认6%，可编辑） */
  tax_rate: number;
  /** 附件ID列表（含模板基础附件 + 自动生成的 docx/pdf） */
  attachment_blob_ids: number[];
  /** 附件防篡改签名 */
  attachment_signature: string;
  esigning?: boolean;
};
export type SubmitBiddingContractForm = {
  /** 每个中选供应商的草稿数据 */
  drafts: SupplierDraft[];
};
export type BiddingSupplierDraftVoBiddingBlobDto = {
  id?: number;
  /** 文件名 */
  filename?: string;
  /** 文件大小 */
  byte_size?: number;
  /** 文件类型 */
  content_type?: string;
  key?: string;
};
/**
 * 附件列表（对齐 ContractDraftContentVO.AttachmentDto 结构）
 */
export type BiddingSupplierDraftVoBiddingAttachmentDto = {
  blob?: BiddingSupplierDraftVoBiddingBlobDto;
  /** 是否自动生成的附件 */
  auto_generated?: boolean;
};
/**
 * 各中选供应商草稿列表
 */
export type BiddingSupplierDraftVo = {
  /** 中选供应商ID */
  bidding_supplier_id?: number;
  /** 供应商名称 */
  supplier_name?: string;
  /** 供应商简称 */
  supplier_short_name?: string;
  /** tab key: #比选单号-供应商简称 */
  tab_key?: string;
  /** 合同名称（bidding.name + "-" + supplier.abbrName，≤30字） */
  name?: string;
  time_range?: DateRange;
  our_party?: OurContractParty;
  other_party?: OtherContractParty;
  partya_bank_info?: PartyaBankInfo;
  partyb_bank_info?: PartybBankInfo;
  invoice_type?: InvoiceType;
  /** 税率（默认 6%） */
  tax_rate?: number;
  total_amount?: Money;
  /** 附件列表（对齐 ContractDraftContentVO.AttachmentDto 结构） */
  attachments?: BiddingSupplierDraftVoBiddingAttachmentDto[];
  /** 附件防篡改签名 */
  attachment_signature?: string;
  esigning?: boolean;
};
/**
 * 金额剔除后跳过的供应商
 */
export type SkippedSupplierDto = {
  /** 供应商名称 */
  supplier_name?: string;
  /** 跳过原因 */
  reason?: string;
};
export type BiddingContractDraftContentVo = {
  /** 各中选供应商草稿列表 */
  drafts?: BiddingSupplierDraftVo[];
  /** 金额剔除后跳过的供应商 */
  skipped_suppliers?: SkippedSupplierDto[];
};
export type ResponseBiddingContractDraftContentVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingContractDraftContentVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QueryBiddingDraftContentForm = {
  /** 比选单ID */
  bidding_id: number;
  /** 合同模板code */
  template_code: string;
};
export type BiddingContractPendingListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 比选单编号/标题关键字 */
  keyword?: string;
  /** 产品ID列表 */
  product_ids?: number[];
  /** 供应商ID列表 */
  supplier_ids?: number[];
  /** 我方主体ID列表 */
  cost_company_ids?: number[];
  signing_type?: ContractSignTypeEnum;
  /** 比选类型列表（多选，枚举名如 WHOLE/EVENT/ANNUAL） */
  bidding_type_list?: string[];
};
/**
 * 中选供应商详情列表
 */
export type SelectedSupplierDto = {
  /** 供应商ID */
  supplier_id?: number;
  /** 供应商名称 */
  supplier_name?: string;
  /** 供应商简称 */
  supplier_short_name?: string;
  /** 中选状态 */
  selected_state?: string;
  /** 预算分配 */
  budget_allocation?: string;
};
/**
 * 分页数据
 */
export type BiddingContractPendingVo = {
  /** 比选单ID */
  bidding_id?: number;
  /** 比选单号 */
  bidding_no?: string;
  /** 比选单名称 */
  bidding_name?: string;
  bidding_type?: BiddingTypeV2Enum;
  /** 所属产品编码 */
  product_identifier?: string;
  cost_company?: CostCompanyDto;
  /** 中选供应商名称列表 */
  supplier_names?: string[];
  signing_type?: ContractSignTypeEnum;
  /** 中选总金额 */
  amount?: number;
  currency?: CurrencyEnum;
  /** 中选供应商详情列表 */
  winning_suppliers?: SelectedSupplierDto[];
};
/**
 * 分页结果
 */
export type PageResultBiddingContractPendingVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: BiddingContractPendingVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultBiddingContractPendingVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultBiddingContractPendingVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type LinkBiddingContractForm = {
  items: Item[];
};
/**
 * 模型类型
 */
export type ModelType = 'DEMAND' | 'BIDDING' | 'TRUMPET';
/**
 * 通用提示文案入参
 */
export type CommonTipTextForm = {
  group_type?: DemandGroupType;
  model_type?: ModelType;
  /** 需求id，如果传递了这个值，也可以不传递groupType、modelType、l3CategoryId */
  demand_id?: number;
  /** 字段key */
  keys?: string[];
  /** 三级需求类型 */
  l3_category_id?: number;
};
/**
 * 提示类型
 */
export type TipTypeEnum =
  | 'QUESTION_HOVER'
  | 'INPUT_PLACEHOLDER'
  | 'INPUT_BOTTOM_PROMPT';
/**
 * 提示文案
 */
export type TipTextVo = {
  /** 字段key */
  tip_key?: string;
  tip_type?: TipTypeEnum;
  /** 提示文案 */
  tip_text?: string;
};
export type ResponseListTipTextVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TipTextVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type CommonTemporarySaveForm = {
  /** 暂存类别，由前端自定义传入，用于区分不同业务场景的暂存数据，建议使用大写字母 */
  type: string;
  /** 暂存编码，由前端自定义传入，用于区分同一类别下的不同暂存数据 */
  code: string;
  /** 暂存内容，JSON 字符串 */
  content: string;
};
export type CommonTemporaryQueryForm = {
  /** 暂存类别，建议使用大写字母 */
  type: string;
  /** 暂存编码 */
  code: string;
};
/**
 * 比选页面传递
 */
export type BiddingParticipantType = 'demand' | 'plan' | 'purchase';
/**
 * 拆单选项表单
 */
export type PlanInfoOptionsForm = {
  /** 是否绑定执行数据 */
  is_bind_execution_data?: number;
  /** 【结构化】【所有】合作内容列表 */
  cooperate_accounts?: DemandCooperateAccountDto[];
  /** expect_begin_at */
  expect_begin_at?: string;
  /** expect_end_at */
  expect_end_at?: string;
};
/**
 * 通用选项表单
 */
export type CommonOptionsForm = {
  /** 关键字 */
  keys?: string[];
  /** 父节点id */
  parent_id?: number;
  /** 主供应商ID */
  main_supplier_id?: number;
  group_type?: DemandGroupType;
  demand_state?: DemandState;
  bidding_state?: BiddingState;
  /** 产品ID列表 */
  product_ids?: number[];
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  supplier_kpi_item_rule_detail_source?: SupplierKpiItemRuleDetailSourceEnum;
  /** 需求分类id列表，支持多级 */
  category_ids?: number[];
  /** 需求单ID */
  demand_id?: number;
  trumpet_state?: TrumpetState;
  /** 小喇叭需求单号 */
  trumpet_demand_id?: number;
  /** 比选单ID */
  bidding_id?: number;
  bidding_type_v2?: BiddingTypeV2Enum;
  bidding_participant_type?: BiddingParticipantType;
  /** 供应商渠道类型 */
  supplier_channel_types?: ChannelType[];
  /** 供应商标签 */
  supplier_tag_ids?: number[];
  outsource_state?: MatchOutsourceStatusEnum;
  /** 外包侧节点状态(多选) */
  outsource_state_list?: MatchOutsourceStatusEnum[];
  purchasing_method?: PurchasingMethodEnum;
  purchasing_strategy_enum?: PurchasingStrategyEnum;
  /** 待办tab类型 */
  outsource_todo_type?: string;
  /** 是否海外invoice，1=是，0=否 */
  is_oversea_invoice?: number;
  /** 是否合同管理 */
  is_contract?: boolean;
  /** 对方费用主体搜索字段 */
  demand_suppliers_owners_keyword?: string;
  /** 我方主体下拉框搜索字段 */
  cost_company_keyword?: string;
  /** 是否来自工作台的查询 */
  from_workspace_query_flag?: boolean;
  /** 渠道供应商ID */
  supplier_id?: number;
  /** 渠道供应商服务类型 */
  service_type?: ChannelType[];
  /** 是否推荐供应商 */
  if_recommend_type?: number;
  /** 比选评审流程版本 */
  review_version?: string;
  /** 需求合作属性名称 */
  cooperate_property_name?: string;
  /** 是否启用供应商使用限制规则 - 当前只在false场景下有判断逻辑 */
  supplier_limit?: boolean;
  plan_info_form?: PlanInfoOptionsForm;
};
/**
 * 上传来源
 */
export type FileUploadSource = '0' | '1';
export type FileRequestUploadFileForm = {
  file_name: string;
  file_size: number;
  source: FileUploadSource;
};
export type FileRequestUploadVo = {
  /** 上传地址 */
  upload_url?: string;
  /** 完成令牌 */
  token?: string;
  /** 额外头部 */
  extra_headers?: {
    [key: string]: any;
  };
};
export type ResponseFileRequestUploadVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: FileRequestUploadVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type FileCompleteUploadForm = {
  /** 令牌 */
  token: string;
};
export type FileCompleteUploadVo = {
  /** 文件地址 */
  url?: string;
  /** 文件id */
  id?: number;
  /** 文件名 */
  file_name?: string;
  /** 文件大小 */
  file_size?: number;
};
export type ResponseFileCompleteUploadVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: FileCompleteUploadVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ExchangeRateForm = {
  from_currency?: CurrencyEnum;
  to_currency?: CurrencyEnum;
  /** 汇率期间，yyyy-MM */
  conversion_period?: string;
};
export type SupplierVersionVo = {
  enable_supplier_version?: string;
};
export type ResponseSupplierVersionVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierVersionVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type CreateClosedLoopDemandCmd = {
  /** 需求单号 */
  id?: number;
  /** 需求标题 */
  title: string;
  group_type?: DemandGroupType;
  /** 需求背景 */
  background: string;
  /** 需求预期 */
  expectation: string;
  /** 一级需求分类 */
  l1_category_id?: number;
  /** 二级需求分类 */
  l2_category_id?: number;
  /** 所属产品 */
  product_id?: number;
  /** 关联项目 */
  number: string;
  priority?: PriorityEnum;
  budget_currency?: CurrencyEnum;
  /** 预计发放金额 */
  estimated_release_amount?: number;
  /** 需求人ids数组 */
  principal_ids: number[];
  /** 执行预期开始日期 */
  estimated_execution_start_at?: string;
  /** 执行预期结束日期 */
  estimated_execution_end_at?: string;
  /** 附件ids数组 */
  demand_attachment_ids?: number[];
};
export type CreateClosedLoopDemandResp = {
  /** 需求Id */
  demand_id?: number;
};
export type ResponseCreateClosedLoopDemandResp = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: CreateClosedLoopDemandResp;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 审批时的状态
 */
export type ClosedLoopDemandState =
  | 'newly_built'
  | 'demand_principal_approval'
  | 'plan_approval'
  | 'activity_design'
  | 'in_progress'
  | 'finished'
  | 'closed';
export type ClosedLoopApprovalBizFormType = 1 | 2 | 3 | 4;
/**
 * 审批时提交的业务表单
 */
export type ClosedLoopApprovalBizCmd = {
  type?: ClosedLoopApprovalBizFormType;
};
/**
 * 考核指标
 */
export type ClosedLoopAssessmentIndicatorRecordDto = {
  /** 序号 */
  index_id?: number;
  /** 指标Coded */
  kpi_code: string;
  kpi_schema: DemandKpiSchemaData;
  /** 达标要求 */
  expect_value?: string;
};
export type ClosedLoopActivityDesignCmd = ClosedLoopApprovalBizCmd & {
  root_id?: number;
  /** 活动标题 */
  title?: string;
  /** 活动详情 */
  activity_detail?: string;
  /** 二级需求分类 */
  l2_category_id?: number;
  /** 三级需求分类 */
  l3_category_id?: number;
  /** 四级需求分类 */
  l4_category_id?: number;
  /** 预计发放金额 */
  estimated_release_amount?: number;
  /** 活动执行日期开始日期 */
  activity_start_at?: string;
  /** 活动执行日期结束日期 */
  activity_end_at?: string;
  /** 活动执行附件ids数组 */
  activity_execution_attachment_ids?: number[];
  /** 考核指标 */
  assessment_indicator_requirements?: ClosedLoopAssessmentIndicatorRecordDto[];
};
/**
 * 营销闭环需求详情-指标信息
 */
export type ClosedLoopAssessmentIndicatorRecord = {
  /** 序号 */
  index_id?: number;
  /** 单号 */
  closed_loop_id?: number;
  /** 指标ID */
  kpi_code?: string;
  kpi_schema?: DemandKpiSchemaData;
  /** 是否直接考核 */
  is_direct?: boolean;
  /** 指标要求 */
  expect_value?: string;
  /** 填写记录 */
  actual_value?: string;
  /** 是否达标 */
  result?: boolean;
};
export type ClosedLoopCaseSubmitClosureCmd = ClosedLoopApprovalBizCmd & {
  /** 效果描述 */
  effect_description?: string;
  /** 活动执行附件ids数组 */
  billing_attachments?: number[];
  /** 活动执行附件ids数组 */
  report_attachments?: number[];
  /** 指标信息 */
  assessment_indicator_records?: ClosedLoopAssessmentIndicatorRecord[];
};
export type ClosedLoopDemandApprovalCmd = {
  /** 需求id */
  id: number;
  /** 备注 */
  note?: string;
  state: ClosedLoopDemandState;
  biz_cmd?:
    | ClosedLoopApprovalBizCmd
    | ClosedLoopActivityDesignCmd
    | ClosedLoopCaseSubmitClosureCmd;
  user?: User;
};
export type EditClosedLoopDemandCmd = {
  /** 单据ID */
  id: number;
  /** 执行预期结束日期 */
  estimated_execution_end_at?: string;
  /** 需求人员 */
  principal_ids?: number[];
  /** 创建人员 */
  author_id?: number;
  /** 需求背景 */
  background?: string;
  /** 需求预期 */
  expectation?: string;
};
/**
 * 审批时的状态
 */
export type ClosedLoopCaseState =
  | 'submit_case_closure'
  | 'approve_settle'
  | 'submit_oa'
  | 'confirm_verification'
  | 'finished'
  | 'closed';
export type ClosedLoopCaseApprovalCmd = {
  /** 需求id */
  id: number;
  /** 备注 */
  note?: string;
  /** 废弃原因 */
  discard_reason?: string;
  state: ClosedLoopCaseState;
  biz_cmd?:
    | ClosedLoopApprovalBizCmd
    | ClosedLoopActivityDesignCmd
    | ClosedLoopCaseSubmitClosureCmd;
  user?: User;
};
export type EditClosedLoopActivityCmd = {
  /** 单据ID */
  id: number;
  /** 活动详情 */
  activity_detail?: string;
  /** 附件ids数组 */
  activity_execution_attachment_ids?: number[];
};
/**
 * 修改品类商务负责人配置
 */
export type CategoryBusinessOwnerUpdateForm = {
  /** 配置 ID */
  id: number;
  /** 一级需求分类 */
  l1_category_id?: number;
  /** 二级需求分类 */
  l2_category_ids?: number[];
  /** 是否包含该一级下所有二级 */
  all_l2?: boolean;
  /** 商务负责人 UserId 列表 */
  user_ids?: number[];
  /** 产品所属: 0=国内,1=国外 */
  oversea_product_flag?: number[];
  /** 需求接收部门: CHANNEL_GROUP=发行中心, MARKET_GROUP=市场支持中心, CD_TRUMPET_GROUP=小喇叭需求, M_CLOSED_LOOP=营销闭环需求 */
  group_types?: DemandGroupType[];
  /** 启用状态: 0=禁用, 1=启用 */
  status?: number;
};
/**
 * 品类商务负责人配置分页查询
 */
export type CategoryBusinessOwnerPageQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 一级需求分类(用于按一级筛选) */
  l1_category_ids?: number[];
  /** 二级需求分类(用于按二级筛选) */
  l2_category_ids?: number[];
  /** 商务负责人姓名/邮箱 模糊匹配 */
  user_name_email_fuzzy?: string;
  /** 需求接收部门筛选 */
  group_types?: DemandGroupType[];
  /** 状态筛选: 0=禁用, 1=启用 */
  status?: number;
};
/**
 * 品类商务负责人配置
 */
export type CategoryBusinessOwnerVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 一级需求分类 ID */
  l1_category_id?: number;
  l1_category?: DemandCategory;
  /** 二级需求分类 ID 列表 */
  l2_category_ids?: number[];
  /** 二级需求分类列表 */
  l2_categories?: DemandCategory[];
  /** 是否包含该一级下所有二级 */
  all_l2?: boolean;
  /** 商务负责人列表 */
  users?: PlannerUserDto[];
  /** 产品所属: 0=国内,1=国外 */
  oversea_product_flag?: number[];
  /** 需求接收部门: CHANNEL_GROUP=发行中心, MARKET_GROUP=市场支持中心, CD_TRUMPET_GROUP=小喇叭需求, M_CLOSED_LOOP=营销闭环需求 */
  group_types?: DemandGroupType[];
  /** 启用状态: 0=禁用, 1=启用 */
  status?: number;
};
export type PageVoCategoryBusinessOwnerVo = {
  total?: number;
  records?: CategoryBusinessOwnerVo[];
};
export type ResponsePageVoCategoryBusinessOwnerVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoCategoryBusinessOwnerVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 品类商务负责人配置-二级需求分类选项查询表单
 */
export type CategoryBusinessOwnerL2SelectionQueryForm = {
  /** 配置 ID, 编辑场景下传入用于排除自身占用 */
  id?: number;
  /** 一级需求分类 ID */
  l1_category_id: number;
};
/**
 * 品类商务负责人-二级需求分类选项
 */
export type CategoryBusinessOwnerL2SelectionItemDto = {
  /** 二级需求分类 ID */
  l2_category_id?: number;
  /** 二级需求分类名称 */
  l2_category_name?: string;
  /** 是否可选, 默认可选; 已被其他配置占用时为 false */
  can_select?: boolean;
};
/**
 * 品类商务负责人-二级品类选项
 */
export type CategoryBusinessOwnerL2SelectionVo = {
  /** 二级品类选项列表 */
  l2_selections?: CategoryBusinessOwnerL2SelectionItemDto[];
  /** 可关联的商务负责人列表 */
  users?: PlannerUserDto[];
};
export type ResponseCategoryBusinessOwnerL2SelectionVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: CategoryBusinessOwnerL2SelectionVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 创建品类商务负责人配置
 */
export type CategoryBusinessOwnerCreateForm = {
  /** 一级需求分类 */
  l1_category_id: number;
  /** 二级需求分类; 留空且 allL2=true 表示该一级下所有二级 */
  l2_category_ids?: number[];
  /** 是否包含该一级下所有二级; 默认 false */
  all_l2?: boolean;
  /** 商务负责人 UserId 列表 */
  user_ids: number[];
  /** 产品所属: 0=国内,1=国外 */
  oversea_product_flag: number[];
  /** 需求接收部门: CHANNEL_GROUP=发行中心, MARKET_GROUP=市场支持中心, CD_TRUMPET_GROUP=小喇叭需求, M_CLOSED_LOOP=营销闭环需求 */
  group_types: DemandGroupType[];
  /** 启用状态: 0=禁用, 1=启用 */
  status: number;
};
/**
 * 更新分类商务人员配置
 */
export type CategoryBusinessMemberConfigUpdateForm = {
  /** 配置ID */
  id: number;
  /** 一级需求类型ID */
  l1_category_id: number;
  /** 需求类型ID列表 */
  category_ids: number[];
  /** 产品所属（位标志: 1=国内, 2=海外, 3=国内+海外） */
  product_area_flag: number;
  /** 商务人员ID列表 */
  user_ids: number[];
  /** 启用状态: 0=禁用, 1=启用 */
  status: number;
  /** 版本号（乐观锁） */
  version: number;
};
/**
 * 分类商务人员配置分页查询
 */
export type CategoryBusinessMemberConfigPageQueryForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 一级需求类型ID（筛选） */
  l1_category_id?: number;
  /** 产品所属（筛选，位标志: 1=国内, 2=海外） */
  product_area_flag?: number;
  /** 状态筛选: 0=禁用, 1=启用 */
  status?: number;
};
/**
 * 分类商务人员配置
 */
export type CategoryBusinessMemberConfigVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 一级需求类型ID */
  l1_category_id?: number;
  /** 一级需求类型名称 */
  l1_category_name?: string;
  /** 需求类型ID列表 */
  category_ids?: number[];
  /** 默认商务人员ID列表 */
  user_ids?: number[];
  /** 产品所属位标志（1=国内, 2=海外, 3=国内+海外） */
  product_area_flag?: number;
  /** 默认商务人员列表 */
  users?: UserSimpleVo[];
  /** 需求类型名称（中文，逗号拼接），如"搜索收口、搜索投放" */
  category_names?: string;
  /** 启用状态: 0=禁用, 1=启用 */
  status?: number;
};
export type PageVoCategoryBusinessMemberConfigVo = {
  total?: number;
  records?: CategoryBusinessMemberConfigVo[];
};
export type ResponsePageVoCategoryBusinessMemberConfigVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoCategoryBusinessMemberConfigVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 创建分类商务人员配置
 */
export type CategoryBusinessMemberConfigCreateForm = {
  /** 一级需求类型ID */
  l1_category_id: number;
  /** 需求类型ID列表 */
  category_ids: number[];
  /** 产品所属（位标志: 1=国内, 2=海外, 3=国内+海外） */
  product_area_flag: number;
  /** 商务人员ID列表 */
  user_ids: number[];
  /** 启用状态: 0=禁用, 1=启用 */
  status: number;
};
/**
 * 预算管理看板查询请求
 */
export type QueryBudgetDashboardCmd = {
  /** 一级单ID */
  demand_id: number;
  /** 是否查询非绑定执行数据的二级单。true（默认）=查询全部（含非绑定）；false=仅查询绑定执行数据的二级单 */
  include_non_bind_execution_data?: boolean;
  /** 是否查询已废弃的二级单。true=包含已废弃；false（默认）=排除已废弃 */
  include_abandoned?: boolean;
};
export type ResponseBudgetDashboardDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BudgetDashboardDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type BudgetAlertEnableCmd = {
  demand_ids: number[];
};
export type BudgetAlertDisableCmd = {
  demand_ids: number[];
};
/**
 * 一级单调整项
 */
export type DemandAdjustItem = {
  /** 一级单ID */
  demand_id?: number;
  /** 是否为发起一级单 */
  is_primary?: boolean;
  /** 调整前预算金额 */
  original_budget?: number;
  /** 预算币种 */
  budget_currency?: string;
  /** 预算调整金额 */
  adjust_amount?: number;
  /** 调整后预算金额 */
  adjusted_budget?: number;
  /** 调整后日均花费 */
  adjusted_daily_avg_fee?: number;
};
/**
 * 二级单调整项
 */
export type PlanAdjustItem = {
  /** 所属一级单ID */
  demand_id?: number;
  /** 二级单ID（新增草稿时为空） */
  plan_demand_id?: number;
  /** 是否为本次新增的草稿二级单 */
  is_new_draft?: boolean;
  /** 调整前预算金额 */
  original_budget?: number;
  /** 预算币种 */
  budget_currency?: string;
  /** 预算调整金额 */
  adjust_amount?: number;
  /** 调整后预算金额 */
  adjusted_budget?: number;
  /** 调整后日均花费 */
  adjusted_daily_avg_fee?: number;
  /** 草稿二级单数据JSON */
  draft_data?: string;
};
/**
 * 梯度考核指标调整项
 */
export type KpiAdjustItem = {
  /** 所属一级单ID */
  demand_id?: number;
  /** 执行周期开始日期 */
  cycle_begin_at?: string;
  /** 执行周期结束日期 */
  cycle_end_at?: string;
  /** 梯度（T1/T2/T3） */
  gradient?: string;
  /** 考核指标code */
  kpi_code?: string;
  /** 考核指标名称 */
  kpi_name?: string;
  /** 调整前预期值 */
  original_expect_value?: string;
  /** 调整后预期值 */
  adjusted_expect_value?: string;
};
/**
 * 暂存/提交预算调整入参
 */
export type SaveBudgetAdjustCmd = {
  /** 预算调整主单ID（首次暂存时为空） */
  order_id?: number;
  /** 发起一级单ID */
  demand_id: number;
  /** 预算调整说明 */
  description?: string;
  /** 提交时页面完整快照JSON（提交时必传，暂存时可选） */
  snapshot?: string;
  /** 一级单调整明细列表 */
  demand_items?: DemandAdjustItem[];
  /** 二级单调整明细列表 */
  plan_items?: PlanAdjustItem[];
  /** 梯度考核指标调整列表 */
  kpi_items?: KpiAdjustItem[];
};
/**
 * 流程状态
 */
export type BudgetAdjustStateEnum =
  | 'PENDING'
  | 'APPROVING'
  | 'EFFECTIVE'
  | 'NOT_EFFECTIVE'
  | 'REJECTED'
  | 'ABANDONED';
/**
 * 一级单调整明细
 */
export type BudgetAdjustDemand = {
  id?: number;
  order_id?: number;
  demand_id?: number;
  is_primary?: boolean;
  original_budget?: number;
  budget_currency?: string;
  adjust_amount?: number;
  adjusted_budget?: number;
  adjusted_daily_avg_fee?: number;
};
/**
 * 二级单调整明细
 */
export type BudgetAdjustPlanItem = {
  id?: number;
  order_id?: number;
  demand_id?: number;
  plan_demand_id?: number;
  is_new_draft?: boolean;
  original_budget?: number;
  budget_currency?: string;
  adjust_amount?: number;
  adjusted_budget?: number;
  adjusted_daily_avg_fee?: number;
  draft_data?: string;
};
/**
 * 梯度考核指标调整明细
 */
export type BudgetAdjustKpiItem = {
  id?: number;
  order_id?: number;
  demand_id?: number;
  cycle_begin_at?: string;
  cycle_end_at?: string;
  gradient?: string;
  kpi_code?: string;
  kpi_name?: string;
  original_expect_value?: string;
  adjusted_expect_value?: string;
};
/**
 * 预算调整详情
 */
export type BudgetAdjustDetailDto = {
  /** 预算调整主单ID */
  order_id?: number;
  /** 发起一级单ID */
  demand_id?: number;
  state?: BudgetAdjustStateEnum;
  /** 流程状态描述 */
  state_desc?: string;
  /** 预算调整说明 */
  description?: string;
  /** 发起人ID */
  initiator_id?: number;
  /** 提交时间 */
  submitted_at?: string;
  /** 生效时间（OA审批通过后调整生效的时间） */
  effective_at?: string;
  /** 提交时刻的页面快照JSON */
  snapshot?: string;
  oa_audit_info?: OaAuditInfo;
  /** 一级单调整明细 */
  demand_items?: BudgetAdjustDemand[];
  /** 二级单调整明细 */
  plan_items?: BudgetAdjustPlanItem[];
  /** 梯度考核指标调整明细 */
  kpi_items?: BudgetAdjustKpiItem[];
  created_by?: UserSimpleVo;
  /** 创建时间 */
  created_at?: string;
  updated_by?: UserSimpleVo;
  /** 最后更新时间 */
  updated_at?: string;
};
export type ResponseBudgetAdjustDetailDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BudgetAdjustDetailDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 查询可关联一级单入参
 */
export type QueryLinkableDemandCmd = {
  /** 当前预算调整单一级单ID */
  demand_id: number;
  /** 关联一级单ID（精确搜索，非必填） */
  search_demand_id?: number;
  /** 关联一级单需求标题（模糊搜索，非必填） */
  search_demand_name?: string;
  /** 当前页码 */
  current?: number;
  /** 每页数量 */
  limit?: number;
};
/**
 * 可关联一级单信息
 */
export type LinkableDemandVo = {
  /** 一级单ID（单号） */
  demand_id?: number;
  /** 需求标题 */
  demand_name?: string;
  /** 预计执行开始日期 */
  expect_begin_at?: string;
  /** 预计执行结束日期 */
  expect_end_at?: string;
  /** 预算剩余金额 */
  remaining_budget?: number;
  /** 预算币种描述 */
  budget_currency?: string;
  /** 所属产品名称 */
  product_name?: string;
  /** 所属产品编码 */
  product_code?: string;
  /** 项目编号（SM编号） */
  project_number?: string;
  /** 需求类型名称 */
  category_name?: string;
};
export type PageVoLinkableDemandVo = {
  total?: number;
  records?: LinkableDemandVo[];
};
export type ResponsePageVoLinkableDemandVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoLinkableDemandVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 查询预算调整记录请求参数
 */
export type BudgetAdjustHistoryCmd = {
  /** 一级单ID */
  demand_id: number;
};
/**
 * 一级单预算调整记录
 */
export type DemandAdjustRecordVo = {
  /** 预算调整单ID */
  order_id?: number;
  /** 一级单ID */
  demand_id?: number;
  /** 是否为发起一级单 */
  is_primary?: boolean;
  /** 生效时间 */
  effective_at?: string;
  /** 提交人ID */
  submitter_id?: number;
  /** 调整前预算金额 */
  original_budget?: number;
  /** 调整后预算金额 */
  adjusted_budget?: number;
  /** 预算币种 */
  budget_currency?: string;
  /** OA单号 */
  oa_id?: string;
  /** 调整单创建时间 */
  created_at?: string;
  /** 预算调整面板快照JSON */
  snapshot?: string;
};
/**
 * 二级单/三级单预算调整记录
 */
export type PlanAdjustRecordVo = {
  /** 预算调整单ID */
  order_id?: number;
  /** 所属一级单ID */
  demand_id?: number;
  /** 二级单/三级单ID */
  plan_demand_id?: number;
  /** 生效时间 */
  effective_time?: string;
  /** 提交人ID */
  submitter_id?: number;
  /** 调整前预算金额 */
  original_budget?: number;
  /** 调整后预算金额 */
  adjusted_budget?: number;
  /** 预算币种 */
  budget_currency?: string;
  /** OA单号 */
  oa_id?: string;
  /** 预算调整面板快照JSON */
  snapshot?: string;
};
/**
 * 预算调整记录查询结果
 */
export type BudgetAdjustHistoryDto = {
  /** 一级单预算调整记录列表（按生效时间降序） */
  demand_records?: DemandAdjustRecordVo[];
  /** 二级单/三级单预算调整记录列表（按生效时间降序） */
  plan_records?: PlanAdjustRecordVo[];
};
export type ResponseBudgetAdjustHistoryDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BudgetAdjustHistoryDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 创建预算调整入参
 */
export type CreateBudgetAdjustCmd = {
  /** 一级单ID */
  demand_id: number;
};
/**
 * 废弃预算调整入参
 */
export type AbandonBudgetAdjustCmd = {
  /** 预算调整主单ID */
  order_id: number;
};
export type BizTaskListForm = {
  /** 单据ID */
  demand_id?: number;
  demand_type?: MpcDemandTypeEnum;
  /** 任务类型（可选，不传则返回所有类型） */
  task_type?: string;
};
/**
 * 业务任务列表项
 */
export type BizTaskVo = {
  /** 任务ID */
  id?: number;
  /** 单据ID */
  demand_id?: number;
  /** 单据类型: 1-需求单 2-小喇叭 3-比选 4-闭环 20-预付单 */
  demand_type?: number;
  /** 任务类型标识 */
  task_type?: string;
  /** 任务名称 */
  task_name?: string;
  /** 任务描述 */
  task_desc?: string;
  /** 执行模式: 0-本地异步 1-外部回调 */
  execute_mode?: number;
  status?: BizTaskStatus;
  /** 状态描述 */
  status_desc?: string;
  /** 发起用户ID */
  user_id?: number;
  /** 进度类型: 0-确定进度（有百分比）1-不确定进度（仅文案） */
  progress_type?: number;
  /** 当前进度 0.0~100.0（仅确定进度有意义） */
  progress?: number;
  /** 进度说明文案 */
  progress_message?: string;
  /** 失败/超时原因 */
  error_message?: string;
  /** 已重试次数 */
  retry_count?: number;
  /** 最大重试次数 */
  max_retry?: number;
  /** 开始执行时间 */
  started_at?: string;
  /** 完成时间 */
  completed_at?: string;
  /** 创建时间 */
  created_at?: string;
};
export type ResponseListBizTaskVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BizTaskVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * Blob数据
 */
export type BlobInfo = {
  /** Blob ID */
  id?: number;
  /** 文件名 */
  filename?: string;
  /** 内容类型 */
  content_type?: string;
  /** 文件大小（字节） */
  byte_size?: number;
  /** 数据内容（JSON） */
  data?: string;
};
/**
 * 附件列表（含 blob.data）
 */
export type AttachmentInfo = {
  /** 附件ID */
  id?: number;
  /** 附件名称 */
  name?: string;
  /** 文件名 */
  filename?: string;
  /** 创建时间 */
  created_at?: string;
  blob?: BlobInfo;
};
/**
 * 业务任务历史推荐结果
 */
export type BizTaskHistoryVo = {
  /** 任务ID */
  id?: number;
  /** 单据ID */
  demand_id?: number;
  /** 单据类型: 1-需求单 2-小喇叭 3-比选 4-闭环 20-预付单 */
  demand_type?: number;
  /** 任务类型标识 */
  task_type?: string;
  /** 任务名称 */
  task_name?: string;
  /** 任务描述 */
  task_desc?: string;
  /** 执行模式: 0-本地异步 1-外部回调 */
  execute_mode?: number;
  status?: BizTaskStatus;
  /** 状态描述 */
  status_desc?: string;
  /** 发起用户ID */
  user_id?: number;
  /** 进度类型: 0-确定进度（有百分比）1-不确定进度（仅文案） */
  progress_type?: number;
  /** 当前进度 0.0~100.0（仅确定进度有意义） */
  progress?: number;
  /** 进度说明文案 */
  progress_message?: string;
  /** 失败/超时原因 */
  error_message?: string;
  /** 已重试次数 */
  retry_count?: number;
  /** 最大重试次数 */
  max_retry?: number;
  /** 开始执行时间 */
  started_at?: string;
  /** 完成时间 */
  completed_at?: string;
  /** 创建时间 */
  created_at?: string;
  /** 附件列表（含 blob.data） */
  attachments?: AttachmentInfo[];
};
export type ResponseListBizTaskHistoryVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BizTaskHistoryVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type AiIdForm = {
  /** 业务ID */
  id?: number;
};
export type BizTaskDemandForm = {
  /** 单据ID */
  demand_id?: number;
};
export type ResponseBizTaskVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BizTaskVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type BizTaskMarketingKolForm = {
  /** 单据ID */
  demand_id?: number;
  /** 达人筛选维度权重 */
  dimensions?: DimensionWeight[];
};
/**
 * 报价字段配置
 */
export type BiddingFieldConfig = {
  code?: string;
  label?: string;
  type?: DemandCooperatePropertyTypeEnum;
  required_mode?: RequiredModeEnum;
  required_condition?: RequiredConditionData;
  options?: ConditionOptionsData;
  outsource_modify?: boolean;
};
/**
 * 编辑报价单配置模板表单
 */
export type EditBiddingQuoteTemplateForm = {
  /** 模板ID */
  id: number;
  /** 版本号（乐观锁） */
  version: number;
  /** 需求类型ID列表（一级需求类型） */
  demand_category_ids: number[];
  /** 标段类目ID */
  segment_category_id?: number;
  /** 方案设计字段配置 */
  design_fields?: BiddingFieldConfig[];
  /** 报价字段配置 */
  quote_fields?: BiddingFieldConfig[];
  /** 报价金额模板类型：1=报价金额, 2=报价金额+比稿税服, 3=报价金额+税率+服务费率+比稿税服 */
  type: number;
  /** 是否生成合同（1=是，0=否） */
  generate_contract?: boolean;
  /** 状态（1=启用，0=禁用） */
  status?: boolean;
};
/**
 * 报价单配置分页查询表单
 */
export type PageBiddingQuoteTemplateForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 需求类型ID列表筛选 */
  demand_category_ids?: number[];
  /** 状态筛选（1=启用，0=禁用） */
  status?: boolean;
};
/**
 * 报价单配置模板列表 VO
 */
export type BiddingQuoteTemplateVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 需求类型ID列表 */
  demand_category_ids?: number[];
  /** 需求类型名称列表 */
  demand_category_names?: string[];
  /** 标段类目ID */
  segment_category_id?: number;
  /** 报价金额模板类型 */
  type?: number;
  /** 是否生成合同 */
  generate_contract?: boolean;
  /** 状态 */
  status?: boolean;
};
/**
 * 分页结果
 */
export type PageResultBiddingQuoteTemplateVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: BiddingQuoteTemplateVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultBiddingQuoteTemplateVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultBiddingQuoteTemplateVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 新增报价单配置模板表单
 */
export type CreateBiddingQuoteTemplateForm = {
  /** 需求类型ID列表（一级需求类型） */
  demand_category_ids: number[];
  /** 标段类目ID */
  segment_category_id?: number;
  /** 方案设计字段配置 */
  design_fields?: BiddingFieldConfig[];
  /** 报价字段配置 */
  quote_fields?: BiddingFieldConfig[];
  /** 报价金额模板类型：1=报价金额, 2=报价金额+比稿税服, 3=报价金额+税率+服务费率+比稿税服 */
  type: number;
  /** 是否生成合同（1=是，0=否） */
  generate_contract?: boolean;
  /** 状态（1=启用，0=禁用） */
  status?: boolean;
};
export type PageNotificationTemplateForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 通知名称（模糊搜索） */
  template_name?: string;
};
/**
 * 比选通知话术模板VO
 */
export type BiddingNotificationTemplateVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 模板名称 */
  template_name?: string;
  /** 模板唯一标识 */
  template_key?: string;
  /** 模板内容（Markdown混合格式） */
  content?: string;
};
/**
 * 分页结果
 */
export type PageResultBiddingNotificationTemplateVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: BiddingNotificationTemplateVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultBiddingNotificationTemplateVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultBiddingNotificationTemplateVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type EditNotificationTemplateForm = {
  /** 模板ID */
  id: number;
  /** 模板内容（Markdown混合格式） */
  content?: string;
};
/**
 * 操作的按钮
 */
export type BiddingActionEnum =
  | 'SUBMIT'
  | 'FORCE_PASS'
  | 'RETURN'
  | 'FORCE_RETURN'
  | 'SAVE'
  | 'CLOSE'
  | 'TIMEOUT';
export type BiddingFlowEvents =
  | 'START_DESIGN'
  | 'DESIGN_PLAN'
  | 'AWAITING_BIDDING'
  | 'BUSINESS_CONFIRM_BID_SP'
  | 'AWAITING_SUBMISSION_BIDS'
  | 'AWAITING_RETRIEVAL_BIDS'
  | 'AWAITING_BIDDING_ADMIN_CONFIRM'
  | 'BID_OPEN_REVIEW'
  | 'UPLOAD_BID_RESULT'
  | 'AWAITING_DECISION_BIDS'
  | 'ACCEPT_BIDING_ADMIN'
  | 'ACCEPT_CHANNEL_LEADER'
  | 'ACCEPT_MARKETING_LEADER'
  | 'DISCARD'
  | 'NONE';
export type BiddingFlowEventV2 =
  | 'START_DESIGN'
  | 'DESIGN_PLAN'
  | 'ACCEPT_PLAN_DESIGN'
  | 'BUSINESS_ACCEPT_PLAN_DESIGN'
  | 'AWAITING_BIDDING'
  | 'ISSUE_BIDS'
  | 'JUDGES_CONFIRM_BIDS'
  | 'AWAITING_SUBMISSION_BIDS'
  | 'BID_OPEN_REVIEW'
  | 'ACCEPT_EVALUATION_RESULT'
  | 'ALLOCATION_BUDGET'
  | 'LEADER_ACCEPT_CONFIRM'
  | 'BUSINESS_LEADER_ACCEPT'
  | 'PLANNER_LEADER_ACCEPT'
  | 'MARKETING_LEADER_ACCEPT'
  | 'MANUAL_SUBMIT_OA'
  | 'OA_PLAN_APPROVE'
  | 'OA_PLAN_APPROVE_ACCEPT'
  | 'BID_RESULT_ACCEPT'
  | 'DISCARD'
  | 'NONE';
export type BiddingMoveDto = {
  id?: number;
  pass?: boolean;
  discard?: boolean;
  bidding_action?: BiddingActionEnum;
  note?: string;
  state: BiddingState;
  /** 流转表单，每个状态会对应不同的表单 */
  form?: {
    [key: string]: any;
  };
  is_end_awaiting_bidding_in_advance?: boolean;
  journal_op_type_enum?: JournalOpTypeEnum;
  flow_pass?: boolean;
  state_next_event?: BiddingFlowEvents;
  state_next_event_v2?: BiddingFlowEventV2;
};
export type BiddingMoveVo = {
  id?: number;
  message?: string;
};
export type ResponseBiddingMoveVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingMoveVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type PageInternalGroupForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 关键字搜索（群号模糊匹配 / 比选单号精确匹配） */
  keyword?: string;
  /** 比选类型（多选：WHOLE/EVENT/OTHER/ANNUAL） */
  bidding_types?: BiddingTypeV2Enum[];
};
/**
 * 比选内部群配置VO
 */
export type BiddingInternalGroupVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 比选单ID（即比选单号） */
  bidding_id?: number;
  /** 内部POPO群号 */
  internal_group_no?: string;
  bidding_type?: BiddingTypeV2Enum;
};
/**
 * 分页结果
 */
export type PageResultBiddingInternalGroupVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: BiddingInternalGroupVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultBiddingInternalGroupVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultBiddingInternalGroupVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type EditInternalGroupForm = {
  /** 记录ID */
  id: number;
  /** 比选单ID */
  bidding_id: number;
  /** 内部POPO群号 */
  internal_group_no: string;
};
export type CreateInternalGroupForm = {
  /** 比选单ID */
  bidding_id: number;
  /** 内部POPO群号 */
  internal_group_no: string;
};
export type PageExternalProductGroupForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 关键字搜索（群号模糊匹配 / 供应商名称模糊匹配） */
  keyword?: string;
  /** 比选类型（多选：WHOLE/EVENT/OTHER/ANNUAL） */
  bidding_types?: BiddingTypeV2Enum[];
  /** 产品ID（多选） */
  product_ids?: number[];
};
/**
 * 比选外部产品群配置VO
 */
export type BiddingExternalProductGroupVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 产品ID列表 */
  product_ids?: number[];
  /** 产品名称列表 */
  product_names?: string[];
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 主供应商名称 */
  supplier_name?: string;
  /** 外部产品POPO群号 */
  group_no?: string;
  bidding_type?: BiddingTypeV2Enum;
  /** 比选类型名称 */
  bidding_type_name?: string;
};
/**
 * 分页结果
 */
export type PageResultBiddingExternalProductGroupVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: BiddingExternalProductGroupVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultBiddingExternalProductGroupVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultBiddingExternalProductGroupVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type EditExternalProductGroupForm = {
  /** 记录ID */
  id: number;
  bidding_type: BiddingTypeV2Enum;
  /** 产品ID列表（多选） */
  product_ids: number[];
  /** 主供应商ID */
  main_supplier_id: number;
  /** 外部产品POPO群号 */
  group_no: string;
};
export type CreateExternalProductGroupForm = {
  bidding_type: BiddingTypeV2Enum;
  /** 产品ID列表（多选） */
  product_ids: number[];
  /** 主供应商ID */
  main_supplier_id: number;
  /** 外部产品POPO群号 */
  group_no: string;
};
export type PageExternalDemandGroupForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 关键字搜索（群号模糊匹配 / 供应商名称模糊匹配） */
  keyword?: string;
  /** 比选类型（多选：WHOLE/EVENT/OTHER/ANNUAL） */
  bidding_types?: BiddingTypeV2Enum[];
};
/**
 * 比选外部发需群配置VO
 */
export type BiddingExternalDemandGroupVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 主供应商名称 */
  supplier_name?: string;
  bidding_type?: BiddingTypeV2Enum;
  /** 外部发需POPO群号 */
  group_no?: string;
};
/**
 * 分页结果
 */
export type PageResultBiddingExternalDemandGroupVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: BiddingExternalDemandGroupVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultBiddingExternalDemandGroupVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultBiddingExternalDemandGroupVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type EditExternalDemandGroupForm = {
  /** 记录ID */
  id: number;
  /** 主供应商ID */
  main_supplier_id: number;
  bidding_type: BiddingTypeV2Enum;
  /** 外部发需POPO群号 */
  group_no: string;
};
export type CreateExternalDemandGroupForm = {
  /** 主供应商ID */
  main_supplier_id: number;
  bidding_type: BiddingTypeV2Enum;
  /** 外部发需POPO群号 */
  group_no: string;
};
export type BiddingEvaluationMemberUpdateForm = {
  id: number;
  remark?: string;
};
export type BiddingEvaluationMemberSearchForm = {
  search_text: string;
};
export type BiddingEvaluationMemberSearchVo = {
  real_name?: string;
  email?: string;
  depid1?: string;
  depid2?: string;
  depid3?: string;
};
export type ResponseListBiddingEvaluationMemberSearchVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingEvaluationMemberSearchVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 评委小组成员类型：MEMBER-成员,ADMIN-管理员
 */
export type BiddingEvaluationMemberTypeEnum = 'MEMBER' | 'ADMIN' | 'EXPERT';
export type BiddingEvaluationMemberCreateForm = {
  member_type: BiddingEvaluationMemberTypeEnum;
  email: string;
  depid1?: string;
  depid2?: string;
  depid3?: string;
  real_name?: string;
  remark?: string;
};
export type BiddingEvaluationMemberDto = {
  member_type?: BiddingEvaluationMemberTypeEnum;
  /** 主键 */
  id?: number;
  /** 姓名 */
  real_name?: string;
  /** 邮箱 */
  email?: string;
  /** 一级部门 */
  depid1?: string;
  /** 二级部门 */
  depid2?: string;
  /** 三级部门 */
  depid3?: string;
  remark?: string;
  status?: boolean;
};
export type ResponseBiddingEvaluationMemberDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingEvaluationMemberDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 招采工作台小红点数量入参
 */
export type BiddingMenuCountForm = object;
/**
 * 招采工作台小红点数量出参
 */
export type BiddingWorkbenchMenuCountDto = {
  key?: string;
  count?: number;
};
export type ResponseListBiddingWorkbenchMenuCountDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingWorkbenchMenuCountDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type BiddingListConditionsForm = {
  search_text?: string;
  l1_category_id?: number[];
  l2_category_id?: number[];
  l3_category_id?: number[];
  l4_category_id?: number[];
  product?: number[];
  principals?: number[];
  priorities?: PriorityEnum[];
  /** 比选类型 */
  bidding_types?: BiddingTypeEnum[];
  /** 比选类型V2 */
  bidding_types_v2?: BiddingTypeV2Enum[];
  /** 比选方式 */
  bidding_methods?: BiddingMethodEnum[];
  /** 比选状态 */
  bidding_states?: BiddingState[];
  /** OA状态 */
  oa_states?: OaState[];
};
/**
 * 招采流程阶段枚举
 */
export type BiddingProcessStageEnum =
  | 'TO_DO_PROCESS'
  | 'COMPLETED_PROCESS'
  | 'MY_INITIATIVE';
/**
 * 内包招采工作台表单
 */
export type BiddingInnerWorkbenchForm = {
  current?: number;
  size?: number;
  order_field?: string;
  order_direction?: string;
  conditions?: BiddingListConditionsForm;
  bidding_process_stage: BiddingProcessStageEnum;
  platform_type?: PlatformTypeEnum;
};
/**
 * 分页数据
 */
export type BiddingWorkbenches = {
  id?: number;
  product_identifier?: string;
  name?: string;
  priority?: string;
  budget_text?: string;
  bidding_type?: string;
  businesses_names?: string[];
  bidding_state_text?: string;
  bidding_state_value?: string;
  oa_state?: OaState;
  /** OA单号 */
  oa_id?: string;
  oa_url?: string;
  operation?: string;
  can_move?: boolean;
  can_edit?: boolean;
  can_modify_select?: boolean;
  budget?: string;
  currency_enum?: CurrencyEnum;
  /** 所属产品 */
  product_id?: number;
  /** 关联项目 */
  number?: string;
  /** 是否可以批量提交审批 */
  can_batch_submit_approve?: boolean;
};
/**
 * 分页结果
 */
export type PageResultBiddingWorkbenches = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: BiddingWorkbenches[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultBiddingWorkbenches = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultBiddingWorkbenches;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商邀请记录查询表单
 */
export type BiddingSupplierInviteRecordForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 比选单ID */
  bidding_id: number;
};
/**
 * 供应商竞选状态
 */
export type BiddingStateEnum =
  | 'BIDDING_PASS'
  | 'BIDDING_CONFIRM_PASS'
  | 'BIDDING_WIN'
  | 'BIDDING_OUT';
/**
 * 比选供应商报价
 */
export type BiddingSupplierQuoteDto = {
  id?: number;
  /** bidding_supplier_id */
  bidding_supplier_id?: number;
  /** cooperate_account_id */
  cooperate_account_id?: number;
  /** quote_price */
  quote_price?: number;
  currency?: CurrencyEnum;
  tender_plan_attachment_dtos?: AttachmentDto[];
  quote_price_attachment_dtos?: AttachmentDto[];
  created_at?: string;
  updated_at?: string;
};
/**
 * 评分明细
 */
export type BiddingScoreGroupMemberInfoDto = {
  /** 评分小组成员id */
  user_id?: number;
  /** 评分小组成员名称 */
  user_name?: string;
  /** 是否已评分 */
  score_done?: boolean;
  /** 评分明细 */
  score?: Item[];
  /** 评分时间 */
  score_time?: string;
  /** 优势 */
  advantage?: string;
  /** 不足 */
  shortage?: string;
  /** 0 不复评 1 复评 */
  re_evaluation?: number;
  /** 复评原因 */
  re_evaluation_reason?: string;
};
/**
 * 评分小组评分
 */
export type BiddingScoreGroupInfoDto = {
  /** 评分小组权重 */
  weight?: number;
  /** 评分小组名称 */
  name?: string;
  /** 评分小组id */
  bidding_score_rule_group_id?: number;
  /** 评分明细 */
  detail?: BiddingScoreGroupMemberInfoDto[];
  /** 小组评分 */
  score?: number;
};
/**
 * 评分详情
 */
export type ScoreInfo = {
  type?: string;
  score?: number;
  detail?: Item[];
  /** 优势 */
  advantage?: string;
  /** 不足 */
  shortage?: string;
  /** 评分小组评分 */
  score_group_infos?: BiddingScoreGroupInfoDto[];
  /** 评分负责人成员id */
  user_id?: number;
  /** 评分负责人成员名称 */
  user_name?: number;
  /** 评分负责人成员邮箱 */
  email?: string;
  /** 汇总时间 */
  summary_time?: string;
  /** 权重 */
  weight?: number;
};
/**
 * 计算类型的配置
 */
export type Computed = {
  type?: string;
  expression?: string;
  dependencies?: string[];
};
/**
 * 报价单模板列
 */
export type BiddingQuoteTemplateColumnVo = {
  /** 字段分组：0=标段字段，1=方案设计字段，2=报价字段，3=报价固定字段 */
  category?: number;
  /** 字段编码 */
  code?: string;
  /** 字段名称 */
  label?: string;
  type?: DemandCooperatePropertyTypeEnum;
  required_mode?: RequiredModeEnum;
  required_condition?: RequiredConditionData;
  options?: ConditionOptionsData;
  /** 供应商是否可修改预填内容 */
  outsource_modify?: boolean;
  /** 排序序号 */
  index_id?: number;
  computed?: Computed;
};
/**
 * 报价 Tab（一个费用类型 + 一份模板列定义）
 */
export type SupplierQuoteTab = {
  /** 报价单配置实例ID（bidding_quote_config.id） */
  config_id?: number;
  /** 费用类型ID列表（从 cost_classifications 中按 cost_classification_index 反查，年框场景为多个） */
  demand_category_ids?: number[];
  /** 费用类型名称列表（从 cost_classifications 中按 cost_classification_index 反查，年框场景为多个） */
  demand_category_names?: string[];
  /** 关联模板ID */
  template_id?: number;
  /** 模板快照版本 */
  template_version?: number;
  /** 报价金额模板类型：1/2/3 */
  type?: number;
  /** 是否启用 */
  enabled?: boolean;
  /** 列定义（方案设计字段 + 报价字段拍平） */
  columns?: BiddingQuoteTemplateColumnVo[];
  /** 明细行（与 bidding_quote_config_detail 一一对应）。每行为平铺 Map：列值（key=column.code） + 保留元字段 configDetailId/outsourceModify/id/indexId/segment_code；前端按 columns[].category 区分渲染 */
  rows?: {
    [key: string]: any;
  }[];
};
/**
 * 供应商议价记录
 */
export type BiddingSupplierBargainDto = {
  /** bidding_supplier_id */
  bidding_supplier_id?: number;
  /** latest */
  latest?: boolean;
  /** quote_price */
  quote_price?: number;
  currency?: CurrencyEnum;
  /** quotePriceAttachments */
  quote_price_attachments?: AttachmentDto[];
  /** 议价时间 */
  created_at?: string;
  /** 报价明细 tabs（columns + rows） */
  tabs?: SupplierQuoteTab[];
};
/**
 * 议价状态
 */
export type BargainStateEnum = 'PENDING' | 'PASS';
/**
 * 邀约状态
 */
export type BiddingSupplierWillingInviteState =
  | 'WILLING'
  | 'UNWILLING'
  | 'NOT_RESPONDING';
/**
 * 新增供应商列表
 */
export type BiddingSupplierDto = {
  id?: number;
  rank?: number;
  supplier_index?: string;
  /** bidding_id */
  bidding_id?: number;
  /** 供应商id */
  supplier_id?: number;
  created_at?: string;
  updated_at?: string;
  /** 是否有意愿邀请 */
  is_willing_invite?: boolean;
  /** 是否确认参与比选 */
  is_participating_in_selection?: boolean;
  /** 是否提交标书 */
  is_tender_submit?: boolean;
  /** 收款账户 */
  bank_account?: string;
  /** 收款账户名 */
  bank_account_name?: string;
  /** 发票归属地区 */
  invoice_belong_area?: string;
  /** 是否海外发票（0=否，1=是） */
  is_oversea_invoice?: number;
  /** 发票类型 */
  invoice_type?: string;
  /** 供应商评分 */
  score?: number;
  /** 比选意见 */
  bidding_opinions?: string;
  bidding_state?: BiddingStateEnum;
  /** 评标意见附件 */
  bidding_opinions_attachment_dtos?: AttachmentDto[];
  /** 比选中的供应商名称，就是供应商1 这种 */
  bidding_supplier_name?: string;
  /** 供应商全称 */
  supplier_full_name?: string;
  /** 供应商名称，主供应商名称 */
  supplier_name?: string;
  /** 供应商渠道类型 */
  supplier_channel_type_name?: string;
  city?: string;
  bidding_supplier_quotes?: BiddingSupplierQuoteDto[];
  /** 保密协议附件 */
  bidding_nda_attachments?: AttachmentDto[];
  /** 评分详情 */
  score_infos?: ScoreInfo[];
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注数量 */
  risk_remark_count?: number;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 是否是低效供应商 */
  is_low_efficiency?: boolean;
  /** 风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 风险拦截标记 */
  risky?: boolean;
  /** 供应商系统标签 */
  sys_tags?: SupplierSysTagVo[];
  /** 供应商议价记录 */
  bargain_dtos?: BiddingSupplierBargainDto[];
  bargain_state?: BargainStateEnum;
  /** 最低价 */
  lowest_price?: boolean;
  /** 平台核价配置列表 */
  platform_prices?: PlatformPriceGroupVo[];
  /** 外部产品群号 */
  external_product_group_no?: string;
  /** 外部发需群号 */
  external_demand_group_no?: string;
  /** 执行群号 */
  execution_group_no?: string;
  /** 是否智能推荐的供应商 */
  smart_recommend?: boolean;
  contract_info?: DemandContractInfoDto;
  /** 比选通过 */
  bidding_pass?: boolean;
  /** 比选方案通过 */
  bidding_confirm_pass?: boolean;
  /** 淘汰 */
  bidding_out?: boolean;
  /** 中选 */
  bidding_win?: boolean;
  willing_invite_state?: BiddingSupplierWillingInviteState;
};
/**
 * 比选单供应商邀请记录VO
 */
export type BiddingSupplierInviteRecordVo = {
  /** 主键ID */
  id?: number;
  /** 比选ID */
  bidding_id?: number;
  /** 操作人名称 */
  operator_name?: string;
  /** 操作人邮箱 */
  operator_email?: string;
  /** 记录时间 */
  created_at?: string;
  /** 状态 */
  human_state_name?: string;
  /** 申请记录附件 */
  attachments?: AttachmentDto[];
  /** 新增供应商列表 */
  supplier_detail_dtolist?: BiddingSupplierDto[];
};
export type PageVoBiddingSupplierInviteRecordVo = {
  total?: number;
  records?: BiddingSupplierInviteRecordVo[];
};
export type ResponsePageVoBiddingSupplierInviteRecordVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoBiddingSupplierInviteRecordVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 开标类型
 */
export type OpenBiddingTypeEnum = 'BID' | 'TECHNICAL_BID' | 'COMMERCIAL_BID';
/**
 * 业务标记
 */
export type BiddingBusinessStateEnum =
  | 'ABANDON_BIDDING'
  | 'LISTEN_BIDDING'
  | 'DEFER_BIDDING'
  | 'FINISH_BIDDING';
/**
 * 比选流程版本
 */
export type BiddingFlowFlagEnum = 'OLD_ONLINE_PROCESS' | 'BIDDING_V2';
/**
 * 保密协议模板类型（新逻辑 confidentialityVersion=1 时使用）：COMMON=通用模板，OTHER=其他
 */
export type NdaTemplateTypeEnum = 'COMMON' | 'OTHER';
/**
 * 费控状态
 */
export type BiddingFeeControlStateEnum = 'NOT_SUBMIT' | 'SUBMITTED' | 'DELETED';
/**
 * 比选评审状态
 */
export type BiddingReviewStateEnum =
  | 'TECH_SCORE'
  | 'BARGAIN'
  | 'SUPPLIER_QUOTE'
  | 'BUSINESS_SCORE';
/**
 * 费用分类明细项
 */
export type CostClassificationItem = {
  /** 费用类型ID列表（一级需求类型ID） */
  demand_category_ids: number[];
  /** 费用类型名称列表（查询时填充，不持久化） */
  demand_category_names?: string[];
  /** 预算金额 */
  budget_amount: number;
  /** 占比（动态计算 = budgetAmount / 总预算 × 100，不持久化） */
  ratio?: number;
};
/**
 * 报价单模板表格
 */
export type BiddingQuoteTemplateTableVo = {
  /** 模板ID */
  template_id?: number;
  /** 模板版本号 */
  template_version?: number;
  /** 需求类型ID列表 */
  demand_category_ids?: number[];
  /** 需求类型名称列表 */
  demand_category_names?: string[];
  /** 标段类目ID */
  segment_category_id?: number;
  /** 报价金额模板类型：1=报价金额，2=报价金额+比稿税服，3=报价金额+税率+服务费率+比稿税服 */
  type?: number;
  /** 是否生成合同 */
  generate_contract?: boolean;
  /** 列信息（designFields + quoteFields 拍平） */
  columns?: BiddingQuoteTemplateColumnVo[];
};
/**
 * 比选报价单配置（显示 + 编辑统一结构）
 */
export type BiddingQuoteConfigVo = {
  /** 报价单配置实例ID（bidding_quote_config.id）；详情回显主键，提交时全量覆盖重建、后端忽略 */
  config_id?: number;
  /** 对应费用分类明细的行索引（costClassifications 列表下标） */
  cost_classification_index?: number;
  /** 是否启用（true=启用，false=禁用；禁用也落库） */
  enabled?: boolean;
  /** 选择的报价单模板ID */
  template_id?: number;
  /** 模板版本号（快照） */
  template_version?: number;
  /** 费用类型ID列表（详情填充，按 costClassificationIndex 反查；提交忽略） */
  demand_category_ids?: number[];
  /** 费用类型名称列表（详情填充；提交忽略） */
  demand_category_names?: string[];
  template?: BiddingQuoteTemplateTableVo;
  /** 明细行（打平）：每行一个 map，key=列code（含 segment_code），value=值或值数组；list 顺序即排序 */
  rows?: {
    [key: string]: any;
  }[];
};
/**
 * 状态节点
 */
export type BottomTab = {
  tab_name?: string;
  value?: string;
};
/**
 * 招采助理
 */
export type MemberDto = {
  id?: number;
  bidding_id?: number;
  user_id?: number;
  user_name?: string;
};
/**
 * 评分规则明细
 */
export type BiddingScoreRuleDetailDto = {
  /** 项目 */
  item: string;
  /** 权重 */
  weight: number;
};
/**
 * 评分小组成员
 */
export type BiddingScoreRuleGroupMemberDto = {
  /** 成员id */
  bidding_member_id?: number;
  /** 用户id */
  user_id?: number;
  /** 用户名称 */
  user_name?: string;
  /** 用户邮箱 */
  email?: string;
  /** 是否已评分 */
  score_done?: boolean;
  /** 评分时间 */
  score_time?: string;
  bidding_score_rule_group_id?: number;
};
/**
 * 评分小组列表
 */
export type BiddingScoreRuleGroupDto = {
  /** 评分小组权重 */
  weight?: number;
  /** 评分小组名称 */
  name?: string;
  /** 评分小组id */
  id?: number;
  /** 评分规则明细 */
  detail?: BiddingScoreRuleDetailDto[];
  /** 评分小组成员 */
  members?: BiddingScoreRuleGroupMemberDto[];
  /** 关联的一级需求类型ID列表（plan/purchase 且 structured=1 时有值） */
  l1_category_ids?: number[];
};
/**
 * 评分规则
 */
export type BiddingScoreRuleResDto = {
  /** 评分类型 1 需求方, 2 职能策划, 3 采购方 */
  type?: string;
  /** 权重 */
  weight?: number;
  /** 评分规则明细 */
  detail?: BiddingScoreRuleDetailDto[];
  /** 评分小组列表 */
  group?: BiddingScoreRuleGroupDto[];
  bidding_score_rule_group_member_dto?: BiddingScoreRuleGroupMemberDto;
};
/**
 * 比选方案状态, 0 待确认, 1 已确认
 */
export type BiddingPlanState = 'TO_BE_CONFIRM' | 'CONFIRMED' | 'REJECTED';
/**
 * 确认允许比选状态, 0 待确认, 1 允许, 2 拒绝
 */
export type BiddingConfirmState = 'TO_BE_CONFIRM' | 'CONFIRMED' | 'REJECT';
export type JudgeReviewMemberDto = {
  id?: number;
  leader?: boolean;
  user_id?: number;
  user_name?: string;
  email?: string;
};
/**
 * 评分小组
 */
export type DetailBiddingJudgeTeamDto = {
  /** 评分类型 1 需求方, 2 职能策划, 3 采购方 */
  type?: string;
  bidding_plan_state?: BiddingPlanState;
  bidding_confirm_state?: BiddingConfirmState;
  /** 方案确认时间 */
  plan_confirm_at?: string;
  /** 比选短名单确认时间 */
  short_confirm_at?: string;
  /** 拒绝比选的原因 */
  reason?: string;
  /** 拒绝比选时间 */
  refuse_at?: string;
  /** 是否复评 */
  re_evaluation?: boolean;
  /** 是否听标 */
  listen_bidding?: boolean;
  /** 轮次属性 */
  turns_number?: number;
  /** 复评原因 */
  re_evaluation_reason?: string;
  /** 听标原因 */
  listen_bidding_reason?: string;
  members?: JudgeReviewMemberDto[];
};
/**
 * 退回原因
 */
export type RejectInfo = {
  reject_reason?: string;
  last_state?: string;
  last_state_name?: string;
};
/**
 * 预算分配
 */
export type BiddingSupplierAmountDto = {
  /** 供应商中选金额分配记录id */
  bidding_supplier_amount_id?: number;
  /** bidding_id */
  bidding_id?: number;
  /** 供应商id */
  supplier_id?: number;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 中选金额 */
  amount?: number;
  /** 备注 */
  remark?: string;
  /** 预算分配及效果预期 */
  budget_allocation_expect?: string;
  /** 预算分配 */
  budget_allocation?: string;
  /** 效果预期 */
  expected_effect?: string;
  /** 执行金额 */
  execution_amount?: number;
  currency?: CurrencyEnum;
  /** 需求类型名称 */
  l1_category_name?: string;
  /** 供应商名称+服务类型 */
  supplier_full_name?: string;
  /** MPC需求单ID */
  mpc_demand_id?: number;
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 风险备注数量 */
  risk_remark_count?: number;
  /** 风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 风险拦截标记 */
  risky?: boolean;
};
/**
 * 风险提示&备注说明
 */
export type BiddingDetailRemark = {
  /** 节点名 */
  node_name?: string;
  user?: UserSimpleVo;
  /** 备注 */
  note?: string;
};
/**
 * 风险提示&备注说明集
 */
export type BiddingDetailRemarksTree = {
  remarks?: BiddingDetailRemark[];
};
/**
 * 开标阶段查看权限
 */
export type BiddingOpenReviewViewAuth = {
  can_see_demand_score?: boolean;
  can_see_plan_score?: boolean;
  can_see_purchase_score?: boolean;
  can_see_final_score?: boolean;
  can_see_quote_price?: boolean;
  can_see_annual_tech_score_on_review?: boolean;
};
/**
 * 需求方负责人确认状态(中选结果复核)
 */
export type RoleConfirmStatusDto = {
  /** 角色负责人列表 */
  members?: UserSimpleVo[];
  /** 确认状态(0-未确认, 1-已确认) */
  confirm_status?: number;
  /** 确认时间 */
  confirm_time?: string;
};
/**
 * 比选单备注记录DTO
 */
export type BiddingNoteRecordDto = {
  /** 比选ID */
  bidding_id?: number;
  /** 操作人名称 */
  operator_name?: string;
  /** 操作人邮箱 */
  operator_email?: string;
  /** 操作人角色 */
  operator_role_name?: string;
  /** 记录时间 */
  created_at?: string;
  /** 状态 */
  human_state_name?: string;
  /** 备注 */
  note?: string;
};
export type BiddingDetailsResDto = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  /** 名称 */
  name?: string;
  /** 产品ID */
  product_id?: number;
  /** 描述 */
  desc?: string;
  /** number */
  number?: string;
  /** 预计开始时间 */
  expect_begin_at?: string;
  /** 预计结束时间 */
  expect_end_at?: string;
  priority?: PriorityEnum;
  /** 预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  state?: BiddingState;
  /** 作者ID */
  author_id?: number;
  group_type?: DemandGroupType;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 四级分类ID */
  l4_category_id?: number;
  /** 是否完成审批 */
  is_approval?: boolean;
  bidding_type?: BiddingTypeEnum;
  bidding_method?: BiddingMethodEnum;
  /** 比选供应商数量 */
  bidding_supplier_num?: number;
  /** 竞选报名截止时间 */
  bid_registration_deadline?: string;
  /** 竞选提案截止时间 */
  bid_proposal_deadline?: string;
  /** 公告内容 */
  announcement_content?: string;
  /** 是否隐藏报价信息 */
  is_hide_quote_price?: boolean;
  /** 比选答疑时间 */
  bidding_qa_time?: string;
  open_bidding_type?: OpenBiddingTypeEnum;
  /** 比选结果说明 */
  accept_bidding_opinions?: string;
  business_state?: BiddingBusinessStateEnum;
  /** 开标时间 */
  open_bidding_time?: string;
  /** 评委小组定标意见 */
  bidding_accept_member_options?: string;
  /** 保存草稿的临时备注 */
  draft_remark?: string;
  /** 比选公告 */
  competition_announcement?: string;
  /** 当前订单支持的供应商库版本 */
  enable_supplier_version?: string;
  /** 是否脱敏外发 */
  desensitization_out?: boolean;
  /** 背景 */
  background?: string;
  /** 预期 */
  expectation?: string;
  /** 复评入群供应商数量 */
  re_evaluation_num?: number;
  flow_flag?: BiddingFlowFlagEnum;
  /** 评分截止时间 */
  score_end_at?: string;
  /** 是否延迟过评分截止时间 */
  delay_score_count?: number;
  bidding_type_v2?: BiddingTypeV2Enum;
  /** 供应商低于3家时填写的理由 */
  issue_bidding_reason?: string;
  /** 是否需要保密协议 */
  need_nda?: boolean;
  /** 保密协议逻辑版本，1=新逻辑（新建/退回草稿），0=旧逻辑（存量历史单） */
  confidentiality_version?: number;
  nda_template_type?: NdaTemplateTypeEnum;
  fee_control_state?: BiddingFeeControlStateEnum;
  /** 评分轮次属性记录 */
  score_turns_num?: number;
  /** 听标时间 */
  listen_bidding_time?: string;
  /** 新增或更新比选答疑附件的时间 */
  save_update_qa_attach_time?: string;
  /** 供应商能力要求 */
  supplier_capability_requirements?: string;
  /** 供应商推荐人姓名 */
  supplier_referrer?: string;
  /** 供应商推荐理由 */
  supplier_referral_reason?: string;
  /** 供应商回避理由 */
  supplier_avoidance_reason?: string;
  /** 第一轮是否听标 */
  first_round_listen_bidding?: boolean;
  review_state?: BiddingReviewStateEnum;
  /** 比选评审流程版本 */
  review_version?: string;
  /** 技术评分是否被跳过 */
  tech_score_skipped?: boolean;
  /** 商务评分是否被跳过（含商务议价） */
  business_score_skipped?: boolean;
  /** 商务议价截止时间 */
  bargain_end_time?: string;
  oa_status?: OaState;
  /** 结构化，0=未使用，1=报价结构化 */
  structured?: number;
  /** 费用采购主体ID，引用 cost_companies.id（M07 新增） */
  cost_company_id?: number;
  /** 作者名称 */
  author_name?: string;
  /** 费用分类明细（M03 API 透出字段，含 names 和 ratio 动态计算值） */
  cost_classifications?: CostClassificationItem[];
  /** 报价单配置列表（M03 方案设计阶段配置，含模板列定义 + 行数据） */
  quote_configs?: BiddingQuoteConfigVo[];
  /** 状态名称 */
  human_state_name?: string;
  /** 产品编号 */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 优先级 */
  translated_priority?: string;
  /** 组类型 */
  translated_group_type?: string;
  /** 状态节点 */
  bottom_tabs?: BottomTab[];
  /** 层级名称（用-连接） */
  category_names?: string;
  /** 一级分类名称 */
  l1_category_name?: string;
  /** 二级分类名称 */
  l2_category_name?: string;
  /** 供应商详情 */
  bidding_suppliers?: BiddingSupplierDto[];
  /** 商务人员 */
  bidding_businesses?: MemberDto[];
  /** 需求人员 */
  bidding_principals?: MemberDto[];
  /** 评标成员 */
  bidding_members?: MemberDto[];
  /** 评标负责人 */
  bidding_admins?: MemberDto[];
  /** 评委专家 */
  bidding_experts?: MemberDto[];
  /** 比选需求附件/采购需求说明文件 */
  bidding_attachments?: AttachmentDto[];
  /** 老板审批通过邮件.attachment */
  approval_email_attachments?: AttachmentDto[];
  /** 比选方案.attachment */
  bidding_plan_attachments?: AttachmentDto[];
  /** 比选材料（对外）attachment */
  bidding_out_attachments?: AttachmentDto[];
  /** 竞选答疑记录.attachment */
  bidding_qa_attachments?: AttachmentDto[];
  /** 评委小组定标意见附件 */
  bidding_accept_member_attachments?: AttachmentDto[];
  /** 延期审批邮件attachment */
  bidding_defer_approval_email_attachments?: AttachmentDto[];
  permit_actions?: PermitActions;
  /** 轮次 */
  turns_number?: number;
  /** 是否为绑定MPC需求的招采流程：1 是, 0 否 */
  bind_demand?: number;
  /** 产品名称 */
  product_name?: string;
  /** 招采助理 */
  bidding_assistants?: MemberDto[];
  /** 脱敏文件 */
  bidding_desensitization_attachments?: AttachmentDto[];
  /** 背景说明补充文件 */
  bidding_background_attachments?: AttachmentDto[];
  /** 保密协议模板 */
  bidding_nda_template_attachments?: AttachmentDto[];
  /** 确认中选需求中上传的审批记录附件 */
  bidding_budget_approval_record_attachments?: AttachmentDto[];
  /** 评分规则 */
  bidding_score_rules?: BiddingScoreRuleResDto[];
  /** 评审委员小组 */
  bidding_judge_teams?: DetailBiddingJudgeTeamDto[];
  reject_info?: RejectInfo;
  /** 预算分配 */
  budget_allocations?: BiddingSupplierAmountDto[];
  /** 年框比选新老逻辑标识：null=非年框，true=新逻辑（selectedSuppliers承载中选结果），false=老逻辑（budgetAllocations承载中选结果） */
  annual_bidding_new_mode?: boolean;
  /** 中选供应商 */
  selected_suppliers?: SelectedSupplierDto[];
  budget_allocation_remarks?: BiddingDetailRemarksTree;
  /** 是否复评 */
  re_evaluation?: boolean;
  /** 是否听标 */
  listen_bidding?: boolean;
  /** OA审批列表信息 */
  oa_audit_desc_list?: OaProcessDescVo[];
  /** 是否完成需求评分 */
  has_score_demand?: boolean;
  /** 是否完成策划评分 */
  has_score_plan?: boolean;
  /** 是否完成采购方评分 */
  has_score_purchase?: boolean;
  /** 是否为虚拟产品，如SM99999 */
  virtual_project?: boolean;
  bidding_open_review_view_auth?: BiddingOpenReviewViewAuth;
  /** 内部群号 */
  internal_group_no?: string;
  business_leader_plan_confirm_status?: RoleConfirmStatusDto;
  business_leader_result_confirm_status?: RoleConfirmStatusDto;
  planner_leader_result_confirm_status?: RoleConfirmStatusDto;
  demander_leader_result_confirm_status?: RoleConfirmStatusDto;
  /** 是否存在平台核价信息 */
  has_platform_price?: boolean;
  /** 评委小组提交备注记录 */
  bidding_note_record_dtosabout_judge_team?: BiddingNoteRecordDto[];
  /** 中选预算分配提交备注记录 */
  bidding_note_record_dtosabout_budget_allocation?: BiddingNoteRecordDto[];
  /** 推荐供应商列表 */
  referrer_supplier_dto_list?: BiddingSupplierDto[];
  /** 回避供应商列表 */
  avoidance_supplier_dto_list?: BiddingSupplierDto[];
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type ResponseBiddingDetailsResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingDetailsResDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type BiddingSupplierHisListDto = {
  id?: number;
  rank?: number;
  supplier_index?: string;
  /** bidding_id */
  bidding_id?: number;
  /** 供应商id */
  supplier_id?: number;
  created_at?: string;
  updated_at?: string;
  /** 是否有意愿邀请 */
  is_willing_invite?: boolean;
  /** 是否确认参与比选 */
  is_participating_in_selection?: boolean;
  /** 是否提交标书 */
  is_tender_submit?: boolean;
  /** 收款账户 */
  bank_account?: string;
  /** 收款账户名 */
  bank_account_name?: string;
  /** 发票归属地区 */
  invoice_belong_area?: string;
  /** 是否海外发票（0=否，1=是） */
  is_oversea_invoice?: number;
  /** 发票类型 */
  invoice_type?: string;
  /** 供应商评分 */
  score?: number;
  /** 比选意见 */
  bidding_opinions?: string;
  bidding_state?: BiddingStateEnum;
  /** 评标意见附件 */
  bidding_opinions_attachment_dtos?: AttachmentDto[];
  /** 比选中的供应商名称，就是供应商1 这种 */
  bidding_supplier_name?: string;
  /** 供应商全称 */
  supplier_full_name?: string;
  /** 供应商名称，主供应商名称 */
  supplier_name?: string;
  /** 供应商渠道类型 */
  supplier_channel_type_name?: string;
  city?: string;
  bidding_supplier_quotes?: BiddingSupplierQuoteDto[];
  /** 保密协议附件 */
  bidding_nda_attachments?: AttachmentDto[];
  /** 评分详情 */
  score_infos?: ScoreInfo[];
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注数量 */
  risk_remark_count?: number;
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 是否是低效供应商 */
  is_low_efficiency?: boolean;
  /** 风险标签 */
  risk_tags?: SupplierRiskTagVo[];
  /** 风险拦截标记 */
  risky?: boolean;
  /** 供应商系统标签 */
  sys_tags?: SupplierSysTagVo[];
  /** 供应商议价记录 */
  bargain_dtos?: BiddingSupplierBargainDto[];
  bargain_state?: BargainStateEnum;
  /** 最低价 */
  lowest_price?: boolean;
  /** 平台核价配置列表 */
  platform_prices?: PlatformPriceGroupVo[];
  /** 外部产品群号 */
  external_product_group_no?: string;
  /** 外部发需群号 */
  external_demand_group_no?: string;
  /** 执行群号 */
  execution_group_no?: string;
  /** 是否智能推荐的供应商 */
  smart_recommend?: boolean;
  contract_info?: DemandContractInfoDto;
  turns_number?: number;
  /** 比选通过 */
  bidding_pass?: boolean;
  /** 比选方案通过 */
  bidding_confirm_pass?: boolean;
  /** 淘汰 */
  bidding_out?: boolean;
  /** 中选 */
  bidding_win?: boolean;
  willing_invite_state?: BiddingSupplierWillingInviteState;
};
export type BiddingSupplierHisDto = {
  his_dtos?: BiddingSupplierHisListDto[];
};
export type ResponseBiddingSupplierHisDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingSupplierHisDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type BiddingReqDto = {
  id?: number;
};
export type BiddingStateJournalResDto = {
  journal_id?: number;
  /** 节点名称 */
  state?: string;
  /** 创建时间 */
  created_at?: string;
  /** 意见 */
  note?: string;
  /** 操作 */
  action?: string;
  operator?: User;
  /** 接收人 */
  next_operator?: User[];
};
export type ResponseListBiddingStateJournalResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingStateJournalResDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 保存或更新比选答疑时间
 */
export type SaveOrUpdateBiddingQaTimeForm = {
  /** 比选答疑时间 */
  bidding_qa_time: string;
  /** 比选Id */
  bidding_id: number;
};
/**
 * 保存或更新比选答疑记录附件
 */
export type SaveOrUpdateBiddingQaAttachForm = {
  /** 答疑记录 */
  bidding_qa_attach_blob_ids: number[];
  /** 比选Id */
  bidding_id: number;
};
export type BiddingReviewHisPostFrom = {
  bidding_id: number;
  type: BiddingParticipantType;
  turn_num: number;
};
export type BiddingJudgeReviewHisDto = {
  participant_type?: BiddingParticipantType;
  /** 轮次属性 */
  turns_number?: number;
  /** 优势 */
  advantage?: string;
  /** 不足 */
  shortage?: string;
  /** 是否复评 */
  re_evaluation?: boolean;
  /** 是否听标 */
  listen_bidding?: boolean;
  /** 复评原因 */
  re_evaluation_reason?: string;
  /** 听标原因 */
  listen_bidding_reason?: string;
  /** 供应商信息 */
  bidding_suppliers?: BiddingSupplierDto[];
};
export type ResponseBiddingJudgeReviewHisDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingJudgeReviewHisDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 单个报价单导入结果
 */
export type ImportQuoteConfigItem = {
  /** 报价单名称 */
  name?: string;
  /** 报价单配置实例ID */
  config_id?: number;
  /** 模板ID */
  template_id?: number;
  /** 模板版本号 */
  template_version?: number;
  /** 标段类目ID（可能为 null） */
  segment_category_id?: number;
  /** 列定义（含完整 category=0 列） */
  columns?: BiddingQuoteTemplateColumnVo[];
  /** 明细行：每行含完整的 category=0 字段值（由后端根据 segment_code 补全） */
  rows?: {
    [key: string]: any;
  }[];
};
/**
 * 报价单配置导入响应
 */
export type ImportQuoteConfigResponse = {
  /** 各报价单解析结果 */
  configs?: ImportQuoteConfigItem[];
};
export type ResponseImportQuoteConfigResponse = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ImportQuoteConfigResponse;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListBiddingSupplierHisListDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingSupplierHisListDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 导出报价明细项
 */
export type ExportQuoteDetailItem = {
  /** Tab 名称（作 Excel Sheet 名） */
  name: string;
  /** 报价配置 ID */
  config_id: number;
  /** 列定义 */
  columns: BiddingQuoteTemplateColumnVo[];
  /** 数据行（每行一个 Map，key=col.code） */
  rows?: {
    [key: string]: any;
  }[];
};
/**
 * 导出报价明细请求
 */
export type ExportQuoteDetailForm = {
  /** 报价明细列表（每个 tab 一项） */
  quote_details: ExportQuoteDetailItem[];
};
/**
 * 单个报价单配置（导出）
 */
export type ExportQuoteConfigItem = {
  /** 报价单配置实例ID（bidding_quote_config.id） */
  config_id?: number;
  /** 报价单模板ID */
  template_id: number;
  /** 模板版本号（快照） */
  template_version: number;
  /** 报价单名称（Sheet 名称） */
  name: string;
  /** 是否启用 */
  enabled?: boolean;
  /** 明细行（打平）：每行一个 map，key=列code，value=值 */
  rows?: {
    [key: string]: any;
  }[];
};
/**
 * 报价单配置导出请求
 */
export type ExportQuoteConfigForm = {
  /** 报价单配置列表，一个配置对应一个 Sheet */
  configs: ExportQuoteConfigItem[];
};
/**
 * 团队成员
 */
export type BiddingJudgeTeamMemberDto = {
  /** bidding_member_id */
  id?: number;
  /** 用户id */
  user_id: number;
  /** 是否是汇总人 */
  leader?: boolean;
};
/**
 * 评委组
 */
export type BiddingJudgeTeamDto = {
  type: BiddingParticipantType;
  /** 团队成员 */
  members: BiddingJudgeTeamMemberDto[];
};
/**
 * 评分小组成员
 */
export type GroupMemberDto = {
  /** 评分小组id */
  bidding_score_rule_group_id?: number;
  /** 评分小组成员id */
  user_ids?: number[];
};
export type EditBiddingMemberForm = {
  /** 招采id */
  bidding_id: number;
  /** 评委组 */
  bidding_judge_teams?: BiddingJudgeTeamDto[];
  /** 比选助理用户ID列表 */
  bidding_assistants?: number[];
  /** 评分小组成员 */
  bidding_score_rule_group_member_dtos?: GroupMemberDto[];
};
export type EditBiddingBaseInfoForm = {
  /** 招采id */
  bidding_id: number;
  /** 背景 */
  background: string;
  /** 预期 */
  expectation: string;
};
export type DelayScoreTimeForm = {
  /** 比选id */
  bidding_id?: number;
  /** 新的评分截止时间 */
  score_deadline?: string;
};
export type CreateBiddingForm = {
  group_type: DemandGroupType;
  name: string;
  desc?: string;
  /** 背景 */
  background: string;
  /** 预期 */
  expectation: string;
  l1_category_id?: number;
  product_id: number;
  bidding_principals_user_ids: number[];
  number: string;
  bidding_business_ids: number[];
  priority: PriorityEnum;
  expect_begin_at: string;
  expect_end_at: string;
  budget: number;
  budget_currency: CurrencyEnum;
  bidding_type_v2: BiddingTypeV2Enum;
  /** 采购需求说明附件：原名称为实施计划/比选需求 */
  bidding_attach_blob_ids: number[];
  /** 比选脱敏附件 */
  bidding_desensitization_blob_ids?: number[];
  /** 保密协议逻辑版本，1=新逻辑（新建/退回草稿），0=旧逻辑（存量历史单） */
  confidentiality_version?: number;
  nda_template_type?: NdaTemplateTypeEnum;
  /** 保密协议模板附件（ndaTemplateType=OTHER 时必填） */
  bidding_nda_template_blob_ids?: number[];
  /** 比选背景附件 */
  bidding_background_blob_ids?: number[];
  approval_email_attach_blob_ids?: number[];
  /** 是否完成审批 */
  is_approval?: boolean;
  /** 是否脱敏输出 */
  desensitization_out?: boolean;
  /** 供应商能力要求 */
  supplier_capability_requirements?: string;
  /** 供应商推荐人姓名 */
  supplier_referrer?: string;
  /** 推荐供应商ID列表 */
  referrer_supplier_id_list?: number[];
  /** 供应商推荐理由 */
  supplier_referral_reason?: string;
  /** 回避供应商ID列表 */
  avoidance_supplier_id_list?: number[];
  /** 供应商回避理由 */
  supplier_avoidance_reason?: string;
  /** 第一轮是否听标 */
  first_round_listen_bidding?: boolean;
  /** 评委小组 */
  bidding_judge_teams?: BiddingJudgeTeamDto[];
  /** 费用分类明细（M03 新增） */
  cost_classifications?: CostClassificationItem[];
  /** 创建后的状态 */
  state?: string;
  /** 是否从需求单创建 */
  bind_demand?: boolean;
};
export type CreateBiddingResultVo = {
  id?: number;
};
export type ResponseCreateBiddingResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: CreateBiddingResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 退回供应商请求表单
 */
export type ReturnSuppliersForm = {
  /** 比选单ID */
  bidding_id: number;
  /** 需要退回的 BiddingSupplier.id 列表，至少选择一家 */
  supplier_ids: number[];
  /** 新的应邀截止日期（精确到天）。若当前日期晚于截止日期则必填；若未过期则可选填，填写时必须是未来日期 */
  new_bid_registration_deadline?: string;
};
export type BatchBiddingSubmitForm = {
  /** 招采id列表 */
  bidding_ids: number[];
  /** 审批总预算 */
  amount?: number;
  currency?: CurrencyEnum;
};
export type AddBiddingSupplierForm = {
  /** 招采id */
  bidding_id: number;
  /** 供应商id列表 */
  supplier_ids: number[];
  /** 接受邀约截止时间 */
  bid_registration_deadline?: string;
  /** 比选公告 */
  competition_announcement?: string;
  /** 申请审批记录BlobId */
  application_approval_record_blob_ids?: number[];
};
/**
 * 听标/议价排期入参配置
 */
export type BidScheduleParamVo = {
  /** 开始时间（YYYY-MM-DD HH:mm） */
  start_time?: string;
  /** 每家时长（分钟） */
  duration_per_vendor?: number;
  /** 提案方式 */
  proposal_method?: string;
  /** 家与家间隔（分钟） */
  interval_minutes?: number;
  /** 午休开始时间（HH:mm） */
  lunch_break_start?: string;
  /** 午休结束时间（HH:mm） */
  lunch_break_end?: string;
  /** 晚间休息开始时间（HH:mm，预留，可为 null） */
  evening_break_start?: string;
  /** 晚间休息结束时间（HH:mm，预留，可为 null） */
  evening_break_end?: string;
};
/**
 * 供应商排期
 */
export type SupplierScheduleDto = {
  /** 供应商ID */
  supplier_id?: string;
  /** 供应商编码 */
  supplier_code?: string;
  /** 供应商名称 */
  supplier_name?: string;
  /** 提案方式 */
  proposal_method?: string;
  /** 开始时间（YYYY-MM-DD HH:mm） */
  start_time?: string;
  /** 结束时间（YYYY-MM-DD HH:mm） */
  end_time?: string;
  /** 排序 */
  sort_order?: number;
};
/**
 * 议价排期保存入参
 */
export type BiddingNegotiateScheduleSaveForm = {
  /** 比选单ID */
  bidding_id: number;
  schedule_param_json: BidScheduleParamVo;
  /** 供应商排期列表（supplier_id/supplier_code/supplier_name） */
  vendors_json: SupplierScheduleDto[];
};
/**
 * 排期查询入参
 */
export type BiddingScheduleQueryForm = {
  /** 比选单ID */
  bidding_id: number;
};
/**
 * 议价排期详情
 */
export type BiddingNegotiateScheduleVo = {
  /** 主键ID */
  id?: number;
  /** 比选单ID */
  bidding_id?: number;
  /** 版本号，从1开始递增 */
  version?: number;
  /** 是否最新版本：0-否，1-是 */
  is_latest?: number;
  schedule_param_json?: BidScheduleParamVo;
  /** 供应商排期列表 */
  vendors_json?: SupplierScheduleDto[];
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
};
export type ResponseBiddingNegotiateScheduleVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingNegotiateScheduleVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 比选流程排期入参配置
 */
export type FlowScheduleParamVo = {
  /** 排期方式：1-正排，2-倒排 */
  schedule_type?: 1 | 2;
  /** 是否有二轮听标：0-否，1-是 */
  has_second_round?: 0 | 1;
  /** QA截止时间（YYYY-MM-DD） */
  qa_deadline?: string;
  /** 线上提案截止时间（YYYY-MM-DD） */
  online_proposal_deadline?: string;
  /** 正排：需求接收日期（YYYY-MM-DD） */
  requirement_receive_date?: string;
  /** 正排：一轮提案日期（YYYY-MM-DD） */
  first_proposal_date?: string;
  /** 正排：方案设计时长（工作日） */
  design_duration?: number;
  /** 倒排：执行群建立日期（YYYY-MM-DD） */
  execution_group_date?: string;
};
/**
 * 排期节点
 */
export type FlowScheduleNodeDto = {
  /** 节点标识 */
  node_id?: string;
  /** 节点名称 */
  node_name?: string;
  /** 开始日期 */
  start_date?: string;
  /** 结束日期 */
  end_date?: string;
  /** 时长(工作日) */
  duration?: number;
};
/**
 * 比选流程排期保存入参
 */
export type BiddingFlowScheduleSaveForm = {
  /** 比选单ID */
  bidding_id: number;
  schedule_param_json: FlowScheduleParamVo;
  /** 排期节点数组 */
  nodes_json: FlowScheduleNodeDto[];
};
/**
 * 比选流程排期详情
 */
export type BiddingFlowScheduleVo = {
  /** 主键ID */
  id?: number;
  /** 比选单ID */
  bidding_id?: number;
  /** 版本号，从1开始递增 */
  version?: number;
  /** 是否最新版本：0-否，1-是 */
  is_latest?: number;
  schedule_param_json?: FlowScheduleParamVo;
  /** 排期节点数组 */
  nodes_json?: FlowScheduleNodeDto[];
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
};
export type ResponseBiddingFlowScheduleVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingFlowScheduleVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 节假日查询入参
 */
export type HrmsCalendarRangeForm = {
  /** 开始日期(yyyy-MM-dd) */
  day_start: string;
  /** 结束日期(yyyy-MM-dd) */
  day_end: string;
};
/**
 * 日历日期信息
 */
export type HrmsCalendarDayVo = {
  /** 日期 */
  day?: string;
  /** 日期类型：DT001-节假日, DT002-休息日, DT003-工作日 */
  day_type?: string;
  /** 节假日类型：HT001-元旦节, HT002-春节, HT003-清明节, HT004-劳动节, HT005-端午节, HT006-中秋节, HT007-国庆节, HT008-公司假, HT010-除夕, HT009-无 */
  holiday_type?: string;
};
export type ResponseListHrmsCalendarDayVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: HrmsCalendarDayVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 听标排期保存入参
 */
export type BiddingBidScheduleSaveForm = {
  /** 比选单ID */
  bidding_id: number;
  schedule_param_json: BidScheduleParamVo;
  /** 供应商排期列表（supplier_id/supplier_code/supplier_name） */
  vendors_json: SupplierScheduleDto[];
};
/**
 * 听标排期详情
 */
export type BiddingBidScheduleVo = {
  /** 主键ID */
  id?: number;
  /** 比选单ID */
  bidding_id?: number;
  /** 版本号，从1开始递增 */
  version?: number;
  /** 是否最新版本：0-否，1-是 */
  is_latest?: number;
  schedule_param_json?: BidScheduleParamVo;
  /** 供应商排期列表 */
  vendors_json?: SupplierScheduleDto[];
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
};
export type ResponseBiddingBidScheduleVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingBidScheduleVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 查询比选方案模板列表表单
 */
export type QueryListBiddingPlanTemplateForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 比选需求类型 */
  bidding_type_v2s?: BiddingTypeV2Enum[];
  /** 模板名称 */
  name_fuzzy?: string;
  /** 生效状态   1=启用,0=禁用 */
  status?: number[];
};
/**
 * 比选方案模板列表VO
 */
export type BiddingPlanTemplateListVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 模板ID */
  template_id?: number;
  /** 模板名称 */
  template_name?: string;
  /** 比选需求类型 */
  bidding_type_v2s?: string[];
  /** 比选需求类型名称 */
  bidding_type_v2_names?: string[];
  /** 生效状态   1=启用,0=禁用 */
  status?: number;
};
/**
 * 分页结果
 */
export type PageResultBiddingPlanTemplateListVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: BiddingPlanTemplateListVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  page?: number;
};
export type ResponsePageResultBiddingPlanTemplateListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultBiddingPlanTemplateListVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListBiddingPlanTemplateListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingPlanTemplateListVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 比选评分项VO
 */
export type BiddingPlanTemplateScoreItemVo = {
  /** 评分项 */
  item?: string;
  /** 分值上限 */
  weight?: number;
};
/**
 * 评委小组明细信息
 */
export type GroupVo = {
  /** 评分小组名称 */
  name?: string;
  /** 权重 */
  weight?: number;
  /** 评分项 */
  detail?: BiddingPlanTemplateScoreItemVo[];
  /** 关联的一级需求类型ID列表（职能策划/采购方商务类型时，与name一一对应） */
  l1_category_ids?: number[];
};
/**
 * 各方评分信息
 */
export type ScoreDetailVo = {
  /** 评分类型 BiddingParticipantType DEMAND(AppConstants.BIDDING_PARTICIPANT_DEMAND, "需求方"),, ,     PLAN(AppConstants.BIDDING_PARTICIPANT_PLAN, "职能策划"),, ,     PURCHASE(AppConstants.BIDDING_PARTICIPANT_PURCHASE, "采购方商务"), */
  type?: string;
  /** 权重 */
  weight?: number;
  /** 评委小组明细信息 */
  group?: GroupVo[];
};
/**
 * 编辑比选评分方案模板入参
 */
export type EditBiddingPlanTemplateForm = {
  /** 模板名称 */
  template_name: string;
  /** 比选需求类型 */
  bidding_type_v2s: BiddingTypeV2Enum[];
  /** 各方评分信息 */
  score_infos?: ScoreDetailVo[];
  /** 状态 */
  status?: number;
  /** 模板ID */
  template_id?: number;
};
/**
 * 创建比选评分方案模板入参
 */
export type CreateBiddingPlanTemplateForm = {
  /** 模板名称 */
  template_name: string;
  /** 比选需求类型 */
  bidding_type_v2s: BiddingTypeV2Enum[];
  /** 各方评分信息 */
  score_infos?: ScoreDetailVo[];
  /** 状态 */
  status?: number;
};
export type AttachmentBlobDto = {
  /** 附件Blob ID */
  blob_id?: number;
  /** 附件S3 Key */
  blob_key?: string;
  /** 附件原始文件名 */
  filename?: string;
  /** 附件内容类型 */
  content_type?: string;
  /** 附件大小，单位字节 */
  size?: number;
  auto_generated?: boolean;
};
export type ResponseAttachmentBlobDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: AttachmentBlobDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type StreamingResponseBody = object;
/**
 * 需求文档状态
 */
export type TrumpetDemandState =
  | 'newly_built'
  | 'demand_principal_approval'
  | 'plan_design'
  | 'plan_approval'
  | 'activity_design'
  | 'in_progress'
  | 'finished'
  | 'closed';
/**
 * 小喇叭费用明细行
 */
export type BudgetDetailItem = {
  /** 费用分类：活动激励类 / 运营服务类 */
  expense_category?: string;
  /** 追加前金额（原币种） */
  original_amount?: number;
  /** 追加后金额（原币种） */
  new_amount?: number;
};
/**
 * 小喇叭追加预算业务快照（最新一次）
 */
export type TrumpetBudgetAdditionInfoVo = {
  /** 对应 demand_oa.id（最新一条，approve_type=106） */
  demand_oa_id?: number;
  /** OA 流程号 */
  oa_id?: string;
  oa_status?: OaState;
  /** 原需求总预算（原币种，与 budgetCurrency 同单位） */
  original_budget?: number;
  /** 追加后需求总预算（原币种） */
  new_budget?: number;
  /** 币种代码（如 CNY / USD） */
  budget_currency?: string;
  /** 追加原因 */
  reason?: string;
  /** 原需求预期 */
  original_expectation?: string;
  /** 追加后需求预期 */
  new_expectation?: string;
  /** 小喇叭费用明细（固定 2 行：活动激励类 / 运营服务类，原币种） */
  budget_details?: BudgetDetailItem[];
  /** 提交人姓名 */
  submit_user_name?: string;
  /** 提交时间（demand_oa.created_at） */
  submit_time?: string;
  /** 审批驳回原因（仅驳回时有值） */
  reject_reason?: string;
};
/**
 * 流程标志
 */
export type TrumpetFlowFlagEnum =
  | 'OLD_ONLINE_PROCESS'
  | 'NORMAL'
  | 'ADD_ASSESSMENT_INDICATORS'
  | 'ADD_PLAN_DESIGN';
/**
 * 退回信息
 */
export type TrumpetRejectInfoVo = {
  reject_reason?: string;
  last_state?: string;
  last_state_name?: string;
};
export type TrumpetDemandDetailVo = {
  /** 需求ID */
  id?: number;
  /** 关联需求单号 */
  mpc_demand_id?: number;
  /** 需求标题 */
  title?: string;
  /** 产品名称 */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 产品ID */
  product_id?: number;
  /** 被接受部门类型 */
  group_type?: string;
  /** 项目编号 */
  number?: string;
  /** 需求背景 */
  background?: string;
  /** 需求预期 */
  expectation?: string;
  /** 预计开始时间 */
  expect_begin_at?: string;
  /** 预计结束时间 */
  expect_end_at?: string;
  /** 需求创建时间 */
  create_date?: string;
  /** priority */
  priority?: string;
  /** 预算 */
  budget?: number;
  /** 预算币种 */
  budget_currency?: string;
  /** 运营服务类预算 */
  budget_operation?: number;
  /** 运营服务类预算-备注 */
  budget_operation_desc?: string;
  /** 活动激励类预算 */
  budget_activity?: number;
  /** 活动激励类预算-备注 */
  budget_activity_desc?: string;
  /** 原借款金额 */
  borrow_amount?: number;
  /** 借款币种 */
  borrow_amount_currency?: string;
  /** 当前可用借款金额 */
  can_use_borrow_amount?: number;
  /** 借款oa流程ID */
  oa_borrow_workflow_id?: string;
  /** 策划 */
  planner?: BaseUserVo[];
  /** 商务 */
  businessman?: BaseUserVo[];
  creator?: BaseUserVo;
  /** 需求人员 */
  principal?: BaseUserVo[];
  /** 运营人员 */
  operators?: BaseUserVo[];
  /** 需求的相关材料 */
  trumpet_demand_attachments?: AttachmentVo[];
  /** 策划案/相关附件 */
  plan_attachments?: AttachmentVo[];
  /** 使用规划 */
  usage_plan?: string;
  demand_status?: TrumpetDemandState;
  /** OA审批列表信息（方案审批） */
  oa_audit_desc_list?: OaProcessDescVo[];
  /** 追加预算OA审批列表信息（按时间倒序，最新在前） */
  budget_increase_oa_audit_desc_list?: OaProcessDescVo[];
  budget_addition_latest?: TrumpetBudgetAdditionInfoVo;
  flow_flag?: TrumpetFlowFlagEnum;
  /** 是否有策划场景，供前端使用 */
  is_need_plan_design?: boolean;
  reject_info?: TrumpetRejectInfoVo;
  l1_category_id?: number;
  l2_category_id?: number;
  l1_category_name?: string;
  l2_category_name?: string;
};
/**
 * 指标信息
 */
export type TrumpetAssessmentIndicatorRequirementVo = {
  /** 序号 */
  index_id?: number;
  /** 二级需求单ID */
  demand_id?: number;
  /** 指标ID */
  kpi_code?: string;
  kpi_schema?: DemandKpiSchemaData;
  /** 是否直接考核 */
  is_direct?: boolean;
  /** 指标要求 */
  expect_value?: string;
};
export type TrumpetActivityDetailVo = {
  /** 活动ID */
  id?: number;
  /** 活动标题 */
  activity_name?: string;
  /** 活动预算 */
  activity_budget?: number;
  /** 活动预算币种 */
  activity_budget_currency?: string;
  /** 策划 */
  planner?: BaseUserVo[];
  /** 商务 */
  businessman?: BaseUserVo[];
  creator?: BaseUserVo;
  /** 需求人员 */
  principal?: BaseUserVo[];
  /** 运营人员 */
  operators?: BaseUserVo[];
  /** 活动执行附件Id */
  execution_attachments?: AttachmentVo[];
  /** 开始时间 */
  start_at?: string;
  /** 结束时间 */
  end_at?: string;
  /** 详情 */
  desc?: string;
  activity_status?: TrumpetActivityState;
  /** 一级单号 */
  root_id?: number;
  /** 三级单号 */
  case_id?: number;
  /** 指标信息 */
  assessment_indicator_requirements?: TrumpetAssessmentIndicatorRequirementVo[];
  reject_info?: TrumpetRejectInfoVo;
  /** 是否使用敏捷方式 */
  use_agile_mode?: boolean;
  l4_category_name?: string;
  l2_category_id?: number;
  l3_category_id?: number;
  l2_category_name?: string;
  l3_category_name?: string;
  l4_category_id?: number;
};
/**
 * 实际发放明细
 */
export type TrumpetActualReleaseDetailVo = {
  /** 发放年月 */
  release_month?: string;
  /** 实际发放金额（含税） */
  actual_amount?: number;
  /** 实际发放金额（不含税） */
  actual_amount_no_tax?: number;
  /** 实际发放金额（税额） */
  actual_tax?: number;
  /** 发放明细附件 */
  release_attachments?: AttachmentVo[];
  /** 更新时间 */
  updated_at?: string;
};
/**
 * 发放明细
 */
export type TrumpetReleaseDetailVo = {
  /** 供应商Id */
  supplier_id?: number;
  /** 供应商名称 */
  supplier_name?: string;
  supplier_vendor_type?: SupplierVendorType;
  /** 预计发放金额 */
  estimated_release_amount?: number;
  estimated_release_currency?: CurrencyEnum;
  /** 实际发放金额 */
  actual_release_amount?: number;
  actual_release_currency?: CurrencyEnum;
  /** 预计发放明细附件 */
  attachments?: AttachmentVo[];
  /** 更新时间 */
  updated_at?: string;
  /** 易费控报销单号 */
  doc_number?: string;
  /** 易费控报销单查看页面url */
  doc_view_url?: string;
  /** 实际发放明细 */
  actual_release_details?: TrumpetActualReleaseDetailVo[];
};
/**
 * 结算状态
 */
export type TrumpetCaseState =
  | 'submit_case_closure'
  | 'plan_accept'
  | 'approve_settle'
  | 'secondary_approve_settle'
  | 'marketing_approve_settle'
  | 'qc_settle'
  | 'market_internal_audit_team_review'
  | 'strategy_research_department_review'
  | 'operations_review'
  | 'submit_oa'
  | 'confirm_verification'
  | 'finished'
  | 'closed';
/**
 * 指标信息
 */
export type TrumpetAssessmentIndicatorRecordVo = {
  /** 序号 */
  index_id?: number;
  /** 小喇叭三级需求单ID */
  demand_id?: number;
  /** 指标ID */
  kpi_code?: string;
  kpi_schema?: DemandKpiSchemaData;
  /** 是否直接考核 */
  is_direct?: boolean;
  /** 指标要求 */
  expect_value?: string;
  /** 填写记录 */
  actual_value?: string;
  /** 是否达标 */
  result?: boolean;
};
export type TrumpetCaseDetailVo = {
  /** 三级单ID */
  id?: number;
  /** 三级单预算 */
  case_budget?: number;
  /** 预计发放金额(含税) */
  estimated_release_amount?: number;
  /** 预计发放币种 */
  estimated_release_currency?: string;
  /** 预计发放总金额 */
  estimated_release_total_amount?: number;
  /** 实际发放金额(含税) */
  actual_release_amount?: number;
  /** 实际发放币种 */
  actual_release_currency?: string;
  /** 效果描述 */
  effect_description?: string;
  /** 借款结余金额 */
  borrow_amount_balance?: number;
  /** 借款结余金额币种 */
  borrow_amount_balance_currency?: string;
  /** 取消发放金额总和 */
  cancel_release_amount?: number;
  /** 发放明细 */
  release_details?: TrumpetReleaseDetailVo[];
  /** 取消发放明细附件 */
  cancel_release_attachments?: AttachmentVo[];
  /** 账单明细附件 */
  billing_attachments?: AttachmentVo[];
  /** 结案报告附件 */
  report_attachments?: AttachmentVo[];
  /** oa流程附件 */
  oa_attachments?: AttachmentVo[];
  /** oa报销流程号 */
  oa_flow_id?: string;
  /** OA审批列表信息 */
  oa_audit_desc_list?: OaProcessDescVo[];
  case_status?: TrumpetCaseState;
  /** 备注信息 */
  note?: string;
  reject_info?: TrumpetRejectInfoVo;
  /** 指标信息 */
  assessment_indicator_records?: TrumpetAssessmentIndicatorRecordVo[];
  /** 是否手动创建三级单 */
  manual_create_case?: boolean;
  /** 是否使用敏捷方式 */
  use_agile_mode?: boolean;
};
export type TrumpetCasePageDetailVo = {
  root_id?: number;
  current_run_type?: TrumpetDemandType;
  current_id?: number;
  flow_status?: string;
  flow_status_name?: string;
  permit_actions?: PermitActions;
  bottom_tab?: BottomTab[];
  remark?: string;
  demand_detail?: TrumpetDemandDetailVo;
  activity_detail?: TrumpetActivityDetailVo;
  case_detail?: TrumpetCaseDetailVo;
  sm_budget?: SmExpenseBudgetQueryVo;
};
export type FieldSchema = {
  field?: string;
  type?: string;
  description?: string;
};
export type AiResponseTrumpetCasePageDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TrumpetCasePageDetailVo;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiSupplierAmountForm = {
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 渠道供应商ID */
  channel_supplier_id?: number;
};
export type AiResponseObject = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: object;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiSupplierStatsSummaryForm = {
  /** 供应商名称（完全匹配） */
  supplier_name: string;
  /** 服务类型（可选）中文 */
  channel_type?: string;
  /** 创建时间起始（可选） */
  created_at_start?: string;
  /** 创建时间结束（可选） */
  created_at_end?: string;
};
export type AiSupplierStatsSummaryVo = {
  /** 需求单发需次数 */
  demand_ask_quote_count?: number;
  /** 需求单报价次数 */
  demand_quote_count?: number;
  /** 需求单中选次数 */
  demand_select_count?: number;
  /** 需求单报价总金额（原币，单位：元） */
  demand_quote_total_amount?: number;
  /** 需求单报价总金额（人民币，单位：元） */
  demand_quote_total_amount_cny?: number;
  demand_quote_total_amount_currency?: CurrencyEnum;
  /** 需求单最终结算总金额（原币，单位：元） */
  demand_settle_total_amount?: number;
  /** 需求单最终结算总金额（人民币，单位：元） */
  demand_settle_total_amount_cny?: number;
  demand_settle_total_amount_currency?: CurrencyEnum;
  /** 需求单合作总金额（原币，单位：元）。合作金额=优先取结算金额，无结算金额时取报价金额 */
  demand_cooperate_total_amount?: number;
  /** 需求单合作总金额（人民币，单位：元）。合作金额=优先取结算金额，无结算金额时取报价金额 */
  demand_cooperate_total_amount_cny?: number;
  demand_cooperate_total_amount_currency?: CurrencyEnum;
  /** 需求单总数量 */
  demand_total_count?: number;
  /** 需求单进行中数量 */
  demand_running_count?: number;
  /** 需求单已结束数量 */
  demand_finished_count?: number;
  /** 需求单已废弃数量 */
  demand_abandon_count?: number;
  /** 需求单平均综合评分 */
  demand_avg_overall_score?: number;
  /** 需求单平均过程评分 */
  demand_avg_processing_score?: number;
  /** 需求单平均结果评分 */
  demand_avg_result_score?: number;
  /** 比选发需次数 */
  bidding_ask_quote_count?: number;
  /** 比选报价次数 */
  bidding_quote_count?: number;
  /** 比选中选次数 */
  bidding_select_count?: number;
  /** 比选报价总金额（原币，单位：元） */
  bidding_quote_total_amount?: number;
  /** 比选报价总金额（人民币，单位：元） */
  bidding_quote_total_amount_cny?: number;
  bidding_quote_total_amount_currency?: CurrencyEnum;
  /** 比选中选总金额（原币，单位：元） */
  bidding_winning_total_amount?: number;
  /** 比选中选总金额（人民币，单位：元） */
  bidding_winning_total_amount_cny?: number;
  bidding_winning_total_amount_currency?: CurrencyEnum;
  /** 比选执行总额（原币，单位：元） */
  bidding_execution_total_amount?: number;
  /** 比选执行总额（人民币，单位：元） */
  bidding_execution_total_amount_cny?: number;
  bidding_execution_total_amount_currency?: CurrencyEnum;
  /** 比选单总数量 */
  bidding_total_count?: number;
  /** 比选单进行中数量 */
  bidding_running_count?: number;
  /** 比选单已结束数量 */
  bidding_finished_count?: number;
  /** 比选单已流标数量 */
  bidding_failed_count?: number;
  /** 比选平均综合评分 */
  bidding_avg_score?: number;
  /** 比选平均需求方技术评分 */
  bidding_avg_demand_score?: number;
  /** 比选平均职能策划技术评分 */
  bidding_avg_plan_score?: number;
  /** 比选平均采购方商务评分 */
  bidding_avg_purchase_score?: number;
  /** 匹配到的渠道供应商数量 */
  matched_supplier_count?: number;
  /** 匹配到的供应商名称列表 */
  matched_supplier_names?: string[];
};
export type AiResponseAiSupplierStatsSummaryVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: AiSupplierStatsSummaryVo;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiPageableIdForm = {
  /** 业务ID */
  id?: number;
  /** 页码，默认1 */
  current?: number;
  /** 每页条数，默认20 */
  limit?: number;
};
export type QueryValidCsCommand = {
  /** 当前页码 */
  current?: number;
  /** 每页数量 */
  limit?: number;
  /** 渠道供应商code */
  code?: string;
  /** 供应商名称/简称：模糊匹配 */
  supplier_name?: string;
  /** 主供应商编码：精确匹配 */
  main_supplier_code?: string;
  /** 主供应商ID(供应商主体ID)：精确匹配 */
  main_supplier_id?: number;
  /** 是否单一来源 */
  single_source?: boolean;
  /** 供应商等级：支持多选，枚举值：储备供应商、重要供应商、核心供应商 */
  supplier_types?: ChannelSupplierType[];
  /** 服务类型：支持多选，枚举值：广告投放，内容营销，生态运营，创意与素材，活动赛事，名人明星，其他 */
  channel_types?: string[];
  /** 渠道供应商状态 */
  channel_supplier_status?: ChannelSupplierStatus[];
  /** 垫支量级：下拉选择，支持多选，枚举值：0-50万、50-100万、100-500万、500万以上、不明 */
  advance_amount_range?: string[];
  /** 类型生效开始时间：日历控件，年月日，支持选择日期范围 */
  valid_start_date?: string;
  /** 类型生效结束时间：日历控件，年月日，支持选择日期范围 */
  valid_end_date?: string;
  /** 支持 */
  tag_ids?: number[];
  /** vmdm编码 */
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  profit_type?: SupplierProfitType;
  area_type?: SupplierAreaType;
  /** 风险提示开启 */
  risk_switch?: boolean;
  /** 包含风险备注 */
  has_risk_remark?: boolean;
  /** 包含供应商备注 */
  has_supplier_comment?: boolean;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 入库时间开始 */
  inbound_start_time?: string;
  /** 入库时间结束 */
  inbound_end_time?: string;
};
/**
 * 资质文件
 */
export type StorageObjectDto = {
  /** 文件id */
  id?: number;
  /** 文件名称 */
  name?: string;
  /** 文件大小 */
  size?: number;
  /** 下载链接 */
  download_url?: string;
  /** 预览链接 */
  preview_url?: string;
};
export type PersonIdentityType =
  | '01'
  | '02'
  | '03'
  | '04'
  | '05'
  | '06'
  | '07'
  | '08'
  | '09'
  | '10'
  | '11'
  | '12';
/**
 * 审批意见
 */
export type ApprovalOpinionDto = {
  main_supplier_id?: number;
  operator?: OperatorDto;
  create_time?: string;
  approval_opinion?: string;
  approval_node?: ApprovalNode;
};
/**
 * 供应商主体
 */
export type MainSupplierDto = {
  /** 主供应商id */
  id?: number;
  /** 主供应商编码 */
  code?: string;
  /** 主供应商名称 */
  name?: string;
  /** 主供应商简称 */
  abbr_name?: string;
  /** 主供应商地址 */
  address?: string;
  /** 主供应商员工总数 */
  staff_amount?: number;
  /** 是否有关联关系 */
  former_employee_founded?: boolean;
  /** 资质文件 */
  attachments?: StorageObjectDto[];
  /** 是否单一来源 */
  single_source?: boolean;
  mdm_code?: string;
  mdm_type?: SupplierVendorType;
  area_type?: SupplierAreaType;
  profit_type?: SupplierProfitType;
  blocked?: boolean;
  identity_type?: PersonIdentityType;
  identity_no?: string;
  /** 风险开关, 0:关闭, 1:开启 */
  risk_switch?: number;
  /** 风险备注 */
  risk_remarks?: SupplierRiskRemarkDto[];
  /** 是否包含风险备注 */
  has_risk_remark?: boolean;
  /** 是否包含供应商备注 */
  has_supplier_comment?: boolean;
  /** 供应商备注 */
  supplier_comments?: SupplierCommentDto[];
  /** 审批意见 */
  approval_opinions?: ApprovalOpinionDto[];
  /** 供应商风险标签 */
  risk_tags?: SupplierRiskTagDto[];
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 公司统一编码 */
  company_code?: string;
  /** 公司财务编码 */
  company_financial_code?: string;
  identity_type_name?: string;
};
/**
 * 服务信息
 */
export type ChannelSupplierContentDto = {
  /** 服务内容 */
  content?: string;
  /** 优势资源平台 */
  advantage_resource_platform?: ChannelSupplierAdvantageResourcePlatform[];
  /** 主要客户 */
  main_customers?: string;
  /** 服务案例 */
  examples?: string;
  /** 网易游戏案例 */
  net_ease_game_examples?: string;
  advance_scale?: ChannelSupplierAdvanceScale;
  /** 推荐项目组 */
  recommended_team?: string;
  /** 推荐人 */
  recommender?: string;
  /** 推荐人邮箱 */
  recommender_mail?: string;
  /** 推荐入库理由 */
  recommendation_reason?: string;
  /** 推荐人与代理商建联背景 */
  recommender_relationship?: string;
};
/**
 * 渠道供应商合同联系人信息
 */
export type ChannelSupplierContractContactDto = {
  /** 合同联系人姓名 */
  contract_contact_name?: string;
  /** 合同联系人邮箱 */
  contract_contact_email?: string;
  /** 合同联系人地址 */
  contract_contact_address?: string;
  /** 合同联系人电话 */
  contract_contact_phone?: string;
};
/**
 * 负责人
 */
export type ChannelSupplierRelatedPersonDto = {
  /** 姓名 */
  name?: string;
  /** 职位 */
  position?: string;
  /** 邮箱 */
  mail?: string;
  /** 电话号码 */
  tel?: string;
};
/**
 * 供应商等级信息
 */
export type ChannelSupplierTypeInfoDto = {
  type?: ChannelSupplierType;
  /** 生效时间，年月日 */
  valid_date?: string;
  /** 失效时间，年月日 */
  invalid_date?: string;
};
/**
 * 标签
 */
export type TagDto = {
  /** 标签id */
  id?: number;
  /** 标签名称 */
  name?: string;
};
/**
 * 账号信息，入库审批通过后，需要根据此账号信息，注册渠道供应商的登录账号，这里记录一下
 */
export type AccountDto = {
  /** 用户名 */
  username?: string;
  /** 邮件 */
  email?: string;
  /** popo账号 */
  popo_account?: string;
  /** 账号状态,0-可用，1-禁用 */
  status?: number;
};
export type ChannelSupplierDetailDto = {
  /** 渠道供应商ID */
  id?: number;
  /** 渠道供应商编码 */
  code?: string;
  main_supplier?: MainSupplierDto;
  channel_type?: ChannelType;
  content?: ChannelSupplierContentDto;
  bank_info?: ChannelSupplierBankDto;
  contract_contact_info?: ChannelSupplierContractContactDto;
  /** 对接人 */
  contacts?: ChannelSupplierRelatedPersonDto[];
  /** 负责人 */
  principals?: ChannelSupplierRelatedPersonDto[];
  /** 账号信息 */
  association_accounts?: string[];
  type_info?: ChannelSupplierTypeInfoDto;
  /** 标签 */
  tags?: TagDto[];
  approval_node?: ApprovalNode;
  approval_node2?: ApprovalNode;
  status?: ChannelSupplierStatus;
  /** 风险拦截标记 */
  risky?: boolean;
  /** 近期评分次数 */
  recent_score_count?: number;
  /** 是否为KOL合作MCN */
  kol_mcn?: boolean;
  /** 账号信息，入库审批通过后，需要根据此账号信息，注册渠道供应商的登录账号，这里记录一下 */
  account_info_list?: AccountDto[];
  /** 入库时间 */
  inbound_at?: string;
  /** 服务类型名称 */
  channel_type_name?: string;
};
export type AiResponseChannelSupplierDetailDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ChannelSupplierDetailDto;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
/**
 * 发票列表DTO
 */
export type InvoiceListDto = {
  /** 发票ID，MPC侧生成的发票表主键ID，同一张发票被删除重新生成，ID会不同 */
  id?: number;
  /** 发票ID，M税务系统生成的发票ID，同一张发票被删除重新生成，ID会不同 */
  invoice_id?: number;
  /** 发票唯一Key，税务系统生成的唯一Key，同一张发票的key相同 */
  invoice_key?: string;
  /** 发票代码 */
  invoice_code?: string;
  /** 发票号码 */
  invoice_no?: string;
  /** 发票对应的主供应商名称 */
  supplier_name?: string;
  /** 发票状态 */
  invoice_state?: string;
  invoice_type?: InvoiceType;
  /** 发票含税总金额 */
  amount_with_tax?: number;
  /** 发票不含税金额 */
  amount_without_tax?: number;
  /** 发票税额 */
  tax?: number;
  /** 发票数据状态 1=生效,0=失效 */
  is_valid?: number;
  /** 更新时间 */
  updated_at?: string;
  /** 创建时间 */
  create_at?: string;
  /** 删除标记，0是未删除，负数是删除 */
  deleted_flag?: number;
  currency?: CurrencyEnum;
  demand_info_list?: InvoiceDemandInfo[];
};
export type AiResponseListInvoiceListDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: InvoiceListDto[];
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiResponseInvoiceDetailDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: InvoiceDetailDto;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiResponseAttachmentBlobDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: AttachmentBlobDto;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiPresignUploadForm = {
  /** 文件名（含扩展名） */
  file_name: string;
  /** 文件 Content-Type，不填时由系统根据文件名后缀自动推断 */
  content_type?: string;
};
export type AiPresignUploadVo = {
  /** S3 预签名上传 URL，客户端使用 HTTP PUT 方法将文件体发送至此地址 */
  upload_url?: string;
  /** 预创建的 Blob ID，上传完成后传给 complete-presign-upload 接口 */
  blob_id?: number;
  /** S3 对象 Key */
  blob_key?: string;
  /** 预签名 URL 有效期（秒） */
  expires_in?: number;
};
export type AiResponseAiPresignUploadVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: AiPresignUploadVo;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiDemandSupplierQuoteForm = {
  /** 需求供应商ID */
  demand_supplier_id?: number;
  /** 合作主体ID */
  cooperate_account_id?: number;
};
export type AiResponseDemandSupplierQuoteWithSettleReportVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSupplierQuoteWithSettleReportVo;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiResponseJournalResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: JournalResDto;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiDemandSupplierQuoteDiffForm = {
  /** 是否差异对比 */
  diff?: boolean;
  /** 需求ID */
  demand_id?: number;
  /** 需求供应商ID */
  demand_supplier_id?: number;
  /** 附件ID */
  blob_id?: number;
  /** 合作主体ID */
  cooperate_account_id?: number;
};
export type AiResponseDemandSupplierQuoteDiffVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSupplierQuoteDiffVo;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiResponseDemandDetailsResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandDetailsResDto;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiDemandHisRoundForm = {
  /** 需求ID */
  demand_id?: number;
  /** 轮次号 */
  turns_number?: number;
};
export type AiResponseDemandHisDetailResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandHisDetailResDto;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiKolInquiryDetailForm = {
  /** 达人主页地址 */
  kol_main_page?: string;
};
/**
 * KOL资源详情（合作询价详情）
 */
export type KolCooperationDetail = {
  main_page_id?: string;
  kol_main_page?: string;
  kol_name?: string;
  platform_name?: string;
  order_platform?: string;
  follower_num?: number;
  is_coop_last6m?: string;
  last_coop_price_cny?: number;
  last_coop_price_cny_per?: number;
  coop_price_cny_per?: number;
  coop_price_cny_per_live?: number;
  his_coop_price_cny_per?: number;
  his_coop_price_cny_per_live?: number;
  coop_cpm_six_month?: number;
  cpm_uv_six_month?: number;
  cpm_pv_six_month?: number;
  coop_dates?: string[];
  all_quote_price_cny?: Item[];
  all_cpm_expected?: Item[];
  coop_infos_suppliers?: Item[];
  coop_infos_self?: Item[];
};
/**
 * KOL询价用详情
 */
export type AiKolInquiryDetailVo = {
  /** KOL资源列表 */
  kol_resource_list?: object;
  kol_resource_detail?: KolCooperationDetail;
};
export type AiResponseAiKolInquiryDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: AiKolInquiryDetailVo;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiDemandSourceQueryForm = {
  /** 一询单ID */
  demand_id?: number;
};
export type AiResponseDemandSourceDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSourceDetailVo;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type ContractTypeVo = {
  /** 合同类型编码 */
  code?: string;
  /** 合同类型名称 */
  name?: string;
};
export type AiResponseListContractTypeVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ContractTypeVo[];
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiResponsePageResultPendingContractVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultPendingContractVo;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type OptionDetailWrapperVoOptionBase = {
  key?: string;
  name?: string;
  desc?: string;
  options?: OptionBase[];
};
export type AiResponseListOptionDetailWrapperVoOptionBase = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OptionDetailWrapperVoOptionBase[];
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiResponseListExchangeRateDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ExchangeRateDto[];
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
/**
 * 营销闭环需求详情-用于渲染权限标志
 */
export type ClosedLoopPermitActions = {
  /** 编辑 */
  edit?: boolean;
  /** 废弃 */
  discard?: boolean;
  /** 拆单 */
  split?: boolean;
  /** 关闭 */
  close?: boolean;
};
/**
 * 营销闭环需求详情-用于渲染权限标志
 */
export type ClosedLoopBottomTab = {
  /** 操作名称 */
  tab_name?: string;
  /** 操作Key */
  value?: string;
};
/**
 * 营销闭环需求详情-用户信息
 */
export type ClosedLoopUser = {
  /** 用户id */
  user_id?: number;
  /** 姓名 */
  name?: string;
  /** 工号 */
  job_number?: string;
  /** 邮件 */
  email?: string;
};
/**
 * 营销闭环需求详情-Blob信息
 */
export type ClosedLoopBlob = {
  /** blob-id */
  id?: number;
  /** 附件名称 */
  filename?: string;
  /** 附件大小 */
  byte_size?: number;
  /** 文件类型 */
  content_type?: string;
  /** 文件的Key */
  key?: string;
};
/**
 * 营销闭环需求详情-附件信息
 */
export type ClosedLoopAttachment = {
  /** 附件Id */
  id?: number;
  blob?: ClosedLoopBlob;
};
/**
 * 营销闭环需求详情-OA审批概述
 */
export type OaProcessInfoVo = {
  process_type?: ApproveTypeEnum;
  /** 关联需求单ID */
  demand_id?: number;
  /** OaId */
  oa_id?: string;
  /** oa流程地址 */
  oa_url?: string;
  /** 提交人Email */
  submit_user_email?: string;
  /** 提交人 */
  submit_user_name?: string;
  /** 提交时间 */
  submit_time?: string;
  oa_status?: OaState;
  /** oa拒绝原因 */
  oa_reject_reason?: string;
};
/**
 * 退回信息
 */
export type RejectInfoVo = {
  reject_reason?: string;
  last_state?: string;
  last_state_name?: string;
};
/**
 * 营销闭环需求详情-一级单详情
 */
export type ClosedLoopDemandDetail = {
  /** 需求ID */
  id?: number;
  group_type?: DemandGroupType;
  /** 需求标题 */
  title?: string;
  /** 需求背景 */
  background?: string;
  /** 需求预期 */
  expectation?: string;
  /** 需求一级目录ID */
  l1_category_id?: number;
  /** 需求一级目录 */
  l1_category_name?: string;
  /** 需求二级目录ID */
  l2_category_id?: number;
  /** 需求二级目录 */
  l2_category_name?: string;
  /** 产品code */
  product_identifier?: string;
  /** 外部系统产品代号 */
  external_product_code?: string;
  /** 产品名称 */
  product_name?: string;
  /** 产品ID */
  product_id?: number;
  /** 项目编号 */
  number?: string;
  /** 需求创建时间 */
  create_date?: string;
  priority?: PriorityEnum;
  /** 预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  /** 预计发放金额 */
  estimated_release_amount?: number;
  /** 预计代扣代缴个税金额 */
  estimated_withholding_tax_amount?: number;
  /** 需求人数组 */
  principal: ClosedLoopUser[];
  /** 执行预期开始日期 */
  estimated_execution_start_at?: string;
  /** OA审批归档时间 */
  oa_finish_time?: string;
  /** 执行预期结束日期 */
  estimated_execution_end_at?: string;
  creator?: ClosedLoopUser;
  /** 需求的相关材料 */
  attachments?: ClosedLoopAttachment[];
  demand_status?: ClosedLoopDemandState;
  /** OA审批列表信息 */
  oa_audit_desc_list?: OaProcessInfoVo[];
  reject_info?: RejectInfoVo;
};
/**
 * 活动状态
 */
export type ClosedLoopActivityState = 'running' | 'finished' | 'closed';
/**
 * 营销闭环需求详情-二级单详情
 */
export type ClosedLoopActivityDetail = {
  /** 活动ID */
  id?: number;
  /** 活动标题 */
  activity_name?: string;
  /** 需求二级目录ID */
  l2_category_id?: number;
  /** 需求二级目录 */
  l2_category_name?: string;
  /** 需求三级目录ID */
  l3_category_id?: number;
  /** 需求三级目录 */
  l3_category_name?: string;
  /** 需求四级目录ID */
  l4_category_id?: number;
  /** 需求四级目录 */
  l4_category_name?: string;
  /** 活动预算 */
  activity_budget?: number;
  /** 活动预算币种 */
  activity_budget_currency?: string;
  /** 预计发放金额 */
  estimated_release_amount?: number;
  /** 预计代扣代缴个税金额 */
  estimated_withholding_tax_amount?: number;
  /** 活动执行附件 */
  execution_attachments?: ClosedLoopAttachment[];
  /** 活动详情 */
  desc?: string;
  /** 指标信息 */
  assessment_indicator_requirements?: ClosedLoopAssessmentIndicatorRecord[];
  activity_status?: ClosedLoopActivityState;
  /** 根ID */
  root_id?: number;
  /** 父ID */
  parent_id?: number;
  /** 活动执行日期开始日期 */
  activity_start_at?: string;
  /** 活动执行日期结束日期 */
  activity_end_at?: string;
  reject_info?: RejectInfoVo;
};
/**
 * 营销闭环需求详情-预付单信息
 */
export type ClosedLoopFeeControlDocInfo = {
  /** 付款单平台 */
  platform?: string;
  /** 付款单号 */
  doc_number?: string;
  doc_status?: FeeControlDocStatusEnum;
  /** 易费控报销单查看页面url */
  doc_view_url?: string;
  /** 发起人姓名 */
  submit_user_name?: string;
  /** 发起人邮箱 */
  submit_user_email?: string;
  /** 发起人UserId */
  submit_user_id?: number;
  /** 付款时间 */
  pay_time?: string;
  /** 创建时间 */
  created_at?: string;
  /** 审批完成事件 */
  complete_at?: string;
  /** 单据类型 */
  doc_type?: string;
  /** 单据金额 */
  amount?: number;
  /** 单据币种 */
  currency?: string;
  /** 还款单号 */
  repayment_doc_number?: string;
};
/**
 * 营销闭环需求详情-发放明细
 */
export type ClosedLoopReleaseDetail = {
  /** 主键Id */
  id?: number;
  /** 发放月份 */
  release_month?: string;
  /** 实际发放金额 */
  actual_release_amount?: number;
  /** 实际发放币种 */
  actual_release_currency?: string;
  /** 实际代扣代缴个税金额 */
  actual_withholding_tax_amount?: number;
};
/**
 * 营销闭环需求详情-三级单详情
 */
export type ClosedLoopCaseDetail = {
  /** 三级单ID */
  id?: number;
  /** 三级单ID */
  title?: string;
  /** 预付款金额 */
  pre_pay_amount?: number;
  /** 费用主体编号 */
  cost_company_code?: string;
  /** 费用主体名称 */
  cost_company_name?: string;
  /** 禁止废弃标识 */
  discard_forbid?: boolean;
  /** 活动商户号-供应商名称 */
  supplier_name?: string;
  /** 效果描述 */
  effect_description?: string;
  /** 实际发放金额 */
  actual_release_amount?: number;
  /** 实际发放币种 */
  actual_release_currency?: string;
  /** 实际代扣代缴个税金额 */
  actual_withholding_tax_amount?: number;
  /** 预计发放金额 */
  estimated_release_amount?: number;
  /** 预计代扣代缴个税金额 */
  estimated_withholding_tax_amount?: number;
  /** 借款结余金额 */
  borrow_amount_balance?: number;
  /** oa报销流程号 */
  oa_flow_id?: string;
  case_status?: ClosedLoopCaseState;
  /** 备注 */
  note?: string;
  reject_info?: RejectInfoVo;
  /** 是否可提交结案 */
  can_submit_case_closure?: boolean;
  /** 预付信息 */
  pre_pay_info_list?: ClosedLoopFeeControlDocInfo[];
  /** 付款信息 */
  payment_info_list?: ClosedLoopFeeControlDocInfo[];
  /** 发放明细 */
  release_details?: ClosedLoopReleaseDetail[];
  /** OA审批列表信息 */
  oa_audit_desc_list?: OaProcessInfoVo[];
  /** 活动执行附件 */
  billing_attachments?: ClosedLoopAttachment[];
  /** 结案信息附件 */
  report_attachments?: ClosedLoopAttachment[];
  /** 指标信息 */
  assessment_indicator_records?: ClosedLoopAssessmentIndicatorRecord[];
};
/**
 * SM预算信息（由 ClosedLoopSmBudgetGateway 在 starter 实现中填充）
 */
export type SmBudgetVo = object;
/**
 * 营销闭环需求详情
 */
export type ClosedLoopDemandDetailVo = {
  /** 小喇叭根文档节点 */
  root_id?: number;
  current_run_type?: ClosedLoopDemandType;
  /** 当前展示的单号id */
  current_id?: number;
  /** 流程状态 */
  flow_status?: string;
  /** 流程状态名 */
  flow_status_name?: string;
  permit_actions?: ClosedLoopPermitActions;
  /** 用于和前端渲染操作区域绑定 */
  bottom_tab?: ClosedLoopBottomTab[];
  /** 备注 */
  remark?: string;
  demand_detail?: ClosedLoopDemandDetail;
  activity_detail?: ClosedLoopActivityDetail;
  case_detail?: ClosedLoopCaseDetail;
  sm_budget?: SmBudgetVo;
};
export type AiResponseClosedLoopDemandDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ClosedLoopDemandDetailVo;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiResponseBiddingDetailsResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingDetailsResDto;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiBiddingSupplierHistoryForm = {
  /** 比选ID */
  bidding_id?: number;
  /** 供应商ID */
  supplier_id?: number;
};
export type AiResponseBiddingSupplierHisDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingSupplierHisDto;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiResponseListBiddingStateJournalResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingStateJournalResDto[];
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type AiExportBiddingListForm = {
  /** 主供应商ID */
  main_supplier_id?: number;
  /** 渠道供应商ID */
  channel_supplier_id?: number;
  /** 比选单号/标题 */
  bidding_id_or_name?: string;
  /** 产品ID列表 */
  product_ids?: number[];
  /** 比选状态列表 */
  states?: BiddingState[];
  /** 比选类型列表 */
  bidding_types?: BiddingTypeV2Enum[];
  /** 服务类型列表 */
  channel_types?: ChannelType[];
};
export type AiAttachmentDownloadUrlForm = {
  /** 附件ID（attachment ID 或 blob ID，取决于 blob 参数） */
  id: number;
  /** 是否按 blob ID 查询，默认 false；为 true 时 id 表示 blob ID，否则表示 attachment ID */
  blob?: boolean;
  /** 链接类型，传 preview 返回预览链接，否则返回下载链接 */
  download?: string;
};
export type AiResponseString = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: string;
  stacktrace?: string[];
  time?: number;
  schema?: FieldSchema[];
  trace_id?: string;
};
export type VoidForm = {
  advance_price_check_id?: number;
  void_reason?: string;
};
export type TriggerForm = {
  advance_price_check_id?: number;
};
/**
 * 提前核价-策划提交确认达人入参
 */
export type PlanSubmitForm = {
  /** 核价记录 id */
  advance_price_check_id: number;
  /** 本次新确认达人的明细 id 列表（必填且非空；必须属于本核价记录且未被确认过） */
  newly_confirmed_detail_ids: number[];
};
/**
 * 提前核价-策划提交确认达人响应
 */
export type PlanSubmitResp = {
  /** 本次提交后的 last_plan_submit_at，前端据此判断「关闭」按钮可用性 */
  last_plan_submit_at?: string;
};
export type ResponsePlanSubmitResp = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PlanSubmitResp;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 提前核价-策划暂存达人确认入参（kol-auto-pricing-enhance Task 2.7）
 */
export type PlanDraftForm = {
  /** 核价记录 id */
  advance_price_check_id: number;
  /** 本次勾选的明细 id 列表（允许为空即清空勾选；前端置灰的已确认明细会被服务端过滤） */
  draft_detail_ids?: number[];
};
/**
 * 提前核价-策划暂存达人确认响应
 */
export type PlanDraftResp = {
  /** 暂存时间 */
  update_at?: string;
};
export type ResponsePlanDraftResp = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PlanDraftResp;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 提前核价-策划关闭核价记录入参
 */
export type PlanCloseForm = {
  /** 核价记录 id */
  advance_price_check_id: number;
};
export type ListForm = {
  biz_id?: number;
  biz_type?: string;
  demand_id?: number;
  confirmed_anchor_name?: string;
};
/**
 * 提前核价状态：AI_CHECKING=智能核价中 / PENDING_CONFIRM=待商务确认核价 / PENDING_PLAN_CONFIRM_DARU=待策划确认核价 / CONFIRMED=已核价 / CLOSED=已关闭 / VOIDED=已作废
 */
export type AdvancePriceCheckStateEnum =
  | 'AI_CHECKING'
  | 'PENDING_CONFIRM'
  | 'PENDING_PLAN_CONFIRM_DARU'
  | 'CONFIRMED'
  | 'CLOSED'
  | 'VOIDED';
/**
 * 提前核价记录列表项
 */
export type AdvancePriceCheckVo = {
  /** 核价记录ID */
  id?: number;
  /** 核价记录单号（如 #123-886-01） */
  record_no?: string;
  /** 关联单据ID（比选单ID或二级需求单ID） */
  demand_id?: number;
  /** 关联单据类型：1=一级需求单 2=比选单 */
  demand_type?: number;
  /** 供应商ID */
  supplier_id?: number;
  /** 当前轮次（从1开始递增） */
  round?: number;
  state?: AdvancePriceCheckStateEnum;
  /** 上传文件名 */
  file_name?: string;
  /** 上传时间 */
  upload_at?: string;
  /** AI核价完成时间 */
  ai_check_at?: string;
  /** 商务确认核价备注 */
  confirm_remark?: string;
  /** 作废原因 */
  void_reason?: string;
  /** 该供应商历史核价记录id列表（按从小到大排序，包含最新一条） */
  history_price_check_ids?: number[];
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** 最近一次策划成功提交确认达人的时间；null=从未提交；前端以此判断'关闭'按钮是否可用 */
  last_plan_submit_at?: string;
  /** 已确认达人昵称列表（去重后取 advance_price_check_detail.plan_confirmed=true 的 kolNickname） */
  confirmed_anchor_names?: string[];
};
export type ResponseListAdvancePriceCheckVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: AdvancePriceCheckVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ExportForm = {
  advance_price_check_id?: number;
};
export type BasePriceConfigCmd = {
  source_name?: string;
  source_type?: string;
  base_price_type?: string;
  base_price_name?: string;
  base_price?: number;
  base_price_currency?: string;
};
export type PriceCheckRemarkCmd = {
  text?: string;
  files?: number[];
};
export type DetailResultCmd = {
  detail_id?: number;
  ai_price_check_result?: string;
  business_price_check_result?: string;
  bargaining_discount?: string;
  business_remark?: string;
  premium_ratio?: number;
  base_price_config?: BasePriceConfigCmd;
  business_price_check_remark?: PriceCheckRemarkCmd;
};
export type DraftForm = {
  advance_price_check_id?: number;
  detail_results?: DetailResultCmd[];
};
export type DraftResp = {
  update_at?: string;
};
export type ResponseDraftResp = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DraftResp;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DetailForm = {
  advance_price_check_id?: number;
  biz_id?: number;
  biz_type?: string;
};
export type AdvancePriceCheck = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  /** 关联一级单ID或比选单ID */
  demand_id?: number;
  /** 关联单据类型：1=一级需求单 2=比选单 */
  demand_type?: number;
  /** 供应商ID */
  supplier_id?: number;
  /** 核价记录单号，格式 #{sourceNo}-{anonymousSupplierCode}{round:02d}，示例 #123-886-01 */
  record_no?: string;
  /** 当前轮次（从1开始递增） */
  round?: number;
  /** 状态：AI_CHECKING=智能核价中 PENDING_CONFIRM=待商务确认核价 PENDING_PLAN_CONFIRM_DARU=待策划确认核价 CONFIRMED=已核价 CLOSED=已关闭 VOIDED=已作废 */
  state?: string;
  /** 上传Excel的文件blobId（关联attachment_blob表） */
  blob_id?: number;
  /** 上传文件名 */
  file_name?: string;
  /** 上传时间 */
  upload_at?: string;
  /** 上传人userId（供应商员工） */
  upload_by?: number;
  /** AI核价完成时间 */
  ai_check_at?: string;
  /** 策划最近一次成功提交确认达人的时间；CLOSED 前置判断（必须至少有一次提交） */
  last_plan_submit_at?: string;
  /** 商务确认核价备注 */
  confirm_remark?: string;
  /** 作废原因 */
  void_reason?: string;
  /** 创建人ID */
  created_by?: number;
  /** 更新人ID */
  updated_by?: number;
  /** 删除标识：0=正常，负数=已删除 */
  deleted_flag?: number;
  /** 该供应商历史核价记录id列表（按从小到大排序，包含最新一条） */
  history_price_check_ids?: number[];
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
/**
 * 商务核价备注
 */
export type PriceCheckRemarkResult = {
  text?: string;
  files?: DemandDetailsResDtoAttachmentDto[];
};
export type AdvancePriceCheckDetail = {
  id?: number;
  destroy?: boolean;
  created_at?: string;
  updated_at?: string;
  /** 关联 advance_price_check.id */
  advance_price_check_id?: number;
  /** 关联业务单据ID（比选单ID 或 方案综合评分法二级单ID） */
  biz_id?: number;
  /** 业务类型：BIDDING=比选单 DEMAND=方案综合评分法二级单 */
  biz_type?: string;
  /** 行序号（Excel中的顺序，从1开始） */
  row_index?: number;
  /** 达人昵称 */
  kol_nickname?: string;
  base_price_source_code?: string;
  /** 达人账号ID */
  kol_account_id?: string;
  /** 粉丝量（保留原始文本，如1.23W） */
  fans_count?: string;
  /** 主页链接 */
  home_page_url?: string;
  /** 达人类型 */
  kol_type?: string;
  /** 合作平台 */
  platform?: string;
  /** 官单/私单：官单/私单 */
  order_type?: string;
  /** 报价金额 */
  quote_price?: number;
  /** 报价币种，默认CNY */
  quote_currency?: string;
  /** 刊例价/裸价（官单时为刊例价，私单时为裸价） */
  list_price?: number;
  /** 档期（合并字段，如"2026-04-01至2026-04-30"） */
  schedule?: string;
  /** 是否能保15日CPM：1=是 0=否 */
  can_keep15day_cpm?: boolean;
  /** 考核指标 */
  check_indicator?: string;
  /** 考核指标预期值 */
  check_indicator_expected_value?: string;
  /** 分发平台（多个逗号分隔，如快手,B站） */
  distribute_platforms?: string;
  /** 是否可分发网易两件套：1=是 0=否 */
  can_distribute_easy?: boolean;
  /** 是否授权原生：1=是 0=否 */
  is_authorized_original?: boolean;
  /** 授权时间 */
  authorization_time?: string;
  /** 采买人数&采买数量：单人单次/单人多次/多人单次 */
  purchase_people_summary?: string;
  /** 合作数量 */
  cooperation_count?: number;
  /** 下单类型（直播/视频图文） */
  order_category?: string;
  /** 主平台报价项目 */
  main_platform_quote_item?: string;
  /** 官单私单code */
  public_order_code?: string;
  /** 供应商备注 */
  supplier_remark?: string;
  /** 基准价（AI核价时查询并写入） */
  base_price?: number;
  /** 基准价来源（如商单平台价/平台直客价/供应商底价） */
  base_price_source?: string;
  /** 基准价类型code */
  base_price_type?: string;
  /** 基准价类型名称 */
  base_price_name?: string;
  /** 基准价币种 */
  base_price_currency?: string;
  /** 溢价比（报价/基准价） */
  premium_ratio?: number;
  /** AI智能核价结果 */
  ai_price_check_result?: string;
  /** 商务核价结果（商务确认时填写） */
  business_price_check_result?: string;
  /** 议价优惠金额（达人维度，商务确认时填写，非必填） */
  bargaining_discount?: string;
  /** 商务核价备注（达人维度，非必填） */
  business_remark?: string;
  /** 策划是否确认本达人本轮核价：false=否 true=是；对应数据库 tinyint(1) */
  plan_confirmed?: boolean;
  /** 策划确认时间 */
  plan_confirmed_at?: string;
  /** 策划确认人 userId */
  plan_confirmed_by?: number;
  /** 创建人ID */
  created_by?: number;
  /** 更新人ID */
  updated_by?: number;
  /** 删除标识：0=正常，负数=已删除 */
  deleted_flag?: number;
  /** 刊例价截图附件列表 */
  list_price_screenshots?: DemandDetailsResDtoAttachmentDto[];
  /** 裸价截图附件列表 */
  floor_price_screenshots?: DemandDetailsResDtoAttachmentDto[];
  business_price_check_remark?: PriceCheckRemarkResult;
  fields_value_null?: SFunctionBaseEntityLongObject[];
};
export type AdvancePriceCheckDetailVo = {
  header?: AdvancePriceCheck;
  details?: AdvancePriceCheckDetail[];
  last_draft_time?: string;
  permit_actions?: PermitActions;
};
export type ResponseAdvancePriceCheckDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: AdvancePriceCheckDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ConfirmForm = {
  advance_price_check_id?: number;
  confirm_remark?: string;
  detail_results?: DetailResultCmd[];
};
export type GetBasePriceByUrlForm = {
  url: string;
  count?: number;
  content_type?: number;
};
export type SubmitFeeDto = {
  ids?: number[];
};
/**
 * 易费控提交结果
 */
export type FeeControlSubmitResVo = {
  /** 单号 */
  doc_number?: string;
  /** 费控链接 */
  fee_control_url?: string;
  /** 信息 */
  msg?: string;
};
export type ResponseFeeControlSubmitResVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: FeeControlSubmitResVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type StartRequirementForm = {
  requirement_number?: string;
  parent_requirement_number?: string;
  top_requirement_number?: string;
  old_requirement_number?: string;
  old_parent_requirement_number?: string;
  old_top_requirement_number?: string;
  requirement_group?: string;
  requirement_group_name?: string;
  requirement_type1?: string;
  requirement_type1_name?: string;
  requirement_type2?: string;
  requirement_type2_name?: string;
  requirement_type3?: string;
  requirement_type3_name?: string;
  requirement_type4?: string;
  requirement_type4_name?: string;
  requirement_name?: string;
  requirement_description?: string;
  project_number?: string;
  product_number?: string;
  pre_requisition_number?: string;
  biz_amount?: string;
  biz_currency_code?: string;
  start_type?: string;
  status?: string;
  create_by?: string;
  responsible_by?: string[];
  date_from?: string;
  date_to?: string;
  supplier_name?: string;
  contract_number?: string;
  no_contract_flag?: boolean;
  is_split?: number;
  effect_description?: string;
  auto_confirm_performance_plan?: boolean;
  acc_entity_code?: string;
  background?: string;
  expectation?: string;
  detail?: string;
  tax_rate?: string;
  purchaser?: number;
};
export type StartRequirementDto = {
  budget_check?: number;
};
export type ResponseStartRequirementDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: StartRequirementDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type RequirementId = {
  requirement_number?: string;
  old_requirement_number?: string;
};
export type FeeControlDocInvoiceAmount = {
  corp_invoice_key?: string;
  requirement_number?: string;
  use_invoice_amount?: number;
};
export type ReimburseRequirementForm = {
  requirement_number_list?: RequirementId[];
  dspf_requirement_number_list?: string[];
  parent_requirement_number?: string;
  top_requirement_number?: string;
  requirement_name?: string;
  reimbursement_description?: string;
  project_number?: string;
  prerequisition_number?: string;
  biz_amount?: string;
  biz_currency_code?: string;
  attachment?: string;
  create_by?: string;
  responsible_by?: string[];
  remark?: string;
  requirement_group?: string;
  doc_invoice_amoun_list?: FeeControlDocInvoiceAmount[];
  contract_number?: string;
  paperless_reimb_flag?: string;
  attachment_id_list?: string[];
};
export type ReimburseRequirementDto = {
  reimbursement_number?: string;
  msg_list?: string[];
  submitted?: boolean;
};
export type ResponseReimburseRequirementDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ReimburseRequirementDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ChangeOwnerForm = {
  requirement_number?: string;
  old_requirement_number?: string;
  requirement_group?: string;
  owners?: string[];
};
export type DropRequirementForm = {
  requirement_number?: string;
  old_requirement_number?: string;
  requirement_group?: string;
  create_by?: string;
  drop_reason?: string;
};
export type RequirementConfirmChangeReq = {
  old_requirement_number?: string;
  old_parent_requirement_number?: string;
  old_top_requirement_number?: string;
  requirement_number?: string;
  parent_requirement_number?: string;
  top_requirement_number?: string;
  requirement_group?: string;
  biz_amount?: number;
  update_by?: string;
  change_reason?: string;
};
export type RequirementConfirmCheckReq = {
  requirement_number?: string;
  parent_requirement_number?: string;
  top_requirement_number?: string;
  old_requirement_number?: string;
  old_parent_requirement_number?: string;
  old_top_requirement_number?: string;
  requirement_group?: string;
  requirement_group_name?: string;
  requirement_type1?: string;
  requirement_type1_name?: string;
  requirement_type2?: string;
  requirement_type2_name?: string;
  requirement_type3?: string;
  requirement_type3_name?: string;
  requirement_type4?: string;
  requirement_type4_name?: string;
  requirement_name?: string;
  requirement_description?: string;
  project_number?: string;
  pre_requisition_number?: string;
  biz_amount?: number;
  biz_currency_code?: string;
  start_type?: string;
  status?: string;
  create_by?: string;
  date_from?: string;
  date_to?: string;
  supplier_name?: string;
  contract_number?: string;
  is_split?: number;
};
export type CloseRequirementForm = {
  requirement_number?: string;
  old_requirement_number?: string;
  requirement_group?: string;
  create_by?: string;
};
export type ChangeProductLineForm = {
  requirement_number?: string;
  old_requirement_number?: string;
  requirement_group?: string;
  product_line_id?: number;
  product_line_name?: string;
};
export type RateQueryForm = {
  from_currency?: string;
  to_currency?: string;
  conversion_period?: string;
  page_no?: number;
  page_size?: number;
};
export type RateQueryDto = {
  from_currency?: string;
  to_currency?: string;
  conversion_type?: string;
  conversion_period?: string;
  conversion_rate_char?: string;
};
export type PageDtoRateQueryDto = {
  page_no?: number;
  page_size?: number;
  total?: number;
  data?: RateQueryDto[];
};
export type ResponsePageDtoRateQueryDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageDtoRateQueryDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ProductLineQueryDto = {
  product_line_code?: string;
  product_line_id?: number;
  product_line_name?: string;
};
export type PageDtoProductLineQueryDto = {
  page_no?: number;
  page_size?: number;
  total?: number;
  data?: ProductLineQueryDto[];
};
export type ResponsePageDtoProductLineQueryDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageDtoProductLineQueryDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type PreRequisitionListQueryForm = {
  page_no?: number;
  page_size?: number;
  project_number?: string;
};
export type PreRequisitionQueryDto = {
  prerequisition_id?: number;
  prerequisition_number?: string;
  project_number?: string;
  description?: string;
  biz_currency_code?: string;
  biz_amount?: string;
  available_amount?: string;
};
export type PageDtoPreRequisitionQueryDto = {
  page_no?: number;
  page_size?: number;
  total?: number;
  data?: PreRequisitionQueryDto[];
};
export type ResponsePageDtoPreRequisitionQueryDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageDtoPreRequisitionQueryDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type FeeControlCloseDto = {
  id?: number;
};
export type ContractQueryDto = {
  contract_num?: string;
  contract_id?: number;
  contract_name?: string;
  salesman_code?: string;
  currency?: string;
  limit_amount?: string;
  opposite_company_names?: string;
  pay_amount?: string;
};
export type PageDtoContractQueryDto = {
  page_no?: number;
  page_size?: number;
  total?: number;
  data?: ContractQueryDto[];
};
export type ResponsePageDtoContractQueryDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageDtoContractQueryDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * MPC单状态（删除、废弃等）
 */
export type MpcStatus = 'DELETE' | 'DEPRECATED' | 'RETURNED' | 'NEW' | 'SUBMIT';
export type SyncCommand = {
  /** 数易效果单单号 */
  dspf_mpc_no?: string;
  mpc_status?: MpcStatus;
  /** MPC单号 */
  requirement_id?: number;
  /** 需求标题 */
  requirement_subject?: string;
  /** 总需求单号 */
  root_requirement_id?: number;
  /** 所属业务模块 */
  requirement_group?: string;
  /** 项目编号 */
  project_number?: string;
  /** 产品编号 */
  product_number?: string;
  /** 需求预算（原币） */
  budget_price?: string;
  /** 币种 */
  budget_currency?: string;
  /** 预计开始日期 */
  expect_begin_at?: string;
  /** 预计完成日期 */
  expect_end_at?: string;
  /** 报价金额 */
  quote_price?: string;
  /** 报价币种 */
  quote_currency?: string;
  /** 最终结算金额 */
  settle_price?: string;
  /** 需求人员 */
  project_principals?: string[];
  /** 创建人 */
  creator?: string;
  /** 创建时间 */
  created_at?: string;
  /** 采购方式 */
  purchase_way?: string;
  /** 服务得分 */
  service_score?: string;
  /** 是否预绑定效果数据单： 0或null - 否; 1：是 */
  pre_bound?: number;
  /** 考核效果标准，方便荷鲁斯之后对整体的广告投放效果进行统计,json格式的字符串, 参考如下geneKpi的方法 */
  kpi?: string;
  /** 一级分类,必填 */
  class1?: number;
  /** 二级分类，必填 */
  class2?: number;
  /** 三级分类，必填 */
  class3?: number;
  /** 四级分类，选填 */
  class4?: number;
  kpi_type?: MediaKpiType;
};
/**
 * 我的工作台-预付流程-待办 筛选表单
 */
export type OsWorkspacePrepaymentTodoListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  /** 预付单号/来源单号/需求标题(模糊查询) */
  search_text?: string;
  /** 预付单状态, 多选 */
  status_list?: number[];
  /** 发票方式, 多选: 1-有票预付 2-无票预付 */
  invoice_method_list?: number[];
  /** 所属产品, 多选 */
  product_id_list?: number[];
  /** 需求分类ID, 多选（关联需求单的业务分类） */
  demand_category_ids?: number[];
};
/**
 * 工作台-预付流程列表项
 */
export type OsWorkspacePrepaymentListVo = {
  /** 预付单ID */
  id?: number;
  /** 预付单号 */
  prepayment_no?: string;
  /** 状态 */
  status?: string;
  /** 单号 */
  demand_id?: number;
  /** 预付状态 */
  status_desc?: string;
  /** 发票方式: 1-有票预付 2-无票预付 */
  invoice_method?: number;
  /** 所属产品ID */
  product_id?: number;
  /** 所属产品 */
  product_code?: string;
  /** 需求标题 */
  demand_title?: string;
  /** 需求类型 */
  category_names?: string;
  /** 发票方式 */
  invoice_method_desc?: string;
  /** 供应商ID */
  supplier_id?: number;
  /** 供应商名称 */
  supplier_name?: string;
  /** 预付金额 */
  prepayment_amount?: number;
  /** 币种 */
  currency?: string;
  /** OA流程号 */
  oa_process_no?: string;
  oa_status?: OaState;
  /** OA链接 */
  oa_url?: string;
  /** 创建人ID */
  creator_id?: number;
  /** 创建人姓名 */
  creator_name?: string;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** 待办类型描述(用于待办列表) */
  todo_type_desc?: string;
  permit_actions?: PermitActions;
  operation?: string;
};
/**
 * 分页结果
 */
export type PageResultOsWorkspacePrepaymentListVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: OsWorkspacePrepaymentListVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultOsWorkspacePrepaymentListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultOsWorkspacePrepaymentListVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商侧需求单状态
 */
export type OsWorkspaceToDoTypeEnum =
  | 'PENDING_QUOTE'
  | 'MAKING'
  | 'SETTLED_UPLOAD'
  | 'ALL_DEMAND';
/**
 * 供应商侧需求单状态筛选条件（可多选）
 */
export type OutsourceWorkbenchTypeEnum =
  | 'PENDING_QUOTE'
  | 'NOT_QUOTED'
  | 'REJECT'
  | 'CONFIRM_QUOTE'
  | 'NOT_SELECTED'
  | 'MAKING'
  | 'REVIEW_QUOTE'
  | 'SETTLED'
  | 'SETTLED_UPLOAD'
  | 'PAYING'
  | 'PAYED'
  | 'SELECTED'
  | 'CANCELED'
  | 'MY_ADVANCE'
  | 'COMPLETED';
/**
 * 供应商侧-我的工作台-常规采购-待办 筛选表单
 */
export type OsWorkspaceDemandTodoListForm = {
  current?: number;
  limit?: number;
  sort_param?: SortParam;
  todo_type: OsWorkspaceToDoTypeEnum;
  /** 供应商侧需求单状态筛选条件（可多选） */
  state_list?: OutsourceWorkbenchTypeEnum[];
  /** 需求单ID/名称(模糊查询) */
  search_text?: string;
  /** 产品, id, 多选 */
  product?: number[];
  /** 需求单类型, 多选 */
  demand_categories?: number[];
  /** 报价截止时间 */
  quote_deadline_range?: string[];
  /** 预计发布时间时间 */
  expect_end_at_range?: string[];
  /** 是否回退 */
  backed?: boolean;
};
/**
 * 供应商侧-我的工作台-需求单-待办
 */
export type OsWorkspaceDemandTodoVo = {
  id?: number;
  /** 需求单号 */
  plan_demand_id?: number;
  depth?: number;
  state?: MatchOutsourceStatusEnum;
  /** 外包侧需求单状态名称 */
  state_text?: string;
  /** 所属产品 */
  product_identifier?: string;
  /** 需求标题 */
  name?: string;
  /** 是否退回 */
  backed?: boolean;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 二级分类名称 */
  l2_category_name?: string;
  /** 需求类型(如果需求一级单，显示需求类型一级；需求二级单/三级单显示需求类型1-2-3-4) */
  category_names?: string;
  purchasing_method?: PurchasingMethodEnum;
  purchasing_strategy?: PurchasingStrategyEnum;
  /** 购买方式 */
  purchasing_method_text?: string;
  /** 报价金额 */
  quote_price?: number;
  quote_price_currency?: CurrencyEnum;
  /** 操作 */
  operation?: string;
  permit_actions?: PermitActions;
};
/**
 * 分页结果
 */
export type PageResultOsWorkspaceDemandTodoVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: OsWorkspaceDemandTodoVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultOsWorkspaceDemandTodoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultOsWorkspaceDemandTodoVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type OutsourceWorkbenchConditionsForm = {
  /** 查询条件（id或者名称） */
  search_text?: string;
  /** 优先级 */
  priorities?: PriorityEnum[];
  /** 产品列表 */
  product?: number[];
  demand_plan_users?: number[];
};
/**
 * 工作台表单
 */
export type OutsourceWorkbenchForm = {
  current?: number;
  size?: number;
  order_field?: string;
  order_direction?: string;
  conditions?: OutsourceWorkbenchConditionsForm;
  state: OutsourceWorkbenchTypeEnum;
  platform_type?: PlatformTypeEnum;
  /** 是否导出 */
  export?: boolean;
};
/**
 * 分页数据
 */
export type BaseOutsourceWorkbenches = {
  id?: number;
  name?: string;
  expect_end_at?: string;
  operation?: string;
  source_id?: number;
  source_type?: string;
  category_names?: string;
};
/**
 * 分页结果
 */
export type PageResultBaseOutsourceWorkbenches = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: BaseOutsourceWorkbenches[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultBaseOutsourceWorkbenches = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultBaseOutsourceWorkbenches;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type OutsourceSetCurrentForm = {
  /** 供应商ID */
  supplier_id: number;
};
export type RubyServiceInfo = {
  id?: number;
  customer_name?: string;
  case_digest?: string;
  netease_case_digest?: string;
  total_employee?: string;
  recommend_group_id?: string;
  referrer_id?: number;
  recommend_reason?: string;
  supplier_id?: number;
  recommend_group_name?: string;
  referrer_name?: string;
  referrer_email?: string;
};
export type RubyBankInfo = {
  id?: number;
  bank_name?: string;
  bank_number?: string;
  invoice_type?: string;
  supplier_id?: number;
  translated_invoice_type?: string;
};
export type RubyAuditUser = {
  id?: number;
  user_id?: number;
  audit_at?: string;
  remark?: string;
  supplier_id?: number;
  user_name?: string;
};
export type RubySupplierEmployee = {
  id?: number;
  status?: string;
  supplier_id?: number;
  user_email?: string;
  user_id?: number;
  user_login?: string;
  user_name?: string;
  user_popo?: string;
  user_status?: string;
};
export type RubySupplierAudit = {
  id?: number;
  supplier_id?: number;
  audit_item_id?: number;
  status?: boolean;
};
export type RubyBlob = {
  id?: number;
  filename?: string;
  byte_size?: number;
  content_type?: string;
};
export type RubyAttach = {
  id?: number;
  blob?: RubyBlob;
};
export type RubySupplierAttach = {
  id?: number;
  name?: string;
  status?: string;
  blob_id?: number;
  supplier_id?: number;
  updated_at?: string;
  cert_attach?: RubyAttach;
  created_at?: string;
};
export type RubySupplierContact = {
  id?: number;
  name?: string;
  job_title?: string;
  email?: string;
  telephone?: string;
  supplier_id?: number;
  contact_type?: string;
  translated_contact_type?: string;
};
export type RubyAuditItem = {
  id?: number;
  parent_cate?: string;
  cate?: string;
  position?: number;
  info?: string;
  category?: number;
  permit_edit?: boolean;
};
export type Option = {
  name?: string;
  value?: object;
};
export type UserOption = {
  id?: number;
  name?: string;
  email?: string;
};
export type RubyOptions = {
  service_types?: Option[];
  statuses?: Option[];
  use_types?: Option[];
  temporary?: Option[];
  whether_former_employees?: Option[];
  advance_payments?: Option[];
  invoice_types?: Option[];
  contact_types?: Option[];
  supplier_types?: Option[];
  regions?: Option[];
  users?: UserOption[];
  parent_companies?: Option[];
  currencies?: Option[];
};
export type RubySupplier = {
  id?: number;
  source_id?: number;
  source_type?: string;
  full_name?: string;
  short_name?: string;
  service_type?: string;
  advance_payment?: string;
  address?: string;
  parent_company?: string;
  status?: string;
  mpc_no?: string;
  region?: string;
  service_content?: string;
  created_at?: string;
  updated_at?: string;
  supplier_type?: string;
  category?: number;
  enterprise_risk_query?: string;
  temporary?: boolean;
  use_type?: number;
  whether_former_employees?: boolean;
  translated_service_type?: string;
  translated_advance_payment?: string;
  translated_status?: string;
  permit_buttons?: {
    [key: string]: any;
  };
  translated_supplier_type?: string;
  translated_use_type?: string;
  service_type_lv1?: string;
  translated_service_type_lv1?: string;
  currency?: string;
  translated_currency?: string;
  service_info?: RubyServiceInfo;
  bank_info?: RubyBankInfo;
  audit_user?: RubyAuditUser;
  supplier_employees?: RubySupplierEmployee[];
  supplier_resources?: SupplierResource[];
  supplier_audits?: RubySupplierAudit[];
  supplier_attaches?: RubySupplierAttach[];
  supplier_contacts?: RubySupplierContact[];
  audit_items?: RubyAuditItem[];
  options?: RubyOptions;
};
export type ResponseRubySupplier = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: RubySupplier;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type OutsourceLoginForm = {
  /** 登录账号 */
  login: string;
  /** 密码 */
  password: string;
  /** 是否记住登录状态 */
  keep_login?: boolean;
  /** 是否已加密 */
  encrypted?: boolean;
  /** 验证码信息 */
  _rucaptcha?: string;
};
export type OutsourceResetPasswordForm = {
  /** 重置密码唯一key */
  key?: string;
  /** 新密码 */
  password?: string;
  /** 确认新密码 */
  password_confirmation?: string;
  /** 是否已加密 */
  encrypted?: boolean;
};
/**
 * 校验结果 - 异常信息
 */
export type OutsourcePasswordCheckErrorInfoDto = {
  /** 旧密码 - 校验的异常信息 */
  old_password?: string[];
  /** 新密码 - 校验的异常信息 */
  password?: string[];
  /** 确认新密码 - 校验的异常信息 */
  password_confirmation?: string[];
};
export type OutsourceResetPasswordResultDto = {
  messages?: OutsourcePasswordCheckErrorInfoDto;
};
export type ResponseOutsourceResetPasswordResultDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceResetPasswordResultDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type OutsourceModifyPasswordForm = {
  /** 旧密码 */
  old_password?: string;
  /** 新密码 */
  password?: string;
  /** 确认新密码 */
  password_confirmation?: string;
  /** 是否已加密 */
  encrypted?: boolean;
};
export type OutsourceModifyPasswordResultDto = {
  messages?: OutsourcePasswordCheckErrorInfoDto;
};
export type ResponseOutsourceModifyPasswordResultDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceModifyPasswordResultDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type OutsourceForgetPasswordForm = {
  /** 登录账号 */
  login: string;
  /** 账号关联的邮箱 */
  email: string;
};
export type OutsourceCheckResetLinkForm = {
  /** 重置密码链接中的唯一key，该key具有有效期 */
  key: string;
};
/**
 * 发票附件
 */
export type PrepaymentInvoiceDto = {
  /** 发票ID，M税务系统生成的发票ID，同一张发票被删除重新生成，ID会不同 */
  invoice_id?: number;
  /** 发票代码 */
  invoice_code?: string;
  /** 发票类型 */
  invoice_type?: string;
  /** 发票号码 */
  invoice_no?: string;
  /** 发票含税总金额 */
  amount_with_tax?: number;
  /** 发票不含税金额 */
  amount_without_tax?: number;
  /** 发票税额 */
  tax?: number;
  /** 发票上传完成后生成得到的blobId */
  blob_id?: number;
};
/**
 * 发票上传
 */
export type PrepaymentInvoiceUploadCmd = {
  /** 需求单ID */
  demand_id: number;
  /** 预付单ID */
  prepayment_id: number;
  invoice_belong_area: InvoiceBelongAreaEnum;
  /** 是否海外invoice */
  is_oversea_invoice: boolean;
  /** 发票附件 */
  invoice_list: PrepaymentInvoiceDto[];
};
/**
 * 附件任务
 */
export type AttachmentTask = {
  /** 文件名 */
  file_name?: string;
  /** 文件大小（字节） */
  file_size?: number;
  /** 上传后的文件key/token */
  file_key?: string;
  /** 文件类型/MIME */
  content_type?: string;
  /** 子目录名称（用于文件夹嵌套） */
  folder_name?: string;
};
/**
 * 供应商资源发布请求
 */
export type MuseResourcePublishForm = {
  /** 需求单ID（三级单） */
  demand_id: number;
  /** 合作内容序号（indexId，如 1, 2, 3） */
  cooperate_index: number;
  /** 一级标品code */
  standard_code1: string;
  /** 二级标品code */
  standard_code2: string;
  /** 作品名称（不填则自动生成） */
  name?: string;
  /** 附件任务列表（前端上传后返回的文件信息） */
  attachment_task_list?: AttachmentTask[];
};
/**
 * 供应商资源发布结果
 */
export type MuseResourcePublishResultVo = {
  /** 资源 ID */
  resource_id?: string;
  /** 资源编码 */
  asset_code?: string;
  /** 资源详情页链接 */
  detail_url?: string;
};
export type ResponseMuseResourcePublishResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseResourcePublishResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 邀请协作人员表单
 */
export type MuseCollaboratorInviteForm = {
  /** 需求单ID */
  demand_id: number;
  /** 设计师邮箱 */
  email: string;
};
/**
 * 暂存/保存结构化结案请求入参
 */
export type OutsourceSaveStructuredSettleReportsDataForm = {
  /** 需求单ID */
  demand_id?: number;
  /** 供应商结算报告，报价行维度 */
  quote_settle_reports?: DemandSupplierQuoteSettleReportDto[];
};
/**
 * 操作类型
 */
export type OutsourceQuoteTableOperateTypeEnum = 'TEMP_SAVE' | 'SAVE';
/**
 * 暂存/保存报价表格数据请求入参
 */
export type OutsourceSaveQuoteTableDataForm = {
  operate_type?: OutsourceQuoteTableOperateTypeEnum;
  /** 需求单ID */
  demand_id?: number;
  /** 供应商ID */
  supplier_id?: number;
  /** 报价实际数据 */
  data?: {
    [key: string]: any;
  }[];
};
export type OutsourceSaveQuoteTableResultDto = {
  /** 附件信息 */
  cooperate_quote_infos?: {
    [key: string]: any;
  };
};
export type ResponseOutsourceSaveQuoteTableResultDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceSaveQuoteTableResultDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 是否隐藏发票上传模块
 */
export type PrepaymentHideInvoiceCmd = {
  /** 需求单ID */
  demand_id: number;
  /** 最终结算金额 */
  settle_price: number;
};
/**
 * 导出报价表格数据请求入参
 */
export type ExportQuoteTableDataForm = {
  /** 需求单ID */
  demand_id?: number;
  /** 供应商ID */
  supplier_id?: number;
  /** 当前填写的报价数据 */
  data?: {
    [key: string]: any;
  }[];
  /** 是否导出空模板 */
  export_empty_template?: boolean;
};
export type OutsourceDemandHisDetailResDto = {
  demand_id?: number;
  permit_actions?: PermitActions;
  /** 供应商详情 */
  demand_suppliers?: DemandSupplierHisDto[];
  demand_plan_info?: JournalDemandDetailsResDtoDemandPlanInfoDto;
};
export type ResponseOutsourceDemandHisDetailResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceDemandHisDetailResDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 状态节点
 */
export type OutsourceDemandDetailsResDtoBottomTab = {
  tab_name?: string;
  value?: string;
};
/**
 * 评估得分
 */
export type OutsourceDemandDetailsResDtoEvaluateScoreDto = {
  name?: string;
  real_score?: number;
};
export type OutsourceDemandDetailsResDtoSuperiorResourceShipDto = {
  id?: number;
  superior_resource_id?: number;
  resourceable_type?: string;
  resourceable_id?: number;
  name?: string;
};
export type OutsourceDemandDetailsResDtoCooperateAccountDto = {
  id?: number;
  /** 序号 */
  index_id?: number;
  demand_plan_info_id?: number;
  account?: string;
  remark?: string;
  superior_resource_ships?: OutsourceDemandDetailsResDtoSuperiorResourceShipDto[];
  screen_shot_info_error_tags?: string[];
  /** 合作内容属性值 */
  property_values?: DemandCooperatePropertyValueDto[];
  /** 【结构化】kpi指标列表 */
  kpi_values?: DemandKpiValueDto[];
  /** 内容上传超时时间标签 */
  delay_label?: string;
  /** 超时级别，0，1，2 */
  delay_grade?: number;
  /** 必要权益列表 */
  necessary_rights?: DemandCooperateRightsValueDto[];
  /** 期望权益列表 */
  expect_rights?: DemandCooperateRightsValueDto[];
  /** 标品属性值 */
  standard_catalog_values?: DemandStandardCatalogValueDto[];
  /** 是否需包含AI */
  need_ai?: boolean;
};
export type OutsourceDemandDetailsResDtoDemandPlannerDto = {
  demand_plan_info_id?: number;
  user_name?: string;
};
export type OutsourceDemandDetailsResDtoBlobDto = {
  id?: number;
  /** 文件名 */
  filename?: string;
  /** 文件大小 */
  byte_size?: number;
  /** 文件类型 */
  content_type?: string;
  key?: string;
  /** 是否自动生成 */
  auto_generated?: boolean;
};
/**
 * 签章结算单附件
 */
export type OutsourceDemandDetailsResDtoAttachmentDto = {
  id?: number;
  blob?: OutsourceDemandDetailsResDtoBlobDto;
};
/**
 * 策划单详情
 */
export type OutsourceDemandDetailsResDtoDemandPlanInfoDto = {
  id?: number;
  demand_id?: number;
  publish_date?: string;
  /** 开始时间 */
  begin_at?: string;
  /** 结束时间 */
  end_at?: string;
  description?: string;
  translated_plan_type?: string;
  /** 整单验收要求 */
  accept_desc?: string;
  /** 是否需全部报价 */
  need_all_quote?: boolean;
  ad_settle_way?: string;
  ad_settle_way_name?: string;
  category_names?: string;
  cooperate_accounts?: OutsourceDemandDetailsResDtoCooperateAccountDto[];
  demand_planners?: OutsourceDemandDetailsResDtoDemandPlannerDto[];
  plan_attachs?: OutsourceDemandDetailsResDtoAttachmentDto[];
  /** 【结构化】【内容营销】指标列表 */
  common_kpi_values?: DemandKpiValueDto[];
};
export type OutsourceDemandDetailsResDtoDemandSupplierQuoteDto = {
  id?: number;
  /** demand_supplier_id */
  demand_supplier_id?: number;
  /** cooperate_account_id */
  cooperate_account_id?: number;
  /** quote_price */
  quote_price?: number;
  currency?: CurrencyEnum;
  /** winning */
  winning?: boolean;
  /** requote_price */
  requote_price?: number;
  translated_currency?: string;
  program_desc?: string;
  last_round_price?: number;
  /** last_quote_price */
  last_quote_price?: number;
  /** 报价金额（优惠前、不含税） */
  quote_price_no_tax?: number;
  /** 报价金额(优惠前、含税和服务费) */
  quote_price_with_tax?: number;
  /** 报价金额(优惠后、不含税) */
  discount_quote_price_no_tax?: number;
  /** 报价金额(优惠后、含税和服务费) */
  discount_quote_price_with_tax?: number;
  /** 报价详情附件 */
  quote_attachments?: OutsourceDemandDetailsResDtoAttachmentDto[];
  /** 合作内容维度的结案报告附件列表 */
  case_close_report_cooperate_account_attachments?: OutsourceDemandDetailsResDtoAttachmentDto[];
  /** 是否需要实际执行 */
  need_actual_execution?: boolean;
  /** 是否含差旅餐补等实报实销，1=是，0=否 */
  contain_travel_expenses_reimbursement?: boolean;
  /** 是否星图度量测试，true=是，false=否 */
  xingtu_measure_test?: boolean;
  /** 抖音分发是否星图度量，true=是，false=否 */
  douyin_distribute_xingtu_measure?: boolean;
  /** 供应商提报的结算金额 */
  supplier_settle_price?: number;
  /** 期望权益 */
  expect_rights?: string;
  /** 报价模板快照，到标品维度 */
  template_detail?: string;
  /** 报价是否含AI */
  quote_contain_ai?: boolean;
  /** AI渗透率 */
  ai_penetration_rate?: number;
  /** AI降本率 */
  ai_cost_reduction_rate?: number;
};
/**
 * 供应商详情
 */
export type OutsourceDemandDetailsResDtoDemandSupplierDto = {
  id?: number;
  /** supplier_id */
  supplier_id?: number;
  /** advance_info */
  advance_info?: string;
  /** contract_no */
  contract_no?: string;
  /** is_self_contact */
  is_self_contact?: boolean;
  /** turns_number */
  turns_number?: number;
  /** overall_score */
  overall_score?: number;
  contract_method?: DemandContractMethodEnum;
  /** 合并报价附件 */
  merge_quote_attachs?: OutsourceDemandDetailsResDtoAttachmentDto[];
  demand_supplier_quotes?: OutsourceDemandDetailsResDtoDemandSupplierQuoteDto[];
  last_round_quotes?: OutsourceDemandDetailsResDtoDemandSupplierQuoteDto[];
  bid_attachs?: OutsourceDemandDetailsResDtoAttachmentDto[];
  /** 报价类型:0=不含税金额；1=含税金额 */
  quote_type?: number;
  /** 税率 */
  tax_rate?: number;
  /** 是否收取服务费,0=不收取,1=收取 */
  service_fee_mode?: number;
  /** 服务费率 */
  service_fee_rate?: number;
  /** 是否内部供应商 */
  inner_supplier?: boolean;
  /** 是否重新报价 */
  re_quotation_flag?: boolean;
  /** 重新报价原因 */
  re_quotation_reason?: string;
  /** 拒单原因 */
  reason?: string;
  /** 是否被供应商拒单 */
  reject_by_supplier?: boolean;
  invoice_type?: InvoiceType;
  invoice_belong_area?: InvoiceBelongAreaEnum;
  /** 是否海外发票,1=海外发票,0=非海外发票 */
  is_oversea_invoice?: number;
  outsource_state?: MatchOutsourceStatusEnum;
  /** 是否被退回 */
  return_flag?: boolean;
  channel_supplier_bank_dto?: ChannelSupplierBankDto;
  /** no_contract_flag */
  no_contract_flag?: boolean;
  /** 报价附件的有效标识 */
  quote_attachment_valid_flag?: boolean;
  /** 发票国家/地区名称 */
  invoice_belong_area_name?: string;
  /** 发票类型名称 */
  invoice_type_name?: string;
};
export type OutsourceDemandDetailsResDtoDemandAdvertisingDto = {
  id?: number;
  ad_settle_way?: string;
  demand_id?: number;
  push_fee_quote?: boolean;
  final_settle_price?: number;
  final_settle_currency?: CurrencyEnum;
  translated_final_settle_currency?: string;
  dspf_settle_price?: number;
  dspf_settle_currency?: CurrencyEnum;
  translated_dspf_settle_currency?: string;
  dspf_offer_price?: number;
  dspf_offer_currency?: CurrencyEnum;
  translated_dspf_offer_currency?: string;
  fee_start_date?: string;
  fee_end_date?: string;
};
/**
 * Muse 归档状态
 */
export type MuseArchiveStatusEnum =
  | 'ARCHIVED_PENDING_SYNC'
  | 'ARCHIVE_COMPLETED';
/**
 * 需求详情额外字段
 */
export type OutsourceDemandDetailsResDtoRootDemandExtColumnDto = {
  purchasing_method?: PurchasingMethodEnum;
  purchasing_strategy?: PurchasingStrategyEnum;
  /** 采购方式展示字符串 */
  translated_purchasing_method?: string;
};
/**
 * 历史已完成预付单信息
 */
export type PrepaymentHisDetail = {
  /** 预付单主键 */
  id?: number;
  /** 预付单编号 */
  prepayment_no?: string;
  /** 预付单状态: 1-待发起 2-发票待上传 3-审批中 4-付款中 5-已完成 6-已废弃 */
  status?: number;
  /** 预付单状态描述 */
  status_desc?: string;
  /** 发票方式: 1-有票预付 2-无票预付 */
  invoice_method?: number;
  /** 发票方式描述 */
  invoice_method_desc?: string;
  /** 预付金额 */
  prepayment_amount?: number;
  currency?: CurrencyEnum;
};
export type OutsourceDemandDetailsResDto = {
  /** 主键 */
  id?: number;
  /** 一级单ID */
  root_id?: number;
  /** 是否支持绑定执行数据 */
  support_bind_execution_data?: boolean;
  /** 是否启用结构化数据 */
  structured?: boolean;
  /** name */
  name?: string;
  /** expect_begin_at */
  expect_begin_at?: string;
  /** expect_end_at */
  expect_end_at?: string;
  priority?: PriorityEnum;
  state?: DemandState;
  /** 子状态 */
  sub_state?: string;
  desc?: string;
  /** 结算金额 */
  settle_price?: string;
  settle_currency?: CurrencyEnum;
  /** 报价金额 */
  quote_price?: string;
  quote_currency?: CurrencyEnum;
  budget_currency?: CurrencyEnum;
  remark?: string;
  finished?: boolean;
  /** depth */
  depth?: number;
  /** 作者名称 */
  author_name?: string;
  /** 状态名称 */
  human_state_name?: string;
  /** 子状态名称 */
  human_sub_state_name?: string;
  /** 优先级 */
  translated_priority?: string;
  /** 状态节点 */
  bottom_tabs?: OutsourceDemandDetailsResDtoBottomTab[];
  /** 是否可以被修改 */
  can_modify?: boolean;
  /** 层级名称（用-连接） */
  category_names?: string;
  /** 一级分类名称 */
  l1_category_name?: string;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 三级分类ID */
  l3_category_id?: number;
  /** 四级分类ID */
  l4_category_id?: number;
  /** 评估得分 */
  evaluate_scores?: OutsourceDemandDetailsResDtoEvaluateScoreDto[];
  demand_plan_info?: OutsourceDemandDetailsResDtoDemandPlanInfoDto;
  /** 是否需全部报价 */
  need_all_quote?: boolean;
  /** 供应商详情 */
  demand_suppliers?: OutsourceDemandDetailsResDtoDemandSupplierDto[];
  settle_report_attachs?: OutsourceDemandDetailsResDtoAttachmentDto[];
  /** 结算单附件 */
  statement_attachs?: OutsourceDemandDetailsResDtoAttachmentDto[];
  /** 签章结算单附件 */
  signed_statement_attachs?: OutsourceDemandDetailsResDtoAttachmentDto[];
  is_self_contact?: boolean;
  current_supplier_id?: number;
  current_demand_supplier_id?: number;
  demand_advertising?: OutsourceDemandDetailsResDtoDemandAdvertisingDto;
  purchasing_method?: PurchasingMethodEnum;
  purchasing_strategy?: PurchasingStrategyEnum;
  /** 采购方式展示字符串 */
  translated_purchasing_method?: string;
  /** 是否绑定执行数据 */
  is_bind_execution_data?: number;
  muse_archive_status?: MuseArchiveStatusEnum;
  flow_flag?: FlowFlagEnum;
  /** 多轮轮次 */
  turns_number?: number;
  root_demand_ext_data?: OutsourceDemandDetailsResDtoRootDemandExtColumnDto;
  /** 报价截至时间 */
  quote_deadline?: string;
  /** 是否需要供应商上传内容营销附件, 0:不需要, 1:需要, -1:不可配置 */
  need_content_marketing_attach?: number;
  /** 发票信息 */
  invoice_list?: InvoiceDetailDto[];
  invoice_belong_area?: InvoiceBelongAreaEnum;
  /** 是否海外INVOICE */
  is_oversea_invoice?: boolean;
  /** 0=原始，1=报价结构化 */
  structured_version?: number;
  product_area?: ProductAreaEnum;
  /** 是否隐藏发票模块 */
  is_hide_invoice?: boolean;
  /** 不需求走费控相关校验 */
  non_need_fee_control_verify?: boolean;
  /** 是否需要上传/查看加热助推数据 */
  need_boost_effect_data?: boolean;
  /** 加热助推效果数据计数 */
  boost_effect_data_count?: number;
  quote_upload_method?: DemandQuoteUploadMethodEnum;
  /** 是否需要供应商提交合作内容维度的结算金额 */
  need_cooperate_account_supplier_settle_price?: boolean;
  /** 是否需要供应商提交结构化结案报告 */
  need_structured_settle_report?: boolean;
  /** 供应商提交合作内容维度的结算金额总计 */
  cooperate_account_supplier_settle_price_sum?: number;
  /** 根据需求类型得出报价类型，0=不含税金额，1=含税金额（报价结构化版本后有值） */
  quote_type?: number;
  /** 内容营销结案结构化，1：新单子，0：老单子 */
  content_marketing_case_structured_version?: number;
  /** 是否全部上传截图（主平台/付费分发） */
  is_screenshot_done?: number;
  /** 是否有监测链接可查看，true=有监测链接，前端可展示查看入口 */
  has_monitor_links?: boolean;
  /** 是否提交了结构化结案报告 */
  submitted_structured_settle_report?: boolean;
  /** 退回标识 */
  return_flag?: boolean;
  /** 退回原因 */
  return_reason?: string;
  plan_type?: DemandPlanTypeEnum;
  '是否支持合作内容_a_i报价&整单报价'?: boolean;
  demand_source_detail_vo?: DemandSourceDetailVo;
  /** 是否需要等待AI审核结果 */
  need_wait_aireview_result?: boolean;
  demand_aireview_result?: DemandAiReviewResultDto;
  /** 历史已完成预付单信息列表 */
  prepayment_his_detail_vos?: PrepaymentHisDetail[];
  permit_actions?: PermitActions;
  is_bind_execution_data_enum?: IsAssociateExecutionDataEnum;
};
export type ResponseOutsourceDemandDetailsResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceDemandDetailsResDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 加热助推效果数据
 */
export type OutsourceBoostEffectUpsertDto = {
  /** 主键ID */
  id?: number;
  /** 需求ID */
  demand_id: number;
  demand_type: MpcDemandTypeEnum;
  /** 合作账号ID */
  kol_code: string;
  /** 合作账号-达人昵称 */
  kol_name: string;
  /** 作品媒体平台 */
  platform: number;
  /** 作品链接 */
  work_url?: string;
  /** 加热费用 */
  boost_cost: number;
  boost_cost_currency: CurrencyEnum;
  /** 加热效果播放量 */
  boost_view_count: number;
  /** 加热开始时间 */
  boost_start_time: string;
  /** 加热结束时间 */
  boost_end_time: string;
  /** 删除标识 */
  delete?: boolean;
};
export type OutsourceBoostEffectUpsertForm = {
  /** 加热助推效果数据 */
  boost_effect_datas: OutsourceBoostEffectUpsertDto[];
};
/**
 * 营销闭环需求详情
 */
export type OutsourceDemandSourceDetailVo = {
  /** 三级需求单类型id */
  l3_category_id?: number;
  /** 需求内容 */
  demand_source_content_vo?: DemandSourceContentVo[];
  /** 指定达人列表 */
  demand_source_kol_vo?: DemandSourceKolVo[];
  /** 定制排查列表 */
  demand_source_custom_inspection_vo?: DemandSourceCustomInspectionVo[];
  /** 供应商列表 */
  demand_source_supplier_vo?: DemandSourceSupplierVo[];
  /** 提报达人列表 */
  demand_source_supplier_kol_vos?: DemandSourceSupplierKolVo[];
  /** 一询单关联的二级单草稿 */
  relate_plan_demand_list?: RelatedPlanDemandVo[];
  demand_source_info?: DemandSourceInfoVo;
  plan_kol_recommend_task?: KolRecommendTaskVo;
  marketing_kol_recommend_task?: KolRecommendTaskVo;
  /** 小喇叭根文档节点 */
  root_id?: number;
  /** 当前展示的单号id */
  current_id?: number;
  /** 流程状态 */
  flow_status?: string;
  /** 流程状态名 */
  flow_status_name?: string;
  /** 备注 */
  remark?: string;
};
export type ResponseOutsourceDemandSourceDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceDemandSourceDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 达人明细
 */
export type SupplierKolCmd = {
  /** 指定达人ID */
  demand_source_kol_id?: number;
  /** 定制排查ID */
  demand_source_custom_inspection_id?: number;
  /** 合作主平台 */
  main_platform?: string;
  /** 主平台账号 */
  main_platform_account?: string;
  /** 主平台主页链接 */
  main_platform_link?: string;
  /** 主平台官单/私单 */
  public_order?: string;
  /** 主平台报价项目 */
  main_platform_quote_item?: string;
  /** 是否独家 */
  exclusive_sign?: string;
  /** 报价 */
  quote_price?: string;
  /** cpm */
  cpm?: string;
  /** 平台报价 */
  platform_quote_price?: ItemVo[];
  /** 平台预期CPM */
  platform_cpm?: ItemVo[];
  /** 平台直播CPM(PV) */
  platform_cpm_pv?: ItemVo[];
  /** 平台直播CPM(UV) */
  platform_cpm_uv?: ItemVo[];
  /** 定制排查属性值 */
  demand_source_custom_inspection_fields_value?: CustomInspectionField[];
  /** 直播CPM(PV) */
  cpm_pv?: number;
  /** 直播CPM(UV) */
  cpm_uv?: number;
  /** 数量 */
  count?: string;
  remark_dto?: RemarkDto;
};
export type SupplierKolExportCmd = {
  /** 一询单id */
  demand_source_id?: number;
  /** 达人明细 */
  supplier_kol_cmd_list?: SupplierKolCmd[];
};
export type OutsourceTemporarySaveForm = {
  /** 暂存类别，由前端自定义传入，用于区分不同业务场景的暂存数据 */
  type: string;
  /** 暂存编码，由前端自定义传入，用于区分同一类别下的不同暂存数据（无需传前缀，后端自动拼接 supplierId 前缀） */
  code: string;
  /** 暂存内容，JSON 字符串 */
  content: string;
};
export type OutsourceTemporaryQueryForm = {
  /** 暂存类别 */
  type: string;
  /** 暂存编码（无需传前缀，后端自动拼接 supplierId 前缀） */
  code: string;
};
export type OutsourceTemporaryDeleteForm = {
  /** 暂存类别 */
  type: string;
  /** 暂存编码（无需传前缀，后端自动拼接 supplierId 前缀） */
  code: string;
};
export type BiddingAnnouncementForm = object;
export type BiddingAnnouncementResDto = {
  /** 关联表Id,如果直接根据bidding直查，则不设置 */
  bidding_supplier_id?: number;
  /** 单号id */
  bidding_id?: number;
  /** 供应商id，如果直接根据bidding直查，则不设置 */
  supplier_id?: number;
  /** 产品名称 */
  product_name?: string;
  /** 需求标题 */
  name?: string;
  /** 预计开始日期 */
  expect_begin_at?: string;
  /** 预计结束日期 */
  expect_end_at?: string;
  /** 项目预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  /** 项目预算币种描述 */
  budget_currency_desc?: string;
  /** 比选报名截止日期 */
  bid_registration_deadline?: string;
  /** 竞选提案截止日期 */
  bid_proposal_deadline?: string;
  /** 公告内容 */
  announcement_content?: string;
  /** 是否有意愿邀请: null: 未选择； true: 有意邀请； false: 拒绝 */
  is_willing_invite?: boolean;
  /** 是否需要保密协议 */
  need_nda?: boolean;
  /** 保密协议 */
  bidding_nda_attachments?: AttachmentDto[];
  /** 保密协议模板 */
  bidding_nda_template_attachments?: AttachmentDto[];
  /** 公告拼接内容 */
  competition_announcement?: string;
};
export type ResponseListBiddingAnnouncementResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingAnnouncementResDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseBiddingAnnouncementResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingAnnouncementResDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseResponse = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: Response;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * Blob
 */
export type OutsourceBiddingDetailsResDtoBlobDto = {
  id?: number;
  filename?: string;
  byte_size?: number;
  content_type?: string;
  key?: string;
};
/**
 * 附件
 */
export type OutsourceBiddingDetailsResDtoAttachmentDto = {
  id?: number;
  blob?: OutsourceBiddingDetailsResDtoBlobDto;
};
/**
 * 权限动作
 */
export type OutsourceBiddingDetailsResDtoPermitActions = {
  can_edit?: boolean;
  can_move?: boolean;
  can_advance_price_check?: boolean;
};
/**
 * 成员
 */
export type OutsourceBiddingDetailsResDtoMemberDto = {
  id?: number;
  bidding_id?: number;
  user_id?: number;
  user_name?: string;
};
/**
 * 底部tab
 */
export type OutsourceBiddingDetailsResDtoBottomTab = {
  tab_name?: string;
  value?: string;
};
/**
 * 合同联系人信息
 */
export type ContractContactInfoVo = {
  /** 联系人姓名 */
  contact_name?: string;
  /** 联系方式 */
  contact_phone?: string;
  /** 联系人邮箱 */
  contact_email?: string;
  /** 联系人地址 */
  contact_address?: string;
};
/**
 * 报价 Tab
 */
export type SupplierQuoteTabDto = {
  /** 配置ID（bidding_quote_config.id） */
  config_id?: number;
  /** 费用类型ID列表 */
  demand_category_ids?: number[];
  /** 费用类型名称列表 */
  demand_category_names?: string[];
  /** 是否启用 */
  enabled?: boolean;
  /** 报价金额模板类型：1=报价金额，2=报价金额+比稿税服，3=报价金额+税率+服务费率+比稿税服 */
  type?: number;
  /** 列定义（方案设计字段 + 报价字段拍平） */
  columns?: BiddingQuoteTemplateColumnVo[];
  /** 明细行（每行一个Map，固定含configDetailId + 各列code对应的值） */
  rows?: {
    [key: string]: any;
  }[];
};
/**
 * 比选供应商报价
 */
export type OutsourceBiddingDetailsResDtoBiddingSupplierQuoteDto = {
  id?: number;
  /** bidding_supplier_id */
  bidding_supplier_id?: number;
  /** cooperate_account_id */
  cooperate_account_id?: number;
  /** quote_price */
  quote_price?: number;
  currency?: CurrencyEnum;
  tender_plan_attachment_dtos?: OutsourceBiddingDetailsResDtoAttachmentDto[];
  quote_price_attachment_dtos?: OutsourceBiddingDetailsResDtoAttachmentDto[];
  /** 报价明细 tabs（结构化 columns + rows，外包端供应商视角填充） */
  tabs?: SupplierQuoteTabDto[];
  created_at?: string;
  updated_at?: string;
};
/**
 * 比选供应商
 */
export type OutsourceBiddingDetailsResDtoBiddingSupplierDto = {
  id?: number;
  /** bidding_id */
  bidding_id?: number;
  /** 供应商id */
  supplier_id?: number;
  /** 供应商编号 */
  supplier_index?: string;
  created_at?: string;
  updated_at?: string;
  /** 是否有意愿邀请 */
  is_willing_invite?: boolean;
  /** 是否提交标书 */
  is_tender_submit?: boolean;
  /** 收款账户 */
  bank_account?: string;
  /** 收款账户名 */
  bank_account_name?: string;
  channel_supplier_bank?: ChannelSupplierBankInfoVo;
  contract_contact_info?: ContractContactInfoVo;
  /** 发票归属地区 */
  invoice_belong_area?: string;
  /** 是否海外发票（0=否，1=是） */
  is_oversea_invoice?: number;
  /** 发票类型 */
  invoice_type?: string;
  supplier_full_name?: string;
  /** 比选中的供应商名称 */
  bidding_supplier_name?: string;
  city?: string;
  bidding_supplier_quotes?: OutsourceBiddingDetailsResDtoBiddingSupplierQuoteDto[];
  /** 保密协议附件 */
  bidding_nda_attachments?: OutsourceBiddingDetailsResDtoAttachmentDto[];
  bidding_state?: BiddingStateEnum;
  /** 比选通过 */
  bidding_pass?: boolean;
  /** 比选方案通过 */
  bidding_confirm_pass?: boolean;
  /** 淘汰 */
  bidding_out?: boolean;
  /** 中选 */
  bidding_win?: boolean;
};
export type OutsourceBiddingDetailsResDto = {
  id?: number;
  /** 名称 */
  name?: string;
  /** 描述 */
  desc?: string;
  /** number */
  number?: string;
  priority?: PriorityEnum;
  /** 作者ID */
  author_id?: number;
  /** 竞选报名截止时间 */
  bid_registration_deadline?: string;
  /** 是否隐藏报价信息 */
  is_hide_quote_price?: boolean;
  open_bidding_type?: OpenBiddingTypeEnum;
  business_state?: BiddingBusinessStateEnum;
  /** 开标时间 */
  open_bidding_time?: string;
  /** 当前订单支持的供应商库版本 */
  enable_supplier_version?: string;
  /** 是否脱敏外发 */
  desensitization_out?: boolean;
  /** 背景 */
  background?: string;
  /** 背景说明补充文件 */
  bidding_background_attachments?: OutsourceBiddingDetailsResDtoAttachmentDto[];
  /** 预期 */
  expectation?: string;
  flow_flag?: BiddingFlowFlagEnum;
  /** 评分截止时间 */
  score_end_at?: string;
  bidding_type_v2?: BiddingTypeV2Enum;
  /** 是否需要保密协议 */
  need_nda?: boolean;
  /** 评分轮次属性记录 */
  score_turns_num?: number;
  /** 听标时间 */
  listen_bidding_time?: string;
  /** 作者名称 */
  author_name?: string;
  /** 状态名称 */
  human_state_name?: string;
  /** 优先级 */
  translated_priority?: string;
  /** 二级分类名称 */
  l2_category_name?: string;
  /** 比选公告 */
  competition_announcement?: string;
  permit_actions?: OutsourceBiddingDetailsResDtoPermitActions;
  /** 轮次 */
  turns_number?: number;
  /** 是否为绑定MPC需求的招采流程：1 是, 0 否 */
  bind_demand?: number;
  /** 产品名称 */
  product_name?: string;
  /** 脱敏文件 */
  bidding_desensitization_attachments?: OutsourceBiddingDetailsResDtoAttachmentDto[];
  /** 保密协议模板 */
  bidding_nda_template_attachments?: OutsourceBiddingDetailsResDtoAttachmentDto[];
  /** 保密协议逻辑版本，1=新逻辑，0=旧逻辑 */
  confidentiality_version?: number;
  nda_template_type?: NdaTemplateTypeEnum;
  /** 是否为虚拟产品，如SM99999 */
  virtual_project?: boolean;
  /** 产品ID */
  product_id?: number;
  /** 产品编号 */
  product_identifier?: string;
  /** 公告内容 */
  announcement_content?: string;
  /** 一级分类ID */
  l1_category_id?: number;
  /** 二级分类ID */
  l2_category_id?: number;
  /** 预算 */
  budget?: number;
  budget_currency?: CurrencyEnum;
  /** 商务人员 */
  bidding_businesses?: OutsourceBiddingDetailsResDtoMemberDto[];
  /** 预计开始时间 */
  expect_begin_at?: string;
  /** 预计结束时间 */
  expect_end_at?: string;
  bidding_type?: BiddingTypeEnum;
  bidding_method?: BiddingMethodEnum;
  /** 比选答疑时间 */
  bidding_qa_time?: string;
  /** 竞选答疑记录附件 */
  bidding_qa_attachments?: OutsourceBiddingDetailsResDtoAttachmentDto[];
  /** 竞选提案截止时间 */
  bid_proposal_deadline?: string;
  /** 比选材料（对外）附件 */
  bidding_out_attachments?: OutsourceBiddingDetailsResDtoAttachmentDto[];
  state?: BiddingState;
  group_type?: DemandGroupType;
  /** 组类型 */
  translated_group_type?: string;
  /** 状态节点 */
  bottom_tabs?: OutsourceBiddingDetailsResDtoBottomTab[];
  /** 层级名称（用-连接） */
  category_names?: string;
  /** 一级分类名称 */
  l1_category_name?: string;
  /** 结构化，0=未使用，1=报价结构化 */
  structured?: number;
  /** 供应商详情 */
  bidding_suppliers?: OutsourceBiddingDetailsResDtoBiddingSupplierDto[];
  /** 供应商id */
  current_supplier_id?: number;
  /** 供应商 - 招采流程关联关系id */
  current_bidding_supplier_id?: number;
};
export type ResponseOutsourceBiddingDetailsResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceBiddingDetailsResDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商报价明细Tab
 */
export type SupplierQuoteTabForm = {
  /** 关联报价单配置实例ID（bidding_quote_config.id） */
  config_id?: number;
  /** 明细行（每行Map固定含configDetailId + 各列code对应的值） */
  rows?: {
    [key: string]: any;
  }[];
};
/**
 * 供应商报价总明细校验请求
 */
export type OutsourceBiddingSupplierCheckForm = {
  /** 多 Tab 报价明细数据 */
  tabs: SupplierQuoteTabForm[];
  /** 报价总金额（用于和明细汇总比对） */
  quote_total_amount: number;
};
/**
 * 供应商达人报价列表查询入参
 */
export type OutsourceListForm = {
  /** demandId */
  demand_id?: number;
  /** productIds 产品ID列表 */
  product_ids?: number[];
  /** priceCheckStates 核价状态列表 */
  price_check_states?: string[];
  /** current */
  current?: number;
  /** pageSize */
  page_size?: number;
};
/**
 * 分页数据
 */
export type AdvancePriceCheckListItemVo = {
  /** advance_price_check 主键ID（最新一轮）；未上传过任何报价时为 null */
  id?: number;
  /** 业务单据ID（比选单ID或二级需求单ID） */
  biz_id?: number;
  /** 业务类型：BIDDING=比选单 DEMAND=常规采购二级单 */
  biz_type?: string;
  /** 单号 */
  biz_number?: string;
  /** 产品名称 */
  product_name?: string;
  /** 产品编码 */
  product_code?: string;
  /** 产品ID */
  product_id?: number;
  /** 需求标题/比选标题 */
  title?: string;
  /** 报价文件名（最新一轮） */
  file_name?: string;
  price_check_state?: AdvancePriceCheckStateEnum;
  operation?: string;
  permit_actions?: PermitActions;
};
/**
 * 分页结果
 */
export type PageResultAdvancePriceCheckListItemVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: AdvancePriceCheckListItemVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultAdvancePriceCheckListItemVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultAdvancePriceCheckListItemVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商达人报价-操作页单条轮次记录（Task 5.2）
 */
export type SupplierKolQuoteRoundVo = {
  /** 核价记录 id */
  advance_price_check_id?: number;
  /** 记录单号（含轮次，如 #123-886-02） */
  record_no?: string;
  /** 轮次号 */
  round?: number;
  /** 报价附件名 */
  file_name?: string;
  /** 报价附件 blobId（下载用） */
  blob_id?: number;
  /** 状态：AI_CHECKING / PENDING_CONFIRM / CONFIRMED / ... */
  state?: string;
  /** 上传时间 */
  upload_at?: string;
  /** 上传人 userId */
  upload_by?: number;
};
export type ResponseListSupplierKolQuoteRoundVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierKolQuoteRoundVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商达人报价-精简列表项（Task 5.1）
 */
export type SupplierKolQuoteListItemVo = {
  /** 核价记录 id（用于操作页跳转） */
  advance_price_check_id?: number;
  /** 来源单号（核价记录单号，如 #123-886-01） */
  record_no?: string;
  /** 所属产品 */
  product_name?: string;
  /** 需求标题 */
  title?: string;
};
/**
 * 分页结果
 */
export type PageResultSupplierKolQuoteListItemVo = {
  /** 页号 */
  current?: number;
  /** 分页大小 */
  limit?: number;
  /** 分页数据 */
  list?: SupplierKolQuoteListItemVo[];
  /** 总记录数 */
  total_count?: number;
  /** 总页数 */
  total_page?: number;
  columns?: ColumnVo[];
  page?: number;
};
export type ResponsePageResultSupplierKolQuoteListItemVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageResultSupplierKolQuoteListItemVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商达人报价-详情查询入参（Task 5.6）
 */
export type DetailIdForm = {
  /** 核价记录 id */
  advance_price_check_id: number;
};
/**
 * 合作内容明细（只读）
 */
export type DetailRow = {
  /** 达人昵称 */
  kol_nickname?: string;
  /** 平台 */
  platform?: string;
  /** 下单类型 */
  order_type?: string;
  /** 主平台报价项目 */
  main_platform_quote_item?: string;
  /** 主页链接 */
  home_page_url?: string;
  /** 基准价 */
  base_price?: number;
  /** 基准价类型 */
  base_price_type?: string;
  /** 议价优惠金额 */
  bargaining_discount?: string;
};
/**
 * 供应商达人报价-详情页视图（只读，Task 5.6）
 */
export type SupplierKolQuoteDetailVo = {
  /** 核价记录 id */
  advance_price_check_id?: number;
  /** 记录单号（含轮次） */
  record_no?: string;
  /** 轮次号 */
  round?: number;
  /** 状态 */
  state?: string;
  /** 附件名 */
  file_name?: string;
  /** 附件 blobId */
  blob_id?: number;
  /** 上传时间 */
  upload_at?: string;
  /** 合作内容维度的报价明细 */
  details?: DetailRow[];
};
export type ResponseSupplierKolQuoteDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierKolQuoteDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type RubyResponseVoPageDtoProjectQueryDto = {
  res_code?: number;
  res_msg?: string;
  data?: PageDtoProjectQueryDto;
  version?: number;
  trace_id?: string;
};
export type RubyResponseVoPageDtoContractQueryDto = {
  res_code?: number;
  res_msg?: string;
  data?: PageDtoContractQueryDto;
  version?: number;
  trace_id?: string;
};
export type FilterOptionsRequest = {
  keys?: string[];
  data?: {
    [key: string]: any;
  };
  extra_attrs?: string[];
  back_project_id?: number;
  auditing?: boolean;
  model?: string;
  menu_key?: string;
  parent_id?: number;
  product_ids?: number[];
  state?: string;
};
export type MobileFilterOptionsResponse = {
  key?: string;
  name?: string;
  desc?: string;
  options?: object[];
};
export type RubyResponseVoListMobileFilterOptionsResponse = {
  res_code?: number;
  res_msg?: string;
  data?: MobileFilterOptionsResponse[];
  version?: number;
  trace_id?: string;
};
/**
 * 状态
 */
export type ExportTaskStatus = 1 | 2 | 3;
export type ExportTaskResultVo = {
  status?: ExportTaskStatus;
  /** 进度，0-100 */
  progress?: number;
  /** 下载的url */
  download_url?: string;
};
export type ResponseExportTaskResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ExportTaskResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type RoleDto = {
  id?: number;
  name?: string;
  permissions?: string[];
};
export type UserDto = {
  id?: number;
  name?: string;
  email?: string;
  admin?: boolean;
  roles?: RoleDto[];
};
export type ResponseUserDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: UserDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type PopoUserRes = {
  name?: string;
  email?: string;
};
export type ResponseListPopoUserRes = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PopoUserRes[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type UsedTrumpetActivityBudgetVo = {
  demand_id?: number;
  used_budget?: number;
  total_budget_activity?: number;
};
export type ResponseUsedTrumpetActivityBudgetVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: UsedTrumpetActivityBudgetVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type TrumpetTreeNodeVo = {
  id?: number;
  root_id?: number;
  name?: string;
  state?: TrumpetState;
  type?: TrumpetDemandType;
  child?: TrumpetTreeNodeVo[];
};
export type ResponseTrumpetTreeNodeVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TrumpetTreeNodeVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseTrumpetCasePageDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TrumpetCasePageDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type MonitorLinkItemVo = {
  /** 合作内容ID */
  sub_demand_id?: string;
  /** 合作内容序号 */
  sub_demand_index_id?: number;
  /** 达人名称 */
  kol_name?: string;
  /** 链接编号，从 1 开始自动递增 */
  link_index?: number;
  /** 监测链接 */
  track_url?: string;
  /** 失效日期 */
  expire_date?: string;
};
export type ResponseListMonitorLinkItemVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MonitorLinkItemVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * QC指标数据列表，结构与index一致
 */
export type Index = {
  /** 别名 */
  index_alias?: string;
  /** 名称 */
  index_name?: string;
  /** 单位 */
  unit?: string;
  /** 是否百分比 */
  is_percent?: boolean;
  /** 指标数据 */
  value?: number;
  /** 是否是预估值 */
  is_predict?: boolean;
};
/**
 * 策划复核QC指标数据
 */
export type DspfQcData = {
  /** QC指标数据列表，结构与index一致 */
  data?: Index[];
  /** 错误信息，数据变动提示 */
  error_msg?: string;
};
export type QueryDataDto = {
  /** 数易单号 */
  dspf_mpc_no?: string;
  /** 1：正常，3：失效，0：该效果单不存在 */
  status?: number;
  /** 投放金额（结算金额） */
  fee?: number;
  currency?: CurrencyEnum;
  /** index_alias与KPI配置保持一致 */
  index?: Index[];
  /** 【新增】订单类型or媒体类型or整体类型的kpi数据，数据结构与index一致 */
  overview?: Index[];
  /** 量级指标名称 */
  scale_index_name?: string;
  /** 量级指标数值 */
  scale_index_value?: number;
  /** 成本指标名称 */
  cost_index_name?: string;
  /** 成本指标数值 */
  cost_index_value?: number;
  dspf_qc_data?: DspfQcData;
};
export type ResponseQueryDataDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: QueryDataDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 一级单策划设计通过
 */
export type PendingDesignMoveDemandDto = {
  plan_infos: DemandPlanInfoDto[];
};
/**
 * 一级单策划设计退回
 */
export type PendingDesignMoveBackDto = object;
/**
 * 二级单策划设计通过
 */
export type PendingDesignMovePlanDto = {
  plan_info: DemandPlanInfoDto;
};
/**
 * 商务评估通过（常规采购方式）
 */
export type NewPendingAssessMoveDemandDto = {
  purchasing_method?: PurchasingMethodEnum;
  purchasing_strategy?: PurchasingStrategyEnum;
  demand_suppliers?: PendingassessDemandSupplierDto[];
  quote_deadline?: string;
  flow_flag?: FlowFlagEnum;
  score_template_code?: string;
  urgent_order_restocking_attachs_blob_ids?: number[];
  leaf?: boolean;
};
/**
 * 商务评估通过
 */
export type PendingAssessMoveDto = {
  ad_settle_way?: AdSettleWayEnum;
  support_type?: DemandSupportTypeEnum;
  need_cp_pay?: boolean;
  quote_deadline?: string;
  demand_suppliers?: PendingassessDemandSupplierDto[];
  demand_plan_info?: DemandPlanInfo;
  score_template_code?: string;
  urgent_order_restocking_attachs_blob_ids?: number[];
  leaf?: boolean;
};
export type PendingquoteDemandSupplierQuoteDto = {
  id?: number;
  cooperate_account_id: number;
  quote_price?: number;
  currency?: CurrencyEnum;
  program_desc?: string;
  /** 报价金额（优惠前、不含税） */
  quote_price_no_tax?: number;
  /** 报价金额(优惠前、含税和服务费) */
  quote_price_with_tax?: number;
  /** 报价金额(优惠后、不含税) */
  discount_quote_price_no_tax?: number;
  /** 报价金额(优惠后、含税和服务费) */
  discount_quote_price_with_tax?: number;
  /** 报价详情附件 */
  quote_attachs_blob_keys?: string[];
  /** 供应商填写的期望权益列表 */
  expect_rights?: DemandRightsValueDto[];
  /** 必要权益列表 */
  necessary_rights?: DemandRightsValueDto[];
};
/**
 * 报价总明细数据
 */
export type QuoteDetailDto = {
  /** 报价数据 */
  quote_data?: {
    [key: string]: any;
  }[];
};
export type PendingquoteDemandSupplierDto = {
  id?: number;
  is_quoted?: boolean;
  demand_supplier_quotes?: PendingquoteDemandSupplierQuoteDto[];
  /** 合并报价附件 */
  merge_quote_attach_blob_keys?: string[];
  quote_detail?: QuoteDetailDto;
  /** 当前使用的模版信息，用于导出功能 */
  template_info?: string;
  bid_attachs_blob_keys?: string[];
  /** 报价类型:0=不含税金额；1=含税金额 */
  quote_type?: number;
  /** 税率 */
  tax_rate?: number;
  /** 是否收取服务费,0=不收取,1=收取 */
  service_fee_mode?: number;
  /** 服务费率 */
  service_fee_rate?: number;
  invoice_type?: InvoiceType;
  invoice_belong_area?: InvoiceBelongAreaEnum;
  /** 是否海外发票,1=海外发票,0=非海外发票 */
  is_oversea_invoice?: number;
  channel_supplier_bank_dto?: ChannelSupplierBankDto;
  merge_quote_attach_blob_key?: string;
};
export type DemandSupplierQuoteSimpleDto = {
  quote_attachs_blob_keys?: string[];
  quote_price?: number;
  quote_price_no_tax?: number;
  quote_price_with_tax?: number;
  discount_quote_price_no_tax?: number;
  discount_quote_price_with_tax?: number;
};
export type DemandSupplierQuoteType = 0 | 1;
/**
 * 供应商报价通过
 */
export type PendingQuoteMoveDto = {
  id: number;
  demand_supplier: PendingquoteDemandSupplierDto;
  remark?: string;
  is_from_deadline_schedule?: string;
  reason?: string;
  demand_supplier_quotes?: DemandSupplierQuoteSimpleDto[];
  quote_type?: DemandSupplierQuoteType;
  service_fee_rate?: number;
  tax_rate?: number;
};
/**
 * 供应商报价评分
 */
export type DemandSupplierScoreForm = {
  /** 供应商报价ID, 对应demand_supplier_quote.id */
  demand_supplier_quote_id?: number;
  /** 评分明细 */
  detail?: DemandSupplierScoreDetailDto[];
  /** 优势(描述) */
  pros?: string;
  /** 劣势(描述) */
  cons?: string;
  /** 评分人 */
  user_id?: number;
};
/**
 * 询比价综合评分
 */
export type QuoteScoreMoveDto = {
  change_quote_score_state?: QuoteScoreStateEnum;
  participant_type?: ParticipantType;
  /** 针对单项报价的评分 */
  score_form?: DemandSupplierScoreForm[];
  /** 商务评分附件 */
  demand_evaluation_attachs_blob_keys?: string[];
  /** 报价截至时间 */
  quote_deadline?: string;
  /** 重新报价供应商信息 */
  demand_suppliers?: BusinessauditquoteDemandSupplierDto[];
  /** 是否需要评分 */
  need_score_state?: boolean;
};
/**
 * 报价待商务确认通过
 */
export type BusinessAuditQuoteMoveDto = {
  need_cp_pay?: boolean;
  quote_deadline?: string;
  demand_suppliers?: BusinessauditquoteDemandSupplierDto[];
  /** 是否需要评分 */
  need_score_state?: boolean;
  demand_evaluation_attachs_blob_keys?: string[];
  /** 费用采购主体/费用归属公司id */
  cost_company_id?: number;
  leaf?: boolean;
};
/**
 * 报价待商务负责人确认通过
 */
export type BusinessLeaderAuditQuoteMoveDto = {
  leaf?: boolean;
};
/**
 * 报价待策划确认/报价待策划负责人确认/报价待需求方负责人确认/通过
 */
export type AuditQuoteMoveDto = {
  target_state?: DemandState;
  leaf?: boolean;
};
/**
 * 发票附件
 */
export type InvoiceDto = {
  /** 发票ID，M税务系统生成的发票ID，同一张发票被删除重新生成，ID会不同 */
  invoice_id?: number;
  /** 发票代码 */
  invoice_code?: string;
  /** 发票号码 */
  invoice_no?: string;
  /** 发票含税总金额 */
  amount_with_tax?: number;
  /** 发票不含税金额 */
  amount_without_tax?: number;
  /** 发票税额 */
  tax?: number;
  /** 发票上传完成后生成得到的blobId */
  blob_id?: number;
};
/**
 * 发票上传
 */
export type InvoiceUploadDto = {
  invoice_belong_area: InvoiceBelongAreaEnum;
  /** 是否海外invoice */
  is_oversea_invoice: boolean;
  /** 发票附件 */
  invoice_list: InvoiceDto[];
  null?: boolean;
};
/**
 * 制作中通过
 */
export type MakingMovePassDto = {
  id: number;
  final_settle_price?: number;
  final_settle_currency?: CurrencyEnum;
  settle_report_attachs_blob_keys?: string[];
  /** 【结构化】【通用】合作内容列表，供应商填写指标数据 */
  cooperate_accounts?: DemandCooperateAccountDto[];
  /** 供应商结算报告，报价行维度 */
  quote_settle_reports?: DemandSupplierQuoteSettleReportDto[];
  /** 【结构化】【内容营销】指标列表，供应商填写指标数据 */
  common_kpi_values?: DemandKpiValueDto[];
  invoice_upload_dto?: InvoiceUploadDto;
  /** 已盖章的结算单附件 */
  signed_statement_attachs_blob_keys?: string[];
  demand_evaluation_attach_blob_keys?: string[];
};
export type MakingDemandSupplierQuoteDto = {
  id?: number;
  requote_price?: number;
  last_quote_price?: number;
  quote_price?: number;
  /** 报价金额（优惠前、不含税） */
  quote_price_no_tax?: number;
  /** 报价金额(优惠前、含税和服务费) */
  quote_price_with_tax?: number;
  /** 报价金额(优惠后、不含税) */
  discount_quote_price_no_tax?: number;
  /** 报价金额(优惠后、含税和服务费) */
  discount_quote_price_with_tax?: number;
  /** 报价详情附件 */
  quote_attachs_blob_keys?: string[];
};
export type MakingDemandSupplierDto = {
  id?: number;
  /** 合并报价附件 */
  merge_quote_attach_blob_keys?: string[];
  bid_attachs_blob_keys?: string[];
  demand_supplier_quotes: MakingDemandSupplierQuoteDto[];
};
/**
 * 制作中退回
 */
export type MakingMoveRejectDto = {
  id: number;
  demand_supplier?: MakingDemandSupplierDto;
  remark?: string;
  demand_supplier_quotes?: DemandSupplierQuoteSimpleDto[];
};
/**
 * demander_evaluate_scores
 */
export type EvaluateScoreDto = {
  /** evaluate_item_id */
  evaluate_item_id?: number;
  /** score */
  score?: number;
};
/**
 * 策划复核通过
 */
export type PlannerAcceptMoveDto = {
  /** no_contract_flag */
  no_contract_flag?: boolean;
  /** contract_no */
  contract_no?: string;
  /** 策划复核报价金额 */
  plan_accept_settle_price?: number;
  /** 最终结算金额 */
  settle_price?: number;
  /** dspf_mpc_no */
  dspf_mpc_no?: string;
  /** dspf_desc */
  dspf_desc?: string;
  /** 意见及建议 */
  suggestion?: string;
  settle_currency?: CurrencyEnum;
  /** settle_report_attachs_blob_ids */
  settle_report_attachs_blob_ids?: DemandBlobDto[];
  /** planner_report_attachs_blob_ids */
  planner_report_attachs_blob_ids?: DemandBlobDto[];
  /** evaluate_scores */
  evaluate_scores?: EvaluateScoreDto[];
  dspf_quota_data?: DspfQuotaData;
  /** 【结构化】【通用】合作内容列表，只处理自定义指标部分 */
  cooperate_accounts?: DemandCooperateAccountDto[];
  /** 【结构化】【内容营销】指标列表，只处理自定义指标部分 */
  common_kpi_values?: DemandKpiValueDto[];
  /** 是否加急 */
  urgent?: boolean;
  /** 加急原因 */
  urgent_reason?: string;
  /** 调整预算金额 */
  adjusted_budget?: number;
  /** 最终预算金额 */
  final_budget?: number;
  /** 是否超预算 */
  over_amount?: boolean;
  /** 超预算说明 */
  over_amount_remark?: string;
  /** 达标结果/预期结果 */
  kpi_result?: string;
  /** 达标说明/未预期说明 */
  kpi_result_remark?: string;
  target_state: DemandState;
  leaf?: boolean;
};
/**
 * 策划负责人复核通过
 */
export type PlannerLeaderAcceptMoveDto = {
  /** 意见及建议 */
  planner_leader_accept_suggestion?: string;
  /** evaluate_scores */
  evaluate_scores?: EvaluateScoreDto[];
  target_state: DemandState;
};
/**
 * 商务验收通过
 */
export type BusinessAcceptMoveDto = {
  /** no_contract_flag */
  no_contract_flag?: boolean;
  /** contract_no */
  contract_no?: string;
  target_state: DemandState;
  leaf?: boolean;
};
/**
 * 需求方验收通过
 */
export type DemanderAcceptMoveDto = {
  /** number */
  number?: string;
  /** 结算金额 */
  settle_price?: number;
  /** demander_evaluate_scores */
  demander_evaluate_scores?: EvaluateScoreDto[];
  /** 【结构化】【通用】合作内容列表，只处理非直接考核的指标 */
  cooperate_accounts?: DemandCooperateAccountDto[];
  /** 【结构化】【内容营销】指标列表，只处理非直接考核的指标 */
  common_kpi_values?: DemandKpiValueDto[];
  /** 效果描述（需求方填写） */
  demander_accept_suggestion?: string;
  /** 是否有效果描述补充 */
  has_demander_accept_suggestion?: boolean;
  /** 项目编号，虚拟项目编号时可编辑 */
  project_code?: string;
  target_state: DemandState;
  leaf?: boolean;
};
/**
 * 结算资料待上传-通过
 */
export type SettledUploadMoveDto = {
  invoice_upload_dto?: InvoiceUploadDto;
  /** 已盖章的结算单附件 */
  signed_statement_attachs_blob_keys?: string[];
  demand_evaluation_attach_blob_keys?: string[];
};
/**
 * QC验收表单
 */
export type QcAcceptMoveDto = {
  target_state?: DemandState;
};
/**
 * 付款节点操作
 */
export type PaymentFeeControlOpEnum = 0 | 1 | 2 | 3;
/**
 * 提交付款 节点move表单对象
 */
export type FeeControlPaymentMoveDto = {
  fee_control_op?: PaymentFeeControlOpEnum;
  /** 费控单据号 */
  doc_number?: string;
  /** 实际支付金额 */
  biz_amount_real?: number;
};
export type DemandMoveOneOfDto = {
  pending_design_move_demand_dto?: PendingDesignMoveDemandDto;
  pending_design_move_back_dto?: PendingDesignMoveBackDto;
  pending_design_move_plan_dto?: PendingDesignMovePlanDto;
  new_pending_assess_move_demand_dto?: NewPendingAssessMoveDemandDto;
  pending_assess_move_dto?: PendingAssessMoveDto;
  pending_quote_move_dto?: PendingQuoteMoveDto;
  quote_score_move_dto?: QuoteScoreMoveDto;
  business_audit_quote_move_dto?: BusinessAuditQuoteMoveDto;
  business_leader_audit_quote_move_dto?: BusinessLeaderAuditQuoteMoveDto;
  audit_quote_move_dto?: AuditQuoteMoveDto;
  making_move_pass_dto?: MakingMovePassDto;
  making_move_reject_dto?: MakingMoveRejectDto;
  planner_accept_move_dto?: PlannerAcceptMoveDto;
  planner_leader_accept_move_dto?: PlannerLeaderAcceptMoveDto;
  business_accept_move_dto?: BusinessAcceptMoveDto;
  demander_accept_move_dto?: DemanderAcceptMoveDto;
  settled_upload_move_dto?: SettledUploadMoveDto;
  qc_accept_move_dto?: QcAcceptMoveDto;
  fee_control_payment_move_dto?: FeeControlPaymentMoveDto;
};
export type ResponseDemandMoveOneOfDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandMoveOneOfDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type OaEmployeesAuthResultDto = {
  employee_code?: string;
  auth_type_list?: string[];
};
export type ResponseListOaEmployeesAuthResultDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OaEmployeesAuthResultDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 小组评分明细
 */
export type GroupItem = {
  /** 评分小组id */
  group_id?: number;
  /** 评分人 */
  user_id?: number;
  /** 权重 */
  weight?: number;
  /** 评分项 */
  score?: Item[];
  /** 优势 */
  advantage?: string;
  /** 不足 */
  shortage?: string;
  /** 评分时间 */
  score_time?: string;
  /** 0 不复评 1 复评 */
  re_evaluation?: number;
  /** 复评原因 */
  re_evaluation_reason?: string;
};
export type Detail = {
  /** 供应商id */
  supplier_id: number;
  score: Item[];
  /** 优势 */
  advantage?: string;
  /** 不足 */
  shortage?: string;
  /** 小组评分明细 */
  group_item?: GroupItem[];
  /** 汇总时间 */
  summary_time?: string;
  /** 权重 */
  weight?: number;
};
export type OperationRecordNodeListDto = {
  act_id?: string;
  task_name?: string;
  detail_list?: Detail[];
};
export type ProcessInstance = {
  form_data?: string;
  form_data_extend?: string;
  process_name?: string;
  start_time?: string;
  remark?: string;
  request_id?: string;
  workflow_id?: string;
  activity_info?: {
    [key: string]: any;
  };
  historic_activity_info?: object[];
  user_process_status?: number;
  operation_record_list?: OperationRecordNodeListDto[];
};
export type ResponseProcessInstance = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ProcessInstance;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ExportTaskResultDto = {
  status?: ExportTaskStatus;
  /** 进度，0-100 */
  progress?: number;
  /** 下载的url */
  download_url?: string;
};
export type ResponseExportTaskResultDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ExportTaskResultDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseSuppliersTagDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SuppliersTagDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type SuppliersTagCatDto = {
  id?: number;
  level?: number;
  parent_id?: number;
  root_id?: number;
  name?: string;
  tag_num?: number;
  children_cats?: SuppliersTagCatDto[];
};
export type ResponseSuppliersTagCatDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SuppliersTagCatDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type SuppliersTagCatTreeVo = {
  cats?: SuppliersTagCatDto[];
  can_add?: boolean;
  can_edit?: boolean;
  can_delete?: boolean;
};
export type ResponseSuppliersTagCatTreeVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SuppliersTagCatTreeVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 推荐会话状态
 */
export type SessionStatusVo = {
  /** 推荐规则编码 */
  recommendation_rule?: string;
  /** 推荐规则描述 */
  recommendation_rule_desc?: string;
  /** 会话状态（PENDING / TRIGGERED / COMPLETED / FAILED） */
  status?: string;
  /** 中台返回的会话 ID（PENDING 阶段为空） */
  session_id?: string;
  /** 错误信息（FAILED 时有值） */
  error_message?: string;
};
/**
 * 单项评分
 */
export type ScoreItemVo = {
  /** 评分维度 key，如 collaboration_fit */
  key?: string;
  /** 评分维度名称，如 合作适配度 */
  name?: string;
  /** 评分值 */
  value?: string;
};
/**
 * 推荐供应商信息
 */
export type RecommendedSupplierVo = {
  /** 供应商 ID */
  supplier_id?: number;
  /** 供应商编码 */
  supplier_code?: string;
  /** 供应商名称 */
  supplier_name?: string;
  /** MDM 供应商编码 */
  mdm_code?: string;
  /** 综合评分 */
  score?: string;
  /** 各维度评分明细列表 */
  score_info?: ScoreItemVo[];
  /** 推荐理由（由算法侧生成） */
  recommend_reason?: string;
  /** 推荐理由高亮关键词列表，前端根据该列表对 recommendReason 中的匹配文本进行高亮处理 */
  highlights?: string[];
  /** 是否营销推荐 */
  marketing_recommend?: boolean;
  /** 是否为新供应商 */
  new_supplier?: boolean;
  /** 渠道类型编码 */
  channel_type?: string;
  /** 渠道类型描述 */
  channel_type_desc?: string;
  /** 渠道供应商类型编码 */
  channel_supplier_type?: number;
  /** 渠道供应商类型描述 */
  channel_supplier_type_desc?: string;
};
/**
 * 推荐规则分组
 */
export type RecommendationGroupVo = {
  /** 推荐会话 ID，可用于针对特定规则提交反馈 */
  session_id?: string;
  /** 推荐规则 key，如 comprehensive_score */
  recommendation_rule?: string;
  /** 推荐规则描述，如 '按综合得分推荐' */
  recommendation_rule_desc?: string;
  /** 是否允许新供应商 */
  allow_new_supplier?: boolean;
  /** 该规则下推荐的供应商列表 */
  supplier_info?: RecommendedSupplierVo[];
};
/**
 * 推荐结果
 */
export type RecommendationResultVo = {
  /** 触发批次号（标识一次推荐请求） */
  trigger_batch?: string;
  /** 推荐状态概览（每条规则的状态） */
  session_statuses?: SessionStatusVo[];
  /** 按推荐规则分组的推荐结果列表 */
  groups?: RecommendationGroupVo[];
};
export type ResponseRecommendationResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: RecommendationResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandRatingRecordVo = {
  /** 需求ID */
  demand_id?: number;
  /** 需求名称 */
  demand_name?: string;
  /** 产品名称 */
  product_name?: string;
  /** 产品标识 */
  product_identifier?: string;
  /** 过程打分 */
  project_processing_score?: number;
  /** 过程打分明细 */
  project_processing_score_detail_list?: EvaluateItemDescVo[];
  /** 过程打分时间 */
  project_processing_score_rating_time?: string;
  /** 结果打分 */
  project_result_score?: number;
  /** 结果打分明细 */
  project_result_score_detail_list?: EvaluateItemDescVo[];
  /** 结果打分时间 */
  project_result_score_rating_time?: string;
};
export type PageVoDemandRatingRecordVo = {
  total?: number;
  records?: DemandRatingRecordVo[];
};
export type ResponsePageVoDemandRatingRecordVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoDemandRatingRecordVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type OpLogBizType = {
  code?: string;
  name?: string;
};
export type SupplierOpLogVo = {
  type?: OpLogBizType;
  content?: string;
  remarks?: string;
  operator?: Operator;
  operation_time?: string;
  approval_no?: string;
};
export type PageVoSupplierOpLogVo = {
  total?: number;
  records?: SupplierOpLogVo[];
};
export type ResponsePageVoSupplierOpLogVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoSupplierOpLogVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ValidChannelSupplierType = 1 | 2 | 3;
export type ResponseValidChannelSupplierType = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ValidChannelSupplierType[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 审批动作
 */
export type SupplierApprovalActionType = 1 | 2 | 3 | 4;
/**
 * 审批动作显示类型
 */
export type SupplierApprovalActionDisplayType =
  | 'SUBMITTED'
  | 'PASSED'
  | 'REJECTED'
  | 'RETURNED'
  | 'PENDING'
  | 'WAITING';
/**
 * 供应商审批进度VO
 */
export type SupplierApprovalProgressVo = {
  /** 审批进度ID */
  id?: number;
  approval_node?: ApprovalNode;
  action_type?: SupplierApprovalActionType;
  /** 是否待审批 */
  pending?: boolean;
  /** 操作人 */
  operators?: Operator[];
  /** 操作时间 */
  operate_time?: string;
  /** 审批意见 */
  reason?: string;
  /** 审批时长 */
  duration?: number;
  display_action_type?: SupplierApprovalActionDisplayType;
};
/**
 * 供应商审批详情VO
 */
export type SupplierApprovalDetailVo = {
  id?: number;
  approval_no?: string;
  type?: SupplierApprovalType;
  main_supplier_id?: number;
  channel_supplier_id?: number;
  approval_node?: ApprovalNode;
  approval_node2?: ApprovalNode;
  status?: SupplierApprovalStatus;
  param1?: string;
  /** 审批进度 */
  progress_list?: SupplierApprovalProgressVo[];
  /** 待审批节点 */
  pending_nodes?: ApprovalNode[];
  supplier_type?: ChannelSupplierType;
  target_supplier_type?: ChannelSupplierType;
  supplier_type_change_type?: ChannelSupplierTypeChangeType;
  /** 申请人 */
  submitter?: string;
  submit_user?: Operator;
  /** 申请时间 */
  submit_time?: string;
  /** 申请原因 */
  submit_reason?: string;
  permission?: ChannelSupplierOperationPermission;
  /** 附件 */
  attachments?: StorageObjectVo[];
};
export type ResponseSupplierApprovalDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierApprovalDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListVmdmSupplierBankVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: VmdmSupplierBankVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type TrumpetAccountVo = {
  /** ID */
  id?: number;
  /** 小喇叭用户ID */
  user_id?: string;
  /** 手机号 */
  phone?: string;
  /** 累计收入 */
  income_amount?: number;
  income_currency?: CurrencyEnum;
  /** 收入作者身份，游戏作者/集团作者  1-游戏作者 2-集团作者 */
  author_type?: number;
};
export type TrumpetAccountAllVo = {
  account_list?: TrumpetAccountVo[];
};
export type ResponseTrumpetAccountAllVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: TrumpetAccountAllVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseMainSupplierVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MainSupplierVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListApprovalOpinionDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ApprovalOpinionDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type SupplierManagerPermissionVo = {
  data_permissions?: ChannelType[];
  has_change_type_permission?: boolean;
  has_edit_permission?: boolean;
  has_export_permission?: boolean;
};
export type ResponseSupplierManagerPermissionVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierManagerPermissionVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ImportTaskStatus = 1 | 2 | 3 | 4;
export type ImportTaskResultVo = {
  status?: ImportTaskStatus;
  success_num?: number;
  fail_num?: number;
  download_url?: string;
};
export type ResponseImportTaskResultVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ImportTaskResultVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商变更项
 */
export type SupplierChangeItemVo = {
  /** 字段名称 */
  field_key?: string;
  /** 字段名称 */
  field_name?: string;
  /** 旧值，any类型 */
  old_value?: object;
  /** 新值，any类型 */
  new_value?: object;
};
/**
 * 供应商变更内容
 */
export type SupplierChangeDetailVo = {
  /** 供应商ID */
  id?: number;
  /** 供应商名称 */
  name?: string;
  channel_type?: ChannelType;
  /** 供应商服务类型名称 */
  channel_type_name?: string;
  /** 供应商变更项列表 */
  items?: SupplierChangeItemVo[];
  /** 供应商供应商风险标签 */
  risk_tags?: SupplierRiskTagVo[];
};
export type ResponseSupplierChangeDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierChangeDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 指标
 */
export type SupplierKpiItemVo = {
  /** 名称 */
  name?: string;
  /** 规则类型，0=默认规则，1=自定义规则 */
  type?: number;
  /** 默认规则，1=中选率，2=应标率，3=投放达标率（仅代投），4=效果达成率 */
  rule_index?: number;
  rule?: SupplierKpiItemRuleData;
  /** 取数范围，月 */
  range?: number;
};
export type SupplierKpiVo = {
  /** 主键id */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 状态 */
  status?: boolean;
  /** 供应商服务类型列表 */
  channel_types?: ChannelType[];
  /** 需求类型列表 */
  category_ids?: number[];
  /** 指标 */
  items?: SupplierKpiItemVo[];
};
export type ResponseSupplierKpiVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SupplierKpiVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListDemandStandardSpecVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandStandardSpecVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseStandardLibraryDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: StandardLibraryDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type Session = {
  user?: UserDto;
};
export type ResponseSession = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: Session;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 比选标段类目 VO
 */
export type SegmentCategoryVo = {
  /** 类目 ID */
  id?: number;
  /** 类目编码（预留拓展） */
  code?: string;
  /** 类目名称 */
  name?: string;
  /** 状态：active=启用，locked=未启用 */
  status?: string;
  /** 字段数量 */
  field_count?: number;
  /** 创建时间 */
  created_at?: string;
  /** 最后更新时间 */
  updated_at?: string;
};
/**
 * 比选标段配置 VO
 */
export type SegmentConfigVo = {
  /** 标段 ID */
  id?: number;
  /** 关联类目 ID */
  segment_category_id?: number;
  /** 关联类目名称 */
  segment_category_name?: string;
  /** 标段号 */
  segment_code?: string;
  /** 状态：active=启用，locked=未启用 */
  status?: string;
  /** 字段值列表 */
  template_value?: FieldValueVo[];
  /** 创建时间 */
  created_at?: string;
  /** 最后更新时间 */
  updated_at?: string;
};
/**
 * 标段供应商详情页
 */
export type SegmentSupplierDetailVo = {
  segment_category?: SegmentCategoryVo;
  segment_config?: SegmentConfigVo;
  /** 标段关联供应商信息 */
  supplier_list?: SegmentSupplierVo[];
};
export type ResponseSegmentSupplierDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SegmentSupplierDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 比选单简要信息
 */
export type BiddingSimpleVo = {
  /** 比选单 ID */
  id?: number;
  /** 比选单号（项目编号） */
  number?: string;
  /** 需求标题 */
  name?: string;
  /** 所属产品名称 */
  product_name?: string;
  /** 比选类型描述（如：整合营销、营销活动等） */
  bidding_type_name?: string;
};
export type ResponseListBiddingSimpleVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingSimpleVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseSegmentConfigVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SegmentConfigVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseMapStringListLong = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: {
    [key: string]: any;
  };
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 字段定义 VO
 */
export type FieldDefinitionVo = {
  /** 属性 Code */
  property_code?: string;
  /** 是否必填 */
  required?: boolean;
  /** 字段状态：active / inactive */
  status?: string;
  property_schema?: PropertySchemaVo;
};
/**
 * 比选标段类目详情 VO
 */
export type SegmentCategoryDetailVo = {
  /** 类目 ID */
  id?: number;
  /** 类目编码（预留拓展） */
  code?: string;
  /** 类目名称 */
  name?: string;
  /** 状态：active=启用，locked=未启用 */
  status?: string;
  /** 字段数量 */
  field_count?: number;
  /** 创建时间 */
  created_at?: string;
  /** 最后更新时间 */
  updated_at?: string;
  /** 字段组定义列表 */
  template?: FieldDefinitionVo[];
};
export type ResponseSegmentCategoryDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: SegmentCategoryDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type EditRoleDetailVo = {
  id?: number;
  name?: string;
  demand_category?: {
    [key: string]: any;
  }[];
  supplier_type?: {
    [key: string]: any;
  }[];
  channel_supplier_type?: string[];
  perms_list?: {
    [key: string]: any;
  }[];
  role_category?: RoleCategory;
  is_support_online_apply?: boolean;
};
export type RubyResponseVoEditRoleDetailVo = {
  res_code?: number;
  res_msg?: string;
  data?: EditRoleDetailVo;
  version?: number;
  trace_id?: string;
};
export type NewRoleDetailVo = {
  demand_category?: {
    [key: string]: any;
  }[];
  supplier_type?: {
    [key: string]: any;
  }[];
  channel_supplier_type?: {
    [key: string]: any;
  }[];
  perms_list?: {
    [key: string]: any;
  }[];
};
export type RubyResponseVoNewRoleDetailVo = {
  res_code?: number;
  res_msg?: string;
  data?: NewRoleDetailVo;
  version?: number;
  trace_id?: string;
};
/**
 * 区域
 */
export type NameValueVoString = {
  name?: string;
  value?: string;
};
/**
 * 区域
 */
export type NameValueVoLong = {
  name?: string;
  value?: number;
};
/**
 * 是否支持星图度量 - 下拉选项
 */
export type NameValueVoBoolean = {
  name?: string;
  value?: boolean;
};
/**
 * 编辑页面-选项数据
 */
export type EditProductOptionsDataVo = {
  /** 区域 */
  area?: NameValueVoString[];
  /** 一级部门 */
  dept1?: NameValueVoLong[];
  /** 二级部门 */
  dept2?: NameValueVoLong[];
  /** 三级部门 */
  dept3?: NameValueVoLong[];
  /** 区域 */
  source?: NameValueVoString[];
  /** 区域 */
  ascription_type?: NameValueVoString[];
  /** 区域 */
  belonging_loc?: NameValueVoString[];
  /** 区域 */
  cost_company_id?: NameValueVoLong[];
  /** 区域 */
  product_type?: NameValueVoString[];
  /** 是否支持星图度量 - 下拉选项 */
  support_xingtu_measure?: NameValueVoBoolean[];
};
export type ProductManagerVo = {
  id?: number;
  name?: string;
  email?: string;
};
/**
 * 产品
 */
export type ProductEditShowVo = {
  id?: number;
  name?: string;
  identifier?: string;
  area?: string;
  source?: string;
  options?: EditProductOptionsDataVo;
  ascription_type?: string;
  belonging_loc?: string;
  cost_company_id?: number;
  dept1_id?: number;
  dept2_id?: number;
  dept3_id?: number;
  product_managers?: ProductManagerVo[];
  product_type?: string;
  support_xingtu_measure?: boolean;
};
export type RubyResponseVoProductEditShowVo = {
  res_code?: number;
  res_msg?: string;
  data?: ProductEditShowVo;
  version?: number;
  trace_id?: string;
};
/**
 * 新增页面-选项数据
 */
export type NewProductOptionsDataVo = {
  /** 区域 - 下拉选项 */
  area?: NameValueVoString[];
  /** 一级部门 - 下拉选项 */
  dept1?: NameValueVoLong[];
  /** 来源 - 下拉选项 */
  source?: NameValueVoString[];
  /** 产品归属 - 下拉选项 */
  ascription_type?: NameValueVoString[];
  /** 产品归属地 - 下拉选项 */
  belonging_loc?: NameValueVoString[];
  /** 费用归属公司 - 下拉选项 */
  cost_company_id?: NameValueVoLong[];
  /** 产品类型 - 下拉选项 */
  product_type?: NameValueVoString[];
  /** 是否支持星图度量 - 下拉选项 */
  support_xingtu_measure?: NameValueVoBoolean[];
};
export type NewProductDataVo = {
  options?: NewProductOptionsDataVo;
};
export type RubyResponseVoNewProductDataVo = {
  res_code?: number;
  res_msg?: string;
  data?: NewProductDataVo;
  version?: number;
  trace_id?: string;
};
export type MultiValueMapStringString = {
  all?: {
    [key: string]: any;
  };
  empty?: boolean;
  [key: string]: any;
};
export type RubyMenuCountItem = {
  key?: string;
  count?: number;
};
export type RubyResponseVoListRubyMenuCountItem = {
  res_code?: number;
  res_msg?: string;
  data?: RubyMenuCountItem[];
  version?: number;
  trace_id?: string;
};
export type DemandStandardCatalogSelectVo = {
  name?: string;
  value?: object;
  level?: number;
  id?: number;
  parent_id?: number;
  root_id?: number;
  code?: string;
  /** 排序， 从小到大 */
  sort?: number;
  bind_category_ids?: number[];
};
export type DemandCategorySelectVo = {
  name?: string;
  value?: object;
  level?: number;
  /** 使用范围 */
  scope?: number;
  /** 是否需要小喇叭策划 */
  trumpet_need_planner?: boolean;
  /** 方案审批支持类型, 0不支持，1能支持，2必须支持 */
  plan_approve?: number;
  /** 是否在需求分类模板中支持绑定执行数据 */
  support_bind_execution_data_in_template?: boolean;
  id?: number;
  parent_id?: number;
  root_id?: number;
  /** 排序， 从小到大 */
  sort?: number;
  /** 是否支持小喇叭敏捷模式 */
  support_trumpet_agile_mode?: boolean;
  bind_standard_ids?: number[];
};
/**
 * 价格库
 */
export type PriceLibrarySelectVo = {
  demand_standard_catalogs?: DemandStandardCatalogSelectVo[];
  demand_categories?: DemandCategorySelectVo[];
};
export type ResponsePriceLibrarySelectVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PriceLibrarySelectVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * Muse资源包创建状态
 */
export type MuseResourceStatusVo = {
  /** 资源创建状态：NOT_CREATED=未创建, CREATED=已创建, CREATE_FAILED=创建失败 */
  status?: string;
  /** 状态描述 */
  status_desc?: string;
  /** Muse资源包链接 */
  resource_url?: string;
};
export type ResponseMuseResourceStatusVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseResourceStatusVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * Muse文件夹及素材概况
 */
export type MuseFolderSummaryVo = {
  /** 合作内容ID */
  cooperate_account_id?: number;
  /** 合作内容名称 */
  cooperate_content?: string;
  /** 标品一级类别 */
  standard_catalog_level1?: string;
  /** Muse文件夹ID */
  folder_id?: string;
  /** Muse文件夹链接 */
  folder_url?: string;
  /** 素材文件数量 */
  asset_count?: number;
  /** 素材总大小（字节） */
  total_size?: number;
};
export type ResponseListMuseFolderSummaryVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseFolderSummaryVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * Muse归档状态
 */
export type MuseArchiveStatusVo = {
  /** 归档状态：NOT_ARCHIVED=未归档, ARCHIVED=已归档, ARCHIVE_FAILED=归档失败 */
  status?: string;
  /** 状态描述 */
  status_desc?: string;
};
export type ResponseMuseArchiveStatusVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseArchiveStatusVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 需求单基本信息
 */
export type MuseDemandInfoVo = {
  /** 需求单ID */
  demand_id?: number;
  /** 需求单号 */
  demand_no?: string;
  /** 需求标题 */
  demand_title?: string;
};
export type ResponseMuseDemandInfoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseDemandInfoVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseInvoiceDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: InvoiceDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseInquiryScoreRuleVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: InquiryScoreRuleVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 权限申请单列表项
 */
export type PermissionApplicationListVo = {
  /** 申请单 ID */
  id?: number;
  /** 申请单号 */
  application_no?: string;
  /** 申请人 user_id */
  applicant_user_id?: number;
  /** 申请人姓名 */
  applicant_name?: string;
  /** 申请人邮箱 */
  applicant_email?: string;
  /** 生效时长小时数，NULL=永久 */
  effective_duration_hours?: number;
  effective_duration?: EffectiveDuration;
  /** 是否永久（effective_duration_hours == null） */
  is_permanent?: boolean;
  oa_status?: OaState;
  /** OA 单号 */
  oa_id?: string;
  /** OA 单据跳转链接 */
  oa_url?: string;
  application_status?: PermissionApplicationStatus;
  /** 申请单状态中文名 */
  application_status_name?: string;
  /** OA 状态中文名 */
  oa_status_name?: string;
  /** 驳回原因 */
  oa_reject_reason?: string;
  /** 创建时间 */
  created_at?: string;
};
export type PageVoPermissionApplicationListVo = {
  total?: number;
  records?: PermissionApplicationListVo[];
};
export type ResponsePageVoPermissionApplicationListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PageVoPermissionApplicationListVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 我的角色权限列表项
 */
export type MyRolePermissionVo = {
  /** 角色 ID */
  role_id?: number;
  /** 角色名称 */
  role_name?: string;
  /** 角色标识/编码 */
  role_identify?: string;
  /** 角色分类 */
  role_category?: string;
  /** 权限开通时间 */
  granted_at?: string;
  /** 生效时长小时数，null=永久 */
  effective_duration_hours?: number;
  /** 生效时长文本（7天/30天/180天/360天/永久） */
  effective_duration_text?: string;
  /** 失效时间，null=永久 */
  expires_at?: string;
  /** 剩余天数（null=永久或已过期） */
  remaining_days?: number;
  permanent?: boolean;
};
export type ResponseListMyRolePermissionVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MyRolePermissionVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 批量删除角色权限请求
 */
export type DeleteMyRolesCmd = {
  /** 角色 ID 列表 */
  role_ids: number[];
};
/**
 * 角色分类树叶子节点
 */
export type RoleTreeLeafVo = {
  /** 角色名称 */
  name?: string;
  /** 角色 ID */
  value?: number;
};
/**
 * 角色分类树节点
 */
export type RoleCategoryTreeNodeVo = {
  /** 分类名称：营销/策划/商务/集团职能 */
  name?: string;
  /** 分类编码：MARKETING/PLANNING/BUSINESS/GROUP_FUNCTION */
  value?: string;
  /** Level 2 角色列表 */
  children?: RoleTreeLeafVo[];
};
export type ResponseListRoleCategoryTreeNodeVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: RoleCategoryTreeNodeVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 产品权限树叶子节点
 */
export type MyProductLeafVo = {
  /** user_belongs 表记录 ID */
  belong_id?: number;
  /** 产品 ID */
  product_id?: number;
  /** 产品名称 */
  product_name?: string;
  /** 产品编号/标识 */
  product_identifier?: string;
  /** 展示名: 产品名(产品编号)，若已停用则追加「（已停用）」 */
  display_name?: string;
  /** 产品状态 (0=启用, 1=锁定) */
  product_status?: number;
  /** 权限开通时间 */
  granted_at?: string;
  /** 失效时间，null=永久 */
  expires_at?: string;
};
/**
 * 产品权限树分类节点
 */
export type MyProductCategoryNode = {
  /** 产品分类: 0=互娱, 1=雷火, 2=音乐, 3=其他 */
  product_source?: number;
  /** 分类 code: huyu/leihuo/music/other_source */
  category_code?: string;
  /** 分类名称: 互娱/雷火/音乐/其他 */
  category_name?: string;
  /** 分类全部权限失效时间，null=永久 */
  charge_all_expires_at?: string;
  /** 用户在该分类下拥有的产品数 */
  owned_count?: number;
  /** 该分类 ACTIVE 产品总数 */
  total_active_count?: number;
  /** Level 2 具体产品列表 */
  products?: MyProductLeafVo[];
  charge_all?: boolean;
};
export type ResponseListMyProductCategoryNode = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MyProductCategoryNode[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 批量删除产品权限请求
 */
export type DeleteMyProductsCmd = {
  /** user_belongs 表主键 ID 列表（具体产品行） */
  belong_ids?: number[];
  /** 大类产品 code 列表: huyu/leihuo/music/other_source */
  category_codes?: string[];
};
export type AiProxyPermissionVo = {
  authorization?: string;
  token?: string;
  expires_seconds?: number;
};
export type ResponseAiProxyPermissionVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: AiProxyPermissionVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 单据动态节点顺序
 */
export type DemandFlowNodesDto = {
  /** 有序节点列表 */
  ordered_nodes?: DemandState[];
};
export type ResponseDemandFlowNodesDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandFlowNodesDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type BoostEffectDetailDto = {
  /** 主键ID */
  id?: number;
  /** 需求ID */
  demand_id?: number;
  demand_type?: MpcDemandTypeEnum;
  /** 合作账号ID */
  kol_code?: string;
  /** 合作账号-达人昵称 */
  kol_name?: string;
  /** 作品媒体平台 */
  platform?: string;
  /** 作品链接 */
  work_url?: string;
  /** 加热费用 */
  boost_cost?: number;
  boost_cost_currency?: CurrencyEnum;
  /** 加热效果播放量 */
  boost_view_count?: number;
  /** 加热开始时间 */
  boost_start_time?: string;
  /** 加热结束时间 */
  boost_end_time?: string;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** 内容上传超时时间标签 */
  delay_label?: string;
  /** 超时级别，0，1，2 */
  delay_grade?: number;
};
export type ResponseListBoostEffectDetailDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BoostEffectDetailDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type QcIntelligentReviewConfigDto = {
  url?: string;
  app_id?: string;
  token?: string;
  env?: string;
};
export type ResponseQcIntelligentReviewConfigDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: QcIntelligentReviewConfigDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 产品信息
 */
export type ProductValue = {
  /** 产品ID */
  id?: number;
  /** 产品标识 */
  identifier?: string;
  /** 产品名称 */
  name?: string;
};
/**
 * 一级单信息
 */
export type PdbcRootDemandInfoVo = {
  /** 一级单ID */
  demand_id?: number;
  /** 一级单名称 */
  name?: string;
  group_type?: DemandGroupType;
  /** 一级需求分类ID */
  l1_category_id?: number;
  /** 一级需求分类名称 */
  l1_category_name?: string;
  /** 预计开始时间 */
  expect_begin_at?: string;
  /** 预计结束时间 */
  expect_end_at?: string;
  priority?: PriorityEnum;
  product?: ProductValue;
  /** 需求负责人列表 */
  demand_principals?: UserSimpleVo[];
  /** 策划人员列表 */
  planners?: UserSimpleVo[];
  /** 费用类型 */
  expense_type?: string;
  /** 策划案/相关文件（来源 demand.kolPlanAttachments，name='kol_plan_attachs'） */
  kol_plan_attachments?: CommonAttachmentDto[];
  /** kol使用规划 */
  kol_usage_plan?: string;
};
/**
 * 汇总信息
 */
export type PdbcSummaryInfoVo = {
  total_budget?: Money;
  total_settled_price?: Money;
  total_dspf_settled_price?: Money;
  /** 预算使用率 */
  budget_usage_rate?: number;
};
/**
 * 供应商分组信息（三层聚合，仅确认报价节点）
 */
export type PdbcSupplierGroupVo = {
  /** 供应商分组行唯一标识，格式：gradientGroupRowKey_supplierId_demandSupplierId */
  row_key?: string;
  /** demand_supplier 表主键 */
  demand_supplier_id?: number;
  /** 供应商ID（来源 demand_supplier.supplier_id） */
  supplier_id?: number;
  /** 供应商全称（格式：name(serviceInfo)） */
  supplier_full_name?: string;
  /** 供应商名称（短名称） */
  supplier_name?: string;
  /** 风险标签列表 */
  risk_tags?: object[];
  /** 是否中选（demand_supplier_quote.winning=true） */
  is_winning?: boolean;
  /** 是否预设中选（仅一家报价时服务端预填 true） */
  is_selected_pre_fill?: boolean;
  /** 是否使用合同（noContractFlag 语义反转：true=不使用合同） */
  use_contract?: boolean;
  /** 合同编号 */
  contract_no?: string;
  /** 供应商比选方案附件列表（来源 demand_supplier.bidAttachments，name='bid_attachs'） */
  bid_attachs?: CommonAttachmentDto[];
  /** 报价方案（来源 demand_supplier_quote.programDesc，中选供应商对应的 quote 记录） */
  program_desc?: string;
  /** 额外权益（供应商已确认）：来源 demand_supplier_quote.expectRights 反序列化，2.3 起取代期望权益 */
  extra_rights?: DemandCooperateRightsValueDto[];
  /** 是否被供应商拒单 */
  reject_by_supplier?: boolean;
  /** 拒单原因 */
  reject_reason?: string;
  /** 是否超时未报价 */
  expired?: boolean;
  /** 备注说明 */
  note?: string;
};
/**
 * 梯度分组信息（二层聚合）
 */
export type PdbcGradientGroupVo = {
  /** 梯度分组行唯一标识，格式：executionCycleRowKey_gradient */
  row_key?: string;
  gradient?: GradientEnum;
  total_init_budget?: Money;
  /** 供应商分组列表（三层聚合，仅确认报价节点填充） */
  supplier_groups?: PdbcSupplierGroupVo[];
};
/**
 * 执行周期信息（一层聚合）
 */
export type PdbcExecutionCycleVo = {
  /** 行唯一标识，格式：startDate_endDate */
  row_key?: string;
  /** 执行周期开始时间 */
  begin_at?: string;
  /** 执行周期结束时间 */
  end_at?: string;
  total_init_budget?: Money;
  /** 梯度分组列表（二层聚合，仅效果买量类型有多个梯度） */
  gradient_groups?: PdbcGradientGroupVo[];
};
/**
 * 风险提示类型
 */
export type RiskTipTypeEnum = 'BUSINESS_LEADER' | 'PLANNER_LEADER';
/**
 * 风险提示信息
 */
export type RiskTipVo = {
  type?: RiskTipTypeEnum;
  /** 风险提示内容 */
  content?: string;
};
/**
 * 核价备注类型
 */
export type PriceCheckRemarkTypeEnum =
  | 'BUSINESS'
  | 'BUSINESS_LEADER'
  | 'PLAN'
  | 'PLAN_LEADER';
/**
 * 带类型的核价备注信息
 */
export type PriceCheckRemarkVo = {
  type?: PriceCheckRemarkTypeEnum;
  /** 文本内容 */
  text?: string;
  /** 文件内容 */
  files?: CommonAttachmentDto[];
};
/**
 * 二级单明细信息
 */
export type PdbcDemandConfirmInfoVo = {
  /** 执行周期行唯一标识 */
  execution_cycle_row_key?: string;
  /** 梯度分组行唯一标识 */
  gradient_group_row_key?: string;
  /** 供应商分组行唯一标识（仅确认报价节点，关联 SupplierGroupVO.rowKey） */
  supplier_group_row_key?: string;
  /** 二级单ID */
  demand_id?: number;
  /** 二级单名称 */
  demand_name?: string;
  state?: DemandStateValue;
  /** 投放渠道 */
  advertising_channel?: string;
  /** 投放形式 */
  advertising_form?: string;
  gradient?: GradientEnum;
  init_budget?: Money;
  budget?: Money;
  purchasing_method?: PurchasingMethodEnum;
  purchasing_strategy?: PurchasingStrategyEnum;
  /** 采购方式展示字符串（由采购方式+采购策略组合生成） */
  translated_purchasing_method?: string;
  /** 是否可编辑采购方式与采购策略（true=可编辑，false=只读） */
  is_purchase_strategy_editable?: boolean;
  plan_supplier?: DemandDetailsResDtoDemandSupplierDto;
  supplier?: DemandDetailsResDtoDemandSupplierDto;
  /** 报价截止时间 */
  quote_deadline?: string;
  /** 费用采购公司ID */
  cost_company_id?: number;
  /** 费用采购公司 */
  cost_company?: string;
  /** 是否使用合同 */
  use_contract?: boolean;
  /** 合同编号 */
  contract_no?: string;
  /** 整单验收要求 */
  accept_desc?: string;
  /** 整单验收要求-列表 */
  accept_list?: string[];
  /** 必要权益列表 */
  necessary_rights?: DemandCooperateRightsValueDto[];
  /** 期望权益列表 */
  expect_rights?: DemandCooperateRightsValueDto[];
  /** 期望权益对比数据列表（各供应商与需求方期望权益的差异） */
  expect_rights_diff_list?: SupplierQuoteRights[];
  kpi_type?: MediaKpiType;
  /** 主要考核指标 */
  main_kpi_values?: DemandKpiValueDto[];
  /** 辅助考核指标 */
  extra_kpi_values?: DemandKpiValueDto[];
  /** 历史风险提示列表（按类型区分：商务负责人、策划负责人，只读回显） */
  risk_tips?: RiskTipVo[];
  /** 风险提示内容（当前节点操作人填写，可编辑） */
  risk_tip?: string;
  /** 是否有风险提示 */
  has_risk_tip?: boolean;
  /** 备注 */
  note?: string;
  /** 附件列表 */
  attachment_list?: CommonAttachmentDto[];
  /** 核价备注列表（按类型区分：商务、策划、需求方，只读展示） */
  remarks?: PriceCheckRemarkVo[];
  /** 供应商比选方案及附件（2.1 节点：取策划选定供应商的 demand_supplier.bidAttachments，name='bid_attachs'） */
  bid_attachs?: CommonAttachmentDto[];
  /** 报价方案（2.1 节点：取策划选定供应商的 demandSupplierQuotes 中第一条 programDesc 不为 null 的值） */
  program_desc?: string;
  /** 最后更新时间 */
  updated_at?: string;
  /** 最后修改版本号（时间戳） */
  modify_version?: number;
};
/**
 * 被退回的需求单信息
 */
export type PdbcReturnedDemandVo = {
  /** 三级单ID */
  id?: number;
  /** 三级单名称 */
  name?: string;
  /** 退回原因 */
  reason?: string;
  /** 退回时间 */
  return_time?: string;
  /** 退回人 */
  return_user?: string;
  from_state?: DemandStateValue;
};
/**
 * 合并询报价批量面板信息
 */
export type PlanDemandBatchConfirmInfoVo = {
  state?: DemandStateValue;
  root_demand_info?: PdbcRootDemandInfoVo;
  summary_info?: PdbcSummaryInfoVo;
  /** 执行周期信息列表（一层聚合，按开始时间升序） */
  execution_cycles?: PdbcExecutionCycleVo[];
  /** 二级单明细列表 */
  demands?: PdbcDemandConfirmInfoVo[];
  /** 被退回的需求单列表 */
  returned_demands?: PdbcReturnedDemandVo[];
};
export type ResponsePlanDemandBatchConfirmInfoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PlanDemandBatchConfirmInfoVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 考核指标调整后
 */
export type DemandKpiValueDiffDto = {
  /** 序号 */
  index_id?: number;
  kpi_type?: DemandQuotaTypeEnum;
  /** 指标分类名称 */
  kpi_type_name?: string;
  kpi_schema: DemandKpiSchemaData;
  /** demand_kpi.code */
  kpi_code: string;
  /** KPI期望值(调整前) */
  before_expect_value?: string;
  /** KPI期望值(调整后) */
  after_expect_value?: string;
  diff_type?: DiffTypeEnum;
};
/**
 * 调整结果：1：待生效，2：已生效，3：未生效
 */
export type KpiAdjustmentResultEnum =
  | 'TO_BE_EFFECTIVE'
  | 'EFFECTIVE'
  | 'NOT_EFFECTIVE';
/**
 * KPI调整单明细
 */
export type DemandKpiAdjustmentContentVo = {
  /** kpi调整单的主键ID */
  kpi_adjustment_id?: number;
  /** kpi调整单的编号 */
  kpi_adjustment_number?: string;
  /** 需求单Id */
  demand_id?: number;
  /** 需求预期调整前 */
  expectation_before?: string;
  /** 需求预期调整后 */
  expectation_after?: string;
  /** 考核指标调整前 */
  kpi_before_list?: DemandKpiValueDto[];
  /** 考核指标调整后 */
  kpi_after_list?: DemandKpiValueDiffDto[];
  /** 调整批注说明 */
  adjustment_note?: string;
  /** 考核指标调整原因 */
  adjustment_reason?: string;
  /** OA审批概览 */
  oa_audit_desc_list?: OaProcessDescVo[];
  /** 发起时间 */
  submit_time?: string;
  kpi_adjustment_result?: KpiAdjustmentResultEnum;
  /** 附件信息 */
  attachments?: AttachmentDto[];
};
export type ResponseListDemandKpiAdjustmentContentVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandKpiAdjustmentContentVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 价格类型（枚举）：中标报价、结案报价
 */
export type DemandSupplierQuoteStageEnum = 'BIDDING_QUOTE' | 'MARKING_QUOTE';
export type DemandSupplierQuoteDetailDto = {
  /** 2级分类id */
  l2_category_id?: number;
  /** 2级分类名 */
  l2_category_name?: string;
  /** 3级分类id */
  l3_category_id?: number;
  /** 3级分类名 */
  l3_category_name?: string;
  /** 列1的值 */
  col1_value?: string;
  /** 列2的值 */
  col2_value?: string;
  /** 当前的层级，顶层是0 */
  level?: number;
  /** 子节点 */
  children?: DemandSupplierQuoteDetailDto[];
  /** 单价1 */
  unit_price?: number;
  /** 单位1 */
  unit1?: string;
  /** 数量1 */
  quantity1?: number;
  /** 单位2 */
  unit2?: string;
  /** 数量2 */
  quantity2?: number;
  /** 总价 */
  total_price?: number;
  info_value?: {
    [key: string]: any;
  };
  quote_stage?: DemandSupplierQuoteStageEnum;
  /** 是否和中标价格有变化 */
  change?: boolean;
  /** 价格类型：中标报价、结案报价 */
  quote_stage_name?: string;
  name?: string;
};
export type DemandSupplierQuoteDetailMergeDto = {
  /** 合作内容id */
  cooperate_account_id?: number;
  /** 供应商报价详情索引，从1开始计数 */
  index_id?: number;
  name?: string;
  children?: DemandSupplierQuoteDetailDto[];
};
export type DemandSupplierQuoteDetailMergeDiffDto = {
  list?: DemandSupplierQuoteDetailMergeDto[];
  head?: string[][];
};
export type ResponseDemandSupplierQuoteDetailMergeDiffDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSupplierQuoteDetailMergeDiffDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandSupplierQuoteDetailDiffDto = {
  list?: DemandSupplierQuoteDetailDto[];
  head?: string[][];
};
export type ResponseDemandSupplierQuoteDetailDiffDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandSupplierQuoteDetailDiffDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 退回的三级单信息
 */
export type DnbbaiReturnedDemandInfoVo = {
  /** 三级单ID */
  id?: number;
  /** 三级单名称 */
  name?: string;
  /** 退回原因 */
  reason?: string;
  /** 退回时间 */
  return_time?: string;
  /** 退回人 */
  return_user?: string;
  from_state?: DemandStateValue;
};
/**
 * 一级单信息
 */
export type DnbbaiRootDemandInfoVo = {
  /** 一级单ID */
  demand_id?: number;
  /** 一级单名称 */
  name?: string;
  group_type?: DemandGroupType;
  /** 一级需求分类ID */
  l1_category_id?: number;
  /** 一级需求分类名称 */
  l1_category_name?: string;
  /** 预计开始时间 */
  expect_begin_at?: string;
  /** 预计结束时间 */
  expect_end_at?: string;
  priority?: PriorityEnum;
  product?: ProductValue;
  /** 产品类型，用于判断产品归属（如 music=云音乐产品） */
  product_type?: string;
  /** 需求负责人列表 */
  demand_principals?: UserSimpleVo[];
  product_area?: ProductAreaEnum;
};
/**
 * 备注，包含了风险信息
 */
export type RemarkVo = {
  node_state?: DemandStateValue;
  /** 节点名 */
  node_name?: string;
  /** 风险提示 */
  risk_info?: string;
  user?: UserSimpleVo;
  /** 备注 */
  note?: string;
  /** 附件列表 */
  attachment_list?: BlobVo[];
};
/**
 * 验收备注
 */
export type DemandAcceptRemarksVo = {
  remarks?: RemarkVo[];
};
/**
 * 超额验收OA流程信息
 */
export type OaInfoVo = {
  oa_id?: string;
  oa_status?: OaState;
  oa_url?: string;
};
/**
 * AI验收信息
 */
export type DemandAiAcceptInfoVo = {
  acceptance_conclusion?: AcceptanceConclusionEnum;
  /** 智能验收不通过次数 */
  ai_reject_count?: number;
  /** AI验收分析报告 */
  ai_analysis_report?: string;
  summary?: string;
  is_amount_consistent?: boolean;
  has_abnormal?: boolean;
};
/**
 * 三级单验收信息
 */
export type DnbbaiDemandAcceptInfoVo = {
  /** 需求单号 */
  demand_id?: number;
  /** 一级需求类型ID */
  l1_category_id?: number;
  /** 二级需求类型ID */
  l2_category_id?: number;
  /** 三级需求类型ID */
  l3_category_id?: number;
  /** 完整的三级类目名称，格式为：一级类目-二级类目-三级类目 */
  full_category_name?: string;
  /** 0=原始，1=报价结构化, 2=结案结构化 */
  structured_version?: number;
  /** 供应商详情 */
  demand_suppliers?: DemandDetailsResDtoDemandSupplierDto[];
  /** 项目编号 */
  project_code?: string;
  /** 是否加急验收 */
  urgent?: boolean;
  state?: DemandStateValue;
  /** 供应商简称 */
  supplier_short_name?: string;
  settle_price?: Money;
  quote_price?: Money;
  /** 是否超额验收 */
  over_amount_flag?: boolean;
  over_amount_cny?: Money;
  /** 发票信息 */
  invoice_list?: InvoiceDetailDto[];
  invoice_belong_area?: InvoiceBelongAreaEnum;
  /** 是否海外INVOICE */
  is_oversea_invoice?: boolean;
  /** 最近一次审批通过的超额验收OA单号 */
  last_approved_over_amount_oa_id?: string;
  /** 合作内容 */
  cooperate_accounts?: JournalDemandDetailsResDtoCooperateAccountDto[];
  /** 结案附件 */
  settle_report_attachs?: CommonAttachmentDto[];
  /** 策划总结报告附件 */
  planner_report_attachs?: CommonAttachmentDto[];
  /** 需求方评估报告附件 */
  demand_evaluation_attachs?: CommonAttachmentDto[];
  /** 结算单附件 */
  statement_attachs?: CommonAttachmentDto[];
  /** 已签署结算单附件 */
  signed_statement_attachs?: CommonAttachmentDto[];
  /** 加急理由 */
  urgent_reason?: string;
  /** 综合得分 */
  overall_score?: number;
  /** 过程评分 */
  score?: number;
  /** 策划评分项 */
  evaluate_scores?: EvaluateScoreVo[];
  /** 结果评分 */
  result_score?: number;
  /** 需求方评分项目 */
  demand_manger_evaluate_scores?: EvaluateScoreVo[];
  /** 执行情况描述（策划填写） */
  dspf_desc?: string;
  /** 验收备注 */
  note?: string;
  /** 策划复核意见 */
  suggestion?: string;
  /** 策划负责人复核意见 */
  planner_leader_accept_suggestion?: string;
  /** 需求方验收意见/效果描述（需求方填写） */
  demander_accept_suggestion?: string;
  accept_remarks?: DemandAcceptRemarksVo;
  /** 是否全部上传截图（主平台/付费分发） */
  is_screenshot_done?: number;
  /** 是否需要供应商提交结构化结案报告 */
  need_structured_settle_report?: boolean;
  /** 是否需要供应商提交合作内容维度的结算金额 */
  need_cooperate_account_supplier_settle_price?: boolean;
  /** 内容营销结案结构化，1：新单子，0：老单子 */
  content_marketing_case_structured_version?: number;
  /** 是否需要供应商上传内容营销附件, 0:不需要, 1:需要, -1:不可配置 */
  need_content_marketing_attach?: number;
  /** 是否早于定标执行 */
  special_matters_reporting?: boolean;
  /** 是否有发票 */
  invoice_exist?: boolean;
  /** 数易MPC单号 */
  dspf_mpc_no?: string;
  oa_status?: OaState;
  /** oa流程号 */
  oa_id?: string;
  /** oa url */
  oa_url?: string;
  oa_approved_settle_price?: Money;
  /** 是否跳过超额验收审批 */
  skip_over_amount_approval?: boolean;
  /** 是否需要上传/查看加热助推数据 */
  need_boost_effect_data?: boolean;
  over_amount_oa_info?: OaInfoVo;
  /** 辅助自定义指标 */
  common_kpi_values?: DemandKpiValueDto[];
  /** 是否提交了结构化结案报告 */
  submitted_structured_settle_report?: boolean;
  /** 供应商提交合作内容维度的结算金额总计 */
  cooperate_account_supplier_settle_price_sum?: number;
  /** 结算方式 */
  ad_settle_way?: string;
  /** 最后更新时间 */
  updated_at?: string;
  /** 最后修改版本号（时间戳） */
  modify_version?: number;
  flow_action_info_value?: DemandFlowActionInfoValue;
  ai_accept_info?: DemandAiAcceptInfoVo;
};
export type DemandNoneBindingBatchAcceptInfoVo = {
  /** 退回的三级单信息 */
  returned_demands?: DnbbaiReturnedDemandInfoVo[];
  state?: DemandStateValue;
  qc_accept_priority?: QcAcceptPriorityEnum;
  root_demand_info?: DnbbaiRootDemandInfoVo;
  /** 三级单列表 */
  demands?: DnbbaiDemandAcceptInfoVo[];
};
export type ResponseDemandNoneBindingBatchAcceptInfoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandNoneBindingBatchAcceptInfoVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 退回的三级单信息
 */
export type ReturnedDemandInfoVo = {
  /** 三级单ID */
  id?: number;
  /** 三级单名称 */
  name?: string;
  /** 退回原因 */
  reason?: string;
  /** 退回时间 */
  return_time?: string;
  /** 退回人 */
  return_user?: string;
  from_state?: DemandStateValue;
};
/**
 * 一级单信息
 */
export type DbaiRootDemandInfoVo = {
  /** 一级单ID */
  demand_id?: number;
  /** 一级单名称 */
  name?: string;
  group_type?: DemandGroupType;
  /** 一级需求分类ID */
  l1_category_id?: number;
  /** 一级需求分类名称 */
  l1_category_name?: string;
  /** 预计开始时间 */
  expect_begin_at?: string;
  /** 预计结束时间 */
  expect_end_at?: string;
  priority?: PriorityEnum;
  product?: ProductValue;
  /** 产品类型，用于判断产品归属（如 music=云音乐产品） */
  product_type?: string;
  /** 需求负责人列表 */
  demand_principals?: UserSimpleVo[];
  /** 策划人员列表 */
  planners?: UserSimpleVo[];
};
/**
 * 汇总信息
 */
export type DbaiSummaryInfoVo = {
  total_budget?: Money;
  total_settled_price?: Money;
  total_dspf_settled_price?: Money;
  /** 预算使用率 */
  budget_usage_rate?: number;
};
/**
 * 执行周期信息
 */
export type DbaiExecutionCycleVo = {
  /** 行唯一标识 */
  row_key?: string;
  /** 执行周期开始时间 */
  begin_at?: string;
  /** 执行周期结束时间 */
  end_at?: string;
  total_init_budget?: Money;
  /** 选择的辅助指标列表 */
  extra_kpis?: DemandKpiVo[];
};
/**
 * 三级单验收信息
 */
export type DbaiDemandAcceptInfoVo = {
  /** 执行周期行唯一标识 */
  execution_cycle_row_key?: string;
  dspf_quota_data?: DspfQuotaData;
  /** 需求单号 */
  demand_id?: number;
  /** 数易MPC单号 */
  dspf_mpc_no?: string;
  /** 是否有发票 */
  invoice_exist?: boolean;
  /** 是否加急验收 */
  urgent?: boolean;
  state?: DemandStateValue;
  /** 投放渠道名称 */
  advertising_channel?: string;
  /** 供应商简称 */
  supplier_short_name?: string;
  /** 投放形式 */
  advertising_form?: string;
  init_budget?: Money;
  /** 调整的预算金额 */
  adjusted_budget?: number;
  /** 调整的预算金额的最小的合适值，如果再小会触发超额 */
  min_adjusted_budget?: number;
  budget?: Money;
  dspf_settled_price?: Money;
  supplier_settled_price?: Money;
  /** 是否超额 */
  over_amount?: boolean;
  /** 超额说明 */
  over_amount_remark?: string;
  kpi_type?: MediaKpiType;
  /** 主要指标 */
  main_kpi_values?: DemandKpiValueDto[];
  /** 辅助自定义指标 */
  extra_kpi_values?: DemandKpiValueDto[];
  /** 结案附件 */
  settle_report_attachs?: CommonAttachmentDto[];
  /** 策划总结报告附件 */
  planner_report_attachs?: CommonAttachmentDto[];
  /** 需求方评估报告附件 */
  demand_evaluation_attachs?: CommonAttachmentDto[];
  /** 结算单附件 */
  statement_attachs?: CommonAttachmentDto[];
  /** 已签署结算单附件 */
  signed_statement_attachs?: CommonAttachmentDto[];
  kpi_result?: DemandKpiAchievementStatus;
  /** 未达标说明 */
  kpi_result_remark?: string;
  /** 加急理由 */
  urgent_reason?: string;
  /** 综合得分 */
  overall_score?: number;
  /** 过程评分 */
  score?: number;
  /** 策划评分项 */
  evaluate_scores?: EvaluateScoreVo[];
  /** 结果评分 */
  result_score?: number;
  /** 需求方评分项目 */
  demand_manger_evaluate_scores?: EvaluateScoreVo[];
  /** 执行情况描述（策划填写） */
  dspf_desc?: string;
  /** 验收备注 */
  note?: string;
  /** 策划复核意见 */
  suggestion?: string;
  /** 策划负责人复核意见 */
  planner_leader_accept_suggestion?: string;
  /** 需求方验收意见/效果描述（需求方填写） */
  demander_accept_suggestion?: string;
  accept_remarks?: DemandAcceptRemarksVo;
  /** 最后更新时间 */
  updated_at?: string;
  /** 最后修改版本号（时间戳） */
  modify_version?: number;
  /** 发票信息，海外产品有发票时返回发票模块数据（只读），无发票为null */
  invoice_info?: object;
  /** 项目编号 */
  project_code?: string;
  /** 是否是虚拟项目 */
  is_virtual_project?: boolean;
  /** 项目编号是否可编辑，虚拟项目编号时为true，非虚拟为false */
  project_code_editable?: boolean;
  /** 结案效果是否展示，根据需求类型和绑定执行数据判断 */
  closing_result_visible?: boolean;
  ai_accept_info?: DemandAiAcceptInfoVo;
  flow_action_info_value?: DemandFlowActionInfoValue;
  gradient?: GradientEnum;
  /** 是否有预算变更记录（按所属二级单是否存在已生效的预算调整单判断） */
  has_adjust_record?: boolean;
};
/**
 * 需求批量验收信息
 */
export type DemandBatchAcceptInfoVo = {
  /** 退回的三级单信息 */
  returned_demands?: ReturnedDemandInfoVo[];
  state?: DemandStateValue;
  qc_accept_priority?: QcAcceptPriorityEnum;
  root_demand_info?: DbaiRootDemandInfoVo;
  summary_info?: DbaiSummaryInfoVo;
  /** 超额说明 */
  over_amount_remark?: string;
  /** 执行周期信息列表 */
  execution_cycles?: DbaiExecutionCycleVo[];
  /** 三级单列表 */
  demands?: DbaiDemandAcceptInfoVo[];
};
export type ResponseDemandBatchAcceptInfoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandBatchAcceptInfoVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandStandardLevelVo = {
  name?: string;
  value?: string;
  desc?: string;
  levels?: string[];
};
export type ResponseListDemandStandardLevelVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandStandardLevelVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 引用的标品信息
 */
export type TemplateStandardItemVo = {
  /** 几级标品 */
  level?: number;
  /** 编码 */
  code?: string;
  /** 在策划拆单中使用 */
  use_in_design?: boolean;
  /** 在供应商报价使用 */
  use_in_quote?: boolean;
  /** 必填 */
  required?: boolean;
  /** 展示名称 */
  label?: string;
  /** 供应商可修改 */
  outsource_modify?: boolean;
  /** 选择的一级标品 */
  standard_ids?: number[];
  /** 一级标品信息 */
  standards?: DemandStandardCatalogVo[];
};
/**
 * 属性列表
 */
export type PropertyRefVo = {
  /** 属性ID */
  property_id?: number;
  required_mode?: RequiredModeEnum;
  required_condition?: RequiredConditionData;
  options?: ConditionOptionsData;
  /** 属性名称 */
  property_name?: string;
  /** 属性code */
  property_code?: string;
  /** 属性字段类型 */
  property_type?: string;
  /** 属性选项（原始） */
  property_options?: string[];
  /** 合同附件是否显示 */
  contract_attachment_visible?: boolean;
};
/**
 * 报价中引用的属性列表
 */
export type PropertyRefInQuoteVo = {
  /** 属性ID */
  property_id?: number;
  required_mode?: RequiredModeEnum;
  required_condition?: RequiredConditionData;
  options?: ConditionOptionsData;
  /** 属性名称 */
  property_name?: string;
  /** 属性code */
  property_code?: string;
  /** 属性字段类型 */
  property_type?: string;
  /** 属性选项（原始） */
  property_options?: string[];
  /** 合同附件是否显示 */
  contract_attachment_visible?: boolean;
  /** 供应商可修改 */
  outsource_modify?: boolean;
};
/**
 * 分发平台报价信息
 */
export type TemplateQuoteItemVo = {
  /** 编码，前端生成 */
  code?: string;
  /** 字段名称 */
  label?: string;
  /** 字段类型 */
  type?: string;
  required_mode?: RequiredModeEnum;
  required_condition?: RequiredConditionData;
  options?: ConditionOptionsData;
  /** 是否预填 */
  prefill?: boolean;
  /** 供应商可修改 */
  outsource_modify?: boolean;
};
/**
 * 期望权益列表
 */
export type TemplateRightsItemVo = {
  /** 权益id */
  id?: number;
  /** 回显：权益内容 */
  content?: string;
  /** 回显：权益类别 */
  category_name?: string;
};
export type DemandCategoryPropertyTemplateDetailVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  type?: DemandCategoryPropertyTemplateTypeEnum;
  /** 分类id */
  category_id?: number;
  /** 分类名称 */
  category_names?: string;
  /** 是否启用 */
  status?: boolean;
  /** 引用的标品信息 */
  standard_items?: TemplateStandardItemVo[];
  /** 属性列表 */
  items?: PropertyRefVo[];
  /** 报价中引用的属性列表 */
  items_in_quote?: PropertyRefInQuoteVo[];
  /** 报价类型 */
  quote_type?: number;
  /** 报价其他信息列表 */
  quote_items?: TemplateQuoteItemVo[];
  /** 分发平台报价信息 */
  platform_items?: TemplateQuoteItemVo[];
  /** 是否有差旅餐饮费用 */
  travel_catering_fee?: boolean;
  travel_catering_options?: ConditionOptionsData;
  unit_type?: QuoteUnitTypeEnum;
  /** 必要权益列表 */
  necessary_rights_items?: TemplateRightsItemVo[];
  /** 期望权益列表 */
  except_rights_items?: TemplateRightsItemVo[];
  /** 验收标准 */
  accept_desc?: string;
  amount_average_type?: AmountAverageTypeEnum;
  /** 自定义分摊配置列表 */
  amount_average_configs?: AmountAverageConfigData[];
  /** 是否含AI模板 */
  contain_ai?: boolean;
  ai_ratio_condition?: RequiredConditionData;
};
export type ResponseDemandCategoryPropertyTemplateDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandCategoryPropertyTemplateDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseQuoteTempVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: QuoteTempVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseDemandKpiVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandKpiVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 需求单发票信息响应DTO
 */
export type DemandInvoiceInfoResDto = {
  /** 需求单ID */
  demand_id?: number;
  /** 发票信息列表 */
  invoice_list?: InvoiceDetailDto[];
  invoice_belong_area?: InvoiceBelongAreaEnum;
  /** 是否海外INVOICE */
  is_oversea_invoice?: boolean;
};
export type ResponseDemandInvoiceInfoResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandInvoiceInfoResDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseDemandCooperatePropertyVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandCooperatePropertyVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseContractTemplateVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ContractTemplateVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListContractTypeVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ContractTypeVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListPropertyRefDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PropertyRefDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandCooperatePropertiesVo = {
  /** 标品 */
  standards?: TemplateStandardItemDto[];
  /** 属性 */
  properties?: PropertyRefDto[];
  /** 必要权益 */
  necessary_rights_list?: DemandRightsContentVo[];
  /** 期望权益 */
  expect_rights_list?: DemandRightsContentVo[];
  /** 验收要求 */
  accept_desc?: string;
  /** 验收要求列表 */
  accept_list?: string[];
  /** 版本号 */
  version?: number;
};
export type ResponseDemandCooperatePropertiesVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandCooperatePropertiesVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseOaProcessInfoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OaProcessInfoVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseClosedLoopDemandDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ClosedLoopDemandDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ClosedLoopTreeNodeVo = {
  id?: number;
  root_id?: number;
  name?: string;
  state?: string;
  type?: ClosedLoopDemandType;
  child?: ClosedLoopTreeNodeVo[];
};
export type ResponseClosedLoopTreeNodeVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ClosedLoopTreeNodeVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseCategoryBusinessMemberConfigVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: CategoryBusinessMemberConfigVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListUserSimpleVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: UserSimpleVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 预算预警配置VO
 */
export type BudgetAlertConfigVo = {
  /** 配置ID */
  id?: number;
  /** 需求单ID */
  demand_id?: number;
  /** 需求单层级: 0-一级单, 1-二级单 */
  demand_depth?: number;
  /** 需求单名称 */
  demand_name?: string;
  /** 需求单编号 */
  demand_number?: string;
  /** 是否开启预警 */
  enabled?: boolean;
  /** 是否已通知 */
  notified?: boolean;
  /** 最后通知时间 */
  last_notified_at?: string;
  /** 预算金额 */
  budget?: number;
  /** 预算币种 */
  budget_currency?: string;
  /** 执行开始日期 */
  begin_at?: string;
  /** 执行结束日期 */
  end_at?: string;
  /** 需求状态 */
  state?: string;
};
export type ResponseListBudgetAlertConfigVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BudgetAlertConfigVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 业务任务轮询状态（轻量级，适合高频轮询）
 */
export type BizTaskPollingVo = {
  /** 任务ID */
  id?: number;
  status?: BizTaskStatus;
  /** 状态描述 */
  status_desc?: string;
  /** 进度类型: 0-确定进度（有百分比）1-不确定进度（仅文案） */
  progress_type?: number;
  /** 当前进度 0.0~100.0（仅确定进度有意义） */
  progress?: number;
  /** 进度说明文案 */
  progress_message?: string;
  /** 失败/超时原因 */
  error_message?: string;
  /** 已重试次数 */
  retry_count?: number;
  /** 开始执行时间 */
  started_at?: string;
  /** 完成时间 */
  completed_at?: string;
};
export type ResponseBizTaskPollingVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BizTaskPollingVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseBiddingQuoteTemplateTableVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingQuoteTemplateTableVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 报价单配置模板详情 VO
 */
export type BiddingQuoteTemplateDetailVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 需求类型ID列表 */
  demand_category_ids?: number[];
  /** 需求类型名称列表 */
  demand_category_names?: string[];
  /** 标段类目ID */
  segment_category_id?: number;
  /** 方案设计字段配置 */
  design_fields?: BiddingFieldConfig[];
  /** 报价字段配置 */
  quote_fields?: BiddingFieldConfig[];
  /** 标段字段配置 */
  segment_fields?: BiddingFieldConfig[];
  /** 报价金额模板类型 */
  type?: number;
  /** 是否生成合同 */
  generate_contract?: boolean;
  /** 状态 */
  status?: boolean;
};
export type ResponseBiddingQuoteTemplateDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingQuoteTemplateDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseBiddingNotificationTemplateVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingNotificationTemplateVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListBiddingEvaluationMemberDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingEvaluationMemberDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商报价明细统一表格
 */
export type BiddingSupplierQuoteTableVo = {
  /** 多 Tab（按费用类型） */
  tabs?: SupplierQuoteTab[];
  /** 报价总金额（来自 bidding_supplier_quote；supplierId 为空时为 null） */
  quote_total_amount?: number;
  /** 报价状态：draft / submitted；supplierId 为空时为 null */
  status?: string;
};
export type ResponseBiddingSupplierQuoteTableVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingSupplierQuoteTableVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseListBiddingQuoteTemplateTableVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingQuoteTemplateTableVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ResponseMapStringObject = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: {
    [key: string]: any;
  };
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type NdaTemplateTypeDownloadEnum = 'COMMON';
export type AttachmentDownloadVo = {
  blob_id?: number;
  download_url?: string;
};
export type ResponseListAttachmentDownloadVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: AttachmentDownloadVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 比选方案模板详情VO
 */
export type BiddingPlanTemplateDetailVo = {
  /** 数据库主键ID */
  id?: number;
  /** 版本号 */
  version?: number;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  created_by_user?: UserSimpleVo;
  updated_by_user?: UserSimpleVo;
  /** 模板ID */
  template_id?: number;
  /** 模板名称 */
  template_name?: string;
  /** 比选需求类型 */
  bidding_type_v2s?: string[];
  /** 需求类型名称 */
  bidding_type_v2_names?: string[];
  /** 生效状态   1=启用,0=禁用 */
  status?: number;
  /** 各方评分信息 */
  score_infos?: ScoreDetailVo[];
};
export type ResponseBiddingPlanTemplateDetailVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: BiddingPlanTemplateDetailVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 菜单
 */
export type MenuInfoDto = {
  /** 菜单key */
  key?: string;
  /** 菜单名称 */
  name?: string;
  /** 子菜单信息 */
  children?: MenuInfoDto[];
  /** 是否默认 */
  default?: boolean;
};
/**
 * watermark_options
 */
export type WatermarkOptionDto = {
  opacity?: number;
  size?: number;
  text?: string;
};
export type UserStatus = 'ACTIVE' | 'LOCKED';
/**
 * 员工类型
 */
export type EmpTypeEnum =
  | 'UNKNOWN'
  | 'NORMAL'
  | 'INTERN'
  | 'COOPERATOR'
  | 'OUTER'
  | 'DISPATCHER';
export type UserInfoDto = {
  /** 用户ID */
  id?: number;
  /** 用户姓名 */
  name?: string;
  /** 用户工号 */
  job_number?: string;
  /** 用户邮箱 */
  email?: string;
  /** 用户账号 */
  login?: string;
  /** 用户状态 */
  mpc_status?: string;
  /** 是否为管理员 */
  admin?: boolean;
  /** 用户头像url */
  avatar_url?: string;
  /** 用户权限列表 */
  permissions?: string[];
  /** 菜单 */
  menus?: MenuInfoDto[];
  /** 灰度配置 */
  gray_config?: {
    [key: string]: any;
  };
  /** 是否初始化默认语言 */
  is_init_default_lang?: boolean;
  /** 可用语言列表 */
  available_lang?: string[];
  /** 语言 */
  language?: string;
  /** 通知配置 */
  notification_setting?: {
    [key: string]: any;
  };
  /** sm_audited_detail */
  sm_audited_detail?: {
    [key: string]: any;
  };
  watermark_options?: WatermarkOptionDto;
  /** 是否拥有角色权限 */
  has_any_role?: boolean;
  /** 是否拥有产品权限 */
  has_any_product?: boolean;
  status?: UserStatus;
  emp_type_enum?: EmpTypeEnum;
  manager_info?: UserInfoDto;
};
export type ResponseUserInfoDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: UserInfoDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type LoginUserSimpleInfoDto = {
  /** 用户ID */
  id?: number;
  /** 是否为管理员 */
  name?: string;
  /** cookie信息 */
  cookies?: {
    name?: string;
    value?: string;
    version?: number;
    comment?: string;
    domain?: string;
    max_age?: number;
    path?: string;
    secure?: boolean;
    http_only?: boolean;
  }[];
};
export type ResponseLoginUserSimpleInfoDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: LoginUserSimpleInfoDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type ExternalKpiValueVo = {
  /** 需求合作内容ID */
  demand_cooperate_account_id?: number;
  /** demand_kpi.code */
  kpi_code?: string;
  /** KPI实际值 */
  actual_value?: string;
  label?: DemandKpiLabel;
  label_obj?: LabelObj;
};
export type ResponseListExternalKpiValueVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: ExternalKpiValueVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type DemandWorkInfoVo = {
  /** 作品数量 */
  num?: number;
  /** 智能营销平台作品状态，0=正常，1=异常 */
  attach_flag?: number;
};
export type ResponseDemandWorkInfoVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: DemandWorkInfoVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type OutsourceSupplierResDto = {
  /** 主键 */
  id?: number;
  /** full_name */
  full_name?: string;
  /** short_name */
  short_name?: string;
  /** 是否当前选中 */
  selected?: boolean;
  /** 是否是新供应商 */
  is_new?: boolean;
  /** 服务类型 */
  channel_type?: string;
  /** 服务类型名称 */
  channel_type_name?: string;
};
export type ResponseListOutsourceSupplierResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceSupplierResDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 预付单列表项
 */
export type PrepaymentListVo = {
  /** 预付单ID */
  id?: number;
  /** 预付单编号 */
  prepayment_no?: string;
  /** 关联需求单ID */
  demand_id?: number;
  /** 预付金额 */
  prepayment_amount?: number;
  /** 币种 */
  currency?: string;
  /** 预付单状态: 1-待发起 2-发票待上传 3-审批中 4-付款中 5-已完成 6-已废弃 */
  status?: number;
  /** 预付单状态描述 */
  status_desc?: string;
  /** 发票方式: 1-有票预付 2-无票预付 */
  invoice_method?: number;
  /** 发票方式描述 */
  invoice_method_desc?: string;
  /** 供应商ID */
  supplier_id?: number;
  /** 供应商名称 */
  supplier_name?: string;
  /** 创建人ID */
  created_by?: number;
  /** 创建人姓名 */
  creator_name?: string;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** 预付款单号 */
  expense_control_no?: string;
  /** 发票数量 */
  invoice_count?: number;
  /** OA流程号 */
  oa_process_no?: string;
  oa_status?: OaState;
};
export type ResponseListPrepaymentListVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: PrepaymentListVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * Muse上传页面URL
 */
export type MuseUploadUrlVo = {
  /** Muse上传页面URL */
  upload_url?: string;
  /** 上传鉴权token */
  token?: string;
  /** 资源包ID */
  resource_pkg_id?: string;
  /** 文件夹ID */
  folder_id?: string;
  /** 文件夹名称 */
  folder_name?: string;
};
export type ResponseMuseUploadUrlVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseUploadUrlVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * Muse 文件上传凭证
 */
export type MuseUploadPermissionVo = {
  /** 鉴权 token */
  token?: string;
  /** 上传地址（链接 2 小时内有效） */
  upload_url?: string;
};
export type ResponseMuseUploadPermissionVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseUploadPermissionVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 合作内容明细项
 */
export type CooperateContentItem = {
  /** 合作内容ID */
  cooperate_account_id?: number;
  /** 合作内容 */
  cooperate_content?: string;
  /** 类别 */
  category?: string;
  /** 合作项目 */
  cooperate_project?: string;
  /** 合作详情 */
  cooperate_detail?: string;
  /** 数量 */
  quantity?: number;
  /** 交付类型：ASSET=资产类交付, VOUCHER=凭证类交付 */
  delivery_type?: string;
  /** Muse文件夹链接 */
  muse_folder_url?: string;
};
/**
 * 免登上传页面数据
 */
export type MuseUploadPageVo = {
  /** 需求单号 */
  demand_no?: string;
  /** 需求标题 */
  demand_title?: string;
  /** 合作内容明细列表 */
  cooperate_contents?: CooperateContentItem[];
};
export type ResponseMuseUploadPageVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseUploadPageVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * Muse文件管理器iframe URL
 */
export type MuseIframeUrlVo = {
  /** iframe完整URL（含认证参数） */
  iframe_url?: string;
};
export type ResponseMuseIframeUrlVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseIframeUrlVo;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * Muse素材上传状态
 */
export type MuseUploadStatusVo = {
  /** 合作内容ID */
  cooperate_account_id?: number;
  /** 合作内容名称 */
  cooperate_content?: string;
  /** 交付类型：ASSET=资产类交付, VOUCHER=凭证类交付 */
  delivery_type?: string;
  /** 是否已上传素材 */
  has_assets?: boolean;
  /** 素材文件数量 */
  asset_count?: number;
};
export type ResponseListMuseUploadStatusVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseUploadStatusVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 协作人员信息
 */
export type MuseCollaboratorVo = {
  /** 协作人员ID */
  id?: number;
  /** 需求单ID */
  demand_id?: number;
  /** 协作人员邮箱 */
  email?: string;
  /** 状态：0=已邀请, 1=已验证, 2=已过期, 3=已撤销 */
  status?: number;
  /** 状态描述 */
  status_desc?: string;
  /** 邀请时间 */
  invited_at?: string;
  /** 过期时间 */
  expire_at?: string;
  /** 验证通过时间 */
  verified_at?: string;
};
export type ResponseListMuseCollaboratorVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseCollaboratorVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * Muse素材文件信息
 */
export type MuseAssetVo = {
  /** 素材ID */
  asset_id?: string;
  /** 文件名 */
  file_name?: string;
  /** 文件大小（字节） */
  file_size?: number;
  /** 上传时间 */
  upload_time?: string;
  /** 预览URL */
  preview_url?: string;
  /** 文件类型 */
  file_type?: string;
};
export type ResponseListMuseAssetVo = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: MuseAssetVo[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type OutsourceBoostEffectDetailDto = {
  /** 主键ID */
  id?: number;
  /** 需求ID */
  demand_id?: number;
  demand_type?: MpcDemandTypeEnum;
  /** 合作账号ID */
  kol_code?: string;
  /** 合作账号-达人昵称 */
  kol_name?: string;
  /** 作品媒体平台 */
  platform?: string;
  /** 作品链接 */
  work_url?: string;
  /** 加热费用 */
  boost_cost?: number;
  boost_cost_currency?: CurrencyEnum;
  /** 加热效果播放量 */
  boost_view_count?: number;
  /** 加热开始时间 */
  boost_start_time?: string;
  /** 加热结束时间 */
  boost_end_time?: string;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** 内容上传超时时间标签 */
  delay_label?: string;
  /** 超时级别，0，1，2 */
  delay_grade?: number;
};
export type ResponseListOutsourceBoostEffectDetailDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceBoostEffectDetailDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type OutsourceSaveStructuredSettleReportsDataDto = {
  /** 供应商结算报告，报价行维度 */
  quote_settle_reports?: DemandSupplierQuoteSettleReportDto[];
  /** 保存的时间 */
  saved_time?: string;
};
export type ResponseOutsourceSaveStructuredSettleReportsDataDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceSaveStructuredSettleReportsDataDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 供应商报价详情响应
 */
export type OutsourceBiddingSupplierQuoteDetailResDto = {
  /** 多 Tab 列表 */
  tabs?: SupplierQuoteTabDto[];
  /** 报价总金额 */
  quote_total_amount?: number;
};
export type ResponseOutsourceBiddingSupplierQuoteDetailResDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceBiddingSupplierQuoteDetailResDto;
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type OutsourceMenuCountResultDto = {
  key?: string;
  count?: number;
};
export type ResponseListOutsourceMenuCountResultDto = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: OutsourceMenuCountResultDto[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
export type RubyResponseVoUserInfoDto = {
  res_code?: number;
  res_msg?: string;
  data?: UserInfoDto;
  version?: number;
  trace_id?: string;
};
export type RubyResponseVoLoginUserSimpleInfoDto = {
  res_code?: number;
  res_msg?: string;
  data?: LoginUserSimpleInfoDto;
  version?: number;
  trace_id?: string;
};
export type FeeControlThirdDemandAbandonCmd = {
  record_id?: number;
  record_type?: MpcDemandTypeEnum;
};
/**
 * 一级需求类型配置等相关配置
 */
export type CategoryEtcConfig = {
  l1_category_ids?: number[];
  oversea_product_flag?: boolean;
  channel_dept_check?: boolean;
};
export type SolarCtDelegation =
  | 'agent'
  | 'preAddSign'
  | 'postAddSign'
  | 'reassign';
export type UserInfoVo = {
  user_id?: number;
  user_code?: string;
  username?: string;
  email?: string;
  alternate_email?: string;
  delegation?: SolarCtDelegation;
  owner_id?: number;
  owner_code?: string;
  owner_name?: string;
  owner_email?: string;
  owner_alternate_email?: string;
};
export type UserAndDept = {
  user?: User;
  dept_id?: string;
  user_info?: UserInfoVo;
};
export type GenCookiesParam = {
  path?: string;
};
export type ResponseListCookie = {
  code?: number;
  sub_code?: string;
  message?: string;
  message_detail?: object;
  data?: {
    name?: string;
    value?: string;
    version?: number;
    comment?: string;
    domain?: string;
    max_age?: number;
    path?: string;
    secure?: boolean;
    http_only?: boolean;
  }[];
  stacktrace?: string[];
  time?: number;
  trace_id?: string;
};
/**
 * 枚举集合
 */
export const enums = {
  // 状态: 0-待执行 1-执行中 2-已完成 3-失败 4-超时 9-已取消
  BizTaskStatusEnum: createEnum([
    // 待执行
    {
      key: 'PENDING',
      value: 'PENDING',
      name: '待执行',
    },
    // 执行中
    {
      key: 'RUNNING',
      value: 'RUNNING',
      name: '执行中',
    },
    // 已完成
    {
      key: 'COMPLETED',
      value: 'COMPLETED',
      name: '已完成',
    },
    // 失败
    {
      key: 'FAILED',
      value: 'FAILED',
      name: '失败',
    },
    // 超时
    {
      key: 'TIMEOUT',
      value: 'TIMEOUT',
      name: '超时',
    },
    // 已取消
    {
      key: 'CANCELLED',
      value: 'CANCELLED',
      name: '已取消',
    },
  ] as const),
  // vmdm类型
  SupplierVendorTypeEnum: createEnum([
    // 企业
    {
      key: 'ENTERPRISE',
      value: 'enterprise',
      name: '企业',
    },
    // 个人
    {
      key: 'INDIVIDUAL',
      value: 'individuals',
      name: '个人',
    },
  ] as const),
  // 组织类型
  SupplierProfitTypeEnum: createEnum([
    // 普通营利组织
    {
      key: 'NORMAL',
      value: 0,
      name: '普通营利组织',
    },
    // 非营利组织
    {
      key: 'NPO',
      value: 1,
      name: '非营利组织',
    },
  ] as const),
  // 地区类型
  SupplierAreaTypeEnum: createEnum([
    // 中国大陆
    {
      key: 'CHINA_MAINLAND',
      value: 0,
      name: '中国大陆',
    },
    // 其他
    {
      key: 'OTHER',
      value: 1,
      name: '其他',
    },
  ] as const),
  ChannelSupplierAdvanceScaleEnum: createEnum([
    // 0-50万
    {
      key: 'UP_TO_50K',
      value: 1,
      name: '0-50万',
    },
    // 50-100万
    {
      key: 'FROM_50K_TO_100K',
      value: 2,
      name: '50-100万',
    },
    // 100-500万
    {
      key: 'FROM_100K_TO_500K',
      value: 3,
      name: '100-500万',
    },
    // 500万以上
    {
      key: 'ABOVE_500K',
      value: 4,
      name: '500万以上',
    },
    // 不明
    {
      key: 'UNCLEAR',
      value: 5,
      name: '不明',
    },
  ] as const),
  ChannelSupplierAdvantageResourcePlatformEnum: createEnum([
    // 微博
    {
      key: 'WEIBO',
      value: 1,
      name: '微博',
    },
    // 微信
    {
      key: 'WECHAT',
      value: 2,
      name: '微信',
    },
    // 美拍
    {
      key: 'MEIPAI',
      value: 3,
      name: '美拍',
    },
    // 秒拍
    {
      key: 'MIAOPAI',
      value: 4,
      name: '秒拍',
    },
    // 快手
    {
      key: 'KUAISHOU',
      value: 5,
      name: '快手',
    },
    // 知乎
    {
      key: 'ZHIHU',
      value: 6,
      name: '知乎',
    },
    // 豆瓣
    {
      key: 'DOUBAN',
      value: 7,
      name: '豆瓣',
    },
    // 抖音
    {
      key: 'DOUYIN',
      value: 8,
      name: '抖音',
    },
    // B站
    {
      key: 'BILIBILI',
      value: 9,
      name: 'B站',
    },
    // 平台号
    {
      key: 'PLATFORM_NUMBER',
      value: 10,
      name: '平台号',
    },
    // 其他
    {
      key: 'OTHER',
      value: 11,
      name: '其他',
    },
    // 网易CC
    {
      key: 'WANGYICC',
      value: 29,
      name: '网易CC',
    },
    // 斗鱼
    {
      key: 'DOUYU',
      value: 30,
      name: '斗鱼',
    },
    // 虎牙
    {
      key: 'HUYA',
      value: 31,
      name: '虎牙',
    },
    // 西瓜
    {
      key: 'XIGUA',
      value: 32,
      name: '西瓜',
    },
    // 抖音
    {
      key: 'DOUYIN_SHORT',
      value: 33,
      name: '抖音',
    },
    // 快手
    {
      key: 'KUAISHOU_SHORT',
      value: 34,
      name: '快手',
    },
    // 触手
    {
      key: 'CHUTOU',
      value: 35,
      name: '触手',
    },
    // 腾讯视频
    {
      key: 'TENCENT_VIDEO',
      value: 100,
      name: '腾讯视频',
    },
    // 17173
    {
      key: '_17173',
      value: 101,
      name: '17173',
    },
    // TapTap
    {
      key: 'TAP_TAP',
      value: 102,
      name: 'TapTap',
    },
    // 番茄小说
    {
      key: 'TAOMEE',
      value: 103,
      name: '番茄小说',
    },
    // 七猫小说
    {
      key: 'QIMAO',
      value: 104,
      name: '七猫小说',
    },
    // 网吧
    {
      key: 'GAME_LAN',
      value: 105,
      name: '网吧',
    },
    // 搜狗
    {
      key: 'SOGOU',
      value: 106,
      name: '搜狗',
    },
    // 360
    {
      key: '_360',
      value: 107,
      name: '360',
    },
    // UC/神马
    {
      key: 'UC_SHANGMA',
      value: 108,
      name: 'UC/神马',
    },
    // 广点通
    {
      key: 'GDT',
      value: 109,
      name: '广点通',
    },
    // 户外广告
    {
      key: 'OUTDOOR_ADVERTISING',
      value: 110,
      name: '户外广告',
    },
    // 豆瓣
    {
      key: 'DOUBAN_SHORT',
      value: 111,
      name: '豆瓣',
    },
    // 好游快爆
    {
      key: 'HOUYA_SHORT',
      value: 112,
      name: '好游快爆',
    },
    // 苹果
    {
      key: 'IOS',
      value: 113,
      name: '苹果',
    },
    // vivo
    {
      key: 'VIVO',
      value: 114,
      name: 'vivo',
    },
    // OPPO
    {
      key: 'OPPO',
      value: 115,
      name: 'OPPO',
    },
    // 华为
    {
      key: 'HUAWEI',
      value: 116,
      name: '华为',
    },
    // 小米
    {
      key: 'XIAOMI',
      value: 117,
      name: '小米',
    },
    // 4399
    {
      key: '_4399',
      value: 118,
      name: '4399',
    },
    // 百度
    {
      key: 'BAI_DU',
      value: 119,
      name: '百度',
    },
    // 京东
    {
      key: 'JINGDONG',
      value: 120,
      name: '京东',
    },
    // 百家号
    {
      key: 'BAIJIA',
      value: 121,
      name: '百家号',
    },
    // 小红书
    {
      key: 'XIAOHONGSHANG',
      value: 122,
      name: '小红书',
    },
    // 网易创作匠
    {
      key: 'XIAOHONGSHANG_SHORT',
      value: 123,
      name: '网易创作匠',
    },
    // QQ
    {
      key: 'QQ',
      value: 124,
      name: 'QQ',
    },
    // 腾讯音乐
    {
      key: 'TENCENT_MUSIC',
      value: 125,
      name: '腾讯音乐',
    },
    // 爱奇艺
    {
      key: 'IQIYI',
      value: 126,
      name: '爱奇艺',
    },
    // Google
    {
      key: 'GOOGLE',
      value: 127,
      name: 'Google',
    },
    // Facebook
    {
      key: 'FACEBOOK',
      value: 128,
      name: 'Facebook',
    },
    // X (Twitter)
    {
      key: 'X_TWITTER',
      value: 129,
      name: 'X (Twitter)',
    },
    // Discord
    {
      key: 'DISCORD',
      value: 130,
      name: 'Discord',
    },
    // YouTube
    {
      key: 'YOU_TUBE',
      value: 131,
      name: 'YouTube',
    },
    // Twitch
    {
      key: 'TWITCH',
      value: 132,
      name: 'Twitch',
    },
    // Reddit
    {
      key: 'REDDIT',
      value: 133,
      name: 'Reddit',
    },
    // PlayStation
    {
      key: 'PLAYSTATION',
      value: 134,
      name: 'PlayStation',
    },
  ] as const),
  // 语种
  LanguageEnum: createEnum([
    // 中文
    {
      key: 'ZH_CN',
      value: 'zh',
      name: '中文',
    },
    // 英文
    {
      key: 'EN',
      value: 'en',
      name: '英文',
    },
    // 日文
    {
      key: 'JA_JP',
      value: 'ja',
      name: '日文',
    },
    // 法语
    {
      key: 'FRENCH',
      value: 'fr',
      name: '法语',
    },
  ] as const),
  // 账号状态
  SupplierEmployeeEnum: createEnum([
    // 可用
    {
      key: 'ACTIVE',
      value: 'ACTIVE',
      name: '可用',
    },
    // 禁用
    {
      key: 'LOCKED',
      value: 'LOCKED',
      name: '禁用',
    },
  ] as const),
  // 当前审批节点2
  ApprovalNodeEnum: createEnum([
    // 商务负责人审核
    {
      key: 'BUSINESS_REVIEW',
      value: 1,
      name: '商务负责人审核',
    },
    // 财务BP审核
    {
      key: 'FINANCIAL_REVIEW',
      value: 2,
      name: '财务BP审核',
    },
    // 已归档
    {
      key: 'END',
      value: 3,
      name: '已归档',
    },
    // 执行方负责人审核
    {
      key: 'PURCHASING_MANAGER_REVIEW',
      value: 4,
      name: '执行方负责人审核',
    },
    // 财务负责人审核
    {
      key: 'FINANCIAL_MANAGER_REVIEW',
      value: 5,
      name: '财务负责人审核',
    },
    // 法务负责人审核
    {
      key: 'LEGAL_REVIEW',
      value: 6,
      name: '法务负责人审核',
    },
    // 策划负责人审核
    {
      key: 'PRODUCT_DESIGNER_REVIEW',
      value: 7,
      name: '策划负责人审核',
    },
    // 发起
    {
      key: 'START',
      value: 8,
      name: '发起',
    },
  ] as const),
  // 渠道类型
  ChannelTypeEnum: createEnum([
    // 广告投放
    {
      key: 'ADVERTISING',
      value: '01',
      name: '广告投放',
    },
    // 喇叭体系
    {
      key: 'CONTENT_MARKETING',
      value: '02',
      name: '喇叭体系',
    },
    // 自有流量运营
    {
      key: 'ECOSYSTEM_OPERATIONS',
      value: '03',
      name: '自有流量运营',
    },
    // 创意与素材
    {
      key: 'CREATIVITY_AND_MATERIALS',
      value: '04',
      name: '创意与素材',
    },
    // 活动与电竞
    {
      key: 'EVENTS',
      value: '05',
      name: '活动与电竞',
    },
    // 名人明星
    {
      key: 'CELEBRITIES',
      value: '06',
      name: '名人明星',
    },
    // 其他
    {
      key: 'OTHER',
      value: '07',
      name: '其他',
    },
    // 整合营销
    {
      key: 'INTEGRATED_MARKETING',
      value: '08',
      name: '整合营销',
    },
    // 舆情舆论
    {
      key: 'PUBLIC_OPINION',
      value: '09',
      name: '舆情舆论',
    },
    // （市场）Web支持
    {
      key: 'WEB_SUPPORT',
      value: '10',
      name: '（市场）Web支持',
    },
    // （市场）泛娱乐合作
    {
      key: 'ENTERTAINMENT_COOPERATION',
      value: '11',
      name: '（市场）泛娱乐合作',
    },
    // （市场）开发者运营
    {
      key: 'DEVELOPER_OPERATIONS',
      value: '12',
      name: '（市场）开发者运营',
    },
    // 集团公关（游戏）
    {
      key: 'GAME_PUBLIC_RELATIONS',
      value: '13',
      name: '集团公关（游戏）',
    },
    // （市场）内容创意与制作
    {
      key: 'CONTENT_CREATION_AND_PRODUCTION',
      value: '14',
      name: '（市场）内容创意与制作',
    },
    // （市场）海外社区整合营销
    {
      key: 'OVERSEAS_COMMUNITY_INTEGRATED_MARKETING',
      value: '15',
      name: '（市场）海外社区整合营销',
    },
    // （市场）品牌合作
    {
      key: 'BRAND_COOPERATION',
      value: '16',
      name: '（市场）品牌合作',
    },
    // （市场）海外发行
    {
      key: 'OVERSEAS_DISTRIBUTION',
      value: '17',
      name: '（市场）海外发行',
    },
    // 云音乐
    {
      key: 'CLOUD_MUSIC',
      value: '18',
      name: '云音乐',
    },
    // 广宣品
    {
      key: 'GXP',
      value: '19',
      name: '广宣品',
    },
    // 公关传播
    {
      key: 'PUBLIC_RELATIONS_COMMUNICATION',
      value: '20',
      name: '公关传播',
    },
    // （海外）广告投放
    {
      key: 'OVERSEAS_ADVERTISING',
      value: '21',
      name: '（海外）广告投放',
    },
    // （海外）喇叭体系
    {
      key: 'OVERSEAS_CONTENT_MARKETING',
      value: '22',
      name: '（海外）喇叭体系',
    },
    // （海外）自有流量运营
    {
      key: 'OVERSEAS_ECOSYSTEM_OPERATIONS',
      value: '23',
      name: '（海外）自有流量运营',
    },
    // （海外）活动与电竞
    {
      key: 'OVERSEAS_EVENTS',
      value: '24',
      name: '（海外）活动与电竞',
    },
    // （海外）公关传播
    {
      key: 'OVERSEAS_PUBLIC_RELATIONS_COMMUNICATION',
      value: '25',
      name: '（海外）公关传播',
    },
    // Web支持
    {
      key: 'WEB_SUPPORT_NEW',
      value: '26',
      name: 'Web支持',
    },
    // 樱桃派对专用主体
    {
      key: 'CHERRY_PARTY',
      value: '27',
      name: '樱桃派对专用主体',
    },
    // （市场）市场调研
    {
      key: 'MARKET_RESEARCH',
      value: '28',
      name: '（市场）市场调研',
    },
    // 小喇叭个人
    {
      key: 'TRUMPET_PERSONAL',
      value: '90',
      name: '小喇叭个人',
    },
  ] as const),
  ChannelSupplierTypeEnum: createEnum([
    // 核心供应商
    {
      key: 'ANNUAL_FRAME',
      value: 3,
      name: '核心供应商',
    },
    // 重要供应商
    {
      key: 'RESERVED',
      value: 2,
      name: '重要供应商',
    },
    // 储备供应商
    {
      key: 'TEMPORARY',
      value: 1,
      name: '储备供应商',
    },
    // 临时供应商
    {
      key: 'LIN_SHI',
      value: 6,
      name: '临时供应商',
    },
    // 限制使用供应商
    {
      key: 'RESTRICTIVE',
      value: 7,
      name: '限制使用供应商',
    },
    // 黑名单供应商
    {
      key: 'BLACKLISTED',
      value: 5,
      name: '黑名单供应商',
    },
    // 淘汰供应商
    {
      key: 'ELIMINATED',
      value: 4,
      name: '淘汰供应商',
    },
    // 冻结供应商
    {
      key: 'OTHER',
      value: 99,
      name: '冻结供应商',
    },
  ] as const),
  // 渠道供应商状态
  ChannelSupplierStatusEnum: createEnum([
    // 有效
    {
      key: 'ACTIVE',
      value: 1,
      name: '有效',
    },
    // 草稿
    {
      key: 'DRAFT',
      value: 2,
      name: '草稿',
    },
    // 审核中
    {
      key: 'PENDING_REVIEW',
      value: 3,
      name: '审核中',
    },
    // 禁入
    {
      key: 'REVIEW_FAILED',
      value: 4,
      name: '禁入',
    },
    // 已退回
    {
      key: 'RETURNED',
      value: 5,
      name: '已退回',
    },
    // 冻结
    {
      key: 'OTHER',
      value: 6,
      name: '冻结',
    },
    // 已淘汰
    {
      key: 'ELIMINATED',
      value: 7,
      name: '已淘汰',
    },
    // 黑名单
    {
      key: 'BLACKLISTED',
      value: 8,
      name: '黑名单',
    },
    // 可重新入库
    {
      key: 'CAN_RE_INBOUND',
      value: 9,
      name: '可重新入库',
    },
  ] as const),
  // 角色分类
  RoleCategoryEnum: createEnum([
    // 营销
    {
      key: 'MARKETING',
      value: 'MARKETING',
      name: '营销',
    },
    // 策划
    {
      key: 'PLANNING',
      value: 'PLANNING',
      name: '策划',
    },
    // 商务
    {
      key: 'BUSINESS',
      value: 'BUSINESS',
      name: '商务',
    },
    // 集团职能
    {
      key: 'GROUP_FUNCTION',
      value: 'GROUP_FUNCTION',
      name: '集团职能',
    },
  ] as const),
  EnvEnum: createEnum([
    {
      key: 'FORMAL',
      value: 'FORMAL',
      name: 'unknown',
    },
    {
      key: 'SANDBOX',
      value: 'SANDBOX',
      name: 'unknown',
    },
  ] as const),
  DelegationTypeEnum: createEnum([
    {
      key: 'AGENT',
      value: 'AGENT',
      name: 'unknown',
    },
    {
      key: 'PRE_ADD_SIGN',
      value: 'PRE_ADD_SIGN',
      name: 'unknown',
    },
    {
      key: 'POST_ADD_SIGN',
      value: 'POST_ADD_SIGN',
      name: 'unknown',
    },
  ] as const),
  NotifyEventTypeEnum: createEnum([
    {
      key: 'DRAFT_CREATE',
      value: 'DRAFT_CREATE',
      name: 'unknown',
    },
    {
      key: 'DRAFT_UPDATE',
      value: 'DRAFT_UPDATE',
      name: 'unknown',
    },
    {
      key: 'START',
      value: 'START',
      name: 'unknown',
    },
    {
      key: 'HANDLE',
      value: 'HANDLE',
      name: 'unknown',
    },
    {
      key: 'USER_APPROVE',
      value: 'USER_APPROVE',
      name: 'unknown',
    },
    {
      key: 'RETURN_TO_START',
      value: 'RETURN_TO_START',
      name: 'unknown',
    },
    {
      key: 'FORCE_REVOKE_TO_START',
      value: 'FORCE_REVOKE_TO_START',
      name: 'unknown',
    },
    {
      key: 'PRE_ADD_SIGN',
      value: 'PRE_ADD_SIGN',
      name: 'unknown',
    },
    {
      key: 'POST_ADD_SIGN',
      value: 'POST_ADD_SIGN',
      name: 'unknown',
    },
    {
      key: 'REASSIGN',
      value: 'REASSIGN',
      name: 'unknown',
    },
    {
      key: 'REASSIGN_STARTER',
      value: 'REASSIGN_STARTER',
      name: 'unknown',
    },
    {
      key: 'REMIND',
      value: 'REMIND',
      name: 'unknown',
    },
    {
      key: 'TERMINATE',
      value: 'TERMINATE',
      name: 'unknown',
    },
    {
      key: 'CANCEL',
      value: 'CANCEL',
      name: 'unknown',
    },
    {
      key: 'ABORT',
      value: 'ABORT',
      name: 'unknown',
    },
    {
      key: 'COPYFOR',
      value: 'COPYFOR',
      name: 'unknown',
    },
    {
      key: 'COPYFOR_CONFIRM',
      value: 'COPYFOR_CONFIRM',
      name: 'unknown',
    },
    {
      key: 'DELETE',
      value: 'DELETE',
      name: 'unknown',
    },
    {
      key: 'END',
      value: 'END',
      name: 'unknown',
    },
    {
      key: 'PROCESS_CREATE_FAIL',
      value: 'PROCESS_CREATE_FAIL',
      name: 'unknown',
    },
  ] as const),
  // 币种
  CurrencyEnum: createEnum([
    // 人民币
    {
      key: 'RMB',
      value: 'RMB',
      name: '人民币',
    },
    // 港币
    {
      key: 'HKD',
      value: 'HKD',
      name: '港币',
    },
    // 美元
    {
      key: 'USD',
      value: 'USD',
      name: '美元',
    },
    // 欧元
    {
      key: 'EUR',
      value: 'EUR',
      name: '欧元',
    },
    // 英镑
    {
      key: 'GBP',
      value: 'GBP',
      name: '英镑',
    },
    // 韩元
    {
      key: 'KRW',
      value: 'KRW',
      name: '韩元',
    },
    // 日元
    {
      key: 'JPY',
      value: 'JPY',
      name: '日元',
    },
    // 新台币
    {
      key: 'TWD',
      value: 'TWD',
      name: '新台币',
    },
    // 新加坡元
    {
      key: 'SGD',
      value: 'SGD',
      name: '新加坡元',
    },
    // 林吉特
    {
      key: 'MYR',
      value: 'MYR',
      name: '林吉特',
    },
    // 澳元
    {
      key: 'AUD',
      value: 'AUD',
      name: '澳元',
    },
    {
      key: 'NULL',
      value: 'NULL',
      name: 'unknown',
    },
  ] as const),
  // 标品交付类型：1-资产类交付，2-凭证类交付
  StandardCatalogDeliveryTypeEnum: createEnum([
    // 资产类交付
    {
      key: 'ASSET',
      value: 1,
      name: '资产类交付',
    },
    // 凭证类交付
    {
      key: 'VOUCHER',
      value: 2,
      name: '凭证类交付',
    },
  ] as const),
  // 二级单状态
  TrumpetActivityStateEnum: createEnum([
    // 进行中
    {
      key: 'RUNNING',
      value: 'running',
      name: '进行中',
    },
    // 已完成
    {
      key: 'FINISHED',
      value: 'finished',
      name: '已完成',
    },
    // 已废弃
    {
      key: 'CLOSED',
      value: 'closed',
      name: '已废弃',
    },
  ] as const),
  DemandGroupTypeEnum: createEnum([
    // 发行中心
    {
      key: 'CHANNEL_GROUP',
      value: 'CHANNEL_GROUP',
      name: '发行中心',
    },
    // 市场支持中心
    {
      key: 'MARKET_GROUP',
      value: 'MARKET_GROUP',
      name: '市场支持中心',
    },
    // 小喇叭需求
    {
      key: 'CD_TRUMPET_GROUP',
      value: 'CD_TRUMPET_GROUP',
      name: '小喇叭需求',
    },
    // 营销闭环需求
    {
      key: 'M_CLOSED_LOOP',
      value: 'M_CLOSED_LOOP',
      name: '营销闭环需求',
    },
  ] as const),
  // 供应商类型
  DemandSupportTypeEnum: createEnum([
    // 仅供应商
    {
      key: 'ONLY_CP',
      value: 'ONLY_CP',
      name: '仅供应商',
    },
    // 供应商+自联
    {
      key: 'CP_SELF_CONTACT',
      value: 'CP_SELF_CONTACT',
      name: '供应商+自联',
    },
    // 仅自联
    {
      key: 'SELF_CONTACT',
      value: 'SELF_CONTACT',
      name: '仅自联',
    },
  ] as const),
  // 需求优先级, 多选
  PriorityEnum: createEnum([
    // 紧急
    {
      key: 'URGENCY_LEVEL',
      value: 'URGENCY_LEVEL',
      name: '紧急',
    },
    // 高
    {
      key: 'HIGH_LEVEL',
      value: 'HIGH_LEVEL',
      name: '高',
    },
    // 中
    {
      key: 'MIDDLE_LEVEL',
      value: 'MIDDLE_LEVEL',
      name: '中',
    },
    // 低
    {
      key: 'LOW_LEVEL',
      value: 'LOW_LEVEL',
      name: '低',
    },
  ] as const),
  // 单号层级
  TrumpetDemandTypeEnum: createEnum([
    // 需求
    {
      key: 'DEMAND',
      value: 1,
      name: '需求',
    },
    // 活动
    {
      key: 'ACTIVITY',
      value: 2,
      name: '活动',
    },
    // 结案
    {
      key: 'CASE',
      value: 3,
      name: '结案',
    },
  ] as const),
  // OA状态, 多选
  OaStateEnum: createEnum([
    // --
    {
      key: 'NOT_SUBMITTED_OTHER',
      value: 'NOT_SUBMITTED_OTHER',
      name: '--',
    },
    // 提交中
    {
      key: 'SUBMITTING',
      value: 'SUBMITTING',
      name: '提交中',
    },
    // 未提交
    {
      key: 'NOT_SUBMITTED',
      value: 'NOT_SUBMITTED',
      name: '未提交',
    },
    // 已提交
    {
      key: 'SUBMITTED',
      value: 'SUBMITTED',
      name: '已提交',
    },
    // 已驳回
    {
      key: 'REJECTED',
      value: 'REJECTED',
      name: '已驳回',
    },
    // 已归档
    {
      key: 'ARCHIVING',
      value: 'ARCHIVING',
      name: '已归档',
    },
  ] as const),
  PlatformTypeEnum: createEnum([
    {
      key: 'INNER_SOURCING',
      value: 'INNER_SOURCING',
      name: 'unknown',
    },
    {
      key: 'MOBILE',
      value: 'MOBILE',
      name: 'unknown',
    },
    {
      key: 'OUTSOURCING',
      value: 'OUTSOURCING',
      name: 'unknown',
    },
  ] as const),
  // 流程状态，支持多选，例如：待策划确认、待发起
  KpiAdjustStateEnum: createEnum([
    {
      key: 'NEWLY_BUILT',
      value: 'newly_built',
      name: 'unknown',
    },
    {
      key: 'PLANNER_APPROVE',
      value: 'planner_approve',
      name: 'unknown',
    },
    {
      key: 'OA_APPROVE',
      value: 'oa_approve',
      name: 'unknown',
    },
    {
      key: 'COMPLETE',
      value: 'complete',
      name: 'unknown',
    },
    {
      key: 'REJECT',
      value: 'reject',
      name: 'unknown',
    },
  ] as const),
  // 需求单状态, 多选
  DemandStateEnum: createEnum([
    {
      key: 'DRAFT',
      value: 'DRAFT',
      name: 'unknown',
    },
    {
      key: 'NEWLY_BUILT',
      value: 'NEWLY_BUILT',
      name: 'unknown',
    },
    {
      key: 'PRE_APPROVE',
      value: 'PRE_APPROVE',
      name: 'unknown',
    },
    {
      key: 'PENDING_DESIGN',
      value: 'PENDING_DESIGN',
      name: 'unknown',
    },
    {
      key: 'PENDING_BIDDING',
      value: 'PENDING_BIDDING',
      name: 'unknown',
    },
    {
      key: 'PENDING_ASSESS',
      value: 'PENDING_ASSESS',
      name: 'unknown',
    },
    {
      key: 'PENDING_QUOTE',
      value: 'PENDING_QUOTE',
      name: 'unknown',
    },
    {
      key: 'QUOTE_SCORE',
      value: 'QUOTE_SCORE',
      name: 'unknown',
    },
    {
      key: 'BUSINESS_AUDIT_QUOTE',
      value: 'BUSINESS_AUDIT_QUOTE',
      name: 'unknown',
    },
    {
      key: 'BUSINESS_LEADER_AUDIT_QUOTE',
      value: 'BUSINESS_LEADER_AUDIT_QUOTE',
      name: 'unknown',
    },
    {
      key: 'PLANNER_AUDIT_QUOTE',
      value: 'PLANNER_AUDIT_QUOTE',
      name: 'unknown',
    },
    {
      key: 'PLANNER_LEADER_AUDIT_QUOTE',
      value: 'PLANNER_LEADER_AUDIT_QUOTE',
      name: 'unknown',
    },
    {
      key: 'MARKETING_AUDIT_QUOTE',
      value: 'MARKETING_AUDIT_QUOTE',
      name: 'unknown',
    },
    {
      key: 'MARKETING_LEADER_AUDIT_QUOTE',
      value: 'MARKETING_LEADER_AUDIT_QUOTE',
      name: 'unknown',
    },
    {
      key: 'FEE_APPROVE',
      value: 'FEE_APPROVE',
      name: 'unknown',
    },
    {
      key: 'MAKING',
      value: 'MAKING',
      name: 'unknown',
    },
    {
      key: 'BIND_EXECUTION_DATA',
      value: 'BIND_EXECUTION_DATA',
      name: 'unknown',
    },
    {
      key: 'UNDERWAY',
      value: 'UNDERWAY',
      name: 'unknown',
    },
    {
      key: 'QC_ACCEPT',
      value: 'QC_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'PLANNER_ACCEPT',
      value: 'PLANNER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'PLANNER_LEADER_ACCEPT',
      value: 'PLANNER_LEADER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'BUSINESS_ACCEPT',
      value: 'BUSINESS_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'DEMANDER_ACCEPT',
      value: 'DEMANDER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'APPROVE_SETTLE',
      value: 'APPROVE_SETTLE',
      name: 'unknown',
    },
    {
      key: 'SUBMIT_OA',
      value: 'SUBMIT_OA',
      name: 'unknown',
    },
    {
      key: 'SUBMIT_REIMBURSE',
      value: 'SUBMIT_REIMBURSE',
      name: 'unknown',
    },
    {
      key: 'CLOSED',
      value: 'CLOSED',
      name: 'unknown',
    },
    {
      key: 'ABANDON',
      value: 'ABANDON',
      name: 'unknown',
    },
    {
      key: 'UNKNOWN',
      value: 'UNKNOWN',
      name: 'unknown',
    },
    {
      key: 'MY_DEMAND',
      value: 'MY_DEMAND',
      name: 'unknown',
    },
  ] as const),
  // 采购方法
  PurchasingMethodEnum: createEnum([
    // 常规采购
    {
      key: 'S_V_NORMAL',
      value: 'S_V_NORMAL',
      name: '常规采购',
    },
    // 老数据无绑定采购方式
    {
      key: 'S_OLD',
      value: 'S_OLD',
      name: '老数据无绑定采购方式',
    },
    // 比选
    {
      key: 'S_TRADITIONAL_BIDDING',
      value: 'S_TRADITIONAL_BIDDING',
      name: '比选',
    },
    // 询比价
    {
      key: 'S_INQUIRY',
      value: 'S_INQUIRY',
      name: '询比价',
    },
    // 引用框架协议
    {
      key: 'S_FRAMEWORK',
      value: 'S_FRAMEWORK',
      name: '引用框架协议',
    },
    // 直接采购
    {
      key: 'S_IMMEDIATELY_BIDDING',
      value: 'S_IMMEDIATELY_BIDDING',
      name: '直接采购',
    },
    // 单一来源采购
    {
      key: 'S_ONLY_ONE_SOURCE',
      value: 'S_ONLY_ONE_SOURCE',
      name: '单一来源采购',
    },
    // 指定采购
    {
      key: 'S_DIRECTIONAL_BIDDING',
      value: 'S_DIRECTIONAL_BIDDING',
      name: '指定采购',
    },
    // 特殊事项报备（紧急需求）
    {
      key: 'S_SPECIAL_MATTER_BIDDING',
      value: 'S_SPECIAL_MATTER_BIDDING',
      name: '特殊事项报备（紧急需求）',
    },
    // 已比选
    {
      key: 'S_BIDDING_FINISHED',
      value: 'S_BIDDING_FINISHED',
      name: '已比选',
    },
    // 引用框架协议(已比选)
    {
      key: 'S_FRAMEWORK_BIDDING_FINISHED',
      value: 'S_FRAMEWORK_BIDDING_FINISHED',
      name: '引用框架协议(已比选)',
    },
    // 紧急需求补单
    {
      key: 'S_URGENT_ORDER_RESTOCKING',
      value: 'S_URGENT_ORDER_RESTOCKING',
      name: '紧急需求补单',
    },
    // 续签采购
    {
      key: 'S_RENEW',
      value: 'S_RENEW',
      name: '续签采购',
    },
  ] as const),
  // 采购策略
  PurchasingStrategyEnum: createEnum([
    // 价格框架
    {
      key: 'PRICE',
      value: 'PRICE',
      name: '价格框架',
    },
    // 折扣框架
    {
      key: 'DISCOUNT',
      value: 'DISCOUNT',
      name: '折扣框架',
    },
    // 价格优先策略
    {
      key: 'LOWEST_PRICE',
      value: 'LOWEST_PRICE',
      name: '价格优先策略',
    },
    // 综合评分策略
    {
      key: 'MULTI_QUOTA',
      value: 'MULTI_QUOTA',
      name: '综合评分策略',
    },
    // 综合评分法（已评分）
    {
      key: 'MULTI_QUOTA_FINISHED',
      value: 'MULTI_QUOTA_FINISHED',
      name: '综合评分法（已评分）',
    },
  ] as const),
  // 二级单类型列表
  DemandPlanTypeEnum: createEnum([
    // 常规采购二级单
    {
      key: 'NORMAL',
      value: 0,
      name: '常规采购二级单',
    },
    // 方案综合评分法
    {
      key: 'MULTI_QUOTA_PLAN',
      value: 1,
      name: '方案综合评分法',
    },
    // KOL合作一询单
    {
      key: 'KOL_SOURCE',
      value: 2,
      name: 'KOL合作一询单',
    },
  ] as const),
  // 付款状态, 仅三级需求单有值
  ReimburseStatusEnum: createEnum([
    // 发票待上传
    {
      key: 'INVOICE_NOT_UPLOADED',
      value: 0,
      name: '发票待上传',
    },
    // 付款单未提交
    {
      key: 'PAYMENT_NOT_SUBMITTED',
      value: 1,
      name: '付款单未提交',
    },
    // 付款中
    {
      key: 'PAYMENT_IN_PROGRESS',
      value: 2,
      name: '付款中',
    },
    // 已完成付款
    {
      key: 'PAYMENT_COMPLETED',
      value: 3,
      name: '已完成付款',
    },
    // 付款单已删除
    {
      key: 'PAYMENT_DELETED',
      value: -1,
      name: '付款单已删除',
    },
  ] as const),
  // 单号层级
  ClosedLoopDemandTypeEnum: createEnum([
    // 需求
    {
      key: 'DEMAND',
      value: 1,
      name: '需求',
    },
    // 活动
    {
      key: 'ACTIVITY',
      value: 2,
      name: '活动',
    },
    // 结案
    {
      key: 'CASE',
      value: 3,
      name: '结案',
    },
  ] as const),
  // 付款状态
  ClosedLoopPayStatusEnum: createEnum([
    // 未付款
    {
      key: 'WAIT_SUBMITTED',
      value: 'wait_submitted',
      name: '未付款',
    },
    // 付款中
    {
      key: 'SUBMITTED',
      value: 'submitted',
      name: '付款中',
    },
    // 已付款
    {
      key: 'ARCHIVED',
      value: 'archived',
      name: '已付款',
    },
  ] as const),
  // 比选类型
  BiddingTypeEnum: createEnum([
    // 简易比稿
    {
      key: 'SIMPLIFIED_TENDER',
      value: 'SIMPLIFIED_TENDER',
      name: '简易比稿',
    },
    // 比稿
    {
      key: 'TENDER',
      value: 'TENDER',
      name: '比稿',
    },
    // 竞选
    {
      key: 'BIDDING',
      value: 'BIDDING',
      name: '竞选',
    },
    // 框架比选
    {
      key: 'FRAMEWORK_TENDER',
      value: 'FRAMEWORK_TENDER',
      name: '框架比选',
    },
  ] as const),
  // 比选类型V2
  BiddingTypeV2Enum: createEnum([
    // 整合营销比选
    {
      key: 'WHOLE',
      value: 'WHOLE',
      name: '整合营销比选',
    },
    // 营销活动比选
    {
      key: 'EVENT',
      value: 'EVENT',
      name: '营销活动比选',
    },
    // 其他类型比选
    {
      key: 'OTHER',
      value: 'OTHER',
      name: '其他类型比选',
    },
    // 年框比选
    {
      key: 'ANNUAL',
      value: 'ANNUAL',
      name: '年框比选',
    },
  ] as const),
  // 比选方式
  BiddingMethodEnum: createEnum([
    // 简易比选
    {
      key: 'SIMPLE_EVALUATION',
      value: 'SIMPLE_EVALUATION',
      name: '简易比选',
    },
    // 业务专家比选
    {
      key: 'EXPERT_EVALUATION',
      value: 'EXPERT_EVALUATION',
      name: '业务专家比选',
    },
    // 综合比选
    {
      key: 'COMPREHENSIVE_EVALUATION',
      value: 'COMPREHENSIVE_EVALUATION',
      name: '综合比选',
    },
  ] as const),
  BiddingStateEnum: createEnum([
    {
      key: 'NEWLY_BUILT',
      value: 'NEWLY_BUILT',
      name: 'unknown',
    },
    {
      key: 'PLAN_DESIGN',
      value: 'PLAN_DESIGN',
      name: 'unknown',
    },
    {
      key: 'AWAITING_BIDDING',
      value: 'AWAITING_BIDDING',
      name: 'unknown',
    },
    {
      key: 'BUSINESS_CONFIRM_BID_SP',
      value: 'BUSINESS_CONFIRM_BID_SP',
      name: 'unknown',
    },
    {
      key: 'AWAITING_SUBMISSION_BIDS',
      value: 'AWAITING_SUBMISSION_BIDS',
      name: 'unknown',
    },
    {
      key: 'AWAITING_RETRIEVAL_BIDS',
      value: 'AWAITING_RETRIEVAL_BIDS',
      name: 'unknown',
    },
    {
      key: 'AWAITING_BIDDING_ADMIN_CONFIRM',
      value: 'AWAITING_BIDDING_ADMIN_CONFIRM',
      name: 'unknown',
    },
    {
      key: 'BID_OPEN_REVIEW',
      value: 'BID_OPEN_REVIEW',
      name: 'unknown',
    },
    {
      key: 'UPLOAD_BID_RESULT',
      value: 'UPLOAD_BID_RESULT',
      name: 'unknown',
    },
    {
      key: 'AWAITING_DECISION_BIDS',
      value: 'AWAITING_DECISION_BIDS',
      name: 'unknown',
    },
    {
      key: 'BIDING_ADMIN_ACCEPT',
      value: 'BIDING_ADMIN_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'CHANNEL_LEADER_ACCEPT',
      value: 'CHANNEL_LEADER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'MARKETING_LEADER_ACCEPT',
      value: 'MARKETING_LEADER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'CLOSED',
      value: 'CLOSED',
      name: 'unknown',
    },
    {
      key: 'ABANDON',
      value: 'ABANDON',
      name: 'unknown',
    },
    {
      key: 'PLAN_DESIGN_ACCEPT',
      value: 'PLAN_DESIGN_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'LEADER_ACCEPT_CONFIRM',
      value: 'LEADER_ACCEPT_CONFIRM',
      name: 'unknown',
    },
    {
      key: 'BUSINESS_PLAN_DESIGN_ACCEPT',
      value: 'BUSINESS_PLAN_DESIGN_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'AWAITING_JUDGES_CONFIRM_BIDS',
      value: 'AWAITING_JUDGES_CONFIRM_BIDS',
      name: 'unknown',
    },
    {
      key: 'EVALUATION_RESULT_ACCEPT',
      value: 'EVALUATION_RESULT_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'BUDGET_ALLOCATION',
      value: 'BUDGET_ALLOCATION',
      name: 'unknown',
    },
    {
      key: 'BUSINESS_LEADER_ACCEPT',
      value: 'BUSINESS_LEADER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'PLANNER_LEADER_ACCEPT',
      value: 'PLANNER_LEADER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'OA_PLAN_APPROVE',
      value: 'OA_PLAN_APPROVE',
      name: 'unknown',
    },
    {
      key: 'OA_PLAN_APPROVE_ACCEPT',
      value: 'OA_PLAN_APPROVE_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'BID_RESULT_ACCEPT',
      value: 'BID_RESULT_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'BIDDING_FAILED',
      value: 'BIDDING_FAILED',
      name: 'unknown',
    },
  ] as const),
  // 状态
  TrumpetStateEnum: createEnum([
    // 新建
    {
      key: 'NEWLY_BUILT',
      value: 'NEWLY_BUILT',
      name: '新建',
    },
    // 需求方负责人审批
    {
      key: 'DEMAND_PRINCIPAL_APPROVAL',
      value: 'DEMAND_PRINCIPAL_APPROVAL',
      name: '需求方负责人审批',
    },
    // 策划设计
    {
      key: 'PLAN_DESIGN',
      value: 'PLAN_DESIGN',
      name: '策划设计',
    },
    // 方案审批
    {
      key: 'PLAN_APPROVAL',
      value: 'PLAN_APPROVAL',
      name: '方案审批',
    },
    // 活动设计
    {
      key: 'ACTIVITY_DESIGN',
      value: 'ACTIVITY_DESIGN',
      name: '活动设计',
    },
    // 执行中
    {
      key: 'IN_PROGRESS',
      value: 'IN_PROGRESS',
      name: '执行中',
    },
    // 已关闭
    {
      key: 'CLOSED',
      value: 'CLOSED',
      name: '已关闭',
    },
    // 活动进行中
    {
      key: 'RUNNING',
      value: 'RUNNING',
      name: '活动进行中',
    },
    // 活动完成
    {
      key: 'ACTIVITY_FINISHED',
      value: 'ACTIVITY_FINISHED',
      name: '活动完成',
    },
    // 活动关闭
    {
      key: 'ACTIVITY_CLOSED',
      value: 'ACTIVITY_CLOSED',
      name: '活动关闭',
    },
    // 提交结案
    {
      key: 'SUBMIT_CASE_CLOSURE',
      value: 'SUBMIT_CASE_CLOSURE',
      name: '提交结案',
    },
    // 策划复核
    {
      key: 'PLAN_ACCEPT',
      value: 'PLAN_ACCEPT',
      name: '策划复核',
    },
    // 需求方负责人验收
    {
      key: 'APPROVE_SETTLE',
      value: 'APPROVE_SETTLE',
      name: '需求方负责人验收',
    },
    // 需求二级负责人验收
    {
      key: 'SECONDARY_APPROVE_SETTLE',
      value: 'SECONDARY_APPROVE_SETTLE',
      name: '需求二级负责人验收',
    },
    // 小喇叭负责人验收
    {
      key: 'MARKETING_APPROVE_SETTLE',
      value: 'MARKETING_APPROVE_SETTLE',
      name: '小喇叭负责人验收',
    },
    // QC验收复核
    {
      key: 'QC_SETTLE',
      value: 'QC_SETTLE',
      name: 'QC验收复核',
    },
    // 市场内审组审核
    {
      key: 'MARKET_INTERNAL_AUDIT_TEAM_REVIEW',
      value: 'MARKET_INTERNAL_AUDIT_TEAM_REVIEW',
      name: '市场内审组审核',
    },
    // 策略研究部审核
    {
      key: 'STRATEGY_RESEARCH_DEPARTMENT_REVIEW',
      value: 'STRATEGY_RESEARCH_DEPARTMENT_REVIEW',
      name: '策略研究部审核',
    },
    // 运营复核
    {
      key: 'OPERATIONS_REVIEW',
      value: 'OPERATIONS_REVIEW',
      name: '运营复核',
    },
    // 提交报销
    {
      key: 'SUBMIT_OA',
      value: 'SUBMIT_OA',
      name: '提交报销',
    },
    // 确认核销
    {
      key: 'CONFIRM_VERIFICATION',
      value: 'CONFIRM_VERIFICATION',
      name: '确认核销',
    },
    // 已完成
    {
      key: 'FINISHED',
      value: 'FINISHED',
      name: '已完成',
    },
    // 我的需求
    {
      key: 'MY_DEMAND',
      value: 'MY_DEMAND',
      name: '我的需求',
    },
  ] as const),
  // 用户类型
  TrumpetMemberTypeEnum: createEnum([
    // 负责人
    {
      key: 'PRINCIPAL',
      value: 1,
      name: '负责人',
    },
    // 商务人员
    {
      key: 'BUSINESS',
      value: 2,
      name: '商务人员',
    },
    // 策划人员
    {
      key: 'PLANNER',
      value: 3,
      name: '策划人员',
    },
    // 运营人员
    {
      key: 'OPERATOR',
      value: 4,
      name: '运营人员',
    },
    // 创建人
    {
      key: 'CREATOR',
      value: 9,
      name: '创建人',
    },
  ] as const),
  // 审批类型
  ApproveTypeEnum: createEnum([
    // 营销方案预算审批
    {
      key: 'PLAN_APPROVE',
      value: 'PLAN_APPROVE',
      name: '营销方案预算审批',
    },
    // 需求前置审批
    {
      key: 'PRE_APPROVE',
      value: 'PRE_APPROVE',
      name: '需求前置审批',
    },
    // 定标审批
    {
      key: 'FEE_APPROVE',
      value: 'FEE_APPROVE',
      name: '定标审批',
    },
    // 营销业务验收与结算
    {
      key: 'SUBMIT_APPROVE',
      value: 'SUBMIT_APPROVE',
      name: '营销业务验收与结算',
    },
    // 特殊事项报备审批（紧急需求）
    {
      key: 'SPECIAL_MATTER_APPROVE',
      value: 'SPECIAL_MATTER_APPROVE',
      name: '特殊事项报备审批（紧急需求）',
    },
    // 指定采购审批
    {
      key: 'DIRECTIONAL_BIDDING_APPROVE',
      value: 'DIRECTIONAL_BIDDING_APPROVE',
      name: '指定采购审批',
    },
    // 比选方案审批
    {
      key: 'BIDDING_PLAN_APPROVE',
      value: 'BIDDING_PLAN_APPROVE',
      name: '比选方案审批',
    },
    // 年框比选方案审批
    {
      key: 'NIANKUANG_BIDDING_PLAN_APPROVE',
      value: 'NIANKUANG_BIDDING_PLAN_APPROVE',
      name: '年框比选方案审批',
    },
    // 小喇叭方案审批
    {
      key: 'TRUMPET_PLAN_APPROVE',
      value: 'TRUMPET_PLAN_APPROVE',
      name: '小喇叭方案审批',
    },
    // 小喇叭营销业务验收与结算
    {
      key: 'TRUMPET_SUBMIT_APPROVE',
      value: 'TRUMPET_SUBMIT_APPROVE',
      name: '小喇叭营销业务验收与结算',
    },
    // 营销闭环方案审批
    {
      key: 'CLOSED_LOOP_PLAN_APPROVE',
      value: 'CLOSED_LOOP_PLAN_APPROVE',
      name: '营销闭环方案审批',
    },
    // 营销闭环营销业务验收与结算
    {
      key: 'CLOSED_LOOP_SUBMIT_APPROVE',
      value: 'CLOSED_LOOP_SUBMIT_APPROVE',
      name: '营销闭环营销业务验收与结算',
    },
    // 效果投放KPI调整审批
    {
      key: 'KPI_ADJUSTMENT_APPROVE',
      value: 'KPI_ADJUSTMENT_APPROVE',
      name: '效果投放KPI调整审批',
    },
    // 小喇叭追加预算审批
    {
      key: 'TRUMPET_BUDGET_INCREASE_APPROVE',
      value: 'TRUMPET_BUDGET_INCREASE_APPROVE',
      name: '小喇叭追加预算审批',
    },
    // 权限申请审批
    {
      key: 'PERMISSION_APPLICATION_APPROVE',
      value: 'PERMISSION_APPLICATION_APPROVE',
      name: '权限申请审批',
    },
    // 超额审批
    {
      key: 'OVER_AMOUNT_APPROVE',
      value: 'OVER_AMOUNT_APPROVE',
      name: '超额审批',
    },
    // 价格库审批
    {
      key: 'PRICE_LIBRARY_APPROVE',
      value: 'PRICE_LIBRARY_APPROVE',
      name: '价格库审批',
    },
    // 预付单审批
    {
      key: 'PREPAYMENT_APPROVE',
      value: 'PREPAYMENT_APPROVE',
      name: '预付单审批',
    },
    // 预算调整审批
    {
      key: 'BUDGET_ADJUST_APPROVE',
      value: 'BUDGET_ADJUST_APPROVE',
      name: '预算调整审批',
    },
    // SM费用类型预算审批
    {
      key: 'SM_EXPENSE_BUDGET_APPROVE',
      value: 'SM_EXPENSE_BUDGET_APPROVE',
      name: 'SM费用类型预算审批',
    },
  ] as const),
  // 询比价评分状态
  QuoteScoreStateEnum: createEnum([
    // 待开启评分
    {
      key: 'NOT_SCORED',
      value: 'NOT_SCORED',
      name: '待开启评分',
    },
    // 待评分
    {
      key: 'SCORING',
      value: 'SCORING',
      name: '待评分',
    },
    // 已评分
    {
      key: 'SCORED',
      value: 'SCORED',
      name: '已评分',
    },
    // 无需评分
    {
      key: 'NO_NEED',
      value: 'NO_NEED',
      name: '无需评分',
    },
  ] as const),
  // 批量处理类型
  DemandBatchHandleTypeEnum: createEnum([
    // 无批量验收
    {
      key: 'NONE',
      value: 0,
      name: '无批量验收',
    },
    // 投放类批量验收
    {
      key: 'DELIVERY',
      value: 1,
      name: '投放类批量验收',
    },
    // 内推类批量验收
    {
      key: 'INTERNAL_PUSH',
      value: 2,
      name: '内推类批量验收',
    },
    // 投放类合并询价批量确认
    {
      key: 'PLAN_DEMAND_DELIVERY',
      value: 3,
      name: '投放类合并询价批量确认',
    },
  ] as const),
  // 平台编码, MPC, DSPF
  DemandKpiPlatformEnum: createEnum([
    // MPC
    {
      key: 'MPC',
      value: 'MPC',
      name: 'MPC',
    },
    // 数易
    {
      key: 'DSPF',
      value: 'DSPF',
      name: '数易',
    },
  ] as const),
  // KPI类型编码, numeric 数值, plain_text 文本
  DemandKpiTypeEnum: createEnum([
    // 数值
    {
      key: 'NUMERIC',
      value: 'numeric',
      name: '数值',
    },
    // 文本
    {
      key: 'PLAIN_TEXT',
      value: 'plain_text',
      name: '文本',
    },
  ] as const),
  // KPI状态编码, disabled-停用, enabled-启用
  CommonEnableStatusEnum: createEnum([
    // 禁用
    {
      key: 'DISABLED',
      value: 0,
      name: '禁用',
    },
    // 启用
    {
      key: 'ENABLED',
      value: 1,
      name: '启用',
    },
  ] as const),
  // 是否正向指标编码, positive-正向, negative-负向
  DemandKpiPositiveEnum: createEnum([
    // 正向指标
    {
      key: 'POSITIVE',
      value: 0,
      name: '正向指标',
    },
    // 负向指标
    {
      key: 'NEGATIVE',
      value: 1,
      name: '负向指标',
    },
  ] as const),
  // 是否对供应商显示编码, no-否, yes-是
  CommonYesNoEnum: createEnum([
    // 否
    {
      key: 'NO',
      value: 0,
      name: '否',
    },
    // 是
    {
      key: 'YES',
      value: 1,
      name: '是',
    },
  ] as const),
  // 短期/长期指标
  DemandKpiLongOrShortTermEnum: createEnum([
    // 长期指标
    {
      key: 'LONG_TERM',
      value: 0,
      name: '长期指标',
    },
    // 短期指标
    {
      key: 'SHORT_TERM',
      value: 1,
      name: '短期指标',
    },
    // 未定义
    {
      key: 'NONE',
      value: 2,
      name: '未定义',
    },
  ] as const),
  TrumpetApprovalBizFormTypeEnum: createEnum([
    {
      key: 'TEST',
      value: 0,
      name: 'unknown',
    },
    {
      key: 'POST_CASE',
      value: 1,
      name: 'unknown',
    },
    {
      key: 'MARKET_INTERNAL_AUDIT',
      value: 2,
      name: 'unknown',
    },
    {
      key: 'STRATEGY_RESEARCH_AUDIT',
      value: 3,
      name: 'unknown',
    },
    {
      key: 'OPERATIONS_AUDIT',
      value: 4,
      name: 'unknown',
    },
    {
      key: 'CONFIRM_VERIFICATION',
      value: 5,
      name: 'unknown',
    },
    {
      key: 'CLOSE',
      value: 6,
      name: 'unknown',
    },
    {
      key: 'APPROVE_SETTLE',
      value: 7,
      name: 'unknown',
    },
    {
      key: 'SECONDARY_APPROVE_SETTLE',
      value: 8,
      name: 'unknown',
    },
    {
      key: 'MARKETING_APPROVE_SETTLE',
      value: 9,
      name: 'unknown',
    },
    {
      key: 'SUMBIT_OA',
      value: 10,
      name: 'unknown',
    },
  ] as const),
  MpcDemandTypeEnum: createEnum([
    // mpc需求单
    {
      key: 'MPC_DEMAND',
      value: 'MPC_DEMAND',
      name: 'mpc需求单',
    },
    // 小喇叭需求单
    {
      key: 'TRUMPET_DEMAND',
      value: 'TRUMPET_DEMAND',
      name: '小喇叭需求单',
    },
    // 比选需求单
    {
      key: 'BIDDING_DEMAND',
      value: 'BIDDING_DEMAND',
      name: '比选需求单',
    },
    // 营销闭环单
    {
      key: 'CLOSED_LOOP_DEMAND',
      value: 'CLOSED_LOOP_DEMAND',
      name: '营销闭环单',
    },
    // 权限申请单
    {
      key: 'PERMISSION_APPLICATION',
      value: 'PERMISSION_APPLICATION',
      name: '权限申请单',
    },
    // 常规采购需求单KPI调整
    {
      key: 'MPC_DEMAND_KPI_ADJUST',
      value: 'MPC_DEMAND_KPI_ADJUST',
      name: '常规采购需求单KPI调整',
    },
    // 预付单
    {
      key: 'PREPAYMENT',
      value: 'PREPAYMENT',
      name: '预付单',
    },
    // 预算调整
    {
      key: 'BUDGET_ADJUST',
      value: 'BUDGET_ADJUST',
      name: '预算调整',
    },
  ] as const),
  ShowRiskTipsEnum: createEnum([
    {
      key: 'SHOW',
      value: 'SHOW',
      name: 'unknown',
    },
    {
      key: 'HIDDEN',
      value: 'HIDDEN',
      name: 'unknown',
    },
  ] as const),
  JournalOpTypeEnum: createEnum([
    // 系统自动处理
    {
      key: 'SYSTEM_AUTO',
      value: 'SYSTEM_AUTO',
      name: '系统自动处理',
    },
    // 创建
    {
      key: 'CREATE',
      value: 'CREATE',
      name: '创建',
    },
    // 废弃
    {
      key: 'DESTROY',
      value: 'DESTROY',
      name: '废弃',
    },
    // 编辑
    {
      key: 'EDIT',
      value: 'EDIT',
      name: '编辑',
    },
    // 提交
    {
      key: 'SUBMIT',
      value: 'SUBMIT',
      name: '提交',
    },
    // 通过
    {
      key: 'PASS',
      value: 'PASS',
      name: '通过',
    },
    // 退回
    {
      key: 'RETURN',
      value: 'RETURN',
      name: '退回',
    },
    // 删除
    {
      key: 'DELETE',
      value: 'DELETE',
      name: '删除',
    },
    // 保存
    {
      key: 'SAVE',
      value: 'SAVE',
      name: '保存',
    },
    // 关闭
    {
      key: 'CLOSE',
      value: 'CLOSE',
      name: '关闭',
    },
    // 强制退回
    {
      key: 'FORCE_RETURN',
      value: 'FORCE_RETURN',
      name: '强制退回',
    },
    // 强制推进
    {
      key: 'FORCE_PASS',
      value: 'FORCE_PASS',
      name: '强制推进',
    },
    // 时间截止
    {
      key: 'TIMEOUT',
      value: 'TIMEOUT',
      name: '时间截止',
    },
    // 完成
    {
      key: 'FINISH',
      value: 'FINISH',
      name: '完成',
    },
    // 更新
    {
      key: 'UPDATE',
      value: 'UPDATE',
      name: '更新',
    },
    // 编辑预算
    {
      key: 'EDIT_BUDGET',
      value: 'EDIT_BUDGET',
      name: '编辑预算',
    },
    // 更新预算金额
    {
      key: 'UPDATE_BUDGET',
      value: 'UPDATE_BUDGET',
      name: '更新预算金额',
    },
    // OA提交
    {
      key: 'OA_SUBMIT',
      value: 'OA_SUBMIT',
      name: 'OA提交',
    },
    // OA驳回
    {
      key: 'OA_REJECT',
      value: 'OA_REJECT',
      name: 'OA驳回',
    },
    // OA归档
    {
      key: 'OA_ARCHIVED',
      value: 'OA_ARCHIVED',
      name: 'OA归档',
    },
    // 保存草稿
    {
      key: 'DEMAND_NEWLY_BUILT_SAVE',
      value: 'DEMAND_NEWLY_BUILT_SAVE',
      name: '保存草稿',
    },
    // 提交策划设计
    {
      key: 'DEMAND_SUBMIT_TO_PENDING_DESIGN',
      value: 'DEMAND_SUBMIT_TO_PENDING_DESIGN',
      name: '提交策划设计',
    },
    // 策划设计-拆单
    {
      key: 'DEMAND_SPLIT_TO_PLAN',
      value: 'DEMAND_SPLIT_TO_PLAN',
      name: '策划设计-拆单',
    },
    // PMO编辑
    {
      key: 'DEMAND_PMO_EDIT',
      value: 'DEMAND_PMO_EDIT',
      name: 'PMO编辑',
    },
    // 编辑合作内容
    {
      key: 'DEMAND_COOPERATE_ACCOUNT_EDIT',
      value: 'DEMAND_COOPERATE_ACCOUNT_EDIT',
      name: '编辑合作内容',
    },
    // 策划编辑三级单
    {
      key: 'DEMAND_PLAN_USER_EDIT_LEAVES',
      value: 'DEMAND_PLAN_USER_EDIT_LEAVES',
      name: '策划编辑三级单',
    },
    // 需求前置审批-发起
    {
      key: 'DEMAND_PRE_APPROVE_SUBMIT',
      value: 'DEMAND_PRE_APPROVE_SUBMIT',
      name: '需求前置审批-发起',
    },
    // 需求前置审批-已归档
    {
      key: 'DEMAND_PRE_APPROVE_ARCHIVED',
      value: 'DEMAND_PRE_APPROVE_ARCHIVED',
      name: '需求前置审批-已归档',
    },
    // 重新报价
    {
      key: 'DEMAND_SUPPLIER_REQUOTE',
      value: 'DEMAND_SUPPLIER_REQUOTE',
      name: '重新报价',
    },
    // OA定标审批-发起
    {
      key: 'DEMAND_FEE_APPROVAL_SUBMIT',
      value: 'DEMAND_FEE_APPROVAL_SUBMIT',
      name: 'OA定标审批-发起',
    },
    // OA营销方案与预算审批-发起
    {
      key: 'DEMAND_PLAN_APPROVAL_SUBMIT',
      value: 'DEMAND_PLAN_APPROVAL_SUBMIT',
      name: 'OA营销方案与预算审批-发起',
    },
    // OA定标审批-已归档
    {
      key: 'DEMAND_FEE_APPROVAL_ARCHIVED',
      value: 'DEMAND_FEE_APPROVAL_ARCHIVED',
      name: 'OA定标审批-已归档',
    },
    // 提交营销业务验收与结算
    {
      key: 'DEMAND_REIMBURSEMENT_SUBMIT',
      value: 'DEMAND_REIMBURSEMENT_SUBMIT',
      name: '提交营销业务验收与结算',
    },
    // 营销业务验收与结算驳回单重新提交
    {
      key: 'DEMAND_REIMBURSEMENT_RESUBMIT_REJECTED',
      value: 'DEMAND_REIMBURSEMENT_RESUBMIT_REJECTED',
      name: '营销业务验收与结算驳回单重新提交',
    },
    // 营销业务验收与结算-已归档
    {
      key: 'DEMAND_REIMBURSEMENT_ARCHIVED',
      value: 'DEMAND_REIMBURSEMENT_ARCHIVED',
      name: '营销业务验收与结算-已归档',
    },
    // 修改合同号
    {
      key: 'DEMAND_MODIFY_CONTRACT_NO',
      value: 'DEMAND_MODIFY_CONTRACT_NO',
      name: '修改合同号',
    },
    // 提交付款
    {
      key: 'DEMAND_PAYMENT_SUBMIT',
      value: 'DEMAND_PAYMENT_SUBMIT',
      name: '提交付款',
    },
    // 付款单已删除
    {
      key: 'DEMAND_PAYMENT_DELETED',
      value: 'DEMAND_PAYMENT_DELETED',
      name: '付款单已删除',
    },
    // 付款单已归档
    {
      key: 'DEMAND_PAYMENT_ARCHIVED',
      value: 'DEMAND_PAYMENT_ARCHIVED',
      name: '付款单已归档',
    },
    // 上传发票
    {
      key: 'DEMAND_UPLOAD_INVOICE',
      value: 'DEMAND_UPLOAD_INVOICE',
      name: '上传发票',
    },
    // 营销业务验收与结算审批-已驳回
    {
      key: 'DEMAND_SUBMIT_OA_REJECTED',
      value: 'DEMAND_SUBMIT_OA_REJECTED',
      name: '营销业务验收与结算审批-已驳回',
    },
    // 拒单
    {
      key: 'DEMAND_SUPPLIER_REJECT',
      value: 'DEMAND_SUPPLIER_REJECT',
      name: '拒单',
    },
    // 开启评分
    {
      key: 'DEMAND_START_QUOTE_SCORE',
      value: 'DEMAND_START_QUOTE_SCORE',
      name: '开启评分',
    },
    // 自动开启评分
    {
      key: 'DEMAND_START_QUOTE_SCORE_AUTO',
      value: 'DEMAND_START_QUOTE_SCORE_AUTO',
      name: '自动开启评分',
    },
    // 策划评分
    {
      key: 'DEMAND_PLANNER_QUOTE_SCORE',
      value: 'DEMAND_PLANNER_QUOTE_SCORE',
      name: '策划评分',
    },
    // 商务评分
    {
      key: 'DEMAND_BUSINESS_QUOTE_SCORE',
      value: 'DEMAND_BUSINESS_QUOTE_SCORE',
      name: '商务评分',
    },
    // 需求方评分
    {
      key: 'DEMAND_DEMANDER_QUOTE_SCORE',
      value: 'DEMAND_DEMANDER_QUOTE_SCORE',
      name: '需求方评分',
    },
    // 修改评分权重
    {
      key: 'DEMAND_EDIT_QUOTE_SCORE_WEIGHT',
      value: 'DEMAND_EDIT_QUOTE_SCORE_WEIGHT',
      name: '修改评分权重',
    },
    // 发起超额审批
    {
      key: 'DEMAND_OVER_AMOUNT_APPROVE_SUBMIT',
      value: 'DEMAND_OVER_AMOUNT_APPROVE_SUBMIT',
      name: '发起超额审批',
    },
    // 编辑结算金额
    {
      key: 'DEMAND_EDIT_SETTLE_AMOUNT',
      value: 'DEMAND_EDIT_SETTLE_AMOUNT',
      name: '编辑结算金额',
    },
    // 超额审批通过
    {
      key: 'DEMAND_OVER_AMOUNT_APPROVE_PASS',
      value: 'DEMAND_OVER_AMOUNT_APPROVE_PASS',
      name: '超额审批通过',
    },
    // 超额审批驳回
    {
      key: 'DEMAND_OVER_AMOUNT_APPROVE_REJECT',
      value: 'DEMAND_OVER_AMOUNT_APPROVE_REJECT',
      name: '超额审批驳回',
    },
    // 发起KPI调整
    {
      key: 'DEMAND_KPI_ADJUST_SUBMIT',
      value: 'DEMAND_KPI_ADJUST_SUBMIT',
      name: '发起KPI调整',
    },
    // KPI调整已归档
    {
      key: 'DEMAND_KPI_ADJUST_ARCHIVED',
      value: 'DEMAND_KPI_ADJUST_ARCHIVED',
      name: 'KPI调整已归档',
    },
    // 考核指标预期调整已归档
    {
      key: 'DEMAND_KPI_ADJUST_CHANGED',
      value: 'DEMAND_KPI_ADJUST_CHANGED',
      name: '考核指标预期调整已归档',
    },
    // 创建计提单
    {
      key: 'DEMAND_CREATE_MONTH_RELEASE',
      value: 'DEMAND_CREATE_MONTH_RELEASE',
      name: '创建计提单',
    },
    // 拆单
    {
      key: 'TRUMPET_SPLIT_DEMAND',
      value: 'TRUMPET_SPLIT_DEMAND',
      name: '拆单',
    },
    // 添加预算
    {
      key: 'TRUMPET_ADD_BUDGET',
      value: 'TRUMPET_ADD_BUDGET',
      name: '添加预算',
    },
    // 追加预算审批提交
    {
      key: 'TRUMPET_ADD_BUDGET_SUBMIT',
      value: 'TRUMPET_ADD_BUDGET_SUBMIT',
      name: '追加预算审批提交',
    },
    // 追加预算审批通过
    {
      key: 'TRUMPET_ADD_BUDGET_PASS',
      value: 'TRUMPET_ADD_BUDGET_PASS',
      name: '追加预算审批通过',
    },
    // 追加预算审批驳回
    {
      key: 'TRUMPET_ADD_BUDGET_REJECT',
      value: 'TRUMPET_ADD_BUDGET_REJECT',
      name: '追加预算审批驳回',
    },
    // 废弃
    {
      key: 'TRUMPET_CLOSE',
      value: 'TRUMPET_CLOSE',
      name: '废弃',
    },
    // 指派策划
    {
      key: 'TRUMPET_EDIT_PLANNER',
      value: 'TRUMPET_EDIT_PLANNER',
      name: '指派策划',
    },
    // 策划编辑一级单
    {
      key: 'TRUMPET_PLAN_USER_EDIT_L1',
      value: 'TRUMPET_PLAN_USER_EDIT_L1',
      name: '策划编辑一级单',
    },
    // 拆单
    {
      key: 'CLOSED_LOOP_SPLIT_DEMAND',
      value: 'CLOSED_LOOP_SPLIT_DEMAND',
      name: '拆单',
    },
    // 废弃
    {
      key: 'CLOSED_LOOP_CLOSE',
      value: 'CLOSED_LOOP_CLOSE',
      name: '废弃',
    },
    // 创建付款单
    {
      key: 'CLOSED_LOOP_SUBMIT_PAYMENT',
      value: 'CLOSED_LOOP_SUBMIT_PAYMENT',
      name: '创建付款单',
    },
    // 需求负责人审批通过
    {
      key: 'CLOSED_LOOP_CASE_APPROVE_SETTLE_PASS',
      value: 'CLOSED_LOOP_CASE_APPROVE_SETTLE_PASS',
      name: '需求负责人审批通过',
    },
    // 需求负责人审批拒绝
    {
      key: 'CLOSED_LOOP_CASE_APPROVE_SETTLE_REJECT',
      value: 'CLOSED_LOOP_CASE_APPROVE_SETTLE_REJECT',
      name: '需求负责人审批拒绝',
    },
    // 添加供应商
    {
      key: 'BIDDING_ADD_SUPPLIER',
      value: 'BIDDING_ADD_SUPPLIER',
      name: '添加供应商',
    },
    // 开标
    {
      key: 'BIDDING_OPEN_REVIEW',
      value: 'BIDDING_OPEN_REVIEW',
      name: '开标',
    },
    // 延期
    {
      key: 'BIDDING_OPEN_REVIEW_DEFER',
      value: 'BIDDING_OPEN_REVIEW_DEFER',
      name: '延期',
    },
    // 开启技术方案比选
    {
      key: 'BIDDING_OPEN_REVIEW_TECHNICAL',
      value: 'BIDDING_OPEN_REVIEW_TECHNICAL',
      name: '开启技术方案比选',
    },
    // 开启商务方案比选
    {
      key: 'BIDDING_OPEN_REVIEW_COMMERCIAL',
      value: 'BIDDING_OPEN_REVIEW_COMMERCIAL',
      name: '开启商务方案比选',
    },
    // 提交OA比选结果审批
    {
      key: 'BIDDING_MANUAL_SUBMIT_OA',
      value: 'BIDDING_MANUAL_SUBMIT_OA',
      name: '提交OA比选结果审批',
    },
    // 通过
    {
      key: 'BIDDING_EVALUATION_RESULT_PASS',
      value: 'BIDDING_EVALUATION_RESULT_PASS',
      name: '通过',
    },
    // 复评
    {
      key: 'BIDDING_EVALUATION_RESULT_RESTART',
      value: 'BIDDING_EVALUATION_RESULT_RESTART',
      name: '复评',
    },
    // 复听
    {
      key: 'BIDDING_EVALUATION_RESULT_LISTEN',
      value: 'BIDDING_EVALUATION_RESULT_LISTEN',
      name: '复听',
    },
    // 延期
    {
      key: 'BIDDING_EVALUATION_RESULT_DEFER',
      value: 'BIDDING_EVALUATION_RESULT_DEFER',
      name: '延期',
    },
    // 流标
    {
      key: 'BIDDING_EVALUATION_RESULT_FAIL',
      value: 'BIDDING_EVALUATION_RESULT_FAIL',
      name: '流标',
    },
    // 提交比选答疑日期
    {
      key: 'BIDDING_SAVE_QA_TIME',
      value: 'BIDDING_SAVE_QA_TIME',
      name: '提交比选答疑日期',
    },
    // 修改比选答疑日期
    {
      key: 'BIDDING_UPDATE_QA_TIME',
      value: 'BIDDING_UPDATE_QA_TIME',
      name: '修改比选答疑日期',
    },
    // 提交比选答疑记录
    {
      key: 'BIDDING_SAVE_QA_ATTACH',
      value: 'BIDDING_SAVE_QA_ATTACH',
      name: '提交比选答疑记录',
    },
    // 修改比选答疑记录
    {
      key: 'BIDDING_UPDATE_QA_ATTACH',
      value: 'BIDDING_UPDATE_QA_ATTACH',
      name: '修改比选答疑记录',
    },
    // 跳过技术评分
    {
      key: 'BIDDING_SKIP_TECH_SCORE',
      value: 'BIDDING_SKIP_TECH_SCORE',
      name: '跳过技术评分',
    },
    // 跳过商务评分
    {
      key: 'BIDDING_SKIP_BUSINESS_SCORE',
      value: 'BIDDING_SKIP_BUSINESS_SCORE',
      name: '跳过商务评分',
    },
    // 发起KPI调整
    {
      key: 'DEMAND_KPI_ADJUSTMENT_SUBMIT',
      value: 'DEMAND_KPI_ADJUSTMENT_SUBMIT',
      name: '发起KPI调整',
    },
    // 发起KPI调整提交OA审批
    {
      key: 'DEMAND_KPI_ADJUSTMENT_PLANNER_PASS',
      value: 'DEMAND_KPI_ADJUSTMENT_PLANNER_PASS',
      name: '发起KPI调整提交OA审批',
    },
    // 发起KPI调整退回
    {
      key: 'DEMAND_KPI_ADJUSTMENT_PLANNER_REJECT',
      value: 'DEMAND_KPI_ADJUSTMENT_PLANNER_REJECT',
      name: '发起KPI调整退回',
    },
    // 发起KPI调整审批驳回
    {
      key: 'DEMAND_KPI_ADJUSTMENT_OA_REJECT',
      value: 'DEMAND_KPI_ADJUSTMENT_OA_REJECT',
      name: '发起KPI调整审批驳回',
    },
    // KPI调整已归档
    {
      key: 'DEMAND_KPI_ADJUSTMENT_OA_PASS',
      value: 'DEMAND_KPI_ADJUSTMENT_OA_PASS',
      name: 'KPI调整已归档',
    },
    // 发起KPI调整撤回
    {
      key: 'DEMAND_KPI_ADJUSTMENT_PRINCIPAL_RECALL',
      value: 'DEMAND_KPI_ADJUSTMENT_PRINCIPAL_RECALL',
      name: '发起KPI调整撤回',
    },
    // 发起预付
    {
      key: 'PREPAYMENT_CREATE',
      value: 'PREPAYMENT_CREATE',
      name: '发起预付',
    },
    // 发起审批
    {
      key: 'PREPAYMENT_SUBMIT',
      value: 'PREPAYMENT_SUBMIT',
      name: '发起审批',
    },
    // 重新发起预付
    {
      key: 'PREPAYMENT_RESUBMIT',
      value: 'PREPAYMENT_RESUBMIT',
      name: '重新发起预付',
    },
    // 撤回审批
    {
      key: 'PREPAYMENT_WITHDRAW',
      value: 'PREPAYMENT_WITHDRAW',
      name: '撤回审批',
    },
    // OA审批通过
    {
      key: 'PREPAYMENT_OA_PASS',
      value: 'PREPAYMENT_OA_PASS',
      name: 'OA审批通过',
    },
    // OA审批驳回
    {
      key: 'PREPAYMENT_OA_REJECT',
      value: 'PREPAYMENT_OA_REJECT',
      name: 'OA审批驳回',
    },
    // 付款完成
    {
      key: 'PREPAYMENT_PAYMENT_COMPLETE',
      value: 'PREPAYMENT_PAYMENT_COMPLETE',
      name: '付款完成',
    },
    // 废弃
    {
      key: 'PREPAYMENT_ABANDON',
      value: 'PREPAYMENT_ABANDON',
      name: '废弃',
    },
    // 上传发票
    {
      key: 'PREPAYMENT_UPLOAD_INVOICE',
      value: 'PREPAYMENT_UPLOAD_INVOICE',
      name: '上传发票',
    },
    // 删除发票
    {
      key: 'PREPAYMENT_REMOVE_INVOICE',
      value: 'PREPAYMENT_REMOVE_INVOICE',
      name: '删除发票',
    },
    // 提交审核
    {
      key: 'SUPPLIER_SUBMIT_REVIEW',
      value: 'SUPPLIER_SUBMIT_REVIEW',
      name: '提交审核',
    },
    // 导出全部方案
    {
      key: 'EXPORT_ALL_PROPOSALS',
      value: 'EXPORT_ALL_PROPOSALS',
      name: '导出全部方案',
    },
    // 暂存
    {
      key: 'BUDGET_ADJUST_SAVE',
      value: 'BUDGET_ADJUST_SAVE',
      name: '暂存',
    },
    // 提交审批
    {
      key: 'BUDGET_ADJUST_SUBMIT',
      value: 'BUDGET_ADJUST_SUBMIT',
      name: '提交审批',
    },
    // OA审批通过-已生效
    {
      key: 'BUDGET_ADJUST_OA_PASS',
      value: 'BUDGET_ADJUST_OA_PASS',
      name: 'OA审批通过-已生效',
    },
    // OA审批通过-不生效
    {
      key: 'BUDGET_ADJUST_OA_PASS_NOT_EFFECTIVE',
      value: 'BUDGET_ADJUST_OA_PASS_NOT_EFFECTIVE',
      name: 'OA审批通过-不生效',
    },
    // OA审批驳回
    {
      key: 'BUDGET_ADJUST_OA_REJECT',
      value: 'BUDGET_ADJUST_OA_REJECT',
      name: 'OA审批驳回',
    },
    // 废弃
    {
      key: 'BUDGET_ADJUST_ABANDON',
      value: 'BUDGET_ADJUST_ABANDON',
      name: '废弃',
    },
    // 编辑辅助自定义指标
    {
      key: 'EDIT_AUXILIARY_KPI',
      value: 'EDIT_AUXILIARY_KPI',
      name: '编辑辅助自定义指标',
    },
    // 申请权限
    {
      key: 'PERMISSION_APPLICATION_CREATE',
      value: 'PERMISSION_APPLICATION_CREATE',
      name: '申请权限',
    },
    // 创建OA权限审批
    {
      key: 'PERMISSION_APPLICATION_OA_SUBMIT',
      value: 'PERMISSION_APPLICATION_OA_SUBMIT',
      name: '创建OA权限审批',
    },
    // OA审批驳回
    {
      key: 'PERMISSION_APPLICATION_OA_REJECT',
      value: 'PERMISSION_APPLICATION_OA_REJECT',
      name: 'OA审批驳回',
    },
    // OA审批通过
    {
      key: 'PERMISSION_APPLICATION_OA_PASS',
      value: 'PERMISSION_APPLICATION_OA_PASS',
      name: 'OA审批通过',
    },
  ] as const),
  FlowEventsEnum: createEnum([
    {
      key: 'SUBMIT_DRAFT',
      value: 'SUBMIT_DRAFT',
      name: 'unknown',
    },
    {
      key: 'START_DESIGN',
      value: 'START_DESIGN',
      name: 'unknown',
    },
    {
      key: 'DESIGN',
      value: 'DESIGN',
      name: 'unknown',
    },
    {
      key: 'ASSESS',
      value: 'ASSESS',
      name: 'unknown',
    },
    {
      key: 'QUOTE',
      value: 'QUOTE',
      name: 'unknown',
    },
    {
      key: 'QUOTE_SCORE',
      value: 'QUOTE_SCORE',
      name: 'unknown',
    },
    {
      key: 'TODO',
      value: 'TODO',
      name: 'unknown',
    },
    {
      key: 'AUDIT_QUOTE_BUSINESS',
      value: 'AUDIT_QUOTE_BUSINESS',
      name: 'unknown',
    },
    {
      key: 'AUDIT_QUOTE_BUSINESS_LEADER',
      value: 'AUDIT_QUOTE_BUSINESS_LEADER',
      name: 'unknown',
    },
    {
      key: 'AUDIT_QUOTE_PLANNER',
      value: 'AUDIT_QUOTE_PLANNER',
      name: 'unknown',
    },
    {
      key: 'AUDIT_QUOTE_MARKETING',
      value: 'AUDIT_QUOTE_MARKETING',
      name: 'unknown',
    },
    {
      key: 'AUDIT_QUOTE_MARKETING_LEADER',
      value: 'AUDIT_QUOTE_MARKETING_LEADER',
      name: 'unknown',
    },
    {
      key: 'AUDIT_QUOTE_PLANNER_LEADER',
      value: 'AUDIT_QUOTE_PLANNER_LEADER',
      name: 'unknown',
    },
    {
      key: 'NONE',
      value: 'NONE',
      name: 'unknown',
    },
    {
      key: 'AUDIT_BING_EXECUTION_DATA',
      value: 'AUDIT_BING_EXECUTION_DATA',
      name: 'unknown',
    },
    {
      key: 'AUDIT_MAKING',
      value: 'AUDIT_MAKING',
      name: 'unknown',
    },
    {
      key: 'AUDIT_PLANNER_ACCEPT',
      value: 'AUDIT_PLANNER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'AUDIT_PLANNER_LEADER_ACCEPT',
      value: 'AUDIT_PLANNER_LEADER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'AUDIT_BUSINESS_ACCEPT',
      value: 'AUDIT_BUSINESS_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'AUDIT_DEMANDER_ACCEPT',
      value: 'AUDIT_DEMANDER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'AUDIT_APPROVE_SETTLE',
      value: 'AUDIT_APPROVE_SETTLE',
      name: 'unknown',
    },
    {
      key: 'AUDIT_QC_ACCEPT',
      value: 'AUDIT_QC_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'AUDIT_SUBMIT_OA',
      value: 'AUDIT_SUBMIT_OA',
      name: 'unknown',
    },
    {
      key: 'AUDIT_SUBMIT_REIMBURSE',
      value: 'AUDIT_SUBMIT_REIMBURSE',
      name: 'unknown',
    },
    {
      key: 'CLOSED',
      value: 'CLOSED',
      name: 'unknown',
    },
    {
      key: 'PRE_SUBMIT_OA',
      value: 'PRE_SUBMIT_OA',
      name: 'unknown',
    },
    {
      key: 'FEE_SUBMIT_OA',
      value: 'FEE_SUBMIT_OA',
      name: 'unknown',
    },
    {
      key: 'START_BIDDING',
      value: 'START_BIDDING',
      name: 'unknown',
    },
    {
      key: 'BREAK_UP',
      value: 'BREAK_UP',
      name: 'unknown',
    },
    {
      key: 'OA_ROLLBACK',
      value: 'OA_ROLLBACK',
      name: 'unknown',
    },
    {
      key: 'FEE_ROLLBACK',
      value: 'FEE_ROLLBACK',
      name: 'unknown',
    },
    {
      key: 'DISCARD',
      value: 'DISCARD',
      name: 'unknown',
    },
  ] as const),
  // AI验收结论
  AcceptanceConclusionEnum: createEnum([
    // 全部通过
    {
      key: 'PASS',
      value: 'PASS',
      name: '全部通过',
    },
    // 有条件通过-低风险
    {
      key: 'CONDITIONAL_PASS_LOW_RISK',
      value: 'CONDITIONAL_PASS_LOW_RISK',
      name: '有条件通过-低风险',
    },
    // 有条件通过-中风险
    {
      key: 'CONDITIONAL_PASS_MID_RISK',
      value: 'CONDITIONAL_PASS_MID_RISK',
      name: '有条件通过-中风险',
    },
    // 不通过
    {
      key: 'REJECT',
      value: 'REJECT',
      name: '不通过',
    },
  ] as const),
  SupplierServiceTypeEnum: createEnum([
    // 社媒全案
    {
      key: 'SERVICE_ALL',
      value: 'SERVICE_ALL',
      name: '社媒全案',
    },
    // 独家资源MCN
    {
      key: 'SERVICE_MCN',
      value: 'SERVICE_MCN',
      name: '独家资源MCN',
    },
    // 线下活动执行
    {
      key: 'SERVICE_OFFLINE',
      value: 'SERVICE_OFFLINE',
      name: '线下活动执行',
    },
    // 素材制作
    {
      key: 'MATERIAL_MAKING',
      value: 'MATERIAL_MAKING',
      name: '素材制作',
    },
    // 直播平台
    {
      key: 'SERVICE_BROADCAST',
      value: 'SERVICE_BROADCAST',
      name: '直播平台',
    },
    // 经纪公司主播
    {
      key: 'SERVICE_ANCHOR',
      value: 'SERVICE_ANCHOR',
      name: '经纪公司主播',
    },
    // 公关全案（线上+线下）
    {
      key: 'PUBLIC_RELATIONS_ALL',
      value: 'PUBLIC_RELATIONS_ALL',
      name: '公关全案（线上+线下）',
    },
    // 公关传播（线上）
    {
      key: 'PUBLIC_RELATIONS_ONLINE',
      value: 'PUBLIC_RELATIONS_ONLINE',
      name: '公关传播（线上）',
    },
    // 公关活动（线下）
    {
      key: 'PUBLIC_RELATIONS_OFFLINE',
      value: 'PUBLIC_RELATIONS_OFFLINE',
      name: '公关活动（线下）',
    },
    // 品牌投放
    {
      key: 'BRAND_LAUNCH',
      value: 'BRAND_LAUNCH',
      name: '品牌投放',
    },
    // 效果投放
    {
      key: 'EFFECT_LAUNCH',
      value: 'EFFECT_LAUNCH',
      name: '效果投放',
    },
    // 全案创意类
    {
      key: 'COMPLETE_CREATIVE_PROGRAM',
      value: 'COMPLETE_CREATIVE_PROGRAM',
      name: '全案创意类',
    },
    // 视频制作类
    {
      key: 'VIDEO_PRODUCTION',
      value: 'VIDEO_PRODUCTION',
      name: '视频制作类',
    },
    // 音频制作类
    {
      key: 'AUDIO_PRODUCTION',
      value: 'AUDIO_PRODUCTION',
      name: '音频制作类',
    },
    // 宣传插画类
    {
      key: 'PROMOTIONAL_ILLUSTRATION',
      value: 'PROMOTIONAL_ILLUSTRATION',
      name: '宣传插画类',
    },
    // 平面设计类
    {
      key: 'GRAPHIC_DESIGN',
      value: 'GRAPHIC_DESIGN',
      name: '平面设计类',
    },
    // 平面摄影类
    {
      key: 'PHOTOGRAPHY',
      value: 'PHOTOGRAPHY',
      name: '平面摄影类',
    },
    // 网络互动类
    {
      key: 'NETWORK_INTERACTION',
      value: 'NETWORK_INTERACTION',
      name: '网络互动类',
    },
    // 艺术合作类
    {
      key: 'ARTISTIC_COOPERATION',
      value: 'ARTISTIC_COOPERATION',
      name: '艺术合作类',
    },
    // 买量广告制作类
    {
      key: 'PURCHASE_AD_PRODUCTION',
      value: 'PURCHASE_AD_PRODUCTION',
      name: '买量广告制作类',
    },
    // 广告制作
    {
      key: 'ADVERTISEMENT_MAKING',
      value: 'ADVERTISEMENT_MAKING',
      name: '广告制作',
    },
    // 广告创意
    {
      key: 'ADVERTISING_CREATIVITY',
      value: 'ADVERTISING_CREATIVITY',
      name: '广告创意',
    },
    // 文娱宣发
    {
      key: 'CULTURE_AND_ENTERTAINMENT_PUBLICITY',
      value: 'CULTURE_AND_ENTERTAINMENT_PUBLICITY',
      name: '文娱宣发',
    },
    // 新媒体运营
    {
      key: 'NEW_MEDIA_OPERATIONS',
      value: 'NEW_MEDIA_OPERATIONS',
      name: '新媒体运营',
    },
    // 整合推广
    {
      key: 'INTEGRATED_PROMOTION',
      value: 'INTEGRATED_PROMOTION',
      name: '整合推广',
    },
    // 真人拍摄视频
    {
      key: 'LIVE_ACTION_VIDEO',
      value: 'LIVE_ACTION_VIDEO',
      name: '真人拍摄视频',
    },
    // 动画
    {
      key: 'ANIMATION_ALL',
      value: 'ANIMATION_ALL',
      name: '动画',
    },
    // 花絮视频
    {
      key: 'HIGHLIGHT_VIDEO',
      value: 'HIGHLIGHT_VIDEO',
      name: '花絮视频',
    },
    // 综艺节目/短剧
    {
      key: 'VARIETY_SHOW_OR_MINISERIES',
      value: 'VARIETY_SHOW_OR_MINISERIES',
      name: '综艺节目/短剧',
    },
    // 动作捕捉
    {
      key: 'MOTION_CAPTURE',
      value: 'MOTION_CAPTURE',
      name: '动作捕捉',
    },
    // 影视后期/包装
    {
      key: 'VIDEO_POST_PACKAGING',
      value: 'VIDEO_POST_PACKAGING',
      name: '影视后期/包装',
    },
    // 虚拟偶像
    {
      key: 'VIRTUAL_IDOL',
      value: 'VIRTUAL_IDOL',
      name: '虚拟偶像',
    },
    // 音乐音效制作
    {
      key: 'MUSIC_SOUND_PRODUCTION',
      value: 'MUSIC_SOUND_PRODUCTION',
      name: '音乐音效制作',
    },
    // 声优及配音
    {
      key: 'VOICE_ACTORS_AND_DUBBING',
      value: 'VOICE_ACTORS_AND_DUBBING',
      name: '声优及配音',
    },
    // 宣传插画
    {
      key: 'PROMOTION_ILLUSTRATION',
      value: 'PROMOTION_ILLUSTRATION',
      name: '宣传插画',
    },
    // 概念设定
    {
      key: 'CONCEPT_SETTING',
      value: 'CONCEPT_SETTING',
      name: '概念设定',
    },
    // 3D模型修图
    {
      key: 'MODEL_RETOUCHING_3D',
      value: 'MODEL_RETOUCHING_3D',
      name: '3D模型修图',
    },
    // 3D模型制作/渲染
    {
      key: 'MODEL_MAKING_RENDERING_3D',
      value: 'MODEL_MAKING_RENDERING_3D',
      name: '3D模型制作/渲染',
    },
    // 后期合成
    {
      key: 'COMPOSITING',
      value: 'COMPOSITING',
      name: '后期合成',
    },
    // 海报设计
    {
      key: 'POSTER_DESIGN',
      value: 'POSTER_DESIGN',
      name: '海报设计',
    },
    // VI设计
    {
      key: 'VI_DESIGN',
      value: 'VI_DESIGN',
      name: 'VI设计',
    },
    // 字体设计
    {
      key: 'FONT_DESIGN',
      value: 'FONT_DESIGN',
      name: '字体设计',
    },
    // 线下体验设计
    {
      key: 'OFFLINE_EXPERIENCE_DESIGN',
      value: 'OFFLINE_EXPERIENCE_DESIGN',
      name: '线下体验设计',
    },
    // 文创衍生品设计/制作
    {
      key: 'DESIGN_PRODUCTION_OF_CULTURAL_AND_CREATIVE_DERIVATIVES',
      value: 'DESIGN_PRODUCTION_OF_CULTURAL_AND_CREATIVE_DERIVATIVES',
      name: '文创衍生品设计/制作',
    },
    // 影视海报
    {
      key: 'MOVIE_POSTER',
      value: 'MOVIE_POSTER',
      name: '影视海报',
    },
    // 平面摄影师
    {
      key: 'GRAPHIC_PHOTOGRAPHER',
      value: 'GRAPHIC_PHOTOGRAPHER',
      name: '平面摄影师',
    },
    // 修图后期
    {
      key: 'POST_RETOUCHING',
      value: 'POST_RETOUCHING',
      name: '修图后期',
    },
    // 服装师
    {
      key: 'DRESSMAKER',
      value: 'DRESSMAKER',
      name: '服装师',
    },
    // 造型师
    {
      key: 'STYLIST',
      value: 'STYLIST',
      name: '造型师',
    },
    // 平面制片公司
    {
      key: 'GRAPHIC_PRODUCTION_COMPANY',
      value: 'GRAPHIC_PRODUCTION_COMPANY',
      name: '平面制片公司',
    },
    // H5
    {
      key: 'HTML5_PAGE',
      value: 'HTML5_PAGE',
      name: 'H5',
    },
    // 渠道投放DEMO
    {
      key: 'CHANNEL_LAUNCH_DEMO',
      value: 'CHANNEL_LAUNCH_DEMO',
      name: '渠道投放DEMO',
    },
    // 渠道页面
    {
      key: 'CHANNEL_PAGE',
      value: 'CHANNEL_PAGE',
      name: '渠道页面',
    },
    // 网页设计制作
    {
      key: 'WEB_DESIGN_PRODUCTION',
      value: 'WEB_DESIGN_PRODUCTION',
      name: '网页设计制作',
    },
    // APP商店图标及卖点展示图
    {
      key: 'APP_STORE_ICON_AND_DISPLAY_DIAGRAM',
      value: 'APP_STORE_ICON_AND_DISPLAY_DIAGRAM',
      name: 'APP商店图标及卖点展示图',
    },
    // 动态动画DEMO
    {
      key: 'DYNAMIC_ANIMATION_DEMO',
      value: 'DYNAMIC_ANIMATION_DEMO',
      name: '动态动画DEMO',
    },
    // 广告投放延展
    {
      key: 'ADVERTISING_EXTENSION',
      value: 'ADVERTISING_EXTENSION',
      name: '广告投放延展',
    },
    // 新媒体艺术
    {
      key: 'NEW_MEDIA_ART',
      value: 'NEW_MEDIA_ART',
      name: '新媒体艺术',
    },
    // 艺术家
    {
      key: 'ARTIST',
      value: 'ARTIST',
      name: '艺术家',
    },
    // 设计师
    {
      key: 'DESIGNER',
      value: 'DESIGNER',
      name: '设计师',
    },
    // 摄影师
    {
      key: 'PHOTOGRAPHER',
      value: 'PHOTOGRAPHER',
      name: '摄影师',
    },
    // 潮流时尚整合商
    {
      key: 'TRENDY_FASHION_INTEGRATOR',
      value: 'TRENDY_FASHION_INTEGRATOR',
      name: '潮流时尚整合商',
    },
    // 买量2D动画视频
    {
      key: 'PURCHASE_2D_ANIMATION_VIDEO',
      value: 'PURCHASE_2D_ANIMATION_VIDEO',
      name: '买量2D动画视频',
    },
    // 游戏内录视频
    {
      key: 'INGAME_VIDEO_RECORDING',
      value: 'INGAME_VIDEO_RECORDING',
      name: '游戏内录视频',
    },
    // UE4引擎视频
    {
      key: 'UE4_ENGINE_VIDEO',
      value: 'UE4_ENGINE_VIDEO',
      name: 'UE4引擎视频',
    },
    // 买量包装剪辑视频
    {
      key: 'PURCHASE_PACKAGING_EDITING_VIDEO',
      value: 'PURCHASE_PACKAGING_EDITING_VIDEO',
      name: '买量包装剪辑视频',
    },
    // 买量实拍视频
    {
      key: 'PURCHASE_REAL_SHOT_VIDEO',
      value: 'PURCHASE_REAL_SHOT_VIDEO',
      name: '买量实拍视频',
    },
    // 公关传播
    {
      key: 'MARKETING_ACTIVITIES',
      value: 'MARKETING_ACTIVITIES',
      name: '公关传播',
    },
    // 营销活动
    {
      key: 'PLAYER_ACTIVITIES',
      value: 'PLAYER_ACTIVITIES',
      name: '营销活动',
    },
    // 稿件撰写
    {
      key: 'MANUSCRIPT_WRITING',
      value: 'MANUSCRIPT_WRITING',
      name: '稿件撰写',
    },
    // 网站维护
    {
      key: 'WEBSITE_MAINTENANCE',
      value: 'WEBSITE_MAINTENANCE',
      name: '网站维护',
    },
    // 媒体投放
    {
      key: 'MEDIA_BUYING',
      value: 'MEDIA_BUYING',
      name: '媒体投放',
    },
    // 其他
    {
      key: 'TRANSLATION_SERVICE',
      value: 'TRANSLATION_SERVICE',
      name: '其他',
    },
    // 全案类
    {
      key: 'WHOLE_CLASS',
      value: 'WHOLE_CLASS',
      name: '全案类',
    },
    // 安卓渠道买量
    {
      key: 'ANDROID_PURCHASED_QUANTITY',
      value: 'ANDROID_PURCHASED_QUANTITY',
      name: '安卓渠道买量',
    },
    // 商务KOL/KOC合作
    {
      key: 'BUSINESS_KOL',
      value: 'BUSINESS_KOL',
      name: '商务KOL/KOC合作',
    },
    // 视觉平面设计
    {
      key: 'VISUAL_GRAPHIC_DESIGN',
      value: 'VISUAL_GRAPHIC_DESIGN',
      name: '视觉平面设计',
    },
    // 买量素材制作
    {
      key: 'BUY_MATERIAL_PRODUCTION',
      value: 'BUY_MATERIAL_PRODUCTION',
      name: '买量素材制作',
    },
    // 社区论坛运营
    {
      key: 'COMMUNITY_BBS_OPERATION',
      value: 'COMMUNITY_BBS_OPERATION',
      name: '社区论坛运营',
    },
    // 内容制作发布
    {
      key: 'CONTENT_PRODUCTION_RELEASE',
      value: 'CONTENT_PRODUCTION_RELEASE',
      name: '内容制作发布',
    },
    // 社群游戏运营
    {
      key: 'COMMUNITY_GAME_OPERATIONS',
      value: 'COMMUNITY_GAME_OPERATIONS',
      name: '社群游戏运营',
    },
    // 赛事场地和搭建服务
    {
      key: 'EVENT_VENUES_BUILDING_SERVICES',
      value: 'EVENT_VENUES_BUILDING_SERVICES',
      name: '赛事场地和搭建服务',
    },
    // 赛事直转播制作
    {
      key: 'DIRECT_BROADCAST_PRODUCTION',
      value: 'DIRECT_BROADCAST_PRODUCTION',
      name: '赛事直转播制作',
    },
    // 第三方资源
    {
      key: 'THIRD_PARTY_RESOURCES',
      value: 'THIRD_PARTY_RESOURCES',
      name: '第三方资源',
    },
    // 赛事全案
    {
      key: 'GAMES_WHOLE',
      value: 'GAMES_WHOLE',
      name: '赛事全案',
    },
    // 赛事内容制作
    {
      key: 'DIRECT_BROADCAST_PRODUCTION_PARTLY',
      value: 'DIRECT_BROADCAST_PRODUCTION_PARTLY',
      name: '赛事内容制作',
    },
  ] as const),
  SupplierAdvancePaymentEnum: createEnum([
    // 0-50万
    {
      key: 'FIRST_AP',
      value: 'FIRST_AP',
      name: '0-50万',
    },
    // 50-100万
    {
      key: 'SECOND_AP',
      value: 'SECOND_AP',
      name: '50-100万',
    },
    // 100-500万
    {
      key: 'THIRD_AP',
      value: 'THIRD_AP',
      name: '100-500万',
    },
    // 500万以上
    {
      key: 'FOURTH_AP',
      value: 'FOURTH_AP',
      name: '500万以上',
    },
    // 不明
    {
      key: 'OTHER_AP',
      value: 'OTHER_AP',
      name: '不明',
    },
  ] as const),
  SupplierStatusEnum: createEnum([
    // 审核通过
    {
      key: 'ACTIVE',
      value: 'ACTIVE',
      name: '审核通过',
    },
    // 待审核
    {
      key: 'PENDING',
      value: 'PENDING',
      name: '待审核',
    },
    // 限制使用
    {
      key: 'LOCKED',
      value: 'LOCKED',
      name: '限制使用',
    },
    // 未通过
    {
      key: 'REJECTED',
      value: 'REJECTED',
      name: '未通过',
    },
  ] as const),
  TemporaryEnum: createEnum([
    // 是
    {
      key: 'TRUE',
      value: 'TRUE',
      name: '是',
    },
    // 否
    {
      key: 'FALSE',
      value: 'FALSE',
      name: '否',
    },
  ] as const),
  WhetherFormerEmployeesEnum: createEnum([
    // 是
    {
      key: 'TRUE',
      value: 'TRUE',
      name: '是',
    },
    // 否
    {
      key: 'FALSE',
      value: 'FALSE',
      name: '否',
    },
  ] as const),
  UseTypeEnum: createEnum([
    // 常用类
    {
      key: 'COMMONLY_USED',
      value: 'COMMONLY_USED',
      name: '常用类',
    },
    // 不常用类
    {
      key: 'NOT_COMMONLY_USED',
      value: 'NOT_COMMONLY_USED',
      name: '不常用类',
    },
    // 储备类
    {
      key: 'STORE_USED',
      value: 'STORE_USED',
      name: '储备类',
    },
  ] as const),
  SupplierTypeEnum: createEnum([
    // 国内（综合）
    {
      key: 'COMPLEX_INLAND',
      value: 'COMPLEX_INLAND',
      name: '国内（综合）',
    },
    // 港澳台及海外（综合）
    {
      key: 'COMPLEX_OVERSEA',
      value: 'COMPLEX_OVERSEA',
      name: '港澳台及海外（综合）',
    },
    // 特定资源（综合）
    {
      key: 'COMPLEX_SPECIAL',
      value: 'COMPLEX_SPECIAL',
      name: '特定资源（综合）',
    },
    // 游戏公关媒体（综合）
    {
      key: 'COMPLEX_GAME',
      value: 'COMPLEX_GAME',
      name: '游戏公关媒体（综合）',
    },
    // 生态运营（综合）
    {
      key: 'COMPLEX_OPERATION',
      value: 'COMPLEX_OPERATION',
      name: '生态运营（综合）',
    },
    // 素材（综合）
    {
      key: 'COMPLEX_MATERIAL',
      value: 'COMPLEX_MATERIAL',
      name: '素材（综合）',
    },
    // 其它（综合）
    {
      key: 'COMPLEX_OTHER',
      value: 'COMPLEX_OTHER',
      name: '其它（综合）',
    },
    // 娱乐营销（综合）
    {
      key: 'COMPLEX_SALE',
      value: 'COMPLEX_SALE',
      name: '娱乐营销（综合）',
    },
    // 内容营销（社媒）
    {
      key: 'SOCIAL_MEDIA_SALE',
      value: 'SOCIAL_MEDIA_SALE',
      name: '内容营销（社媒）',
    },
    // 广告投放（媒介）
    {
      key: 'MEDIA_AD',
      value: 'MEDIA_AD',
      name: '广告投放（媒介）',
    },
    // 活动赛事（电竞）
    {
      key: 'ESPORTS_EVENT',
      value: 'ESPORTS_EVENT',
      name: '活动赛事（电竞）',
    },
    // 国内（流量分发）
    {
      key: 'FLOW_INLAND',
      value: 'FLOW_INLAND',
      name: '国内（流量分发）',
    },
    // WEB支持（流量分发）
    {
      key: 'FLOW_WEB_SUPPORT',
      value: 'FLOW_WEB_SUPPORT',
      name: 'WEB支持（流量分发）',
    },
    // 流量运营（流量分发）
    {
      key: 'FLOW_OPERATION',
      value: 'FLOW_OPERATION',
      name: '流量运营（流量分发）',
    },
    // 品牌创意公司（品牌创意）
    {
      key: 'BRAND_COMPANY',
      value: 'BRAND_COMPANY',
      name: '品牌创意公司（品牌创意）',
    },
    // 品牌创意个人（品牌创意）
    {
      key: 'BRAND_PERSON',
      value: 'BRAND_PERSON',
      name: '品牌创意个人（品牌创意）',
    },
    // 海外公关活动（海外）
    {
      key: 'OVERSEA_ACTIVITIES',
      value: 'OVERSEA_ACTIVITIES',
      name: '海外公关活动（海外）',
    },
    // UA素材制作（海外）
    {
      key: 'OVERSEA_UA_MATERIAL',
      value: 'OVERSEA_UA_MATERIAL',
      name: 'UA素材制作（海外）',
    },
    // 本地化翻译（海外）
    {
      key: 'OVERSEA_LOCALIZED',
      value: 'OVERSEA_LOCALIZED',
      name: '本地化翻译（海外）',
    },
    // 海外网站维护（海外）
    {
      key: 'OVERSEA_WEBSITE',
      value: 'OVERSEA_WEBSITE',
      name: '海外网站维护（海外）',
    },
    // UA投放（海外）
    {
      key: 'OVERSEA_UA_LAUNCH',
      value: 'OVERSEA_UA_LAUNCH',
      name: 'UA投放（海外）',
    },
  ] as const),
  RoleStatusEnum: createEnum([
    // 正常
    {
      key: 'ACTIVE',
      value: 'ACTIVE',
      name: '正常',
    },
    // 锁定
    {
      key: 'LOCKED',
      value: 'LOCKED',
      name: '锁定',
    },
  ] as const),
  UserBelongableTypeEnum: createEnum([
    {
      key: 'GROUP',
      value: 'GROUP',
      name: 'unknown',
    },
    {
      key: 'PRODUCT',
      value: 'PRODUCT',
      name: 'unknown',
    },
  ] as const),
  // invoice_type
  BankInfoInvoiceTypeEnum: createEnum([
    // 小规模纳税人（3%）
    {
      key: 'SMALL_SCALE_TAX',
      value: 'SMALL_SCALE_TAX',
      name: '小规模纳税人（3%）',
    },
    // 增值税专用发票（6%）
    {
      key: 'GENERAL_TAX',
      value: 'GENERAL_TAX',
      name: '增值税专用发票（6%）',
    },
    // 其他
    {
      key: 'OTHER_TAX',
      value: 'OTHER_TAX',
      name: '其他',
    },
    // 无
    {
      key: 'NON_TAX',
      value: 'NON_TAX',
      name: '无',
    },
  ] as const),
  SupplierAttacheEnum: createEnum([
    // 审核通过
    {
      key: 'ACTIVE',
      value: 'ACTIVE',
      name: '审核通过',
    },
    // 新增
    {
      key: 'ADDED',
      value: 'ADDED',
      name: '新增',
    },
    // 删除
    {
      key: 'REMOVED',
      value: 'REMOVED',
      name: '删除',
    },
  ] as const),
  ContactTypeEnum: createEnum([
    // 负责人
    {
      key: 'LEADER',
      value: 'LEADER',
      name: '负责人',
    },
    // 对接人
    {
      key: 'INTERFACE',
      value: 'INTERFACE',
      name: '对接人',
    },
  ] as const),
  SupplierCategoryEnum: createEnum([
    // 公关
    {
      key: 'PR',
      value: 'PR',
      name: '公关',
    },
    // 媒介
    {
      key: 'MM',
      value: 'MM',
      name: '媒介',
    },
    // 品牌创意
    {
      key: 'BC',
      value: 'BC',
      name: '品牌创意',
    },
    // 海外（综合）
    {
      key: 'OC',
      value: 'OC',
      name: '海外（综合）',
    },
    // 流量分发
    {
      key: 'FD',
      value: 'FD',
      name: '流量分发',
    },
    // 电竞
    {
      key: 'ES',
      value: 'ES',
      name: '电竞',
    },
  ] as const),
  SupplierListTypeEnum: createEnum([
    // 供应商管理列表
    {
      key: 'ALL_LIST',
      value: 'ALL_LIST',
      name: '供应商管理列表',
    },
    // 审核供应商信息列表
    {
      key: 'AUDIT_LIST',
      value: 'AUDIT_LIST',
      name: '审核供应商信息列表',
    },
  ] as const),
  // 审批类型
  SupplierApprovalTypeEnum: createEnum([
    // 入库
    {
      key: 'INBOUND',
      value: 'INBOUND',
      name: '入库',
    },
    // 信息变更
    {
      key: 'INFO_CHANGE',
      value: 'INFO_CHANGE',
      name: '信息变更',
    },
    // 升级到核心
    {
      key: 'UDG_UP_TO_ANNUL_FRAME',
      value: 'UDG_UP_TO_ANNUL_FRAME',
      name: '升级到核心',
    },
    // 升降级到重要
    {
      key: 'UDG_UP_DOWN_TO_RESERVED',
      value: 'UDG_UP_DOWN_TO_RESERVED',
      name: '升降级到重要',
    },
    // 升降级到储备
    {
      key: 'UDG_UP_DOWN_TO_TEMPORARY',
      value: 'UDG_UP_DOWN_TO_TEMPORARY',
      name: '升降级到储备',
    },
    // 升降级到临时
    {
      key: 'UDG_UP_DOWN_TO_LIN_SHI',
      value: 'UDG_UP_DOWN_TO_LIN_SHI',
      name: '升降级到临时',
    },
    // 降级到限制使用
    {
      key: 'UDG_DOWN_TO_RESTRICTIVE',
      value: 'UDG_DOWN_TO_RESTRICTIVE',
      name: '降级到限制使用',
    },
    // 出库到黑名单
    {
      key: 'UDG_OUTBOUND_TO_BLACKLISTED',
      value: 'UDG_OUTBOUND_TO_BLACKLISTED',
      name: '出库到黑名单',
    },
    // 出库到淘汰供应商
    {
      key: 'UDG_OUTBOUND_TO_ELIMINATED',
      value: 'UDG_OUTBOUND_TO_ELIMINATED',
      name: '出库到淘汰供应商',
    },
    // 出库到其他
    {
      key: 'UDG_OUTBOUND_TO_OTHER',
      value: 'UDG_OUTBOUND_TO_OTHER',
      name: '出库到其他',
    },
  ] as const),
  // 简化审批类型
  SupplierSimplifyApprovalTypeEnum: createEnum([
    // 升降级
    {
      key: 'UP_DOWN',
      value: 'UP_DOWN',
      name: '升降级',
    },
    // 出库
    {
      key: 'OUTBOUND',
      value: 'OUTBOUND',
      name: '出库',
    },
    // 黑名单
    {
      key: 'BLACKLIST',
      value: 'BLACKLIST',
      name: '黑名单',
    },
    // 信息变更
    {
      key: 'INFO_CHANGE',
      value: 'INFO_CHANGE',
      name: '信息变更',
    },
    // 入库
    {
      key: 'INBOUND',
      value: 'INBOUND',
      name: '入库',
    },
  ] as const),
  // 供应商升降级状态类型
  ChannelSupplierTypeChangeTypeEnum: createEnum([
    // 升级
    {
      key: 'UPGRADE',
      value: 1,
      name: '升级',
    },
    // 降级
    {
      key: 'DOWNGRADE',
      value: 2,
      name: '降级',
    },
    // 出库
    {
      key: 'OUTBOUND',
      value: 3,
      name: '出库',
    },
  ] as const),
  // 审核状态
  SupplierApprovalStatusEnum: createEnum([
    // 进行中
    {
      key: 'RUNNING',
      value: 0,
      name: '进行中',
    },
    // 通过
    {
      key: 'PASSED',
      value: 1,
      name: '通过',
    },
    // 拒绝
    {
      key: 'REJECTED',
      value: 2,
      name: '拒绝',
    },
    // 退回
    {
      key: 'RETURNED',
      value: 3,
      name: '退回',
    },
  ] as const),
  SupplierKpiItemTypeEnum: createEnum([
    // 默认规则
    {
      key: 'DEFAULT',
      value: 0,
      name: '默认规则',
    },
    // 自定义规则
    {
      key: 'CUSTOM',
      value: 1,
      name: '自定义规则',
    },
  ] as const),
  // 来源
  SupplierKpiItemRuleDetailSourceEnum: createEnum([
    // 过程评分
    {
      key: 'PROCESSING_SCORE',
      value: 1,
      name: '过程评分',
    },
    // 结果评分
    {
      key: 'RESULT_SCORE',
      value: 2,
      name: '结果评分',
    },
    // 综合评分
    {
      key: 'SCORE',
      value: 3,
      name: '综合评分',
    },
  ] as const),
  // 是否自定义，1=自定义，0=标准
  KpiValueCustomEnum: createEnum([
    // 自定义
    {
      key: 'CUSTOM',
      value: 1,
      name: '自定义',
    },
    // 标准
    {
      key: 'NORMAL',
      value: 0,
      name: '标准',
    },
  ] as const),
  // KPI标签枚举
  DemandKpiLabelEnum: createEnum([
    // CPM超时相关标签
    {
      key: 'OVER_15DAY_CPM',
      value: 'OVER_15DAY_CPM',
      name: 'CPM超时相关标签',
    },
  ] as const),
  // 产品分类全部权限列表（互娱/雷火/音乐/其他）
  ProductSourceEnum: createEnum([
    // 互娱
    {
      key: 'HUYU',
      value: 'HUYU',
      name: '互娱',
    },
    // 雷火
    {
      key: 'LEIHUO',
      value: 'LEIHUO',
      name: '雷火',
    },
    // 音乐
    {
      key: 'MUSIC',
      value: 'MUSIC',
      name: '音乐',
    },
    // 其他
    {
      key: 'OTHER',
      value: 'OTHER',
      name: '其他',
    },
  ] as const),
  ReportFormsTypeEnum: createEnum([
    // 未关单任务数据报表
    {
      key: 'NOT_CLOSED_DEMAND',
      value: 'NOT_CLOSED_DEMAND',
      name: '未关单任务数据报表',
    },
  ] as const),
  // 状态
  PriceLibraryStatusEnum: createEnum([
    // 审批中
    {
      key: 'APPROVAL',
      value: 'APPROVAL',
      name: '审批中',
    },
    // 生效
    {
      key: 'ACTIVE',
      value: 'ACTIVE',
      name: '生效',
    },
    // 已退回
    {
      key: 'REFUSE',
      value: 'REFUSE',
      name: '已退回',
    },
    // 失效
    {
      key: 'INVALID',
      value: 'INVALID',
      name: '失效',
    },
  ] as const),
  // 发票方式：1-有票预付（先票后款），2-无票预付（先款后票）
  InvoiceMethodEnum: createEnum([
    // 有票预付
    {
      key: 'INVOICED',
      value: 'INVOICED',
      name: '有票预付',
    },
    // 无票预付
    {
      key: 'UNINVOICED',
      value: 'UNINVOICED',
      name: '无票预付',
    },
  ] as const),
  // 变更类型
  JournalDetailChangeTypeEnum: createEnum([
    // 已更新
    {
      key: 'JOURNAL_CHANGED_NO_DETAIL',
      value: 'JOURNAL_CHANGED_NO_DETAIL',
      name: '已更新',
    },
    // 从 xxx 变更为 yyy
    {
      key: 'JOURNAL_CHANGED',
      value: 'JOURNAL_CHANGED',
      name: '从 xxx 变更为 yyy',
    },
    // 已删除
    {
      key: 'JOURNAL_DELETED',
      value: 'JOURNAL_DELETED',
      name: '已删除',
    },
    // 设置为 xxx
    {
      key: 'JOURNAL_SET_TO',
      value: 'JOURNAL_SET_TO',
      name: '设置为 xxx',
    },
    // 已创建
    {
      key: 'JOURNAL_CREATED',
      value: 'JOURNAL_CREATED',
      name: '已创建',
    },
  ] as const),
  // 参数类型
  JournalDetailValueArgTypeEnum: createEnum([
    // 金额
    {
      key: 'AMOUNT',
      value: 'AMOUNT',
      name: '金额',
    },
    // 需求单/比选/小喇叭/营销闭环
    {
      key: 'PROJECT',
      value: 'PROJECT',
      name: '需求单/比选/小喇叭/营销闭环',
    },
    // 用户
    {
      key: 'USER',
      value: 'USER',
      name: '用户',
    },
  ] as const),
  // 需求单/小喇叭/比选
  JournalTypeEnum: createEnum([
    {
      key: 'DEMAND',
      value: 'demand',
      name: 'unknown',
    },
    {
      key: 'DEMAND_PLAN_INFO',
      value: 'demand_plan_info',
      name: 'unknown',
    },
    {
      key: 'DEMAND_PRINCIPAL',
      value: 'demand_principal',
      name: 'unknown',
    },
    {
      key: 'DEMAND_PLANNER',
      value: 'demand_planner',
      name: 'unknown',
    },
    {
      key: 'DEMAND_SUPPLIER',
      value: 'demand_supplier',
      name: 'unknown',
    },
    {
      key: 'DEMAND_SOURCE_SUPPLIER_SIGN_KOL',
      value: 'demand_source_supplier_sign_kol',
      name: 'unknown',
    },
    {
      key: 'DEMAND_SUPPLIER_QUOTE',
      value: 'demand_supplier_quote',
      name: 'unknown',
    },
    {
      key: 'DEMAND_SUPPLIER_SCORE',
      value: 'demand_supplier_score',
      name: 'unknown',
    },
    {
      key: 'DEMAND_MEMBER',
      value: 'demand_member',
      name: 'unknown',
    },
    {
      key: 'DEMAND_FEE_CONTROL_RECORD',
      value: 'demand_fee_control_record',
      name: 'unknown',
    },
    {
      key: 'DEMAND_EXPECTATION_KPI_INDICATOR',
      value: 'demand_expectation_kpi_indicator',
      name: 'unknown',
    },
    {
      key: 'DEMAND_PLAN_KPI_VALUE',
      value: 'demand_plan_kpi_value',
      name: 'unknown',
    },
    {
      key: 'DEMAND_ROUNDS',
      value: 'demand_rounds',
      name: 'unknown',
    },
    {
      key: 'BIDDING',
      value: 'bidding',
      name: 'unknown',
    },
    {
      key: 'BIDDING_JUDGE_REVIEW',
      value: 'bidding_judge_review',
      name: 'unknown',
    },
    {
      key: 'BIDDING_MEMBER',
      value: 'bidding_member',
      name: 'unknown',
    },
    {
      key: 'BIDDING_SUPPLIER',
      value: 'bidding_supplier',
      name: 'unknown',
    },
    {
      key: 'BIDDING_SUPPLIER_QUOTE',
      value: 'bidding_supplier_quote',
      name: 'unknown',
    },
    {
      key: 'BIDDING_SUPPLIER_SCORE',
      value: 'bidding_supplier_score',
      name: 'unknown',
    },
    {
      key: 'BIDDING_SUPPLIER_SCORE_GROUP',
      value: 'bidding_supplier_score_group',
      name: 'unknown',
    },
    {
      key: 'BIDDING_CONFIRM_STATUS_RECORD',
      value: 'bidding_confirm_status_record',
      name: 'unknown',
    },
    {
      key: 'TRUMPET_DEMAND',
      value: 'trumpet_demand',
      name: 'unknown',
    },
    {
      key: 'TRUMPET_MEMBER',
      value: 'trumpet_member',
      name: 'unknown',
    },
    {
      key: 'CLOSED_LOOP_DEMAND',
      value: 'closed_loop_demand',
      name: 'unknown',
    },
    {
      key: 'CLOSED_LOOP_MEMBER',
      value: 'closed_loop_member',
      name: 'unknown',
    },
    {
      key: 'DEMAND_KPI_ADJUSTMENT_ORDER',
      value: 'demand_kpi_adjustment_order',
      name: 'unknown',
    },
    {
      key: 'PREPAYMENT',
      value: 'prepayment',
      name: 'unknown',
    },
    {
      key: 'BUDGET_ADJUST_ORDER',
      value: 'budget_adjust_order',
      name: 'unknown',
    },
    {
      key: 'PERMISSION_APPLICATION',
      value: 'permission_application',
      name: 'unknown',
    },
  ] as const),
  // 使用范围
  DemandCategoryScopeEnum: createEnum([
    {
      key: 'ONLY_TRANS',
      value: 'ONLY_TRANS',
      name: 'unknown',
    },
    {
      key: 'NORMAL',
      value: 'NORMAL',
      name: 'unknown',
    },
    {
      key: 'FOR_FILTER',
      value: 'FOR_FILTER',
      name: 'unknown',
    },
  ] as const),
  // 方案审批支持类型
  DemandCategoryPlanApproveEnum: createEnum([
    // 不支持
    {
      key: 'NOT_SUPPORT',
      value: 'NOT_SUPPORT',
      name: '不支持',
    },
    // 能支持
    {
      key: 'CAN_SUPPORT',
      value: 'CAN_SUPPORT',
      name: '能支持',
    },
    // 必须支持
    {
      key: 'MUST_SUPPORT',
      value: 'MUST_SUPPORT',
      name: '必须支持',
    },
  ] as const),
  // 条件不符合后的处理
  RequiredModeEnum: createEnum([
    // 非必填
    {
      key: 'NOT_REQUIRED',
      value: 0,
      name: '非必填',
    },
    // 必填
    {
      key: 'REQUIRED',
      value: 1,
      name: '必填',
    },
    // 条件必填
    {
      key: 'CONDITION',
      value: 2,
      name: '条件必填',
    },
    // 不可填
    {
      key: 'DISABLED',
      value: 3,
      name: '不可填',
    },
  ] as const),
  DemandCooperatePropertyTypeEnum: createEnum([
    // 下拉列表
    {
      key: 'DROPDOWN_LIST',
      value: 'dropdown_list',
      name: '下拉列表',
    },
    // 下拉单选
    {
      key: 'DROPDOWN_RADIO',
      value: 'dropdown_radio',
      name: '下拉单选',
    },
    // 数值
    {
      key: 'NUMERIC',
      value: 'numeric',
      name: '数值',
    },
    // 日期(年月日)
    {
      key: 'DATE_INPUT',
      value: 'date_input',
      name: '日期(年月日)',
    },
    // 日期范围(年月日)
    {
      key: 'DATE_RANGE',
      value: 'date_range',
      name: '日期范围(年月日)',
    },
    // 长文本
    {
      key: 'TEXT',
      value: 'text',
      name: '长文本',
    },
    // 长文本+附件
    {
      key: 'TEXT_WITH_UPLOAD',
      value: 'text_with_upload',
      name: '长文本+附件',
    },
    // 短文本
    {
      key: 'INPUT',
      value: 'input',
      name: '短文本',
    },
    // 百分比
    {
      key: 'PERCENTAGE',
      value: 'percentage',
      name: '百分比',
    },
  ] as const),
  // 标品级别
  StandardCatalogLevelEnum: createEnum([
    // 一级（类别）
    {
      key: 'LEVEL_1',
      value: 1,
      name: '一级（类别）',
    },
    // 二级（合作项目）
    {
      key: 'LEVEL_2',
      value: 2,
      name: '二级（合作项目）',
    },
    // 三级（报价项目）
    {
      key: 'LEVEL_3',
      value: 3,
      name: '三级（报价项目）',
    },
  ] as const),
  // 考核指标-操作类型
  DiffTypeEnum: createEnum([
    // 无调整
    {
      key: 'SAME',
      value: 'SAME',
      name: '无调整',
    },
    // 新增
    {
      key: 'ADD',
      value: 'ADD',
      name: '新增',
    },
    // 删除
    {
      key: 'DELETE',
      value: 'DELETE',
      name: '删除',
    },
    // 调整
    {
      key: 'MODIFY',
      value: 'MODIFY',
      name: '调整',
    },
  ] as const),
  FlowFlagEnum: createEnum([
    // 历史老数据的处理流程
    {
      key: 'OLD_ONLINE_PROCESS',
      value: 'OLD_ONLINE_PROCESS',
      name: '历史老数据的处理流程',
    },
    // 第一级需求单未确定采购方式
    {
      key: 'NO_SELECT',
      value: 'NO_SELECT',
      name: '第一级需求单未确定采购方式',
    },
    // 绑定执行数据特有流程(无需求方的审核)
    {
      key: 'BIND_EXECUTE_FLOW',
      value: 'BIND_EXECUTE_FLOW',
      name: '绑定执行数据特有流程(无需求方的审核)',
    },
    // 创建前有定制审核的流程(需求方选择了要前置审批)
    {
      key: 'PRE_AFTER_AUDIT',
      value: 'PRE_AFTER_AUDIT',
      name: '创建前有定制审核的流程(需求方选择了要前置审批)',
    },
    // 嵌套采购流程的MPC的流程
    {
      key: 'NORMAL_BIDDING_MIX_MPC',
      value: 'NORMAL_BIDDING_MIX_MPC',
      name: '嵌套采购流程的MPC的流程',
    },
    // 带后置OA审批的常规MPC流程
    {
      key: 'NORMAL_MPC_OA',
      value: 'NORMAL_MPC_OA',
      name: '带后置OA审批的常规MPC流程',
    },
    // 现有MPC流程
    {
      key: 'NORMAL_MPC',
      value: 'NORMAL_MPC',
      name: '现有MPC流程',
    },
    // 方案综合评分法
    {
      key: 'MULTI_QUOTA_PLAN_DEMAND',
      value: 'MULTI_QUOTA_PLAN_DEMAND',
      name: '方案综合评分法',
    },
  ] as const),
  // 产品区域
  ProductAreaEnum: createEnum([
    // 国内
    {
      key: 'DOMESTIC',
      value: 'DOMESTIC',
      name: '国内',
    },
    // 海外
    {
      key: 'OVERSEAS',
      value: 'OVERSEAS',
      name: '海外',
    },
  ] as const),
  // platformPrices
  PlatformPriceTypeEnum: createEnum([
    {
      key: 'NONE',
      value: 'NONE',
      name: 'unknown',
    },
    {
      key: 'COOPERATE_ACCOUNT',
      value: 'COOPERATE_ACCOUNT',
      name: 'unknown',
    },
    {
      key: 'PLATFORM_AND_OFFICIAL_PRIVATE',
      value: 'PLATFORM_AND_OFFICIAL_PRIVATE',
      name: 'unknown',
    },
  ] as const),
  // 梯度 (T1/T2/T3/T4)
  GradientEnum: createEnum([
    // T1梯度
    {
      key: 'T1',
      value: 'T1',
      name: 'T1梯度',
    },
    // T2梯度
    {
      key: 'T2',
      value: 'T2',
      name: 'T2梯度',
    },
    // T3梯度
    {
      key: 'T3',
      value: 'T3',
      name: 'T3梯度',
    },
    // T4梯度
    {
      key: 'T4',
      value: 'T4',
      name: 'T4梯度',
    },
    // T5梯度
    {
      key: 'T5',
      value: 'T5',
      name: 'T5梯度',
    },
    // T6梯度
    {
      key: 'T6',
      value: 'T6',
      name: 'T6梯度',
    },
    // T7梯度
    {
      key: 'T7',
      value: 'T7',
      name: 'T7梯度',
    },
    // T8梯度
    {
      key: 'T8',
      value: 'T8',
      name: 'T8梯度',
    },
    // T9梯度
    {
      key: 'T9',
      value: 'T9',
      name: 'T9梯度',
    },
    // T10梯度
    {
      key: 'T10',
      value: 'T10',
      name: 'T10梯度',
    },
    // T11梯度
    {
      key: 'T11',
      value: 'T11',
      name: 'T11梯度',
    },
    // T12梯度
    {
      key: 'T12',
      value: 'T12',
      name: 'T12梯度',
    },
    // T13梯度
    {
      key: 'T13',
      value: 'T13',
      name: 'T13梯度',
    },
    // T14梯度
    {
      key: 'T14',
      value: 'T14',
      name: 'T14梯度',
    },
    // T15梯度
    {
      key: 'T15',
      value: 'T15',
      name: 'T15梯度',
    },
  ] as const),
  // 合同来源，前端目前无需关注，仅后端数据层处理依赖
  ContractSourceTypeEnum: createEnum([
    // 复用合同
    {
      key: 'REUSE',
      value: 'REUSE',
      name: '复用合同',
    },
    // 新建合同
    {
      key: 'NEW_CREATE',
      value: 'NEW_CREATE',
      name: '新建合同',
    },
  ] as const),
  // 合同签约类型，前端目前无需关注，仅后端数据层处理依赖
  ContractSignTypeEnum: createEnum([
    // 自动生成合同
    {
      key: 'ONLINE',
      value: 'online',
      name: '自动生成合同',
    },
    // 人工上传合同
    {
      key: 'OFFLINE',
      value: 'offline',
      name: '人工上传合同',
    },
  ] as const),
  // 参与方, 策划/商务/需求方, 一次提交仅支持一方
  ParticipantTypeEnum: createEnum([
    // 策划
    {
      key: 'PLANNER',
      value: 'planner',
      name: '策划',
    },
    // 商务
    {
      key: 'BUSINESS',
      value: 'business',
      name: '商务',
    },
    // 需求方
    {
      key: 'DEMANDER',
      value: 'demander',
      name: '需求方',
    },
  ] as const),
  // 发票类型
  InvoiceTypeEnum: createEnum([
    {
      key: 'INVOICE_10100',
      value: 'INVOICE_10100',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10100A',
      value: 'INVOICE_10100A',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10101',
      value: 'INVOICE_10101',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10102',
      value: 'INVOICE_10102',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10102A',
      value: 'INVOICE_10102A',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10102B',
      value: 'INVOICE_10102B',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10103',
      value: 'INVOICE_10103',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10104',
      value: 'INVOICE_10104',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10400',
      value: 'INVOICE_10400',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10400A',
      value: 'INVOICE_10400A',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10506',
      value: 'INVOICE_10506',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10503',
      value: 'INVOICE_10503',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10505',
      value: 'INVOICE_10505',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10505A',
      value: 'INVOICE_10505A',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10500',
      value: 'INVOICE_10500',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10200',
      value: 'INVOICE_10200',
      name: 'unknown',
    },
    {
      key: 'INVOICE_20105',
      value: 'INVOICE_20105',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10107',
      value: 'INVOICE_10107',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10108',
      value: 'INVOICE_10108',
      name: 'unknown',
    },
    {
      key: 'INVOICE_99999',
      value: 'INVOICE_99999',
      name: 'unknown',
    },
    {
      key: 'INVOICE_90001',
      value: 'INVOICE_90001',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10090',
      value: 'INVOICE_10090',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10091',
      value: 'INVOICE_10091',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10700',
      value: 'INVOICE_10700',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10600',
      value: 'INVOICE_10600',
      name: 'unknown',
    },
    {
      key: 'INVOICE_10800',
      value: 'INVOICE_10800',
      name: 'unknown',
    },
    {
      key: 'INVOICE_VIRTUAL',
      value: 'INVOICE_VIRTUAL',
      name: 'unknown',
    },
  ] as const),
  // 发票国家/地区
  InvoiceBelongAreaEnum: createEnum([
    // 中国大陆
    {
      key: 'MAINLAND_CHINA',
      value: 1,
      name: '中国大陆',
    },
    // 澳大利亚及新西兰
    {
      key: 'ANZ',
      value: 3,
      name: '澳大利亚及新西兰',
    },
    // 加拿大
    {
      key: 'CA',
      value: 4,
      name: '加拿大',
    },
    // 西班牙
    {
      key: 'ES',
      value: 5,
      name: '西班牙',
    },
    // 欧洲
    {
      key: 'EU',
      value: 6,
      name: '欧洲',
    },
    // 日本
    {
      key: 'JP',
      value: 7,
      name: '日本',
    },
    // 韩国
    {
      key: 'KR',
      value: 8,
      name: '韩国',
    },
    // 美国
    {
      key: 'US',
      value: 9,
      name: '美国',
    },
    // 除以上地区以外
    {
      key: 'COMMON',
      value: 99,
      name: '除以上地区以外',
    },
  ] as const),
  // 需求单关联合同的方式
  DemandContractMethodEnum: createEnum([
    // 无需合同
    {
      key: 'NO_NEED',
      value: 'NO_NEED',
      name: '无需合同',
    },
    // 复用合同
    {
      key: 'REUSE',
      value: 'REUSE',
      name: '复用合同',
    },
    // 新建合同-线上签署
    {
      key: 'NEW_CREATE_ONLINE',
      value: 'NEW_CREATE_ONLINE',
      name: '新建合同-线上签署',
    },
    // 新建合同-手动签署
    {
      key: 'NEW_CREATE_MANUAL',
      value: 'NEW_CREATE_MANUAL',
      name: '新建合同-手动签署',
    },
  ] as const),
  // 供应商侧状态
  MatchOutsourceStatusEnum: createEnum([
    {
      key: 'NEWLY_BUILT',
      value: 'NEWLY_BUILT',
      name: 'unknown',
    },
    {
      key: 'PENDING_QUOTE',
      value: 'PENDING_QUOTE',
      name: 'unknown',
    },
    {
      key: 'NOT_QUOTED',
      value: 'NOT_QUOTED',
      name: 'unknown',
    },
    {
      key: 'CONFIRM_QUOTE',
      value: 'CONFIRM_QUOTE',
      name: 'unknown',
    },
    {
      key: 'NOT_SELECTED',
      value: 'NOT_SELECTED',
      name: 'unknown',
    },
    {
      key: 'SELECTED',
      value: 'SELECTED',
      name: 'unknown',
    },
    {
      key: 'MAKING',
      value: 'MAKING',
      name: 'unknown',
    },
    {
      key: 'REVIEW_QUOTE',
      value: 'REVIEW_QUOTE',
      name: 'unknown',
    },
    {
      key: 'SETTLED',
      value: 'SETTLED',
      name: 'unknown',
    },
    {
      key: 'SETTLED_UPLOAD',
      value: 'SETTLED_UPLOAD',
      name: 'unknown',
    },
    {
      key: 'PAYING',
      value: 'PAYING',
      name: 'unknown',
    },
    {
      key: 'PAYED',
      value: 'PAYED',
      name: 'unknown',
    },
    {
      key: 'CANCELED',
      value: 'CANCELED',
      name: 'unknown',
    },
    {
      key: 'MY_ADVANCE',
      value: 'MY_ADVANCE',
      name: 'unknown',
    },
    {
      key: 'REJECT',
      value: 'REJECT',
      name: 'unknown',
    },
    {
      key: 'COMPLETED',
      value: 'COMPLETED',
      name: 'unknown',
    },
  ] as const),
  MediaKpiTypeEnum: createEnum([
    // 无类型
    {
      key: 'UNKNOWN_TYPE',
      value: 0,
      name: '无类型',
    },
    // 订单达标
    {
      key: 'ORDER_KPI_TYPE',
      value: 1,
      name: '订单达标',
    },
    // 媒体达标
    {
      key: 'MEDIA_KPI_TYPE',
      value: 2,
      name: '媒体达标',
    },
    // 整体达标
    {
      key: 'OVERALL_KPI_TYPE',
      value: 3,
      name: '整体达标',
    },
  ] as const),
  // 指标类型
  DemandQuotaTypeEnum: createEnum([
    // 主要指标
    {
      key: 'MAIN',
      value: 'MAIN',
      name: '主要指标',
    },
    // 其他指标
    {
      key: 'OTHER',
      value: 'OTHER',
      name: '其他指标',
    },
    // 其他指标
    {
      key: 'COMMON',
      value: 'COMMON',
      name: '其他指标',
    },
    // 其他指标
    {
      key: 'COOPERATE',
      value: 'COOPERATE',
      name: '其他指标',
    },
    // 辅助指标
    {
      key: 'AUXILIARY',
      value: 'AUXILIARY',
      name: '辅助指标',
    },
  ] as const),
  // QC验收优先级：0=老流程(NOT_SET)，1=新流程(PRIORITY)
  QcAcceptPriorityEnum: createEnum([
    {
      key: 'NOT_SET',
      value: 0,
      name: 'unknown',
    },
    {
      key: 'PRIORITY',
      value: 1,
      name: 'unknown',
    },
  ] as const),
  // NO 不支持kol，YES 支持kol
  KolSupportTypeEnum: createEnum([
    {
      key: 'NO',
      value: 'NO',
      name: 'unknown',
    },
    {
      key: 'YES',
      value: 'YES',
      name: 'unknown',
    },
  ] as const),
  // 续约单类型
  RenewTypeEnum: createEnum([
    // 非续约单
    {
      key: 'NONE',
      value: 'NONE',
      name: '非续约单',
    },
    // 续约常规采购单
    {
      key: 'NORMAL',
      value: 'NORMAL',
      name: '续约常规采购单',
    },
    // 续约小喇叭运营单
    {
      key: 'TRUMPET',
      value: 'TRUMPET',
      name: '续约小喇叭运营单',
    },
  ] as const),
  // 退回状态枚举
  AuditQuoteReturnStatusEnum: createEnum([
    // OA退回
    {
      key: 'OA_REJECTED',
      value: 'OA_REJECTED',
      name: 'OA退回',
    },
  ] as const),
  // 关联业务类型（如：小喇叭、比选）
  DemandRelateBizTypeEnum: createEnum([
    // 小喇叭
    {
      key: 'TRUMPET',
      value: 'TRUMPET',
      name: '小喇叭',
    },
    // 比选
    {
      key: 'BIDDING',
      value: 'BIDDING',
      name: '比选',
    },
  ] as const),
  // 报价明细上传方式
  DemandQuoteUploadMethodEnum: createEnum([
    // 单个上传
    {
      key: 'SINGLE_UPLOAD',
      value: 'SINGLE_UPLOAD',
      name: '单个上传',
    },
    // 合并上传
    {
      key: 'MULTI_UPLOAD',
      value: 'MULTI_UPLOAD',
      name: '合并上传',
    },
  ] as const),
  // 能否创建 KPI调整单的状态
  DemandKpiCreateStateEnum: createEnum([
    {
      key: 'CAN_CREATE',
      value: 'CAN_CREATE',
      name: 'unknown',
    },
    {
      key: 'OA_PROCESSING',
      value: 'OA_PROCESSING',
      name: 'unknown',
    },
    {
      key: 'CAN_NOT_CREATE',
      value: 'CAN_NOT_CREATE',
      name: 'unknown',
    },
  ] as const),
  // 需求分类模板类型
  DemandCategoryPropertyTemplateTypeEnum: createEnum([
    // 未初始化
    {
      key: 'UNINITIALIZED',
      value: 0,
      name: '未初始化',
    },
    // KOL类
    {
      key: 'KOL',
      value: 1,
      name: 'KOL类',
    },
    // 非KOL类
    {
      key: 'NON_KOL',
      value: 2,
      name: '非KOL类',
    },
  ] as const),
  // 付款状态
  FeeControlDocStatusEnum: createEnum([
    // 付款中
    {
      key: 'SUBMITTED',
      value: 'SUBMITTED',
      name: '付款中',
    },
    // 已付款
    {
      key: 'ARCHIVED',
      value: 'ARCHIVED',
      name: '已付款',
    },
    // 付款单已删除
    {
      key: 'DELETED',
      value: 'DELETED',
      name: '付款单已删除',
    },
    // 进行中
    {
      key: 'PROCESSING',
      value: 'PROCESSING',
      name: '进行中',
    },
    // 已完成
    {
      key: 'COMPLETE',
      value: 'COMPLETE',
      name: '已完成',
    },
    // 已拒绝
    {
      key: 'REJECTED',
      value: 'REJECTED',
      name: '已拒绝',
    },
    // 已终止
    {
      key: 'TERMINATED',
      value: 'TERMINATED',
      name: '已终止',
    },
  ] as const),
  // 周期聚合-风险等级：HIGH-高风险, MEDIUM-中风险, LOW-正常
  AlertLevelEnum: createEnum([
    // 高风险
    {
      key: 'HIGH',
      value: 'HIGH',
      name: '高风险',
    },
    // 中风险
    {
      key: 'MEDIUM',
      value: 'MEDIUM',
      name: '中风险',
    },
    // 正常
    {
      key: 'LOW',
      value: 'LOW',
      name: '正常',
    },
  ] as const),
  // 数据类型：PLAN_DEMAND-二级单明细
  BudgetDataTypeEnum: createEnum([
    // 二级单明细
    {
      key: 'PLAN_DEMAND',
      value: 'PLAN_DEMAND',
      name: '二级单明细',
    },
  ] as const),
  // 合同系统流程状态
  SolarContractStatusEnum: createEnum([
    {
      key: 'DRAFT',
      value: 'DRAFT',
      name: 'unknown',
    },
    {
      key: 'APPROVING',
      value: 'APPROVING',
      name: 'unknown',
    },
    {
      key: 'FEE_SIGN_APPROVED',
      value: 'FEE_SIGN_APPROVED',
      name: 'unknown',
    },
    {
      key: 'EFFECTIVE',
      value: 'EFFECTIVE',
      name: 'unknown',
    },
    {
      key: 'EXPIRED',
      value: 'EXPIRED',
      name: 'unknown',
    },
    {
      key: 'CANCELLED',
      value: 'CANCELLED',
      name: 'unknown',
    },
  ] as const),
  // 策划预筛选类型
  PlanSuggestionStateEnum: createEnum([
    // 强推
    {
      key: 'HIGHLY_RECOMMENDATION',
      value: 3,
      name: '强推',
    },
    // 建议
    {
      key: 'RECOMMENDATION',
      value: 2,
      name: '建议',
    },
    // 不推荐
    {
      key: 'NOT_RECOMMENDATION',
      value: 1,
      name: '不推荐',
    },
  ] as const),
  // 达标结果/预期结果
  DemandKpiAchievementStatusEnum: createEnum([
    // 达预期
    {
      key: 'PASS',
      value: 'pass',
      name: '达预期',
    },
    // 部分达预期
    {
      key: 'PART_PASS',
      value: 'part_pass',
      name: '部分达预期',
    },
    // 未达预期
    {
      key: 'NO_PASS',
      value: 'no_pass',
      name: '未达预期',
    },
    // 无法判断
    {
      key: 'UNKNOWN',
      value: 'unknown',
      name: '无法判断',
    },
  ] as const),
  // 退回状态
  KpiAdjustmentReturnStateEnum: createEnum([
    // 策划退回
    {
      key: 'PLANNER_RETURN',
      value: 'PLANNER_RETURN',
      name: '策划退回',
    },
    // OA退回
    {
      key: 'OA_RETURN',
      value: 'OA_RETURN',
      name: 'OA退回',
    },
  ] as const),
  IsAssociateExecutionDataEnum: createEnum([
    // 否
    {
      key: 'FALSE',
      value: 'FALSE',
      name: '否',
    },
    // 是
    {
      key: 'TRUE',
      value: 'TRUE',
      name: '是',
    },
  ] as const),
  // 发票状态
  InvoiceStateEnum: createEnum([
    // 已使用
    {
      key: 'USED',
      value: 'USED',
      name: '已使用',
    },
    // 已释放
    {
      key: 'RELEASED',
      value: 'RELEASED',
      name: '已释放',
    },
  ] as const),
  // 权限有效期
  EffectiveDurationEnum: createEnum([
    // 7天
    {
      key: 'DAYS_7',
      value: 'DAYS_7',
      name: '7天',
    },
    // 30天
    {
      key: 'DAYS_30',
      value: 'DAYS_30',
      name: '30天',
    },
    // 180天
    {
      key: 'DAYS_180',
      value: 'DAYS_180',
      name: '180天',
    },
    // 360天
    {
      key: 'DAYS_360',
      value: 'DAYS_360',
      name: '360天',
    },
    // 永久
    {
      key: 'PERMANENT',
      value: 'PERMANENT',
      name: '永久',
    },
  ] as const),
  DemandMoveCheckFailTypeEnum: createEnum([
    // 预算超出
    {
      key: 'BUDGET_OVER',
      value: 'BUDGET_OVER',
      name: '预算超出',
    },
    // 综合评分法供应商预算超出
    {
      key: 'MULTI_QUOTE_BUDGET_OVER',
      value: 'MULTI_QUOTE_BUDGET_OVER',
      name: '综合评分法供应商预算超出',
    },
    // 有在途的预付流程
    {
      key: 'PREPAYMENT_IN_PROGRESS',
      value: 'PREPAYMENT_IN_PROGRESS',
      name: '有在途的预付流程',
    },
    // 有已完成的预付单需确认
    {
      key: 'PREPAYMENT_COMPLETED_CONFIRM',
      value: 'PREPAYMENT_COMPLETED_CONFIRM',
      name: '有已完成的预付单需确认',
    },
  ] as const),
  DemandOperateSourceEnum: createEnum([
    // 默认
    {
      key: 'DEFAULT',
      value: 'DEFAULT',
      name: '默认',
    },
    // 内容营销
    {
      key: 'CONTENT_MARKETING',
      value: 'CONTENT_MARKETING',
      name: '内容营销',
    },
  ] as const),
  // 一询单状态
  DemandSourceStateEnum: createEnum([
    {
      key: 'PENDING_DESIGN',
      value: 'PENDING_DESIGN',
      name: 'unknown',
    },
    {
      key: 'PENDING_ASSESS',
      value: 'PENDING_ASSESS',
      name: 'unknown',
    },
    {
      key: 'PLANNER_AUDIT_QUOTE',
      value: 'PLANNER_AUDIT_QUOTE',
      name: 'unknown',
    },
    {
      key: 'MARKETING_AUDIT_QUOTE',
      value: 'MARKETING_AUDIT_QUOTE',
      name: 'unknown',
    },
    {
      key: 'PENDING_QUOTE',
      value: 'PENDING_QUOTE',
      name: 'unknown',
    },
    {
      key: 'CLOSED',
      value: 'CLOSED',
      name: 'unknown',
    },
    {
      key: 'ABANDON',
      value: 'ABANDON',
      name: 'unknown',
    },
  ] as const),
  AdSettleWayEnum: createEnum([
    // 一口价结算
    {
      key: 'BUYOUT_PRICE',
      value: 'BUYOUT_PRICE',
      name: '一口价结算',
    },
    // 按消耗结算
    {
      key: 'BY_CONSUME',
      value: 'BY_CONSUME',
      name: '按消耗结算',
    },
  ] as const),
  QuoteUnitTypeEnum: createEnum([
    // 单数量
    {
      key: 'SINGLE',
      value: 0,
      name: '单数量',
    },
    // 多数量
    {
      key: 'MULTI',
      value: 1,
      name: '多数量',
    },
    // 无数量
    {
      key: 'NONE',
      value: 2,
      name: '无数量',
    },
  ] as const),
  DemandSourceApprovalBizFormTypeEnum: createEnum([
    {
      key: 'PLAN_DESIGN',
      value: 1,
      name: 'unknown',
    },
    {
      key: 'BUSINESS_ASSESS',
      value: 2,
      name: 'unknown',
    },
    {
      key: 'SUPPLIER_QUOTE',
      value: 3,
      name: 'unknown',
    },
    {
      key: 'PLAN_ASSESS',
      value: 4,
      name: 'unknown',
    },
    {
      key: 'PRINCIPAL_AUDIT',
      value: 5,
      name: 'unknown',
    },
    {
      key: 'BUSINESS_REQUOTE',
      value: 6,
      name: 'unknown',
    },
    {
      key: 'ABANDON',
      value: 7,
      name: 'unknown',
    },
  ] as const),
  // 默认采购方式
  PendingAssessPurchasingMethodEnum: createEnum([
    // 询比价
    {
      key: 'S_INQUIRY',
      value: 'S_INQUIRY',
      name: '询比价',
    },
    // 引用框架协议
    {
      key: 'S_FRAMEWORK',
      value: 'S_FRAMEWORK',
      name: '引用框架协议',
    },
    // 直接采购
    {
      key: 'S_IMMEDIATELY_BIDDING',
      value: 'S_IMMEDIATELY_BIDDING',
      name: '直接采购',
    },
    // 单一来源
    {
      key: 'S_ONLY_ONE_SOURCE',
      value: 'S_ONLY_ONE_SOURCE',
      name: '单一来源',
    },
    // 指定采购
    {
      key: 'S_DIRECTIONAL_BIDDING',
      value: 'S_DIRECTIONAL_BIDDING',
      name: '指定采购',
    },
    // 特殊事项报备
    {
      key: 'S_SPECIAL_MATTER_BIDDING',
      value: 'S_SPECIAL_MATTER_BIDDING',
      name: '特殊事项报备',
    },
    // 已比选
    {
      key: 'S_BIDDING_FINISHED',
      value: 'S_BIDDING_FINISHED',
      name: '已比选',
    },
    // 引用框架协议(已比选)
    {
      key: 'S_FRAMEWORK_BIDDING_FINISHED',
      value: 'S_FRAMEWORK_BIDDING_FINISHED',
      name: '引用框架协议(已比选)',
    },
    // 紧急需求补单
    {
      key: 'S_URGENT_ORDER_RESTOCKING',
      value: 'S_URGENT_ORDER_RESTOCKING',
      name: '紧急需求补单',
    },
    // 续签采购
    {
      key: 'S_RENEW',
      value: 'S_RENEW',
      name: '续签采购',
    },
  ] as const),
  // 级别
  RightsCategoryLevelEnum: createEnum([
    // 一级
    {
      key: 'LEVEL_1',
      value: 1,
      name: '一级',
    },
    // 二级
    {
      key: 'LEVEL_2',
      value: 2,
      name: '二级',
    },
  ] as const),
  // 分摊类型
  AmountAverageTypeEnum: createEnum([
    // 默认规则
    {
      key: 'DEFAULT',
      value: 0,
      name: '默认规则',
    },
    // 自定义
    {
      key: 'CUSTOM',
      value: 1,
      name: '自定义',
    },
  ] as const),
  AmountAverageDimensionEnum: createEnum([
    // 三级单维度
    {
      key: 'LEAVES_DEMAND',
      value: 0,
      name: '三级单维度',
    },
    // 合作内容维度
    {
      key: 'COOPERATE_ACCOUNT',
      value: 1,
      name: '合作内容维度',
    },
  ] as const),
  AmountAveragePeriodValueTypeEnum: createEnum([
    // 二级单执行时间
    {
      key: 'DEMAND_EXECUTION_TIME',
      value: 0,
      name: '二级单执行时间',
    },
    // 合作内容字段
    {
      key: 'COOPERATE_ACCOUNT_FIELD',
      value: 1,
      name: '合作内容字段',
    },
  ] as const),
  // 报价内容模板类型：0=模板1, 1=模板2
  QuoteTempTypeEnum: createEnum([
    // 模板1
    {
      key: 'TEMPLATE1',
      value: 0,
      name: '模板1',
    },
    // 模板2
    {
      key: 'TEMPLATE2',
      value: 1,
      name: '模板2',
    },
  ] as const),
  // 报价明细上传方式（1-单个上传；2-合并上传）
  QuoteUploadMethodEnum: createEnum([
    // 单个上传
    {
      key: 'SINGLE_UPLOAD',
      value: 1,
      name: '单个上传',
    },
    // 合并上传
    {
      key: 'MULTI_UPLOAD',
      value: 2,
      name: '合并上传',
    },
  ] as const),
  // 核价规则类型
  PriceCheckTypeEnum: createEnum([
    {
      key: 'PERCENT',
      value: 'percent',
      name: 'unknown',
    },
    {
      key: 'FORMULA',
      value: 'formula',
      name: 'unknown',
    },
  ] as const),
  SolarCtProcessStatusEnum: createEnum([
    {
      key: 'DRAFT',
      value: 'DRAFT',
      name: 'unknown',
    },
    {
      key: 'AUDITING',
      value: 'AUDITING',
      name: 'unknown',
    },
    {
      key: 'ARCHIVED',
      value: 'ARCHIVED',
      name: 'unknown',
    },
    {
      key: 'DELETED',
      value: 'DELETED',
      name: 'unknown',
    },
    {
      key: 'TERMINATED',
      value: 'TERMINATED',
      name: 'unknown',
    },
  ] as const),
  // 标签类型
  TagTypeEnum: createEnum([
    // 风险名单
    {
      key: 'BLOCKLIST',
      value: 'BLOCKLIST',
      name: '风险名单',
    },
    // 企业风险
    {
      key: 'ENTERPRISE_RISK',
      value: 'ENTERPRISE_RISK',
      name: '企业风险',
    },
    // 企业基本信息
    {
      key: 'ENTERPRISE_BASIC',
      value: 'ENTERPRISE_BASIC',
      name: '企业基本信息',
    },
    // 历史合作
    {
      key: 'HISTORY_CORPORATION',
      value: 'HISTORY_CORPORATION',
      name: '历史合作',
    },
  ] as const),
  // 标签级别
  TagLevelEnum: createEnum([
    // 限制合作
    {
      key: 'LIMIT_COOPERATION',
      value: 'LIMIT_COOPERATION',
      name: '限制合作',
    },
    // 高风险
    {
      key: 'HIGH',
      value: 'HIGH',
      name: '高风险',
    },
    // 中风险
    {
      key: 'MEDIUM',
      value: 'MEDIUM',
      name: '中风险',
    },
    // 低风险
    {
      key: 'LOW',
      value: 'LOW',
      name: '低风险',
    },
    // 其他
    {
      key: 'OTHER',
      value: 'OTHER',
      name: '其他',
    },
  ] as const),
  // 模型类型
  ModelTypeEnum: createEnum([
    // 常规采购
    {
      key: 'DEMAND',
      value: 'DEMAND',
      name: '常规采购',
    },
    // 比选
    {
      key: 'BIDDING',
      value: 'BIDDING',
      name: '比选',
    },
    // 小喇叭
    {
      key: 'TRUMPET',
      value: 'TRUMPET',
      name: '小喇叭',
    },
  ] as const),
  // 提示类型
  TipTypeEnum: createEnum([
    // 问题悬浮提示
    {
      key: 'QUESTION_HOVER',
      value: 'QUESTION_HOVER',
      name: '问题悬浮提示',
    },
    // 输入框内部暗提示
    {
      key: 'INPUT_PLACEHOLDER',
      value: 'INPUT_PLACEHOLDER',
      name: '输入框内部暗提示',
    },
    // 输入框底部提示
    {
      key: 'INPUT_BOTTOM_PROMPT',
      value: 'INPUT_BOTTOM_PROMPT',
      name: '输入框底部提示',
    },
  ] as const),
  // 比选页面传递
  BiddingParticipantTypeEnum: createEnum([
    // 需求方
    {
      key: 'DEMAND',
      value: 'demand',
      name: '需求方',
    },
    // 职能策划
    {
      key: 'PLAN',
      value: 'plan',
      name: '职能策划',
    },
    // 采购方
    {
      key: 'PURCHASE',
      value: 'purchase',
      name: '采购方',
    },
  ] as const),
  // 上传来源
  FileUploadSourceEnum: createEnum([
    // 渠道供应商 - 供应商
    {
      key: 'CS_MAIN_SUPPLIER',
      value: '0',
      name: '渠道供应商 - 供应商',
    },
    // 渠道供应商 - 数据导入
    {
      key: 'CS_DATA_IMPORT',
      value: '1',
      name: '渠道供应商 - 数据导入',
    },
  ] as const),
  // 审批时的状态
  ClosedLoopDemandStateEnum: createEnum([
    // 新建
    {
      key: 'NEWLY_BUILT',
      value: 'newly_built',
      name: '新建',
    },
    // 需求方负责人审批
    {
      key: 'DEMAND_PRINCIPAL_APPROVAL',
      value: 'demand_principal_approval',
      name: '需求方负责人审批',
    },
    // 方案审批
    {
      key: 'PLAN_APPROVAL',
      value: 'plan_approval',
      name: '方案审批',
    },
    // 活动设计
    {
      key: 'ACTIVITY_DESIGN',
      value: 'activity_design',
      name: '活动设计',
    },
    // 执行中
    {
      key: 'IN_PROGRESS',
      value: 'in_progress',
      name: '执行中',
    },
    // 已完成
    {
      key: 'FINISHED',
      value: 'finished',
      name: '已完成',
    },
    // 已废弃
    {
      key: 'CLOSED',
      value: 'closed',
      name: '已废弃',
    },
  ] as const),
  ClosedLoopApprovalBizFormTypeEnum: createEnum([
    {
      key: 'ACTIVITY_DESIGN',
      value: 1,
      name: 'unknown',
    },
    {
      key: 'SUBMIT_CASE_CLOSURE',
      value: 2,
      name: 'unknown',
    },
    {
      key: 'APPROVE_SETTLE_PASS',
      value: 3,
      name: 'unknown',
    },
    {
      key: 'APPROVE_SETTLE_REJECT',
      value: 4,
      name: 'unknown',
    },
  ] as const),
  // 审批时的状态
  ClosedLoopCaseStateEnum: createEnum([
    {
      key: 'SUBMIT_CASE_CLOSURE',
      value: 'submit_case_closure',
      name: 'unknown',
    },
    {
      key: 'APPROVE_SETTLE',
      value: 'approve_settle',
      name: 'unknown',
    },
    {
      key: 'SUBMIT_OA',
      value: 'submit_oa',
      name: 'unknown',
    },
    {
      key: 'CONFIRM_VERIFICATION',
      value: 'confirm_verification',
      name: 'unknown',
    },
    {
      key: 'FINISHED',
      value: 'finished',
      name: 'unknown',
    },
    {
      key: 'CLOSED',
      value: 'closed',
      name: 'unknown',
    },
  ] as const),
  // 流程状态
  BudgetAdjustStateEnum: createEnum([
    // 待发起
    {
      key: 'PENDING',
      value: 'PENDING',
      name: '待发起',
    },
    // 审批中
    {
      key: 'APPROVING',
      value: 'APPROVING',
      name: '审批中',
    },
    // 已生效
    {
      key: 'EFFECTIVE',
      value: 'EFFECTIVE',
      name: '已生效',
    },
    // 不生效
    {
      key: 'NOT_EFFECTIVE',
      value: 'NOT_EFFECTIVE',
      name: '不生效',
    },
    // 已驳回
    {
      key: 'REJECTED',
      value: 'REJECTED',
      name: '已驳回',
    },
    // 已废弃
    {
      key: 'ABANDONED',
      value: 'ABANDONED',
      name: '已废弃',
    },
  ] as const),
  // 操作的按钮
  BiddingActionEnum: createEnum([
    // 提交
    {
      key: 'SUBMIT',
      value: 'SUBMIT',
      name: '提交',
    },
    // 强制推进
    {
      key: 'FORCE_PASS',
      value: 'FORCE_PASS',
      name: '强制推进',
    },
    // 退回
    {
      key: 'RETURN',
      value: 'RETURN',
      name: '退回',
    },
    // 强制退回
    {
      key: 'FORCE_RETURN',
      value: 'FORCE_RETURN',
      name: '强制退回',
    },
    // 保存
    {
      key: 'SAVE',
      value: 'SAVE',
      name: '保存',
    },
    // 关闭
    {
      key: 'CLOSE',
      value: 'CLOSE',
      name: '关闭',
    },
    // 超时
    {
      key: 'TIMEOUT',
      value: 'TIMEOUT',
      name: '超时',
    },
  ] as const),
  BiddingFlowEventsEnum: createEnum([
    {
      key: 'START_DESIGN',
      value: 'START_DESIGN',
      name: 'unknown',
    },
    {
      key: 'DESIGN_PLAN',
      value: 'DESIGN_PLAN',
      name: 'unknown',
    },
    {
      key: 'AWAITING_BIDDING',
      value: 'AWAITING_BIDDING',
      name: 'unknown',
    },
    {
      key: 'BUSINESS_CONFIRM_BID_SP',
      value: 'BUSINESS_CONFIRM_BID_SP',
      name: 'unknown',
    },
    {
      key: 'AWAITING_SUBMISSION_BIDS',
      value: 'AWAITING_SUBMISSION_BIDS',
      name: 'unknown',
    },
    {
      key: 'AWAITING_RETRIEVAL_BIDS',
      value: 'AWAITING_RETRIEVAL_BIDS',
      name: 'unknown',
    },
    {
      key: 'AWAITING_BIDDING_ADMIN_CONFIRM',
      value: 'AWAITING_BIDDING_ADMIN_CONFIRM',
      name: 'unknown',
    },
    {
      key: 'BID_OPEN_REVIEW',
      value: 'BID_OPEN_REVIEW',
      name: 'unknown',
    },
    {
      key: 'UPLOAD_BID_RESULT',
      value: 'UPLOAD_BID_RESULT',
      name: 'unknown',
    },
    {
      key: 'AWAITING_DECISION_BIDS',
      value: 'AWAITING_DECISION_BIDS',
      name: 'unknown',
    },
    {
      key: 'ACCEPT_BIDING_ADMIN',
      value: 'ACCEPT_BIDING_ADMIN',
      name: 'unknown',
    },
    {
      key: 'ACCEPT_CHANNEL_LEADER',
      value: 'ACCEPT_CHANNEL_LEADER',
      name: 'unknown',
    },
    {
      key: 'ACCEPT_MARKETING_LEADER',
      value: 'ACCEPT_MARKETING_LEADER',
      name: 'unknown',
    },
    {
      key: 'DISCARD',
      value: 'DISCARD',
      name: 'unknown',
    },
    {
      key: 'NONE',
      value: 'NONE',
      name: 'unknown',
    },
  ] as const),
  BiddingFlowEventV2Enum: createEnum([
    {
      key: 'START_DESIGN',
      value: 'START_DESIGN',
      name: 'unknown',
    },
    {
      key: 'DESIGN_PLAN',
      value: 'DESIGN_PLAN',
      name: 'unknown',
    },
    {
      key: 'ACCEPT_PLAN_DESIGN',
      value: 'ACCEPT_PLAN_DESIGN',
      name: 'unknown',
    },
    {
      key: 'BUSINESS_ACCEPT_PLAN_DESIGN',
      value: 'BUSINESS_ACCEPT_PLAN_DESIGN',
      name: 'unknown',
    },
    {
      key: 'AWAITING_BIDDING',
      value: 'AWAITING_BIDDING',
      name: 'unknown',
    },
    {
      key: 'ISSUE_BIDS',
      value: 'ISSUE_BIDS',
      name: 'unknown',
    },
    {
      key: 'JUDGES_CONFIRM_BIDS',
      value: 'JUDGES_CONFIRM_BIDS',
      name: 'unknown',
    },
    {
      key: 'AWAITING_SUBMISSION_BIDS',
      value: 'AWAITING_SUBMISSION_BIDS',
      name: 'unknown',
    },
    {
      key: 'BID_OPEN_REVIEW',
      value: 'BID_OPEN_REVIEW',
      name: 'unknown',
    },
    {
      key: 'ACCEPT_EVALUATION_RESULT',
      value: 'ACCEPT_EVALUATION_RESULT',
      name: 'unknown',
    },
    {
      key: 'ALLOCATION_BUDGET',
      value: 'ALLOCATION_BUDGET',
      name: 'unknown',
    },
    {
      key: 'LEADER_ACCEPT_CONFIRM',
      value: 'LEADER_ACCEPT_CONFIRM',
      name: 'unknown',
    },
    {
      key: 'BUSINESS_LEADER_ACCEPT',
      value: 'BUSINESS_LEADER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'PLANNER_LEADER_ACCEPT',
      value: 'PLANNER_LEADER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'MARKETING_LEADER_ACCEPT',
      value: 'MARKETING_LEADER_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'MANUAL_SUBMIT_OA',
      value: 'MANUAL_SUBMIT_OA',
      name: 'unknown',
    },
    {
      key: 'OA_PLAN_APPROVE',
      value: 'OA_PLAN_APPROVE',
      name: 'unknown',
    },
    {
      key: 'OA_PLAN_APPROVE_ACCEPT',
      value: 'OA_PLAN_APPROVE_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'BID_RESULT_ACCEPT',
      value: 'BID_RESULT_ACCEPT',
      name: 'unknown',
    },
    {
      key: 'DISCARD',
      value: 'DISCARD',
      name: 'unknown',
    },
    {
      key: 'NONE',
      value: 'NONE',
      name: 'unknown',
    },
  ] as const),
  // 评委小组成员类型：MEMBER-成员,ADMIN-管理员
  BiddingEvaluationMemberTypeEnum: createEnum([
    {
      key: 'MEMBER',
      value: 'MEMBER',
      name: 'unknown',
    },
    {
      key: 'ADMIN',
      value: 'ADMIN',
      name: 'unknown',
    },
    {
      key: 'EXPERT',
      value: 'EXPERT',
      name: 'unknown',
    },
  ] as const),
  // 招采流程阶段枚举
  BiddingProcessStageEnum: createEnum([
    {
      key: 'TO_DO_PROCESS',
      value: 'TO_DO_PROCESS',
      name: 'unknown',
    },
    {
      key: 'COMPLETED_PROCESS',
      value: 'COMPLETED_PROCESS',
      name: 'unknown',
    },
    {
      key: 'MY_INITIATIVE',
      value: 'MY_INITIATIVE',
      name: 'unknown',
    },
  ] as const),
  // 供应商竞选状态
  BiddingState2Enum: createEnum([
    // 竞选通过
    {
      key: 'BIDDING_PASS',
      value: 'BIDDING_PASS',
      name: '竞选通过',
    },
    // 方案确认通过
    {
      key: 'BIDDING_CONFIRM_PASS',
      value: 'BIDDING_CONFIRM_PASS',
      name: '方案确认通过',
    },
    // 中选通过
    {
      key: 'BIDDING_WIN',
      value: 'BIDDING_WIN',
      name: '中选通过',
    },
    // 淘汰出局
    {
      key: 'BIDDING_OUT',
      value: 'BIDDING_OUT',
      name: '淘汰出局',
    },
  ] as const),
  // 议价状态
  BargainStateEnum: createEnum([
    // 待提交报价
    {
      key: 'PENDING',
      value: 'PENDING',
      name: '待提交报价',
    },
    // 已提交议价
    {
      key: 'PASS',
      value: 'PASS',
      name: '已提交议价',
    },
  ] as const),
  // 邀约状态
  BiddingSupplierWillingInviteStateEnum: createEnum([
    // 有意愿
    {
      key: 'WILLING',
      value: 'WILLING',
      name: '有意愿',
    },
    // 无意愿
    {
      key: 'UNWILLING',
      value: 'UNWILLING',
      name: '无意愿',
    },
    // 未响应
    {
      key: 'NOT_RESPONDING',
      value: 'NOT_RESPONDING',
      name: '未响应',
    },
  ] as const),
  // 开标类型
  OpenBiddingTypeEnum: createEnum([
    // 开始比选
    {
      key: 'BID',
      value: 'BID',
      name: '开始比选',
    },
    // 技术方案开启
    {
      key: 'TECHNICAL_BID',
      value: 'TECHNICAL_BID',
      name: '技术方案开启',
    },
    // 商务报价开启
    {
      key: 'COMMERCIAL_BID',
      value: 'COMMERCIAL_BID',
      name: '商务报价开启',
    },
  ] as const),
  // 业务标记
  BiddingBusinessStateEnum: createEnum([
    // 流标
    {
      key: 'ABANDON_BIDDING',
      value: 'ABANDON_BIDDING',
      name: '流标',
    },
    // 听标
    {
      key: 'LISTEN_BIDDING',
      value: 'LISTEN_BIDDING',
      name: '听标',
    },
    // 延期
    {
      key: 'DEFER_BIDDING',
      value: 'DEFER_BIDDING',
      name: '延期',
    },
    // 比选完成
    {
      key: 'FINISH_BIDDING',
      value: 'FINISH_BIDDING',
      name: '比选完成',
    },
  ] as const),
  // 比选流程版本
  BiddingFlowFlagEnum: createEnum([
    // 历史老数据的处理流程
    {
      key: 'OLD_ONLINE_PROCESS',
      value: 'OLD_ONLINE_PROCESS',
      name: '历史老数据的处理流程',
    },
    // V2版本比选流程
    {
      key: 'BIDDING_V2',
      value: 'BIDDING_V2',
      name: 'V2版本比选流程',
    },
  ] as const),
  // 保密协议模板类型（新逻辑 confidentialityVersion=1 时使用）：COMMON=通用模板，OTHER=其他
  NdaTemplateTypeEnum: createEnum([
    // 通用模板
    {
      key: 'COMMON',
      value: 'COMMON',
      name: '通用模板',
    },
    // 其他
    {
      key: 'OTHER',
      value: 'OTHER',
      name: '其他',
    },
  ] as const),
  // 费控状态
  BiddingFeeControlStateEnum: createEnum([
    // 未发起冻结
    {
      key: 'NOT_SUBMIT',
      value: 'NOT_SUBMIT',
      name: '未发起冻结',
    },
    // 预算已冻结
    {
      key: 'SUBMITTED',
      value: 'SUBMITTED',
      name: '预算已冻结',
    },
    // 预算已释放
    {
      key: 'DELETED',
      value: 'DELETED',
      name: '预算已释放',
    },
  ] as const),
  // 比选评审状态
  BiddingReviewStateEnum: createEnum([
    // 技术评分
    {
      key: 'TECH_SCORE',
      value: 'TECH_SCORE',
      name: '技术评分',
    },
    // 商务议价
    {
      key: 'BARGAIN',
      value: 'BARGAIN',
      name: '商务议价',
    },
    // 供应商报价
    {
      key: 'SUPPLIER_QUOTE',
      value: 'SUPPLIER_QUOTE',
      name: '供应商报价',
    },
    // 商务评分
    {
      key: 'BUSINESS_SCORE',
      value: 'BUSINESS_SCORE',
      name: '商务评分',
    },
  ] as const),
  // 比选方案状态, 0 待确认, 1 已确认
  BiddingPlanStateEnum: createEnum([
    // 待确认
    {
      key: 'TO_BE_CONFIRM',
      value: 'TO_BE_CONFIRM',
      name: '待确认',
    },
    // 已确认
    {
      key: 'CONFIRMED',
      value: 'CONFIRMED',
      name: '已确认',
    },
    // 已拒绝
    {
      key: 'REJECTED',
      value: 'REJECTED',
      name: '已拒绝',
    },
  ] as const),
  // 确认允许比选状态, 0 待确认, 1 允许, 2 拒绝
  BiddingConfirmStateEnum: createEnum([
    // 待确认
    {
      key: 'TO_BE_CONFIRM',
      value: 'TO_BE_CONFIRM',
      name: '待确认',
    },
    // 允许
    {
      key: 'CONFIRMED',
      value: 'CONFIRMED',
      name: '允许',
    },
    // 拒绝
    {
      key: 'REJECT',
      value: 'REJECT',
      name: '拒绝',
    },
  ] as const),
  // 需求文档状态
  TrumpetDemandStateEnum: createEnum([
    // 新建
    {
      key: 'NEWLY_BUILT',
      value: 'newly_built',
      name: '新建',
    },
    // 需求方负责人审批
    {
      key: 'DEMAND_PRINCIPAL_APPROVAL',
      value: 'demand_principal_approval',
      name: '需求方负责人审批',
    },
    // 策划设计
    {
      key: 'PLAN_DESIGN',
      value: 'plan_design',
      name: '策划设计',
    },
    // 方案审批
    {
      key: 'PLAN_APPROVAL',
      value: 'plan_approval',
      name: '方案审批',
    },
    // 活动设计
    {
      key: 'ACTIVITY_DESIGN',
      value: 'activity_design',
      name: '活动设计',
    },
    // 执行中
    {
      key: 'IN_PROGRESS',
      value: 'in_progress',
      name: '执行中',
    },
    // 已完成
    {
      key: 'FINISHED',
      value: 'finished',
      name: '已完成',
    },
    // 已关闭
    {
      key: 'CLOSED',
      value: 'closed',
      name: '已关闭',
    },
  ] as const),
  // 流程标志
  TrumpetFlowFlagEnum: createEnum([
    // 历史老数据的处理流程
    {
      key: 'OLD_ONLINE_PROCESS',
      value: 'OLD_ONLINE_PROCESS',
      name: '历史老数据的处理流程',
    },
    // 小喇叭初期版本
    {
      key: 'NORMAL',
      value: 'NORMAL',
      name: '小喇叭初期版本',
    },
    // 添加考核指标
    {
      key: 'ADD_ASSESSMENT_INDICATORS',
      value: 'ADD_ASSESSMENT_INDICATORS',
      name: '添加考核指标',
    },
    // 添加策划设计节点
    {
      key: 'ADD_PLAN_DESIGN',
      value: 'ADD_PLAN_DESIGN',
      name: '添加策划设计节点',
    },
  ] as const),
  // 结算状态
  TrumpetCaseStateEnum: createEnum([
    // 提交结案
    {
      key: 'SUBMIT_CASE_CLOSURE',
      value: 'submit_case_closure',
      name: '提交结案',
    },
    // 策划复核
    {
      key: 'PLAN_ACCEPT',
      value: 'plan_accept',
      name: '策划复核',
    },
    // 需求方负责人验收
    {
      key: 'APPROVE_SETTLE',
      value: 'approve_settle',
      name: '需求方负责人验收',
    },
    // 需求方二级负责人验收
    {
      key: 'SECONDARY_APPROVE_SETTLE',
      value: 'secondary_approve_settle',
      name: '需求方二级负责人验收',
    },
    // 小喇叭负责人验收
    {
      key: 'MARKETING_APPROVE_SETTLE',
      value: 'marketing_approve_settle',
      name: '小喇叭负责人验收',
    },
    // QC验收复核
    {
      key: 'QC_SETTLE',
      value: 'qc_settle',
      name: 'QC验收复核',
    },
    // 市场内审组审核
    {
      key: 'MARKET_INTERNAL_AUDIT_TEAM_REVIEW',
      value: 'market_internal_audit_team_review',
      name: '市场内审组审核',
    },
    // 策略研究部审核
    {
      key: 'STRATEGY_RESEARCH_DEPARTMENT_REVIEW',
      value: 'strategy_research_department_review',
      name: '策略研究部审核',
    },
    // 运营复核
    {
      key: 'OPERATIONS_REVIEW',
      value: 'operations_review',
      name: '运营复核',
    },
    // 营销业务验收与结算
    {
      key: 'SUBMIT_OA',
      value: 'submit_oa',
      name: '营销业务验收与结算',
    },
    // 确认核销
    {
      key: 'CONFIRM_VERIFICATION',
      value: 'confirm_verification',
      name: '确认核销',
    },
    // 已完成
    {
      key: 'FINISHED',
      value: 'finished',
      name: '已完成',
    },
    // 已废弃
    {
      key: 'CLOSED',
      value: 'closed',
      name: '已废弃',
    },
  ] as const),
  PersonIdentityTypeEnum: createEnum([
    // 居民身份证
    {
      key: 'RESIDENCE_ID_CARD',
      value: '01',
      name: '居民身份证',
    },
    // 港澳居民来往内地通行证
    {
      key: 'MAINLAND_TRAVEL_PERMIT_FOR_HONG_KONG_MACAU_RESIDENTS',
      value: '02',
      name: '港澳居民来往内地通行证',
    },
    // 中华人民共和国港澳居民居住证
    {
      key: 'RESIDENCE_PERMIT_FOR_HONG_KONG_MACAU_RESIDENTS',
      value: '03',
      name: '中华人民共和国港澳居民居住证',
    },
    // 台湾居民来往大陆通行证
    {
      key: 'TAIWAN_TRAVEL_PERMIT',
      value: '04',
      name: '台湾居民来往大陆通行证',
    },
    // 中华人民共和国台湾居民居住证
    {
      key: 'RESIDENCE_PERMIT_FOR_TAIWAN_RESIDENTS',
      value: '05',
      name: '中华人民共和国台湾居民居住证',
    },
    // 中国护照
    {
      key: 'CHINESE_PASSPORT',
      value: '06',
      name: '中国护照',
    },
    // 外国护照
    {
      key: 'FOREIGN_PASSPORT',
      value: '07',
      name: '外国护照',
    },
    // 外国人永久居留身份证(外国人永久居留证)
    {
      key: 'PERMANENT_RESIDENCE_ID_CARD',
      value: '08',
      name: '外国人永久居留身份证(外国人永久居留证)',
    },
    // 中华人民共和国外国人工作许可证(A类)
    {
      key: 'WORK_PERMIT_A',
      value: '09',
      name: '中华人民共和国外国人工作许可证(A类)',
    },
    // 中华人民共和国外国人工作许可证(B类)
    {
      key: 'WORK_PERMIT_B',
      value: '10',
      name: '中华人民共和国外国人工作许可证(B类)',
    },
    // 中华人民共和国外国人工作许可证(C类)
    {
      key: 'WORK_PERMIT_C',
      value: '11',
      name: '中华人民共和国外国人工作许可证(C类)',
    },
    // 其他个人证件
    {
      key: 'OTHER_PERSON_ID',
      value: '12',
      name: '其他个人证件',
    },
  ] as const),
  // 活动状态
  ClosedLoopActivityStateEnum: createEnum([
    // 进行中
    {
      key: 'RUNNING',
      value: 'running',
      name: '进行中',
    },
    // 已完成
    {
      key: 'FINISHED',
      value: 'finished',
      name: '已完成',
    },
    // 已废弃
    {
      key: 'CLOSED',
      value: 'closed',
      name: '已废弃',
    },
  ] as const),
  // 提前核价状态：AI_CHECKING=智能核价中 / PENDING_CONFIRM=待商务确认核价 / PENDING_PLAN_CONFIRM_DARU=待策划确认核价 / CONFIRMED=已核价 / CLOSED=已关闭 / VOIDED=已作废
  AdvancePriceCheckStateEnum: createEnum([
    // 智能核价中
    {
      key: 'AI_CHECKING',
      value: 'AI_CHECKING',
      name: '智能核价中',
    },
    // 待商务确认核价
    {
      key: 'PENDING_CONFIRM',
      value: 'PENDING_CONFIRM',
      name: '待商务确认核价',
    },
    // 待策划确认核价
    {
      key: 'PENDING_PLAN_CONFIRM_DARU',
      value: 'PENDING_PLAN_CONFIRM_DARU',
      name: '待策划确认核价',
    },
    // 已核价
    {
      key: 'CONFIRMED',
      value: 'CONFIRMED',
      name: '已核价',
    },
    // 已关闭
    {
      key: 'CLOSED',
      value: 'CLOSED',
      name: '已关闭',
    },
    // 已作废
    {
      key: 'VOIDED',
      value: 'VOIDED',
      name: '已作废',
    },
  ] as const),
  // MPC单状态（删除、废弃等）
  MpcStatusEnum: createEnum([
    {
      key: 'DELETE',
      value: 'DELETE',
      name: 'unknown',
    },
    {
      key: 'DEPRECATED',
      value: 'DEPRECATED',
      name: 'unknown',
    },
    {
      key: 'RETURNED',
      value: 'RETURNED',
      name: 'unknown',
    },
    {
      key: 'NEW',
      value: 'NEW',
      name: 'unknown',
    },
    {
      key: 'SUBMIT',
      value: 'SUBMIT',
      name: 'unknown',
    },
  ] as const),
  // 供应商侧需求单状态
  OsWorkspaceToDoTypeEnum: createEnum([
    {
      key: 'PENDING_QUOTE',
      value: 'PENDING_QUOTE',
      name: 'unknown',
    },
    {
      key: 'MAKING',
      value: 'MAKING',
      name: 'unknown',
    },
    {
      key: 'SETTLED_UPLOAD',
      value: 'SETTLED_UPLOAD',
      name: 'unknown',
    },
    {
      key: 'ALL_DEMAND',
      value: 'ALL_DEMAND',
      name: 'unknown',
    },
  ] as const),
  // 供应商侧需求单状态筛选条件（可多选）
  OutsourceWorkbenchTypeEnum: createEnum([
    {
      key: 'PENDING_QUOTE',
      value: 'PENDING_QUOTE',
      name: 'unknown',
    },
    {
      key: 'NOT_QUOTED',
      value: 'NOT_QUOTED',
      name: 'unknown',
    },
    {
      key: 'REJECT',
      value: 'REJECT',
      name: 'unknown',
    },
    {
      key: 'CONFIRM_QUOTE',
      value: 'CONFIRM_QUOTE',
      name: 'unknown',
    },
    {
      key: 'NOT_SELECTED',
      value: 'NOT_SELECTED',
      name: 'unknown',
    },
    {
      key: 'MAKING',
      value: 'MAKING',
      name: 'unknown',
    },
    {
      key: 'REVIEW_QUOTE',
      value: 'REVIEW_QUOTE',
      name: 'unknown',
    },
    {
      key: 'SETTLED',
      value: 'SETTLED',
      name: 'unknown',
    },
    {
      key: 'SETTLED_UPLOAD',
      value: 'SETTLED_UPLOAD',
      name: 'unknown',
    },
    {
      key: 'PAYING',
      value: 'PAYING',
      name: 'unknown',
    },
    {
      key: 'PAYED',
      value: 'PAYED',
      name: 'unknown',
    },
    {
      key: 'SELECTED',
      value: 'SELECTED',
      name: 'unknown',
    },
    {
      key: 'CANCELED',
      value: 'CANCELED',
      name: 'unknown',
    },
    {
      key: 'MY_ADVANCE',
      value: 'MY_ADVANCE',
      name: 'unknown',
    },
    {
      key: 'COMPLETED',
      value: 'COMPLETED',
      name: 'unknown',
    },
  ] as const),
  // 操作类型
  OutsourceQuoteTableOperateTypeEnum: createEnum([
    // 暂存
    {
      key: 'TEMP_SAVE',
      value: 'TEMP_SAVE',
      name: '暂存',
    },
    // 保存
    {
      key: 'SAVE',
      value: 'SAVE',
      name: '保存',
    },
  ] as const),
  // Muse 归档状态
  MuseArchiveStatusEnum: createEnum([
    // 已归档待同步
    {
      key: 'ARCHIVED_PENDING_SYNC',
      value: 'ARCHIVED_PENDING_SYNC',
      name: '已归档待同步',
    },
    // 归档完成
    {
      key: 'ARCHIVE_COMPLETED',
      value: 'ARCHIVE_COMPLETED',
      name: '归档完成',
    },
  ] as const),
  // 状态
  ExportTaskStatusEnum: createEnum([
    // 执行中
    {
      key: 'RUNNING',
      value: 1,
      name: '执行中',
    },
    // 执行失败
    {
      key: 'FAIL',
      value: 2,
      name: '执行失败',
    },
    // 执行成功
    {
      key: 'SUCCESS',
      value: 3,
      name: '执行成功',
    },
  ] as const),
  DemandSupplierQuoteTypeEnum: createEnum([
    // 不含税金额
    {
      key: 'EXCLUDING_TAX',
      value: 0,
      name: '不含税金额',
    },
    // 含税金额
    {
      key: 'INCLUDING_TAX',
      value: 1,
      name: '含税金额',
    },
  ] as const),
  // 付款节点操作
  PaymentFeeControlOpEnum: createEnum([
    // 提交费控/重新提交费控
    {
      key: 'SUBMIT_TO_FEE_CONTROL',
      value: 0,
      name: '提交费控/重新提交费控',
    },
    // 费控删除付款单
    {
      key: 'FEE_CONTROL_DELETE_OUR_PAYMENT',
      value: 1,
      name: '费控删除付款单',
    },
    // 费控归档付款单
    {
      key: 'FEE_CONTROL_ARCHIVE_OUR_PAYMENT',
      value: 2,
      name: '费控归档付款单',
    },
    // 退回到发票待上传
    {
      key: 'BACK_TO_UPLOAD_INVOICE',
      value: 3,
      name: '退回到发票待上传',
    },
  ] as const),
  ValidChannelSupplierTypeEnum: createEnum([
    // 储备供应商
    {
      key: 'TEMPORARY',
      value: 1,
      name: '储备供应商',
    },
    // 重要供应商
    {
      key: 'RESERVED',
      value: 2,
      name: '重要供应商',
    },
    // 核心供应商
    {
      key: 'ANNUAL_FRAME',
      value: 3,
      name: '核心供应商',
    },
  ] as const),
  // 审批动作
  SupplierApprovalActionTypeEnum: createEnum([
    // 通过
    {
      key: 'PASS',
      value: 1,
      name: '通过',
    },
    // 拒绝
    {
      key: 'REJECT',
      value: 2,
      name: '拒绝',
    },
    // 退回
    {
      key: 'RETURN',
      value: 3,
      name: '退回',
    },
    // 发起
    {
      key: 'SUBMIT',
      value: 4,
      name: '发起',
    },
  ] as const),
  // 审批动作显示类型
  SupplierApprovalActionDisplayTypeEnum: createEnum([
    // 已提交
    {
      key: 'SUBMITTED',
      value: 'SUBMITTED',
      name: '已提交',
    },
    // 已通过
    {
      key: 'PASSED',
      value: 'PASSED',
      name: '已通过',
    },
    // 已禁入
    {
      key: 'REJECTED',
      value: 'REJECTED',
      name: '已禁入',
    },
    // 已退回
    {
      key: 'RETURNED',
      value: 'RETURNED',
      name: '已退回',
    },
    // 审核中
    {
      key: 'PENDING',
      value: 'PENDING',
      name: '审核中',
    },
    // 待审核
    {
      key: 'WAITING',
      value: 'WAITING',
      name: '待审核',
    },
  ] as const),
  ImportTaskStatusEnum: createEnum([
    // 执行中
    {
      key: 'RUNNING',
      value: 1,
      name: '执行中',
    },
    // 导入失败，系统异常
    {
      key: 'FAIL',
      value: 2,
      name: '导入失败，系统异常',
    },
    // 部分成功，部分失败
    {
      key: 'PARTIAL_SUCCESS',
      value: 3,
      name: '部分成功，部分失败',
    },
    // 执行成功
    {
      key: 'SUCCESS',
      value: 4,
      name: '执行成功',
    },
  ] as const),
  // 风险提示类型
  RiskTipTypeEnum: createEnum([
    // 商务负责人风险提示
    {
      key: 'BUSINESS_LEADER',
      value: 'BUSINESS_LEADER',
      name: '商务负责人风险提示',
    },
    // 策划负责人风险提示
    {
      key: 'PLANNER_LEADER',
      value: 'PLANNER_LEADER',
      name: '策划负责人风险提示',
    },
  ] as const),
  // 核价备注类型
  PriceCheckRemarkTypeEnum: createEnum([
    // 商务备注
    {
      key: 'BUSINESS',
      value: 'BUSINESS',
      name: '商务备注',
    },
    // 商务负责人备注
    {
      key: 'BUSINESS_LEADER',
      value: 'BUSINESS_LEADER',
      name: '商务负责人备注',
    },
    // 策划备注
    {
      key: 'PLAN',
      value: 'PLAN',
      name: '策划备注',
    },
    // 策划负责人备注
    {
      key: 'PLAN_LEADER',
      value: 'PLAN_LEADER',
      name: '策划负责人备注',
    },
  ] as const),
  // 调整结果：1：待生效，2：已生效，3：未生效
  KpiAdjustmentResultEnum: createEnum([
    // 待生效
    {
      key: 'TO_BE_EFFECTIVE',
      value: 'TO_BE_EFFECTIVE',
      name: '待生效',
    },
    // 已生效
    {
      key: 'EFFECTIVE',
      value: 'EFFECTIVE',
      name: '已生效',
    },
    // 未生效
    {
      key: 'NOT_EFFECTIVE',
      value: 'NOT_EFFECTIVE',
      name: '未生效',
    },
  ] as const),
  // 价格类型（枚举）：中标报价、结案报价
  DemandSupplierQuoteStageEnum: createEnum([
    // 中标报价
    {
      key: 'BIDDING_QUOTE',
      value: 'BIDDING_QUOTE',
      name: '中标报价',
    },
    // 结案报价
    {
      key: 'MARKING_QUOTE',
      value: 'MARKING_QUOTE',
      name: '结案报价',
    },
  ] as const),
  NdaTemplateTypeDownloadEnum: createEnum([
    // 通用模板
    {
      key: 'COMMON',
      value: 'COMMON',
      name: '通用模板',
    },
  ] as const),
  UserStatusEnum: createEnum([
    {
      key: 'ACTIVE',
      value: 'ACTIVE',
      name: 'unknown',
    },
    {
      key: 'LOCKED',
      value: 'LOCKED',
      name: 'unknown',
    },
  ] as const),
  // 员工类型
  EmpTypeEnum: createEnum([
    {
      key: 0,
      value: 'UNKNOWN',
      name: 'unknown',
    },
    {
      key: 1,
      value: 'NORMAL',
      name: 'unknown',
    },
    {
      key: 2,
      value: 'INTERN',
      name: 'unknown',
    },
    {
      key: 3,
      value: 'COOPERATOR',
      name: 'unknown',
    },
    {
      key: 4,
      value: 'OUTER',
      name: 'unknown',
    },
    {
      key: 5,
      value: 'DISPATCHER',
      name: 'unknown',
    },
  ] as const),
  SolarCtDelegationEnum: createEnum([
    {
      key: 'AGENT',
      value: 'agent',
      name: 'unknown',
    },
    {
      key: 'PRE_ADD_SIGN',
      value: 'preAddSign',
      name: 'unknown',
    },
    {
      key: 'POST_ADD_SIGN',
      value: 'postAddSign',
      name: 'unknown',
    },
    {
      key: 'REASSIGN',
      value: 'reassign',
      name: 'unknown',
    },
  ] as const),
};
/**
 * 查询任务详情（路径参数） /api/v2/biz-task/{id}
 */
export function v2BizTaskById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseBizTaskDetailVo>(`/api/v2/biz-task/${id}`, {
    ...opts,
  });
}
/**
 * 查询任务详情（路径参数） /api/v2/biz-task/{id}
 */
export function v2BizTaskByIdUsingPost(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseBizTaskDetailVo>(`/api/v2/biz-task/${id}`, {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/mobile/v2/accounts/popo_authorize
 */
export function mobileAccountPopoAuthorize(opts?: ReqOpts) {
  return request<unknown>('/api/mobile/v2/accounts/popo_authorize', {
    ...opts,
  });
}
/**
 * unknown /api/mobile/v2/accounts/popo_authorize
 */
export function mobileAccountPopoAuthorizeUsingPost(opts?: ReqOpts) {
  return request<unknown>('/api/mobile/v2/accounts/popo_authorize', {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/mobile/v2/accounts/access_popo
 */
export function mobileAccountAccessPopo(
  params: {
    timestamp: string;
    nonce: string;
    signature: string;
    echostr?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<unknown>(
    createUri('/api/mobile/v2/accounts/access_popo', query),
    { ...opts }
  );
}
/**
 * unknown /api/mobile/v2/accounts/access_popo
 */
export function mobileAccountAccessPopoUsingPost(
  params: {
    timestamp: string;
    nonce: string;
    signature: string;
    echostr?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<unknown>(
    createUri('/api/mobile/v2/accounts/access_popo', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/user
 */
export function userGetWithAssociate(data: CreateUserCmd, opts?: ReqOpts) {
  return request<ResponseObject>('/api/v2/user', {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * 编辑供应商使用限制配置 /api/v2/supplier/usage-limit/config/{id}
 */
export function supplierUsageLimitConfigUpdate(
  params: {
    id: number;
  },
  data: SupplierUsageLimitConfigForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseVoid>(`/api/v2/supplier/usage-limit/config/${id}`, {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * 【供应商主体维度】获取供应商主体关联信息 /api/v2/supplier-new/{mainSupplierId}/supplier-links
 */
export function supplierControllerNewQuerySupplierLinks(
  params: {
    main_supplier_id: number;
  },
  opts?: ReqOpts
) {
  const { main_supplier_id } = params;
  return request<ResponseListSupplierLinkVo>(
    `/api/v2/supplier-new/${main_supplier_id}/supplier-links`,
    { ...opts }
  );
}
/**
 * 【供应商主体维度】修改供应商主体关联信息 /api/v2/supplier-new/{mainSupplierId}/supplier-links
 */
export function supplierControllerNewEditSupplierLinks(
  params: {
    main_supplier_id: number;
  },
  data: EditSupplierLinkForm,
  opts?: ReqOpts
) {
  const { main_supplier_id } = params;
  return request<ResponseVoid>(
    `/api/v2/supplier-new/${main_supplier_id}/supplier-links`,
    { method: 'PUT', data, ...opts }
  );
}
/**
 * 【供应商主体维度】修改供应商主体关联信息（单条） /api/v2/supplier-new/{mainSupplierId}/supplier-link/{targetId}
 */
export function supplierControllerNewEditSupplierLink(
  params: {
    main_supplier_id: number;
    target_id: number;
  },
  data: SupplierLinkInfo,
  opts?: ReqOpts
) {
  const { main_supplier_id, target_id } = params;
  return request<ResponseVoid>(
    `/api/v2/supplier-new/${main_supplier_id}/supplier-link/${target_id}`,
    { method: 'PUT', data, ...opts }
  );
}
/**
 * 【供应商主体维度】删除供应商主体关联信息 /api/v2/supplier-new/{mainSupplierId}/supplier-link/{targetId}
 */
export function supplierControllerNewDeleteSupplierLink(
  params: {
    main_supplier_id: number;
    target_id: number;
  },
  opts?: ReqOpts
) {
  const { main_supplier_id, target_id } = params;
  return request<ResponseVoid>(
    `/api/v2/supplier-new/${main_supplier_id}/supplier-link/${target_id}`,
    { method: 'DELETE', ...opts }
  );
}
/**
 * 设置单一来源 /api/v2/supplier-new/{id}/set-single-source
 */
export function supplierControllerNewSetSingleSource(
  params: {
    id: number;
  },
  data: ChannelSupplierSetSingleSourceForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(
    `/api/v2/supplier-new/${id}/set-single-source`,
    { method: 'PUT', data, ...opts }
  );
}
/**
 * 【20240620版本】更新已入库供应商 /api/v2/supplier-new/valid/{id}
 */
export function supplierControllerNewUpdateValidFull(
  params: {
    id: number;
  },
  data: UpdateValidSupplierForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseChannelSupplierDetailVo>(
    `/api/v2/supplier-new/valid/${id}`,
    { method: 'PUT', data, ...opts }
  );
}
/**
 * 更新有效的渠道供应商 /api/v2/supplier-new/valid/{id}
 */
export function supplierControllerNewUpdateValid(
  params: {
    id: number;
  },
  data: UpdateValidChannelSupplierForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(`/api/v2/supplier-new/valid/${id}`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【20240620版本】更新未入库供应商 /api/v2/supplier-new/un-approval/{id}
 */
export function supplierControllerNewUpdateUnApprovalFull(
  params: {
    id: number;
  },
  data: UpdateUnApprovalSupplierForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseChannelSupplierDetailVo>(
    `/api/v2/supplier-new/un-approval/${id}`,
    { method: 'PUT', data, ...opts }
  );
}
/**
 * 更新未审核渠道供应商 /api/v2/supplier-new/un-approval/{id}
 */
export function supplierControllerNewUpdateUnApproval(
  params: {
    id: number;
  },
  data: UpdateUnApprovalChannelSupplierForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(`/api/v2/supplier-new/un-approval/${id}`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 更新主供应商 /api/v2/supplier-new/main-supplier/{id}
 */
export function mainSupplierUpdateMainSupplier(
  params: {
    id: number;
  },
  data: UpdateMainSupplierForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(`/api/v2/supplier-new/main-supplier/${id}`, {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * 查询供应商备注 /api/v2/supplier-new/main-supplier/{id}/supplier-comment
 */
export function mainSupplierQuerySupplierComment(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseListSupplierCommentDto>(
    `/api/v2/supplier-new/main-supplier/${id}/supplier-comment`,
    { ...opts }
  );
}
/**
 * 增加供应商备注 /api/v2/supplier-new/main-supplier/{id}/supplier-comment
 */
export function mainSupplierAddSupplierComment(
  params: {
    id: number;
  },
  data: MainSupplierAddSupplierCommentForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(
    `/api/v2/supplier-new/main-supplier/${id}/supplier-comment`,
    { method: 'PUT', data, ...opts }
  );
}
/**
 * 【20240620版本】设置单一来源 /api/v2/supplier-new/main-supplier/{id}/set-single-source
 */
export function mainSupplierSetSingleSource(
  params: {
    id: number;
  },
  data: MainSupplierSetSingleSourceForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(
    `/api/v2/supplier-new/main-supplier/${id}/set-single-source`,
    { method: 'PUT', data, ...opts }
  );
}
/**
 * 风险提示开关 /api/v2/supplier-new/main-supplier/{id}/risk-switch
 */
export function mainSupplierRiskSwitch(
  params: {
    id: number;
  },
  data: MainSupplierRiskSwitchForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(
    `/api/v2/supplier-new/main-supplier/${id}/risk-switch`,
    { method: 'PUT', data, ...opts }
  );
}
/**
 * 查询供应商风险备注 /api/v2/supplier-new/main-supplier/{id}/risk-remark
 */
export function mainSupplierQueryRiskRemark(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseListSupplierRiskRemarkDto>(
    `/api/v2/supplier-new/main-supplier/${id}/risk-remark`,
    { ...opts }
  );
}
/**
 * 增加供应商风险备注 /api/v2/supplier-new/main-supplier/{id}/risk-remark
 */
export function mainSupplierAddRiskRemark(
  params: {
    id: number;
  },
  data: MainSupplierAddRiskRemarkForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(
    `/api/v2/supplier-new/main-supplier/${id}/risk-remark`,
    { method: 'PUT', data, ...opts }
  );
}
/**
 * 创建数据导出任务 /api/v2/supplier-new/data-export-task
 */
export function channelSupplierExportCreateTask(
  data: CreateExportTaskForm,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/supplier-new/data-export-task', {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * 创建渠道供应商 /api/v2/supplier-new/create
 */
export function supplierControllerNewCreate(
  data: CreateChannelSupplierForm,
  opts?: ReqOpts
) {
  return request<ResponseLong>('/api/v2/supplier-new/create', {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier-new/change-invalid-date/{id}
 */
export function supplierControllerNewChangeChannelSupplierInvalidDate(
  params: {
    id: number;
  },
  data: UpdateValidSupplierForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(
    `/api/v2/supplier-new/change-invalid-date/${id}`,
    { method: 'PUT', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/roles/{id}
 */
export function v2RubyMigrateRolesById(
  params: {
    id: number;
  },
  data: EditRoleCmd,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<RubyResponseVoVoid>(`/api/v2/ruby_migrate/roles/${id}`, {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/roles/{id}
 */
export function v2RubyMigrateRolesByIdUsingDelete(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<RubyResponseVoVoid>(`/api/v2/ruby_migrate/roles/${id}`, {
    method: 'DELETE',
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/products/{id}
 */
export function v2RubyMigrateProductsById(
  params: {
    id: number;
  },
  data: SaveProductCmd,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<RubyResponseVo>(`/api/v2/ruby_migrate/products/${id}`, {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/products/{id}
 */
export function v2RubyMigrateProductsByIdUsingDelete(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<RubyResponseVoVoid>(`/api/v2/ruby_migrate/products/${id}`, {
    method: 'DELETE',
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/supplier-new/{id}
 */
export function outsourceNewSupplierQueryById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseChannelSupplierDetailVo>(
    `/api/outsource/v2/supplier-new/${id}`,
    { ...opts }
  );
}
/**
 * unknown /api/outsource/v2/supplier-new/{id}
 */
export function outsourceNewSupplierUpdateById(
  params: {
    id: number;
  },
  data: OutsourceSupplierEditForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(`/api/outsource/v2/supplier-new/${id}`, {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * unknown /support/solar/sendNewAccountEmail
 */
export function supportSolarSendNewAccountEmail(
  data: SendNewAccountEmailReq,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/support/solar/sendNewAccountEmail', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/solar/checkLogin
 */
export function supportSolarCheckLogin(data: CheckLoginReq, opts?: ReqOpts) {
  return request<ResponseOutsourceCheckLoginResultDto>(
    '/support/solar/checkLogin',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/solar/supplier/saveSupplierDevice
 */
export function supportRelaySolarSupplierSaveSupplierDevice(
  data: MuseSupplierDeviceSaveRequest,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/solar/supplier/saveSupplierDevice',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/solar/supplier/saveSupplierAccount
 */
export function supportRelaySolarSupplierSaveSupplierAccount(
  data: MuseSupplierAccountSaveRequest,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/solar/supplier/saveSupplierAccount',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/solar/supplier/removeSupplierDevice
 */
export function supportRelaySolarSupplierRemoveSupplierDevice(
  data: MuseSupplierDeviceDeleteRequest,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/solar/supplier/removeSupplierDevice',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/solar/supplier/removeSupplierAccount
 */
export function supportRelaySolarSupplierRemoveSupplierAccount(
  data: MuseSupplierAccountDeleteRequest,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/solar/supplier/removeSupplierAccount',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/solar/supplier/receive_supplier_risk_switch
 */
export function supportRelaySolarSupplierReceiveSupplierRiskSwitch(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/solar/supplier/receive_supplier_risk_switch',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/solar/supplier/receive_supplier_note
 */
export function supportRelaySolarSupplierReceiveSupplierNote(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/solar/supplier/receive_supplier_note',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/solar/supplier/pushSupplier
 */
export function supportRelaySolarSupplierPushSupplier(
  data: MuseSupplierRequest,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/support/relay/solar/supplier/pushSupplier', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/relay/solar/supplier/openSupplierSpace
 */
export function supportRelaySolarSupplierOpenSupplierSpace(
  data: MuseSupplierSpaceRequest,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/solar/supplier/openSupplierSpace',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/solar/supplier/museSupplierInit
 */
export function supportRelaySolarSupplierMuseSupplierInit(
  params: {
    remark: string;
  },
  data: SupplierBasicDto,
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListString>(
    createUri('/support/relay/solar/supplier/museSupplierInit', query),
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/receive_supplier_onboarding_notify_message
 */
export function supportRelayReceiveSupplierOnboardingNotifyMessage(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/receive_supplier_onboarding_notify_message',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/receive_supplier_grade_notify_message
 */
export function supportRelayReceiveSupplierGradeNotifyMessage(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/receive_supplier_grade_notify_message',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/receive_supplier_device_notify_message
 */
export function supportRelayReceiveSupplierDeviceNotifyMessage(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/receive_supplier_device_notify_message',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/receive_supplier_change_notify_message
 */
export function supportRelayReceiveSupplierChangeNotifyMessage(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/receive_supplier_change_notify_message',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/receive_supplier_account_notify_message
 */
export function supportRelayReceiveSupplierAccountNotifyMessage(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/receive_supplier_account_notify_message',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/relay/receive_qc_review_result
 */
export function supportRelayReceiveQcReviewResult(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/support/relay/receive_qc_review_result', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/relay/receive_contract_platform_notify_message
 */
export function supportRelayReceiveContractPlatformNotifyMessage(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/support/relay/receive_contract_platform_notify_message',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 接收OA审批回调 /support/prepayment/oa/receive
 */
export function supportPrepaymentOaReceive(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/prepayment/oa/receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/trumpet_add_budget_receive
 */
export function supportOaTrumpetAddBudgetReceive(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/trumpet_add_budget_receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/test_user_center
 */
export function supportOaTestUserCenter(
  params: {
    job_number: string;
    token: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseObject>(
    createUri('/support/oa/test_user_center', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /support/oa/sm_expense_budget_receive
 */
export function supportOaSmExpenseBudgetReceive(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/sm_expense_budget_receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/receive
 */
export function supportOaReceive(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/recieve
 */
export function supportOaRecieve(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/recieve', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/query/risk_tag
 */
export function supportOaQueryRiskTag(data: OaQueryRiskTagReq, opts?: ReqOpts) {
  return request<EaseFlowResponseListListOaQueryVmdmRiskTagRes>(
    '/support/oa/query/risk_tag',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/oa/price_library_audit/receive
 */
export function supportOaPriceLibraryAuditReceive(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/support/oa/price_library_audit/receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/prepayment/receive
 */
export function supportOaPrepaymentReceive(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/prepayment/receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/pre_recieve
 */
export function supportOaPreRecieve(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/pre_recieve', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/pre_receive
 */
export function supportOaPreReceive(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/pre_receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/plan_receive
 */
export function supportOaPlanReceive(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/plan_receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/plan_recieve
 */
export function supportOaPlanRecieve(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/plan_recieve', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/permission_application_receive
 */
export function supportOaPermissionApplicationReceive(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/support/oa/permission_application_receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/permission_application_recieve
 */
export function supportOaPermissionApplicationRecieve(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/support/oa/permission_application_recieve', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/over_amount_recieve
 */
export function supportOaOverAmountRecieve(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/over_amount_recieve', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/over_amount_receive
 */
export function supportOaOverAmountReceive(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/over_amount_receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/niankuang_bidding_plan_receive
 */
export function supportOaNiankuangBiddingPlanReceive(
  data: string,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/support/oa/niankuang_bidding_plan_receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/kpi_adjustment/receive
 */
export function supportOaKpiAdjustmentReceive(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/kpi_adjustment/receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/fee_receive
 */
export function supportOaFeeReceive(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/fee_receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/fee_recieve
 */
export function supportOaFeeRecieve(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/fee_recieve', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/oa/budget_adjust/receive
 */
export function supportOaBudgetAdjustReceive(data: string, opts?: ReqOpts) {
  return request<ResponseObject>('/support/oa/budget_adjust/receive', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/mpc/demand/third/close
 */
export function demandOpenApiThirdDemandClose(
  data: DemandThirdCloseReq,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/support/mpc/demand/third/close', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/mpc/demand/bill/push
 */
export function demandOpenApiDemandBillPush(
  data: DemandBillPushReq,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/support/mpc/demand/bill/push', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/fee_control/payment_del/callback
 */
export function supportFeeControlPaymentDelCallback(
  data: FeeControlRequestParam,
  opts?: ReqOpts
) {
  return request<FeeControlResponseVoid>(
    '/support/fee_control/payment_del/callback',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/fee_control/callback
 */
export function supportFeeControlCallback(
  data: FeeControlRequestParam,
  opts?: ReqOpts
) {
  return request<FeeControlResponseVoid>('/support/fee_control/callback', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/closed_loop/demand/month/release
 */
export function closedLoopDemandOpenApiMonthRelease(
  data: ClosedLoopDemandMonthReleaseCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/support/closed_loop/demand/month/release', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/closed_loop/demand/fee_control/callback/status
 */
export function feeControlOpenApiFeeControlPaymentApprovedCallback(
  data: string,
  opts?: ReqOpts
) {
  return request<ExpResponseVoid>(
    '/support/closed_loop/demand/fee_control/callback/status',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/fee_control/callback/status
 */
export function feeControlOpenApiFeeControlPaymentApprovedCallbackUsingPost(
  data: string,
  opts?: ReqOpts
) {
  return request<ExpResponseVoid>('/support/fee_control/callback/status', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/closed_loop/demand/discard/forbid
 */
export function closedLoopDemandOpenApiClosedLoopDemandDetailDiscardForbid(
  data: ClosedLoopDemandDiscardForbidCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/support/closed_loop/demand/discard/forbid', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /support/closed_loop/demand/case/detail
 */
export function closedLoopDemandOpenApiGetClosedLoopDemandDetail(
  data: GetClosedLoopDemandDetailReq,
  opts?: ReqOpts
) {
  return request<ResponseOpenApiCloseLoopCaseDemandVo>(
    '/support/closed_loop/demand/case/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /support/closed_loop/demand/activity/end
 */
export function closedLoopDemandOpenApiActivityEnd(
  data: ClosedLoopDemandActivityEndCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/support/closed_loop/demand/activity/end', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 外部系统回调 /support/biz-task/callback
 */
export function bizTaskCallbackCallback(
  data: BizTaskCallbackForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/support/biz-task/callback', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /openapi/test/mock-submit-oa-archived2
 */
export function openapiTestMockSubmitOaArchived(
  data: OaNotifyDto,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/openapi/test/mock-submit-oa-archived2', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /openapi/common/trumpet-case/sync-groups
 */
export function openapiCommonTrumpetCaseSyncGroups(
  data: TrumpetSyncDataTrumpetAccountGroupDto,
  opts?: ReqOpts
) {
  return request<ResponseSyncDataResult>(
    '/openapi/common/trumpet-case/sync-groups',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/trumpet-supplier/sync-groups
 */
export function openapiCommonTrumpetCaseSyncGroupsUsingPost(
  data: TrumpetSyncDataTrumpetAccountGroupDto,
  opts?: ReqOpts
) {
  return request<ResponseSyncDataResult>(
    '/openapi/common/trumpet-supplier/sync-groups',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/trumpet-supplier/sync-certification
 */
export function openapiCommonTrumpetSupplierSyncCertification(
  data: TrumpetSyncDataTrumpetAccountCertificationDto,
  opts?: ReqOpts
) {
  return request<ResponseSyncDataResult>(
    '/openapi/common/trumpet-supplier/sync-certification',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/trumpet-supplier/sync-award
 */
export function openapiCommonTrumpetSupplierSyncAward(
  data: TrumpetSyncDataTrumpetAccountAwardDto,
  opts?: ReqOpts
) {
  return request<ResponseSyncDataResult>(
    '/openapi/common/trumpet-supplier/sync-award',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/trumpet-supplier/createOrUpdate
 */
export function openapiCommonTrumpetSupplierCreateOrUpdate(
  data: TrumpetAccountDto[],
  opts?: ReqOpts
) {
  return request<ResponseSyncDataResult>(
    '/openapi/common/trumpet-supplier/createOrUpdate',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/trumpet-case/submit_case_closure/pass
 */
export function openapiCommonPassSubmitCaseClosure(
  data: TrumpetCaseSubmitCaseClosurePassReq,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/openapi/common/trumpet-case/submit_case_closure/pass',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/trumpet-case/release
 */
export function openapiCommonHandleRelease(
  data: ReleaseDetailReq,
  opts?: ReqOpts
) {
  return request<ResponseBoolean>('/openapi/common/trumpet-case/release', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /openapi/common/trumpet-case/delete
 */
export function openapiCommonDelete(
  data: TrumpetCaseDeleteReq,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/openapi/common/trumpet-case/delete', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /openapi/common/trumpet-case/create
 */
export function openapiCommonCreate(
  data: TrumpetCaseCreateReq,
  opts?: ReqOpts
) {
  return request<ResponseTrumpetCaseCreateRes>(
    '/openapi/common/trumpet-case/create',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/trumpet-case/bill/handle
 */
export function openapiCommonRewardHandle(
  data: CaseBillHandleReq[],
  opts?: ReqOpts
) {
  return request<ResponseBoolean>('/openapi/common/trumpet-case/bill/handle', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /openapi/common/trumpet-activity/update
 */
export function openapiCommonUpdateTrumpetActivity(
  data: TrumpetActivityUpdateReq,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/openapi/common/trumpet-activity/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /openapi/common/trumpet-activity/standardLibrary
 */
export function openapiCommonQueryStandardLibrary(
  data: TrumpetPriceLibraryQueryReq,
  opts?: ReqOpts
) {
  return request<ResponseTrumpetStandardLibraryQueryRes>(
    '/openapi/common/trumpet-activity/standardLibrary',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/trumpet-activity/priceliberary
 */
export function openapiCommonQueryPriceLibrary(
  data: TrumpetPriceLibraryQueryReq,
  opts?: ReqOpts
) {
  return request<ResponseTrumpetPriceLibraryQueryRes>(
    '/openapi/common/trumpet-activity/priceliberary',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/split_plan_form
 */
export function openapiCommonSplitPlanForm(
  data: OpenapiSplitPlanFormReqDto,
  opts?: ReqOpts
) {
  return request<ResponseOpenapiSplitPlanFormRes>(
    '/openapi/common/split_plan_form',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/query-trumpet-activity/{activityOrderId}
 */
export function openapiCommonQueryTrumpetActivity(
  params: {
    activity_order_id: number;
  },
  opts?: ReqOpts
) {
  const { activity_order_id } = params;
  return request<ResponseTrumpetActivityVo>(
    `/openapi/common/query-trumpet-activity/${activity_order_id}`,
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /openapi/common/product_info/list_all_oa
 */
export function openapiCommonListAllProductInfoForOa(opts?: ReqOpts) {
  return request<ResponseProductInfoListRes>(
    '/openapi/common/product_info/list_all_oa',
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /openapi/common/product_info/list_all
 */
export function openapiCommonListAllProductInfo(opts?: ReqOpts) {
  return request<ResponseListProductInfoRes>(
    '/openapi/common/product_info/list_all',
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /openapi/common/filter_options
 */
export function openapiCommonFilterOptions(
  data: OpenapiCommonOptionsForm,
  opts?: ReqOpts
) {
  return request<ResponseListOpenapiOptionDetailWrapperVoOptionBase>(
    '/openapi/common/filter_options',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/demand_info
 */
export function openapiCommonGetDemandInfo(
  data: DemandInfoReqDto,
  opts?: ReqOpts
) {
  return request<ResponseDemandInfoRes>('/openapi/common/demand_info', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /openapi/common/content-market-system/work/kpi
 */
export function openapiCommonSyncWorkKpi(
  data: ContentMarketWorkKpiReq,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/openapi/common/content-market-system/work/kpi',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/content-market-system/work/exception
 */
export function openapiCommonSyncWorkException(
  data: ContentMarketWorkExceptionReq,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/openapi/common/content-market-system/work/exception',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/content-market-system/notify/uploadEffectData
 */
export function openapiCommonNotifyUploadEffectData(
  data: ContentMarketWorkNotifyReq,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/openapi/common/content-market-system/notify/uploadEffectData',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/content-market-system/notify/plannerPopoForSupplierKocWrong
 */
export function openapiCommonNotifyPlannerPopoForSupplierKocWrong(
  data: ContentMarketSupplierKocWrongNotify,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/openapi/common/content-market-system/notify/plannerPopoForSupplierKocWrong',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/content-market-system/notify/nativeAdvertising
 */
export function openapiCommonNotifyNativeAdvertising(
  data: ContentMarketWorkNotifyReq,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/openapi/common/content-market-system/notify/nativeAdvertising',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/content-market-system/notify/businessPopoForSupplierKocWrong
 */
export function openapiCommonNotifyBusinessPopoForSupplierKocWrong(
  data: ContentMarketSupplierKocWrongNotify,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/openapi/common/content-market-system/notify/businessPopoForSupplierKocWrong',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/content-market-system/demand/queryUserPermission
 */
export function openapiCommonQueryUserPermission(
  data: ContentMarketQueryUserPermissionReq,
  opts?: ReqOpts
) {
  return request<ResponseContentMarketQueryUserPermissionRes>(
    '/openapi/common/content-market-system/demand/queryUserPermission',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /openapi/common/assess_to_quote
 */
export function openapiCommonAssessToQuote(
  data: OpenapiAssessToQuoteReqDto,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/openapi/common/assess_to_quote', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【202502我的工作台】我的工作台-喇叭待办列表 /api/v2/workspace/trumpet/todo_list
 */
export function myWorkspaceGetTrumpetTodoList(
  data: WorkspaceTrumpetTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceTrumpetTodoVo>(
    '/api/v2/workspace/trumpet/todo_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-导出喇叭待办列表 /api/v2/workspace/trumpet/todo_list/export-demand-task
 */
export function myWorkspaceCreateExportTodoTrumpetTask(
  data: WorkspaceTrumpetTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponseString>(
    '/api/v2/workspace/trumpet/todo_list/export-demand-task',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-喇叭已办列表 /api/v2/workspace/trumpet/done_list
 */
export function myWorkspaceGetTrumpetDoneList(
  data: WorkspaceTrumpetDoneListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceTrumpetDoneVo>(
    '/api/v2/workspace/trumpet/done_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-喇叭全部列表 /api/v2/workspace/trumpet/all_list
 */
export function myWorkspaceGetTrumpetAllList(
  data: WorkspaceTrumpetAllListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceTrumpetAllVo>(
    '/api/v2/workspace/trumpet/all_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-喇叭全部列表 /api/v2/workspace/trumpet/all_list/export-demand-task
 */
export function myWorkspaceCreateExportAllTrumpetTask(
  data: WorkspaceTrumpetAllListForm,
  opts?: ReqOpts
) {
  return request<ResponseString>(
    '/api/v2/workspace/trumpet/all_list/export-demand-task',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/workspace/refresh_todo_user_data
 */
export function myWorkspaceRefreshTodoUserData(
  data: WorkbenchRefreshTodoUserDataForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/workspace/refresh_todo_user_data', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/workspace/refresh_role_details
 */
export function myWorkspaceRefreshRoleDetails(
  params: {
    role_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseVoid>(
    createUri('/api/v2/workspace/refresh_role_details', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 【我的工作台】预付流程-待办 /api/v2/workspace/prepayment/todo_list
 */
export function myWorkspaceGetPrepaymentTodoList(
  data: WorkspacePrepaymentTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspacePrepaymentListVo>(
    '/api/v2/workspace/prepayment/todo_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】预付流程-已办 /api/v2/workspace/prepayment/done_list
 */
export function myWorkspaceGetPrepaymentDoneList(
  data: WorkspacePrepaymentDoneListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspacePrepaymentListVo>(
    '/api/v2/workspace/prepayment/done_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】预付流程-全部 /api/v2/workspace/prepayment/all_list
 */
export function myWorkspaceGetPrepaymentAllList(
  data: WorkspacePrepaymentAllListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspacePrepaymentListVo>(
    '/api/v2/workspace/prepayment/all_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-小红点数量 /api/v2/workspace/menus_count
 */
export function myWorkspaceMenusCount(
  data: WorkbenchMenuCountForm,
  opts?: ReqOpts
) {
  return request<ResponseListMyWorkspaceMenuCountVo>(
    '/api/v2/workspace/menus_count',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】KPI调整单-待办 /api/v2/workspace/demand_kpi_adjust/todo_list
 */
export function myWorkspaceMyDemandKpiAdjustTodoList(
  data: WorkspaceDemandKpiAdjustTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceDemandKpiAdjustVo>(
    '/api/v2/workspace/demand_kpi_adjust/todo_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】KPI调整单-已办 /api/v2/workspace/demand_kpi_adjust/done_list
 */
export function myWorkspaceMyDemandKpiAdjustDoneList(
  data: WorkspaceDemandKpiAdjustDoneListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceDemandKpiAdjustVo>(
    '/api/v2/workspace/demand_kpi_adjust/done_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】KPI调整单-全部 /api/v2/workspace/demand_kpi_adjust/all_list
 */
export function myWorkspaceMyDemandKpiAdjustAllList(
  data: WorkspaceDemandKpiAdjustAllListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceDemandKpiAdjustVo>(
    '/api/v2/workspace/demand_kpi_adjust/all_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-常规采购待办列表 /api/v2/workspace/demand/todo_list
 */
export function myWorkspaceGetDemandTodoList(
  data: WorkspaceDemandTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceDemandTodoVo>(
    '/api/v2/workspace/demand/todo_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/workspace/demand/todo_list/export-demand-task
 */
export function myWorkspaceCreateExportTodoDemandTask(
  data: WorkspaceDemandTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponseString>(
    '/api/v2/workspace/demand/todo_list/export-demand-task',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-常规采购已办列表 /api/v2/workspace/demand/done_list
 */
export function myWorkspaceGetDemandDoneList(
  data: WorkspaceDemandDoneListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceDemandDoneVo>(
    '/api/v2/workspace/demand/done_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-常规采购全部列表 /api/v2/workspace/demand/all_list
 */
export function myWorkspaceGetDemandAllList(
  data: WorkspaceDemandAllListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceDemandAllVo>(
    '/api/v2/workspace/demand/all_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/workspace/demand/all_list/export-demand-task
 */
export function myWorkspaceCreateExportAllDemandTask(
  data: WorkspaceDemandAllListForm,
  opts?: ReqOpts
) {
  return request<ResponseString>(
    '/api/v2/workspace/demand/all_list/export-demand-task',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】营销闭环需求-待办 /api/v2/workspace/closed_loop/todo_list
 */
export function myWorkspaceMyClosedLoopTodoList(
  data: WorkspaceClosedLoopTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceClosedLoopListVo>(
    '/api/v2/workspace/closed_loop/todo_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】营销闭环需求-全部 /api/v2/workspace/closed_loop/my_list
 */
export function myWorkspaceClosedLoopMyList(
  data: WorkbenchClosedLoopForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceClosedLoopMyListVo>(
    '/api/v2/workspace/closed_loop/my_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-小红点数量 /api/v2/workspace/closed_loop/menus_count
 */
export function myWorkspaceClosedLoopMenusCount(
  data: WorkbenchMenuCountForm,
  opts?: ReqOpts
) {
  return request<ResponseListWorkspaceClosedLoopWorkspaceMenuCountVo>(
    '/api/v2/workspace/closed_loop/menus_count',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】营销闭环需求-全部 /api/v2/workspace/closed_loop/list
 */
export function myWorkspaceClosedLoopList(
  data: WorkbenchClosedLoopForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceClosedLoopListVo>(
    '/api/v2/workspace/closed_loop/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】营销闭环需求-已办 /api/v2/workspace/closed_loop/done_list
 */
export function myWorkspaceMyClosedLoopDoneList(
  data: WorkspaceClosedLoopDoneListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceClosedLoopListVo>(
    '/api/v2/workspace/closed_loop/done_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】营销闭环需求-全部 /api/v2/workspace/closed_loop/child_list
 */
export function myWorkspaceClosedLoopChildList(
  data: WorkspaceClosedLoopAllListForm,
  opts?: ReqOpts
) {
  return request<ResponseListWorkspaceClosedLoopMyListVo>(
    '/api/v2/workspace/closed_loop/child_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】营销闭环需求-全部 /api/v2/workspace/closed_loop/all_list
 */
export function myWorkspaceMyClosedLoopAllList(
  data: WorkbenchClosedLoopForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceClosedLoopListVo>(
    '/api/v2/workspace/closed_loop/all_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】预算调整单-待办 /api/v2/workspace/budget_adjust/todo_list
 */
export function myWorkspaceGetBudgetAdjustTodoList(
  data: WorkspaceBudgetAdjustTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceBudgetAdjustListVo>(
    '/api/v2/workspace/budget_adjust/todo_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】预算调整单-已办 /api/v2/workspace/budget_adjust/done_list
 */
export function myWorkspaceGetBudgetAdjustDoneList(
  data: WorkspaceBudgetAdjustDoneListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceBudgetAdjustListVo>(
    '/api/v2/workspace/budget_adjust/done_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【我的工作台】预算调整单-全部 /api/v2/workspace/budget_adjust/all_list
 */
export function myWorkspaceGetBudgetAdjustAllList(
  data: WorkspaceBudgetAdjustAllListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceBudgetAdjustListVo>(
    '/api/v2/workspace/budget_adjust/all_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-比选待办列表 /api/v2/workspace/bidding/todo_list
 */
export function myWorkspaceMyBiddingWorkspaceTodoList(
  data: MyWorkspaceBiddingTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingWorkspaceList>(
    '/api/v2/workspace/bidding/todo_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-比选已办列表 /api/v2/workspace/bidding/done_list
 */
export function myWorkspaceMyBiddingWorkspaceDoneList(
  data: MyWorkspaceBiddingDoneListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingWorkspaceList>(
    '/api/v2/workspace/bidding/done_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202502我的工作台】我的工作台-比选全部列表 /api/v2/workspace/bidding/all_list
 */
export function myWorkspaceMyBiddingWorkspaceAllList(
  data: MyWorkspaceBiddingAllListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingWorkspaceList>(
    '/api/v2/workspace/bidding/all_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 小喇叭菜单列表红点 /api/v2/workbenches/trumpet/menus_count
 */
export function trumpetWorkBenchMenusCount(
  data: WorkbenchMenuCountForm,
  opts?: ReqOpts
) {
  return request<ResponseListWorkbenchMenuCountFormDto>(
    '/api/v2/workbenches/trumpet/menus_count',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 小喇叭列表 /api/v2/workbenches/trumpet/list
 */
export function trumpetWorkBenchTrumpetList(
  data: TrumpetWorkbenchForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultObject>('/api/v2/workbenches/trumpet/list', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 小喇叭子列表 /api/v2/workbenches/trumpet/child_list
 */
export function trumpetWorkBenchChildList(
  data: TrumpetChildForm,
  opts?: ReqOpts
) {
  return request<ResponseListTrumpetMyDemandListVo>(
    '/api/v2/workbenches/trumpet/child_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/workbenches/menus_count
 */
export function workbenchesMenusCount(
  data: WorkbenchMenuCountForm,
  opts?: ReqOpts
) {
  return request<ResponseListWorkbenchMenuCountFormDto>(
    '/api/v2/workbenches/menus_count',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/workbenches/list
 */
export function workbenchesList(data: WorkbenchForm, opts?: ReqOpts) {
  return request<ResponsePageResultWorkbenches>('/api/v2/workbenches/list', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/workbenches/child_list
 */
export function workbenchesChildList(data: ChildForm, opts?: ReqOpts) {
  return request<ResponseListWorkbenches>('/api/v2/workbenches/child_list', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/woodpecker/import
 */
export function woodpeckerImportWood(
  data?: GenericFormData<{
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  return request<ResponseMapObjectObject>('/api/v2/woodpecker/import', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/trumpet/demand/{id}/submit_loan_info
 */
export function trumpetDemandSubmitLoanInfo(
  params: {
    id: number;
  },
  data: TrumpetLoanInfoForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<Response>(`/api/v2/trumpet/demand/${id}/submit_loan_info`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/trumpet/demand/{id}/split
 */
export function trumpetDemandSplit(
  params: {
    id: number;
  },
  data: SplitTrumpetDemandForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseSplitTrumpetDemandVo>(
    `/api/v2/trumpet/demand/${id}/split`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/trumpet/demand/{id}/planner_edit
 */
export function trumpetDemandPlannerEdit(
  params: {
    id: number;
  },
  data: PlannerEditTrumpetDemandForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseVoid>(`/api/v2/trumpet/demand/${id}/planner_edit`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/trumpet/demand/{id}/edit
 */
export function trumpetDemandEdit(
  params: {
    id: number;
  },
  data: EditTrumpetDemandForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<Response>(`/api/v2/trumpet/demand/${id}/edit`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/trumpet/demand/{id}/editPlanner
 */
export function trumpetDemandEditPlanner(
  params: {
    id: number;
  },
  data: EditTrumpetDemandPlannerForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseVoid>(`/api/v2/trumpet/demand/${id}/editPlanner`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/trumpet/demand/{id}/close
 */
export function trumpetDemandClose(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseTrumpetMoveVo>(`/api/v2/trumpet/demand/${id}/close`, {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/v2/trumpet/demand/{id}/approval/reject
 */
export function trumpetDemandApprovalReject(
  params: {
    id: number;
  },
  data: TrumpetApprovalForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseTrumpetMoveVo>(
    `/api/v2/trumpet/demand/${id}/approval/reject`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/trumpet/demand/{id}/approval/pass
 */
export function trumpetDemandApprovalPass(
  params: {
    id: number;
  },
  data: TrumpetApprovalForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseTrumpetMoveVo>(
    `/api/v2/trumpet/demand/${id}/approval/pass`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/trumpet/demand/{id}/add_budget
 */
export function trumpetDemandAddBudget(
  params: {
    id: number;
  },
  data: AddBudgetForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseAddBudgetResultVo>(
    `/api/v2/trumpet/demand/${id}/add_budget`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/trumpet/demand/create
 */
export function trumpetDemandCreate(
  data: CreateTrumpetDemandForm,
  opts?: ReqOpts
) {
  return request<ResponseCreateTrumpetDemandVo>(
    '/api/v2/trumpet/demand/create',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 小喇叭三级单关闭 /api/v2/trumpet/case/{id}/close
 */
export function trumpetCaseClose(
  params: {
    id: number;
  },
  data: DeleteCaseForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseTrumpetMoveVo>(`/api/v2/trumpet/case/${id}/close`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 小喇叭三级单审批拒绝 /api/v2/trumpet/case/{id}/approval/reject
 */
export function trumpetCaseApprovalReject(
  params: {
    id: number;
  },
  data: TrumpetApprovalForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseTrumpetMoveVo>(
    `/api/v2/trumpet/case/${id}/approval/reject`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 小喇叭三级单审批通过 /api/v2/trumpet/case/{id}/approval/pass
 */
export function trumpetCaseApprovalPass(
  params: {
    id: number;
  },
  data: TrumpetApprovalForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseTrumpetMoveVo>(
    `/api/v2/trumpet/case/${id}/approval/pass`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/trumpet/activity/{id}/edit_activity_budget
 */
export function trumpetActivityEditActivityBudget(
  params: {
    id: number;
  },
  data: EditActivityBudgetForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<Response>(
    `/api/v2/trumpet/activity/${id}/edit_activity_budget`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/trumpet/activity/{id}/edit
 */
export function trumpetActivityEdit(
  params: {
    id: number;
  },
  data: EditTrumpetActivityForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<Response>(`/api/v2/trumpet/activity/${id}/edit`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/trumpet/activity/{id}/delete
 */
export function trumpetActivityDelete(
  params: {
    id: number;
  },
  data: DeleteActivityForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<Response>(`/api/v2/trumpet/activity/${id}/delete`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 营销方案OA审批主体结构查询(多次提交OA) /api/v2/third-part/oa-query/plan-oa-record-simple
 */
export function oaQueryGetOaProcessDescForPlan(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListOaProcessDescVo>(
    createUri('/api/v2/third-part/oa-query/plan-oa-record-simple', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 其他OA审批主体结构查询(多次提交OA) /api/v2/third-part/oa-query/oa-record-simple
 */
export function oaQueryGetOaProcessDesc(
  params: {
    approve_type?: ApproveTypeEnum;
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListOaProcessDescVo>(
    createUri('/api/v2/third-part/oa-query/oa-record-simple', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 营销方案OA审批详情查询 /api/v2/third-part/oa-query/oa-process-plan
 */
export function oaQueryQueryPlanOaProcessDetail(
  params: {
    oa_id: string;
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseOaProcessDetailVo>(
    createUri('/api/v2/third-part/oa-query/oa-process-plan', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 其他OA审批详情查询 /api/v2/third-part/oa-query/oa-process-id
 */
export function oaQueryQueryOaProcessDetail(
  params: {
    oa_id: string;
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseOaProcessDetailVo>(
    createUri('/api/v2/third-part/oa-query/oa-process-id', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 查询需求单关联的OA流程信息 /api/v2/third-part/oa-query/oa-process-detail
 */
export function oaQueryGetOaProcessDetail(
  params: {
    demand_type: MpcDemandTypeEnum;
    approve_type: ApproveTypeEnum;
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListOaProcessDescVo>(
    createUri('/api/v2/third-part/oa-query/oa-process-detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 查询OA审批详情 /api/v2/third-part/oa-query/oa-process-approve-detail
 */
export function oaQueryQueryOaProcessApproveDetail(
  params: {
    demand_type: MpcDemandTypeEnum;
    approve_type: ApproveTypeEnum;
    demand_id: number;
    oa_id: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseOaProcessDetailVo>(
    createUri('/api/v2/third-part/oa-query/oa-process-approve-detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 比选单OA审批主体结构查询 /api/v2/third-part/oa-query/bidding-oa-record-simple
 */
export function oaQueryGetBiddingOaProcessDesc(
  params: {
    approve_type?: ApproveTypeEnum;
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListOaProcessDescVo>(
    createUri('/api/v2/third-part/oa-query/bidding-oa-record-simple', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 比选OA审批详情查询 /api/v2/third-part/oa-query/bidding-oa-process-id
 */
export function oaQueryQueryBiddingOaProcessDetail(
  params: {
    oa_id: string;
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseOaProcessDetailVo>(
    createUri('/api/v2/third-part/oa-query/bidding-oa-process-id', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/test/test
 */
export function testIndex(data: DemandMoveDto, opts?: ReqOpts) {
  return request<Response>('/api/v2/test/test', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/test/testQuerySupplierQuote
 */
export function testTestQuerySupplierQuote(data: number[], opts?: ReqOpts) {
  return request<ResponseListQuerySupplierQuoteDto>(
    '/api/v2/test/testQuerySupplierQuote',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/test/testPushFeeProvisionSchedule
 */
export function testTestPushFeeProvisionSchedule(data: string, opts?: ReqOpts) {
  return request<ResponseVoid>('/api/v2/test/testPushFeeProvisionSchedule', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/test/test/sendQcReq
 */
export function testSendQcReq(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<Response>(createUri('/api/v2/test/test/sendQcReq', query), {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/v2/test/test/qcIntelligentReviewService
 */
export function testTestQc(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandAiReviewResultDto>(
    createUri('/api/v2/test/test/qcIntelligentReviewService', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/test/test/firstDemandDelete
 */
export function testFirstDemandDelete(
  data: FeeControlFirstDemandDeleteCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/test/test/firstDemandDelete', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/test/sendRemainderImmediately
 */
export function testSendRemainderImmediately(
  params: {
    kpi_adjustment_id: number;
    user_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseVoid>(
    createUri('/api/v2/test/sendRemainderImmediately', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/test/notify/test
 */
export function testNotifyTest(data: NotifyTestDto, opts?: ReqOpts) {
  return request<Response>('/api/v2/test/notify/test', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/test/createKpiAdjustmentApprovalProcess
 */
export function testCreateKpiAdjustmentApprovalProcess(
  params: {
    order_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<Response>(
    createUri('/api/v2/test/createKpiAdjustmentApprovalProcess', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/temporary/select
 */
export function demandCommonTemporarySelectDemand(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(createUri('/api/v2/temporary/select', query), {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/v2/temporary/save
 */
export function demandCommonTemporarySaveDemand(data: string, opts?: ReqOpts) {
  return request<ResponseString>('/api/v2/temporary/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier_tag/tag/swift_status
 */
export function supplierTagSwiftTagStatus(
  data: SwiftTagCommand,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/supplier_tag/tag/swift_status', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier_tag/tag/list
 */
export function supplierTagListTags(data: ListTagCommand, opts?: ReqOpts) {
  return request<ResponseTagListPageVoSuppliersTagDto>(
    '/api/v2/supplier_tag/tag/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/supplier_tag/tag/edit
 */
export function supplierTagEditTag(data: EditTagCommand, opts?: ReqOpts) {
  return request<Response>('/api/v2/supplier_tag/tag/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier_tag/tag/delete
 */
export function supplierTagDeleteTag(
  params: {
    tag_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<Response>(
    createUri('/api/v2/supplier_tag/tag/delete', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/supplier_tag/tag/create
 */
export function supplierTagCreateTag(data: CreateTagCommand, opts?: ReqOpts) {
  return request<Response>('/api/v2/supplier_tag/tag/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier_tag/cat/edit
 */
export function supplierTagEditCat(data: EditTagCatCommand, opts?: ReqOpts) {
  return request<Response>('/api/v2/supplier_tag/cat/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier_tag/cat/delete
 */
export function supplierTagDeleteCat(
  params: {
    cat_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<Response>(
    createUri('/api/v2/supplier_tag/cat/delete', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/supplier_tag/cat/create
 */
export function supplierTagCreateCat(
  data: CreateTagCatCommand,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/supplier_tag/cat/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 新增供应商使用限制配置 /api/v2/supplier/usage-limit/config
 */
export function supplierUsageLimitConfigSave(
  data: SupplierUsageLimitConfigForm,
  opts?: ReqOpts
) {
  return request<ResponseLong>('/api/v2/supplier/usage-limit/config', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 分页查询供应商使用限制配置列表 /api/v2/supplier/usage-limit/config/page
 */
export function supplierUsageLimitConfigPage(
  data: SupplierUsageLimitConfigPageForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultSupplierUsageLimitConfigVo>(
    '/api/v2/supplier/usage-limit/config/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/supplier/save
 */
export function supplierSave(data: Supplier, opts?: ReqOpts) {
  return request<ResponseString>('/api/v2/supplier/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 提交调研反馈 /api/v2/supplier/recommendation/feedback
 */
export function supplierRecommendationSubmitFeedback(
  data: SubmitFeedbackCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/supplier/recommendation/feedback', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier/list
 */
export function supplierList(data?: SupplierManageListForm, opts?: ReqOpts) {
  return request<Response>('/api/v2/supplier/list', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier/import
 */
export function supplierImportSupplier(
  data?: GenericFormData<{
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  return request<ResponseMapObjectObject>('/api/v2/supplier/import', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier/importRewritePstAndSendEmail
 */
export function supplierImportRewritePstAndSendEmail(
  data?: GenericFormData<{
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  return request<ResponseMapObjectObject>(
    '/api/v2/supplier/importRewritePstAndSendEmail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/supplier/importEasy
 */
export function supplierImportEasySupplier(
  data?: GenericFormData<{
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  return request<ResponseMapObjectObject>('/api/v2/supplier/importEasy', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier/filter_options
 */
export function supplierFilterOptions(
  data?: SupplierManageListForm,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/supplier/filter_options', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier/export
 */
export function supplierExportSupplier(
  data: SupplierExportReq,
  opts?: ReqOpts
) {
  return request<unknown>('/api/v2/supplier/export', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier/discard
 */
export function supplierDiscard(
  data: {
    [key: string]: any;
  },
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/supplier/discard', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier/detail
 */
export function supplierShow(data: object, opts?: ReqOpts) {
  return request<ResponseSupplier>('/api/v2/supplier/detail', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier/audit
 */
export function supplierAudit(data: SupplieAuditDto, opts?: ReqOpts) {
  return request<ResponseString>('/api/v2/supplier/audit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【20240620版本】创建供应商 /api/v2/supplier-new
 */
export function supplierControllerNewCreateFull(
  data: CreateSupplierForm,
  opts?: ReqOpts
) {
  return request<ResponseChannelSupplierDetailVo>('/api/v2/supplier-new', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【供应商主体维度】新增供应商主体关联信息（支持多条） /api/v2/supplier-new/{mainSupplierId}/supplier-link
 */
export function supplierControllerNewAddSupplierLink(
  params: {
    main_supplier_id: number;
  },
  data: SupplierLinkInfo[],
  opts?: ReqOpts
) {
  const { main_supplier_id } = params;
  return request<ResponseVoid>(
    `/api/v2/supplier-new/${main_supplier_id}/supplier-link`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【月度/年度等金额】查询渠道供应商年度金额 /api/v2/supplier-new/{mainSupplierId}/query-yearly-total-amount
 */
export function supplierControllerNewQueryYearlyTotalAmount(
  params: {
    main_supplier_id: number;
  },
  data: QueryChannelSupplierTotalAmountForm,
  opts?: ReqOpts
) {
  const { main_supplier_id } = params;
  return request<ResponsePageVoChannelSupplierTotalAmountRecordVo>(
    `/api/v2/supplier-new/${main_supplier_id}/query-yearly-total-amount`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【月度/年度等金额】查询渠道供应商月度金额 /api/v2/supplier-new/{mainSupplierId}/query-monthly-total-amount
 */
export function supplierControllerNewQueryMonthlyTotalAmount(
  params: {
    main_supplier_id: number;
  },
  data: QueryChannelSupplierTotalAmountForm,
  opts?: ReqOpts
) {
  const { main_supplier_id } = params;
  return request<ResponsePageVoChannelSupplierTotalAmountRecordVo>(
    `/api/v2/supplier-new/${main_supplier_id}/query-monthly-total-amount`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询主供应商需求 /api/v2/supplier-new/{mainSupplierId}/query-demand
 */
export function supplierControllerNewQueryDemand(
  params: {
    main_supplier_id: number;
  },
  data: QueryCsDemandForm,
  opts?: ReqOpts
) {
  const { main_supplier_id } = params;
  return request<ResponsePageWithExtInfoVoChannelSupplierDemandVoChannelSupplierQueryTotalResultVo>(
    `/api/v2/supplier-new/${main_supplier_id}/query-demand`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【供应商综合评分】查询渠道供应商需求汇总 /api/v2/supplier-new/{mainSupplierId}/query-demand-summary
 */
export function supplierControllerNewQueryDemandSummary(
  params: {
    main_supplier_id: number;
  },
  opts?: ReqOpts
) {
  const { main_supplier_id } = params;
  return request<ResponseChannelSupplierDemandSummaryVo>(
    `/api/v2/supplier-new/${main_supplier_id}/query-demand-summary`,
    { method: 'POST', ...opts }
  );
}
/**
 * 【供应商详情页优化2.0&供应商列表优化】主供应商维度查询必选单 /api/v2/supplier-new/{mainSupplierId}/query-bidding
 */
export function supplierControllerNewQueryBidding(
  params: {
    main_supplier_id: number;
  },
  data: QueryCsBiddingForm,
  opts?: ReqOpts
) {
  const { main_supplier_id } = params;
  return request<ResponsePageWithExtInfoVoChannelSupplierBiddingVoChannelSupplierBiddingTotalResultVo>(
    `/api/v2/supplier-new/${main_supplier_id}/query-bidding`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 创建查询供应商需求导出需求任务 /api/v2/supplier-new/{mainSupplierId}/export-demand-task
 */
export function supplierControllerNewCreateExportDemandTask(
  params: {
    main_supplier_id: number;
  },
  data: QueryCsDemandForm,
  opts?: ReqOpts
) {
  const { main_supplier_id } = params;
  return request<ResponseString>(
    `/api/v2/supplier-new/${main_supplier_id}/export-demand-task`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 创建比选导出需求任务 /api/v2/supplier-new/{mainSupplierId}/export-bidding-task
 */
export function supplierControllerNewCreateExportBiddingTask(
  params: {
    main_supplier_id: number;
  },
  data: QueryCsBiddingForm,
  opts?: ReqOpts
) {
  const { main_supplier_id } = params;
  return request<ResponseString>(
    `/api/v2/supplier-new/${main_supplier_id}/export-bidding-task`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 更新渠道供应商标签 /api/v2/supplier-new/{id}/tags
 */
export function supplierControllerNewUpdateTags(
  params: {
    id: number;
  },
  data: UpdateChannelSupplierTagsForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(`/api/v2/supplier-new/${id}/tags`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 提交审核 /api/v2/supplier-new/{id}/submit-approval
 */
export function supplierControllerNewSubmit(
  params: {
    id: number;
  },
  data?: SubmitChannelSupplierForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseObject>(`/api/v2/supplier-new/${id}/submit-approval`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【供应商综合评分】查询供应商主要服务产品信息 /api/v2/supplier-new/{id}/query-supplier-product
 */
export function supplierControllerNewQuerySupplierProduct(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseListProductDemandVo>(
    `/api/v2/supplier-new/${id}/query-supplier-product`,
    { method: 'POST', ...opts }
  );
}
/**
 * 【供应商综合评分】供应商近期综合评分更新记录 /api/v2/supplier-new/{id}/query-demand-overall-score-recent-update-list
 */
export function supplierControllerNewQueryDemandOverallScoreRecentUpdateList(
  params: {
    id: number;
  },
  data: QueryDemandOverallScoreUpdateListForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponsePageVoOverallScoreUpdateRecordVo>(
    `/api/v2/supplier-new/${id}/query-demand-overall-score-recent-update-list`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【供应商综合评分】供应商近期综合评分更新详情 /api/v2/supplier-new/{id}/query-demand-overall-score-recent-update-detail
 */
export function supplierControllerNewQueryDemandOverallScoreRecentUpdateDetail(
  params: {
    id: number;
  },
  data: QueryDemandOverallScoreUpdateDetailForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponsePageVoOverallScoreUpdateDetailVo>(
    `/api/v2/supplier-new/${id}/query-demand-overall-score-recent-update-detail`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【供应商综合评分】供应商历史综合评分更新记录 /api/v2/supplier-new/{id}/query-demand-overall-score-all-update-list
 */
export function supplierControllerNewQueryDemandOverallScoreAllUpdateList(
  params: {
    id: number;
  },
  data: QueryDemandOverallScoreUpdateListForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponsePageVoOverallScoreUpdateRecordVo>(
    `/api/v2/supplier-new/${id}/query-demand-overall-score-all-update-list`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【供应商综合评分】供应商历史综合评分更新详情 /api/v2/supplier-new/{id}/query-demand-overall-score-all-update-detail
 */
export function supplierControllerNewQueryDemandOverallScoreAllUpdateDetail(
  params: {
    id: number;
  },
  data: QueryDemandOverallScoreUpdateDetailForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponsePageVoOverallScoreUpdateDetailVo>(
    `/api/v2/supplier-new/${id}/query-demand-overall-score-all-update-detail`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【20241017供应商升降级】变更供应商类型 /api/v2/supplier-new/{id}/change-channel-type
 */
export function supplierControllerNewChangeChannelType(
  params: {
    id: number;
  },
  data: ChangeChannelSupplierTypeForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<Blob>(`/api/v2/supplier-new/${id}/change-channel-type`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【20241017供应商升降级】变更供应商类型 /api/v2/supplier-new/{id}/change-channel-supplier-type
 */
export function supplierControllerNewChangeChannelTypeUsingPost(
  params: {
    id: number;
  },
  data: ChangeChannelSupplierTypeForm,
  opts?: ReqOpts
) {
  const { id } = params;
  return request<Blob>(
    `/api/v2/supplier-new/${id}/change-channel-supplier-type`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/supplier-new/{approval_id}/approval-return
 */
export function supplierControllerNewApprovalReturn(
  params: {
    approval_id: string;
    the_approval_id?: string;
  },
  data: ChannelSupplierApprovalReturnForm,
  opts?: ReqOpts
) {
  const { approval_id, ...query } = params;
  return request<ResponseObject>(
    createUri(`/api/v2/supplier-new/${approval_id}/approval-return`, query),
    { method: 'POST', data, ...opts }
  );
}
/**
 * 审批拒绝/禁入 /api/v2/supplier-new/{approval_id}/approval-reject
 */
export function supplierControllerNewApprovalReject(
  params: {
    approval_id: string;
    the_approval_id?: string;
  },
  data: ChannelSupplierApprovalRejectForm,
  opts?: ReqOpts
) {
  const { approval_id, ...query } = params;
  return request<ResponseObject>(
    createUri(`/api/v2/supplier-new/${approval_id}/approval-reject`, query),
    { method: 'POST', data, ...opts }
  );
}
/**
 * 审批通过 /api/v2/supplier-new/{approval_id}/approval-pass
 */
export function supplierControllerNewApprovalPass(
  params: {
    approval_id: string;
    the_approval_id?: string;
  },
  data: ChannelSupplierApprovalPassForm,
  opts?: ReqOpts
) {
  const { approval_id, ...query } = params;
  return request<ResponseObject>(
    createUri(`/api/v2/supplier-new/${approval_id}/approval-pass`, query),
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/supplier-new/update-vmdm-bank-account
 */
export function supplierControllerNewUpdateVmdmBankAccount(
  data: UpdateVmdmBankCommand,
  opts?: ReqOpts
) {
  return request<ResponseVmdmSupplierBankVo>(
    '/api/v2/supplier-new/update-vmdm-bank-account',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202501供应商入口调整】供应商管理-我的发起列表 /api/v2/supplier-new/supplier-approval/query-my-submitted-list
 */
export function supplierControllerNewQueryMySubmittedList(
  data: QueryMySubmittedCsForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoMySubmittedCsVo>(
    '/api/v2/supplier-new/supplier-approval/query-my-submitted-list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202501供应商入口调整】供应商管理-我的已办列表 /api/v2/supplier-new/supplier-approval/query-my-approved-list
 */
export function supplierControllerNewQueryMyApprovedList(
  data: QueryMyApprovalCsForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoMyApprovalCsVo>(
    '/api/v2/supplier-new/supplier-approval/query-my-approved-list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/supplier-new/simpleDemand
 */
export function supplierControllerNewSimpleDemand(
  params: {
    supplier_id: number;
    page: number;
    page_size: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListDemandSimpleVo>(
    createUri('/api/v2/supplier-new/simpleDemand', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/supplier-new/searchScoreItems
 */
export function supplierControllerNewSearchScoreItems(
  params: {
    demand_id: number;
    type: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandEvaluateScoreDescVo>(
    createUri('/api/v2/supplier-new/searchScoreItems', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 【供应商主体维度】筛选查询-已入库供应商列表 /api/v2/supplier-new/query-valid-supplier
 */
export function supplierControllerNewQueryValidSupplier(
  data: QueryValidSupplierForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoValidSupplierVo>(
    '/api/v2/supplier-new/query-valid-supplier',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【20240620版本】筛选查询-已入库渠道供应商列表 /api/v2/supplier-new/query-valid-channel-supplier
 */
export function supplierControllerNewQueryValidChannelSupplier(
  data: QueryValidCsForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoValidChannelSupplierVo>(
    '/api/v2/supplier-new/query-valid-channel-supplier',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询奖励列表 /api/v2/supplier-new/query-supplier-trumpet-aci-list
 */
export function supplierControllerNewQuerySupplierTrumpetAccountIncome(
  data: QuerySupplierTrumpetAccountInComeForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoSupplierTrumpetAccountIncomeVo>(
    '/api/v2/supplier-new/query-supplier-trumpet-aci-list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询团列表 /api/v2/supplier-new/query-supplier-trumpet-acg-list
 */
export function supplierControllerNewQuerySupplierTrumpetAccountGroup(
  data: QuerySupplierTrumpetAgrForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoSupplierTrumpetAccountGroupVo>(
    '/api/v2/supplier-new/query-supplier-trumpet-acg-list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202501供应商入口调整】筛选查询 入库审核中 渠道供应商列表 /api/v2/supplier-new/query-pending-review-storage-supplier
 */
export function supplierControllerNewQueryPendingReviewStorageSupplier(
  data: QueryNotInStorageCsForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoNotInStorageCsVo>(
    '/api/v2/supplier-new/query-pending-review-storage-supplier',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202501供应商入口调整】筛选查询-待审核列表 /api/v2/supplier-new/query-pending-approval-list
 */
export function supplierControllerNewQueryApprovalList(
  data: QueryApprovalPendingCsForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoApprovalPendingCsVo>(
    '/api/v2/supplier-new/query-pending-approval-list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202501供应商入口调整】筛选查询 出库/黑名单 渠道供应商列表 /api/v2/supplier-new/query-not-in-storage-supplier
 */
export function supplierControllerNewQueryNotInStorageSupplier(
  data: QueryNotInStorageCsForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoNotInStorageCsVo>(
    '/api/v2/supplier-new/query-not-in-storage-supplier',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询个人供应商列表 /api/v2/supplier-new/query-individual-supplier-list
 */
export function supplierControllerNewQueryIndividualSupplierList(
  data: QueryIndividualSupplierForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoIndividualSupplierVo>(
    '/api/v2/supplier-new/query-individual-supplier-list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 根据cs渠道供应商id,查询个人供应商详情 /api/v2/supplier-new/query-individual-supplier-detail/{id}
 */
export function supplierControllerNewQueryIndividualSupplierDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseIndividualSupplierDetailVo>(
    `/api/v2/supplier-new/query-individual-supplier-detail/${id}`,
    { method: 'POST', ...opts }
  );
}
/**
 * 供应商详情-审核记录,支持主供应商或者单个渠道供应商 /api/v2/supplier-new/query-cs-approval-record-list
 */
export function supplierControllerNewQueryCsApprovalRecordList(
  data: QueryChannelSupplierApprovalRecordForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoChannelSupplierApprovalRecordVo>(
    '/api/v2/supplier-new/query-cs-approval-record-list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 供应商菜单列表红点 /api/v2/supplier-new/menus_count
 */
export function supplierControllerNewMenusCount(
  data: WorkbenchMenuCountForm,
  opts?: ReqOpts
) {
  return request<ResponseListWorkbenchMenuCountFormDto>(
    '/api/v2/supplier-new/menus_count',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 创建主供应商 /api/v2/supplier-new/main-supplier/create
 */
export function mainSupplierCreateMainSupplier(
  data: CreateMainSupplierForm,
  opts?: ReqOpts
) {
  return request<ResponseLong>('/api/v2/supplier-new/main-supplier/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 创建数据导入任务 /api/v2/supplier-new/data-import-task/create-import-task
 */
export function channelSupplierImportCreateImportTask(
  data: CreateImportTaskForm,
  opts?: ReqOpts
) {
  return request<ResponseString>(
    '/api/v2/supplier-new/data-import-task/create-import-task',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 渠道供应商查询需求 /api/v2/supplier-new/cs/{channelSupplierId}/query-demand
 */
export function supplierControllerNewCsQueryDemand(
  params: {
    channel_supplier_id: number;
  },
  data: QueryCsDemandForm,
  opts?: ReqOpts
) {
  const { channel_supplier_id } = params;
  return request<ResponsePageWithExtInfoVoChannelSupplierDemandVoChannelSupplierQueryTotalResultVo>(
    `/api/v2/supplier-new/cs/${channel_supplier_id}/query-demand`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 渠道供应商需求汇总 /api/v2/supplier-new/cs/{channelSupplierId}/query-demand-summary
 */
export function supplierControllerNewCsQueryDemandSummary(
  params: {
    channel_supplier_id: number;
  },
  opts?: ReqOpts
) {
  const { channel_supplier_id } = params;
  return request<ResponseChannelSupplierDemandSummaryVo>(
    `/api/v2/supplier-new/cs/${channel_supplier_id}/query-demand-summary`,
    { method: 'POST', ...opts }
  );
}
/**
 * 渠道供应商维度查询必选单 /api/v2/supplier-new/cs/{channelSupplierId}/query-bidding
 */
export function supplierControllerNewCsQueryBidding(
  params: {
    channel_supplier_id: number;
  },
  data: QueryCsBiddingForm,
  opts?: ReqOpts
) {
  const { channel_supplier_id } = params;
  return request<ResponsePageWithExtInfoVoChannelSupplierBiddingVoChannelSupplierBiddingTotalResultVo>(
    `/api/v2/supplier-new/cs/${channel_supplier_id}/query-bidding`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 创建查询渠道供应商需求导出需求任务 /api/v2/supplier-new/cs/{channelSupplierId}/export-demand-task
 */
export function supplierControllerNewCreateExportDemandTaskByCs(
  params: {
    channel_supplier_id: number;
  },
  data: QueryCsDemandForm,
  opts?: ReqOpts
) {
  const { channel_supplier_id } = params;
  return request<ResponseString>(
    `/api/v2/supplier-new/cs/${channel_supplier_id}/export-demand-task`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 创建子供应商比选导出需求任务 /api/v2/supplier-new/cs/{channelSupplierId}/export-bidding-task
 */
export function supplierControllerNewCreateExportBiddingTaskByCs(
  params: {
    channel_supplier_id: number;
  },
  data: QueryCsBiddingForm,
  opts?: ReqOpts
) {
  const { channel_supplier_id } = params;
  return request<ResponseString>(
    `/api/v2/supplier-new/cs/${channel_supplier_id}/export-bidding-task`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【20240620版本】校验渠道供应商 /api/v2/supplier-new/check-supplier
 */
export function supplierControllerNewCheckSupplier(
  data: CheckChannelSupplierForm,
  opts?: ReqOpts
) {
  return request<ResponseCheckSupplierResultVo>(
    '/api/v2/supplier-new/check-supplier',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【20241017升降级】批量筛选查询-待审核列表计数 /api/v2/supplier-new/batch-query-pending-approval-list-count
 */
export function supplierControllerNewBatchQueryApprovalListCount(
  data: QueryApprovalPendingCsForm[],
  opts?: ReqOpts
) {
  return request<ResponseListInteger>(
    '/api/v2/supplier-new/batch-query-pending-approval-list-count',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 批量审批处理 /api/v2/supplier-new/batch-approval
 */
export function supplierControllerNewBatchApproval(
  data: BatchApprovalForm,
  opts?: ReqOpts
) {
  return request<ResponseBatchApprovalResultVo>(
    '/api/v2/supplier-new/batch-approval',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/supplier-new/approval-no/{approval_no}/approval-return
 */
export function supplierControllerNewApprovalReturnByApprovalNo(
  params: {
    approval_no: string;
  },
  data: ChannelSupplierApprovalReturnForm,
  opts?: ReqOpts
) {
  const { approval_no } = params;
  return request<ResponseObject>(
    `/api/v2/supplier-new/approval-no/${approval_no}/approval-return`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202501供应商入口调整】审批拒绝/禁入 /api/v2/supplier-new/approval-no/{approval_no}/approval-reject
 */
export function supplierControllerNewApprovalRejectByApprovalNo(
  params: {
    approval_no: string;
  },
  data: ChannelSupplierApprovalRejectForm,
  opts?: ReqOpts
) {
  const { approval_no } = params;
  return request<ResponseObject>(
    `/api/v2/supplier-new/approval-no/${approval_no}/approval-reject`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【202501供应商入口调整】审批通过 /api/v2/supplier-new/approval-no/{approval_no}/approval-pass
 */
export function supplierControllerNewApprovalPassByApprovalNo(
  params: {
    approval_no: string;
  },
  data: ChannelSupplierApprovalPassForm,
  opts?: ReqOpts
) {
  const { approval_no } = params;
  return request<ResponseObject>(
    `/api/v2/supplier-new/approval-no/${approval_no}/approval-pass`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/supplier-kpi/list
 */
export function supplierKpiList(
  data: SupplierKpiPageableQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultSupplierKpiSimpleVo>(
    '/api/v2/supplier-kpi/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/supplier-kpi/edit
 */
export function supplierKpiEdit(data: EditSupplierKpiForm, opts?: ReqOpts) {
  return request<ResponseVoid>('/api/v2/supplier-kpi/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/supplier-kpi/create
 */
export function supplierKpiCreate(data: CreateSupplierKpiForm, opts?: ReqOpts) {
  return request<ResponseLong>('/api/v2/supplier-kpi/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/submitOa/trumpetManualSubmit
 */
export function submitOaTrumpetManualSubmit(opts?: ReqOpts) {
  return request<ResponseInteger>('/api/v2/submitOa/trumpetManualSubmit', {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/v2/submitOa/manualSubmit
 */
export function submitOaManualSubmit(
  params: {
    group_type: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseObject>(
    createUri('/api/v2/submitOa/manualSubmit', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/submitOa/manualBatchSubmit
 */
export function submitOaManualBatchSubmit(
  data: ManualBatchSubmitParam,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v2/submitOa/manualBatchSubmit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/submitOa/create_trumpet_plan_approve
 */
export function submitOaCreateTrumpetPlanApprove(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseObject>(
    createUri('/api/v2/submitOa/create_trumpet_plan_approve', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/submitOa/create_plan_approve
 */
export function submitOaCreatePlanApprove(
  data: OaPlanApprovalForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v2/submitOa/create_plan_approve', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/submitOa/autoSubmitInfo
 */
export function submitOaSubmitOa(data: AutoSubmitOaParam, opts?: ReqOpts) {
  return request<ResponseListSubmitOaDto>('/api/v2/submitOa/autoSubmitInfo', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 编辑标品信息 /api/v2/standard/library/update
 */
export function v2StandardLibraryUpdate(
  data: StandardLibraryUpdateCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/standard/library/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 获取标品库列表 /api/v2/standard/library/list
 */
export function v2StandardLibraryList(
  data: StandardLibraryListReq,
  opts?: ReqOpts
) {
  return request<ResponsePageVoStandardLibraryDto>(
    '/api/v2/standard/library/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 单个新增标品 /api/v2/standard/library/create
 */
export function v2StandardLibraryCreate(
  data: StandardLibraryCreateOrUpdateCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/standard/library/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/solar/invoke
 */
export function solarInvoke(data: SolarInvokeRequest, opts?: ReqOpts) {
  return request<SolarInvokeResponse>('/api/v2/solar/invoke', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/sm-expense-budget/query
 */
export function v2SmExpenseBudgetQuery(
  data: SmExpenseBudgetQueryForm,
  opts?: ReqOpts
) {
  return request<ResponseSmExpenseBudgetQueryVo>(
    '/api/v2/sm-expense-budget/query',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 全量 Diff 保存标段供应商 /api/v2/segment_supplier/save
 */
export function v2SegmentSupplierSave(
  data: SegmentSupplierSaveForm,
  opts?: ReqOpts
) {
  return request<ResponseListSegmentSupplierVo>(
    '/api/v2/segment_supplier/save',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 新建/更新标段配置（id 为空则新建，否则更新） /api/v2/segment_config/save
 */
export function v2SegmentConfigSave(data: SegmentConfigForm, opts?: ReqOpts) {
  return request<ResponseLong>('/api/v2/segment_config/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 标段配置分页列表（多条件：类目名 / 标段编码模糊 / 状态） /api/v2/segment_config/page
 */
export function v2SegmentConfigPage(
  data: SegmentConfigQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageSegmentConfigPageVo>(
    '/api/v2/segment_config/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 新建/更新类目（id 为空则新建，否则更新） /api/v2/segment_category/save
 */
export function v2SegmentCategorySave(
  data: SegmentCategoryForm,
  opts?: ReqOpts
) {
  return request<ResponseLong>('/api/v2/segment_category/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 类目分页列表（多条件：类目名 / 标段编码模糊 / 状态） /api/v2/segment_category/page
 */
export function v2SegmentCategoryPage(
  data: SegmentCategoryQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageSegmentCategoryPageVo>(
    '/api/v2/segment_category/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询标段号列表（支持分页、类目ID精确匹配、标段号/字段值模糊搜索） /api/v2/segment/list
 */
export function v2SegmentList(data: SegmentListForm, opts?: ReqOpts) {
  return request<ResponsePageSegmentConfigPageVo>('/api/v2/segment/list', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/users/search_active_users
 */
export function v2RubyMigrateUsersSearchActiveUsers(
  data: SearchUserCmd,
  opts?: ReqOpts
) {
  return request<RubyResponseVoListSimpleUserVo>(
    '/api/v2/ruby_migrate/users/search_active_users',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/users/search
 */
export function v2RubyMigrateUsersSearch(data: SearchUserCmd, opts?: ReqOpts) {
  return request<RubyResponseVoListSimpleUserVo>(
    '/api/v2/ruby_migrate/users/search',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/users/save
 */
export function v2RubyMigrateUsersSave(data: EditUserCmd, opts?: ReqOpts) {
  return request<RubyResponseVoUserVo>('/api/v2/ruby_migrate/users/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/users/delete_role
 */
export function v2RubyMigrateUsersDeleteRole(
  data: DeleteRoleMemberCmd,
  opts?: ReqOpts
) {
  return request<RubyResponseVoVoid>('/api/v2/ruby_migrate/users/delete_role', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/users/delete_admin
 */
export function v2RubyMigrateUsersDeleteAdmin(
  data: SetUserAdminCmd,
  opts?: ReqOpts
) {
  return request<RubyResponseVoVoid>(
    '/api/v2/ruby_migrate/users/delete_admin',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/users/charge_product
 */
export function v2RubyMigrateUsersChargeProduct(
  data: EditUserChargeProductsCmd,
  opts?: ReqOpts
) {
  return request<RubyResponseVoUserChargeProductsVo>(
    '/api/v2/ruby_migrate/users/charge_product',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/users/batch_save
 */
export function v2RubyMigrateUsersBatchSave(
  data: BatchUpdateUserCmd,
  opts?: ReqOpts
) {
  return request<RubyResponseVoListUserVo>(
    '/api/v2/ruby_migrate/users/batch_save',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/users/add_admin
 */
export function v2RubyMigrateUsersAddAdmin(
  data: SetUserAdminCmd,
  opts?: ReqOpts
) {
  return request<RubyResponseVoVoid>('/api/v2/ruby_migrate/users/add_admin', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/suppliers/{action}
 */
export function rubyProxyOldJavaSupplierOther(
  params: {
    module: string;
    action: string;
  },
  data?: string,
  opts?: ReqOpts
) {
  const { module, action } = params;
  return request<RubyResponseVoMapStringObject>(
    `/api/v2/ruby_migrate/suppliers/${action}`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/{module}/suppliers/{action}
 */
export function rubyProxyOldJavaSupplierOtherUsingPost(
  params: {
    module: string;
    action: string;
  },
  data?: string,
  opts?: ReqOpts
) {
  const { module, action } = params;
  return request<RubyResponseVoMapStringObject>(
    `/api/v2/ruby_migrate/${module}/suppliers/${action}`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/suppliers/audit_list
 */
export function rubyProxyOldJavaSupplierAuditList(
  params: {
    module: string;
  },
  data?: string,
  opts?: ReqOpts
) {
  const { module } = params;
  return request<RubyResponseVoMapStringObject>(
    '/api/v2/ruby_migrate/suppliers/audit_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/{module}/suppliers/audit_list
 */
export function rubyProxyOldJavaSupplierAuditListUsingPost(
  params: {
    module: string;
  },
  data?: string,
  opts?: ReqOpts
) {
  const { module } = params;
  return request<RubyResponseVoMapStringObject>(
    `/api/v2/ruby_migrate/${module}/suppliers/audit_list`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/{module}/supplier_recommends/{action}
 */
export function rubyProxyOldJavaSupplierRecommends(
  params: {
    module: string;
    action: string;
  },
  data?: string,
  opts?: ReqOpts
) {
  const { module, action } = params;
  return request<RubyResponseVoMapStringObject>(
    `/api/v2/ruby_migrate/${module}/supplier_recommends/${action}`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/supplier_recommends/{action}
 */
export function rubyProxyOldJavaSupplierRecommendsUsingPost(
  params: {
    module: string;
    action: string;
  },
  data?: string,
  opts?: ReqOpts
) {
  const { module, action } = params;
  return request<RubyResponseVoMapStringObject>(
    `/api/v2/ruby_migrate/supplier_recommends/${action}`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/supplier_rankings/rankings
 */
export function rubyProxyOldJavaSupplierRankings(
  params: {
    module: string;
  },
  data?: string,
  opts?: ReqOpts
) {
  const { module } = params;
  return request<RubyResponseVoMapStringObject>(
    '/api/v2/ruby_migrate/supplier_rankings/rankings',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/{module}/supplier_rankings/rankings
 */
export function rubyProxyOldJavaSupplierRankingsUsingPost(
  params: {
    module: string;
  },
  data?: string,
  opts?: ReqOpts
) {
  const { module } = params;
  return request<RubyResponseVoMapStringObject>(
    `/api/v2/ruby_migrate/${module}/supplier_rankings/rankings`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/roles
 */
export function v2RubyMigrateRoles(data: CreateRoleCmd, opts?: ReqOpts) {
  return request<RubyResponseVoRoleDetailVo>('/api/v2/ruby_migrate/roles', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/roles/member
 */
export function v2RubyMigrateRolesMember(
  data: RoleMemberQuery,
  opts?: ReqOpts
) {
  return request<RubyResponseVoPageVoRoleMemberVo>(
    '/api/v2/ruby_migrate/roles/member',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/roles/list
 */
export function v2RubyMigrateRolesList(data: RoleQuery, opts?: ReqOpts) {
  return request<RubyResponseVoListRoleVo>('/api/v2/ruby_migrate/roles/list', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/projects/fee_control_contract_list
 */
export function rubyFeeControlFeeControlContractList(
  data: ContractQueryForm,
  opts?: ReqOpts
) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/projects/fee_control_contract_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/fee_control_contract_list
 */
export function rubyFeeControlFeeControlContractListUsingPost(
  data: ContractQueryForm,
  opts?: ReqOpts
) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/fee_control_contract_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/products
 */
export function v2RubyMigrateProducts(data: SaveProductCmd, opts?: ReqOpts) {
  return request<RubyResponseVo>('/api/v2/ruby_migrate/products', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/products/list
 */
export function v2RubyMigrateProductsList(data: ProductQuery, opts?: ReqOpts) {
  return request<RubyResponseVoPageVoProductVo>(
    '/api/v2/ruby_migrate/products/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/products/is_deleted
 */
export function v2RubyMigrateProductsIsDeleted(
  data: DeleteProductCheckCmd,
  opts?: ReqOpts
) {
  return request<RubyResponseVoVoid>(
    '/api/v2/ruby_migrate/products/is_deleted',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/products/batch_update
 */
export function v2RubyMigrateProductsBatchUpdate(
  data: BatchUpdateProductStatusCmd,
  opts?: ReqOpts
) {
  return request<RubyResponseVoBatchUpdateProductStatusResultVo>(
    '/api/v2/ruby_migrate/products/batch_update',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/{module}/my_workbenches/{state}
 */
export function rubyProxyOldJavaMyWorkbenches(
  params: {
    module: string;
    state: string;
  },
  data?: string,
  opts?: ReqOpts
) {
  const { module, state } = params;
  return request<RubyResponseVoMapStringObject>(
    `/api/v2/ruby_migrate/${module}/my_workbenches/${state}`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/my_workbenches/{state}
 */
export function rubyProxyOldJavaMyWorkbenchesUsingPost(
  params: {
    module: string;
    state: string;
  },
  data?: string,
  opts?: ReqOpts
) {
  const { module, state } = params;
  return request<RubyResponseVoMapStringObject>(
    `/api/v2/ruby_migrate/my_workbenches/${state}`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/history
 */
export function rubyGlobalSearchHistory(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoListString>(
    '/api/v2/ruby_migrate/global_searches/history',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/mobile/global_searches/history
 */
export function rubyGlobalSearchHistoryUsingPost(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoListString>(
    '/api/v2/ruby_migrate/mobile/global_searches/history',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/mobile/global_searches/clear_history
 */
export function rubyGlobalSearchClearHistory(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/mobile/global_searches/clear_history',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/clear_history
 */
export function rubyGlobalSearchClearHistoryUsingPost(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/global_searches/clear_history',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/mobile/global_searches/all
 */
export function rubyGlobalSearchMobileAll(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/mobile/global_searches/all',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/groups/member
 */
export function v2RubyMigrateGroupsMember(data: UserQuery, opts?: ReqOpts) {
  return request<RubyResponseVoGroupMemberVo>(
    '/api/v2/ruby_migrate/groups/member',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/groups/list
 */
export function v2RubyMigrateGroupsList(data: ListGroupCmd, opts?: ReqOpts) {
  return request<RubyResponseVoListDepartmentTreeNodeVo>(
    '/api/v2/ruby_migrate/groups/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/groups/delete_manager
 */
export function v2RubyMigrateGroupsDeleteManager(
  data: DeleteGroupManagerCmd,
  opts?: ReqOpts
) {
  return request<RubyResponseVoSimpleUserDto>(
    '/api/v2/ruby_migrate/groups/delete_manager',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/groups/add_manager
 */
export function v2RubyMigrateGroupsAddManager(
  data: AddGroupManagerCmd,
  opts?: ReqOpts
) {
  return request<RubyResponseVoSimpleUserDto>(
    '/api/v2/ruby_migrate/groups/add_manager',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/social_media
 */
export function rubyGlobalSearchSocialMedia(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/global_searches/social_media',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/public_relation
 */
export function rubyGlobalSearchPublicRelation(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/global_searches/public_relation',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/oversea
 */
export function rubyGlobalSearchOversea(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/global_searches/oversea',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/medium
 */
export function rubyGlobalSearchMedium(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/global_searches/medium',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/market
 */
export function rubyGlobalSearchMarket(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/global_searches/market',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/live_streaming
 */
export function rubyGlobalSearchLiveStreaming(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/global_searches/live_streaming',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/flow_distribution
 */
export function rubyGlobalSearchFlowDistribution(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/global_searches/flow_distribution',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/esports
 */
export function rubyGlobalSearchEsports(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/global_searches/esports',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/channel
 */
export function rubyGlobalSearchChannel(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/global_searches/channel',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/brand_creativity
 */
export function rubyGlobalSearchBrandCreativity(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/global_searches/brand_creativity',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/global_searches/all
 */
export function rubyGlobalSearchAll(
  data?: RubyGlobalSearchRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoRubyGlobalSearchResult>(
    '/api/v2/ruby_migrate/global_searches/all',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/dspf/config_url
 */
export function rubyDspfConfigUrl(data: ConfigUrlDto, opts?: ReqOpts) {
  return request<RubyResponseVoConfigUrlResult>(
    '/api/v2/ruby_migrate/dspf/config_url',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/dspf/config-url
 */
export function rubyDspfConfigUrlUsingPost(data: ConfigUrlDto, opts?: ReqOpts) {
  return request<RubyResponseVoConfigUrlResult>(
    '/api/v2/ruby_migrate/dspf/config-url',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 获取下拉选项 /api/v2/ruby_migrate/common/filter_options
 */
export function rubyCommonFilterOptions(data?: string, opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/common/filter_options',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/accounts/set_notification
 */
export function rubyAccountsSetNotification(
  data: RubyAccountsSetNotificationRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoVoid>(
    '/api/v2/ruby_migrate/accounts/set_notification',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/accounts/set_language
 */
export function rubyAccountsSetLanguage(
  data: RubyAccountsSetLanguageRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoVoid>(
    '/api/v2/ruby_migrate/accounts/set_language',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/**
 */
export function rubyProxyOldJavaGetOther(opts?: ReqOpts) {
  return request<RubyResponseVoObject>('/api/v2/ruby_migrate/**', { ...opts });
}
/**
 * unknown /api/v2/ruby_migrate/**
 */
export function rubyProxyOldJavaPostOther(data?: string, opts?: ReqOpts) {
  return request<RubyResponseVoObject>('/api/v2/ruby_migrate/**', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/reportForms/export
 */
export function reportFormsExport(data: ReportFormsExportForm, opts?: ReqOpts) {
  return request<Response>('/api/v2/reportForms/export', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/reportForms/daf_url
 */
export function reportFormsDafUrl(opts?: ReqOpts) {
  return request<ResponseString>('/api/v2/reportForms/daf_url', {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/v2/price-library/queryPrice
 */
export function v2PriceLibraryQueryPrice(
  data: PriceLibraryQueryDetailForm,
  opts?: ReqOpts
) {
  return request<ResponsePriceLibraryVo>('/api/v2/price-library/queryPrice', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/price-library/effectList
 */
export function v2PriceLibraryEffectList(
  data: PriceLibraryQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoPriceLibraryVo>(
    '/api/v2/price-library/effectList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/price-library/approvalList
 */
export function v2PriceLibraryApprovalList(
  data: PriceLibraryApprovalQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoPriceLibraryVo>(
    '/api/v2/price-library/approvalList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/price-library/addPrice
 */
export function v2PriceLibraryAddPrice(
  data: PriceLibraryAddForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/price-library/addPrice', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/price-history/priceList
 */
export function v2PriceHistoryPriceList(
  data: PriceHistoryQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoPriceHistoryVo>(
    '/api/v2/price-history/priceList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 提交OA审批 /api/v2/prepayment/{prepaymentId}/submit-approval
 */
export function v2PrepaymentByPrepaymentIdSubmitApproval(
  params: {
    prepayment_id: number;
  },
  opts?: ReqOpts
) {
  const { prepayment_id } = params;
  return request<ResponsePrepaymentDetailVo>(
    `/api/v2/prepayment/${prepayment_id}/submit-approval`,
    { method: 'POST', ...opts }
  );
}
/**
 * 创建预付单 /api/v2/prepayment/submit
 */
export function v2PrepaymentSubmit(data: PrepaymentSubmitCmd, opts?: ReqOpts) {
  return request<ResponseLong>('/api/v2/prepayment/submit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 更新并重新提交预付单 /api/v2/prepayment/resubmit
 */
export function v2PrepaymentResubmit(
  data: PrepaymentUpdateCmd,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/prepayment/resubmit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询预付单流程进度与更新历史 /api/v2/prepayment/journals
 */
export function prepaymentJournalJournals(data: JournalReqDto, opts?: ReqOpts) {
  return request<ResponseJournalResDto>('/api/v2/prepayment/journals', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 废弃预付单 /api/v2/prepayment/abandon
 */
export function v2PrepaymentAbandon(
  data: PrepaymentAbandonCmd,
  opts?: ReqOpts
) {
  return request<ResponsePrepaymentDetailVo>('/api/v2/prepayment/abandon', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/planner-group/update
 */
export function plannerGroupUpdate(
  data: PlannerGroupUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/planner-group/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/planner-group/page
 */
export function plannerGroupPage(
  data: PlannerGroupPageQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoPlannerGroupVo>('/api/v2/planner-group/page', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/planner-group/l2-selections
 */
export function plannerGroupQueryL2Selections(
  data: PlannerGroupL2SelectionQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePlannerGroupL2SelectionVo>(
    '/api/v2/planner-group/l2-selections',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/planner-group/create
 */
export function plannerGroupCreate(
  data: PlannerGroupCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/planner-group/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * OA嵌入页查询汇率 /api/v2/oa/budget-adjust/exchange/rate/query
 */
export function v2OaBudgetAdjustExchangeRateQuery(
  params: {
    token: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListExchangeRateDto>(
    createUri('/api/v2/oa/budget-adjust/exchange/rate/query', query),
    { method: 'POST', ...opts }
  );
}
/**
 * Muse资源创建失败后手动重试 /api/v2/muse/resource/{demandId}/retry-create
 */
export function v2MuseResourceByDemandIdRetryCreate(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id } = params;
  return request<ResponseVoid>(
    `/api/v2/muse/resource/${demand_id}/retry-create`,
    { method: 'POST', ...opts }
  );
}
/**
 * 登录确认（供中台回调，校验邮箱+验证码） /api/v2/muse/collaborator/auth/verify
 */
export function v2MuseCollaboratorAuthVerify(
  data: MuseCollaboratorVerifyForm,
  opts?: ReqOpts
) {
  return request<ResponseMuseCollaboratorVerifyResultVo>(
    '/api/v2/muse/collaborator/auth/verify',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 创建KPI调整单 /api/v2/kpi/adjustment/submit
 */
export function kpiAdjustmentSubmit(
  data: KpiAdjustmentSubmitCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/kpi/adjustment/submit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * KPI调整单需求方撤回 /api/v2/kpi/adjustment/principal/recall
 */
export function kpiAdjustmentPrincipalRecall(
  data: KpiAdjustApprovalCmdVoid,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/kpi/adjustment/principal/recall', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * KPI调整单策划退回 /api/v2/kpi/adjustment/planner/reject
 */
export function kpiAdjustmentPlannerReject(
  data: KpiAdjustApprovalCmdVoid,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/kpi/adjustment/planner/reject', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * KPI调整单策划通过 /api/v2/kpi/adjustment/planner/pass
 */
export function kpiAdjustmentPlannerPass(
  data: KpiAdjustApprovalCmdKpiAdjustPlanApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/kpi/adjustment/planner/pass', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * KPI调整单删除 /api/v2/kpi/adjustment/planner/delete
 */
export function kpiAdjustmentDelete(data: KpiAdjustDeleteCmd, opts?: ReqOpts) {
  return request<ResponseVoid>('/api/v2/kpi/adjustment/planner/delete', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 导出预填报价表格数据 /api/v2/kpi/adjustment/plan/demand/kpi/export
 */
export function kpiAdjustmentExportPlanDemandKpi(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<unknown>(
    createUri('/api/v2/kpi/adjustment/plan/demand/kpi/export', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 节点记录 /api/v2/kpi/adjustment/node_records
 */
export function kpiAdjustmentListNodeRecords(
  data: GetKpiAdjustmentNodeRecordsCmd,
  opts?: ReqOpts
) {
  return request<ResponseJournalResDto>('/api/v2/kpi/adjustment/node_records', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * KPI调整单详情 /api/v2/kpi/adjustment/detail
 */
export function kpiAdjustmentDetail(
  data: GetKpiAdjustmentDetailCmd,
  opts?: ReqOpts
) {
  return request<ResponseKpiAdjustmentDetailIncludeDemandDto>(
    '/api/v2/kpi/adjustment/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 小喇叭全局搜索 /api/v2/java_proxy/global_searchs/cd_trumpet
 */
export function trumpetSearchGlobalSearch(
  data: GlobalSearchForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultTrumpetWorkBenchGlobalSearchVo>(
    '/api/v2/java_proxy/global_searchs/cd_trumpet',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 小喇叭根据OA单号进行全局搜索 /api/v2/java_proxy/global_searchs/cd_trumpet-by-oa-id
 */
export function trumpetSearchGlobalSearchTrumpetByOaId(
  data: GlobalSearchForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultTrumpetWorkBenchGlobalSearchVo>(
    '/api/v2/java_proxy/global_searchs/cd_trumpet-by-oa-id',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 营销闭环全局搜索 /api/v2/java_proxy/global_searchs/cd_closed_loop
 */
export function closedLoopSearchGlobalSearch(
  data: ClosedLoopGlobalSearchForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultClosedLoopWorkBenchGlobalSearchVo>(
    '/api/v2/java_proxy/global_searchs/cd_closed_loop',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 营销闭环根据OA单号进行全局搜索 /api/v2/java_proxy/global_searchs/cd_closed_loop-by-oa-id
 */
export function closedLoopSearchGlobalSearchTrumpetByOaId(
  data: ClosedLoopGlobalSearchForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultClosedLoopWorkBenchGlobalSearchVo>(
    '/api/v2/java_proxy/global_searchs/cd_closed_loop-by-oa-id',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 根据OA流程号搜索 /api/v2/java_proxy/global_searchs/all-by-oa-id
 */
export function searchGlobalSearchAllByOaId(
  data: GlobalSearchForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkBenchGlobalSearchVo>(
    '/api/v2/java_proxy/global_searchs/all-by-oa-id',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/invoice/list
 */
export function invoiceListInvoicePage(
  data: InvoiceQueryPageForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoInvoiceListVo>('/api/v2/invoice/list', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/inquiry-score-rule/update
 */
export function inquiryScoreRuleUpdate(
  data: InquiryScoreRuleUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/inquiry-score-rule/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/inquiry-score-rule/page
 */
export function inquiryScoreRulePage(
  data: InquiryScoreRulePageQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoInquiryScoreRuleVo>(
    '/api/v2/inquiry-score-rule/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/inquiry-score-rule/create
 */
export function inquiryScoreRuleCreate(
  data: InquiryScoreRuleCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/inquiry-score-rule/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 创建权限申请并推送 OA /api/v2/iam/permission-application
 */
export function v2IamPermissionApplication(
  data: CreatePermissionApplicationCmd,
  opts?: ReqOpts
) {
  return request<ResponsePermissionApplicationDetailVo>(
    '/api/v2/iam/permission-application',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/fee/project/page_list
 */
export function feeQueryProjectList(
  data: ProjectListQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageDtoProjectQueryDto>(
    '/api/v2/fee/project/page_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/fee/project/calculate_project_budget
 */
export function feeCalculateProjectBudget(
  data: CalculateProjectBudgetDto,
  opts?: ReqOpts
) {
  return request<ResponseCalculateProjectBudgetVo>(
    '/api/v2/fee/project/calculate_project_budget',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/fee/project/bidding/page_list
 */
export function feeQueryBiddingProjectList(
  data: ProjectListQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageDtoProjectQueryDto>(
    '/api/v2/fee/project/bidding/page_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/fee-type-l2-permission/update
 */
export function feeTypeL2PermissionUpdate(
  data: FeeTypeL2PermissionUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/fee-type-l2-permission/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/fee-type-l2-permission/page
 */
export function feeTypeL2PermissionPage(
  data: FeeTypeL2PermissionPageQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoFeeTypeL2PermissionVo>(
    '/api/v2/fee-type-l2-permission/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/fee-type-l2-permission/l2-selections
 */
export function feeTypeL2PermissionQueryL2Selections(
  data: FeeTypeL2PermissionL2SelectionQueryForm,
  opts?: ReqOpts
) {
  return request<ResponseFeeTypeL2PermissionL2SelectionVo>(
    '/api/v2/fee-type-l2-permission/l2-selections',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/fee-type-l2-permission/delete
 */
export function feeTypeL2PermissionDelete(
  data: {
    [key: string]: any;
  },
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/fee-type-l2-permission/delete', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/fee-type-l2-permission/create
 */
export function feeTypeL2PermissionCreate(
  data: FeeTypeL2PermissionCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/fee-type-l2-permission/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/dspf/sync/new/ad
 */
export function adDspfAdSyncNew(data: AdSyncNewDto, opts?: ReqOpts) {
  return request<ResponseBoolean>('/api/v2/dspf/sync/new/ad', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/dspf/ad-config-url
 */
export function adDspfAdConfigUrl(data: CommonConfigUrlDto, opts?: ReqOpts) {
  return request<ResponseConfigUrlResult>('/api/v2/dspf/ad-config-url', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 更新数易产品映射 /api/v2/dspf-mapping/product/update
 */
export function v2DspfMappingProductUpdate(
  data: DspfMappingProductUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/dspf-mapping/product/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询数易维度列表 /api/v2/dspf-mapping/product/query-dspf-dimension
 */
export function v2DspfMappingProductQueryDspfDimension(
  data: QueryDspfDimensionForm,
  opts?: ReqOpts
) {
  return request<ResponseDspfDimensionVo>(
    '/api/v2/dspf-mapping/product/query-dspf-dimension',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 分页查询数易产品映射列表 /api/v2/dspf-mapping/product/page
 */
export function v2DspfMappingProductPage(
  data: DspfMappingProductPageQuery,
  opts?: ReqOpts
) {
  return request<ResponsePageResultDspfMappingProductVo>(
    '/api/v2/dspf-mapping/product/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询数易产品映射详情 /api/v2/dspf-mapping/product/detail
 */
export function v2DspfMappingProductDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDspfMappingProductVo>(
    createUri('/api/v2/dspf-mapping/product/detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 删除数易产品映射 /api/v2/dspf-mapping/product/delete
 */
export function v2DspfMappingProductDelete(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseVoid>(
    createUri('/api/v2/dspf-mapping/product/delete', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 创建数易产品映射 /api/v2/dspf-mapping/product/create
 */
export function v2DspfMappingProductCreate(
  data: DspfMappingProductCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseLong>('/api/v2/dspf-mapping/product/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 更新产品绑定方式映射 /api/v2/dspf-mapping/product-bind/update
 */
export function v2DspfMappingProductBindUpdate(
  data: DspfMappingProductBindUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/dspf-mapping/product-bind/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 分页查询产品绑定方式映射列表 /api/v2/dspf-mapping/product-bind/page
 */
export function v2DspfMappingProductBindPage(
  data: DspfMappingProductBindPageQuery,
  opts?: ReqOpts
) {
  return request<ResponsePageResultDspfMappingProductBindVo>(
    '/api/v2/dspf-mapping/product-bind/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询产品绑定方式映射详情 /api/v2/dspf-mapping/product-bind/detail
 */
export function v2DspfMappingProductBindDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDspfMappingProductBindVo>(
    createUri('/api/v2/dspf-mapping/product-bind/detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 删除产品绑定方式映射 /api/v2/dspf-mapping/product-bind/delete
 */
export function v2DspfMappingProductBindDelete(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseVoid>(
    createUri('/api/v2/dspf-mapping/product-bind/delete', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 创建产品绑定方式映射 /api/v2/dspf-mapping/product-bind/create
 */
export function v2DspfMappingProductBindCreate(
  data: DspfMappingProductBindCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseLong>('/api/v2/dspf-mapping/product-bind/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 更新映射 /api/v2/dspf-mapping-supplier/update
 */
export function v2DspfMappingSupplierUpdate(
  data: DspfMappingSupplierUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/dspf-mapping-supplier/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 分页查询映射列表 /api/v2/dspf-mapping-supplier/page
 */
export function v2DspfMappingSupplierPage(
  data: DspfMappingSupplierPageQuery,
  opts?: ReqOpts
) {
  return request<ResponsePageResultDspfMappingSupplierListVo>(
    '/api/v2/dspf-mapping-supplier/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询映射详情 /api/v2/dspf-mapping-supplier/detail
 */
export function v2DspfMappingSupplierDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDspfMappingSupplierDetailVo>(
    createUri('/api/v2/dspf-mapping-supplier/detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 根据供应商ID查询映射详情 /api/v2/dspf-mapping-supplier/detail-by-supplier
 */
export function v2DspfMappingSupplierDetailBySupplier(
  params: {
    supplier_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDspfMappingSupplierDetailVo>(
    createUri('/api/v2/dspf-mapping-supplier/detail-by-supplier', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 删除映射 /api/v2/dspf-mapping-supplier/delete
 */
export function v2DspfMappingSupplierDelete(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseVoid>(
    createUri('/api/v2/dspf-mapping-supplier/delete', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 创建映射 /api/v2/dspf-mapping-supplier/create
 */
export function v2DspfMappingSupplierCreate(
  data: DspfMappingSupplierCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseLong>('/api/v2/dspf-mapping-supplier/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 更新数易渠道映射 /api/v2/dspf-mapping-channel/update
 */
export function v2DspfMappingChannelUpdate(
  data: DspfMappingChannelUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/dspf-mapping-channel/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 分页查询数易渠道映射 /api/v2/dspf-mapping-channel/page
 */
export function v2DspfMappingChannelPage(
  data: DspfMappingChannelPageQuery,
  opts?: ReqOpts
) {
  return request<ResponsePageResultDspfMappingChannelListVo>(
    '/api/v2/dspf-mapping-channel/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询数易渠道映射详情 /api/v2/dspf-mapping-channel/detail
 */
export function v2DspfMappingChannelDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDspfMappingChannelDetailVo>(
    createUri('/api/v2/dspf-mapping-channel/detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 删除数易渠道映射 /api/v2/dspf-mapping-channel/delete
 */
export function v2DspfMappingChannelDelete(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseVoid>(
    createUri('/api/v2/dspf-mapping-channel/delete', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 创建数易渠道映射 /api/v2/dspf-mapping-channel/create
 */
export function v2DspfMappingChannelCreate(
  data: DspfMappingChannelCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseLong>('/api/v2/dspf-mapping-channel/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands_move/preCheck
 */
export function demandMovePreCheck(data: DemandMoveDto, opts?: ReqOpts) {
  return request<ResponsePreCheckResultVoObject>(
    '/api/v2/demands_move/preCheck',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands_move/move
 */
export function demandMoveMove(data: DemandMoveDto, opts?: ReqOpts) {
  return request<ResponseDemandMoveVo>('/api/v2/demands_move/move', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands_move/batch_move
 */
export function demandMoveBatchMove(data: DemandBatchMoveDto, opts?: ReqOpts) {
  return request<ResponseListDemandMoveVo>('/api/v2/demands_move/batch_move', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/with_siblings
 */
export function demandDetailWithSiblings(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListDemandDetailsResDto>(
    createUri('/api/v2/demands/with_siblings', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 查询小喇叭一级单列表 /api/v2/demands/trumpet_first_list
 */
export function demandGetTrumpetAllList(
  data: WorkspaceTrumpetAllListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceTrumpetAllVo>(
    '/api/v2/demands/trumpet_first_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands/suppliers
 */
export function demandGetSuppliers(data: SupplierQueryForm, opts?: ReqOpts) {
  return request<ResponseListSupplierChooseDto>('/api/v2/demands/suppliers', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/supplier_quote_settle_reports
 */
export function demandQueryDemandSupplierQuoteSettleReports(
  params: {
    demand_supplier_id: number;
    cooperate_account_id?: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandSupplierQuoteWithSettleReportVo>(
    createUri('/api/v2/demands/supplier_quote_settle_reports', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/demands/split
 */
export function demandSplit(data: SplitDemandPlanInfoForm, opts?: ReqOpts) {
  return request<ResponseListLong>('/api/v2/demands/split', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询二级单需求列表 /api/v2/demands/second_list
 */
export function demandQuerySecondDemandList(
  data: WorkspaceDemandAllListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultWorkspaceDemandAllVo>(
    '/api/v2/demands/second_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 追加预算：搜索关联需求二级单号（仅内部用户） /api/v2/demands/search_related_id
 */
export function demandSearchRelatedId(
  data: SearchRelatedDemandForm,
  opts?: ReqOpts
) {
  return request<ResponseListRelatedDemandInfoVo>(
    '/api/v2/demands/search_related_id',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands/reminder
 */
export function demandReminder(data: DemandReqDto, opts?: ReqOpts) {
  return request<ResponseReminderResDto>('/api/v2/demands/reminder', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【20240704版本】查询相似付款单据 /api/v2/demands/query_similar_demands_for_payment
 */
export function demandQuerySimilarDemandsForPayment(
  data: QuerySimilarDemandsForPaymentDto,
  opts?: ReqOpts
) {
  return request<ResponseListDemandSimplePaymentInfoVo>(
    '/api/v2/demands/query_similar_demands_for_payment',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询超额验收信息 /api/v2/demands/query_over_amount_info
 */
export function demandQueryOverAmountInfo(
  data: QueryOverAmountInfoForm,
  opts?: ReqOpts
) {
  return request<ResponseOverAmountInfoDto>(
    '/api/v2/demands/query_over_amount_info',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands/pmo_edit
 */
export function demandPmoEdit(data: PmoEditDemandForm, opts?: ReqOpts) {
  return request<Response>('/api/v2/demands/pmo_edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 暂存面板数据 /api/v2/demands/plan-batch-confirm/save_draft
 */
export function planDemandBatchConfirmSaveDraft(
  data: DemandTemporaryForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/v2/demands/plan-batch-confirm/save_draft',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 批量驳回 /api/v2/demands/plan-batch-confirm/reject
 */
export function planDemandBatchConfirmBatchReject(
  data: PlanDemandBatchRejectForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandBatchAcceptResultDto>(
    '/api/v2/demands/plan-batch-confirm/reject',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 批量确认提交（统一入口） /api/v2/demands/plan-batch-confirm/confirm
 */
export function planDemandBatchConfirmBatchConfirm(
  data: PlanDemandBatchConfirmForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandBatchAcceptResultDto>(
    '/api/v2/demands/plan-batch-confirm/confirm',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands/over_amount/save_or_submit
 */
export function demandSaveOrSubmitOverAmount(
  data: SaveOrSubmitOverAmountForm,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/demands/over_amount/save_or_submit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 发起费用计提-月消耗 /api/v2/demands/month/release
 */
export function demandDemandMonthRelease(
  data: DemandMonthReleaseDto,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demands/month/release', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/modify_quote
 */
export function demandModifyQuote(data: ModifyQuoteDto, opts?: ReqOpts) {
  return request<ResponseObject>('/api/v2/demands/modify_quote', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【电子发票】修改合同号 /api/v2/demands/modify_contract_no
 */
export function demandModifyContractNo(
  data: ModifyContractNoDto,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demands/modify_contract_no', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/left_navs
 */
export function demandLeftNavs(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListLeftNavsVo>(
    createUri('/api/v2/demands/left_navs', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/demands/leaves/plan_user_edit
 */
export function demandPlannerEditLeaves(
  data: PlanUserEditLeavesForm,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/demands/leaves/plan_user_edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/journals
 */
export function demandJournals(data: JournalReqDto, opts?: ReqOpts) {
  return request<ResponseJournalResDto>('/api/v2/demands/journals', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 重新报价页面获取供应商详情 /api/v2/demands/get_suppliers_by_ids
 */
export function demandGetSuppliersByIds(
  data: SupplierByIdsQueryForm,
  opts?: ReqOpts
) {
  return request<ResponseListSupplierChooseItemDto>(
    '/api/v2/demands/get_suppliers_by_ids',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 获取报价表格数据 /api/v2/demands/get_quote_table_data
 */
export function demandGetQuoteTableData(
  data: GetQuoteTableDataForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandQuoteTableDto>(
    '/api/v2/demands/get_quote_table_data',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands/getGroupType
 */
export function demandGetGroupType(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandDetailsResDto>(
    createUri('/api/v2/demands/getGroupType', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 导出预填报价表格数据 /api/v2/demands/export_prefill_quote_table_data
 */
export function demandExportQuoteTableData(
  data: ExportPrefillQuoteTableDataForm,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/demands/export_prefill_quote_table_data', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 导出页面合作内容 /api/v2/demands/export_cooperate_data
 */
export function demandExportCooperateData(
  data: DemandExportForm,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/demands/export_cooperate_data', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/edit
 */
export function demandEdit(data: EditDemandForm, opts?: ReqOpts) {
  return request<Response>('/api/v2/demands/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/editPlanInfo
 */
export function demandEditPlanInfo(
  data: EditDemandPlanInfoForm,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/demands/editPlanInfo', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/editLeaves
 */
export function demandEditLeaves(data: EditDemandLeavesForm, opts?: ReqOpts) {
  return request<Response>('/api/v2/demands/editLeaves', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/editAuxiliaryKpi
 */
export function demandEditAuxiliaryKpi(
  data: EditAuxiliaryKpiForm,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/demands/editAuxiliaryKpi', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/edit/quote_weight
 */
export function demandEditQuoteWeight(
  data: EditDemandQuoteWeightForm,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/demands/edit/quote_weight', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/diff/supplier_quote
 */
export function demandDiffSupplierQuote(
  params: {
    demand_id: number;
    demand_supplier_id?: number;
    blob_id: number;
    cooperate_account_id?: number;
    diff: boolean;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandSupplierQuoteDiffVo>(
    createUri('/api/v2/demands/diff/supplier_quote', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 查看历史报价详情 /api/v2/demands/detail_his_round_for_supplier
 */
export function demandDetailHisRoundForSupplier(
  params: {
    demand_id: number;
    supplier_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandHisDetailResDto>(
    createUri('/api/v2/demands/detail_his_round_for_supplier', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 【询报价评分线上化】获取需求单历史轮次详情 /api/v2/demands/detail_his_round
 */
export function demandDetailHisRound(
  params: {
    demand_id: number;
    turns_number: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandHisDetailResDto>(
    createUri('/api/v2/demands/detail_his_round', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/demands/detail
 */
export function demandDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandDetailsResDto>(
    createUri('/api/v2/demands/detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/demands/destroy/{id}
 */
export function demandDestroy(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseBoolean>(`/api/v2/demands/destroy/${id}`, {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/demand/detail/export-plan-task
 */
export function demandCreateExportDemandPlanTask(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/v2/demands/demand/detail/export-plan-task', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/demands/demand/detail/batch-edit-plan-budget
 */
export function demandBatchEditPlanBudget(
  data: BatchEditPlanBudgetForm,
  opts?: ReqOpts
) {
  return request<Response>(
    '/api/v2/demands/demand/detail/batch-edit-plan-budget',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands/create_demand_bidding
 */
export function demandCreateBiddingProcess(
  data: CreateDemandBiddingForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandBiddingVo>(
    '/api/v2/demands/create_demand_bidding',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands/create
 */
export function demandCreate(data: CreateDemandForm, opts?: ReqOpts) {
  return request<ResponseCreateDemandResultVo>('/api/v2/demands/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/cooperate_account/edit
 */
export function demandCooperateAccountEdit(
  data: EditDemandCooperateAccountForm,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/demands/cooperate_account/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【待拟制列表】修改合同号 /api/v2/demands/contract_ready_draft_list/modify_contract_no
 */
export function demandModifyContractNoInContractReadyDraftList(
  data: ModifyContractNoDto,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/v2/demands/contract_ready_draft_list/modify_contract_no',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands/close
 */
export function demandClose(data: CloseDemandForm, opts?: ReqOpts) {
  return request<ResponseVoid>('/api/v2/demands/close', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/clear_dspf_mpc_no
 */
export function demandClearDspfMpcNo(data: DspfInputForm, opts?: ReqOpts) {
  return request<ResponseBoolean>('/api/v2/demands/clear_dspf_mpc_no', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 校验预填报价表格数据（前端目前暂时没有使用该接口，待后期需求启用） /api/v2/demands/check_prefill_quote_table_data
 */
export function demandCheckPrefillQuoteTableData(
  data: ExportPrefillQuoteTableDataForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandPrefillQuoteDataCheckResultDto>(
    '/api/v2/demands/check_prefill_quote_table_data',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 计算超额验收金额 /api/v2/demands/calc/over_amount
 */
export function demandCalcOverAmount(data: Blob, opts?: ReqOpts) {
  return request<ResponseCalcOverAmountDto>(
    '/api/v2/demands/calc/over_amount',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 批量添加辅助自定义指标 /api/v2/demands/batchAddAuxiliaryKpi
 */
export function demandBatchAddAuxiliaryKpi(
  data: BatchAddAuxiliaryKpiForm,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/demands/batchAddAuxiliaryKpi', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/batch-accept/save_draft
 */
export function demandBatchAcceptSaveDraft(
  data: DemandTemporaryForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demands/batch-accept/save_draft', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demands/batch-accept/reject
 */
export function demandBatchAcceptBatchRejectDemands(
  data: DemandBatchRejectForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandBatchAcceptResultDto>(
    '/api/v2/demands/batch-accept/reject',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands/batch-accept/query_draft
 */
export function demandBatchAcceptQueryDraft(
  params: {
    demand_id: number;
    state: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/v2/demands/batch-accept/query_draft', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/demands/batch-accept/none_binding/cooperate_account_check
 */
export function demandBatchAcceptNoneBindingBatchAcceptDemandsCooperateAccountCheck(
  data: DemandBatchNoneBindingAcceptForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandBatchAcceptResultDto>(
    '/api/v2/demands/batch-accept/none_binding/cooperate_account_check',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands/batch-accept/none_binding/accept
 */
export function demandBatchAcceptNoneBindingBatchAcceptDemands(
  data: DemandBatchNoneBindingAcceptForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandBatchAcceptResultDto>(
    '/api/v2/demands/batch-accept/none_binding/accept',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demands/batch-accept/accept
 */
export function demandBatchAcceptBatchAcceptDemands(
  data: DemandBatchAcceptForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandBatchAcceptResultDto>(
    '/api/v2/demands/batch-accept/accept',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求标品】分页查询 /api/v2/demand_standard_library/page_tree_list
 */
export function demandStandardLibraryPageWithTreeList(
  data: PageDemandStandardCatalogForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultDemandStandardCatalogVo>(
    '/api/v2/demand_standard_library/page_tree_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求标品】根据级别分页查询 /api/v2/demand_standard_library/page_tiled_list
 */
export function demandStandardLibraryPageByLevelWithTiledList(
  data: PageDemandStandardCatalogForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultDemandStandardCatalogVo>(
    '/api/v2/demand_standard_library/page_tiled_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求标品】查询标品选项值及下属选项值 /api/v2/demand_standard_library/list_value_and_sub_value
 */
export function demandStandardLibraryListValueAndSubValue(
  data: ListValueAndSubValueForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandStandardCatalogListVo>(
    '/api/v2/demand_standard_library/list_value_and_sub_value',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求标品】根据入参Code列表查询 /api/v2/demand_standard_library/list_by_code_list
 */
export function demandStandardLibraryListByCodeList(
  data: DemandStandardCatalogListParam,
  opts?: ReqOpts
) {
  return request<ResponseListDemandStandardCatalogVo>(
    '/api/v2/demand_standard_library/list_by_code_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求标品】编辑 /api/v2/demand_standard_library/edit
 */
export function demandStandardLibraryEdit(
  data: EditDemandStandardCatalogForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand_standard_library/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【需求标品】新建 /api/v2/demand_standard_library/create
 */
export function demandStandardLibraryCreate(
  data: CreateDemandStandardCatalogForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand_standard_library/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【需求标品】分页查询 /api/v2/demand_standard_catalog/page_tree_list
 */
export function demandStandardCatalogPageWithTreeList(
  data: PageDemandStandardCatalogForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultDemandStandardCatalogVo>(
    '/api/v2/demand_standard_catalog/page_tree_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求标品】根据级别分页查询 /api/v2/demand_standard_catalog/page_tiled_list
 */
export function demandStandardCatalogPageByLevelWithTiledList(
  data: PageDemandStandardCatalogForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultDemandStandardCatalogVo>(
    '/api/v2/demand_standard_catalog/page_tiled_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求标品】查询标品选项值及下属选项值 /api/v2/demand_standard_catalog/list_value_and_sub_value
 */
export function demandStandardCatalogListValueAndSubValue(
  data: ListValueAndSubValueForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandStandardCatalogListVo>(
    '/api/v2/demand_standard_catalog/list_value_and_sub_value',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求标品】根据入参Code列表查询 /api/v2/demand_standard_catalog/list_by_code_list
 */
export function demandStandardCatalogListByCodeList(
  data: DemandStandardCatalogListParam,
  opts?: ReqOpts
) {
  return request<ResponseListDemandStandardCatalogVo>(
    '/api/v2/demand_standard_catalog/list_by_code_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求标品】编辑 /api/v2/demand_standard_catalog/edit
 */
export function demandStandardCatalogEdit(
  data: EditDemandStandardCatalogForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand_standard_catalog/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【需求标品】新建 /api/v2/demand_standard_catalog/create
 */
export function demandStandardCatalogCreate(
  data: CreateDemandStandardCatalogForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand_standard_catalog/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 一询单详情查看 /api/v2/demand_source/{id}/case_page_detail
 */
export function demandSourceDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseDemandSourceDetailVo>(
    `/api/v2/demand_source/${id}/case_page_detail`,
    { method: 'POST', ...opts }
  );
}
/**
 * 一询单创建 /api/v2/demand_source/split
 */
export function demandSourceSave(data: DemandSourceCreateCmd, opts?: ReqOpts) {
  return request<ResponseBoolean>('/api/v2/demand_source/split', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 标品价格库自动核价结果查询接口（新品类只读【查看核价结果】，单供应商） /api/v2/demand_source/price_check/price_library_check/result
 */
export function demandSourcePriceCheckPriceLibraryCheckResult(
  data: PriceLibraryCheckResultForm,
  opts?: ReqOpts
) {
  return request<ResponsePriceLibraryCheckResultVo>(
    '/api/v2/demand_source/price_check/price_library_check/result',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 商务确认核价：覆盖行级核价结果/商务核价备注 + 标记商务核价状态=已核价 /api/v2/demand_source/price_check/price_library_check/confirm
 */
export function demandSourcePriceCheckConfirmPriceCheck(
  data: PriceCheckConfirmCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/v2/demand_source/price_check/price_library_check/confirm',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 商务核价信息保存接口 /api/v2/demand_source/price_check/price_check/save
 */
export function demandSourcePriceCheckPriceCheckSave(
  data: DemandSourcePriceCheckSaveCmd,
  opts?: ReqOpts
) {
  return request<ResponseDemandSourcePriceCheckSaveResp>(
    '/api/v2/demand_source/price_check/price_check/save',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 商务核价信息暂存接口 /api/v2/demand_source/price_check/price_check/draft
 */
export function demandSourcePriceCheckPriceCheckDraft(
  data: DemandSourcePriceCheckSaveCmd,
  opts?: ReqOpts
) {
  return request<ResponseDemandSourcePriceCheckSaveResp>(
    '/api/v2/demand_source/price_check/price_check/draft',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 商务核价信息查询接口 /api/v2/demand_source/price_check/price_check/detail
 */
export function demandSourcePriceCheckPriceCheckDetail(
  data: GetDemandSourcePriceCheckDetailForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandSourcePriceCheckDetailVo>(
    '/api/v2/demand_source/price_check/price_check/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 基准价查询接口 /api/v2/demand_source/price_check/base/price
 */
export function demandSourcePriceCheckReferencePrice(
  data: GetDemandSourceReferencePriceForm,
  opts?: ReqOpts
) {
  return request<ResponseListDemandSourceReferencePriceVo>(
    '/api/v2/demand_source/price_check/base/price',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 基准价批量查询接口 /api/v2/demand_source/price_check/base/price/batch
 */
export function demandSourcePriceCheckBatchGetReferencePrice(
  data: BatchGetDemandSourceReferencePriceForm,
  opts?: ReqOpts
) {
  return request<ResponseListDemandSourceReferencePriceVo>(
    '/api/v2/demand_source/price_check/base/price/batch',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 报价信息溢价比计算 /api/v2/demand_source/premium_rate/compute
 */
export function demandSourcePremiumRateCompute(
  data: DemandSourcePremiumRateComputeCmd,
  opts?: ReqOpts
) {
  return request<ResponseDemandSourcePremiumRateComputeVo>(
    '/api/v2/demand_source/premium_rate/compute',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 一询单保存 /api/v2/demand_source/demand/save
 */
export function demandSourceDemandSave(
  data: DemandSourceApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/demand_source/demand/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 一询单拒绝 /api/v2/demand_source/demand/reject
 */
export function demandSourceDemandReject(
  data: DemandSourceApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/demand_source/demand/reject', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 商务评估节点供应商一询信息查询 /api/v2/demand_source/demand/pending_assess/getSupplierInfo
 */
export function demandSourceGetPendingAssessGetSupplierInfo(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponsePendingAssessSupplierInfoVo>(
    createUri(
      '/api/v2/demand_source/demand/pending_assess/getSupplierInfo',
      query
    ),
    { method: 'POST', ...opts }
  );
}
/**
 * 一询单通过 /api/v2/demand_source/demand/pass
 */
export function demandSourceDemandPass(
  data: DemandSourceApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/demand_source/demand/pass', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 一询单关闭 /api/v2/demand_source/demand/close
 */
export function demandSourceClose(
  data: DemandSourceApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/demand_source/demand/close', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 根据达人链接获取内容营销数据 /api/v2/demand_source/content/getKolInfo
 */
export function demandSourceGetKolInfo(
  params: {
    main_platform_link: string;
    content_type: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandSourceKolVo>(
    createUri('/api/v2/demand_source/content/getKolInfo', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 根据批量达人链接获取内容营销数据 /api/v2/demand_source/content/batchGetKolInfo
 */
export function demandSourceGetKolInfoUsingPost(
  data: BatchGetKolInfoCmd,
  opts?: ReqOpts
) {
  return request<ResponseListDemandSourceKolVo>(
    '/api/v2/demand_source/content/batchGetKolInfo',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 根据批量达人链接获取内容营销数据导出到合作内容 /api/v2/demand_source/content/batchGetCoopKolInfo
 */
export function demandCoopKolInfoGetKolInfoCoop(
  data: BatchGetKolInfoCmd,
  opts?: ReqOpts
) {
  return request<ResponseListDemandCooperateAccountDto>(
    '/api/v2/demand_source/content/batchGetCoopKolInfo',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求权益】分页查询权益内容列表 /api/v2/demand_rights/page_content_list
 */
export function demandRightsPageContentList(
  data: PageDemandRightsContentForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultDemandRightsContentVo>(
    '/api/v2/demand_rights/page_content_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求权益】权益类目树形结构 /api/v2/demand_rights/list_category_tree
 */
export function demandRightsListCategoryTree(
  data: ListTreeForDemandRightsCategoryForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandRightsTreeVo>(
    '/api/v2/demand_rights/list_category_tree',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求权益】编辑权益内容 /api/v2/demand_rights/edit/content
 */
export function demandRightsEditContent(
  data: EditDemandRightsContentForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand_rights/edit/content', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【需求权益】编辑权益类目 /api/v2/demand_rights/edit/category
 */
export function demandRightsEditCategory(
  data: EditDemandRightsCategoryForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand_rights/edit/category', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【需求权益】删除权益类目 /api/v2/demand_rights/delete/category
 */
export function demandRightsDeleteCategory(
  data: DeleteDemandRightsCategoryForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand_rights/delete/category', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【需求权益】新建权益内容 /api/v2/demand_rights/create/content
 */
export function demandRightsCreateContent(
  data: CreateDemandRightsContentForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand_rights/create/content', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【需求权益】新建权益类目 /api/v2/demand_rights/create/category
 */
export function demandRightsCreateCategory(
  data: CreateDemandRightsCategoryForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand_rights/create/category', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【结构化】【需求分类配置属性】分页查询 /api/v2/demand_category_property/pageList
 */
export function demandCategoryPropertyPageList(
  data: PageDemandCategoryPropertyForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultDemandCategoryPropertyTemplateVo>(
    '/api/v2/demand_category_property/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【结构化】【需求分类配置属性】启用 /api/v2/demand_category_property/online
 */
export function demandCategoryPropertyOnline(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseVoid>(
    createUri('/api/v2/demand_category_property/online', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 【结构化】【需求分类配置属性】停用 /api/v2/demand_category_property/offline
 */
export function demandCategoryPropertyOffline(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseVoid>(
    createUri('/api/v2/demand_category_property/offline', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 【结构化】【需求分类配置属性】导入关键指标配置 /api/v2/demand_category_property/import_kpi_config
 */
export function demandCategoryPropertyImportKpiConfig(
  data?: GenericFormData<{
    /** Excel文件 */
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  return request<ResponseString>(
    '/api/v2/demand_category_property/import_kpi_config',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【结构化】【需求分类配置属性】编辑 /api/v2/demand_category_property/edit
 */
export function demandCategoryPropertyEdit(
  data: EditDemandCategoryPropertyForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand_category_property/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【结构化】【需求分类配置属性】删除 /api/v2/demand_category_property/delete
 */
export function demandCategoryPropertyDelete(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseVoid>(
    createUri('/api/v2/demand_category_property/delete', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 【结构化】【需求分类配置属性】新建 /api/v2/demand_category_property/create
 */
export function demandCategoryPropertyCreate(
  data: CreateDemandCategoryPropertyForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand_category_property/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 手动触发数易数据同步（更新预算及需求单信息） /api/v2/demand/autoSplitRule/sync
 */
export function v2DemandAutoSplitRuleSync(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListAutoSplitRuleVo>(
    createUri('/api/v2/demand/autoSplitRule/sync', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 查询一级单下所有自动拆单规则 /api/v2/demand/autoSplitRule/list
 */
export function v2DemandAutoSplitRuleList(
  params: {
    demand_id: number;
    include_abandoned?: boolean;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListAutoSplitRuleVo>(
    createUri('/api/v2/demand/autoSplitRule/list', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 批量操作自动拆单规则 /api/v2/demand/autoSplitRule/batch
 */
export function v2DemandAutoSplitRuleBatch(
  data: AutoSplitRuleBatchForm,
  opts?: ReqOpts
) {
  return request<ResponseListAutoSplitRuleVo>(
    '/api/v2/demand/autoSplitRule/batch',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demand-source-template/update
 */
export function demandSourceMatchTemplateUpdate(
  data: DemandSourceMatchTemplateUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand-source-template/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demand-source-template/page
 */
export function demandSourceMatchTemplatePage(
  data: DemandSourceMatchTemplateQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoDemandSourceMatchTemplateVo>(
    '/api/v2/demand-source-template/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demand-source-template/detail
 */
export function demandSourceMatchTemplateDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandSourceMatchTemplateAllVo>(
    createUri('/api/v2/demand-source-template/detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/demand-source-template/create
 */
export function demandSourceMatchTemplateCreate(
  data: DemandSourceMatchTemplateCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand-source-template/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demand-quote-temp/update
 */
export function demandQuoteTempUpdate(
  data: FeQuoteTempUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v2/demand-quote-temp/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demand-quote-temp/page
 */
export function demandQuoteTempPage(
  data: QuoteTempPageQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoQuoteTempVo>('/api/v2/demand-quote-temp/page', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demand-quote-temp/create
 */
export function demandQuoteTempCreate(
  data: FeQuoteTempCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v2/demand-quote-temp/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demand-price-check/update
 */
export function demandPriceCheckTemplateUpdate(
  data: DemandPriceCheckTemplateUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand-price-check/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demand-price-check/page
 */
export function demandPriceCheckTemplatePage(
  data: DemandPriceCheckTemplateQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoDemandPriceCheckTemplateVo>(
    '/api/v2/demand-price-check/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demand-price-check/detail
 */
export function demandPriceCheckTemplateDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandPriceCheckTemplateVo>(
    createUri('/api/v2/demand-price-check/detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/demand-price-check/create
 */
export function demandPriceCheckTemplateCreate(
  data: DemandPriceCheckTemplateCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/demand-price-check/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demand-kpi/update
 */
export function demandKpiUpdate(data: DemandKpiUpdateForm, opts?: ReqOpts) {
  return request<ResponseDemandKpiCreateUpdateRespVo>(
    '/api/v2/demand-kpi/update',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demand-kpi/page
 */
export function demandKpiPage(data: DemandKpiQueryForm, opts?: ReqOpts) {
  return request<ResponsePageVoDemandKpiVo>('/api/v2/demand-kpi/page', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/demand-kpi/create
 */
export function demandKpiCreate(data: DemandKpiCreateForm, opts?: ReqOpts) {
  return request<ResponseDemandKpiCreateUpdateRespVo>(
    '/api/v2/demand-kpi/create',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demand-invoice/memory-upload
 */
export function demandInvoiceUploadInvoice(
  params: {
    demand_id: number;
    belong_area: number;
    is_oversea_invoice: boolean;
  },
  data?: GenericFormData<{
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseInvoiceUploadResultVo>(
    createUri('/api/v2/demand-invoice/memory-upload', query),
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demand-cooperate-property/update
 */
export function demandCooperatePropertyUpdate(
  data: DemandCooperatePropertyUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandCooperatePropertyCreateUpdateRespVo>(
    '/api/v2/demand-cooperate-property/update',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demand-cooperate-property/page
 */
export function demandCooperatePropertyPage(
  data: DemandCooperatePropertyQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoDemandCooperatePropertyVo>(
    '/api/v2/demand-cooperate-property/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/demand-cooperate-property/create
 */
export function demandCooperatePropertyCreate(
  data: DemandCooperatePropertyCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandCooperatePropertyCreateUpdateRespVo>(
    '/api/v2/demand-cooperate-property/create',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/copy/query-demand
 */
export function copyDemandGetDemandAllList(
  data: WorkspaceDemandAllListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultDemandCopyVo>('/api/v2/copy/query-demand', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/copy/copy-check
 */
export function copyDemandCheckModel(
  data: CopyDemandCheckForm,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/copy/copy-check', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询合同审核流程信息 /api/v2/contract/workflow/detail
 */
export function contractContractWorkflowDetail(
  data: QueryContractWorkflowForm,
  opts?: ReqOpts
) {
  return request<ResponseSolarContractWorkFlowOpenVo>(
    '/api/v2/contract/workflow/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/contract/update/contractSignType
 */
export function contractUpdateContractSignType(
  data: UpdateContractSignTypeForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/contract/update/contractSignType', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/contract/template/updateByCode
 */
export function contractTemplateUpdateByCode(
  data: UpdateContractTemplateCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/contract/template/updateByCode', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/contract/template/save
 */
export function contractTemplateSave(
  data: NewContractTemplateCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/contract/template/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/contract/template/queryList
 */
export function contractTemplateQueryList(
  data: ContractTemplateQuery,
  opts?: ReqOpts
) {
  return request<ResponsePageVoContractTemplateVo>(
    '/api/v2/contract/template/queryList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/contract/template/queryListForChoose
 */
export function contractTemplateQueryListForChoose(
  data: ContractTemplateQuery,
  opts?: ReqOpts
) {
  return request<ResponsePageVoContractTemplateVo>(
    '/api/v2/contract/template/queryListForChoose',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 提交合同 /api/v2/contract/submitContract
 */
export function contractSubmitContract(
  data: SubmitContractForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/contract/submitContract', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询合同创建的默认草稿内容 /api/v2/contract/queryContractDraftContent
 */
export function contractQueryContractDraftContent(
  data: QueryContractDraftContentForm,
  opts?: ReqOpts
) {
  return request<ResponseContractDraftContentVo>(
    '/api/v2/contract/queryContractDraftContent',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/contract/pendingList
 */
export function contractPendingList(
  data: PendingContractQuery,
  opts?: ReqOpts
) {
  return request<ResponsePageResultPendingContractVo>(
    '/api/v2/contract/pendingList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 更新比选供应商签约类型 /api/v2/contract/bidding/update/contractSignType
 */
export function contractBiddingUpdateContractSignType(
  data: UpdateBiddingContractSignTypeForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/v2/contract/bidding/update/contractSignType',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 提交比选合同（保存草稿 + 中台传值，一步完成） /api/v2/contract/bidding/submitContract
 */
export function contractBiddingSubmitContract(
  data: SubmitBiddingContractForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/contract/bidding/submitContract', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 重新生成比选合同附件（仅生成附件，不保存草稿） /api/v2/contract/bidding/regenerateContract
 */
export function contractBiddingRegenerateContract(
  data: SubmitBiddingContractForm,
  opts?: ReqOpts
) {
  return request<ResponseBiddingContractDraftContentVo>(
    '/api/v2/contract/bidding/regenerateContract',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查看比选合同草稿预览 + 生成 Word/PDF 附件 /api/v2/contract/bidding/queryDraftContent
 */
export function contractBiddingQueryDraftContent(
  data: QueryBiddingDraftContentForm,
  opts?: ReqOpts
) {
  return request<ResponseBiddingContractDraftContentVo>(
    '/api/v2/contract/bidding/queryDraftContent',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 比选合同待拟制列表查询 /api/v2/contract/bidding/pending_list
 */
export function contractBiddingPendingList(
  data: BiddingContractPendingListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingContractPendingVo>(
    '/api/v2/contract/bidding/pending_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 比选合同关联合同（人工上传模式） /api/v2/contract/bidding/linkContract
 */
export function contractBiddingLinkContract(
  data: LinkBiddingContractForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/contract/bidding/linkContract', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 获取提示文案 /api/v2/common/tip_texts
 */
export function commonTipTexts(data: CommonTipTextForm, opts?: ReqOpts) {
  return request<ResponseListTipTextVo>('/api/v2/common/tip_texts', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 保存通用暂存数据 /api/v2/common/temporary_data/save
 */
export function commonSaveTemporary(
  data: CommonTemporarySaveForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/common/temporary_data/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询通用暂存数据 /api/v2/common/temporary_data/query
 */
export function commonQueryTemporary(
  data: CommonTemporaryQueryForm,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/common/temporary_data/query', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/common/getAdConsumeSettleDemandCategories
 */
export function commonGetAdConsumeSettleDemandCategories(opts?: ReqOpts) {
  return request<ResponseListLong>(
    '/api/v2/common/getAdConsumeSettleDemandCategories',
    { method: 'POST', ...opts }
  );
}
/**
 * 获取下拉选项 /api/v2/common/filter_options
 */
export function commonFilterOptions(data?: CommonOptionsForm, opts?: ReqOpts) {
  return request<Blob>('/api/v2/common/filter_options', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 请求上传文件 /api/v2/common/file/request-upload-file
 */
export function fileRequestUploadFile(
  data: FileRequestUploadFileForm,
  opts?: ReqOpts
) {
  return request<ResponseFileRequestUploadVo>(
    '/api/v2/common/file/request-upload-file',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 完成文件上传 /api/v2/common/file/complete-upload-file
 */
export function fileCompleteUploadFile(
  data: FileCompleteUploadForm,
  opts?: ReqOpts
) {
  return request<ResponseFileCompleteUploadVo>(
    '/api/v2/common/file/complete-upload-file',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 币种查询接口 /api/v2/common/exchange/rate/query
 */
export function commonExchangeRateQuery(
  data: ExchangeRateForm,
  opts?: ReqOpts
) {
  return request<ResponseListExchangeRateDto>(
    '/api/v2/common/exchange/rate/query',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 临时导入数据 /api/v2/common/data/{type}/memory-import-data
 */
export function dataMemoryImportData(
  params: {
    type: string;
  },
  data?: GenericFormData<{
    /** 文件 */
    file: Blob;
    /** 当前的场景信息 */
    scene?: string;
  }>,
  opts?: ReqOpts
) {
  const { type } = params;
  return request<Blob>(`/api/v2/common/data/${type}/memory-import-data`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 直接导入价格库数据 /api/v2/common/data/{type}/import_price_library_data
 */
export function dataImportPriceLibraryData(
  params: {
    type: string;
  },
  data?: GenericFormData<{
    /** 文件 */
    file: Blob;
    /** 当前的场景信息 */
    scene?: string;
  }>,
  opts?: ReqOpts
) {
  const { type } = params;
  return request<Blob>(
    `/api/v2/common/data/${type}/import_price_library_data`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 直接导入数据 /api/v2/common/data/{type}/import-data
 */
export function dataImportData(
  params: {
    type: string;
  },
  data?: GenericFormData<{
    /** 文件 */
    file: Blob;
    /** 当前的场景信息 */
    scene?: string;
  }>,
  opts?: ReqOpts
) {
  const { type } = params;
  return request<Blob>(`/api/v2/common/data/${type}/import-data`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/common/common_control_info
 */
export function commonGetCommonControlInfo(opts?: ReqOpts) {
  return request<ResponseSupplierVersionVo>(
    '/api/v2/common/common_control_info',
    { method: 'POST', ...opts }
  );
}
/**
 * 营销采购提交 /api/v2/closed_loop/demand/submit
 */
export function closedLoopDemandSubmit(
  data: CreateClosedLoopDemandCmd,
  opts?: ReqOpts
) {
  return request<ResponseCreateClosedLoopDemandResp>(
    '/api/v2/closed_loop/demand/submit',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 营销采购一级单拆单 /api/v2/closed_loop/demand/split
 */
export function closedLoopDemandDemandSplit(
  data: ClosedLoopDemandApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/closed_loop/demand/split', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 营销采购新建 /api/v2/closed_loop/demand/save
 */
export function closedLoopDemandSave(
  data: CreateClosedLoopDemandCmd,
  opts?: ReqOpts
) {
  return request<ResponseCreateClosedLoopDemandResp>(
    '/api/v2/closed_loop/demand/save',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 营销采购一级单拒绝 /api/v2/closed_loop/demand/reject
 */
export function closedLoopDemandDemandReject(
  data: ClosedLoopDemandApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/closed_loop/demand/reject', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 营销采购一级单通过 /api/v2/closed_loop/demand/pass
 */
export function closedLoopDemandDemandPass(
  data: ClosedLoopDemandApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/closed_loop/demand/pass', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 营销采购一级单编辑 /api/v2/closed_loop/demand/edit
 */
export function closedLoopDemandDemandEdit(
  data: EditClosedLoopDemandCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/closed_loop/demand/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 营销采购一级单关闭 /api/v2/closed_loop/demand/close
 */
export function closedLoopDemandDemandClose(
  data: ClosedLoopDemandApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/closed_loop/demand/close', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 三级单提交结案 /api/v2/closed_loop/case/submit_closure
 */
export function closedLoopDemandSubmitClosure(
  data: ClosedLoopCaseApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/closed_loop/case/submit_closure', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 三级单需求负责人审批拒绝 /api/v2/closed_loop/case/reject
 */
export function closedLoopDemandApproveSettleReject(
  data: ClosedLoopCaseApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/closed_loop/case/reject', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 三级单需求负责人审批通过 /api/v2/closed_loop/case/pass
 */
export function closedLoopDemandApproveSettlePass(
  data: ClosedLoopCaseApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/closed_loop/case/pass', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 三级单废弃 /api/v2/closed_loop/case/discard
 */
export function closedLoopDemandCaseDiscard(
  data: ClosedLoopCaseApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/closed_loop/case/discard', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 营销采购二级单编辑 /api/v2/closed_loop/activity/edit
 */
export function closedLoopDemandActivityEdit(
  data: EditClosedLoopActivityCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/closed_loop/activity/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/category-business-owner/update
 */
export function categoryBusinessOwnerUpdate(
  data: CategoryBusinessOwnerUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/category-business-owner/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/category-business-owner/page
 */
export function categoryBusinessOwnerPage(
  data: CategoryBusinessOwnerPageQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoCategoryBusinessOwnerVo>(
    '/api/v2/category-business-owner/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/category-business-owner/l2-selections
 */
export function categoryBusinessOwnerQueryL2Selections(
  data: CategoryBusinessOwnerL2SelectionQueryForm,
  opts?: ReqOpts
) {
  return request<ResponseCategoryBusinessOwnerL2SelectionVo>(
    '/api/v2/category-business-owner/l2-selections',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/category-business-owner/create
 */
export function categoryBusinessOwnerCreate(
  data: CategoryBusinessOwnerCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/category-business-owner/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/category-business-member-config/update
 */
export function categoryBusinessMemberConfigUpdate(
  data: CategoryBusinessMemberConfigUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/v2/category-business-member-config/update',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/category-business-member-config/page
 */
export function categoryBusinessMemberConfigPage(
  data: CategoryBusinessMemberConfigPageQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoCategoryBusinessMemberConfigVo>(
    '/api/v2/category-business-member-config/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/category-business-member-config/create
 */
export function categoryBusinessMemberConfigCreate(
  data: CategoryBusinessMemberConfigCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/v2/category-business-member-config/create',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询预算管理看板 /api/v2/budget-manage/dashboard
 */
export function v2BudgetManageDashboard(
  data: QueryBudgetDashboardCmd,
  opts?: ReqOpts
) {
  return request<ResponseBudgetDashboardDto>(
    '/api/v2/budget-manage/dashboard',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 导出预算管理看板 /api/v2/budget-manage/dashboard/export
 */
export function v2BudgetManageDashboardExport(
  data: QueryBudgetDashboardCmd,
  opts?: ReqOpts
) {
  return request<unknown>('/api/v2/budget-manage/dashboard/export', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 批量开启预警通知 /api/v2/budget-alert/config/enable
 */
export function v2BudgetAlertConfigEnable(
  data: BudgetAlertEnableCmd,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/budget-alert/config/enable', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 批量关闭预警通知 /api/v2/budget-alert/config/disable
 */
export function v2BudgetAlertConfigDisable(
  data: BudgetAlertDisableCmd,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/budget-alert/config/disable', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 规则校验 /api/v2/budget-adjust/validate
 */
export function v2BudgetAdjustValidate(
  data: SaveBudgetAdjustCmd,
  opts?: ReqOpts
) {
  return request<ResponseListString>('/api/v2/budget-adjust/validate', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 提交预算调整 /api/v2/budget-adjust/submit
 */
export function v2BudgetAdjustSubmit(
  data: SaveBudgetAdjustCmd,
  opts?: ReqOpts
) {
  return request<ResponseBudgetAdjustDetailDto>(
    '/api/v2/budget-adjust/submit',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 暂存预算调整 /api/v2/budget-adjust/save
 */
export function v2BudgetAdjustSave(data: SaveBudgetAdjustCmd, opts?: ReqOpts) {
  return request<ResponseBudgetAdjustDetailDto>('/api/v2/budget-adjust/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询可关联一级单列表 /api/v2/budget-adjust/linkable-demands
 */
export function v2BudgetAdjustLinkableDemands(
  data: QueryLinkableDemandCmd,
  opts?: ReqOpts
) {
  return request<ResponsePageVoLinkableDemandVo>(
    '/api/v2/budget-adjust/linkable-demands',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询预算调整单操作日志 /api/v2/budget-adjust/journals
 */
export function budgetAdjustJournalJournals(
  data: JournalReqDto,
  opts?: ReqOpts
) {
  return request<ResponseJournalResDto>('/api/v2/budget-adjust/journals', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询预算调整记录 /api/v2/budget-adjust/history
 */
export function v2BudgetAdjustHistory(
  data: BudgetAdjustHistoryCmd,
  opts?: ReqOpts
) {
  return request<ResponseBudgetAdjustHistoryDto>(
    '/api/v2/budget-adjust/history',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 发起预算调整 /api/v2/budget-adjust/create
 */
export function v2BudgetAdjustCreate(
  data: CreateBudgetAdjustCmd,
  opts?: ReqOpts
) {
  return request<ResponseBudgetAdjustDetailDto>(
    '/api/v2/budget-adjust/create',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 废弃预算调整 /api/v2/budget-adjust/abandon
 */
export function v2BudgetAdjustAbandon(
  data: AbandonBudgetAdjustCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/budget-adjust/abandon', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询单据关联任务列表 /api/v2/biz-task/list
 */
export function v2BizTaskList(params: BizTaskListForm, opts?: ReqOpts) {
  const { ...query } = params;
  return request<ResponseListBizTaskVo>(
    createUri('/api/v2/biz-task/list', query),
    { ...opts }
  );
}
/**
 * 查询单据关联任务列表（POST） /api/v2/biz-task/list
 */
export function v2BizTaskListUsingPost(data: BizTaskListForm, opts?: ReqOpts) {
  return request<ResponseListBizTaskVo>('/api/v2/biz-task/list', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询历史推荐结果（含附件 blob.data） /api/v2/biz-task/history
 */
export function v2BizTaskHistory(
  params: {
    demand_id: number;
    demand_type: MpcDemandTypeEnum;
    task_type?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListBizTaskHistoryVo>(
    createUri('/api/v2/biz-task/history', query),
    { ...opts }
  );
}
/**
 * 查询历史推荐结果（POST，含附件 blob.data） /api/v2/biz-task/history
 */
export function v2BizTaskHistoryUsingPost(
  data: BizTaskListForm,
  opts?: ReqOpts
) {
  return request<ResponseListBizTaskHistoryVo>('/api/v2/biz-task/history', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询任务详情（请求体，CLI 专用） /api/v2/biz-task/detail
 */
export function v2BizTaskDetail(data: AiIdForm, opts?: ReqOpts) {
  return request<ResponseBizTaskDetailVo>('/api/v2/biz-task/detail', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 策划达人智能推荐创建任务 /api/v2/biz-task/ai-plan-kol-recommend
 */
export function v2BizTaskAiPlanKolRecommend(
  data: BizTaskDemandForm,
  opts?: ReqOpts
) {
  return request<ResponseBizTaskVo>('/api/v2/biz-task/ai-plan-kol-recommend', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 需求方达人智能推荐创建任务 /api/v2/biz-task/ai-marketing-kol-recommend
 */
export function v2BizTaskAiMarketingKolRecommend(
  data: BizTaskMarketingKolForm,
  opts?: ReqOpts
) {
  return request<ResponseBizTaskVo>(
    '/api/v2/biz-task/ai-marketing-kol-recommend',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【比选报价单配置】编辑 /api/v2/bidding_quote_template/update
 */
export function biddingQuoteTemplateUpdate(
  data: EditBiddingQuoteTemplateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/bidding_quote_template/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【比选报价单配置】分页查询 /api/v2/bidding_quote_template/pageList
 */
export function biddingQuoteTemplatePageList(
  data: PageBiddingQuoteTemplateForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingQuoteTemplateVo>(
    '/api/v2/bidding_quote_template/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【比选报价单配置】删除 /api/v2/bidding_quote_template/delete
 */
export function biddingQuoteTemplateDelete(
  data: EditBiddingQuoteTemplateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/bidding_quote_template/delete', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【比选报价单配置】新增 /api/v2/bidding_quote_template/create
 */
export function biddingQuoteTemplateCreate(
  data: CreateBiddingQuoteTemplateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/bidding_quote_template/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 分页查询通知模板列表 /api/v2/bidding_notification_template/page
 */
export function v2BiddingNotificationTemplatePage(
  data: PageNotificationTemplateForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingNotificationTemplateVo>(
    '/api/v2/bidding_notification_template/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 编辑模板内容 /api/v2/bidding_notification_template/edit
 */
export function v2BiddingNotificationTemplateEdit(
  data: EditNotificationTemplateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/bidding_notification_template/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 提交评分 /api/v2/bidding_move/submit_score
 */
export function biddingMoveSubmitScore(data: BiddingMoveDto, opts?: ReqOpts) {
  return request<ResponseBiddingMoveVo>('/api/v2/bidding_move/submit_score', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 招采流转 /api/v2/bidding_move/move
 */
export function biddingMoveMove(data: BiddingMoveDto, opts?: ReqOpts) {
  return request<ResponseBiddingMoveVo>('/api/v2/bidding_move/move', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【内部群配置】分页查询 /api/v2/bidding_group_config/internal/page
 */
export function v2BiddingGroupConfigInternalPage(
  data: PageInternalGroupForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingInternalGroupVo>(
    '/api/v2/bidding_group_config/internal/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【内部群配置】编辑 /api/v2/bidding_group_config/internal/edit
 */
export function v2BiddingGroupConfigInternalEdit(
  data: EditInternalGroupForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/bidding_group_config/internal/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【内部群配置】新增 /api/v2/bidding_group_config/internal/create
 */
export function v2BiddingGroupConfigInternalCreate(
  data: CreateInternalGroupForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/bidding_group_config/internal/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【外部产品群配置】分页查询 /api/v2/bidding_group_config/external_product/page
 */
export function v2BiddingGroupConfigExternalProductPage(
  data: PageExternalProductGroupForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingExternalProductGroupVo>(
    '/api/v2/bidding_group_config/external_product/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【外部产品群配置】编辑 /api/v2/bidding_group_config/external_product/edit
 */
export function v2BiddingGroupConfigExternalProductEdit(
  data: EditExternalProductGroupForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/v2/bidding_group_config/external_product/edit',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【外部产品群配置】新增 /api/v2/bidding_group_config/external_product/create
 */
export function v2BiddingGroupConfigExternalProductCreate(
  data: CreateExternalProductGroupForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/v2/bidding_group_config/external_product/create',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【外部发需群配置】分页查询 /api/v2/bidding_group_config/external_demand/page
 */
export function v2BiddingGroupConfigExternalDemandPage(
  data: PageExternalDemandGroupForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingExternalDemandGroupVo>(
    '/api/v2/bidding_group_config/external_demand/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【外部发需群配置】编辑 /api/v2/bidding_group_config/external_demand/edit
 */
export function v2BiddingGroupConfigExternalDemandEdit(
  data: EditExternalDemandGroupForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/v2/bidding_group_config/external_demand/edit',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【外部发需群配置】新增 /api/v2/bidding_group_config/external_demand/create
 */
export function v2BiddingGroupConfigExternalDemandCreate(
  data: CreateExternalDemandGroupForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/v2/bidding_group_config/external_demand/create',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding_evaluation_member/update
 */
export function biddingEvaluationMemberUpdate(
  data: BiddingEvaluationMemberUpdateForm,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/v2/bidding_evaluation_member/update', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding_evaluation_member/search
 */
export function biddingEvaluationMemberSearch(
  data: BiddingEvaluationMemberSearchForm,
  opts?: ReqOpts
) {
  return request<ResponseListBiddingEvaluationMemberSearchVo>(
    '/api/v2/bidding_evaluation_member/search',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding_evaluation_member/create
 */
export function biddingEvaluationMemberCreate(
  data: BiddingEvaluationMemberCreateForm,
  opts?: ReqOpts
) {
  return request<ResponseBiddingEvaluationMemberDto>(
    '/api/v2/bidding_evaluation_member/create',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 手动触发听标通知（E9） /api/v2/bidding/{id}/trigger_presentation_supplier_product_notice
 */
export function v2BiddingByIdTriggerPresentationSupplierProductNotice(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseVoid>(
    `/api/v2/bidding/${id}/trigger_presentation_supplier_product_notice`,
    { method: 'POST', ...opts }
  );
}
/**
 * 手动触发听标通知（E8） /api/v2/bidding/{id}/trigger_presentation_internal_notice
 */
export function v2BiddingByIdTriggerPresentationInternalNotice(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseVoid>(
    `/api/v2/bidding/${id}/trigger_presentation_internal_notice`,
    { method: 'POST', ...opts }
  );
}
/**
 * 手动触发议价通知（E13） /api/v2/bidding/{id}/trigger_negotiation_supplier_product_notice
 */
export function v2BiddingByIdTriggerNegotiationSupplierProductNotice(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseVoid>(
    `/api/v2/bidding/${id}/trigger_negotiation_supplier_product_notice`,
    { method: 'POST', ...opts }
  );
}
/**
 * 手动触发议价通知（E12） /api/v2/bidding/{id}/trigger_negotiation_internal_notice
 */
export function v2BiddingByIdTriggerNegotiationInternalNotice(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseVoid>(
    `/api/v2/bidding/${id}/trigger_negotiation_internal_notice`,
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/bidding/workbenches/menus_count
 */
export function innerBiddingWorkbenchesMenusCount(
  data: BiddingMenuCountForm,
  opts?: ReqOpts
) {
  return request<ResponseListBiddingWorkbenchMenuCountDto>(
    '/api/v2/bidding/workbenches/menus_count',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding/workbenches/list
 */
export function innerBiddingWorkbenchesList(
  data: BiddingInnerWorkbenchForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingWorkbenches>(
    '/api/v2/bidding/workbenches/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding/view-invite-record
 */
export function biddingViewInviteRecord(
  data: BiddingSupplierInviteRecordForm,
  opts?: ReqOpts
) {
  return request<ResponsePageVoBiddingSupplierInviteRecordVo>(
    '/api/v2/bidding/view-invite-record',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding/turn_detail
 */
export function biddingTurnDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseBiddingDetailsResDto>(
    createUri('/api/v2/bidding/turn_detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/bidding/supplier_his_post
 */
export function biddingHisSupplierBiddingDetail(
  params: {
    bidding_id: number;
    supplier_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseBiddingSupplierHisDto>(
    createUri('/api/v2/bidding/supplier_his_post', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/bidding/state/journals
 */
export function biddingStateJournals(data: BiddingReqDto, opts?: ReqOpts) {
  return request<ResponseListBiddingStateJournalResDto>(
    '/api/v2/bidding/state/journals',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding/save_or_update/bidding_qa_time
 */
export function biddingSaveOrUpdateBiddingQaTime(
  data: SaveOrUpdateBiddingQaTimeForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/api/v2/bidding/save_or_update/bidding_qa_time',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding/save_or_update/bidding_qa_attach
 */
export function biddingSaveOrUpdateBiddingQaAttach(
  data: SaveOrUpdateBiddingQaAttachForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/api/v2/bidding/save_or_update/bidding_qa_attach',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding/review_his_post
 */
export function biddingReviewHisPost(
  data: BiddingReviewHisPostFrom,
  opts?: ReqOpts
) {
  return request<ResponseBiddingJudgeReviewHisDto>(
    '/api/v2/bidding/review_his_post',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding/reminder
 */
export function biddingReminder(data: BiddingReqDto, opts?: ReqOpts) {
  return request<ResponseReminderResDto>('/api/v2/bidding/reminder', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding/journals
 */
export function biddingJournals(data: JournalReqDto, opts?: ReqOpts) {
  return request<ResponseJournalResDto>('/api/v2/bidding/journals', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 导入报价单配置 /api/v2/bidding/import_quote_config
 */
export function biddingImportQuoteConfig(
  data?: GenericFormData<{
    configs: string;
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  return request<ResponseImportQuoteConfigResponse>(
    '/api/v2/bidding/import_quote_config',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding/his_post
 */
export function biddingHisBiddingDetail(
  params: {
    bidding_id: number;
    turns_number: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListBiddingSupplierHisListDto>(
    createUri('/api/v2/bidding/his_post', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 导出报价明细为 Excel /api/v2/bidding/export_quote_detail
 */
export function biddingExportQuoteDetail(
  data: ExportQuoteDetailForm,
  opts?: ReqOpts
) {
  return request<unknown>('/api/v2/bidding/export_quote_detail', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 导出报价单配置 /api/v2/bidding/export_quote_config
 */
export function biddingExportQuoteConfig(
  data: ExportQuoteConfigForm,
  opts?: ReqOpts
) {
  return request<unknown>('/api/v2/bidding/export_quote_config', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding/edit_member
 */
export function biddingEditMember(data: EditBiddingMemberForm, opts?: ReqOpts) {
  return request<ResponseObject>('/api/v2/bidding/edit_member', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding/edit_base_info
 */
export function biddingEditBaseInfo(
  data: EditBiddingBaseInfoForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v2/bidding/edit_base_info', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding/detail
 */
export function biddingDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseBiddingDetailsResDto>(
    createUri('/api/v2/bidding/detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/bidding/delay-score-time
 */
export function biddingDelayScoreTime(
  data: DelayScoreTimeForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v2/bidding/delay-score-time', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding/create
 */
export function biddingCreate(data: CreateBiddingForm, opts?: ReqOpts) {
  return request<ResponseCreateBiddingResultVo>('/api/v2/bidding/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding/confidentiality/return-suppliers
 */
export function biddingConfidentialityReturnSuppliers(
  data: ReturnSuppliersForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/api/v2/bidding/confidentiality/return-suppliers',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding/confidentiality/pass-confirm
 */
export function biddingConfidentialityPassConfidentialityConfirm(
  params: {
    bidding_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseObject>(
    createUri('/api/v2/bidding/confidentiality/pass-confirm', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/bidding/batch-submit
 */
export function biddingBatchSubmit(
  data: BatchBiddingSubmitForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v2/bidding/batch-submit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding/add-supplier
 */
export function biddingAddNewSupplier(
  data: AddBiddingSupplierForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v2/bidding/add-supplier', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding-schedule/negotiate/save
 */
export function biddingScheduleSaveNegotiateSchedule(
  data: BiddingNegotiateScheduleSaveForm,
  opts?: ReqOpts
) {
  return request<ResponseBoolean>('/api/v2/bidding-schedule/negotiate/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding-schedule/negotiate/detail
 */
export function biddingScheduleGetNegotiateSchedule(
  data: BiddingScheduleQueryForm,
  opts?: ReqOpts
) {
  return request<ResponseBiddingNegotiateScheduleVo>(
    '/api/v2/bidding-schedule/negotiate/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding-schedule/flow/save
 */
export function biddingScheduleSaveFlowSchedule(
  data: BiddingFlowScheduleSaveForm,
  opts?: ReqOpts
) {
  return request<ResponseBoolean>('/api/v2/bidding-schedule/flow/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding-schedule/flow/detail
 */
export function biddingScheduleGetFlowSchedule(
  data: BiddingScheduleQueryForm,
  opts?: ReqOpts
) {
  return request<ResponseBiddingFlowScheduleVo>(
    '/api/v2/bidding-schedule/flow/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding-schedule/calendar/range
 */
export function biddingScheduleGetCalendarRange(
  data: HrmsCalendarRangeForm,
  opts?: ReqOpts
) {
  return request<ResponseListHrmsCalendarDayVo>(
    '/api/v2/bidding-schedule/calendar/range',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/bidding-schedule/bid/save
 */
export function biddingScheduleSaveBidSchedule(
  data: BiddingBidScheduleSaveForm,
  opts?: ReqOpts
) {
  return request<ResponseBoolean>('/api/v2/bidding-schedule/bid/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/bidding-schedule/bid/detail
 */
export function biddingScheduleGetBidSchedule(
  data: BiddingScheduleQueryForm,
  opts?: ReqOpts
) {
  return request<ResponseBiddingBidScheduleVo>(
    '/api/v2/bidding-schedule/bid/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【比选评分模板】分页查询比选评分模板列表 /api/v2/bidding-plan-template/page
 */
export function biddingPlanTemplatePage(
  data: QueryListBiddingPlanTemplateForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingPlanTemplateListVo>(
    '/api/v2/bidding-plan-template/page',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【比选评分模板】列表查询比选评分模板列表 /api/v2/bidding-plan-template/list
 */
export function biddingPlanTemplateList(
  data: QueryListBiddingPlanTemplateForm,
  opts?: ReqOpts
) {
  return request<ResponseListBiddingPlanTemplateListVo>(
    '/api/v2/bidding-plan-template/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【比选评分模板】编辑比选评分模板 /api/v2/bidding-plan-template/edit
 */
export function biddingPlanTemplateEdit(
  data: EditBiddingPlanTemplateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/bidding-plan-template/edit', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【比选评分模板】创建模板 /api/v2/bidding-plan-template/create
 */
export function biddingPlanTemplateCreate(
  data: CreateBiddingPlanTemplateForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/bidding-plan-template/create', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/attachments/upload
 */
export function attachmentBlobUpload(
  params: {
    model?: string;
    model_id?: number;
    attach_type?: string;
  },
  data?: GenericFormData<{
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseAttachmentBlobDto>(
    createUri('/api/v2/attachments/upload', query),
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ai/trumpet/list/todo
 */
export function aiTrumpetTodoList(
  data?: WorkspaceTrumpetTodoListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/trumpet/list/todo', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ai/trumpet/list/done
 */
export function aiTrumpetDoneList(
  data?: WorkspaceTrumpetDoneListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/trumpet/list/done', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ai/trumpet/list/all
 */
export function aiTrumpetAllList(
  data?: WorkspaceTrumpetAllListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/trumpet/list/all', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ai/trumpet/export-json
 */
export function aiTrumpetExportJson(
  data?: WorkspaceTrumpetAllListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/trumpet/export-json', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 小喇叭详情 /api/v2/ai/trumpet/detail
 */
export function aiTrumpetDetail(data: AiIdForm, opts?: ReqOpts) {
  return request<AiResponseTrumpetCasePageDetailVo>(
    '/api/v2/ai/trumpet/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 供应商年度金额统计 /api/v2/ai/supplier/yearly-amount
 */
export function aiSupplierYearlyAmount(
  data: AiSupplierAmountForm,
  opts?: ReqOpts
) {
  return request<AiResponseObject>('/api/v2/ai/supplier/yearly-amount', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 供应商统计汇总 /api/v2/ai/supplier/stats-summary
 */
export function aiSupplierStatsSummary(
  data: AiSupplierStatsSummaryForm,
  opts?: ReqOpts
) {
  return request<AiResponseAiSupplierStatsSummaryVo>(
    '/api/v2/ai/supplier/stats-summary',
    { method: 'POST', data, ...opts }
  );
}
/**
 * Solar通用查询 /api/v2/ai/supplier/solar/invoke
 */
export function aiSupplierSolarInvoke(
  data: SolarInvokeRequest,
  opts?: ReqOpts
) {
  return request<SolarInvokeResponse>('/api/v2/ai/supplier/solar/invoke', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 供应商评级记录 /api/v2/ai/supplier/rating-records
 */
export function aiSupplierRatingRecords(
  data: AiPageableIdForm,
  opts?: ReqOpts
) {
  return request<AiResponseObject>('/api/v2/ai/supplier/rating-records', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 供应商操作记录 /api/v2/ai/supplier/op-records
 */
export function aiSupplierOpRecords(data: AiPageableIdForm, opts?: ReqOpts) {
  return request<AiResponseObject>('/api/v2/ai/supplier/op-records', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 供应商月度金额统计 /api/v2/ai/supplier/monthly-amount
 */
export function aiSupplierMonthlyAmount(
  data: AiSupplierAmountForm,
  opts?: ReqOpts
) {
  return request<AiResponseObject>('/api/v2/ai/supplier/monthly-amount', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 供应商列表 /api/v2/ai/supplier/list
 */
export function aiSupplierList(data?: QueryValidCsCommand, opts?: ReqOpts) {
  return request<StreamingResponseBody>('/api/v2/ai/supplier/list', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 供应商导出JSON /api/v2/ai/supplier/export-json
 */
export function aiSupplierExportJson(
  data?: QueryValidCsCommand,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/supplier/export-json', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 供应商详情 /api/v2/ai/supplier/detail
 */
export function aiSupplierDetail(data: AiIdForm, opts?: ReqOpts) {
  return request<AiResponseChannelSupplierDetailDto>(
    '/api/v2/ai/supplier/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 发票列表 /api/v2/ai/invoice/list
 */
export function aiInvoiceList(data?: InvoiceQueryPageForm, opts?: ReqOpts) {
  return request<AiResponseListInvoiceListDto>('/api/v2/ai/invoice/list', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 发票详情 /api/v2/ai/invoice/detail
 */
export function aiInvoiceDetail(data: AiIdForm, opts?: ReqOpts) {
  return request<AiResponseInvoiceDetailDto>('/api/v2/ai/invoice/detail', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 上传文件 /api/v2/ai/file/upload
 */
export function v2AiFileUpload(
  data?: GenericFormData<{
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  return request<AiResponseAttachmentBlobDto>('/api/v2/ai/file/upload', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 获取 S3 预签名上传 URL（第一步） /api/v2/ai/file/presign-upload
 */
export function v2AiFilePresignUpload(
  data: AiPresignUploadForm,
  opts?: ReqOpts
) {
  return request<AiResponseAiPresignUploadVo>(
    '/api/v2/ai/file/presign-upload',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 完成预签名上传（第二步） /api/v2/ai/file/complete-presign-upload
 */
export function v2AiFileCompletePresignUpload(
  params: {
    blob_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<AiResponseAttachmentBlobDto>(
    createUri('/api/v2/ai/file/complete-presign-upload', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 需求供应商报价查询 /api/v2/ai/demand/supplier-quote-reports
 */
export function aiDemandSupplierQuoteReports(
  data: AiDemandSupplierQuoteForm,
  opts?: ReqOpts
) {
  return request<AiResponseDemandSupplierQuoteWithSettleReportVo>(
    '/api/v2/ai/demand/supplier-quote-reports',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 需求待办列表 /api/v2/ai/demand/list/todo
 */
export function aiDemandTodoList(
  data?: WorkspaceDemandTodoListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/demand/list/todo', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 需求已办列表 /api/v2/ai/demand/list/done
 */
export function aiDemandDoneList(
  data?: WorkspaceDemandDoneListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/demand/list/done', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 需求全部列表 /api/v2/ai/demand/list/all
 */
export function aiDemandAllList(
  data?: WorkspaceDemandAllListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/demand/list/all', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 需求流程日志 /api/v2/ai/demand/journals
 */
export function aiDemandJournals(data: AiIdForm, opts?: ReqOpts) {
  return request<AiResponseJournalResDto>('/api/v2/ai/demand/journals', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 需求导出JSON /api/v2/ai/demand/export-json
 */
export function aiDemandExportJson(
  data?: WorkspaceDemandAllListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/demand/export-json', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 供应商报价差异对比 /api/v2/ai/demand/diff/supplier_quote
 */
export function aiDemandDiffSupplierQuote(
  data: AiDemandSupplierQuoteDiffForm,
  opts?: ReqOpts
) {
  return request<AiResponseDemandSupplierQuoteDiffVo>(
    '/api/v2/ai/demand/diff/supplier_quote',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 需求详情 /api/v2/ai/demand/detail
 */
export function aiDemandDetail(data: AiIdForm, opts?: ReqOpts) {
  return request<AiResponseDemandDetailsResDto>('/api/v2/ai/demand/detail', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 需求历史轮次详情 /api/v2/ai/demand/detail-his-round
 */
export function aiDemandDetailHisRound(
  data: AiDemandHisRoundForm,
  opts?: ReqOpts
) {
  return request<AiResponseDemandHisDetailResDto>(
    '/api/v2/ai/demand/detail-his-round',
    { method: 'POST', data, ...opts }
  );
}
/**
 * KOL询价用详情（根据达人主页地址获取资源列表及合作询价详情） /api/v2/ai/demand-source/kol-inquiry-detail
 */
export function aiDemandSourceKolInquiryDetail(
  data: AiKolInquiryDetailForm,
  opts?: ReqOpts
) {
  return request<AiResponseAiKolInquiryDetailVo>(
    '/api/v2/ai/demand-source/kol-inquiry-detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 一询单详情（含定制排查列表、供应商列表等全部内容） /api/v2/ai/demand-source/detail
 */
export function aiDemandSourceDetail(
  data: AiDemandSourceQueryForm,
  opts?: ReqOpts
) {
  return request<AiResponseDemandSourceDetailVo>(
    '/api/v2/ai/demand-source/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ai/contract/types
 */
export function aiContractTypes(opts?: ReqOpts) {
  return request<AiResponseListContractTypeVo>('/api/v2/ai/contract/types', {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/v2/ai/contract/pending-list
 */
export function aiContractPendingList(
  data?: PendingContractQuery,
  opts?: ReqOpts
) {
  return request<AiResponsePageResultPendingContractVo>(
    '/api/v2/ai/contract/pending-list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ai/common/filter-options
 */
export function aiCommonFilterOptions(
  data?: CommonOptionsForm,
  opts?: ReqOpts
) {
  return request<AiResponseListOptionDetailWrapperVoOptionBase>(
    '/api/v2/ai/common/filter-options',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ai/common/exchange-rate
 */
export function aiCommonExchangeRate(data: ExchangeRateForm, opts?: ReqOpts) {
  return request<AiResponseListExchangeRateDto>(
    '/api/v2/ai/common/exchange-rate',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/ai/closed-loop/list/todo
 */
export function aiClosedLoopTodoList(
  data?: WorkspaceClosedLoopTodoListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/closed-loop/list/todo', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ai/closed-loop/list/done
 */
export function aiClosedLoopDoneList(
  data?: WorkspaceClosedLoopDoneListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/closed-loop/list/done', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ai/closed-loop/list/all
 */
export function aiClosedLoopAllList(
  data?: WorkspaceClosedLoopAllListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/closed-loop/list/all', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/ai/closed-loop/export-json
 */
export function aiClosedLoopExportJson(
  data?: WorkspaceClosedLoopAllListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/closed-loop/export-json', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 闭环详情 /api/v2/ai/closed-loop/detail
 */
export function aiClosedLoopDetail(data: AiIdForm, opts?: ReqOpts) {
  return request<AiResponseClosedLoopDemandDetailVo>(
    '/api/v2/ai/closed-loop/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 比选轮次详情 /api/v2/ai/bidding/turn-detail
 */
export function aiBiddingTurnDetail(data: AiIdForm, opts?: ReqOpts) {
  return request<AiResponseBiddingDetailsResDto>(
    '/api/v2/ai/bidding/turn-detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 比选供应商历史 /api/v2/ai/bidding/supplier-history
 */
export function aiBiddingSupplierHistory(
  data: AiBiddingSupplierHistoryForm,
  opts?: ReqOpts
) {
  return request<AiResponseBiddingSupplierHisDto>(
    '/api/v2/ai/bidding/supplier-history',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 比选状态日志 /api/v2/ai/bidding/state-journals
 */
export function aiBiddingStateJournals(data: AiIdForm, opts?: ReqOpts) {
  return request<AiResponseListBiddingStateJournalResDto>(
    '/api/v2/ai/bidding/state-journals',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 比选待办列表 /api/v2/ai/bidding/list/todo
 */
export function aiBiddingTodoList(
  data?: MyWorkspaceBiddingTodoListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/bidding/list/todo', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 比选已办列表 /api/v2/ai/bidding/list/done
 */
export function aiBiddingDoneList(
  data?: MyWorkspaceBiddingDoneListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/bidding/list/done', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 比选全部列表 /api/v2/ai/bidding/list/all
 */
export function aiBiddingAllList(
  data?: MyWorkspaceBiddingAllListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/bidding/list/all', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 比选流程日志 /api/v2/ai/bidding/journals
 */
export function aiBiddingJournals(data: AiIdForm, opts?: ReqOpts) {
  return request<AiResponseJournalResDto>('/api/v2/ai/bidding/journals', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 比选导出JSON /api/v2/ai/bidding/export-json
 */
export function aiBiddingExportJson(
  data?: MyWorkspaceBiddingAllListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>('/api/v2/ai/bidding/export-json', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 供应商维度比选单导出JSON /api/v2/ai/bidding/export-bidding-list
 */
export function aiBiddingExportBiddingList(
  data: AiExportBiddingListForm,
  opts?: ReqOpts
) {
  return request<StreamingResponseBody>(
    '/api/v2/ai/bidding/export-bidding-list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 比选详情 /api/v2/ai/bidding/detail
 */
export function aiBiddingDetail(data: AiIdForm, opts?: ReqOpts) {
  return request<AiResponseBiddingDetailsResDto>('/api/v2/ai/bidding/detail', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 获取附件下载链接或预览链接 /api/v2/ai/attachment/download-url
 */
export function aiAttachmentDownloadUrl(
  data: AiAttachmentDownloadUrlForm,
  opts?: ReqOpts
) {
  return request<AiResponseString>('/api/v2/ai/attachment/download-url', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 作废核价记录 /api/v2/advance_price_check/void
 */
export function v2AdvancePriceCheckVoid(data: VoidForm, opts?: ReqOpts) {
  return request<ResponseVoid>('/api/v2/advance_price_check/void', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 手动触发AI核价（测试用） /api/v2/advance_price_check/trigger_ai_price_check
 */
export function v2AdvancePriceCheckTriggerAiPriceCheck(
  data?: TriggerForm,
  opts?: ReqOpts
) {
  return request<ResponseString>(
    '/api/v2/advance_price_check/trigger_ai_price_check',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 提前核价 - 策划提交确认达人 /api/v2/advance_price_check/plan/submit
 */
export function v2AdvancePriceCheckPlanSubmit(
  data: PlanSubmitForm,
  opts?: ReqOpts
) {
  return request<ResponsePlanSubmitResp>(
    '/api/v2/advance_price_check/plan/submit',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 提前核价 - 策划暂存确认达人 /api/v2/advance_price_check/plan/draft
 */
export function v2AdvancePriceCheckPlanDraft(
  data: PlanDraftForm,
  opts?: ReqOpts
) {
  return request<ResponsePlanDraftResp>(
    '/api/v2/advance_price_check/plan/draft',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 提前核价 - 策划关闭核价记录 /api/v2/advance_price_check/plan/close
 */
export function v2AdvancePriceCheckPlanClose(
  data: PlanCloseForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/advance_price_check/plan/close', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 提前核价记录列表 /api/v2/advance_price_check/list
 */
export function v2AdvancePriceCheckList(data: ListForm, opts?: ReqOpts) {
  return request<ResponseListAdvancePriceCheckVo>(
    '/api/v2/advance_price_check/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 导出核价结果 /api/v2/advance_price_check/export
 */
export function v2AdvancePriceCheckExport(data: ExportForm, opts?: ReqOpts) {
  return request<unknown>('/api/v2/advance_price_check/export', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 暂存核价信息 /api/v2/advance_price_check/draft
 */
export function v2AdvancePriceCheckDraft(data: DraftForm, opts?: ReqOpts) {
  return request<ResponseDraftResp>('/api/v2/advance_price_check/draft', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 提前核价详情 /api/v2/advance_price_check/detail
 */
export function v2AdvancePriceCheckDetail(data: DetailForm, opts?: ReqOpts) {
  return request<ResponseAdvancePriceCheckDetailVo>(
    '/api/v2/advance_price_check/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 商务确认核价 /api/v2/advance_price_check/confirm
 */
export function v2AdvancePriceCheckConfirm(data: ConfirmForm, opts?: ReqOpts) {
  return request<ResponseVoid>('/api/v2/advance_price_check/confirm', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 基准价查询（根据链接和数量） /api/v2/advance_price_check/base/price
 */
export function v2AdvancePriceCheckBasePrice(
  data: GetBasePriceByUrlForm,
  opts?: ReqOpts
) {
  return request<ResponseListDemandSourceReferencePriceVo>(
    '/api/v2/advance_price_check/base/price',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/feeControls/requirement/submit
 */
export function feeControlSystemRequirementSubmit(
  data: SubmitFeeDto,
  opts?: ReqOpts
) {
  return request<ResponseFeeControlSubmitResVo>(
    '/api/v2/feeControls/requirement/submit',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/requirement/submit
 */
export function feeControlSystemRequirementSubmitUsingPost(
  data: SubmitFeeDto,
  opts?: ReqOpts
) {
  return request<ResponseFeeControlSubmitResVo>(
    '/api/v1/feeControls/requirement/submit',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/requirement/start
 */
export function feeControlSystemStartRequirement(
  data: StartRequirementForm,
  opts?: ReqOpts
) {
  return request<ResponseStartRequirementDto>(
    '/api/v1/feeControls/requirement/start',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/feeControls/requirement/start
 */
export function feeControlSystemStartRequirementUsingPost(
  data: StartRequirementForm,
  opts?: ReqOpts
) {
  return request<ResponseStartRequirementDto>(
    '/api/v2/feeControls/requirement/start',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/feeControls/requirement/reimbursement
 */
export function feeControlSystemReimburseRequirement(
  data: ReimburseRequirementForm,
  opts?: ReqOpts
) {
  return request<ResponseReimburseRequirementDto>(
    '/api/v2/feeControls/requirement/reimbursement',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/requirement/reimbursement
 */
export function feeControlSystemReimburseRequirementUsingPost(
  data: ReimburseRequirementForm,
  opts?: ReqOpts
) {
  return request<ResponseReimburseRequirementDto>(
    '/api/v1/feeControls/requirement/reimbursement',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/requirement/ownerChange
 */
export function feeControlSystemChangeOwner(
  data: ChangeOwnerForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/api/v1/feeControls/requirement/ownerChange',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/feeControls/requirement/ownerChange
 */
export function feeControlSystemChangeOwnerUsingPost(
  data: ChangeOwnerForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/api/v2/feeControls/requirement/ownerChange',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/requirement/drop
 */
export function feeControlSystemDropRequirement(
  data: DropRequirementForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v1/feeControls/requirement/drop', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/feeControls/requirement/drop
 */
export function feeControlSystemDropRequirementUsingPost(
  data: DropRequirementForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v2/feeControls/requirement/drop', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/feeControls/requirement/confirm/update
 */
export function feeControlSystemRequirementConfirmUpdate(
  data: RequirementConfirmChangeReq,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/api/v2/feeControls/requirement/confirm/update',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/requirement/confirm/update
 */
export function feeControlSystemRequirementConfirmUpdateUsingPost(
  data: RequirementConfirmChangeReq,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/api/v1/feeControls/requirement/confirm/update',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/requirement/confirm/check
 */
export function feeControlSystemRequirementConfirmCheck(
  data: RequirementConfirmCheckReq,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/api/v1/feeControls/requirement/confirm/check',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/feeControls/requirement/confirm/check
 */
export function feeControlSystemRequirementConfirmCheckUsingPost(
  data: RequirementConfirmCheckReq,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/api/v2/feeControls/requirement/confirm/check',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/feeControls/requirement/close
 */
export function feeControlSystemCloseRequirement(
  data: CloseRequirementForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v2/feeControls/requirement/close', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v1/feeControls/requirement/close
 */
export function feeControlSystemCloseRequirementUsingPost(
  data: CloseRequirementForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>('/api/v1/feeControls/requirement/close', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/feeControls/requirement/changeProductLine
 */
export function feeControlSystemChangeProductLine(
  data: ChangeProductLineForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/api/v2/feeControls/requirement/changeProductLine',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/requirement/changeProductLine
 */
export function feeControlSystemChangeProductLineUsingPost(
  data: ChangeProductLineForm,
  opts?: ReqOpts
) {
  return request<ResponseObject>(
    '/api/v1/feeControls/requirement/changeProductLine',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/rate/pageList
 */
export function feeControlSystemQueryRateList(
  data: RateQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageDtoRateQueryDto>(
    '/api/v1/feeControls/rate/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/feeControls/rate/pageList
 */
export function feeControlSystemQueryRateListUsingPost(
  data: RateQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageDtoRateQueryDto>(
    '/api/v2/feeControls/rate/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/project/pageList
 */
export function feeControlSystemQueryProjectList(
  data: ProjectListQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageDtoProjectQueryDto>(
    '/api/v1/feeControls/project/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/feeControls/project/pageList
 */
export function feeControlSystemQueryProjectListUsingPost(
  data: ProjectListQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageDtoProjectQueryDto>(
    '/api/v2/feeControls/project/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/feeControls/productLine/list
 */
export function feeControlSystemQueryProductLineList(opts?: ReqOpts) {
  return request<ResponsePageDtoProductLineQueryDto>(
    '/api/v2/feeControls/productLine/list',
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/productLine/list
 */
export function feeControlSystemQueryProductLineListUsingPost(opts?: ReqOpts) {
  return request<ResponsePageDtoProductLineQueryDto>(
    '/api/v1/feeControls/productLine/list',
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/prerequisition/pageList
 */
export function feeControlSystemQueryPreRequisitionList(
  data: PreRequisitionListQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageDtoPreRequisitionQueryDto>(
    '/api/v1/feeControls/prerequisition/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v2/feeControls/prerequisition/pageList
 */
export function feeControlSystemQueryPreRequisitionListUsingPost(
  data: PreRequisitionListQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageDtoPreRequisitionQueryDto>(
    '/api/v2/feeControls/prerequisition/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/feeControlClosed
 */
export function feeControlSystemFeeControlClosed(
  data: FeeControlCloseDto,
  opts?: ReqOpts
) {
  return request<Response>('/api/v1/feeControls/feeControlClosed', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/feeControls/feeControlClosed
 */
export function feeControlSystemFeeControlClosedUsingPost(
  data: FeeControlCloseDto,
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/feeControls/feeControlClosed', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/feeControls/contract/pageList
 */
export function feeControlSystemQueryContractList(
  data: ContractQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageDtoContractQueryDto>(
    '/api/v2/feeControls/contract/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/feeControls/contract/pageList
 */
export function feeControlSystemQueryContractListUsingPost(
  data: ContractQueryForm,
  opts?: ReqOpts
) {
  return request<ResponsePageDtoContractQueryDto>(
    '/api/v1/feeControls/contract/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/v1/dspf/sync
 */
export function dspfSync(data: SyncCommand, opts?: ReqOpts) {
  return request<ResponseString>('/api/v1/dspf/sync', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v1/dspf/config-url
 */
export function dspfConfigUrl(
  params: {
    dspf_mpc_no?: string;
    product_number: string;
    requirement_group: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(createUri('/api/v1/dspf/config-url', query), {
    ...opts,
  });
}
/**
 * unknown /api/v1/dspf/config-url
 */
export function dspfConfigUrlUsingPost(data: ConfigUrlDto, opts?: ReqOpts) {
  return request<ResponseConfigUrlResult>('/api/v1/dspf/config-url', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 【供应商侧】我的工作台-预付流程全部列表 /api/outsource/v2/workspace/prepayment/settled_upload_list
 */
export function outsourceMyWorkspaceGetPrepaymentSettledUploadList(
  data: OsWorkspacePrepaymentTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultOsWorkspacePrepaymentListVo>(
    '/api/outsource/v2/workspace/prepayment/settled_upload_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【供应商侧】我的工作台-预付流程全部列表 /api/outsource/v2/workspace/prepayment/all_list
 */
export function outsourceMyWorkspaceGetPrepaymentAllList(
  data: OsWorkspacePrepaymentTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultOsWorkspacePrepaymentListVo>(
    '/api/outsource/v2/workspace/prepayment/all_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【供应商侧】我的工作台-常规采购待办列表 /api/outsource/v2/workspace/demand/todo_list
 */
export function outsourceMyWorkspaceGetDemandTodoList(
  data: OsWorkspaceDemandTodoListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultOsWorkspaceDemandTodoVo>(
    '/api/outsource/v2/workspace/demand/todo_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/workbenches/list
 */
export function outsourceWorkbenchesList(
  data: OutsourceWorkbenchForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBaseOutsourceWorkbenches>(
    '/api/outsource/v2/workbenches/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/temporary/select
 */
export function outsourceDemandCommonTemporarySelectDemand(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/outsource/v2/temporary/select', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/outsource/v2/temporary/save
 */
export function outsourceDemandCommonTemporarySaveDemand(
  data: DemandTemporaryForm,
  opts?: ReqOpts
) {
  return request<ResponseString>('/api/outsource/v2/temporary/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/suppliers/set_current
 */
export function outsourceSuppliersSetCurrent(
  data: OutsourceSetCurrentForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/outsource/v2/suppliers/set_current', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/suppliers/edit
 */
export function outsourceSuppliersEdit(opts?: ReqOpts) {
  return request<ResponseRubySupplier>('/api/outsource/v2/suppliers/edit', {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/suppliers/detail
 */
export function outsourceSuppliersDetail(opts?: ReqOpts) {
  return request<ResponseRubySupplier>('/api/outsource/v2/suppliers/detail', {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/suppliers/apply_audit
 */
export function outsourceSuppliersApplyAudit(opts?: ReqOpts) {
  return request<ResponseVoid>('/api/outsource/v2/suppliers/apply_audit', {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/supplier-new/update-vmdm-bank-account
 */
export function outsourceNewSupplierUpdateVmdmBankAccount(
  data: UpdateVmdmBankCommand,
  opts?: ReqOpts
) {
  return request<ResponseVmdmSupplierBankVo>(
    '/api/outsource/v2/supplier-new/update-vmdm-bank-account',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/solar/nonauth/invoke
 */
export function solarNonAuthInvoke(data: SolarInvokeRequest, opts?: ReqOpts) {
  return request<SolarInvokeResponse>(
    '/api/outsource/v2/solar/nonauth/invoke',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/solar/cooperation/invoke
 */
export function solarCooperationInvoke(
  data: SolarInvokeRequest,
  opts?: ReqOpts
) {
  return request<SolarInvokeResponse>(
    '/api/outsource/v2/solar/cooperation/invoke',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/sessions
 */
export function outsourceSessionsLogin(
  data: OutsourceLoginForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/outsource/v2/sessions', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/sessions
 */
export function outsourceSessionsLoginOut(opts?: ReqOpts) {
  return request<ResponseVoid>('/api/outsource/v2/sessions', {
    method: 'DELETE',
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/sessions/reset_password
 */
export function outsourceSessionsResetPassword(
  data: OutsourceResetPasswordForm,
  opts?: ReqOpts
) {
  return request<ResponseOutsourceResetPasswordResultDto>(
    '/api/outsource/v2/sessions/reset_password',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/sessions/modify_password
 */
export function outsourceSessionsModifyPassword(
  data: OutsourceModifyPasswordForm,
  opts?: ReqOpts
) {
  return request<ResponseOutsourceModifyPasswordResultDto>(
    '/api/outsource/v2/sessions/modify_password',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/sessions/forget_password
 */
export function outsourceSessionsForgetPassword(
  data: OutsourceForgetPasswordForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/outsource/v2/sessions/forget_password', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/sessions/check_reset_link
 */
export function outsourceSessionsCheckResetLink(
  data: OutsourceCheckResetLinkForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/outsource/v2/sessions/check_reset_link', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * upload invoice /api/outsource/v2/prepayment/upload-invoice
 */
export function prepaymentOutsourceUploadInvoice(
  data: PrepaymentInvoiceUploadCmd,
  opts?: ReqOpts
) {
  return request<ResponsePrepaymentDetailVo>(
    '/api/outsource/v2/prepayment/upload-invoice',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 查询预付单流程进度与更新历史 /api/outsource/v2/prepayment/journals
 */
export function outsourcePrepaymentJournalJournals(
  data: JournalReqDto,
  opts?: ReqOpts
) {
  return request<ResponseJournalResDto>(
    '/api/outsource/v2/prepayment/journals',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 供应商资源发布（前端上传文件后调用，将素材发布到 Muse 资源包） /api/outsource/v2/muse/resource/publish
 */
export function outsourceV2MuseResourcePublish(
  data: MuseResourcePublishForm,
  opts?: ReqOpts
) {
  return request<ResponseMuseResourcePublishResultVo>(
    '/api/outsource/v2/muse/resource/publish',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 重发协作邀请邮件 /api/outsource/v2/muse/collaborator/resend/{collaboratorId}
 */
export function outsourceV2MuseCollaboratorResendByCollaboratorId(
  params: {
    collaborator_id: number;
  },
  opts?: ReqOpts
) {
  const { collaborator_id } = params;
  return request<ResponseVoid>(
    `/api/outsource/v2/muse/collaborator/resend/${collaborator_id}`,
    { method: 'POST', ...opts }
  );
}
/**
 * 邀请协作人员 /api/outsource/v2/muse/collaborator/invite
 */
export function outsourceV2MuseCollaboratorInvite(
  data: MuseCollaboratorInviteForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/outsource/v2/muse/collaborator/invite', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 供应商流转需求单 /api/outsource/v2/demands_move/move
 */
export function outsourceDemandMoveMove(data: DemandMoveDto, opts?: ReqOpts) {
  return request<ResponseDemandMoveVo>('/api/outsource/v2/demands_move/move', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/demands/supplier_quote_settle_reports
 */
export function outsourceDemandQueryDemandSupplierQuoteSettleReports(
  params: {
    demand_supplier_id: number;
    cooperate_account_id?: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandSupplierQuoteWithSettleReportVo>(
    createUri('/api/outsource/v2/demands/supplier_quote_settle_reports', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 保存结构化结算报告 /api/outsource/v2/demands/save_structured_settle_reports_data
 */
export function outsourceDemandSaveStructuredSettleReportsData(
  data: OutsourceSaveStructuredSettleReportsDataForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/outsource/v2/demands/save_structured_settle_reports_data',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 保存报价表格数据 /api/outsource/v2/demands/save_quote_table_data
 */
export function outsourceDemandSaveQuoteTableData(
  data: OutsourceSaveQuoteTableDataForm,
  opts?: ReqOpts
) {
  return request<ResponseOutsourceSaveQuoteTableResultDto>(
    '/api/outsource/v2/demands/save_quote_table_data',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/demands/journals
 */
export function outsourceDemandJournals(data: DemandReqDto, opts?: ReqOpts) {
  return request<ResponseJournalResDto>('/api/outsource/v2/demands/journals', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * ifHideInvoice /api/outsource/v2/demands/hide-invoice
 */
export function outsourceDemandHideInvoice(
  data: PrepaymentHideInvoiceCmd,
  opts?: ReqOpts
) {
  return request<ResponseBoolean>('/api/outsource/v2/demands/hide-invoice', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 获取报价表格数据 /api/outsource/v2/demands/get_quote_table_data
 */
export function outsourceDemandGetQuoteTableData(
  data: GetQuoteTableDataForm,
  opts?: ReqOpts
) {
  return request<ResponseDemandQuoteTableDto>(
    '/api/outsource/v2/demands/get_quote_table_data',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【电子发票】生成需求结算单 /api/outsource/v2/demands/gen_statement_blob
 */
export function outsourceDemandGenStatementBlob(
  params: {
    id: number;
    attach?: boolean;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseAttachmentBlobDto>(
    createUri('/api/outsource/v2/demands/gen_statement_blob', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 导出报价表格数据 /api/outsource/v2/demands/export_quote_table_data
 */
export function outsourceDemandExportQuoteTableData(
  data: ExportQuoteTableDataForm,
  opts?: ReqOpts
) {
  return request<Response>(
    '/api/outsource/v2/demands/export_quote_table_data',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/demands/diff/supplier_quote
 */
export function outsourceDemandDiffSupplierQuote(
  params: {
    demand_id: number;
    demand_supplier_id: number;
    blob_id: number;
    cooperate_account_id?: number;
    diff: boolean;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandSupplierQuoteDiffVo>(
    createUri('/api/outsource/v2/demands/diff/supplier_quote', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 查看历史报价详情 /api/outsource/v2/demands/detail_his_round_for_supplier
 */
export function outsourceDemandDetailHisRoundForSupplier(
  params: {
    demand_id: number;
    supplier_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseOutsourceDemandHisDetailResDto>(
    createUri('/api/outsource/v2/demands/detail_his_round_for_supplier', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/outsource/v2/demands/detail
 */
export function outsourceDemandDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseOutsourceDemandDetailsResDto>(
    createUri('/api/outsource/v2/demands/detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * 批量更新加热助推效果数据（包含新增、删除） /api/outsource/v2/demands/batchUpsertBoostEffectData
 */
export function outsourceDemandBatchUpsertBoostEffectData(
  data: OutsourceBoostEffectUpsertForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/outsource/v2/demands/batchUpsertBoostEffectData',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 【需求标品】根据入参Code列表查询 /api/outsource/v2/demand_standard_catalog/list_by_code_list
 */
export function outsourceDemandStandardCatalogListByCodeList(
  data: DemandStandardCatalogListParam,
  opts?: ReqOpts
) {
  return request<ResponseListDemandStandardCatalogVo>(
    '/api/outsource/v2/demand_standard_catalog/list_by_code_list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/demand_source/{id}/case_page_detail
 */
export function outsourceDemandSourceDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseOutsourceDemandSourceDetailVo>(
    `/api/outsource/v2/demand_source/${id}/case_page_detail`,
    { method: 'POST', ...opts }
  );
}
/**
 * 一询单拒单 /api/outsource/v2/demand_source/demand/reject
 */
export function outsourceDemandSourceDemandReject(
  data: DemandSourceApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>(
    '/api/outsource/v2/demand_source/demand/reject',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 一询单提报 /api/outsource/v2/demand_source/demand/pass
 */
export function outsourceDemandSourceDemandPass(
  data: DemandSourceApprovalCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>(
    '/api/outsource/v2/demand_source/demand/pass',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 供应商导入达人 /api/outsource/v2/demand_source/demand/importDemandSourceKol
 */
export function outsourceDemandSourceImportDemandSourceKol(
  data?: GenericFormData<{
    file: Blob;
    /** 一询单ID */
    demand_source_id: string;
  }>,
  opts?: ReqOpts
) {
  return request<ResponseBoolean>(
    '/api/outsource/v2/demand_source/demand/importDemandSourceKol',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 供应商导出达人 /api/outsource/v2/demand_source/demand/createKolExportTask
 */
export function outsourceDemandSourceCreateExportDemandSourceKolTask(
  data: SupplierKolExportCmd,
  opts?: ReqOpts
) {
  return request<ResponseString>(
    '/api/outsource/v2/demand_source/demand/createKolExportTask',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 根据批量达人链接获取内容营销数据 /api/outsource/v2/demand_source/content/batchGetKolInfo
 */
export function outsourceDemandSourceGetKolInfo(
  data: BatchGetKolInfoCmd,
  opts?: ReqOpts
) {
  return request<ResponseListDemandSourceKolVo>(
    '/api/outsource/v2/demand_source/content/batchGetKolInfo',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/demand-invoice/memory-upload
 */
export function outsourceDemandInvoiceUploadInvoice(
  params: {
    demand_id: number;
    belong_area: number;
    is_oversea_invoice: boolean;
  },
  data?: GenericFormData<{
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseInvoiceUploadResultVo>(
    createUri('/api/outsource/v2/demand-invoice/memory-upload', query),
    { method: 'POST', data, ...opts }
  );
}
/**
 * 获取提示文案 /api/outsource/v2/common/tip_texts
 */
export function outsourceCommonTipTexts(
  data: CommonTipTextForm,
  opts?: ReqOpts
) {
  return request<ResponseListTipTextVo>('/api/outsource/v2/common/tip_texts', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 保存外包通用暂存数据 /api/outsource/v2/common/temporary_data/save
 */
export function outsourceCommonSaveTemporary(
  data: OutsourceTemporarySaveForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/outsource/v2/common/temporary_data/save', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 查询外包通用暂存数据 /api/outsource/v2/common/temporary_data/query
 */
export function outsourceCommonQueryTemporary(
  data: OutsourceTemporaryQueryForm,
  opts?: ReqOpts
) {
  return request<ResponseString>(
    '/api/outsource/v2/common/temporary_data/query',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 删除外包通用暂存数据 /api/outsource/v2/common/temporary_data/delete
 */
export function outsourceCommonDeleteTemporary(
  data: OutsourceTemporaryDeleteForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>(
    '/api/outsource/v2/common/temporary_data/delete',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 获取下拉选项 /api/outsource/v2/common/filter_options
 */
export function outsourceCommonFilterOptions(
  data?: CommonOptionsForm,
  opts?: ReqOpts
) {
  return request<Blob>('/api/outsource/v2/common/filter_options', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 币种查询接口 /api/outsource/v2/common/exchange/rate/query
 */
export function outsourceCommonExchangeRateQuery(
  data: ExchangeRateForm,
  opts?: ReqOpts
) {
  return request<ResponseListExchangeRateDto>(
    '/api/outsource/v2/common/exchange/rate/query',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 临时导入数据 /api/outsource/v2/common/data/{type}/memory-import-data
 */
export function outsourceDataMemoryImportData(
  params: {
    type: string;
  },
  data?: GenericFormData<{
    file: Blob;
    /** 当前的场景信息 */
    scene?: string;
  }>,
  opts?: ReqOpts
) {
  const { type } = params;
  return request<Blob>(
    `/api/outsource/v2/common/data/${type}/memory-import-data`,
    { method: 'POST', data, ...opts }
  );
}
/**
 * 直接导入数据 /api/outsource/v2/common/data/{type}/import-data
 */
export function outsourceDataImportData(
  params: {
    type: string;
  },
  data?: GenericFormData<{
    file: Blob;
    /** 当前的场景信息 */
    scene?: string;
  }>,
  opts?: ReqOpts
) {
  const { type } = params;
  return request<Blob>(`/api/outsource/v2/common/data/${type}/import-data`, {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/bidding_move/move
 */
export function outsourceBiddingMoveMove(data: BiddingMoveDto, opts?: ReqOpts) {
  return request<ResponseBiddingMoveVo>('/api/outsource/v2/bidding_move/move', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/bidding/workbenches/menus_count
 */
export function outBiddingWorkbenchesMenusCount(
  data: BiddingMenuCountForm,
  opts?: ReqOpts
) {
  return request<ResponseListBiddingWorkbenchMenuCountDto>(
    '/api/outsource/v2/bidding/workbenches/menus_count',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/bidding/workbenches/list
 */
export function outBiddingWorkbenchesList(
  data: BiddingInnerWorkbenchForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultBiddingWorkbenches>(
    '/api/outsource/v2/bidding/workbenches/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/bidding/workbenches/announcement/list
 */
export function outBiddingWorkbenchesListUsingPost(
  data: BiddingAnnouncementForm,
  opts?: ReqOpts
) {
  return request<ResponseListBiddingAnnouncementResDto>(
    '/api/outsource/v2/bidding/workbenches/announcement/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/outsource/v2/bidding/workbenches/announcement/can_modify/get
 */
export function outBiddingWorkbenchesGetCanModifySelectAnnouncement(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseBiddingAnnouncementResDto>(
    createUri(
      '/api/outsource/v2/bidding/workbenches/announcement/can_modify/get',
      query
    ),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/outsource/v2/bidding/supplier_his_post
 */
export function outsourceBiddingGetDetailHisBiddingTurns(
  params: {
    bidding_id: number;
    supplier_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseBiddingSupplierHisDto>(
    createUri('/api/outsource/v2/bidding/supplier_his_post', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/outsource/v2/bidding/journals
 */
export function outsourceBiddingJournals(data: BiddingReqDto, opts?: ReqOpts) {
  return request<ResponseJournalResDto>('/api/outsource/v2/bidding/journals', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * 导入报价明细 /api/outsource/v2/bidding/import_quote_detail
 */
export function outsourceBiddingImportQuoteDetail(
  data?: GenericFormData<{
    configs: string;
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  return request<ResponseResponse>(
    '/api/outsource/v2/bidding/import_quote_detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 导出报价明细为 Excel /api/outsource/v2/bidding/export_quote_detail
 */
export function outsourceBiddingExportQuoteDetail(
  data: ExportQuoteDetailForm,
  opts?: ReqOpts
) {
  return request<unknown>('/api/outsource/v2/bidding/export_quote_detail', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/bidding/detail
 */
export function outsourceBiddingDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseOutsourceBiddingDetailsResDto>(
    createUri('/api/outsource/v2/bidding/detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/outsource/v2/bidding/check_quote_detail
 */
export function outsourceBiddingCheckQuoteDetail(
  data: OutsourceBiddingSupplierCheckForm,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/outsource/v2/bidding/check_quote_detail', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/attachments/upload
 */
export function outsourceAttachmentUpload(
  data?: GenericFormData<{
    file: Blob;
  }>,
  opts?: ReqOpts
) {
  return request<ResponseAttachmentBlobDto>(
    '/api/outsource/v2/attachments/upload',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 供应商达人报价列表（比选单+二级单跨表分页） /api/outsource/v2/advance_price_check/list
 */
export function outsourceV2AdvancePriceCheckList(
  data: OutsourceListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultAdvancePriceCheckListItemVo>(
    '/api/outsource/v2/advance_price_check/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 供应商达人报价-操作页历史列表（Task 5.2） /api/outsource/v2/advance_price_check/kol/quote/operation
 */
export function outsourceV2AdvancePriceCheckKolQuoteOperation(
  data: OperationForm,
  opts?: ReqOpts
) {
  return request<ResponseListSupplierKolQuoteRoundVo>(
    '/api/outsource/v2/advance_price_check/kol/quote/operation',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 供应商达人报价-精简列表（kol-auto-pricing-enhance Task 5.1） /api/outsource/v2/advance_price_check/kol/quote/list
 */
export function outsourceV2AdvancePriceCheckKolQuoteList(
  data: OutsourceListForm,
  opts?: ReqOpts
) {
  return request<ResponsePageResultSupplierKolQuoteListItemVo>(
    '/api/outsource/v2/advance_price_check/kol/quote/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * 供应商达人报价-详情页（只读，Task 5.6） /api/outsource/v2/advance_price_check/kol/quote/detail
 */
export function outsourceV2AdvancePriceCheckKolQuoteDetail(
  data: DetailIdForm,
  opts?: ReqOpts
) {
  return request<ResponseSupplierKolQuoteDetailVo>(
    '/api/outsource/v2/advance_price_check/kol/quote/detail',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/mobile/v2/workbenches/menus_count
 */
export function mobileMenusCount(data: WorkbenchMenuCountForm, opts?: ReqOpts) {
  return request<ResponseListWorkbenchMenuCountFormDto>(
    '/api/mobile/v2/workbenches/menus_count',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/mobile/v2/workbenches/list
 */
export function mobileList(data: WorkbenchForm, opts?: ReqOpts) {
  return request<ResponsePageResultWorkbenches>(
    '/api/mobile/v2/workbenches/list',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/mobile/v2/project/pageList
 */
export function mobileCommonQueryProjectList(
  data: ProjectListQueryForm,
  opts?: ReqOpts
) {
  return request<RubyResponseVoPageDtoProjectQueryDto>(
    '/api/mobile/v2/project/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/mobile/v2/demands_move/move
 */
export function mobileMove(data: DemandMoveDto, opts?: ReqOpts) {
  return request<ResponseDemandMoveVo>('/api/mobile/v2/demands_move/move', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/mobile/v2/demands_move/batch_move
 */
export function mobileBatchMove(data: DemandBatchMoveDto, opts?: ReqOpts) {
  return request<ResponseListDemandMoveVo>(
    '/api/mobile/v2/demands_move/batch_move',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/mobile/v2/demands/with_siblings
 */
export function mobileDetailWithSiblings(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListDemandDetailsResDto>(
    createUri('/api/mobile/v2/demands/with_siblings', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/mobile/v2/demands/detail
 */
export function mobileDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandDetailsResDto>(
    createUri('/api/mobile/v2/demands/detail', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/mobile/v2/contract/pageList
 */
export function mobileCommonQueryContractList(
  data: ContractQueryForm,
  opts?: ReqOpts
) {
  return request<RubyResponseVoPageDtoContractQueryDto>(
    '/api/mobile/v2/contract/pageList',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /api/mobile/v2/common/filter_options
 */
export function mobileCommonFilterOptions(
  data: FilterOptionsRequest,
  opts?: ReqOpts
) {
  return request<RubyResponseVoListMobileFilterOptionsResponse>(
    '/api/mobile/v2/common/filter_options',
    { method: 'POST', data, ...opts }
  );
}
/**
 * unknown /
 */
export function indexIndex(opts?: ReqOpts) {
  return request<string>('/', { ...opts });
}
/**
 * unknown /support/oa/create_pre_approval_process_inner
 */
export function supportOaCreatePreApprovalProcessInner(
  params: {
    demand_id: number;
    token: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseObject>(
    createUri('/support/oa/create_pre_approval_process_inner', query),
    { ...opts }
  );
}
/**
 * unknown /openapi/test/mock-submit-oa-archived
 */
export function openapiTestMockSubmitOaArchivedUsingGet(
  params: {
    oa_id: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseVoid>(
    createUri('/openapi/test/mock-submit-oa-archived', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/workspace/export-demand-task/{taskId}
 */
export function myWorkspaceGetExportDemandTaskResult(
  params: {
    task_id: string;
  },
  opts?: ReqOpts
) {
  const { task_id } = params;
  return request<ResponseExportTaskResultVo>(
    `/api/v2/workspace/export-demand-task/${task_id}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/workbenches/export
 */
export function workbenchesExportDemand(
  params: {
    state: string;
    group_type: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/v2/workbenches/export', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/woodpecker/export
 */
export function woodpeckerExportSupplier(opts?: ReqOpts) {
  return request<ResponseString>('/api/v2/woodpecker/export', { ...opts });
}
/**
 * unknown /api/v2/user/{id}
 */
export function userGet(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseUserDto>(`/api/v2/user/${id}`, { ...opts });
}
/**
 * unknown /api/v2/user/getPopoUserList
 */
export function userGetPopoUserList(
  params: {
    search_key: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListPopoUserRes>(
    createUri('/api/v2/user/getPopoUserList', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/trumpet/demand/{id}/get_used_budget
 */
export function trumpetDemandGetUsedBudget(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseUsedTrumpetActivityBudgetVo>(
    `/api/v2/trumpet/demand/${id}/get_used_budget`,
    { ...opts }
  );
}
/**
 * 树列表 /api/v2/trumpet/common/query/{id}/tree_list
 */
export function trumpetCommonQueryListTree(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseTrumpetTreeNodeVo>(
    `/api/v2/trumpet/common/query/${id}/tree_list`,
    { ...opts }
  );
}
/**
 * 节点记录 /api/v2/trumpet/common/query/{id}/node_records
 */
export function trumpetCommonQueryListNodeRecords(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseJournalResDto>(
    `/api/v2/trumpet/common/query/${id}/node_records`,
    { ...opts }
  );
}
/**
 * 自适配各级单详情展示 /api/v2/trumpet/common/query/{id}/case_page_detail
 */
export function trumpetCommonQueryShowDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseTrumpetCasePageDetailVo>(
    `/api/v2/trumpet/common/query/${id}/case_page_detail`,
    { ...opts }
  );
}
/**
 * 发通知 /api/v2/trumpet/common/action/{id}/send_remainder
 */
export function trumpetCommonSendRemainder(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseReminderResDto>(
    `/api/v2/trumpet/common/action/${id}/send_remainder`,
    { ...opts }
  );
}
/**
 * 查询MPC监测链接（自动识别二级单/三级单） /api/v2/third-part/content-market-query/getMonitorLinks
 */
export function contentMarketQueryGetMonitorLinks(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListMonitorLinkItemVo>(
    createUri('/api/v2/third-part/content-market-query/getMonitorLinks', query),
    { ...opts }
  );
}
/**
 * 生成[内容营销平台-数据报表查看]页面请求url /api/v2/third-part/content-market-query/genDashboardPageUrl
 */
export function contentMarketQueryGenDashboardPageUrl(
  params: {
    demand_id: number;
    case_demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri(
      '/api/v2/third-part/content-market-query/genDashboardPageUrl',
      query
    ),
    { ...opts }
  );
}
/**
 * 导出监测链接到Excel（异步生成，返回 taskId） /api/v2/third-part/content-market-query/exportMonitorLinks
 */
export function contentMarketQueryExportMonitorLinks(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri(
      '/api/v2/third-part/content-market-query/exportMonitorLinks',
      query
    ),
    { ...opts }
  );
}
/**
 * unknown /api/v2/test/testQueryDspfData/{dspfNo}
 */
export function testTestQueryDspfData(
  params: {
    dspf_no: string;
  },
  opts?: ReqOpts
) {
  const { dspf_no } = params;
  return request<ResponseQueryDataDto>(
    `/api/v2/test/testQueryDspfData/${dspf_no}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/test/testErrorPath/{key}
 */
export function testTestErrorPath(
  params: {
    key: string;
  },
  opts?: ReqOpts
) {
  const { key } = params;
  return request<ResponseBoolean>(`/api/v2/test/testErrorPath/${key}`, {
    ...opts,
  });
}
/**
 * unknown /api/v2/test/testErrorLog
 */
export function testTestErrorLog(opts?: ReqOpts) {
  return request<ResponseBoolean>('/api/v2/test/testErrorLog', { ...opts });
}
/**
 * unknown /api/v2/test/testDealAwaitingSubmissionBidsTimeOut/{id}
 */
export function testTestDealAwaitingSubmissionBidsTimeOut(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseBoolean>(
    `/api/v2/test/testDealAwaitingSubmissionBidsTimeOut/${id}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/test/testDealAwaitingBiddingTimeOut/{id}
 */
export function testTestDealAwaitingBiddingTimeOut(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseBoolean>(
    `/api/v2/test/testDealAwaitingBiddingTimeOut/${id}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/test/test/move/oneOf
 */
export function testTestMoveOneOf(opts?: ReqOpts) {
  return request<ResponseDemandMoveOneOfDto>('/api/v2/test/test/move/oneOf', {
    ...opts,
  });
}
/**
 * unknown /api/v2/test/oaAuth
 */
export function testOaAuth(
  params: {
    job_number: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListOaEmployeesAuthResultDto>(
    createUri('/api/v2/test/oaAuth', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/test/oa/{id}
 */
export function testOa(
  params: {
    id: string;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseProcessInstance>(`/api/v2/test/oa/${id}`, { ...opts });
}
/**
 * unknown /api/v2/test/login/{id}
 */
export function testLogin(
  params: {
    id: string;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseString>(`/api/v2/test/login/${id}`, { ...opts });
}
/**
 * unknown /api/v2/test/can_move/{demandId}/{userId}
 */
export function testCanMove(
  params: {
    demand_id: number;
    user_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id, user_id } = params;
  return request<Response>(`/api/v2/test/can_move/${demand_id}/${user_id}`, {
    ...opts,
  });
}
/**
 * 查询导出任务结果 /api/v2/task/query
 */
export function v2TaskQuery(
  params: {
    task_id: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseExportTaskResultDto>(
    createUri('/api/v2/task/query', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/supplier_tag/tag/view
 */
export function supplierTagViewTag(
  params: {
    tag_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseSuppliersTagDto>(
    createUri('/api/v2/supplier_tag/tag/view', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/supplier_tag/cat/view
 */
export function supplierTagViewCat(
  params: {
    cat_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseSuppliersTagCatDto>(
    createUri('/api/v2/supplier_tag/cat/view', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/supplier_tag/cat/tree
 */
export function supplierTagCatTree(opts?: ReqOpts) {
  return request<ResponseSuppliersTagCatTreeVo>(
    '/api/v2/supplier_tag/cat/tree',
    { ...opts }
  );
}
/**
 * 查询推荐结果 /api/v2/supplier/recommendation/result
 */
export function supplierRecommendationGetResult(
  params: {
    demand_id: number;
    demand_type?: number;
    force_refresh?: boolean;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseRecommendationResultVo>(
    createUri('/api/v2/supplier/recommendation/result', query),
    { ...opts }
  );
}
/**
 * 根据cs渠道供应商id,查询渠道供应商详情 /api/v2/supplier-new/{id}
 */
export function supplierControllerNewQueryById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseChannelSupplierDetailVo>(
    `/api/v2/supplier-new/${id}`,
    { ...opts }
  );
}
/**
 * 查询供应商评分记录 /api/v2/supplier-new/{id}/rating_records
 */
export function supplierControllerNewRatingRecords(
  params: {
    id: number;
    current?: number;
    limit?: number;
  },
  opts?: ReqOpts
) {
  const { id, ...query } = params;
  return request<ResponsePageVoDemandRatingRecordVo>(
    createUri(`/api/v2/supplier-new/${id}/rating_records`, query),
    { ...opts }
  );
}
/**
 * 查询供应商操作记录 /api/v2/supplier-new/{id}/op_records
 */
export function supplierControllerNewOpRecords(
  params: {
    id: number;
    current?: number;
    limit?: number;
  },
  opts?: ReqOpts
) {
  const { id, ...query } = params;
  return request<ResponsePageVoSupplierOpLogVo>(
    createUri(`/api/v2/supplier-new/${id}/op_records`, query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/supplier-new/validTypes
 */
export function supplierControllerNewValidTypes(opts?: ReqOpts) {
  return request<ResponseValidChannelSupplierType>(
    '/api/v2/supplier-new/validTypes',
    { ...opts }
  );
}
/**
 * 【202501供应商入口调整】供应商管理-审核详情页面 /api/v2/supplier-new/supplier-approval/detail/{approvalNo}
 */
export function supplierControllerNewQuerySupplierApprovalDetail(
  params: {
    approval_no: string;
  },
  opts?: ReqOpts
) {
  const { approval_no } = params;
  return request<ResponseSupplierApprovalDetailVo>(
    `/api/v2/supplier-new/supplier-approval/detail/${approval_no}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/supplier-new/simpleDemandTest
 */
export function supplierControllerNewSimpleDemandTest(
  params: {
    supplier_id: number;
    page: number;
    page_size: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListDemandSimpleVo>(
    createUri('/api/v2/supplier-new/simpleDemandTest', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/supplier-new/searchScoreItemsTest
 */
export function supplierControllerNewSearchScoreItemsTest(
  params: {
    demand_id: number;
    type: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandEvaluateScoreDescVo>(
    createUri('/api/v2/supplier-new/searchScoreItemsTest', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/supplier-new/query-vmdm-bank-account
 */
export function supplierControllerNewQueryVmdmBankAccount(
  params: {
    id?: number;
    mdm_code?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListVmdmSupplierBankVo>(
    createUri('/api/v2/supplier-new/query-vmdm-bank-account', query),
    { ...opts }
  );
}
/**
 * 查询所有关联账号信息 /api/v2/supplier-new/query-account-all/{id}
 */
export function supplierControllerNewQueryTrumpetAccountAll(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseTrumpetAccountAllVo>(
    `/api/v2/supplier-new/query-account-all/${id}`,
    { ...opts }
  );
}
/**
 * 查询主供应商详情 /api/v2/supplier-new/main-supplier/{id}/query
 */
export function mainSupplierQueryMainSupplier(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseMainSupplierVo>(
    `/api/v2/supplier-new/main-supplier/${id}/query`,
    { ...opts }
  );
}
/**
 * 查询主供应商的审批意见 /api/v2/supplier-new/main-supplier/{id}/approval-opinion
 */
export function mainSupplierQueryApprovalOpinion(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseListApprovalOpinionDto>(
    `/api/v2/supplier-new/main-supplier/${id}/approval-opinion`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/supplier-new/get-supplier-manager-permission
 */
export function supplierControllerNewGetSupplierManagerPermission(
  opts?: ReqOpts
) {
  return request<ResponseSupplierManagerPermissionVo>(
    '/api/v2/supplier-new/get-supplier-manager-permission',
    { ...opts }
  );
}
/**
 * 【20240620版本】获取导出结果 /api/v2/supplier-new/export-task/{taskId}
 */
export function supplierControllerNewGetExportTaskResult(
  params: {
    task_id: string;
  },
  opts?: ReqOpts
) {
  const { task_id } = params;
  return request<ResponseExportTaskResultVo>(
    `/api/v2/supplier-new/export-task/${task_id}`,
    { ...opts }
  );
}
/**
 * 【20240620版本】获取导出结果 /api/v2/supplier-new/export-demand-task/{taskId}
 */
export function supplierControllerNewGetExportDemandTaskResult(
  params: {
    task_id: string;
  },
  opts?: ReqOpts
) {
  const { task_id } = params;
  return request<ResponseExportTaskResultVo>(
    `/api/v2/supplier-new/export-demand-task/${task_id}`,
    { ...opts }
  );
}
/**
 * 获取数据导入结果 /api/v2/supplier-new/data-import-task/{taskId}/result
 */
export function channelSupplierImportGetImportTaskResult(
  params: {
    task_id: string;
  },
  opts?: ReqOpts
) {
  const { task_id } = params;
  return request<ResponseImportTaskResultVo>(
    `/api/v2/supplier-new/data-import-task/${task_id}/result`,
    { ...opts }
  );
}
/**
 * 获取数据导入异常文件 /api/v2/supplier-new/data-import-task/{taskId}/error-file
 */
export function channelSupplierImportGetImportTaskErrorFile(
  params: {
    task_id: string;
  },
  opts?: ReqOpts
) {
  const { task_id } = params;
  return request<ResponseString>(
    `/api/v2/supplier-new/data-import-task/${task_id}/error-file`,
    { ...opts }
  );
}
/**
 * 下载导入模板 /api/v2/supplier-new/data-import-task/template
 */
export function channelSupplierImportGetImportTaskTemplate(opts?: ReqOpts) {
  return request<ResponseString>(
    '/api/v2/supplier-new/data-import-task/template',
    { ...opts }
  );
}
/**
 * 获取导出结果 /api/v2/supplier-new/data-export-task/{taskId}/result
 */
export function channelSupplierExportGetTaskResult(
  params: {
    task_id: string;
  },
  opts?: ReqOpts
) {
  const { task_id } = params;
  return request<ResponseExportTaskResultVo>(
    `/api/v2/supplier-new/data-export-task/${task_id}/result`,
    { ...opts }
  );
}
/**
 * 【20240620版本】获取变更详情 /api/v2/supplier-new/change-detail/{id}
 */
export function supplierControllerNewQueryChangeDetailById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<Blob>(`/api/v2/supplier-new/change-detail/${id}`, { ...opts });
}
/**
 * unknown /api/v2/supplier-new/change-detail/approval-no/{approvalNo}
 */
export function supplierControllerNewQueryChangeDetailByApprovalNo(
  params: {
    approval_no: string;
  },
  opts?: ReqOpts
) {
  const { approval_no } = params;
  return request<ResponseSupplierChangeDetailVo>(
    `/api/v2/supplier-new/change-detail/approval-no/${approval_no}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/supplier-kpi/detail
 */
export function supplierKpiDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseSupplierKpiVo>(
    createUri('/api/v2/supplier-kpi/detail', query),
    { ...opts }
  );
}
/**
 * 获取规格参考内容 /api/v2/standard/library/getDemandStandardSpecList
 */
export function v2StandardLibraryGetDemandStandardSpecList(
  params: {
    catalog_id: number;
    level_template: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListDemandStandardSpecVo>(
    createUri('/api/v2/standard/library/getDemandStandardSpecList', query),
    { ...opts }
  );
}
/**
 * 获取标品详情 /api/v2/standard/library/detail/{id}
 */
export function v2StandardLibraryDetailById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseStandardLibraryDto>(
    `/api/v2/standard/library/detail/${id}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/session
 */
export function sessionCurrent(opts?: ReqOpts) {
  return request<ResponseSession>('/api/v2/session', { ...opts });
}
/**
 * 查询有效期覆盖指定日期的供应商 /api/v2/segment_supplier/valid
 */
export function v2SegmentSupplierValid(
  params: {
    segment_config_id: number;
    date: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListSegmentSupplierVo>(
    createUri('/api/v2/segment_supplier/valid', query),
    { ...opts }
  );
}
/**
 * 标段供应商详情（含类目、标段配置、供应商列表） /api/v2/segment_supplier/detail
 */
export function v2SegmentSupplierDetail(
  params: {
    segment_config_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseSegmentSupplierDetailVo>(
    createUri('/api/v2/segment_supplier/detail', query),
    { ...opts }
  );
}
/**
 * 根据关键字搜索比选单列表 /api/v2/segment_supplier/biddings/search
 */
export function v2SegmentSupplierBiddingsSearch(
  params: {
    keyword?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListBiddingSimpleVo>(
    createUri('/api/v2/segment_supplier/biddings/search', query),
    { ...opts }
  );
}
/**
 * 标段详情 /api/v2/segment_config/{id}
 */
export function v2SegmentConfigById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseSegmentConfigVo>(`/api/v2/segment_config/${id}`, {
    ...opts,
  });
}
/**
 * 按类目最新 template 清理 template_value 废弃 key 并重算 MD5 /api/v2/segment_config/sync_template_value
 */
export function v2SegmentConfigSyncTemplateValue(opts?: ReqOpts) {
  return request<ResponseInteger>(
    '/api/v2/segment_config/sync_template_value',
    { ...opts }
  );
}
/**
 * 全表重算 MD5（按类目最新 template 过滤 template_value 后） /api/v2/segment_config/filtered_md5_map
 */
export function v2SegmentConfigFilteredMd5Map(opts?: ReqOpts) {
  return request<ResponseMapStringListLong>(
    '/api/v2/segment_config/filtered_md5_map',
    { ...opts }
  );
}
/**
 * 类目详情（含字段组定义） /api/v2/segment_category/{id}
 */
export function v2SegmentCategoryById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseSegmentCategoryDetailVo>(
    `/api/v2/segment_category/${id}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/roles/{id}/edit
 */
export function v2RubyMigrateRolesByIdEdit(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<RubyResponseVoEditRoleDetailVo>(
    `/api/v2/ruby_migrate/roles/${id}/edit`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/roles/new
 */
export function v2RubyMigrateRolesNew(opts?: ReqOpts) {
  return request<RubyResponseVoNewRoleDetailVo>(
    '/api/v2/ruby_migrate/roles/new',
    { ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/products/{id}/edit
 */
export function v2RubyMigrateProductsByIdEdit(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<RubyResponseVoProductEditShowVo>(
    `/api/v2/ruby_migrate/products/${id}/edit`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/products/new
 */
export function v2RubyMigrateProductsNew(opts?: ReqOpts) {
  return request<RubyResponseVoNewProductDataVo>(
    '/api/v2/ruby_migrate/products/new',
    { ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/accounts/user_info
 */
export function rubyAccountsUserInfo(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/accounts/user_info',
    { ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/accounts/menus_count
 */
export function rubyAccountsMenusCount(
  params: MultiValueMapStringString,
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<RubyResponseVoListRubyMenuCountItem>(
    createUri('/api/v2/ruby_migrate/accounts/menus_count', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/accounts/login_with_url
 */
export function rubyAccountsLoginWithUrl(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<RubyResponseVoObject>(
    createUri('/api/v2/ruby_migrate/accounts/login_with_url', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/reportForms/export_sc
 */
export function reportFormsExportSettledConsumption(opts?: ReqOpts) {
  return request<Response>('/api/v2/reportForms/export_sc', { ...opts });
}
/**
 * unknown /api/v2/reportForms/export_cur_doing
 */
export function reportFormsExportCurDoing(
  params: {
    start_date: string;
    end_date: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<Response>(
    createUri('/api/v2/reportForms/export_cur_doing', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/reportForms/export_all
 */
export function reportFormsExportAll(
  params: {
    start_date: string;
    end_date: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/v2/reportForms/export_all', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/price-library/getStandardAndCategory
 */
export function v2PriceLibraryGetStandardAndCategory(opts?: ReqOpts) {
  return request<ResponsePriceLibrarySelectVo>(
    '/api/v2/price-library/getStandardAndCategory',
    { ...opts }
  );
}
/**
 * 获取预付单详情 /api/v2/prepayment/{prepaymentId}
 */
export function v2PrepaymentByPrepaymentId(
  params: {
    prepayment_id: number;
  },
  opts?: ReqOpts
) {
  const { prepayment_id } = params;
  return request<ResponsePrepaymentDetailVo>(
    `/api/v2/prepayment/${prepayment_id}`,
    { ...opts }
  );
}
/**
 * 批量判断预付金额是否超标 /api/v2/prepayment/exceed-check
 */
export function v2PrepaymentExceedCheck(
  params: {
    demand_ids: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListLong>(
    createUri('/api/v2/prepayment/exceed-check', query),
    { ...opts }
  );
}
/**
 * OA嵌入页查询预算调整详情 /api/v2/oa/budget-adjust/detail
 */
export function v2OaBudgetAdjustDetail(
  params: {
    token: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseBudgetAdjustDetailDto>(
    createUri('/api/v2/oa/budget-adjust/detail', query),
    { ...opts }
  );
}
/**
 * 查询Muse资源包创建状态 /api/v2/muse/resource/{demandId}/status
 */
export function v2MuseResourceByDemandIdStatus(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id } = params;
  return request<ResponseMuseResourceStatusVo>(
    `/api/v2/muse/resource/${demand_id}/status`,
    { ...opts }
  );
}
/**
 * 查询文件夹及素材概况 /api/v2/muse/resource/{demandId}/folders
 */
export function v2MuseResourceByDemandIdFolders(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id } = params;
  return request<ResponseListMuseFolderSummaryVo>(
    `/api/v2/muse/resource/${demand_id}/folders`,
    { ...opts }
  );
}
/**
 * 查询归档状态 /api/v2/muse/resource/{demandId}/archive-status
 */
export function v2MuseResourceByDemandIdArchiveStatus(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id } = params;
  return request<ResponseMuseArchiveStatusVo>(
    `/api/v2/muse/resource/${demand_id}/archive-status`,
    { ...opts }
  );
}
/**
 * 获取需求单基本信息（供中台登录页渲染） /api/v2/muse/collaborator/auth/info/{demandId}
 */
export function v2MuseCollaboratorAuthInfoByDemandId(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id } = params;
  return request<ResponseMuseDemandInfoVo>(
    `/api/v2/muse/collaborator/auth/info/${demand_id}`,
    { ...opts }
  );
}
/**
 * 发通知 /api/v2/kpi/adjustment/{id}/send_remainder
 */
export function kpiAdjustmentSendRemainder(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseReminderResDto>(
    `/api/v2/kpi/adjustment/${id}/send_remainder`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/invoice/detail
 */
export function invoiceQueryDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseInvoiceDetailVo>(
    createUri('/api/v2/invoice/detail', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/inquiry-score-rule/detail/{id}
 */
export function inquiryScoreRuleDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseInquiryScoreRuleVo>(
    `/api/v2/inquiry-score-rule/detail/${id}`,
    { ...opts }
  );
}
/**
 * 申请单详情 /api/v2/iam/permission-application/{id}
 */
export function v2IamPermissionApplicationById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponsePermissionApplicationDetailVo>(
    `/api/v2/iam/permission-application/${id}`,
    { ...opts }
  );
}
/**
 * 查询当前用户的申请记录 /api/v2/iam/permission-application/list
 */
export function v2IamPermissionApplicationList(
  params: {
    keyword?: string;
    oa_status?: OaState;
    application_status?: PermissionApplicationStatus;
    created_at_start?: number;
    created_at_end?: number;
    page_number?: number;
    page_size?: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponsePageVoPermissionApplicationListVo>(
    createUri('/api/v2/iam/permission-application/list', query),
    { ...opts }
  );
}
/**
 * 查询当前用户的角色权限列表 /api/v2/iam/my-permissions/roles
 */
export function v2IamMyPermissionsRoles(opts?: ReqOpts) {
  return request<ResponseListMyRolePermissionVo>(
    '/api/v2/iam/my-permissions/roles',
    { ...opts }
  );
}
/**
 * 批量删除当前用户的角色权限 /api/v2/iam/my-permissions/roles
 */
export function v2IamMyPermissionsRolesUsingDelete(
  data: DeleteMyRolesCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/iam/my-permissions/roles', {
    method: 'DELETE',
    data,
    ...opts,
  });
}
/**
 * 查询支持线上申请的角色（按角色分类聚合的两级树形） /api/v2/iam/my-permissions/roles/applicable
 */
export function v2IamMyPermissionsRolesApplicable(opts?: ReqOpts) {
  return request<ResponseListRoleCategoryTreeNodeVo>(
    '/api/v2/iam/my-permissions/roles/applicable',
    { ...opts }
  );
}
/**
 * 查询当前用户的产品权限树形结构 /api/v2/iam/my-permissions/products
 */
export function v2IamMyPermissionsProducts(
  params: {
    keyword?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListMyProductCategoryNode>(
    createUri('/api/v2/iam/my-permissions/products', query),
    { ...opts }
  );
}
/**
 * 批量删除当前用户的产品权限 /api/v2/iam/my-permissions/products
 */
export function v2IamMyPermissionsProductsUsingDelete(
  data: DeleteMyProductsCmd,
  opts?: ReqOpts
) {
  return request<ResponseVoid>('/api/v2/iam/my-permissions/products', {
    method: 'DELETE',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/echo/getSign
 */
export function v2EchoGetSign(opts?: ReqOpts) {
  return request<ResponseString>('/api/v2/echo/getSign', { ...opts });
}
/**
 * unknown /api/v2/echo/ai_proxy/permission_info
 */
export function v2EchoAiProxyPermissionInfo(opts?: ReqOpts) {
  return request<ResponseAiProxyPermissionVo>(
    '/api/v2/echo/ai_proxy/permission_info',
    { ...opts }
  );
}
/**
 * unknown /api/v2/dspf/view
 */
export function dspfQueryView(
  params: {
    dspf_mpc_no: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<object>(createUri('/api/v2/dspf/view', query), { ...opts });
}
/**
 * unknown /api/v2/dspf/overview_outcome
 */
export function dspfQueryOverviewOutcome(
  params: {
    dspf_mpc_nos: string[];
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<object>(createUri('/api/v2/dspf/overview_outcome', query), {
    ...opts,
  });
}
/**
 * unknown /api/v2/dspf/overview
 */
export function dspfQueryOverview(
  params: {
    dspf_mpc_nos: string[];
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<object>(createUri('/api/v2/dspf/overview', query), {
    ...opts,
  });
}
/**
 * 获取单据动态节点顺序 /api/v2/demands/{id}/flow-nodes
 */
export function demandFlowNodes(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseDemandFlowNodesDto>(
    `/api/v2/demands/${id}/flow-nodes`,
    { ...opts }
  );
}
/**
 * 判断是否满足预生成星图监测链接的触发条件 /api/v2/demands/xingtu_measure_should_pre_generate
 */
export function demandXingtuMeasureShouldPreGenerate(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseBoolean>(
    createUri('/api/v2/demands/xingtu_measure_should_pre_generate', query),
    { ...opts }
  );
}
/**
 * 查询加热助推效果数据 /api/v2/demands/queryBoostEffectData
 */
export function demandQueryBoostEffectData(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListBoostEffectDetailDto>(
    createUri('/api/v2/demands/queryBoostEffectData', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/demands/qc/review/cfg
 */
export function demandGetQcReviewConfig(opts?: ReqOpts) {
  return request<ResponseQcIntelligentReviewConfigDto>(
    '/api/v2/demands/qc/review/cfg',
    { ...opts }
  );
}
/**
 * 查询暂存数据 /api/v2/demands/plan-batch-confirm/query_draft
 */
export function planDemandBatchConfirmQueryDraft(
  params: {
    demand_id: number;
    state: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/v2/demands/plan-batch-confirm/query_draft', query),
    { ...opts }
  );
}
/**
 * 获取合并询报价批量面板信息 /api/v2/demands/plan-batch-confirm/detail
 */
export function planDemandBatchConfirmGetBatchConfirmDetail(
  params: {
    demand_id: number;
    state?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponsePlanDemandBatchConfirmInfoVo>(
    createUri('/api/v2/demands/plan-batch-confirm/detail', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/demands/kpi_adjustment/record
 */
export function demandKpiAdjustmentRecord(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListDemandKpiAdjustmentContentVo>(
    createUri('/api/v2/demands/kpi_adjustment/record', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/demands/export_detail_kol
 */
export function demandExportExportDetailKol(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<unknown>(
    createUri('/api/v2/demands/export_detail_kol', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/demands/export_detail_his_round_kol
 */
export function demandExportDetailHisRound(
  params: {
    demand_id: number;
    turns_number: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<unknown>(
    createUri('/api/v2/demands/export_detail_his_round_kol', query),
    { ...opts }
  );
}
/**
 * 导出全部方案（常规采购） /api/v2/demands/export_all_proposals
 */
export function demandExportExportAllProposals(
  params: {
    demand_id: number;
    turns_number: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/v2/demands/export_all_proposals', query),
    { ...opts }
  );
}
/**
 * 下载需求结案报告模板 /api/v2/demands/download_case_template
 */
export function demandDownloadCaseTemplate(opts?: ReqOpts) {
  return request<ResponseString>('/api/v2/demands/download_case_template', {
    ...opts,
  });
}
/**
 * 【结构化】获取合并报价差别详情 /api/v2/demands/diff_quote_detail_merge
 */
export function demandDiffQuoteDetailMerge(
  params: {
    demand_supplier_id: number;
    quote_attachment_id: number;
    blob?: boolean;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandSupplierQuoteDetailMergeDiffDto>(
    createUri('/api/v2/demands/diff_quote_detail_merge', query),
    { ...opts }
  );
}
/**
 * 【结构化】获取报价差别详情 /api/v2/demands/diff_quote_detail
 */
export function demandDiffQuoteDetail(
  params: {
    demand_supplier_quote_id: number;
    quote_attachment_id: number;
    blob?: boolean;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandSupplierQuoteDetailDiffDto>(
    createUri('/api/v2/demands/diff_quote_detail', query),
    { ...opts }
  );
}
/**
 * 获取批量验收信息 /api/v2/demands/batch-accept/none_binding/detail
 */
export function demandBatchAcceptGetNoneBindingBatchAcceptInfo(
  params: {
    demand_id: number;
    state?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandNoneBindingBatchAcceptInfoVo>(
    createUri('/api/v2/demands/batch-accept/none_binding/detail', query),
    { ...opts }
  );
}
/**
 * 获取批量验收信息 /api/v2/demands/batch-accept/detail
 */
export function demandBatchAcceptGetBatchAcceptInfo(
  params: {
    demand_id: number;
    state?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandBatchAcceptInfoVo>(
    createUri('/api/v2/demands/batch-accept/detail', query),
    { ...opts }
  );
}
/**
 * 【需求标品】查询下属标品，id /api/v2/demand_standard_library/sub_list/{parentId}
 */
export function demandStandardLibraryListSubLevelListById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseListDemandStandardCatalogVo>(
    `/api/v2/demand_standard_library/sub_list/${parent_id}`,
    { ...opts }
  );
}
/**
 * 【需求标品】查询下属标品，code /api/v2/demand_standard_library/sub_list/{code}
 */
export function demandStandardLibraryListSubLevelListByCode(
  params: {
    code: string;
  },
  opts?: ReqOpts
) {
  const { code } = params;
  return request<ResponseListDemandStandardCatalogVo>(
    `/api/v2/demand_standard_library/sub_list/${code}`,
    { ...opts }
  );
}
/**
 * 【需求标品】查询下属标品，id /api/v2/demand_standard_catalog/sub_list/{parentId}
 */
export function demandStandardCatalogListSubLevelListById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseListDemandStandardCatalogVo>(
    `/api/v2/demand_standard_catalog/sub_list/${parent_id}`,
    { ...opts }
  );
}
/**
 * 【需求标品】查询下属标品，code /api/v2/demand_standard_catalog/sub_list/{code}
 */
export function demandStandardCatalogListSubLevelListByCode(
  params: {
    code: string;
  },
  opts?: ReqOpts
) {
  const { code } = params;
  return request<ResponseListDemandStandardCatalogVo>(
    `/api/v2/demand_standard_catalog/sub_list/${code}`,
    { ...opts }
  );
}
/**
 * 【需求标品】标品等级列表查询 /api/v2/demand_standard_catalog/list_demand_standard_level
 */
export function demandStandardCatalogListDemandStandardLevel(opts?: ReqOpts) {
  return request<ResponseListDemandStandardLevelVo>(
    '/api/v2/demand_standard_catalog/list_demand_standard_level',
    { ...opts }
  );
}
/**
 * 内容营销达人弹框登录接口 /api/v2/demand_source/content/loginUrl
 */
export function demandSourceGetContentLoginUrl(
  params: {
    id?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/v2/demand_source/content/loginUrl', query),
    { ...opts }
  );
}
/**
 * 【报价结构化】按分类查询报价表格模板 /api/v2/demand_category_property/quote_table_by_category
 */
export function demandCategoryPropertyQuoteTable(
  params: {
    category_id: number;
    has_cooperate_account_column?: boolean;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandQuoteTableDto>(
    createUri(
      '/api/v2/demand_category_property/quote_table_by_category',
      query
    ),
    { ...opts }
  );
}
/**
 * 【结构化】【需求分类配置属性】按分类查询关联详情 /api/v2/demand_category_property/detail_by_category
 */
export function demandCategoryPropertyDetailByCategory(
  params: {
    category_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandCategoryPropertyTemplateDetailVo>(
    createUri('/api/v2/demand_category_property/detail_by_category', query),
    { ...opts }
  );
}
/**
 * 【结构化】【需求分类配置属性】需求分类属性关联详情 /api/v2/demand_category_property/detail
 */
export function demandCategoryPropertyDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandCategoryPropertyTemplateDetailVo>(
    createUri('/api/v2/demand_category_property/detail', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/demand-quote-temp/detail/{id}
 */
export function demandQuoteTempDetailById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseQuoteTempVo>(
    `/api/v2/demand-quote-temp/detail/${id}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/demand-kpi/detail/{id}
 */
export function demandKpiDetailById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseDemandKpiVo>(`/api/v2/demand-kpi/detail/${id}`, {
    ...opts,
  });
}
/**
 * unknown /api/v2/demand-invoice/info
 */
export function demandInvoiceGetInvoiceInfo(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandInvoiceInfoResDto>(
    createUri('/api/v2/demand-invoice/info', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/demand-cooperate-property/list-all
 */
export function demandCooperatePropertyListAll(opts?: ReqOpts) {
  return request<ResponsePageVoDemandCooperatePropertyVo>(
    '/api/v2/demand-cooperate-property/list-all',
    { ...opts }
  );
}
/**
 * unknown /api/v2/demand-cooperate-property/detail/{id}
 */
export function demandCooperatePropertyDetailById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseDemandCooperatePropertyVo>(
    `/api/v2/demand-cooperate-property/detail/${id}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/contract/template/queryByCode
 */
export function contractTemplateQueryByCode(
  params: {
    code: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseContractTemplateVo>(
    createUri('/api/v2/contract/template/queryByCode', query),
    { ...opts }
  );
}
/**
 * 获取合同类型 /api/v2/contract/getAllContractType
 */
export function contractGetAllContractType(opts?: ReqOpts) {
  return request<ResponseListContractTypeVo>(
    '/api/v2/contract/getAllContractType',
    { ...opts }
  );
}
/**
 * 【结构化】获取属性定义 /api/v2/common/{categoryId}/cooperate_properties
 */
export function commonGetDemandCooperateProperties(
  params: {
    category_id: number;
  },
  opts?: ReqOpts
) {
  const { category_id } = params;
  return request<ResponseListPropertyRefDto>(
    `/api/v2/common/${category_id}/cooperate_properties`,
    { ...opts }
  );
}
/**
 * 【报价结构化】获取属性定义 /api/v2/common/{categoryId}/cooperate_properties2
 */
export function commonGetDemandCooperateProperties2(
  params: {
    category_id: number;
  },
  opts?: ReqOpts
) {
  const { category_id } = params;
  return request<ResponseDemandCooperatePropertiesVo>(
    `/api/v2/common/${category_id}/cooperate_properties2`,
    { ...opts }
  );
}
/**
 * OA状态查询接口 /api/v2/common/query/oa_info
 */
export function commonExchangeRateQueryUsingGet(
  params: {
    id: number;
    demand_type: MpcDemandTypeEnum;
    approve_type: ApproveTypeEnum;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseOaProcessInfoVo>(
    createUri('/api/v2/common/query/oa_info', query),
    { ...opts }
  );
}
/**
 * 获取附件预览地址 /api/v2/common/file/preview-url
 */
export function fileGetPreviewUrl(
  params: {
    file_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/v2/common/file/preview-url', query),
    { ...opts }
  );
}
/**
 * 获取附件下载地址 /api/v2/common/file/download-url
 */
export function fileGetDownloadUrl(
  params: {
    file_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/v2/common/file/download-url', query),
    { ...opts }
  );
}
/**
 * 下载导入模板 /api/v2/common/data/{type}/import-template
 */
export function dataDownloadImportTemplate(
  params: {
    type: string;
  },
  opts?: ReqOpts
) {
  const { type } = params;
  return request<unknown>(`/api/v2/common/data/${type}/import-template`, {
    ...opts,
  });
}
/**
 * 自适配各级单详情展示 /api/v2/closed_loop/{id}/case_page_detail
 */
export function closedLoopDemandShowDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseClosedLoopDemandDetailVo>(
    `/api/v2/closed_loop/${id}/case_page_detail`,
    { ...opts }
  );
}
/**
 * 树列表 /api/v2/closed_loop/common/query/{id}/tree_list
 */
export function closedLoopCommonQueryListTree(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseClosedLoopTreeNodeVo>(
    `/api/v2/closed_loop/common/query/${id}/tree_list`,
    { ...opts }
  );
}
/**
 * 节点记录 /api/v2/closed_loop/common/query/{id}/node_records
 */
export function closedLoopCommonQueryListNodeRecords(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseJournalResDto>(
    `/api/v2/closed_loop/common/query/${id}/node_records`,
    { ...opts }
  );
}
/**
 * 发通知 /api/v2/closed_loop/common/action/{id}/send_remainder
 */
export function closedLoopCommonSendRemainder(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseReminderResDto>(
    `/api/v2/closed_loop/common/action/${id}/send_remainder`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/category-business-member-config/{id}
 */
export function categoryBusinessMemberConfigDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseCategoryBusinessMemberConfigVo>(
    `/api/v2/category-business-member-config/${id}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/category-business-member-config/match-business-users
 */
export function categoryBusinessMemberConfigMatchBusinessUsers(
  params: {
    l3_category_id: number;
    product_area: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListLong>(
    createUri(
      '/api/v2/category-business-member-config/match-business-users',
      query
    ),
    { ...opts }
  );
}
/**
 * unknown /api/v2/category-business-member-config/business-users
 */
export function categoryBusinessMemberConfigListBusinessUsers(opts?: ReqOpts) {
  return request<ResponseListUserSimpleVo>(
    '/api/v2/category-business-member-config/business-users',
    { ...opts }
  );
}
/**
 * 查询当前人员可配置的二级单列表 /api/v2/budget-alert/config/plan-demands
 */
export function v2BudgetAlertConfigPlanDemands(opts?: ReqOpts) {
  return request<ResponseListBudgetAlertConfigVo>(
    '/api/v2/budget-alert/config/plan-demands',
    { ...opts }
  );
}
/**
 * 查询指定需求单的预警配置 /api/v2/budget-alert/config/list-by-demand
 */
export function v2BudgetAlertConfigListByDemand(
  params: {
    demand_ids: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListBudgetAlertConfigVo>(
    createUri('/api/v2/budget-alert/config/list-by-demand', query),
    { ...opts }
  );
}
/**
 * 导出预算调整详情 /api/v2/budget-adjust/export/{id}
 */
export function v2BudgetAdjustExportById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<unknown>(`/api/v2/budget-adjust/export/${id}`, { ...opts });
}
/**
 * 查看预算调整详情 /api/v2/budget-adjust/detail/{id}
 */
export function v2BudgetAdjustDetailById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseBudgetAdjustDetailDto>(
    `/api/v2/budget-adjust/detail/${id}`,
    { ...opts }
  );
}
/**
 * 轮询任务状态（轻量级，仅返回状态和进度，适合前端高频轮询） /api/v2/biz-task/polling/{id}
 */
export function v2BizTaskPollingById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseBizTaskPollingVo>(`/api/v2/biz-task/polling/${id}`, {
    ...opts,
  });
}
/**
 * 【比选报价单配置】预览模板表格（含列定义，无数据行） /api/v2/bidding_quote_template/preview
 */
export function biddingQuoteTemplatePreview(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseBiddingQuoteTemplateTableVo>(
    createUri('/api/v2/bidding_quote_template/preview', query),
    { ...opts }
  );
}
/**
 * 【比选报价单配置】详情 /api/v2/bidding_quote_template/detail
 */
export function biddingQuoteTemplateDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseBiddingQuoteTemplateDetailVo>(
    createUri('/api/v2/bidding_quote_template/detail', query),
    { ...opts }
  );
}
/**
 * 查询模板详情 /api/v2/bidding_notification_template/{id}
 */
export function v2BiddingNotificationTemplateById(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseBiddingNotificationTemplateVo>(
    `/api/v2/bidding_notification_template/${id}`,
    { ...opts }
  );
}
/**
 * unknown /api/v2/bidding_evaluation_member/list
 */
export function biddingEvaluationMemberList(
  params: {
    member_type?: BiddingEvaluationMemberTypeEnum;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListBiddingEvaluationMemberDto>(
    createUri('/api/v2/bidding_evaluation_member/list', query),
    { ...opts }
  );
}
/**
 * 查询供应商报价明细 /api/v2/bidding/{id}/supplier/{sid}/quote_detail
 */
export function biddingGetSupplierQuoteDetail(
  params: {
    id: number;
    sid: number;
    turns_number?: number;
  },
  opts?: ReqOpts
) {
  const { id, sid, ...query } = params;
  return request<ResponseBiddingSupplierQuoteTableVo>(
    createUri(`/api/v2/bidding/${id}/supplier/${sid}/quote_detail`, query),
    { ...opts }
  );
}
/**
 * 获取报价单模板列表 /api/v2/bidding/{id}/quote_template_list
 */
export function biddingQuoteTemplateList(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseListBiddingQuoteTemplateTableVo>(
    `/api/v2/bidding/${id}/quote_template_list`,
    { ...opts }
  );
}
/**
 * 查询合同信息 /api/v2/bidding/{id}/contract_info
 */
export function biddingGetContractInfo(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseMapStringObject>(
    `/api/v2/bidding/${id}/contract_info`,
    { ...opts }
  );
}
/**
 * 合同中台审批详情代理 /api/v2/bidding/{id}/contract_approval_detail
 */
export function biddingGetContractApprovalDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseMapStringObject>(
    `/api/v2/bidding/${id}/contract_approval_detail`,
    { ...opts }
  );
}
/**
 * 导出全部方案（比选） /api/v2/bidding/export_all_proposals
 */
export function biddingExportAllProposals(
  params: {
    bidding_id: number;
    turns_number: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/v2/bidding/export_all_proposals', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/bidding/confidentiality/common-template
 */
export function biddingConfidentialityGetCommonNdaTemplate(
  params: {
    type?: NdaTemplateTypeDownloadEnum;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListAttachmentDownloadVo>(
    createUri('/api/v2/bidding/confidentiality/common-template', query),
    { ...opts }
  );
}
/**
 * 【比选评分模板】比选评分模板详情 /api/v2/bidding-plan-template/detail
 */
export function biddingPlanTemplateDetail(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseBiddingPlanTemplateDetailVo>(
    createUri('/api/v2/bidding-plan-template/detail', query),
    { ...opts }
  );
}
/**
 * 直接下载文件，已废弃 /api/v2/attachments/{id}
 */
export function attachmentBlobDownload(
  params: {
    id: number;
    download: string;
    blob?: boolean;
    is_blob?: boolean;
    is_blob?: boolean;
  },
  opts?: ReqOpts
) {
  const { id, ...query } = params;
  return request<unknown>(createUri(`/api/v2/attachments/${id}`, query), {
    ...opts,
  });
}
/**
 * 【新】获取文件下载链接 /api/v2/attachments/{id}/url
 */
export function attachmentBlobDownloadUrl(
  params: {
    id: number;
    download?: string;
    blob?: boolean;
    is_blob?: boolean;
    is_blob?: boolean;
  },
  opts?: ReqOpts
) {
  const { id, ...query } = params;
  return request<ResponseString>(
    createUri(`/api/v2/attachments/${id}/url`, query),
    { ...opts }
  );
}
/**
 * 【结构化】获取上传文件解析内容 /api/v2/attachments/{id}/data
 */
export function attachmentBlobData(
  params: {
    id: number;
    download: string;
    blob?: boolean;
    is_blob?: boolean;
    is_blob?: boolean;
    json?: boolean;
  },
  opts?: ReqOpts
) {
  const { id, ...query } = params;
  return request<ResponseObject>(
    createUri(`/api/v2/attachments/${id}/data`, query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/accounts/user_info
 */
export function accountsUserInfo(opts?: ReqOpts) {
  return request<ResponseUserInfoDto>('/api/v2/accounts/user_info', {
    ...opts,
  });
}
/**
 * unknown /api/v2/accounts/openid_verify
 */
export function accountsOpenidVerify(
  params: {
    callback_params: {
      [key: string]: any;
    };
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<unknown>(createUri('/api/v2/accounts/openid_verify', query), {
    ...opts,
  });
}
/**
 * unknown /api/v2/accounts/login_with_url
 */
export function accountsLoginWithUrl(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseLoginUserSimpleInfoDto>(
    createUri('/api/v2/accounts/login_with_url', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/accounts/login_out
 */
export function accountsLoginOut(opts?: ReqOpts) {
  return request<ResponseVoid>('/api/v2/accounts/login_out', { ...opts });
}
/**
 * unknown /api/v2/accounts/login
 */
export function accountsLoginPage(opts?: ReqOpts) {
  return request<unknown>('/api/v2/accounts/login', { ...opts });
}
/**
 * unknown /api/v1/dspf/view
 */
export function dspfView(
  params: {
    dspf_mpc_no: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<object>(createUri('/api/v1/dspf/view', query), { ...opts });
}
/**
 * unknown /api/v1/dspf/view-url
 */
export function dspfViewUrl(
  params: {
    dspf_mpc_no: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(createUri('/api/v1/dspf/view-url', query), {
    ...opts,
  });
}
/**
 * unknown /api/v1/dspf/external-url
 */
export function dspfExternalUrl(
  params: {
    requirement_ids: string[];
    requirement_group: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri('/api/v1/dspf/external-url', query),
    { ...opts }
  );
}
/**
 * unknown /api/outsource/v2/workbenches/export-demand-task/{taskId}
 */
export function outsourceWorkbenchesGetExportDemandTaskResult(
  params: {
    task_id: string;
  },
  opts?: ReqOpts
) {
  const { task_id } = params;
  return request<ResponseExportTaskResultVo>(
    `/api/outsource/v2/workbenches/export-demand-task/${task_id}`,
    { ...opts }
  );
}
/**
 * 获取相关指标信息 /api/outsource/v2/third-part/content-market-query/kpiValues
 */
export function outsourceContentMarketQueryGetKpiValues(
  params: {
    case_demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListExternalKpiValueVo>(
    createUri(
      '/api/outsource/v2/third-part/content-market-query/kpiValues',
      query
    ),
    { ...opts }
  );
}
/**
 * 即时获取合作内容相关指标信息 /api/outsource/v2/third-part/content-market-query/kpiValuesImmediately
 */
export function outsourceContentMarketQueryGetKpiValuesImmediately(
  params: {
    case_demand_id: number;
    cooperate_account_id: number;
    supplier_settle_price: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListExternalKpiValueVo>(
    createUri(
      '/api/outsource/v2/third-part/content-market-query/kpiValuesImmediately',
      query
    ),
    { ...opts }
  );
}
/**
 * 查询MPC监测链接（自动识别二级单/三级单） /api/outsource/v2/third-part/content-market-query/getMonitorLinks
 */
export function outsourceContentMarketQueryGetMonitorLinks(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListMonitorLinkItemVo>(
    createUri(
      '/api/outsource/v2/third-part/content-market-query/getMonitorLinks',
      query
    ),
    { ...opts }
  );
}
/**
 * 查询三级需求单作品信息（数据来自内容营销平台） /api/outsource/v2/third-part/content-market-query/getDemandWorkNum
 */
export function outsourceContentMarketQueryGetDemandWorkNum(
  params: {
    case_demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseInteger>(
    createUri(
      '/api/outsource/v2/third-part/content-market-query/getDemandWorkNum',
      query
    ),
    { ...opts }
  );
}
/**
 * 查询三级需求单作品信息（数据来自内容营销平台） /api/outsource/v2/third-part/content-market-query/getDemandWorkInfo
 */
export function outsourceContentMarketQueryGetDemandWorkInfo(
  params: {
    case_demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandWorkInfoVo>(
    createUri(
      '/api/outsource/v2/third-part/content-market-query/getDemandWorkInfo',
      query
    ),
    { ...opts }
  );
}
/**
 * 生成[内容营销平台-供应商上传数据]页面请求url /api/outsource/v2/third-part/content-market-query/genUpdateDataPageUrlForSupplier
 */
export function outsourceContentMarketQueryGenUpdateDataPageUrlForSupplier(
  params: {
    demand_id: number;
    case_demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri(
      '/api/outsource/v2/third-part/content-market-query/genUpdateDataPageUrlForSupplier',
      query
    ),
    { ...opts }
  );
}
/**
 * 导出监测链接到Excel（异步生成，返回 taskId） /api/outsource/v2/third-part/content-market-query/exportMonitorLinks
 */
export function outsourceContentMarketQueryExportMonitorLinks(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseString>(
    createUri(
      '/api/outsource/v2/third-part/content-market-query/exportMonitorLinks',
      query
    ),
    { ...opts }
  );
}
/**
 * 查询导出任务结果 /api/outsource/v2/task/query
 */
export function outsourceV2TaskQuery(
  params: {
    task_id: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseExportTaskResultDto>(
    createUri('/api/outsource/v2/task/query', query),
    { ...opts }
  );
}
/**
 * 查询供应商评分记录 /api/outsource/v2/supplier-new/{id}/rating_records
 */
export function outsourceNewSupplierRatingRecords(
  params: {
    id: number;
    current?: number;
    limit?: number;
  },
  opts?: ReqOpts
) {
  const { id, ...query } = params;
  return request<ResponsePageVoDemandRatingRecordVo>(
    createUri(`/api/outsource/v2/supplier-new/${id}/rating_records`, query),
    { ...opts }
  );
}
/**
 * unknown /api/outsource/v2/supplier-new/query-vmdm-bank-account
 */
export function outsourceNewSupplierQueryVmdmBankAccount(
  params: {
    id?: number;
    mdm_code?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListVmdmSupplierBankVo>(
    createUri('/api/outsource/v2/supplier-new/query-vmdm-bank-account', query),
    { ...opts }
  );
}
/**
 * unknown /api/outsource/v2/supplier-new/list
 */
export function outsourceNewSupplierList(opts?: ReqOpts) {
  return request<ResponseListOutsourceSupplierResDto>(
    '/api/outsource/v2/supplier-new/list',
    { ...opts }
  );
}
/**
 * get prepayment detail /api/outsource/v2/prepayment/{prepaymentId}
 */
export function prepaymentOutsourceGetDetail(
  params: {
    prepayment_id: number;
  },
  opts?: ReqOpts
) {
  const { prepayment_id } = params;
  return request<ResponsePrepaymentDetailVo>(
    `/api/outsource/v2/prepayment/${prepayment_id}`,
    { ...opts }
  );
}
/**
 * query pending invoice list /api/outsource/v2/prepayment/pending-invoice/{supplierId}
 */
export function prepaymentOutsourceQueryPendingInvoiceList(
  params: {
    supplier_id: number;
  },
  opts?: ReqOpts
) {
  const { supplier_id } = params;
  return request<ResponseListPrepaymentListVo>(
    `/api/outsource/v2/prepayment/pending-invoice/${supplier_id}`,
    { ...opts }
  );
}
/**
 * list prepayment by demand /api/outsource/v2/prepayment/list-by-demand/{demandId}
 */
export function prepaymentOutsourceGetListByDemandId(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id } = params;
  return request<ResponseListPrepaymentListVo>(
    `/api/outsource/v2/prepayment/list-by-demand/${demand_id}`,
    { ...opts }
  );
}
/**
 * 获取Muse上传页面URL /api/outsource/v2/muse/upload-url/{demandId}/{cooperateIndex}/{standardCode1}/{standardCode2}
 */
export function outsourceV2MuseUploadUrlByDemandIdAndCooperateIndexAndStandardCode1AndStandardCode2(
  params: {
    demand_id: number;
    cooperate_index: number;
    standard_code1: string;
    standard_code2: string;
  },
  opts?: ReqOpts
) {
  const { demand_id, cooperate_index, standard_code1, standard_code2 } = params;
  return request<ResponseMuseUploadUrlVo>(
    `/api/outsource/v2/muse/upload-url/${demand_id}/${cooperate_index}/${standard_code1}/${standard_code2}`,
    { ...opts }
  );
}
/**
 * 获取 Muse 文件上传凭证（token + uploadUrl，前端拿到后直接上传文件到 Muse） /api/outsource/v2/muse/upload-permission/{demandId}/{cooperateIndex}
 */
export function outsourceV2MuseUploadPermissionByDemandIdAndCooperateIndex(
  params: {
    demand_id: number;
    cooperate_index: number;
  },
  opts?: ReqOpts
) {
  const { demand_id, cooperate_index } = params;
  return request<ResponseMuseUploadPermissionVo>(
    `/api/outsource/v2/muse/upload-permission/${demand_id}/${cooperate_index}`,
    { ...opts }
  );
}
/**
 * 免登上传页面数据（合作内容列表） /api/outsource/v2/muse/upload-page/{demandId}
 */
export function outsourceV2MuseUploadPageByDemandId(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id } = params;
  return request<ResponseMuseUploadPageVo>(
    `/api/outsource/v2/muse/upload-page/${demand_id}`,
    { ...opts }
  );
}
/**
 * 校验供应商是否已完成素材上传（资源包下每个文件夹都有附件） /api/outsource/v2/muse/upload-complete-check/{demandId}
 */
export function outsourceV2MuseUploadCompleteCheckByDemandId(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id } = params;
  return request<ResponseBoolean>(
    `/api/outsource/v2/muse/upload-complete-check/${demand_id}`,
    { ...opts }
  );
}
/**
 * 获取Muse文件管理器iframe URL /api/outsource/v2/muse/resource/{demandId}/{cooperateAccountId}/iframe-url
 */
export function outsourceV2MuseResourceByDemandIdAndCooperateAccountIdIframeUrl(
  params: {
    demand_id: number;
    cooperate_account_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id, cooperate_account_id } = params;
  return request<ResponseMuseIframeUrlVo>(
    `/api/outsource/v2/muse/resource/${demand_id}/${cooperate_account_id}/iframe-url`,
    { ...opts }
  );
}
/**
 * 查询各合作内容素材上传状态 /api/outsource/v2/muse/resource/{demandId}/upload-status
 */
export function outsourceV2MuseResourceByDemandIdUploadStatus(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id } = params;
  return request<ResponseListMuseUploadStatusVo>(
    `/api/outsource/v2/muse/resource/${demand_id}/upload-status`,
    { ...opts }
  );
}
/**
 * 查询已邀请协作人员列表 /api/outsource/v2/muse/collaborator/list/{demandId}
 */
export function outsourceV2MuseCollaboratorListByDemandId(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { demand_id } = params;
  return request<ResponseListMuseCollaboratorVo>(
    `/api/outsource/v2/muse/collaborator/list/${demand_id}`,
    { ...opts }
  );
}
/**
 * 获取文件夹下已有素材列表（按三级单ID+合作内容序号+一级标品code+二级标品code定位Muse文件夹） /api/outsource/v2/muse/asset/list/{childDemandId}/{indexId}/{standardCatalogL1Code}/{standardCatalogL2Code}
 */
export function outsourceV2MuseAssetListByChildDemandIdAndIndexIdAndStandardCatalogL1CodeAndStandardCatalogL2Code(
  params: {
    child_demand_id: number;
    index_id: number;
    standard_catalog_l1_code: string;
    standard_catalog_l2_code: string;
  },
  opts?: ReqOpts
) {
  const {
    child_demand_id,
    index_id,
    standard_catalog_l1_code,
    standard_catalog_l2_code,
  } = params;
  return request<ResponseListMuseAssetVo>(
    `/api/outsource/v2/muse/asset/list/${child_demand_id}/${index_id}/${standard_catalog_l1_code}/${standard_catalog_l2_code}`,
    { ...opts }
  );
}
/**
 * 查询加热助推效果数据 /api/outsource/v2/demands/queryBoostEffectData
 */
export function outsourceDemandQueryBoostEffectData(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListOutsourceBoostEffectDetailDto>(
    createUri('/api/outsource/v2/demands/queryBoostEffectData', query),
    { ...opts }
  );
}
/**
 * 获取临时保存的结构化结算报告数据 /api/outsource/v2/demands/get_saved_structured_settle_reports_data
 */
export function outsourceDemandGetSavedStructuredSettleReportsData(
  params: {
    demand_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseOutsourceSaveStructuredSettleReportsDataDto>(
    createUri(
      '/api/outsource/v2/demands/get_saved_structured_settle_reports_data',
      query
    ),
    { ...opts }
  );
}
/**
 * 下载需求结案报告模板 /api/outsource/v2/demands/download_case_template
 */
export function outsourceDemandDownloadCaseTemplate(opts?: ReqOpts) {
  return request<ResponseString>(
    '/api/outsource/v2/demands/download_case_template',
    { ...opts }
  );
}
/**
 * 【结构化】获取合并报价差别详情 /api/outsource/v2/demands/diff_quote_detail_merge
 */
export function outsourceDemandDiffQuoteDetailMerge(
  params: {
    demand_supplier_id: number;
    quote_attachment_id: number;
    blob?: boolean;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandSupplierQuoteDetailMergeDiffDto>(
    createUri('/api/outsource/v2/demands/diff_quote_detail_merge', query),
    { ...opts }
  );
}
/**
 * 【结构化】获取报价差别详情 /api/outsource/v2/demands/diff_quote_detail
 */
export function outsourceDemandDiffQuoteDetail(
  params: {
    demand_supplier_quote_id: number;
    quote_attachment_id: number;
    blob?: boolean;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseDemandSupplierQuoteDetailDiffDto>(
    createUri('/api/outsource/v2/demands/diff_quote_detail', query),
    { ...opts }
  );
}
/**
 * 下载导入模板 /api/outsource/v2/common/data/{type}/import-template
 */
export function outsourceDataDownloadImportTemplate(
  params: {
    type: string;
  },
  opts?: ReqOpts
) {
  const { type } = params;
  return request<unknown>(
    `/api/outsource/v2/common/data/${type}/import-template`,
    { ...opts }
  );
}
/**
 * unknown /api/outsource/v2/bidding/quote_detail
 */
export function outsourceBiddingQuoteDetail(
  params: {
    bidding_id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseOutsourceBiddingSupplierQuoteDetailResDto>(
    createUri('/api/outsource/v2/bidding/quote_detail', query),
    { ...opts }
  );
}
/**
 * unknown /api/outsource/v2/attachments/{id}
 */
export function outsourceAttachmentDownload(
  params: {
    id: number;
    download: string;
    blob?: boolean;
    is_blob?: boolean;
    is_blob?: boolean;
  },
  opts?: ReqOpts
) {
  const { id, ...query } = params;
  return request<unknown>(
    createUri(`/api/outsource/v2/attachments/${id}`, query),
    { ...opts }
  );
}
/**
 * 【新】获取文件下载链接 /api/outsource/v2/attachments/{id}/url
 */
export function outsourceAttachmentDownloadUrl(
  params: {
    id: number;
    download: string;
    blob?: boolean;
    is_blob?: boolean;
    is_blob?: boolean;
  },
  opts?: ReqOpts
) {
  const { id, ...query } = params;
  return request<ResponseString>(
    createUri(`/api/outsource/v2/attachments/${id}/url`, query),
    { ...opts }
  );
}
/**
 * 【结构化】获取上传文件解析内容 /api/outsource/v2/attachments/{id}/data
 */
export function outsourceAttachmentData(
  params: {
    id: number;
    download: string;
    blob?: boolean;
    is_blob?: boolean;
    is_blob?: boolean;
    json?: boolean;
  },
  opts?: ReqOpts
) {
  const { id, ...query } = params;
  return request<ResponseObject>(
    createUri(`/api/outsource/v2/attachments/${id}/data`, query),
    { ...opts }
  );
}
/**
 * unknown /api/outsource/v2/accounts/user_info
 */
export function outsourceAccountsUserInfo(opts?: ReqOpts) {
  return request<ResponseUserInfoDto>('/api/outsource/v2/accounts/user_info', {
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/accounts/menus_count
 */
export function outsourceAccountsMenusCount(opts?: ReqOpts) {
  return request<ResponseListOutsourceMenuCountResultDto>(
    '/api/outsource/v2/accounts/menus_count',
    { ...opts }
  );
}
/**
 * unknown /api/outsource/v2/accounts/login_with_url
 */
export function outsourceAccountsLoginWithUrl(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseLoginUserSimpleInfoDto>(
    createUri('/api/outsource/v2/accounts/login_with_url', query),
    { ...opts }
  );
}
/**
 * unknown /api/mobile/v2/attachments/{id}
 */
export function mobileCommonDownload(
  params: {
    id: number;
    is_blob?: boolean;
    download?: string;
  },
  opts?: ReqOpts
) {
  const { id, ...query } = params;
  return request<string[]>(
    createUri(`/api/mobile/v2/attachments/${id}`, query),
    { ...opts }
  );
}
/**
 * unknown /api/mobile/v2/accounts/user_info
 */
export function mobileAccountUserInfo(opts?: ReqOpts) {
  return request<RubyResponseVoUserInfoDto>(
    '/api/mobile/v2/accounts/user_info',
    { ...opts }
  );
}
/**
 * unknown /api/mobile/v2/accounts/popo_entry
 */
export function mobileAccountPopoEntry(
  params: {
    code: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<unknown>(
    createUri('/api/mobile/v2/accounts/popo_entry', query),
    { ...opts }
  );
}
/**
 * unknown /api/mobile/v2/accounts/openid_verify
 */
export function mobileAccountOpenidVerify(
  params: {
    callback_params: {
      [key: string]: any;
    };
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<unknown>(
    createUri('/api/mobile/v2/accounts/openid_verify', query),
    { ...opts }
  );
}
/**
 * unknown /api/mobile/v2/accounts/login_with_url
 */
export function mobileAccountLoginWithUrl(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<RubyResponseVoLoginUserSimpleInfoDto>(
    createUri('/api/mobile/v2/accounts/login_with_url', query),
    { ...opts }
  );
}
/**
 * unknown /api/mobile/v2/accounts/login_out
 */
export function mobileAccountLoginOut(opts?: ReqOpts) {
  return request<RubyResponseVoVoid>('/api/mobile/v2/accounts/login_out', {
    ...opts,
  });
}
/**
 * unknown /api/mobile/v2/accounts/login
 */
export function mobileAccountLoginPage(opts?: ReqOpts) {
  return request<unknown>('/api/mobile/v2/accounts/login', { ...opts });
}
/**
 * unknown /api/v2/bidding_evaluation_member/remove/{id}
 */
export function biddingEvaluationMemberRemove(
  params: {
    id: number;
  },
  opts?: ReqOpts
) {
  const { id } = params;
  return request<ResponseString>(
    `/api/v2/bidding_evaluation_member/remove/${id}`,
    { method: 'DELETE', ...opts }
  );
}
/**
 * 撤销协作邀请 /api/outsource/v2/muse/collaborator/{collaboratorId}
 */
export function outsourceV2MuseCollaboratorByCollaboratorId(
  params: {
    collaborator_id: number;
  },
  opts?: ReqOpts
) {
  const { collaborator_id } = params;
  return request<ResponseVoid>(
    `/api/outsource/v2/muse/collaborator/${collaborator_id}`,
    { method: 'DELETE', ...opts }
  );
}
/**
 * 删除指定素材 /api/outsource/v2/muse/asset/{demandId}/{attachmentId}
 */
export function outsourceV2MuseAssetByDemandIdAndAttachmentId(
  params: {
    demand_id: number;
    attachment_id: string;
  },
  opts?: ReqOpts
) {
  const { demand_id, attachment_id } = params;
  return request<ResponseVoid>(
    `/api/outsource/v2/muse/asset/${demand_id}/${attachment_id}`,
    { method: 'DELETE', ...opts }
  );
}
/**
 * unknown /support/echo/hi
 */
export function echoEcho(
  params: {
    message?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<string>(createUri('/support/echo/hi', query), { ...opts });
}
/**
 * unknown /support/echo/hi
 */
export function echoEchoUsingPut(
  params: {
    message?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<string>(createUri('/support/echo/hi', query), {
    method: 'PUT',
    ...opts,
  });
}
/**
 * unknown /support/echo/hi
 */
export function echoEchoUsingPost(
  params: {
    message?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<string>(createUri('/support/echo/hi', query), {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /support/echo/hi
 */
export function echoEchoUsingDelete(
  params: {
    message?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<string>(createUri('/support/echo/hi', query), {
    method: 'DELETE',
    ...opts,
  });
}
/**
 * unknown /support/echo/hi
 */
export function echoEchoUsingOptions(
  params: {
    message?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<string>(createUri('/support/echo/hi', query), {
    method: 'OPTIONS',
    ...opts,
  });
}
/**
 * unknown /support/echo/hi
 */
export function echoEchoUsingHead(
  params: {
    message?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<string>(createUri('/support/echo/hi', query), {
    method: 'HEAD',
    ...opts,
  });
}
/**
 * unknown /support/echo/hi
 */
export function echoEchoUsingPatch(
  params: {
    message?: string;
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<string>(createUri('/support/echo/hi', query), {
    method: 'PATCH',
    ...opts,
  });
}
/**
 * unknown /support/echo/exception
 */
export function echoException(opts?: ReqOpts) {
  return request<unknown>('/support/echo/exception', { ...opts });
}
/**
 * unknown /support/echo/exception
 */
export function echoExceptionUsingPut(opts?: ReqOpts) {
  return request<unknown>('/support/echo/exception', {
    method: 'PUT',
    ...opts,
  });
}
/**
 * unknown /support/echo/exception
 */
export function echoExceptionUsingPost(opts?: ReqOpts) {
  return request<unknown>('/support/echo/exception', {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /support/echo/exception
 */
export function echoExceptionUsingDelete(opts?: ReqOpts) {
  return request<unknown>('/support/echo/exception', {
    method: 'DELETE',
    ...opts,
  });
}
/**
 * unknown /support/echo/exception
 */
export function echoExceptionUsingOptions(opts?: ReqOpts) {
  return request<unknown>('/support/echo/exception', {
    method: 'OPTIONS',
    ...opts,
  });
}
/**
 * unknown /support/echo/exception
 */
export function echoExceptionUsingHead(opts?: ReqOpts) {
  return request<unknown>('/support/echo/exception', {
    method: 'HEAD',
    ...opts,
  });
}
/**
 * unknown /support/echo/exception
 */
export function echoExceptionUsingPatch(opts?: ReqOpts) {
  return request<unknown>('/support/echo/exception', {
    method: 'PATCH',
    ...opts,
  });
}
/**
 * unknown /api/v2/test/thirdDemandAbandon
 */
export function testThirdDemandAbandon(
  params: FeeControlThirdDemandAbandonCmd,
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<unknown>(createUri('/api/v2/test/thirdDemandAbandon', query), {
    ...opts,
  });
}
/**
 * unknown /api/v2/test/thirdDemandAbandon
 */
export function testThirdDemandAbandonUsingPut(
  data: FeeControlThirdDemandAbandonCmd,
  opts?: ReqOpts
) {
  return request<unknown>('/api/v2/test/thirdDemandAbandon', {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/test/thirdDemandAbandon
 */
export function testThirdDemandAbandonUsingPost(
  data: FeeControlThirdDemandAbandonCmd,
  opts?: ReqOpts
) {
  return request<unknown>('/api/v2/test/thirdDemandAbandon', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/test/thirdDemandAbandon
 */
export function testThirdDemandAbandonUsingDelete(
  data: FeeControlThirdDemandAbandonCmd,
  opts?: ReqOpts
) {
  return request<unknown>('/api/v2/test/thirdDemandAbandon', {
    method: 'DELETE',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/test/thirdDemandAbandon
 */
export function testThirdDemandAbandonUsingOptions(
  data: FeeControlThirdDemandAbandonCmd,
  opts?: ReqOpts
) {
  return request<unknown>('/api/v2/test/thirdDemandAbandon', {
    method: 'OPTIONS',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/test/thirdDemandAbandon
 */
export function testThirdDemandAbandonUsingHead(
  data: FeeControlThirdDemandAbandonCmd,
  opts?: ReqOpts
) {
  return request<unknown>('/api/v2/test/thirdDemandAbandon', {
    method: 'HEAD',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/test/thirdDemandAbandon
 */
export function testThirdDemandAbandonUsingPatch(
  data: FeeControlThirdDemandAbandonCmd,
  opts?: ReqOpts
) {
  return request<unknown>('/api/v2/test/thirdDemandAbandon', {
    method: 'PATCH',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/test/noNeedBusinessAcceptCategoryIds
 */
export function testNoNeedBusinessAcceptCategoryIds(opts?: ReqOpts) {
  return request<CategoryEtcConfig[]>(
    '/api/v2/test/noNeedBusinessAcceptCategoryIds',
    { ...opts }
  );
}
/**
 * unknown /api/v2/test/noNeedBusinessAcceptCategoryIds
 */
export function testNoNeedBusinessAcceptCategoryIdsUsingPut(opts?: ReqOpts) {
  return request<CategoryEtcConfig[]>(
    '/api/v2/test/noNeedBusinessAcceptCategoryIds',
    { method: 'PUT', ...opts }
  );
}
/**
 * unknown /api/v2/test/noNeedBusinessAcceptCategoryIds
 */
export function testNoNeedBusinessAcceptCategoryIdsUsingPost(opts?: ReqOpts) {
  return request<CategoryEtcConfig[]>(
    '/api/v2/test/noNeedBusinessAcceptCategoryIds',
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/test/noNeedBusinessAcceptCategoryIds
 */
export function testNoNeedBusinessAcceptCategoryIdsUsingDelete(opts?: ReqOpts) {
  return request<CategoryEtcConfig[]>(
    '/api/v2/test/noNeedBusinessAcceptCategoryIds',
    { method: 'DELETE', ...opts }
  );
}
/**
 * unknown /api/v2/test/noNeedBusinessAcceptCategoryIds
 */
export function testNoNeedBusinessAcceptCategoryIdsUsingOptions(
  opts?: ReqOpts
) {
  return request<CategoryEtcConfig[]>(
    '/api/v2/test/noNeedBusinessAcceptCategoryIds',
    { method: 'OPTIONS', ...opts }
  );
}
/**
 * unknown /api/v2/test/noNeedBusinessAcceptCategoryIds
 */
export function testNoNeedBusinessAcceptCategoryIdsUsingHead(opts?: ReqOpts) {
  return request<CategoryEtcConfig[]>(
    '/api/v2/test/noNeedBusinessAcceptCategoryIds',
    { method: 'HEAD', ...opts }
  );
}
/**
 * unknown /api/v2/test/noNeedBusinessAcceptCategoryIds
 */
export function testNoNeedBusinessAcceptCategoryIdsUsingPatch(opts?: ReqOpts) {
  return request<CategoryEtcConfig[]>(
    '/api/v2/test/noNeedBusinessAcceptCategoryIds',
    { method: 'PATCH', ...opts }
  );
}
/**
 * unknown /api/v2/test/getUsersNormalFirst
 */
export function testGetUsersNormalFirst(
  params: {
    ids: number[];
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<UserAndDept[]>(
    createUri('/api/v2/test/getUsersNormalFirst', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/test/getUsersNormalFirst
 */
export function testGetUsersNormalFirstUsingPut(
  params: {
    ids: number[];
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<UserAndDept[]>(
    createUri('/api/v2/test/getUsersNormalFirst', query),
    { method: 'PUT', ...opts }
  );
}
/**
 * unknown /api/v2/test/getUsersNormalFirst
 */
export function testGetUsersNormalFirstUsingPost(
  params: {
    ids: number[];
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<UserAndDept[]>(
    createUri('/api/v2/test/getUsersNormalFirst', query),
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/test/getUsersNormalFirst
 */
export function testGetUsersNormalFirstUsingDelete(
  params: {
    ids: number[];
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<UserAndDept[]>(
    createUri('/api/v2/test/getUsersNormalFirst', query),
    { method: 'DELETE', ...opts }
  );
}
/**
 * unknown /api/v2/test/getUsersNormalFirst
 */
export function testGetUsersNormalFirstUsingOptions(
  params: {
    ids: number[];
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<UserAndDept[]>(
    createUri('/api/v2/test/getUsersNormalFirst', query),
    { method: 'OPTIONS', ...opts }
  );
}
/**
 * unknown /api/v2/test/getUsersNormalFirst
 */
export function testGetUsersNormalFirstUsingHead(
  params: {
    ids: number[];
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<UserAndDept[]>(
    createUri('/api/v2/test/getUsersNormalFirst', query),
    { method: 'HEAD', ...opts }
  );
}
/**
 * unknown /api/v2/test/getUsersNormalFirst
 */
export function testGetUsersNormalFirstUsingPatch(
  params: {
    ids: number[];
  },
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<UserAndDept[]>(
    createUri('/api/v2/test/getUsersNormalFirst', query),
    { method: 'PATCH', ...opts }
  );
}
/**
 * unknown /api/v2/test/closedLoopCaseSubmitPaymentSchedule
 */
export function testClosedLoopCaseSubmitPaymentSchedule(opts?: ReqOpts) {
  return request<Response>('/api/v2/test/closedLoopCaseSubmitPaymentSchedule', {
    ...opts,
  });
}
/**
 * unknown /api/v2/test/closedLoopCaseSubmitPaymentSchedule
 */
export function testClosedLoopCaseSubmitPaymentScheduleUsingPut(
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/test/closedLoopCaseSubmitPaymentSchedule', {
    method: 'PUT',
    ...opts,
  });
}
/**
 * unknown /api/v2/test/closedLoopCaseSubmitPaymentSchedule
 */
export function testClosedLoopCaseSubmitPaymentScheduleUsingPost(
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/test/closedLoopCaseSubmitPaymentSchedule', {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/v2/test/closedLoopCaseSubmitPaymentSchedule
 */
export function testClosedLoopCaseSubmitPaymentScheduleUsingDelete(
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/test/closedLoopCaseSubmitPaymentSchedule', {
    method: 'DELETE',
    ...opts,
  });
}
/**
 * unknown /api/v2/test/closedLoopCaseSubmitPaymentSchedule
 */
export function testClosedLoopCaseSubmitPaymentScheduleUsingOptions(
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/test/closedLoopCaseSubmitPaymentSchedule', {
    method: 'OPTIONS',
    ...opts,
  });
}
/**
 * unknown /api/v2/test/closedLoopCaseSubmitPaymentSchedule
 */
export function testClosedLoopCaseSubmitPaymentScheduleUsingHead(
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/test/closedLoopCaseSubmitPaymentSchedule', {
    method: 'HEAD',
    ...opts,
  });
}
/**
 * unknown /api/v2/test/closedLoopCaseSubmitPaymentSchedule
 */
export function testClosedLoopCaseSubmitPaymentScheduleUsingPatch(
  opts?: ReqOpts
) {
  return request<Response>('/api/v2/test/closedLoopCaseSubmitPaymentSchedule', {
    method: 'PATCH',
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/projects/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateList(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/projects/fee_control_rate_list',
    { ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/projects/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingPut(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/projects/fee_control_rate_list',
    { method: 'PUT', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/projects/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingPost(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/projects/fee_control_rate_list',
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/projects/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingDelete(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/projects/fee_control_rate_list',
    { method: 'DELETE', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/projects/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingOptions(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/projects/fee_control_rate_list',
    { method: 'OPTIONS', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/projects/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingHead(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/projects/fee_control_rate_list',
    { method: 'HEAD', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/projects/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingPatch(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/projects/fee_control_rate_list',
    { method: 'PATCH', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingGet(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/fee_control_rate_list',
    { ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingPut2(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/fee_control_rate_list',
    { method: 'PUT', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingPost2(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/fee_control_rate_list',
    { method: 'POST', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingDelete2(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/fee_control_rate_list',
    { method: 'DELETE', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingOptions2(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/fee_control_rate_list',
    { method: 'OPTIONS', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingHead2(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/fee_control_rate_list',
    { method: 'HEAD', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/fee_control_rate_list
 */
export function rubyFeeControlFeeControlRateListUsingPatch2(opts?: ReqOpts) {
  return request<RubyResponseVoObject>(
    '/api/v2/ruby_migrate/fee_control_rate_list',
    { method: 'PATCH', ...opts }
  );
}
/**
 * unknown /api/v2/ruby_migrate/excel_reports/export
 */
export function rubyProxyOldJavaPostExport(opts?: ReqOpts) {
  return request<unknown>('/api/v2/ruby_migrate/excel_reports/export', {
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/excel_reports/export
 */
export function rubyProxyOldJavaPostExportUsingPut(opts?: ReqOpts) {
  return request<unknown>('/api/v2/ruby_migrate/excel_reports/export', {
    method: 'PUT',
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/excel_reports/export
 */
export function rubyProxyOldJavaPostExportUsingPost(opts?: ReqOpts) {
  return request<unknown>('/api/v2/ruby_migrate/excel_reports/export', {
    method: 'POST',
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/excel_reports/export
 */
export function rubyProxyOldJavaPostExportUsingDelete(opts?: ReqOpts) {
  return request<unknown>('/api/v2/ruby_migrate/excel_reports/export', {
    method: 'DELETE',
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/excel_reports/export
 */
export function rubyProxyOldJavaPostExportUsingOptions(opts?: ReqOpts) {
  return request<unknown>('/api/v2/ruby_migrate/excel_reports/export', {
    method: 'OPTIONS',
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/excel_reports/export
 */
export function rubyProxyOldJavaPostExportUsingHead(opts?: ReqOpts) {
  return request<unknown>('/api/v2/ruby_migrate/excel_reports/export', {
    method: 'HEAD',
    ...opts,
  });
}
/**
 * unknown /api/v2/ruby_migrate/excel_reports/export
 */
export function rubyProxyOldJavaPostExportUsingPatch(opts?: ReqOpts) {
  return request<unknown>('/api/v2/ruby_migrate/excel_reports/export', {
    method: 'PATCH',
    ...opts,
  });
}
/**
 * unknown /api/mobile/v2/login/gen_cookies
 */
export function loginGenCookies(params: GenCookiesParam, opts?: ReqOpts) {
  const { ...query } = params;
  return request<ResponseListCookie>(
    createUri('/api/mobile/v2/login/gen_cookies', query),
    { ...opts }
  );
}
/**
 * unknown /api/mobile/v2/login/gen_cookies
 */
export function loginGenCookiesUsingPut(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/mobile/v2/login/gen_cookies', {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * unknown /api/mobile/v2/login/gen_cookies
 */
export function loginGenCookiesUsingPost(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/mobile/v2/login/gen_cookies', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/mobile/v2/login/gen_cookies
 */
export function loginGenCookiesUsingDelete(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/mobile/v2/login/gen_cookies', {
    method: 'DELETE',
    data,
    ...opts,
  });
}
/**
 * unknown /api/mobile/v2/login/gen_cookies
 */
export function loginGenCookiesUsingOptions(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/mobile/v2/login/gen_cookies', {
    method: 'OPTIONS',
    data,
    ...opts,
  });
}
/**
 * unknown /api/mobile/v2/login/gen_cookies
 */
export function loginGenCookiesUsingHead(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/mobile/v2/login/gen_cookies', {
    method: 'HEAD',
    data,
    ...opts,
  });
}
/**
 * unknown /api/mobile/v2/login/gen_cookies
 */
export function loginGenCookiesUsingPatch(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/mobile/v2/login/gen_cookies', {
    method: 'PATCH',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/login/gen_cookies
 */
export function loginGenCookiesUsingGet(
  params: GenCookiesParam,
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListCookie>(
    createUri('/api/outsource/v2/login/gen_cookies', query),
    { ...opts }
  );
}
/**
 * unknown /api/outsource/v2/login/gen_cookies
 */
export function loginGenCookiesUsingPut2(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/outsource/v2/login/gen_cookies', {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/login/gen_cookies
 */
export function loginGenCookiesUsingPost2(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/outsource/v2/login/gen_cookies', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/login/gen_cookies
 */
export function loginGenCookiesUsingDelete2(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/outsource/v2/login/gen_cookies', {
    method: 'DELETE',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/login/gen_cookies
 */
export function loginGenCookiesUsingOptions2(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/outsource/v2/login/gen_cookies', {
    method: 'OPTIONS',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/login/gen_cookies
 */
export function loginGenCookiesUsingHead2(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/outsource/v2/login/gen_cookies', {
    method: 'HEAD',
    data,
    ...opts,
  });
}
/**
 * unknown /api/outsource/v2/login/gen_cookies
 */
export function loginGenCookiesUsingPatch2(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/outsource/v2/login/gen_cookies', {
    method: 'PATCH',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/login/gen_cookies
 */
export function loginGenCookiesUsingGet2(
  params: GenCookiesParam,
  opts?: ReqOpts
) {
  const { ...query } = params;
  return request<ResponseListCookie>(
    createUri('/api/v2/login/gen_cookies', query),
    { ...opts }
  );
}
/**
 * unknown /api/v2/login/gen_cookies
 */
export function loginGenCookiesUsingPut3(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/v2/login/gen_cookies', {
    method: 'PUT',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/login/gen_cookies
 */
export function loginGenCookiesUsingPost3(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/v2/login/gen_cookies', {
    method: 'POST',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/login/gen_cookies
 */
export function loginGenCookiesUsingDelete3(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/v2/login/gen_cookies', {
    method: 'DELETE',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/login/gen_cookies
 */
export function loginGenCookiesUsingOptions3(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/v2/login/gen_cookies', {
    method: 'OPTIONS',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/login/gen_cookies
 */
export function loginGenCookiesUsingHead3(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/v2/login/gen_cookies', {
    method: 'HEAD',
    data,
    ...opts,
  });
}
/**
 * unknown /api/v2/login/gen_cookies
 */
export function loginGenCookiesUsingPatch3(
  data?: GenCookiesParam,
  opts?: ReqOpts
) {
  return request<ResponseListCookie>('/api/v2/login/gen_cookies', {
    method: 'PATCH',
    data,
    ...opts,
  });
}
